// auth.js MUST be imported first — it sets up window.client, window.authedUser,
// and all other globals that the rest of the app depends on.
import { AppState } from './auth.js';

// nav.js — side-effect import, initializes navigation UI on ReadyToRender
import './nav.js';

// Tablesort number sort plugin — enables numeric sorting on data-sort values
import 'tablesort/dist/sorts/tablesort.number.min.js';

// This can be used when the new Codebuild is in place that writes version.json.
import versionInfo from '../version.json';
import moment from 'moment';
window.moment = moment;
import { buildFormattingConfig } from './helpers/numeric-formatter.js';
import { normalizeDateString, dateStringToUnix, fastFormatDate } from './helpers/date-utils.js';
import { extractCertificateNumber } from './helpers/transaction-utils.js';
import { filterDocuments, filterDocsByYear } from './helpers/document-filters.js';
import { calcTotalPages, getPageSlice, renderPaginationControls } from './helpers/pagination.js';
import { sortDocuments } from './helpers/sort-utils.js';
import {
	dateRangeOptions as dateRangeOptionsET,
	formatDateRangeLabel,
	generateDateRangeOptionsHtml as generateDateRangeOptionsHtmlET,
	getDateRangeTimestamps as getDateRangeTimestampsET,
	filterByDateRange as filterByDateRangeET,
	addCustomOptionClickHandler,
	updateDateRangeDropdownLabel,
	clearCustomDateRange,
	openCustomDateRangeModal as openCustomDateRangeModalHelper,
} from './helpers/date-range-filter.js';

// Dynamic page script imports — webpack creates separate chunks per page.
// Page scripts listen for ReadyToRender, so we must ensure the chunk is loaded before that fires.
// Store the import promise so ReadyToRender dispatch can await it.
const _pagePath = window.location.pathname.replace(/\/+$/, '') || '/';
const _pageImports = [];

// Root page (dashboard)
if (_pagePath === '' || _pagePath === '/') {
	_pageImports.push(import('./charting.js'));
	_pageImports.push(import('./onboarding.js'));
}

// Investor portal pages
if (_pagePath.startsWith('/portfolio')) _pageImports.push(import('../portfolio/portfolio.js'));
else if (_pagePath.startsWith('/trade')) _pageImports.push(import('../trade/trade.js'));
else if (_pagePath.startsWith('/documents/view')) {
	/* no page script */
} else if (_pagePath.startsWith('/documents')) {
	/* no page script — rendering handled by loadPageContent */
} else if (_pagePath.startsWith('/calendar') && !_pagePath.startsWith('/admin')) _pageImports.push(import('../calendar/calendar.js'));
else if (_pagePath.startsWith('/profile') && !_pagePath.startsWith('/admin')) _pageImports.push(import('../profile/profile.js'));
else if (_pagePath.startsWith('/reports/view')) {
	/* no page script */
} else if (_pagePath.startsWith('/reports') && !_pagePath.startsWith('/admin')) _pageImports.push(import('../reports/reports.js'));
else if (_pagePath.startsWith('/campaign/view')) {
	/* no page script */
} else if (_pagePath.startsWith('/campaign')) _pageImports.push(import('../campaign/campaign.js'));
else if (_pagePath.startsWith('/data-room/view')) {
	/* no page script */
} else if (_pagePath.startsWith('/data-room')) _pageImports.push(import('../data-room/data-room.js'));
else if (_pagePath.startsWith('/updates/view')) {
	/* no page script */
} else if (_pagePath.startsWith('/updates')) _pageImports.push(import('../updates/updates.js'));
else if (_pagePath.startsWith('/faq/view')) {
	/* no page script */
} else if (_pagePath.startsWith('/faq')) _pageImports.push(import('../faq/faq.js'));
else if (_pagePath.startsWith('/actions')) _pageImports.push(import('../actions/action.js'));
else if (_pagePath.startsWith('/team')) _pageImports.push(import('../team/team.js'));
else if (_pagePath.startsWith('/rooms')) _pageImports.push(import('../rooms/room.js'));
else if (_pagePath.startsWith('/signature')) _pageImports.push(import('../signature/signature.js'));
else if (_pagePath.startsWith('/forms')) _pageImports.push(import('../forms/forms.js'));
else if (_pagePath.startsWith('/enroll')) _pageImports.push(import('../enroll/enroll.js'));
else if (_pagePath.startsWith('/survey')) _pageImports.push(import('../survey/survey.js'));
else if (_pagePath.startsWith('/proxy-voting')) _pageImports.push(import('../proxy-voting/proxy-voting.js'));
else if (_pagePath.startsWith('/investor-agreement')) _pageImports.push(import('../investor-agreement/investor-agreement.js'));
else if (_pagePath.startsWith('/start-guide')) {
	/* no page script - uses start-guide.js loaded differently */
} else if (_pagePath.startsWith('/signin')) _pageImports.push(import('../signin/signin.js'));
else if (_pagePath.startsWith('/sso-login')) _pageImports.push(import('../sso-login/sso-login.js'));
else if (_pagePath.startsWith('/app')) _pageImports.push(import('../app/app.js'));
else if (_pagePath.startsWith('/supervisor')) _pageImports.push(import('../admin/users/users.js'));
// Admin pages
else if (_pagePath.startsWith('/admin/dashboard')) {
	/* Vue-powered, no separate page script */
} else if (_pagePath.startsWith('/admin/users/new')) {
	/* new-equity.js loaded inline */
} else if (_pagePath.startsWith('/admin/users/edit')) _pageImports.push(import('../admin/users/edit/edit.js'));
else if (_pagePath.startsWith('/admin/users')) {
	_pageImports.push(import('./onboarding.js'));
	_pageImports.push(import('../admin/users/users.js'));
} else if (_pagePath.startsWith('/admin/allocator')) _pageImports.push(import('../admin/allocator/allocator.js'));
else if (_pagePath.startsWith('/admin/buy-sell')) _pageImports.push(import('../admin/buy-sell/buy-sell.js'));
else if (_pagePath.startsWith('/admin/calendar')) _pageImports.push(import('../admin/calendar/calendar.js'));
else if (_pagePath.startsWith('/admin/content-mgr')) _pageImports.push(import('../admin/content-mgr/content-mgr.js'));
else if (_pagePath.startsWith('/admin/custom-reports')) _pageImports.push(import('../admin/custom-reports/custom-reports.js'));
else if (_pagePath.startsWith('/admin/enrollment')) _pageImports.push(import('../admin/enrollment/enrollment.js'));
else if (_pagePath.startsWith('/admin/fundraising')) _pageImports.push(import('../admin/fundraising/fundraising.js'));
else if (_pagePath.startsWith('/admin/ledgers')) _pageImports.push(import('../admin/ledgers/ledgers.js'));
else if (_pagePath.startsWith('/admin/payments')) _pageImports.push(import('../admin/payments/payments.js'));
else if (_pagePath.startsWith('/admin/profile')) _pageImports.push(import('../admin/profile/profile.js'));
else if (_pagePath.startsWith('/admin/report-builder')) _pageImports.push(import('../admin/report-builder/report-builder.js'));
else if (_pagePath.startsWith('/admin/report-viewer')) _pageImports.push(import('../admin/report-viewer/report-viewer.js'));
else if (_pagePath.startsWith('/admin/reports')) _pageImports.push(import('../admin/reports/reports.js'));
else if (_pagePath.startsWith('/admin/requests')) _pageImports.push(import('../admin/requests/requests.js'));
else if (_pagePath.startsWith('/admin/rooms')) _pageImports.push(import('../admin/rooms/rooms.js'));
else if (_pagePath.startsWith('/admin/signatures')) _pageImports.push(import('../admin/signatures/signatures.js'));
else if (_pagePath.startsWith('/admin/start')) _pageImports.push(import('../admin/start/start.js'));
else if (_pagePath.startsWith('/admin/surveying')) _pageImports.push(import('../admin/surveying/surveying.js'));
else if (_pagePath.startsWith('/admin/tax-forms')) _pageImports.push(import('../admin/tax-forms/tax-forms.js'));
else if (_pagePath.startsWith('/admin/updates')) _pageImports.push(import('../admin/updates/updates.js'));
else if (_pagePath.startsWith('/admin/voting')) _pageImports.push(import('../admin/voting/voting.js'));

// Expose promise so ReadyToRender can wait for page scripts to load
window._pageScriptReady = Promise.all(_pageImports);

// Dispatch ReadyToRender only after page script chunks have loaded
function fireReadyToRender() {
	window._pageScriptReady.then(() => {
		document.dispatchEvent(new Event('ReadyToRender'));
	});
}

// Service worker registration (most pages)
import('./swregistration.js');

const logVersionInfoInput = (versionInfoInput) => {
	// Client-specific: None
	// Early return if no valid input
	if (!versionInfoInput || Object.keys(versionInfoInput).length === 0) return;

	const logLines = ['- Deployment: Id: ' + versionInfoInput.version];

	// Handle releasedFeatures (production format)
	if (versionInfoInput.releasedFeatures && versionInfoInput.releasedFeatures.features && versionInfoInput.releasedFeatures.features.length > 0) {
		// Remove duplicates
		const seen = new Set();
		const uniqueFeatures = versionInfoInput.releasedFeatures.features.filter((feature) => {
			const key = feature.type + ':' + feature.ref;
			if (seen.has(key)) return false;
			seen.add(key);
			return true;
		});
		const featureStrings = uniqueFeatures.map((feature) => {
			return feature.type === 'issue' ? '#' + feature.ref : 'Branch: ' + feature.ref;
		});
		logLines.push('  - Released Features: ' + featureStrings.join(' , '));
		logLines.push('  - Base Branch: ' + versionInfoInput.releasedFeatures.baseBranch);
		logLines.push('  - From Commit: ' + versionInfoInput.releasedFeatures.fromCommit + ' to ' + versionInfoInput.releasedFeatures.toCommit);
	}
	// Handle pendingFeatures (staging format)
	else if (versionInfoInput.pendingFeatures && versionInfoInput.pendingFeatures.length > 0) {
		// Remove duplicates
		const seen = new Set();
		const uniqueFeatures = versionInfoInput.pendingFeatures.filter((feature) => {
			const key = feature.type + ':' + feature.ref;
			if (seen.has(key)) return false;
			seen.add(key);
			return true;
		});
		const featureStrings = uniqueFeatures.map((feature) => {
			return feature.type === 'issue' ? '#' + feature.ref : 'Branch: ' + feature.ref;
		});
		logLines.push('  - Pending Features: ' + featureStrings.join(' , '));
	} else {
		logLines.push('  - Features: None');
	}

	console.log(logLines.join('\n'));
};
logVersionInfoInput(versionInfo);

let TRACE = false;
let tracer = 'START';

// DOM element refs — hoisted to module scope so multiple functions can share them
let viewCapAcctIconLarge, viewCapAcctIconSmall;
let viewEventsTransReportIconLarge, viewEventsTransReportIconSmall;
let duplicateModal, cookieModal;
let hero, aboutContent, metrics, faq, canEnroll;
let callToActionDetailsSmall, callToActionDetailsLarge;
if (TRACE) {
	console.log = function (err, message) {
		tracer +=
			' | ' +
			(err.toString() + (message ? ' - ' + message : ''))
				.substring(0, 200)
				.trim()
				.replace(/object/gi, '')
				.replace(/undefined/gi, '');
	};
}

const start = Date.now();
console.log('Start Time: ' + new Date());

import * as Sentry from '@sentry/browser';

// Determine environment based on hostname
let environment = 'development';
if (typeof window !== 'undefined') {
	const hostname = window.location.hostname;
	if (hostname === 'www.staging.nthround.io' || hostname === 'staging.nthround.io') {
		environment = 'staging';
	} else if (hostname.endsWith('.nthround.io')) {
		environment = 'production';
	}
}

Sentry.init({
	dsn: 'https://8f918fe3dde03a9659c86ea7fcbb1094@o4510342923223040.ingest.us.sentry.io/4510342926696448',

	// Environment configuration
	environment: environment,

	// Enable integrations for comprehensive error tracking
	integrations: [
		Sentry.browserTracingIntegration(),
		Sentry.replayIntegration({
			maskAllText: false,
			blockAllMedia: false,
		}),
	],

	// Performance Monitoring
	tracesSampleRate: 0.1, // Capture 10% of transactions for performance monitoring

	// Session Replay
	replaysSessionSampleRate: 0.1, // Sample 10% of sessions
	replaysOnErrorSampleRate: 1.0, // Sample 100% of sessions with errors

	// PII
	sendDefaultPii: true,

	// Error filtering
	ignoreErrors: [
		// Browser extensions
		'top.GLOBALS',
		'chrome-extension://',
		'moz-extension://',
		// Random network errors
		'NetworkError',
		'Non-Error promise rejection captured',
	],
});

const qs = require('qs');
const NumAbbr = require('number-abbreviate');
// Removed: Quill, Papa, QRCode, AccessParser, Api2Pdf - now lazy loaded below

// Dynamic import loaders for performance optimization
let _xlsxCache = null;
window.loadXLSX = async function () {
	if (_xlsxCache) return _xlsxCache;
	try {
		console.log('[Performance] Lazy loading XLSX library...');
		const module = await import(/* webpackChunkName: "xlsx" */ 'xlsx');
		_xlsxCache = module.default || module;
		console.log('[Performance] XLSX library loaded');
		return _xlsxCache;
	} catch (err) {
		console.error('[Performance] Failed to load XLSX library:', err);
		throw new Error('XLSX library could not be loaded. Please try uploading a CSV file instead.');
	}
};

import { Buffer } from 'buffer';
import { saveAs } from 'file-saver';
// Removed: ConvertApi - now lazy loaded via window.loadConvertApi()
import { giftTransfer, resetGiftTransferState } from './helpers/gift-transfer.js';
import { renderUserSettings } from './helpers/user-settings.js';
import { addFootnoteAndAdjustSpacing, addFootnoteToPortfolioGraph, addFootnoteToClassImage } from './helpers/footnote.js';
import { buildStackedTimeSeriestooltip, buildValueCompositionTooltip, buildClassTimeSeriestooltip } from './helpers/chart-tooltips.js';
import { buildAccountSummary, generateLoansReport, generateAmortizationSchedule as _generateAmortizationSchedule } from './helpers/create-reports.js';
import { reportHtmlHandler } from './transactions_modal.js';
import { fetchScopedTransactions } from './services/TransactionFetchService.js';

import './helpers/ach-helpers.js';

// Calculation Engine imports
import { FundInvestorCalculator } from './calculators/FundInvestorCalculator.js';
import { PrivateCompany409ACalculator } from './calculators/PrivateCompany409ACalculator.js';
import { ContributionsAccrualsCalculator } from './calculators/ContributionsAccrualsCalculator.js';
import { ValuationPlusBasisCalculator } from './calculators/ValuationPlusBasisCalculator.js';
import { TvpiFundCalculator } from './calculators/TvpiFundCalculator.js';
import { IncomeAllocationsCalculator } from './calculators/IncomeAllocationsCalculator.js';
import { CalculatorRegistry } from './calculators/CalculatorRegistry.js';
import { CalculationService } from './services/CalculationService.js';
import { HeadlineService } from './services/HeadlineService.js';
import { EquityCardService } from './services/EquityCardService.js';
import { EquityCardFormatter } from './formatters/EquityCardFormatter.js';
import { ChartFormatter } from './formatters/ChartFormatter.js';
import { HeadlineFormatter } from './formatters/HeadlineFormatter.js';
import { GraphTypes, TimeSeriesStrategies } from './calculators/GraphTypes.js';
import { TimeSeriesBuilder } from './builders/TimeSeriesBuilder.js';
import { SessionStorageService } from './services/SessionStorageService.js';
import { CapTableCacheService } from './services/CapTableCacheService.js';
import {
	isUserTransaction,
	getActiveUserIds,
	isConsolidatedView,
	getSignedQuantityForUser,
	getUserTransactionsForClass,
	isActiveTranche,
	getEffectiveStrikePriceForUser,
	calculateVestedFromSchedule,
} from './helpers/equity-calculation-utils.js';
import { buildCalculatorInput, isTvpiValuation } from './helpers/calculator-input-builder.js';
import { computeShareholderPreview } from './helpers/preview-shareholder.js';
import { isCoreDataValid, showFatalError } from './helpers/error-ui.js';
import { fetchTransactionsForPreview } from './helpers/preview-transactions.js';
window.CapTableCacheService = CapTableCacheService;
CapTableCacheService.init();

// Set client-specific number formatting for equity card templates (BW: always 4 decimals)
if (isBW) {
	EquityCardFormatter.numberFormatOptions = { minimumFractionDigits: 4, maximumFractionDigits: 4 };
	HeadlineFormatter.numberFormatOptions = { minimumFractionDigits: 4, maximumFractionDigits: 4 };
}

// Reset gift transfer modal state when shareholder data finishes loading,
// so the next GT click rebuilds with complete transaction data.
document.addEventListener('ShareholderComplete', () => resetGiftTransferState());
document.addEventListener('CarsAndObserversComplete', () => resetGiftTransferState());

// Dynamic import loader for PDF.js library
let _pdfjsCache = null;
window.loadPDFJS = async function () {
	if (_pdfjsCache) return _pdfjsCache;
	try {
		console.log('[Performance] Lazy loading PDF.js library...');
		const module = await import(/* webpackChunkName: "pdfjs" */ 'pdfjs-dist/build/pdf.min.mjs');
		module.GlobalWorkerOptions.workerSrc = '/pdfjs-dist/build/pdf.worker.min.mjs';
		_pdfjsCache = module;
		window.pdfjsLib = module;
		console.log('[Performance] PDF.js library loaded');
		return _pdfjsCache;
	} catch (err) {
		console.error('[Performance] Failed to load PDF.js library:', err);
		throw new Error('PDF.js library could not be loaded. Please try refreshing the page.');
	}
};

// Dynamic import loader for Quill (Rich Text Editor - ~209KB)
let _quillCache = null;
window.loadQuill = async function () {
	if (_quillCache) return _quillCache;
	try {
		console.log('[Performance] Lazy loading Quill library...');
		const module = await import(/* webpackChunkName: "quill" */ 'quill');
		_quillCache = module.default || module;
		window.Quill = _quillCache;
		console.log('[Performance] Quill library loaded');
		return _quillCache;
	} catch (err) {
		console.error('[Performance] Failed to load Quill library:', err);
		throw new Error('Rich text editor could not be loaded. Please try refreshing the page.');
	}
};

// Dynamic import loader for QRCode (easyqrcodejs - ~53KB)
let _qrcodeCache = null;
window.loadQRCode = async function () {
	if (_qrcodeCache) return _qrcodeCache;
	try {
		console.log('[Performance] Lazy loading QRCode library...');
		const module = await import(/* webpackChunkName: "qrcode" */ 'easyqrcodejs');
		_qrcodeCache = module.default || module;
		console.log('[Performance] QRCode library loaded');
		return _qrcodeCache;
	} catch (err) {
		console.error('[Performance] Failed to load QRCode library:', err);
		throw new Error('QR code generator could not be loaded. Please try refreshing the page.');
	}
};

// Dynamic import loader for ConvertApi (Document Conversion - ~7KB)
let _convertApiCache = null;
window.loadConvertApi = async function () {
	if (_convertApiCache) return _convertApiCache;
	try {
		console.log('[Performance] Lazy loading ConvertApi library...');
		const module = await import(/* webpackChunkName: "convertapi" */ 'convertapi-js');
		_convertApiCache = module.default || module;
		console.log('[Performance] ConvertApi library loaded');
		return _convertApiCache;
	} catch (err) {
		console.error('[Performance] Failed to load ConvertApi library:', err);
		throw new Error('Document conversion could not be loaded. Please try refreshing the page.');
	}
};

// Dynamic import loader for ExcelJS (Excel generation - ~700KB)
let _exceljsCache = null;
window.loadExcelJS = async function () {
	if (_exceljsCache) return _exceljsCache;
	try {
		console.log('[Performance] Lazy loading ExcelJS library...');
		const module = await import(/* webpackChunkName: "exceljs" */ 'exceljs');
		_exceljsCache = module.default || module;
		window.ExcelJS = _exceljsCache;
		console.log('[Performance] ExcelJS library loaded');
		return _exceljsCache;
	} catch (err) {
		console.error('[Performance] Failed to load ExcelJS library:', err);
		throw new Error('Excel export library could not be loaded. Please try refreshing the page.');
	}
};

// Dynamic import loader for Api2Pdf (PDF generation API)
let _api2pdfCache = null;
window.loadApi2Pdf = async function () {
	if (_api2pdfCache) return _api2pdfCache;
	try {
		console.log('[Performance] Lazy loading Api2Pdf library...');
		const module = await import(/* webpackChunkName: "api2pdf" */ 'api2pdf');
		_api2pdfCache = module.default || module;
		console.log('[Performance] Api2Pdf library loaded');
		return _api2pdfCache;
	} catch (err) {
		console.error('[Performance] Failed to load Api2Pdf library:', err);
		throw new Error('PDF generation library could not be loaded. Please try refreshing the page.');
	}
};

// Dynamic import loader for jsPDF (Client-side PDF generation - ~200KB)
let _jspdfCache = null;
window.loadJsPDF = async function () {
	if (_jspdfCache) return _jspdfCache;
	try {
		console.log('[Performance] Lazy loading jsPDF library...');
		const module = await import(/* webpackChunkName: "jspdf" */ 'jspdf');
		_jspdfCache = module.default || module;
		console.log('[Performance] jsPDF library loaded');
		return _jspdfCache;
	} catch (err) {
		console.error('[Performance] Failed to load jsPDF library:', err);
		throw new Error('PDF generation library could not be loaded. Please try refreshing the page.');
	}
};

// Dynamic import loader for jspdf-autotable (Table plugin for jsPDF - ~50KB)
// In v5.x the plugin is no longer auto-applied in bundled environments; we must call applyPlugin(jsPDF).
// Load jsPDF first so we apply the plugin to the same constructor used by the app.
let _autoTableCache = null;
window.loadAutoTable = async function () {
	if (_autoTableCache) return _autoTableCache;
	try {
		console.log('[Performance] Lazy loading jspdf-autotable library...');
		const jsPDF = await window.loadJsPDF();
		const atModule = await import(/* webpackChunkName: "jspdf-autotable" */ 'jspdf-autotable');
		// Support both ESM named export and CJS/namespace (staging/production bundling)
		const applyPlugin = atModule.applyPlugin || (atModule.default && atModule.default.applyPlugin);
		if (typeof applyPlugin !== 'function') {
			throw new Error('jspdf-autotable applyPlugin not found. Check that jspdf-autotable is loaded correctly.');
		}
		applyPlugin(jsPDF);
		_autoTableCache = true; // Mark as loaded
		console.log('[Performance] jspdf-autotable library loaded and applied to jsPDF');
		return _autoTableCache;
	} catch (err) {
		console.error('[Performance] Failed to load jspdf-autotable library:', err);
		throw new Error('PDF table plugin could not be loaded. Please try refreshing the page.');
	}
};

// Vue 3 Dashboard - Lazy loaded to reduce bundle size
let app;
let pinia;
let _vueInitialized = false;

window.initVueDashboard = async function () {
	if (_vueInitialized) {
		console.log('[Performance] Vue dashboard already initialized');
		return;
	}

	console.log('[Performance] Lazy loading Vue 3 dashboard...');

	try {
		const [{ createApp }, { createPinia }, Dashboard] = await Promise.all([
			import(/* webpackChunkName: "vue-core" */ 'vue'),
			import(/* webpackChunkName: "vue-core" */ 'pinia'),
			import(/* webpackChunkName: "vue-dashboard" */ '../shared_assets/components/dashboard/Dashboard.vue'),
		]);

		app = createApp(Dashboard.default || Dashboard);
		pinia = createPinia();
		app.use(pinia);
		app.provide('client', client);
		app.mount('#vue-app');

		_vueInitialized = true;
		console.log('[Performance] Vue 3 dashboard loaded and mounted');
	} catch (err) {
		console.error('[Performance] Failed to load Vue dashboard:', err);
		throw new Error('Dashboard could not be loaded. Please try refreshing the page.');
	}
};

// Date range filter state for E&T modal (utilities imported from helpers/date-range-filter.js)
let selectedETDateRange = 'ALL';
let customDateStartET = null,
	customDateEndET = null;

// Reset date filter state (used when switching tabs)
function resetDateFilterState() {
	selectedETDateRange = 'ALL';
	customDateStartET = null;
	customDateEndET = null;
	// Clear modal inputs if they exist
	const modalStartInput = document.getElementById('modalDateRangeStart');
	const modalEndInput = document.getElementById('modalDateRangeEnd');
	if (modalStartInput) modalStartInput.value = '';
	if (modalEndInput) modalEndInput.value = '';
}

// Listen for tab switch event to reset date filter
document.addEventListener('DashboardTabSwitch', resetDateFilterState);

let search = window.location.search;
if (search.startsWith('?testclient=')) {
	let testclient = search.replace('?testclient=', '');
	if (nthAdmin) {
		document.cookie = 'nth.TestClient=' + testclient;
		window.location.href = 'http://' + window.location.host;
	}
	// else window.location.href = 'https://' + testclient + '.nthround.io/';
}

let pn = window.location.pathname;

// Helper function to ensure twoFactorModal is non-dismissible
// Called after any modal initialization to prevent being overwritten
const ensureTwoFactorModalNonDismissible = function () {
	const twoFactorModal = document.getElementById('twoFactorModal');
	if (twoFactorModal) {
		const instance = M.Modal.getInstance(twoFactorModal);
		if (instance) {
			instance.destroy();
		}
		M.Modal.init(twoFactorModal, { dismissible: false });
		console.log('🔒 twoFactorModal set to non-dismissible');
	}
};
if (!pn.endsWith('/')) window.location.replace(pn + '/' + search);
if (pn != pn.toLowerCase()) window.location.replace(pn.toLowerCase() + '/' + search);
if (pn == '/nda/') window.location.replace('/forms/?form=NDA');
// Join page: redirect to signin unless join_page flag is enabled (checked after clientData loads)
if (pn == '/admin/' || pn.includes('/shareholders')) window.location.replace('/');
if (window.location.href.endsWith('/#')) window.location.href = window.location.href.slice(0, -1);
if (window.location.href.endsWith('/#!')) window.location.href = window.location.href.slice(0, -2);

// globals
const NO_BASIS = isACC || isAES || isH5H;
const HAS_STMTS = isVMS || isJMH;
const REQUIRE_ENROLL = isAlera || isJMS || isONW || isStaging;
// const TRADING_CLASSES = ["C","NTH","NTHP","ONW","ROKK","LP","GDAT"];	// replaced by showTradeCTA under each equity class

// ============================================================
// CALCULATION ENGINE BOOTSTRAP
// ============================================================

// Create registry and register all calculators
const calculatorRegistry = new CalculatorRegistry();
calculatorRegistry
	.register(FundInvestorCalculator)
	.register(PrivateCompany409ACalculator)
	.register(ContributionsAccrualsCalculator)
	.register(ValuationPlusBasisCalculator)
	.register(TvpiFundCalculator)
	.register(IncomeAllocationsCalculator);

// Create service - uses clientData.flags.default_value_method or falls back to private_company_409a
const calculationService = new CalculationService(calculatorRegistry, {});

// Expose registry globally for settings UI to dynamically populate calculator dropdown
window.calculatorRegistry = calculatorRegistry;

// Expose functions referenced by dynamically imported page scripts
window.enableUserEditControls = enableUserEditControls;
window.finishSetAlias = finishSetAlias;

// Session Storage Service initialization
SessionStorageService.init({
	setLabels,
	autoPopulateUser,
	sessionStorageSize,
	getStandardTransactionsFromFile,
	getAleraHoldingsFromFile,
});

// ============================================================

// global fields
let symbol;
let className;
let acctnames;
let workspaces;
let freshSignIn;
let campaignHref;
let reportEndDate;
let reportStartDate;
let origClientDataAbout;

let labelButtonCTA = ''; // was "Sign " + language.label_proxy_voting
let labelButtonDone = '';
let labelHeadingCTA = ''; // was language.label_new_offering

// Tablesort instances for cleanup
let equityTableSort = null;
let debtTableSort = null;

// sessionStorage and flags
let UFL;
let USE_STRIKE;
let SHOW_STATS;
let SHADE_GRAPH;
let SHOW_RETURNS; // isMH
let SHOW_AGGREGATE;
let SHOW_TAX_BASIS_COLUMN;
let USE_TAX_BASIS;
let NEVER_ENROLLED; // hide button to folks with no signatures or investor_ARCHIVE

// event breadcrumbs
let typoError;
let domFinished;
let authFinished;
let storageFinished;
let extDataFinished;
let capTableReady;
let renderEventFired;
let aleraSafetyCount = 0;
let refreshTime = Date.now();
window.refreshTime = refreshTime;

// language fields
let usePct = false;
let labelROI = 'ROI';
let labelTVPI = 'TVPI';
let labelHeadline = '';
let sharesType = 'shares';
let vestingType = 'shares';
let labelTeamTab = 'Team';
let labelInvestments = '';
let labelEquityType = 'Shares';
let labelUpdatesTab = 'Updates';
let labelReportsTab = 'Reports';
let labelTaxBasis = 'Tax Basis';
let labelProspects = 'Prospects';
let labelDataRoomTab = 'Data Room';
let labelValuation = 'Share Price';
let labelGain = 'Capital&nbsp;Gain';
let labelPaidIn = 'Paid-In Capital';
let labelPortfolioTab = 'Portfolio';
let labelShareholders = 'Investors';
let labelCostPrice = 'Price (basis)';
let labelUncalled = 'Uncalled Balance';

// listenerbreadcrumbs
let addedStatsHeadlineAlready;
let addedBuildETListenerAlready;
let addedVestingListenerAlready;
let addedToolTipListenerAlready;
let addedLandingListenersAlready;
let addedChartClickListenerAlready;
let addedResultantPDFListenerAlready;
let addedEventsTransListenersAlready;
let addedScenarioHelpListenerAlready;
let addedExportTranchesListenerAlready;
let addedAuthenticatorListenersAlready;
let addedPayNowListenerAlready;
let addedFirstEntryEventListenerAlready;
let addedAuthenticatorChallengerAlready;
let addedAccountSummaryListenersAlready;
let addedExportValuationsListenerAlready;

DOMPurify.setConfig({
	SAFE_FOR_TEMPLATES: true,
	FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover', 'onfocus', 'onkeydown', 'onchange', 'onpaste', 'onsubmit'], // Blocks event-based attacks
	FORBID_TAGS: ['script', 'iframe', 'object', 'embed', 'form'], // Blocks risky elements
	ALLOWED_TAGS: [
		'b',
		'i',
		'u',
		'p',
		'span',
		'strong',
		'em',
		'br',
		'div',
		'a',
		'ol',
		'li',
		'ul',
		'sup',
		'img',
		'hr',
		'table',
		'thead',
		'tbody',
		'tr',
		'td', // ✅ NEW
		'html',
		'head',
		'meta',
		'style',
		'body', // ✅ CAREFULLY ALLOW — see note below
	],
	ALLOWED_ATTR: ['style', 'class', 'href', 'target', 'src', 'alt', 'title', 'height', 'width', 'colspan', 'rowspan'],
	ALLOW_ARIA_ATTR: true,
	ALLOW_DATA_ATTR: false, // Prevents `data-*` attributes (often used in phishing attacks)
	KEEP_CONTENT: true, // Prevents certain inner content from being nuked
});

// Since you're allowing inline images in emails (often data:image/png;base64,...), make sure you're okay with data: URIs.
// DOMPurify allows them by default, but you may want to limit that to just images.
// If you want to be strict and only allow data: URIs for images, you can use a hook like this:
/* DOMPurify.addHook('uponSanitizeAttribute', (node, data) => {
	if (data.attrName === 'src' && node.tagName === 'IMG') {
		const value = data.attrValue || '';
		if (value.startsWith('data:') && !value.match(/^data:image\/(png|jpeg|gif|webp);base64,/)) data.keepAttr = false;
	}
}); */

// Backup the original DOMPurify.sanitize method
const originalSanitize = DOMPurify.sanitize;

DOMPurify.sanitize = function (inputHTML, config) {
	// Ensure input is valid
	if (typeof inputHTML !== 'string') return inputHTML;

	// Preserve base64 <img> tags to avoid DOMPurify slowdowns on large pasted content
	const imgPlaceholders = [];
	const strippedHTML = inputHTML.replace(/<img[^>]+src="data:image\/[^"]+"[^>]*>/gi, (match) => {
		imgPlaceholders.push(match);
		return `__IMG_PLACEHOLDER_${imgPlaceholders.length - 1}__`;
	});

	// AST PATH: If no Mustache variables, sanitize immediately (skip encoding/decoding)
	const needsMustacheHandling = strippedHTML.includes('{{') && strippedHTML.includes('}}');
	const encodedHTML = needsMustacheHandling ? strippedHTML.replace(/{{/g, '__MUSTACHE_START__').replace(/}}/g, '__MUSTACHE_END__') : strippedHTML;

	// Sanitize using DOMPurify
	let sanitizedHTML = originalSanitize(encodedHTML, config);

	// Decode Mustache variables back after sanitization
	if (needsMustacheHandling) {
		sanitizedHTML = sanitizedHTML.replace(/__MUSTACHE_START__/g, '{{').replace(/__MUSTACHE_END__/g, '}}');
	}

	// Restore base64 image tags
	imgPlaceholders.forEach((img, i) => {
		sanitizedHTML = sanitizedHTML.replace(`__IMG_PLACEHOLDER_${i}__`, img);
	});

	return sanitizedHTML;
};

readSessionStorage();

// This needs to be configured first, because it can get called so early.
// But it also needs a delay so that DOM can get rendered (crazy...)
document.addEventListener(
	'scaffoldAuthenticatorModals',
	function (e) {
		setTimeout(() => {
			if (USE_AUTHENTICATOR || authenticatorValue == 'SignIn') scaffoldAuthenticatorModals();
		}, 50); // 500);
	},
	false
);

// Use browser back to break Catch 22 with Switch Account + Confidentiality modal
window.addEventListener('popstate', function (event) {
	if (event.state && event.state.modal == 'switchAccount') location.reload();
});

if (performance.navigation.type == 1) {
	//this will always be true now due to performance.navigation deprecation
	if (!blurBlock) {
		sessionStorage.clear();
		blurNames = null;
		userData = null;
		clientData = null;
		capTableData = [];
	}
	console.info('RELOAD');
	let href = window.location.href;
	if (href.includes('?edit')) window.location.href = href.replace('?edit', '');
}

// 3/26/2024 WITH AUTHENTICATOR, THE NEXT 20 LINES NOW FEEL NOT WORTH IT (just to potetially save milliseconds of load time...)

if ((pn.includes('/admin') && !capTableData) || !userData || !clientData || performance.navigation.type == 1 || location.search.includes('refresh=true')) {
	if (!USE_PIN && !USE_AUTHENTICATOR) refreshSessionStorage(false); // <-- For high security, don't do this unless/until already authed
	freshSignIn = true;
}

// 5/14/2023 This seems to have started causing a signin screen flash (GitHub #1118)
if (false) {
	// typeof checkAuth !== "function") {
	console.log('Function checkAuth not found.');
	console.log('Redirecting - no checkAuth');
	// Join and forms do not require auth
	if (!pn.includes('/signin') && !pn.includes('/join') && !pn.includes('/forms')) window.location.replace('/signin/');
}

if (pn.includes('/admin') && !USE_PIN && !USE_AUTHENTICATOR) {
	sessionStorage.removeItem('stockCerts');
	checkAuth();
	// 3/23/2024 moved to auth.js
	// if (USE_AUTHENTICATOR && !addedAuthenticatorListenersAlready) scaffoldAuthenticatorModals();
	if (isAlera && userData && userData.userid != authedUser) refreshSessionStorage(false);
} else {
	// 3/26/2024 START HERE IF THE ABOVE LINES ARE REMOVED
	if (pn.includes('/admin')) sessionStorage.removeItem('stockCerts');
	AppState.clientData = JSON.parse(sessionStorage.getItem(client + '.Data'));
	if (clientData) {
		checkAuth();
		// 3/23/2024 moved to auth.js
		// if (USE_AUTHENTICATOR && !addedAuthenticatorListenersAlready) scaffoldAuthenticatorModals();
		setLabels('launch');
	} else {
		let currentlyRaisingEndpoint = urlBase + '/clientConfig/currently-raising?client=' + client;

		// If we are on a shareholder route, check if fundraising (campaign?) is currently active for the client
		// Definitely don't understand this.  Why is currently_raising flag so special???  12/23/2023
		// It seems to redirect to the Join page rather than authenticate.
		// 3/26/2024 Nevermind.  With Authenticator, the code below is critical.
		axios
			.get(currentlyRaisingEndpoint, {
				params: {
					client: client,
					userid: getAuthedUser(),
					cookie: cookie,
				},
			})
			.then(async function (res) {
				if (!res.data || !res.data.Item) {
					typoError = true;
					document.getElementById('loading').innerHTML = `
				<div class="p-2 center-align" style="width: 2000px; margin: 0 auto;">
					<div class="mb-4"><img src="./../../resources/assets/fix_car.png" alt="Man fixing car" class="responsive-img" style="width:500px; height:auto;"/></div>
					<p class="text-secondary">Hmm... weird, we can't find the page you're looking for. <br>Please check that your url is valid. Thank you. (support@nthround.com)</p>
				</div>
			  `;
					// alert("We can't find that page...\nPlease check for typo's in the url above.");
					return;
				}

				if (pn.includes('/signin')) {
					const ssoConfig = res.data.Item.sso;
					let ssoEnabled = false;
					if (ssoConfig?.enabled === true) {
						ssoEnabled = true;
					}
					const ssoRedirectUrl = ssoConfig?.idp_login_url;
					if (ssoEnabled && ssoRedirectUrl) {
						window.location.href = ssoRedirectUrl;
					}
				}
				// 3/26/2024 added next line to handle default setting USE_AUTHENTICATOR = true, but becomes false below
				// See auth.js line 1256 (which won't run) - document.dispatchEvent(new Event('RefreshSessionStorage'));
				// !!! The order below is crucial !!!
				if (pn.includes('/app')) res.data.Item.use_authenticator = false;
				if (USE_AUTHENTICATOR && !res.data.Item.use_authenticator) refreshSessionStorage(false); // <noteworthy for when we hit the missing captable form session warning the third time
				AppState.USE_AUTHENTICATOR = res.data.Item.use_authenticator;

				// Careful, because clientData gets set asynchronously elsewhere...
				if (!clientData) clientData = {};
				clientData.use_authenticator = USE_AUTHENTICATOR;
				clientData.company_logo = res.data.Item.company_logo;

				// let currently_raising = res.data.Item.currently_raising;
				// console.log(currently_raising);
				checkAuth();
			})
			.catch(function (err) {
				console.error(err);
				if (!typoError)
					document.getElementById('loading').innerHTML = `
				<div class="p-2 center-align" style="width: 2000px; margin: 0 auto;">
					<div class="mb-4"><img src="./../../resources/assets/fix_car.png" alt="Man fixing car" class="responsive-img" style="width:500px; height:auto;"/></div>
					<p class="text-secondary">Oops, this is awkward... Please try again in a few minutes! Thank you. (support@nthround.com)</p>
				</div>
			`;
				return 'Fatal error.';
			});
	}
}

// START: Dwolla helpers
const getDwollaProcessor = () => clientData?.ach_env;

const getDwollaEnv = () => {
	const processor = getDwollaProcessor();
	if (processor === 'dwolla_sandbox') return 'sandbox';
	else if (processor === 'dwolla_prod') return 'production';
};

const getPlaidEnvForDwolla = () => {
	const processor = getDwollaProcessor();
	if (processor === 'dwolla_sandbox') return 'sandbox';
	else if (processor === 'dwolla_prod') return 'prod';
};

const getDwollaAchKey = () => {
	// Eventually this should look into client config
	if (getDwollaEnv() === 'production') {
		return 'ach_record_dwolla_prod';
	}
	return 'ach_record_dwolla_sandbox';
};

const getUserDwollaData = (userDataInput) => userDataInput[getDwollaAchKey()];

const createDwollaNthApiTokenForPayNow = () => {
	return new Promise((resolve, reject) => {
		axios
			.post(
				dwollaTokenAPI,
				JSON.stringify({
					client: client,
					userid: authedUser,
					cookie: cookie,
					processor: getDwollaProcessor(),
					proxy_method: 'create_nth_api_token',
				})
			)
			.then((createNthApiTokenRes) => {
				resolve(createNthApiTokenRes);
			})
			.catch((createNthApiTokenErr) => {
				reject(createNthApiTokenErr);
			});
	});
};
// END: Dwolla helpers

function initModal() {}

function setAlias(e, dataId = null) {
	document.body.style.cursor = 'wait';
	document.getElementById('workspaceModal').style.backgroundColor = '#eeeeee';
	let switchAccountSpinner = document.getElementById('switchAccountSpinner');
	if (switchAccountSpinner) switchAccountSpinner.classList.remove('hidden');
	sessionStorage.removeItem('stockCerts');
	let loadCount = Number(sessionStorage.getItem('loadCount'));
	sessionStorage.setItem('loadCount', loadCount + 1);
	let selectedUser = dataId || this.getAttribute('data-id');
	if (selectedUser != userData.userid) {
		currentShareClass = SHOW_AGGREGATE ? -1 : 0;
		sessionStorage.setItem('filterUpdates', 'ALL');
		sessionStorage.setItem('filterReports', 'ALL');
		sessionStorage.setItem('filterDocuments', '');
	}
	console.log(selectedUser);
	axios.defaults.withCredentials = cookie ? false : true;

	if (selectedUser == 'ALL') {
		// Filter to consolidated_accounts if configured (set in DB)
		// Persist to sessionStorage so the filter survives switching to an individual account and back
		let accountsToFetch = [...workspaces];
		let consolidatedAccounts = userData?.clients?.[client]?.consolidated_accounts;
		if (!Array.isArray(consolidatedAccounts) || !consolidatedAccounts.length) {
			try {
				consolidatedAccounts = JSON.parse(sessionStorage.getItem('consolidated_accounts'));
			} catch (_) {}
		}
		if (Array.isArray(consolidatedAccounts) && consolidatedAccounts.length > 0) {
			sessionStorage.setItem('consolidated_accounts', JSON.stringify(consolidatedAccounts));
			accountsToFetch = accountsToFetch.filter((ws) => consolidatedAccounts.includes(ws));
		}
		if (!accountsToFetch.length) {
			M.toast({ html: 'No accounts configured for consolidated view' });
			document.body.style.cursor = 'default';
			return;
		}

		let requests = accountsToFetch.map((user) =>
			axios.get(clientURL, {
				params: {
					viewid: user,
					client: client,
					userid: authedUser,
					cookie: cookie,
					host: window.location.host,
				},
			})
		);

		Promise.all(requests)
			.then((responses) => {
				// Combine the data from all accounts
				let consolidatedUserData = [];
				let consolidatedClientData = [];

				responses.forEach((response) => {
					if (response.data.length < 2) return;
					let accountData = response.data[0]['user'];
					AppState.clientData = response.data[1]['client'];
					consolidatedUserData.push(accountData);
				});

				function rollupUserData(userDataArray, filteredWorkspaces) {
					const activeWorkspaces = filteredWorkspaces || workspaces;
					const consolidated = {
						user_name: 'Consolidated View of ' + capitalize(decimalToWords(activeWorkspaces.length)) + '(' + activeWorkspaces.length + ') Accounts',
						friendly_name: sessionStorage.getItem('consolidatedViewer'),
						userid: 'consolidated.view',
						consolidatedUserIds: [...activeWorkspaces], // Array of included alias userids for filtering
						consolidatedTags: {}, // Tags keyed by userid for Asplundh FFM matching
						carlist: [client],
						clients: {
							[client]: {
								documents: [],
								shares: {},
								consolidatedTransactions: [], // Transactions with holder attribution
								vestingByHolder: {}, // Vesting data keyed by userid
								show_stats_headline: true,
								value24x: null, // Initialize for summing across accounts
							},
						},
					};

					const seenConsolidatedNrids = new Set();
					userDataArray.forEach((data) => {
						const dataClient = data.clients[client];
						if (!dataClient) return;

						const holderName = data.friendly_name || data.user_name || data.userid;

						// Store tags for this user (used by Asplundh FFM matching)
						if (dataClient.tags) {
							consolidated.consolidatedTags[data.userid] = {
								tags: dataClient.tags,
								holderName: holderName,
							};
						}

						// Check if dataClient.documents exists and is an array
						if (dataClient.documents && Array.isArray(dataClient.documents)) {
							// Add the holder property to each document before concatenating
							// Defensive check: filter out null/undefined docs before accessing userid
							const markedDocuments = dataClient.documents
								.filter((doc) => doc)
								.map((doc) => {
									return { ...doc, holder: data?.userid, holderName: holderName };
								});
							// Concatenate the marked documents
							consolidated.clients[client].documents = consolidated.clients[client].documents.concat(markedDocuments);
						}

						// Aggregate shares
						if (dataClient.shares) {
							Object.keys(dataClient.shares).forEach((shareClass) => {
								if (!consolidated.clients[client].shares[shareClass]) {
									consolidated.clients[client].shares[shareClass] = {
										approved: 0,
										committed: 0,
										owned: 0,
										events: [],
									};
								}

								const sharesClient = dataClient.shares[shareClass];
								consolidated.clients[client].shares[shareClass].approved += Number(sharesClient.approved || 0);
								consolidated.clients[client].shares[shareClass].committed += Number(sharesClient.committed || 0);
								consolidated.clients[client].shares[shareClass].owned += Number(sharesClient.owned || 0);

								if (sharesClient.events && Array.isArray(sharesClient.events)) {
									// Add holder attribution to events
									const markedEvents = sharesClient.events.map((e) => ({ ...e, holder: data?.userid, holderName: holderName }));
									consolidated.clients[client].shares[shareClass].events =
										consolidated.clients[client].shares[shareClass].events.concat(markedEvents);
								}

								// Consolidate vesting schedules by holder
								if (sharesClient.vesting) {
									if (!consolidated.clients[client].vestingByHolder[data.userid]) {
										consolidated.clients[client].vestingByHolder[data.userid] = {
											holderName: holderName,
											shareClasses: {},
										};
									}
									consolidated.clients[client].vestingByHolder[data.userid].shareClasses[shareClass] = sharesClient.vesting;

									// Aggregate vesting into consolidated shares so calculators can access it
									if (!consolidated.clients[client].shares[shareClass].vesting) {
										consolidated.clients[client].shares[shareClass].vesting = {};
									}
									Object.entries(sharesClient.vesting).forEach(([grantKey, grant]) => {
										// Prefix grant key with userid to avoid collisions across holders
										const consolidatedKey = data.userid + '::' + grantKey;
										consolidated.clients[client].shares[shareClass].vesting[consolidatedKey] = grant;
									});
								}
							});
						}

						// Aggregate value24x (sum across all accounts)
						if (dataClient.value24x) {
							const parsedValue = parseFloat(dataClient.value24x.replace(/[^.\d]/g, '')) || 0;
							if (consolidated.clients[client].value24x === null) {
								consolidated.clients[client].value24x = 0;
							}
							consolidated.clients[client].value24x += parsedValue;
						}

						// Consolidate transactions from clientData.equity for this user
						if (clientData.equity) {
							Object.keys(clientData.equity).forEach((shareClass) => {
								const equityClass = clientData.equity[shareClass];
								if (equityClass.transactions && Array.isArray(equityClass.transactions)) {
									equityClass.transactions.forEach((t) => {
										if (!t) return;
										const sender = t.sender?.toLowerCase();
										const receiver = t.receiver?.toLowerCase();
										const userId = data.userid?.toLowerCase();
										if (sender === userId || receiver === userId) {
											// Deduplicate: skip if already added by another consolidated user
											const nridKey = shareClass + ':' + t.nrid;
											if (seenConsolidatedNrids.has(nridKey)) return;
											seenConsolidatedNrids.add(nridKey);
											// Add holder attribution and shareClass to transaction
											consolidated.clients[client].consolidatedTransactions.push({
												...t,
												shareClass: shareClass,
												holder: data.userid,
												holderName: holderName,
											});
										}
									});
								}
							});
						}
					});

					// Convert value24x sum to string format
					if (consolidated.clients[client].value24x !== null) {
						consolidated.clients[client].value24x = String(consolidated.clients[client].value24x);
					}

					return consolidated;
				}

				// save shareholderData for revert before Voting, Trade, BuyBack, Share Transfer
				sessionStorage.setItem('shareholder.Data', sessionStorage.getItem('user.Data'));

				// needed for re-render of Vue component E&T tab data
				fireReadyToRender();

				// Perform the roll-up or merge of user and client data
				AppState.userData = rollupUserData(consolidatedUserData, accountsToFetch);

				// For EXTERNAL_DATA clients, load transactions for all consolidated userids
				if (EXTERNAL_DATA && userData.consolidatedUserIds?.length) {
					if (isAlera) {
						// Alera uses dynamic column positions from clientData.transactions_columns
						loadConsolidatedAleraTransactions(userData.consolidatedUserIds, () => {
							finishSetAlias();
						});
					} else {
						// Standard external data format (sender/receiver columns)
						loadConsolidatedExternalTransactions(userData.consolidatedUserIds, () => {
							finishSetAlias();
						});
					}
				} else {
					finishSetAlias();
				}
			})
			.catch((error) => {
				console.error('Error fetching account data:', error);
			});
	} else {
		axios
			.get(clientURL, {
				params: {
					viewid: selectedUser,
					client: client,
					userid: authedUser,
					cookie: cookie,
					host: window.location.host,
				},
			})
			.then(function (response) {
				if (response.data.length < 2) return;
				AppState.userData = response.data[0]['user'];
				// console.log("new alias:", userData);
				AppState.clientData = response.data[1]['client'];
				if (STD_EXT_DATA) getStandardTransactionsFromFile('userData', null, false, false, true, '');
				else if (isAlera) getAleraHoldingsFromFile('userData', null, false, false, true, '');
				else finishSetAlias();
			});
	}
}

function finishSetAlias() {
	document.body.style.cursor = 'default';
	let switchAccountSpinner = document.getElementById('switchAccountSpinner');
	if (switchAccountSpinner) switchAccountSpinner.classList.add('hidden');
	M.Modal.getInstance(document.getElementById('workspaceModal')).close();
	AppState.isAdmin = userData?.carlist?.includes('^' + client) || false;
	sessionStorage.setItem('user.Data', JSON.stringify(userData));
	// 2-26-2025 Why are we doing this???  It wipes out Transactions...
	// 9-15-2025 What?????  Without it, Observers see no Transactions!  (see Bohler email dated 9-15-2025)
	sessionStorage.setItem(client + '.Data', JSON.stringify(clientData));
	console.log('Client/User complete: ' + (Date.now() - refreshTime) + ' ms');
	setLabels('setAlias');
	refreshCapTable(false);
	// Reset calculator and time series results so they re-run with the new user/consolidated data
	window.lastCalculationResult = null;
	window.lastTimeSeriesResult = null;
	fireReadyToRender();

	if (window.analytics) {
		window.analytics.track('Switched User', {
			userEmail: authedUser,
			workspace: client,
			userName: userData.user_name,
		});
	}
}

function setLabels(from) {
	// console.trace();
	if (!clientData || typeof clientData !== 'object' || Array.isArray(clientData)) return;

	UFL = clientData.use_fund_language || isJMS;
	window.UFL = UFL;
	AppState.itsaFund = UFL || clientData.use_real_estate_language;
	labelShareholders = itsaFund ? 'Investors' : 'Shareholders';
	labelReportsTab = AppState.FUNDING ? 'Financials' : 'Reports';

	let flags = clientData.flags;
	if (!flags) flags = clientData.flags = {};

	if (flags) {
		AppState.HIDE_CTA = flags.hide_cta_button; // greendatacenters;
		AppState.BUYERS_ONLY = flags.buyers_only;
		SHOW_RETURNS = flags.show_returns; // isMH
		USE_TAX_BASIS = flags.use_tax_basis;
		SHOW_TAX_BASIS_COLUMN = flags.show_tax_basis_column; // Controls visibility of Tax Basis column
		SHOW_AGGREGATE = flags.show_aggregate; //isAES || isAND || isJMH || isACR || isORR || isMH;
		// moved up to here on 6/2/2023 for Pharmajet
		AppState.INCLUDE_ROC = flags.include_roc; //is3MG || isNMS || isACR || isGPG || client == "prodoscapital";
		SHOW_STATS = flags.show_stats_headline; //is1251 || isGDC;	// client-level flag
	}

	AppState.EXTERNAL_DATA = !isVMS && clientData.transactions_data;
	AppState.STD_EXT_DATA = AppState.EXTERNAL_DATA && !isAlera; // && !isASP;
	AppState.USE_TEMPLATES = clientData.templates && clientData.templates.length > 0;
	AppState.USE_CERTIFICATES = AppState.USE_TEMPLATES && clientData.templates.some((t) => t.includes('_Stock_Certificate'));

	AppState.formattingConfig = buildFormattingConfig(clientData?.decimalsConfig);

	let language = clientData.language;
	if (language) {
		labelROI = language.label_roi || labelROI;
		labelGain = language.label_gain || labelGain;
		labelTVPI = language.label_tvpi || labelTVPI;
		labelTeamTab = language.label_team_tab || labelTeamTab;
		labelHeadline = language.label_headline || labelHeadline;
		labelPaidIn = language.label_paidincapital || labelPaidIn;
		labelTaxBasis = language.label_tax_basis || labelTaxBasis;
		labelProspects = language.label_prospects || labelProspects;
		labelValuation = language.label_valuation || labelValuation;
		labelCostPrice = language.label_cost_price || labelCostPrice;
		labelEquityType = language.label_equitytype || labelEquityType;
		labelUncalled = language.label_uncalledbalance || labelUncalled;
		labelUpdatesTab = language.label_updates_tab || labelUpdatesTab;
		labelReportsTab = language.label_reports_tab || labelReportsTab;
		labelInvestments = language.label_investments || labelInvestments;
		labelDataRoomTab = language.label_data_room_tab || labelDataRoomTab;
		labelShareholders = language.label_shareholders || labelShareholders;
		labelPortfolioTab = language.label_portfolio_tab || labelPortfolioTab;
	}

	// =============================================================================
	// HEADLINE TEMPLATE MIGRATION REFERENCE
	// =============================================================================
	// These client-specific templates should be configured in the database at:
	// clientData.language.headline_template
	// clientData.language.headline_subtitle_template
	// clientData.language.class_headline_template
	// clientData.language.class_subtitle_template
	//
	// Template Syntax (Handlebars-style):
	//   Variables: {{variableName}}
	//   Conditionals: {{#if var}}content{{/if}} or {{#if var}}true{{else}}false{{/if}}
	//   Comparisons: {{#if tvpi > 1}}above 1x{{/if}}
	//   Defaults: {{var | default: "fallback"}}
	//
	// CLIENT CONFIGURATIONS:
	// -----------------------------------------------------------------------------
	// FAV (also set flags.default_value_method = 'tvpi_fund', flags.percentage_precision = 4)
	//   headline_template: "Your Portfolio Value: {{totalValue}}"
	//   headline_subtitle_template: "{{tvpi}} TVPI"
	//   class_headline_template: "{{className}}: {{classValue}}"
	//   class_subtitle_template: "{{#if classTvpi}}{{classTvpi}} TVPI{{/if}}"
	//
	// PHJ
	//   headline_template: "Your Total {{labelPaidIn}}: {{totalPaidInCapital}}"
	//
	// ACR
	//   headline_template: "Your Total {{labelPaidIn}}: {{totalPaidInCapital}}"
	//   headline_subtitle_template: "{{#if totalCash}}({{totalCash}} in cash){{/if}}"
	//
	// ORR
	//   headline_template: "Current Value: {{totalCurrentValue}}"
	//   class_headline_template: "Current Value of your {{symbol}}: {{classValue}}"
	//
	// NTH, PSS
	//   class_headline_template: "Value of your {{symbol}} {{accountType}}: {{classValue}}"
	//   class_subtitle_template: "{{#if currentClassValuationDate}}(as of {{currentClassValuationDate}}){{/if}}"
	//
	// BW (uses grant values parsed from vesting conditions)
	//   headline_template: "Your Current {{#if totalForecastedValue}}Vested {{/if}}Value as of {{valuationDate}}: {{vestedValue}}"
	//   headline_subtitle_template: "{{#if totalForecastedValue}}Forecasted Unvested Grant Value: {{totalForecastedValue}}{{/if}}"
	//   headline_tertiary_template: "{{#if totalOriginalGrantValue}}Original Grant Value: {{totalOriginalGrantValue}}{{/if}}"
	//   class_headline_template: "Your Current {{#if classUnvestedValue}}Vested {{/if}}Value: {{classVestedValue}}"
	//   class_subtitle_template: "{{#if classUnvestedValue}}Unvested Grant Value: {{classUnvestedValue}}{{/if}}"
	//   class_tertiary_template: "Total: {{classValue}}"
	// =============================================================================

	// TEMP TEST — hard code force critical campaigns Active for local/staging testing only.  Comment out after finished testing.
	if (env === 'dev' || env === 'staging') {
		if (isFish) {
			const _testCamp = 'May 2026 Stock Purchase Election \u2013 General Staff';
			clientData.active_campaign = _testCamp;
			clientData.currently_raising = true; // <-- Show Campaign button in Company Profile
			clientData.tabs.show_campaign = true; // <-- old (obsolete?) campaign tab, maybe irrelevant
			clientData.campaigns_NEW.surveying[_testCamp].status = 'Active';
		} else if (isUSV) {
			const _testCamp = '2026 QSST Election - Shareholder Consent';
			clientData.active_campaign = _testCamp;
			clientData.currently_raising = true; // <-- Show Campaign button in Company Profile
			clientData.tabs.show_campaign = true; // <-- old (obsolete?) campaign tab, maybe irrelevant
			clientData.campaigns_NEW.signatures[_testCamp].status = 'Active';
		}
	}

	// Only one at a time!
	AppState.SURVEYING =
		AppState.VIEWING =
		AppState.VOTING =
		AppState.PAYING =
		AppState.BUYSELL =
		AppState.SIGNING =
		AppState.FUNDING =
		AppState.REQUESTS =
			false;

	AppState.activePaymentsCampaign =
		Object.values(clientData.campaigns_NEW?.payments || {}).find((c) => c.status === 'Active') ||
		clientData.campaigns?.payments?.find((c) => c.status === 'Active') ||
		null;

	AppState.PAYING = AppState.activePaymentsCampaign ? true : false;

	AppState.activeRequestsCampaign =
		Object.values(clientData.campaigns_NEW?.requests || {}).find((c) => c.status === 'Active') ||
		clientData.campaigns?.requests?.find((c) => c.status === 'Active') ||
		null;

	AppState.REQUESTS = AppState.activeRequestsCampaign ? true : false;

	// FOR CAMPAIGNS
	let viewingCampaign = clientData.campaigns_NEW
		? Object.values(clientData.campaigns_NEW.viewing || {}).find((item) => item.status === 'Active')
		: clientData.campaigns?.viewing?.find((item) => item.status === 'Active');

	const _draftPreview = new URLSearchParams(window.location.search).get('previewCampaign');
	if (_draftPreview && (nthAdmin || isAdmin)) {
		AppState.campaignName = _draftPreview.replace(/_/g, ' ');
		AppState.previewMode = true;
	} else {
		AppState.campaignName = clientData.active_campaign || viewingCampaign?.name || null;
	}

	if (campaignName) {
		let campaignFlavor;
		if (clientData.campaigns_NEW) {
			// New JSON structure
			for (const [flavor, campaigns] of Object.entries(clientData.campaigns_NEW)) {
				const _resolvedKey = previewMode
					? Object.keys(campaigns).find((k) => k.toLowerCase() === AppState.campaignName.toLowerCase())
					: AppState.campaignName;
				if (_resolvedKey && campaigns[_resolvedKey]) {
					if (previewMode) AppState.campaignName = _resolvedKey;
					AppState.activeCampaign = campaigns[_resolvedKey];
					campaignFlavor = flavor;
					break;
				}
			}
		} else if (clientData.campaigns) {
			// Old JSON structure
			AppState.activeCampaign = Object.values(clientData.campaigns)
				.flat()
				.find((c) => c.name === AppState.campaignName);
			campaignFlavor = Object.keys(clientData.campaigns).find((flavor) => clientData.campaigns[flavor]?.includes(AppState.activeCampaign)) || null;
		}

		// Default values
		AppState.campaignClasses = AppState.activeCampaign?.classes || ['ALL'];
		if (AppState.campaignClasses.includes('ALL') && clientData?.equity) {
			AppState.campaignClasses = Object.keys(clientData.equity).sort();
			// Preserve the original client config for reference. Do not mutate.
			// if (AppState.activeCampaign?.classes?.includes?.('ALL')) AppState.activeCampaign.classes = AppState.campaignClasses;
		}
		AppState.campaignTags = AppState.activeCampaign?.tags;
		AppState.campaignDocuments = [AppState.campaignName];
		labelHeadingCTA = AppState.campaignName;

		switch (campaignFlavor) {
			case 'surveying':
				AppState.SURVEYING = true;
				labelButtonCTA = 'Complete Survey Now';
				labelButtonDone = 'Completeed - Thank you!';
				campaignHref = '/survey/';
				break;
			case 'voting':
				AppState.VOTING = true;
				labelButtonCTA = 'Vote Now';
				labelButtonDone = 'Voted - Thank you!';
				// campaignHref = '/proxy-voting/';  <-- passed in as activeCampaign.campaignHref
				break;
			case 'payments': // NEVER USED HERE, right?
				AppState.PAYING = true;
				labelButtonCTA = 'Pay Now';
				break;
			case 'buysell':
				AppState.BUYSELL = true;
				labelButtonCTA = 'Go to Buy Sell';
				break;
			case 'signatures':
				// ENROLL = true;
				AppState.SIGNING = true;
				labelButtonCTA = 'Sign Now';
				labelButtonDone = 'Signed - Thank you!';
				campaignHref = '/signature/'; // '/enroll/'
				break;
			case 'fundraising':
				AppState.FUNDING = true;
				labelButtonCTA = 'Invest Now';
				campaignHref = '/investor-agreement/';
				break;
			case 'viewing':
				AppState.VIEWING = true;
				break;
		}

		// Label overrides if the active campaign has any custom fields
		if (AppState.activeCampaign?.labelButton) labelButtonCTA = AppState.activeCampaign.labelButton; // was "Sign " + language.label_proxy_voting
		if (AppState.activeCampaign?.campaignHref) campaignHref = AppState.activeCampaign.campaignHref;
		if (AppState.activeCampaign?.labelBtnDone) labelButtonDone = AppState.activeCampaign.labelBtnDone;
		if (AppState.activeCampaign?.labelHeading) labelHeadingCTA = AppState.activeCampaign.labelHeading; // was language.label_new_offering
		if (AppState.activeCampaign?.documents?.length) AppState.campaignDocuments = AppState.activeCampaign.documents; // was language.name_proxy_form
	}

	if (userData) {
		let userTags = userData.clients[client]?.tags || [];
		const _userStatus = String(userData.clients[client]?.status || '');
		const _statusAllowed = !AppState.activeCampaign?.restrictStatus?.length || AppState.activeCampaign.restrictStatus.map(String).includes(_userStatus);
		AppState.SHOW_INVEST = (!AppState.activeCampaign?.restrictTag || userTags?.includes(AppState.activeCampaign.restrictTag)) && _statusAllowed;
		userData.user_name = nameWithOverride(userData.userid);
		SHOW_STATS = userData.clients[client]?.show_stats_headline ?? false; // user-level flag
	}

	function adjustHexColor(baseHex, percent) {
		// Parse r, g, b values
		let r = parseInt(baseHex.substring(0, 2), 16);
		let g = parseInt(baseHex.substring(2, 4), 16);
		let b = parseInt(baseHex.substring(4, 6), 16);

		// Calculate how much to lighten (positive percent) or darken (negative percent)
		if (percent > 0) {
			// Lighten by moving RGB values closer to 255 (white)
			r = Math.round(r + (255 - r) * (percent / 100));
			g = Math.round(g + (255 - g) * (percent / 100));
			b = Math.round(b + (255 - b) * (percent / 100));
		} else {
			// Darken by moving RGB values closer to 0 (black)
			r = Math.round(r * (1 + percent / 100));
			g = Math.round(g * (1 + percent / 100));
			b = Math.round(b * (1 + percent / 100));
		}

		// Return the resulting hex color
		return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
	}

	if (clientData.brand_color?.length > 2) {
		const brandColor = clientData.brand_color
			.replace(/^#/, '')
			.slice(0, 6)
			.replace(/^(.)(.)(.)$/, '$1$1$2$2$3$3')
			.padEnd(6, '0'); // hex color normalization
		nthColor[1] = '#' + brandColor;
		nthColor[0] = adjustHexColor(brandColor, -25);
		nthColor[2] = adjustHexColor(brandColor, 70);
		console.log('Brand colors: ' + nthColor.slice(0, 3));

		function isColorDark(hex) {
			const r = parseInt(hex.slice(0, 2), 16);
			const g = parseInt(hex.slice(2, 4), 16);
			const b = parseInt(hex.slice(4, 6), 16);
			// Using W3C formula: https://www.w3.org/TR/WCAG20/#relativeluminancedef
			const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
			return luminance < 0.5;
		}

		// Change .alert css to Marvin Yellow
		let stylesheets = document.styleSheets;
		for (let i = 0; i < stylesheets.length; i++) {
			let stylesheet = stylesheets[i];
			if (stylesheet.href && stylesheet.href.includes('main.css')) {
				for (let j = 0; j < stylesheet.cssRules.length; j++) {
					let rule = stylesheet.cssRules[j];
					if (rule.selectorText == '.alert') {
						// rule.style.color = adjustHexColor(brandColor, -70);
						rule.style.backgroundColor = '#' + brandColor;
						rule.style.color = isColorDark(brandColor) ? '#ffffff' : adjustHexColor(brandColor, -70);
					}
				}
			}
		}
	}

	if (!origClientDataAbout) origClientDataAbout = clientData.about;
}

function postVestingSchedules() {
	// doesn't work wtih external data source...
	if (EXTERNAL_DATA) return;
	if (!userData?.clients?.[client]) return;

	// ONLY WILL ENGAGE WHEN AN ADMIN HAS BROUGHT IN CAP TABLE DATA
	let ADJUST_CAP_TABLE = true; // isNTH || isStaging || isONW || isROK || isH5H || ["onwardpe","onwardre","dropps"].includes(client);
	let postCount = 0;
	let doneCount = 0;
	let emailBody = 'The following Cap Table adjustments have been made for Vesting:<ul>';
	let now = Math.floor(Date.now() / 1000);
	let vestingShares = {};
	if (getAuthType().includes('admin')) {
		if (!capTableData) capTableData = NthUtil.sessionStore.readCapTable(client);
		if (!capTableData) return;
		Object.keys(capTableData).forEach(function (user) {
			vestingShares = {};
			let userShares = capTableData[user].clients[client].shares;
			Object.keys(userShares).forEach(function (s) {
				if (userShares[s].vesting) {
					vestingShares[s] = userShares[s].vesting;
					postVesting(capTableData[user].userid, s, vestingShares[s]);
				}
			});
			// console.log(capTableData[user].userid, vestingShares);
		});
	} else {
		let userShares = userData.clients[client].shares || {};
		Object.keys(userShares).forEach(function (s) {
			if (userShares[s].vesting) {
				vestingShares[s] = userShares[s].vesting;
				postVesting(userData.userid, s, vestingShares[s]);
			}
		});
		// console.log(vestingShares);
	}

	function sendPostingNotice() {
		if (emailBody.length > 75) {
			const sendEmailParams = {
				userid: authedUser,
				client: client,
				cookie: cookie,
				sender_name: 'Nth Round', // (via " + authedUser + ")",
				recipient: 'support@nthround.com',
				subject: 'Vesting Posted on ' + client + '.nthround.io',
				message: 'Hi Nth Round Support--<br><br>' + emailBody,
			};

			// First, Send email to the shareholder
			axios
				.post('https://api.nthround.io/portal/auth/send-email', JSON.stringify(baseEmailParams))
				.then((sendEmailToShareholderResponse) => {
					console.log('Sent email to shareholder');

					// Next, send email to the site owner.
					if (clientData.flags?.forward_vesting_alert_to_site_owner === true) {
						axios
							.post(
								'https://api.nthround.io/portal/auth/send-email',
								JSON.stringify({
									...sendEmailParams,
									recipient: clientData.contact_email,
								})
							)
							.then((sendEmailToSiteOwnerResponse) => {
								console.log('Sent email to site owner');
							})
							.catch((sendEmailToSiteOwnerErr) => {
								console.log('Error sending email to site owner');
								console.error(sendEmailToSiteOwnerErr);
							});
					}
				})
				.catch((sendEmailToShareholderErr) => {
					console.log('Error sending email to shareholder');
					console.error(sendEmailToShareholderErr);
				});
		}
	}

	function postVesting(user, shareClass, vesting) {
		Object.keys(vesting).forEach(function (issue) {
			if (!vesting[issue] || !vesting[issue].schedule || !capTableData) return;
			Object.keys(vesting[issue].schedule).forEach(function (d) {
				if (Number(d) < now && !vesting[issue].schedule[d].posted) {
					postCount++;
					console.log('Post ' + d);
					let index = capTableData.findIndex((c) => c.userid == user);
					if (index < 0 || !capTableData[index]?.clients?.[client]?.shares) return;
					let holderShares = capTableData[index].clients[client].shares;
					holderShares[shareClass].vesting[issue].schedule[d]['posted'] = true;
					if (ADJUST_CAP_TABLE) {
						let owned = Number(holderShares[shareClass].owned);
						let approved = Number(holderShares[shareClass].approved);
						// Don't adjust accounts with zero balances.  They likely are departed folks.
						if (owned == 0 && approved == 0) return;
						let increase = Number(holderShares[shareClass].vesting[issue].schedule[d].amount);

						// Abandoned this reverse fund logic on 4/27/2023, for Maguire Hayden
						// Funds with vesting, like Maguire Hayden, flip left and right cap table balances.
						if (false) holderShares[shareClass].owned = (owned + increase).toString();
						else holderShares[shareClass].approved = (approved + increase).toString();
						let userName = capTableData.find((x) => user == x.userid).user_name;
						emailBody +=
							'<li>' +
							shareClass +
							' Approved balance for ' +
							userName +
							' (' +
							user +
							') increased by ' +
							increase +
							' (' +
							approved +
							' to ' +
							(approved + increase) +
							')</li>';
					}
					// console.log(holderShares);

					capTableData[index].clients[client].shares = holderShares;
					NthUtil.sessionStore.updateCapTable(client, capTableData);

					if (user == authedUser) {
						userData.clients[client].shares = holderShares;
						sessionStorage.setItem('user.Data', JSON.stringify(userData));
					}

					// Post updated values to the DB
					axios
						.post(
							postURL,
							JSON.stringify({
								eventName: 'index.vesting_posted',
								investor: user,
								userid: authedUser,
								client: client,
								cookie: cookie,
								clients: {
									[client]: {
										shares: holderShares,
									},
								},
							})
						)
						.then(function (res) {
							doneCount++;
							if (doneCount == postCount) {
								sendPostingNotice();
								refreshCapTable();
							}
							// console.log(res);

							if (window.analytics)
								window.analytics.track('Vesting Event Posted', {
									userEmail: user,
									workspace: client,
									userName: capTableData[index].user_name,
								});
						})
						.catch(function (err) {
							console.error(err);
						});
				}
			});
		});
	}
}

function showAvailableWorkspaces() {
	workspaces = [];
	// acctnames = [];	// Chris commented out on 6/30/2025
	let ownership = [];
	let friendlynames = [];

	// don't need JSON.parse here??
	// let currentUserData = JSON.parse(sessionStorage.getItem('user.Data'));
	// if (!currentUserData) {
	// 	console.log("NO user.Data storage for showAvailableWorkspaces.");
	// 	return;
	// }
	let currentUid = userData?.userid || authedUser || ''; // <-- Uncaught (in promise) TypeError: Cannot read property 'userid' of null
	// console.log(currentUid)

	// if already previewing, just update the modal with the Current indicator, and then BAIL OUT.
	let previewer = sessionStorage.getItem('previewer');
	const previewUserData = JSON.parse(sessionStorage.getItem('shareholder.Data') || 'null');
	const hasPreviewTarget = !!(previewUserData?.userid && previewUserData.userid !== authedUser);
	let canPreview = isSupv || isExec || getAuthType().includes('admin') || hasPreviewTarget;
	if (!canPreview) {
		sessionStorage.removeItem('previewer');
		sessionStorage.removeItem('preview');
		previewer = null;
	}
	let loadCount = Number(sessionStorage.getItem('loadCount'));
	let preview =
		(getAuthType().includes('admin') || previewer || hasPreviewTarget) &&
		(currentUid != authedUser || hasPreviewTarget) &&
		(loadCount == 0 || sessionStorage.getItem('preview') || hasPreviewTarget); // can't use isAdmin here!
	if (pn.includes('/admin') || pn.includes('/supervisor')) preview = false;
	if (!preview) sessionStorage.removeItem('previewer');
	sessionStorage.setItem('preview', preview);

	let workspacesList = document.getElementById('workspacesList');
	let workspaceItems = document.getElementsByClassName('workspace-list-item');

	if (preview && workspacesList && loadCount > 1) {
		sessionStorage.setItem('loadCount', loadCount + 1);

		let previewAlert = document.getElementById('previewAlert');
		if (previewAlert) {
			const uEmail = userData?.userid || authedUser || '';
			const uName = (userData?.user_name || uEmail).replace(/_/g, ' ');
			previewAlert.innerHTML = `<div class="alert" title="${DOMPurify.sanitize(uEmail)}" data-email="${DOMPurify.sanitize(uEmail)}"><span class="mr-4">Previewing: <strong>${DOMPurify.sanitize(uName)}</strong></span><a href="#" id="exitPreview">Exit Preview</a></div>`;
			instantiateExitListener();
		}
		Object.keys(workspaceItems).forEach((i) => workspaces.push(workspaceItems[i].getAttribute('data-id')));
		getAccountNames();
		return;
	}

	if (canPreview && (preview || preview == 'true') && preview != 'null' && preview != 'false') {
		let previewUserData = JSON.parse(sessionStorage.getItem('shareholder.Data'));
		if (previewUserData && previewUserData.clients[client]) {
			workspaces = JSON.parse(JSON.stringify(previewUserData.clients[client].alias || []));
			if (workspaces && !workspaces.includes(previewUserData.userid)) workspaces.unshift(previewUserData.userid);
		}
	} else {
		// USERS RELY ON ALIAS DATA, NOT CAP TABLE DATA
		if (!capTableData) capTableData = NthUtil.sessionStore.readCapTable(client);
		if (capTableData && isAdmin) {
			let authedData = capTableData.find((h) => h.userid == authedUser);
			if (authedData) {
				let authedClientData = authedData.clients[client];
				if (authedClientData.alias) workspaces = JSON.parse(JSON.stringify(authedClientData.alias)); // new object
			}
		} else {
			let aliasData = JSON.parse(sessionStorage.getItem('alias.Data'));
			if (aliasData) {
				workspaces = [];
				// Defensive check: filter out null/undefined before accessing userid
				aliasData.filter((a) => a?.userid).forEach((a) => workspaces.push(a.userid));
			} else if (userData?.clients?.[client]?.alias) {
				if (userData.userid != authedUser && !getAuthType().includes('admin')) {
					sendAlert(
						'Userid Mismatch for Workspaces (Aliases)?? (' + client + '.nthround.io)',
						'userData.userid = ' + userData.userid + '<br>authedUser = ' + authedUser
					);
					if (!canPreview) {
						signOut();
						return;
					}
				}
				workspaces = JSON.parse(JSON.stringify(userData.clients[client].alias));
			}
		}
		if (workspaces && !workspaces.includes(authedUser)) workspaces.unshift(authedUser);
	}

	lockDownWorkspaces(workspaces, preview);

	// this may be too confusing, and misleading
	// if (userData.userid != authedUser && !workspaces.includes(authedUser))
	// 	workspaces.unshift(authedUser);
	// console.log('Multiple workspace options', workspaces);

	if (
		(getAuthType().includes('admin') || previewer || hasPreviewTarget) &&
		(authedUser !== currentUid || hasPreviewTarget) &&
		preview != 'null' &&
		!pn.includes('admin')
	) {
		const bannerUser = hasPreviewTarget ? previewUserData : userData;
		const uEmail = bannerUser?.userid || userData?.userid || authedUser || '';
		const uName = (bannerUser?.user_name || userData?.user_name || uEmail).replace(/_/g, ' ');
		if (!document.getElementById('previewAlert')) {
			let banner = document.createElement('div');
			banner.className = 'fixed-wrap';
			banner.id = 'previewAlert';
			banner.innerHTML = `<div class="alert" title="${DOMPurify.sanitize(uEmail)}" data-email="${DOMPurify.sanitize(uEmail)}"><span class="mr-4">Previewing: <strong>${DOMPurify.sanitize(uName)} </strong></span><a href="#" id="exitPreview">Exit Preview</a></div>`;
			document.body.prepend(banner);
		} else {
			document.getElementById('previewAlert').innerHTML =
				`<div class="alert" title="${DOMPurify.sanitize(uEmail)}" data-email="${DOMPurify.sanitize(uEmail)}"><span class="mr-4">Previewing: <strong>${DOMPurify.sanitize(uName)} </strong></span><a href="#" id="exitPreview">Exit Preview</a></div>`;
		}
		instantiateExitListener();
	} else if (document.getElementById('previewAlert')) {
		document.getElementById('previewAlert').remove();
	}

	function instantiateExitListener() {
		const exitPreview = document.getElementById('exitPreview');
		if (exitPreview)
			exitPreview.addEventListener('click', function (e) {
				// fireReadyToRender();
				sessionStorage.removeItem('hasAliases');
				sessionStorage.removeItem('shareholder.Data');
				sessionStorage.removeItem('preview');
				let previewer = sessionStorage.getItem('previewer');
				if (previewer) {
					sessionStorage.removeItem('previewer');
					if (previewer == authedUser) window.location.replace('/supervisor/');
					else {
						console.log('***Speed Test - BEFORE axios.get() : ' + (Date.now() - refreshTime) + ' ms');
						axios
							.get(clientURL, {
								params: {
									viewid: previewer,
									client: client,
									userid: authedUser,
									cookie: cookie,
									host: window.location.host,
								},
							})
							.then(function (response) {
								console.log('***Speed Test - AFTER axios.get() : ' + (Date.now() - refreshTime) + ' ms');
								AppState.userData = response.data[0]['user'];
								if (userData) {
									let path = 'supervisor';
									if (!userData.carlist) userData.carlist = [];
									if (STD_EXT_DATA) getStandardTransactionsFromFile('userData', null, true, false, false, path);
									else if (isAlera) getAleraHoldingsFromFile('userData', null, true, false, false, path);
									else {
										if (blurNames) userData.user_name = capTableData.find((u) => u.userid == previewer).user_name;
										sessionStorage.setItem('user.Data', JSON.stringify(userData));
										window.location.replace(/supervisor/);
									}
								}
								if (window.analytics) {
									window.analytics.track('Resstored Previewer', {
										userEmail: authedUser,
										workspace: client,
										uid: previewer,
									});
								}
							})
							.catch(function (err) {
								showToast('error-toast', 'Hmm. Something went wrong with restorePreviewer.');
								console.error(err);
							});
					}
				} else if (getAuthType().includes('admin')) {
					if (isSupv) {
						AppState.capTableData = null;
						sessionStorage.removeItem(client + '.CapTable');
					}
					isSupv = isExec = false;
					window.location.replace('/admin/users/');
				} else {
					location.reload();
				}
			});
	}

	document.addEventListener('contextmenu', function (e) {
		let email = e.target.dataset.email || e.target.closest('.alert')?.dataset.email;
		if (email) {
			e.preventDefault();
			const copyContent = async () => {
				try {
					await navigator.clipboard.writeText(email);
					showToast('success-toast', email + ' copied to clipboard');
				} catch (err) {
					// for Safari
					let copyEmailModal = document.getElementById('copyEmailModal');
					let emailAddress = document.getElementById('emailAddress');
					M.Modal.getInstance(copyEmailModal).open();
					emailAddress.value = email;
					M.textareaAutoResize(emailAddress);
					M.updateTextFields();
					emailAddress.select();
					emailAddress.setSelectionRange(0, 99999); // For mobile devices
					document.getElementById('copyEmail').addEventListener('click', function (e) {
						emailAddress.select();
						emailAddress.setSelectionRange(0, 99999); // For mobile devices
						document.execCommand('copy');
						M.Modal.getInstance(copyEmailModal).close();
					});
				}
			};
			copyContent();
		}
	});

	if (!workspaces || workspaces.length == 1) {
		// This user is not an observer for anyone so hide the account selection trigger
		// Code seems to run too early, before menu is rendered
		let x = document.getElementsByClassName('workspaceModalMenu');
		for (let i = 0; i < x.length; i++) {
			x[i].style.display = 'none';
		}
	}

	getAccountNames();

	function getAccountNames() {
		// array parallel to workspaces
		// acctnames = [];	// Chris commented out on 6/30/2025
		let aliasData;
		if (workspaces && workspaces.length > 1) {
			// USERS RELY ON ALIAS DATA, NOT CAP TABLE DATA
			if (!capTableData) capTableData = NthUtil.sessionStore.readCapTable(client);
			if (capTableData && isAdmin) {
				loadAccountNames();
			} else {
				if (!aliasData) aliasData = JSON.parse(sessionStorage.getItem('alias.Data'));
				if (aliasData) {
					loadAccountNames();
				} else {
					const uid = authedUser;
					const cl = client;
					const tok = cookie;
					const ws = Array.isArray(workspaces) ? workspaces.slice(0) : [];
					axios
						.post(
							'https://api.nthround.io/portal/user/alias-names',
							JSON.stringify({
								eventName: 'index.observer_workspaces',
								alias_list: ws,
								shareholder: userData.userid,
								client: cl,
								userid: uid,
								cookie: tok,
							})
						)
						.then(function (res) {
							aliasData = res.data;

							if (aliasData && aliasData.action === 'SIGN_OUT') {
								sendAlert(
									'Workspace Mismatch (' + cl + ')',
									'eventName=' + (aliasData.eventName || '') + '<br>' + 'reason=' + (aliasData.reason || '')
								);
								signOut();
								return;
							}

							if (!Array.isArray(aliasData)) {
								console.warn('alias-names unexpected response', aliasData);
								sendAlert(
									'alias-names unexpected response (' + cl + ')',
									'eventName=index.observer_workspaces<br>' +
										'type=' +
										typeof aliasData +
										'<br>' +
										'keys=' +
										(aliasData && typeof aliasData === 'object' ? Object.keys(aliasData).slice(0, 20).join(',') : '')
								);
								signOut();
								return;
							}

							sessionStorage.setItem('alias.Data', JSON.stringify(aliasData));
							fireReadyToRender();
							loadAccountNames();
						})
						.catch(function (error) {
							console.error(error);
							sendAlert(
								'alias-names failed (' + cl + ')',
								'eventName=index.observer_workspaces<br>' +
									'status=' +
									(error?.response?.status || '') +
									'<br>' +
									'message=' +
									(error?.message || '')
							);
							signOut();
						});
				}
			}

			function loadAccountNames() {
				acctnames = [];
				let counter = 1;
				let filterWorkspaces = [];
				workspaces.forEach(function (ws) {
					// USERS RELY ON ALIAS DATA, NOT CAP TABLE DATA
					let holder =
						capTableData && isAdmin
							? capTableData.find((holder) => holder.userid == ws)
							: aliasData
								? aliasData.find((alias) => alias.userid == ws)
								: null;
					if (holder) {
						filterWorkspaces.push(ws);
						// can't use nameWithOverride function here because users/trustees do not have cap table data
						holder.user_name = holder.clients?.[client]?.override_name ? holder.clients[client].override_name : holder.user_name;
						if (acctnames.includes(holder.user_name)) {
							acctnames.push(holder.user_name.replace(/_/g, ' ') + '~' + counter);
							counter++;
						} else {
							acctnames.push(holder.user_name.replace(/_/g, ' '));
						}
					}
				});
				workspaces = filterWorkspaces;

				let accountName = document.getElementById('accountName');
				if (accountName && acctnames.length > 1) {
					let sameName = false;
					let firstNames = [];
					acctnames.forEach(function (name) {
						let firstName = name.split(' ')[0].replace(/_/g, ' ').replace(/,$/, '');
						if (firstNames.includes(firstName)) {
							sameName = true;
							return;
						} else {
							firstNames.push(firstName);
						}
					});
					if (sameName || currentUid.includes('+') || workspaces.length > 1) {
						accountName.classList.remove('hidden');
						let dispAcctName = userData.user_name.replace(/_/g, ' ');
						if (dispAcctName.startsWith('Consolidated View '))
							dispAcctName = '<span style="color: #299AA4; font-weight: 700;">' + dispAcctName + '</span>';
						accountName.innerHTML =
							'Account: ' +
							dispAcctName +
							"&ensp;&ensp;<a id='workSpaceModalTrigger' href='#workspaceModal' class='modal-trigger'>Switch Account</a>";
					} else {
						accountName.classList.add('hidden');
						accountName.innerHTML = '';
					}
				}

				const indices = Array.from(acctnames.keys());
				indices.sort((a, b) => acctnames[a].localeCompare(acctnames[b]));
				acctnames = indices.map((i) => acctnames[i]);
				workspaces = indices.map((i) => workspaces[i]);

				// Ensure the workspace associated with userData.userid is at the top
				// 4/27/2025 Stopped doing this.  It's confusing and unexpected.
				// const userIndex = workspaces.indexOf(userData.userid);
				// if (userIndex > 0) {
				// 	// Move userData.userid to the top of both acctnames and workspaces
				// 	const userAcctName = acctnames.splice(userIndex, 1)[0]; // Remove and capture it
				// 	const userWorkspace = workspaces.splice(userIndex, 1)[0]; // Remove and capture it
				// 	acctnames.unshift(userAcctName); // Add it to the top
				// 	workspaces.unshift(userWorkspace); // Add it to the top
				// }

				workspaces.forEach(function (ws) {
					// USERS RELY ON ALIAS DATA, NOT CAP TABLE DATA
					let thisUser =
						capTableData && isAdmin
							? capTableData.find((holder) => holder.userid == ws)
							: aliasData
								? aliasData.find((alias) => alias.userid == ws)
								: null;
					if (thisUser) {
						let userShares = {};
						// LAZY LOAD OF CAP TABLE BROKE OWNERSHIP/TRUSTEE.
						// Fixed by extending axios.get('https://api.nthround.io/portal/user/alias-names' to include alias.shares data
						if (thisUser.clients) userShares = thisUser.clients[client].shares;
						else if (thisUser.shares) userShares = thisUser.shares;
						// 3/20/2024 - for Etnyre and others, consider any class for ownership
						// ownership[ws] = Number(userShares["C"] ? userShares["C"].owned : userShares["ONW"] ? userShares["ONW"].owned : "0");
						if (true) {
							//isPHJ) {
							ownership[ws] = 0;
							classes.forEach((c) => (ownership[ws] += Number(userShares[c] ? userShares[c].owned : '0')));
						}
						if (thisUser.friendly_name) friendlynames[ws] = thisUser.friendly_name.replace(/_/g, ' ');
					}
				});

				function createWorkspaceListHTML(holderUid) {
					return workspaces
						.map((workspace, index) => {
							const accountName = acctnames[index].split('~')[0];
							const isOwner = holderUid == workspace;
							const isTrustee = holderUid == workspace.replace(/\+[^@]*?(?=@)/, '') && ownership[workspace] > 0;
							const isCurrent = currentUid == workspace;
							const friendlyName = friendlynames[workspace] ? friendlynames[workspace] : workspace;

							return `
							<div class="workspace-list-item flex justify-between items-center text-secondary" data-id="${workspace}">
								<div class="flex items-center" style="max-width: ${isASP ? '550px' : '430px'};">
									<span class="mr-8">${accountName}
										<p class="mt-0 mb-0 text-xs">
											${isOwner ? `<span class="pill pill--owner">Owner</span>&ensp;` : isTrustee ? `<span class="pill pill--owner">Trustee</span>&ensp;` : ``}
											${friendlyName}
										</p>
									</span>
									${isCurrent ? `<div class="pill pill--current">Current</div>` : ``}
								</div>
								<svg class="w-4 h-4 text-secondary" style="flex-shrink:0;" viewBox="0 0 10 9">
									<g stroke="currentColor" stroke-width="1.5" fill="none" fill-rule="evenodd" stroke-linecap="round" stroke-linejoin="round">
										<path d="M6.108 8.25l3.16-3.457-3.16-3.391M8.87 4.826H4.124"></path>
										<path d="M6.108 8.25l3.16-3.457-3.16-3.391M1.151 4.826h-.4M8.87 4.826H4.124"></path>
									</g>
								</svg>
							</div>
						`;
						})
						.join('');
				}

				function createWorkspaceModalHTML(workspacesHTML, consolidatedViewer) {
					let ca = userData?.clients?.[client]?.consolidated_accounts;
					if (!Array.isArray(ca) || !ca.length) {
						try {
							ca = JSON.parse(sessionStorage.getItem('consolidated_accounts'));
						} catch (_) {}
					}
					const consolidatedLinkText =
						Array.isArray(ca) && ca.length > 0 && ca.length < workspaces.length
							? 'Consolidated View (' + ca.length + ' of ' + workspaces.length + ' accounts)'
							: 'Consolidated View of All Accounts';
					return `
						<div id="workspaceModal" class="modal ${isASP ? 'modal-md' : 'modal-sm'}">
							<div class="modal-content">
								<h2 class="text-2xl font-bold mt-0 mb-1">Where would you like to go?</h2>
								<p class="text-secondary text-sm mt-0 mb-4">
									(You have been granted access to ${workspaces.length > 5 ? workspaces.length + ' separate' : 'multiple'} accounts.)
								</p>
								<div id="workspacesList">${workspacesHTML}</div>
								${
									consolidatedViewer
										? `
									<div class="pt-1" style="margin: 0px auto; text-align: center;">
										<a id="viewAllAccounts" href="#" class="link-accent" data-id="ALL">${consolidatedLinkText}</a>
									</div>
									<div>
										<svg id="switchAccountSpinner" class="spinner hidden" viewBox="0 0 50 50" style="position: absolute; bottom: 100px; left: 50%; width: 75px; height: 75px;">
											<circle class="path" cx="25" cy="25" r="20" fill="none" stroke-width="3"></circle>
										</svg>
									</div>
								`
										: ``
								}
							</div>
						</div>
					`;
				}

				function renderWorkspaces() {
					let shData = JSON.parse(sessionStorage.getItem('shareholder.Data'));
					let holderUid = shData ? shData.userid : getCookie('nth.TestUser') ? getCookie('nth.TestUser') : authedUser;
					let workspaceModalElement = document.getElementById('workspaceModal');
					let authedElem = document.getElementById('authed');
					if (!workspaceModalElement && authedElem) {
						workspaceModal = document.createElement('div');
						authedElem.appendChild(workspaceModal);
						let consolidatedViewer = userData.clients[client].consolidated_viewer || sessionStorage.getItem('consolidatedViewer');
						if (consolidatedViewer) sessionStorage.setItem('consolidatedViewer', consolidatedViewer);
						else sessionStorage.removeItem('consolidatedViewer');
						let workspacesHTML = createWorkspaceListHTML(holderUid);
						workspaceModal.innerHTML = createWorkspaceModalHTML(workspacesHTML, consolidatedViewer);
					} else document.getElementById('workspacesList').innerHTML = createWorkspaceListHTML(holderUid);
					// Push a new state to the history with an identifier for the modal
					history.pushState({ modal: 'switchAccount' }, null, '');
				}

				let workspaceModal;
				if (pn == '/' || pn == '') renderWorkspaces();

				let viewAllAccounts = document.getElementById('viewAllAccounts');
				if (viewAllAccounts) viewAllAccounts.addEventListener('click', setAlias);
				document.querySelectorAll('.workspace-list-item').forEach((elem) => {
					elem.addEventListener('click', setAlias, false);
				});

				let isNth = currentUid.includes('nthround.com');
				let isOwner = clientData?.contact_email?.toLowerCase() == currentUid;
				if (loadCount == 1 && getAuthType().includes('admin')) loadCount = 2; // handle admin scenario
				if (workspaces.length > 1 && loadCount < 2 && !isOwner && !(isNth && isAdmin)) {
					//!isAdmin) {
					setTimeout(function () {
						sessionStorage.setItem('loadCount', loadCount + 1);
						if (userData?.clients?.[client]?.consolidated_viewer) {
							//userData.userid == "cldmag1@yahoo.com") {
							setAlias(null, 'ALL');
						} else {
							let workspaceDiv = document.getElementById('workspaceModal');
							if (workspaceDiv) {
								let workspaceModal = M.Modal.getInstance(workspaceDiv);
								if (workspaceModal) workspaceModal.open();
								sessionStorage.setItem('hasAliases', true);
							}
						}
					}, 50);
				}

				// not sure why we need to do this again...  but we do.
				let modalOptions = { onOpenStart: initModal };
				let modalElems = document.querySelectorAll('.modal');
				if (typeof M !== 'undefined' && M != null) {
					M.Modal.init(modalElems, modalOptions);
					ensureTwoFactorModalNonDismissible();
					// Allow workspace modal to be dismissed, but ensure body overflow is restored
					let workspaceModalEl = document.getElementById('workspaceModal');
					if (workspaceModalEl && M.Modal.getInstance(workspaceModalEl)) {
						M.Modal.getInstance(workspaceModalEl).options.onCloseEnd = function () {
							document.body.style.overflow = '';
						};
					}
				}
			}
		}
	}

	return { workspaces, acctnames };
}

function lockDownWorkspaces(workspaces, preview) {
	if (pn.startsWith('/admin')) return;

	let ownerData = JSON.parse(sessionStorage.getItem('shareholder.Data'));
	let alias = null;
	let aliasOwner = null; // for logging

	// --- 1) PREVIEW MODE: alias must belong to the PREVIEW SUBJECT (stable)
	if (preview) {
		alias = ownerData?.clients?.[client]?.alias || null;
		aliasOwner = ownerData?.userid || 'shareholder.Data';
	}

	// --- 2) NORMAL MODE: alias must belong to the AUTHED USER (stable)
	if (!alias) {
		if (ownerData?.userid === authedUser) {
			alias = ownerData?.clients?.[client]?.alias || null;
			aliasOwner = 'shareholder.Data';
		} else if (userData?.userid === authedUser) {
			alias = userData?.clients?.[client]?.alias || null;
			aliasOwner = 'userData';
		} else {
			let authed = Array.isArray(capTableData) ? capTableData.find((u) => u.userid === authedUser) : null;
			alias = authed?.clients?.[client]?.alias || null;
			aliasOwner = 'capTableData';
		}
	}

	// --- 3) Build expected set
	let expectedSet = new Set();
	if (Array.isArray(alias)) {
		alias.forEach((a) => {
			if (typeof a !== 'string') return;
			a = a.trim().toLowerCase();
			if (a) expectedSet.add(a);
		});
	}
	// always allow self for whichever identity is the anchor for this mode:
	const anchorUid = preview ? ownerData?.userid || '' : authedUser || '';
	if (anchorUid) expectedSet.add(anchorUid.trim().toLowerCase());

	const expected = expectedSet.size;
	const actual = Array.isArray(workspaces) ? workspaces.length : 0;

	// If we have no alias source, only allow tiny workspace lists.
	// (Otherwise “missing alias” can accidentally look OK.)
	const hasAlias = expected > 0;
	let ok = hasAlias ? actual - expected <= 2 : actual <= 2;

	if (!ok) {
		console.warn('Workspace mismatch:', {
			client,
			preview,
			aliasOwner, // where alias came from: shareholder.Data / userData / capTableData
			anchorUid, // the identity whose alias rules we enforced
			authedUser,
			userDataUser: userData?.userid,
			shareholderDataUser: ownerData?.userid,
			expectedAliasCount: expected,
			actualWorkspaceCount: actual,
			firstUnexpected: (() => {
				if (!Array.isArray(workspaces)) return null;
				let bad = workspaces.find((w) => !expectedSet.has((w || '').toLowerCase()));
				return bad || null;
			})(),
			aliasSample: Array.isArray(alias) ? alias.slice(0, 8) : [],
			workspacesSample: (workspaces || []).slice(0, 8),
		});
	}

	const capSize = Array.isArray(capTableData) ? capTableData.length : 0;
	const hardLimit = Math.max(30, Math.floor(capSize / 7));

	const NTH_TESTING = true;

	if (!ok || actual > hardLimit) {
		if (NTH_TESTING) {
			if (nthAdmin)
				alert(
					'Workspace Mismatch (' +
						client +
						')\n' +
						'authedUser=' +
						authedUser +
						'\nuserData.userid=' +
						(userData?.userid || '') +
						'\nexpected(alias)=' +
						expected +
						'\nactual(workspaces)=' +
						actual +
						'\nhardLimit=' +
						hardLimit +
						'\ncapTableSize=' +
						capSize
				);
		} else {
			sendAlert(
				'Workspace Mismatch (' + client + ')',
				'authedUser=' +
					authedUser +
					'<br>userData.userid=' +
					(userData?.userid || '') +
					'<br>expected(alias)=' +
					expected +
					'<br>actual(workspaces)=' +
					actual +
					'<br>hardLimit=' +
					hardLimit +
					'<br>capTableSize=' +
					capSize
			);
			signOut();
		}
	}

	return ok;
}

function readSessionStorage() {
	SessionStorageService.readSessionStorage();
}
function refreshSessionStorage(generate = true) {
	SessionStorageService.refreshSessionStorage(generate);
}
function refreshCapTable(generate = true) {
	SessionStorageService.refreshCapTable(generate);
}
function getShareholderData(shareholder, refresh) {
	refreshTime = Date.now();

	// USERS RELY ON LOADING FRESH DATA, NOT CAP TABLE DATA

	// let's try preloading...
	let fastLoad = false;
	AppState.shareholderData = capTableData.find((h) => h.userid == shareholder);
	if (shareholderData) {
		fastLoad = true;
		let observers = [];
		capTableData.forEach((h) => {
			if (h.clients[client].alias && h.clients[client].alias.includes(shareholder)) observers.push(h.userid);
		});
		shareholderData.observers = observers;
		sessionStorage.setItem('shareholder.Data', JSON.stringify(shareholderData));
		document.dispatchEvent(new CustomEvent('ShareholderComplete', { detail: { refreshed: false } }));
	}

	if (shareholderData && shareholderData.userid == shareholder && !refresh && !NO_DOCS) {
		console.log('Same shareholder complete: ' + (Date.now() - refreshTime) + ' ms');
		document.dispatchEvent(new CustomEvent('ShareholderComplete', { detail: { refreshed: true } }));
		// } else if (shareholderData && shareholderData.userid && document.referrer.includes("/admin/users")) {
		// 	console.log("Referrer shareholder complete: " + (Date.now() - refreshTime) + " ms");
		// 	document.dispatchEvent(new Event('ShareholderComplete'));
		//document.dispatchEvent(new Event('StorageComplete'));
	} else {
		// Asplundh is an exception: we rely on userData for cap table balance, and cross-check to sync with Ledger Transactions (external data)
		if (EXTERNAL_DATA && !isASP) enableUserEditControls(false);

		// Return user data by email or ethereum address
		console.log('***Speed Test - BEFORE axios.get() : ' + (Date.now() - refreshTime) + ' ms');
		axios
			.get(shareholderURL, {
				params: {
					shareholders: [shareholder],
					userid: authedUser,
					client: client,
					cookie: cookie,
				},
				paramsSerializer: function (params) {
					return qs.stringify(params, { arrayFormat: 'repeat' });
				},
			})
			.then(function (resp) {
				console.log('***Speed Test - AFTER axios.get() : ' + (Date.now() - refreshTime) + ' ms');
				// Filter to return only data for current client
				if (!resp.data[0]) {
					autoPopulateUser(shareholder);
					// alert("Sorry, no " + client + " record was found for this shareholder.  Please try refreshing your browser.  If this continues, please contact Nth Round support.");
					window.location.replace('/admin/users/');
					return;
				}
				if (resp.data[0]['Item']) {
					AppState.shareholderData = resp.data[0]['Item'];
					shareholderData.carlist = resp.data[1];
					shareholderData.observers = resp.data[2] || [];
					shareholderData.rooms = resp.data[3] || [];
					shareholderData.authenticator = resp.data[4] || '';
					if (STD_EXT_DATA) getStandardTransactionsFromFile('shareholderData', null, false, fastLoad, false, '');
					else if (isAlera) getAleraHoldingsFromFile('shareholderData', null, false, fastLoad, false, '');
					else {
						sessionStorage.setItem('shareholder.Data', JSON.stringify(shareholderData));
						console.log('Shareholder storage complete: ' + (Date.now() - refreshTime) + ' ms');
						if (fastLoad) document.dispatchEvent(new Event('CarsAndObserversComplete'));
						else document.dispatchEvent(new CustomEvent('ShareholderComplete', { detail: { refreshed: true } }));
						//if (refresh) renderShareholderView(shareholder);

						// potentially need new cap table url from ClientConfig (e.g., just created a new user and then GENERATED a *new* cap table image)
						refreshSessionStorage(false); //refreshCapTable(false);
					}
				}
			})
			.catch(function (resp) {
				console.error('[Shareholder] Fetch failed:', resp?.message || resp);
			});
	}
}

async function getAleraHoldingsFromFile(caller, res, redirect, fastLoad, newAlias, path) {
	// If an admin cancels ONE confirmation, we cancel ALL remaining updates this run
	let CANCEL_ALL_ALERA_UPDATES = false;
	let aleraUpdateQueue = Promise.resolve();

	// Convert Excel column letters to numbers (A=1, B=2, ..., Z=26)
	const excelColumnToIndex = (letter) => letter.charCodeAt(0) - 65;

	// Dynamically declare constants in the global scope
	for (const [letter, key] of Object.entries(clientData.transactions_columns)) {
		const constName = `${key.toUpperCase()}_COL`;
		globalThis[constName] = excelColumnToIndex(letter);
	}
	if (!AppState.LIVE_TRADING) AppState.LIVE_TRADING = clientData.trading_platform;
	// let customApproved = clientData.campaigns_NEW.buysell["2024 AMP Activity"].custom_approved;	// activeCampaign?.custom_approved || {};
	let customApproved = (() => {
		const campaigns = clientData.campaigns_NEW?.buysell || {};
		const validCampaigns = Object.entries(campaigns)
			.filter(([name, campaign]) => campaign.custom_approved)
			.sort(([nameA], [nameB]) => nameB.localeCompare(nameA)); // Sort by name descending
		return validCampaigns.length ? validCampaigns[0][1].custom_approved : {};
	})();

	let MAX_BUY = Number(activeCampaign?.maxBuy) || 1e10; // **** MUST MATCH TRADE.JS LINE 68 ****

	let stockRecordFile = await getContentUrl(clientData.transactions_data); // "1749656187416_Alera_Cap_Table.csv";
	fetch(stockRecordFile)
		.then(function (response) {
			// console.log(response);
			return response.text();
		})
		.then(function (csv) {
			let userRows;
			let array = cleanCSV(csv);
			let somethingChanged = false;

			// Build a map: userid/email -> all matching transaction rows (ONE PASS, big speedup)
			const transactionsByUser = array.reduce((acc, t) => {
				const email = (t[EMAIL_COL] || '').toLowerCase().trim();
				if (!email) return acc;
				(acc[email] ||= []).push(t);
				return acc;
			}, {});

			switch (caller) {
				case 'userData':
					userRows = transactionsByUser[(userData.userid || '').toLowerCase().trim()] || [];
					userData.clients[client].shares = newSharesObject(userData, true, userRows);
					sessionStorage.setItem('user.Data', JSON.stringify(userData));
					if (!pn.startsWith('/admin')) document.dispatchEvent(new Event('ExtDataComplete'));
					if (redirect) {
						sessionStorage.setItem('shareholder.Data', JSON.stringify(userData));
						window.location.replace('/' + path);
					} else if (newAlias) finishSetAlias();
					break;
				case 'shareholderData':
					// to prevent double-counting, clear any existing transactions
					classes.forEach((c) => {
						if (clientData.equity[c]) clientData.equity[c].transactions = [];
					});
					userRows = transactionsByUser[(shareholderData.userid || '').toLowerCase().trim()] || [];
					shareholderData.clients[client].shares = newSharesObject(shareholderData, true, userRows);
					sessionStorage.setItem('shareholder.Data', JSON.stringify(shareholderData));
					enableUserEditControls(true);
					console.log('Shareholder storage complete: ' + (Date.now() - refreshTime) + ' ms');
					if (fastLoad) document.dispatchEvent(new Event('CarsAndObserversComplete'));
					else document.dispatchEvent(new CustomEvent('ShareholderComplete', { detail: { refreshed: true } }));
					// refreshCapTable();
					break;
				case 'capTable':
					// Status now comes from each User record (clients[client].status), not from transactions.
					const supvStatus = (userData.clients[client].status || '').toLowerCase().trim();

					let userStatusMap;
					if (isSupv && !isExec) {
						// Build a map: userid -> status (from user records)
						userStatusMap = (res?.data || []).reduce((map, row) => {
							const it = row?.Item;
							const uid = (it?.userid || '').toLowerCase().trim();
							const s = (it?.clients?.[client]?.status || '').toLowerCase().trim();
							if (uid) map[uid] = s;
							return map;
						}, {});
						// Ensure current supervisor has a status in the map (fallback)
						if (!userStatusMap[userData.userid]) userStatusMap[userData.userid] = supvStatus;
					} else if (!getAuthType().includes('admin') && !isExec) {
						document.dispatchEvent(new Event('ExtDataComplete'));
						document.dispatchEvent(new Event('CapTableReady'));
						return;
					}

					AppState.capTableData = [];

					res.data.forEach(function (elem) {
						if (!elem?.Item?.clients) return;

						let thisUserData = elem.Item;
						let userClient = thisUserData.clients[client];

						// AUTOPOPULATE any userlisted user that is missing client data
						if (!userClient || !userClient.shares) {
							thisUserData = autoPopulateUser(thisUserData.userid);
							elem.Item = thisUserData;
							userClient = thisUserData.clients[client];
						}

						// Supervisor filter: compare user status (from user record) to supervisor's status
						if (isSupv && !isExec) {
							const uStatus = (userStatusMap[thisUserData.userid] || '').toLowerCase().trim();
							if (uStatus !== supvStatus) return;
						}

						// only push populated users
						if (!userClient?.shares) {
							console.error('[CapTable] User missing shares data:', elem?.userid);
						} else {
							let tagsAll = userClient.tags || [];
							let transAll = transactionsByUser[thisUserData.userid] || [];

							// Enrich tags + idNumber from transactions (but NOT status)
							transAll.forEach((t) => {
								const firm = t[FIRM_COL];
								const aff = t[AFFILIATE_COL];
								const eeid = t[EEID_COL];

								if (firm && firm.toUpperCase() !== 'N/A' && !tagsAll.includes(firm)) tagsAll.push(firm);
								if (aff && aff.toUpperCase() !== 'N/A' && !tagsAll.includes(aff)) tagsAll.push(aff);

								const eeidNum = Number(String(eeid || '').replace(/[^.\d]/g, ''));
								if (eeidNum) elem.Item.clients[client].idNumber = String(eeid).replace(/[^\d]/g, '');
							});

							elem.Item.clients[client].tags = tagsAll;
							userRows = transactionsByUser[(thisUserData.userid || '').toLowerCase().trim()] || [];
							elem.Item.clients[client].shares = newSharesObject(thisUserData, false, userRows);

							capTableData.push(elem.Item);
						}
					});

					AppState.capTableData = removeNthAdmins(capTableData);
					if (blurNames) {
						let blurNamesArray = blurNames.split('\r\n');
						for (let i = 0; i < capTableData.length; i++) capTableData[i].user_name = blurNamesArray[i];
					}
					NthUtil.sessionStore.updateCapTable(client, capTableData);
					console.log('CapTable complete: ' + (Date.now() - refreshTime) + ' ms');
					document.dispatchEvent(new Event('ExtDataComplete'));
					document.dispatchEvent(new Event('CapTableReady'));
					sessionStorageSize();
					break;
			}

			function newSharesObject(objUserData, pushTransactions, userRows = []) {
				let sumCB = 0;
				let sumC = 0;
				let sumCE = 0;
				let sumPA = 0;
				let sumPF = 0;
				let sumPFp = 0;
				let sumOrig = 0;
				let issueDate;
				let vestingFlavor;
				// let sumCPI = 0;

				let sumNegPA = 0;
				let sumNegPF = 0;
				let sumNegPFp = 0;

				let receiver = objUserData.userid;
				let receiverName = objUserData.user_name;
				let oldShares = objUserData.clients[client].shares;
				if (oldShares && oldShares['C']) oldShares['C'].permitPurchase = true;

				// Example: data-driven P-A vesting overrides (no hard-coded logic)
				// oldShares.override = {
				// 	"kyle.samuel@aleragroup.com": {
				// 		accel_start_date_utc: "2025-09-19", // date only; we add +seconds to avoid collisions
				// 		programs: {
				// 			"April 1st, 2024 Award": { accel_first_n: 2, forfeit_idx: [1] },  // forfeit 2nd of the accelerated
				// 			"January 1st, 2025 Award": { accel_first_n: 4, forfeit_idx: [] }
				// 		}
				// 	}
				// };

				const OVERRIDE = oldShares['P-A']?.override || null;

				// Safety: never auto-persist shares for users whose P-A includes Custom/Bespoke vesting rows,
				// unless we are explicitly in an admin repair run.
				const PA_CUSTOM_PRESENT =
					userRows?.some((r) => /Profits Interest \(Class P\)/.test(r[CLASS_COL]) && /custom|bespoke/i.test(String(r[VESTING_COL] || ''))) === true;

				// Only allow persistence during an explicit, confirmed admin repair mode
				const ALLOW_PERSIST_CUSTOM = typeof PA_REPAIR_MODE !== 'undefined' && PA_REPAIR_MODE === true;

				const fractionRegex = /\(?\s*(\d+)\s*\/\s*3\s*\)?(?:\W*vest\w*)?/i;

				let transCB = [],
					transC = [],
					transCE = [],
					transPA = [],
					transPF = [],
					transPFp = [],
					transOrig = [];

				for (let t of userRows) {
					if (t[EMAIL_COL]?.toLowerCase() !== receiver) continue;

					// if (receiver == "kvisciglia@orionrisk.com") {
					// 	console.log(receiver);
					// 	t[VESTING_COL] = "Custom 1/3 Vested";
					// }

					const cls = t[CLASS_COL];
					const vest = t[VESTING_COL];
					const orig = t[ORIG_UNITS_COL];
					const ent = t[ENTITY_COL]?.trim();

					if (orig) transOrig.push(t);
					if (/Profits Interest\s*\(\s*Class\s+(?:C\s*-?\s*)?B\s*\)/i.test(cls)) transCB.push(t);
					else if (/Profits Interest \(Class P\)/.test(cls)) transPA.push(t);
					else if (/Profits Interest \(Field\)/.test(cls) && vest !== 'Y') transPF.push(t);
					else if (/Profits Interest \(Field\)/.test(cls) && vest === 'Y') transPFp.push(t);
					else if (/Common/.test(cls) && !ent) transC.push(t);
					else if (/Common/.test(cls) && ent) transCE.push(t);
				}

				if (transCB.length > 0) transCB.forEach((t) => (sumCB += Number(t[UNITS_COL].replace(/^\(/, '-').replace(/[^-.\d]/g, '')) || 0));
				if (transC.length > 0) transC.forEach((t) => (sumC += Number(t[UNITS_COL].replace(/^\(/, '-').replace(/[^-.\d]/g, '')) || 0));
				if (transCE.length > 0) transCE.forEach((t) => (sumCE += Number(t[UNITS_COL].replace(/^\(/, '-').replace(/[^-.\d]/g, '')) || 0));
				if (transOrig.length > 0) transOrig.forEach((t) => (sumOrig += Number(t[ORIG_UNITS_COL].replace(/^\(/, '-').replace(/[^-.\d]/g, '')) || 0));
				if (transPA.length > 0) {
					// transPA.forEach(t => sumPA += Number(t[UNITS_COL].replace(/^\(/, '-').replace(/[^-.\d]/g, '')) || 0);
					transPA.forEach((t) => {
						const val = Number(t[UNITS_COL].replace(/^\(/, '-').replace(/[^-.\d]/g, '')) || 0;
						if (val > 0) sumPA += val;
						else if (val < 0) sumNegPA += val;
					});
					issueDate = new Date(transPA[0][DATE_COL]).toLocaleDateString('en-US');
					vestingFlavor = transPA[0][VESTING_COL];
				}
				if (transPF.length > 0) {
					// transPF.forEach(t => sumPF += Number(t[UNITS_COL].replace(/^\(/, '-').replace(/[^-.\d]/g, '')) || 0);
					transPF.forEach((t) => {
						const val = Number(t[UNITS_COL].replace(/^\(/, '-').replace(/[^-.\d]/g, '')) || 0;
						if (val > 0) sumPF += val;
						else if (val < 0) sumNegPF += val;
					});
					issueDate = new Date(transPF[0][DATE_COL]).toLocaleDateString('en-US');
					vestingFlavor = transPF[0][VESTING_COL];
				}
				if (transPFp.length > 0) {
					// transPFp.forEach(t => sumPFp += Number(t[UNITS_COL].replace(/^\(/, '-').replace(/[^-.\d]/g, '')) || 0);
					transPFp.forEach((t) => {
						const val = Number(t[UNITS_COL].replace(/^\(/, '-').replace(/[^-.\d]/g, '')) || 0;
						if (val > 0) sumPFp += val;
						else if (val < 0) sumNegPFp += val;
					});
					issueDate = new Date(transPFp[0][DATE_COL]).toLocaleDateString('en-US');
					vestingFlavor = transPFp[0][VESTING_COL];
				}

				// ignore small sums -- set them to zero
				let sums = { sumCB, sumC, sumCE, sumPA, sumPF, sumPFp, sumOrig };
				Object.keys(sums).forEach((key) => {
					if (Math.abs(sums[key]) < 0.0001) sums[key] = 0;
				});
				({ sumCB, sumC, sumCE, sumPA, sumPF, sumPFp, sumOrig } = sums);

				let newSharesObj = {};
				let permitPurch = Object.keys(customPermit).includes(receiver) ? customPermit[receiver] : ALERA_PERMIT_PURCHASE;
				let approvedPct = Object.keys(customApproved).includes(receiver) ? Number(customApproved[receiver]) : ALERA_APPROVED_PERCENT;
				let investor = objUserData.clients[client].investor;
				let election = investor ? investor.election : '';
				let appC = ((sumC + sumCE) * approvedPct) / 100;
				if (appC > sumC) appC = sumC;

				const isActiveBuySell = activeCampaign && Number(activeCampaign.maxBuy) > 0;
				let approved = (election == 'Buy' && isActiveBuySell ? MAX_BUY : appC).toString();

				if (sumCB > 0) {
					newSharesObj['CB'] = {
						owned: sumCB.toString(),
						approved: '0',
						committed: '0',
						permitPurchase: false,
					};
				}
				if (sumC > 0) {
					newSharesObj['C'] = {
						owned: sumC.toString(),
						approved: approved,
						committed: '0',
						permitPurchase: permitPurch,
					};
				}
				if (sumCE > 0) {
					newSharesObj['C-E'] = {
						owned: sumCE.toString(),
						approved: '0',
						committed: '0',
						permitPurchase: false,
					};
				}

				// Only used during trading: allow any indirect shareholder, with no C shares, to purchase C shares
				// In 2023, just C-E owners, not other classes
				// Also, set hide_zero_balances flag from True to False (to show C class with zeros)
				if ((ENROLL || LIVE_TRADING) && sumC == 0 && sumCE > 0) {
					//(sumCE > 0 || sumCB > 0 || sumP > 0)) {
					newSharesObj['C'] = {
						owned: '0',
						approved: approved,
						committed: '0',
						permitPurchase: permitPurch,
					};
				} else if (sumC == 0) delete newSharesObj['C'];

				if (sumPA > 0) {
					newSharesObj['P-A'] = {
						approved: '0',
						committed: '0',
						owned: (sumPA + sumNegPA).toString(),
						permitPurchase: false,
						vesting: {},
					};

					// Track total forfeiture "pool" from ALL negative P-A rows (multiple contra rows supported)
					let totalNegPool = 0;

					// Build P-A vesting programs from POSITIVE rows only; accumulate negative pool from NEGATIVE rows.
					// We do NOT attach negatives to a specific grant anymore: we apply forfeiture globally/future-first.
					let approvedPA = 0;

					// Helps uniquify multiple awards on the same date (same base program name)
					const programCountByBase = {};

					for (const tPA of transPA) {
						vestingFlavor = tPA[VESTING_COL] || '';

						// Parse quantity (handles "(123)" format)
						const rawQty =
							Number(
								String(tPA[UNITS_COL] || '')
									.replace(/^\(/, '-')
									.replace(/[^-.\d]/g, '')
							) || 0;

						// Negatives contribute to the forfeiture pool and do NOT create programs
						if (rawQty < 0) {
							totalNegPool += Math.abs(rawQty);
							continue;
						}
						if (rawQty === 0) continue;

						issueDate = new Date(tPA[DATE_COL]);
						if (isNaN(issueDate)) {
							if (receiver !== 'temp-unallocated_p_units@nthround.net') console.warn('MISSING OR INVALID ISSUE DATE for ' + receiver);
							continue;
						}

						issueDate = issueDate.toLocaleDateString('en-US');
						const mDate = moment(issueDate + ' UTC', 'MM/DD/YYYY');

						// Default base program name
						let baseProgramName = mDate.format('MMMM Do, YYYY') + ' Award';
						let programName = baseProgramName;

						// If Custom/Bespoke, we try to map to an existing named program in oldShares
						if (/custom|bespoke/i.test(vestingFlavor)) {
							const userVesting = oldShares?.['P-A']?.vesting || {};
							const keys = Object.keys(userVesting);

							// Prefer a non-date / non-"Award" label key if present
							const nonAwardKey = keys.find((k) => {
								const trimmed = k.trim();
								const label = trimmed.split(' Award')[0];
								return !trimmed.includes(' Award') || !moment(label, 'MMMM Do, YYYY', true).isValid();
							});

							const matchKey = nonAwardKey || 'Custom Program';

							// If matchKey exists, clone it and use it as the program name
							if (matchKey && userVesting[matchKey]) {
								programName = matchKey;
								newSharesObj['P-A'].vesting[programName] = structuredClone(userVesting[matchKey]);

								// Reset any stale vested flags (we will re-apply vested-by-fraction below)
								const vest = newSharesObj['P-A'].vesting[programName];

								if (vest?.goals && typeof vest.goals === 'object') {
									Object.values(vest.goals).forEach((g) => {
										if (g && typeof g === 'object') {
											if ('vested' in g) g.vested = false;
											if ('forfeited' in g) delete g.forfeited;
										}
									});
								}
								if (vest?.schedule && typeof vest.schedule === 'object') {
									Object.entries(vest.schedule).forEach(([k, v]) => {
										if (Number.isFinite(Number(k)) && v && typeof v === 'object') {
											if ('vested' in v) v.vested = false;
											if ('forfeited' in v) delete v.forfeited;
										}
									});
								}

								// Apply vested-by-fraction (n/3) if present
								const fracMatch = vestingFlavor.match(fractionRegex);
								if (fracMatch) {
									const n = Number(fracMatch[1]);
									if (Number.isFinite(n) && n > 0) {
										// Goals-based: vest first n goals (keeps your prior intent)
										if (vest.goals && typeof vest.goals === 'object') {
											const goalKeys = Object.keys(vest.goals);
											for (let i = 0; i < goalKeys.length && i < n; i++) {
												const gKey = goalKeys[i];
												if (vest.goals[gKey]) vest.goals[gKey].vested = true;
											}
										}

										// Schedule-based: vest the earliest fraction (n/3) by amount (your prior intent)
										if (vest.schedule && typeof vest.schedule === 'object') {
											const entries = Object.entries(vest.schedule)
												.filter(([k]) => Number.isFinite(Number(k)))
												.sort((a, b) => Number(a[0]) - Number(b[0])); // earliest -> latest

											if (entries.length) {
												const totalAmt = entries.reduce((sum, [, v]) => sum + (Number(v.amount) || 0), 0);
												const targetAmt = totalAmt * (n / 3);

												let running = 0;
												for (const [, v] of entries) {
													if (running >= targetAmt) break;
													v.vested = true;
													running += Number(v.amount) || 0;
												}
											}
										}
									}
								}
							} else {
								console.warn(`No custom schedule found for ${receiver} on ${mDate.format('MMMM Do, YYYY')}`);
								// fall through: if no old schedule found, we continue building from flavor rules below
							}
						}

						// Uniquify multiple awards with same base name (only applies when using default date-based naming)
						if (programName === baseProgramName) {
							const n = programCountByBase[baseProgramName] || 0;
							programCountByBase[baseProgramName] = n + 1;
							if (n > 0) programName += '+'.repeat(n);
						}

						// Build schedules/goals if we haven't already created this program via custom-clone
						if (!newSharesObj['P-A'].vesting[programName]) {
							// 5-year end date for cliff
							const endDate = mDate.clone().add(5, 'years').unix();

							// Build vesting dates:
							// NOTE: tranche 0 is INCLUDED in schedule and is forfeitable under new algorithm
							let vDates = new Array(16).fill(0);
							if (vestingFlavor.startsWith('60% Time')) vDates.push(0, 0, 0, 0);

							if (vestingFlavor.startsWith('60% Time')) {
								vDates.push(0, 0, 0, 0);
								vDates[0] = mDate.unix(); // grant date (tranche 0)
								let thisDate = mDate.endOf('quarter').subtract(1, 'days');
								vDates.forEach(function (d, i) {
									if (i > 0) {
										vDates[i] = thisDate.unix();
										thisDate = thisDate.add(80, 'days').endOf('quarter').subtract(1, 'days');
									}
								});
							} else {
								let thisDate = mDate.clone().add(1, 'years');
								vDates.forEach(function (d, i) {
									vDates[i] = thisDate.unix();
									thisDate = thisDate.add(3, 'months');
								});
							}

							// Flavor: 50/50 or 100% Time
							if (vestingFlavor.startsWith('50% Time') || vestingFlavor.startsWith('100% Time')) {
								let allTime = vestingFlavor.startsWith('100% Time');
								let denom = allTime ? 20 : 40;

								let sched = {};
								sched[vDates[0]] = { amount: ((4 * rawQty) / denom).toString() };

								let trancheDates = [...vDates.slice(1, 16), endDate]; // remaining tranches
								let amtObj = { amount: (rawQty / denom).toString() };

								for (let i = 0; i < trancheDates.length; i++) {
									const ts = trancheDates[i];
									sched[ts] = { amount: amtObj.amount };
								}

								if (allTime) {
									newSharesObj['P-A'].vesting[programName] = { schedule: sched };
								} else {
									newSharesObj['P-A'].vesting[programName] = {
										goals: {
											'When the Lead Investors receive at least 2.0x their Capital': { amount: (rawQty / 6).toString() },
											'When the Lead Investors receive at least 2.5x their Capital': { amount: (rawQty / 6).toString() },
											'When the Lead Investors receive at least 3.0x their Capital': { amount: (rawQty / 6).toString() },
										},
										schedule: sched,
									};
								}
							} else if (vestingFlavor.startsWith('60% Time')) {
								let denom = 50;
								let amtObj = { amount: (rawQty / denom).toString() };

								let sched = {};
								sched[vDates[0]] = { amount: (0.2 * rawQty).toString() }; // tranche 0

								for (let i = 1; i <= 20; i++) {
									sched[vDates[i]] = { amount: amtObj.amount };
								}

								newSharesObj['P-A'].vesting[programName] = {
									goals: {
										'Net Revenue from 4-1-28 to 3-31-29 equals or exceeds $20,514,000 (representing >= 17.65% growth)': {
											amount: (rawQty * 0.4).toString(),
										},
									},
									schedule: sched,
								};
							} else if (vestingFlavor.startsWith('100% Performance')) {
								newSharesObj['P-A'].vesting[programName] = {
									goals: {
										'When new business generated exceeds the 1st Milestone': { amount: (rawQty / 3).toString() },
										'When new business generated exceeds the 2nd Milestone': { amount: (rawQty / 3).toString() },
										'When new business generated exceeds the 3rd Milestone': { amount: (rawQty / 3).toString() },
									},
								};

								// Apply vested-by-fraction (n/3) if present
								const goals = newSharesObj['P-A'].vesting[programName].goals;
								Object.values(goals).forEach((g) => {
									if (g && typeof g === 'object' && 'vested' in g) g.vested = false;
								});

								const vestedMatch = vestingFlavor.match(fractionRegex);
								if (vestedMatch) {
									const vestedTranches = parseInt(vestedMatch[1], 10);
									if (Number.isFinite(vestedTranches) && vestedTranches > 0) {
										for (let i = 1; i <= vestedTranches; i++) {
											const key = `When new business generated exceeds the ${getOrdinalSuffix(i)} Milestone`;
											if (goals?.[key]) goals[key].vested = true;
										}
									}
								}
							} else if (!/custom|bespoke/i.test(vestingFlavor)) {
								// 100% 5-Year Cliff
								newSharesObj['P-A'].vesting[programName] = {
									schedule: {
										[mDate.clone().add(5, 'years').unix()]: { amount: rawQty.toString() },
									},
								};
							}
						}

						// Attach threshold if present
						const vest = newSharesObj['P-A']?.vesting?.[programName];
						if (vest && tPA[PRICE_COL]) vest.threshold = tPA[PRICE_COL];
					}

					// If vesting ended up empty, delete it to keep payload clean
					if (newSharesObj['P-A']?.vesting && Object.keys(newSharesObj['P-A'].vesting).length === 0) {
						delete newSharesObj['P-A'].vesting;
					}

					// If vesting ended up empty, delete it to keep payload clean
					if (newSharesObj['P-A']?.vesting && Object.keys(newSharesObj['P-A'].vesting).length === 0) {
						delete newSharesObj['P-A'].vesting;
					}

					// Apply data-driven vesting overrides (acceleration / forced forfeits)
					applyPAVestingOverrides(newSharesObj['P-A'], receiver);

					// === GLOBAL FORFEITURE PASS (multi-program, multi-negative) ===
					// Rules:
					// 1) Forfeit UNVESTED GOALS first (newest programs first)
					// 2) Then forfeit SCHEDULE tranches global future-first across all programs (includes tranche 0)
					// 3) Use forfeited flags + adjust amounts
					if (totalNegPool > 0 && newSharesObj['P-A']?.vesting) {
						applyPAForfeitures(newSharesObj['P-A'].vesting, totalNegPool);
					}

					// IMPORTANT: remove transient override-only markers BEFORE any DynamoDB compare/save
					stripPALocks(newSharesObj['P-A']);

					// === APPROVED PA recompute (exclude forfeited pieces) ===
					approvedPA = 0;
					const now = Date.now();

					if (newSharesObj['P-A']?.vesting) {
						Object.keys(newSharesObj['P-A'].vesting).forEach((programName) => {
							const vest = newSharesObj['P-A'].vesting[programName];
							if (!vest) return;

							if (vest.schedule && typeof vest.schedule === 'object') {
								Object.keys(vest.schedule).forEach((k) => {
									const entry = vest.schedule[k];
									const ts = Number(k);

									if (!entry) return;

									// Skip forfeited schedule tranches
									if (entry.forfeited) return;

									const isPast = Number.isFinite(ts) && ts * 1000 < now;
									const isForcedVested = !!entry.vested || !!entry.posted;

									if (isPast || isForcedVested) approvedPA += Number(entry.amount) || 0;
								});
							}

							if (vest.goals && typeof vest.goals === 'object') {
								Object.values(vest.goals).forEach((g) => {
									if (!g) return;
									if (g.forfeited) return;
									if (g.vested) approvedPA += Number(g.amount) || 0;
								});
							}
						});
					}

					newSharesObj['P-A'].approved = approvedPA.toString();
				}

				// Forfeit engine for P-A: multi-program, multi-negative
				// Strategy:
				// 1) Forfeit unvested goals first (newest programs first)
				// 2) Then forfeit schedule tranches global future-first (includes tranche 0)
				// NOTE: We do NOT support partial forfeits.
				function applyPAForfeitures(vestingObj, negPool) {
					// Work in 4-decimal fixed-point integers to avoid float drift
					const toUnits = (n) => Math.round((Number(n) || 0) * 10000);
					const fromUnits = (u) => u / 10000;

					// Tiny epsilon in “units” (0.0001 = 1). Absorbs CSV/JS rounding seams safely.
					const EPS = 5; // 0.0005

					let remaining = toUnits(negPool);
					if (remaining <= 0) return;

					// Build program list with a sortable "newest first" signal.
					const programs = Object.keys(vestingObj || {})
						.map((name) => {
							let ts = 0;
							const maybe = String(name).split(' Award')[0].trim();
							if (moment(maybe, 'MMMM Do, YYYY', true).isValid()) {
								ts = moment(maybe + ' UTC', 'MMMM Do, YYYY').unix();
							}
							return { name, ts, v: vestingObj[name] };
						})
						.sort((a, b) => b.ts - a.ts || a.name.localeCompare(b.name));

					// -------------------------------
					// 1) GOALS: forfeit UNVESTED first
					// -------------------------------
					for (const p of programs) {
						if (remaining <= 0) break;

						const goals = p.v?.goals;
						if (!goals || typeof goals !== 'object') continue;

						for (const gKey of Object.keys(goals)) {
							if (remaining <= 0) break;

							const g = goals[gKey];
							if (!g || typeof g !== 'object') continue;

							// Only forfeit goals that are NOT vested
							if (g.vested) continue;

							const amtU = toUnits(g.amount);
							if (amtU <= 0) continue;

							// Full-only forfeiture with epsilon
							if (remaining + EPS >= amtU) {
								g.forfeited = true;
								remaining -= amtU;
							} else {
								// No partials: stop here
								break;
							}
						}
					}

					if (remaining <= 0) return;

					// -----------------------------------------------
					// 2) SCHEDULE: global future-first across programs
					// -----------------------------------------------
					const scheduleSlots = [];

					for (const p of programs) {
						const sched = p.v?.schedule;
						if (!sched || typeof sched !== 'object') continue;

						for (const k of Object.keys(sched)) {
							const ts = Number(k);
							if (!Number.isFinite(ts)) continue;

							const entry = sched[k];
							if (!entry || typeof entry !== 'object') continue;

							// Skip already-forfeited slots (don’t double count)
							if (entry.forfeited) continue;

							// CRITICAL: Override-protected (accelerated-keep) tranches are NOT eligible for global forfeiture
							if (entry._lock) continue;

							const amtU = toUnits(entry.amount);
							if (amtU <= 0) continue;

							scheduleSlots.push({
								ts,
								programName: p.name,
								key: k,
								entry,
								amtU,
							});
						}
					}

					// Sort: most future first; tie-break by program name for stability
					scheduleSlots.sort((a, b) => b.ts - a.ts || a.programName.localeCompare(b.programName));

					for (const slot of scheduleSlots) {
						if (remaining <= 0) break;

						// Full-only forfeiture with epsilon
						if (remaining + EPS >= slot.amtU) {
							slot.entry.forfeited = true;
							remaining -= slot.amtU;
						} else {
							// No partials: stop here
							break;
						}
					}

					if (remaining > EPS) {
						console.warn(`P-A forfeiture pool exceeded available tranches by ~${fromUnits(remaining).toFixed(4)}`);
					}
				}

				function applyPAVestingOverrides(paNode, receiverEmail) {
					if (!paNode?.vesting || typeof paNode.vesting !== 'object') return;
					if (!OVERRIDE?.programs) return;

					const accelBaseTs = OVERRIDE.accel_start_date_utc ? moment(OVERRIDE.accel_start_date_utc + ' UTC', 'YYYY-MM-DD').unix() : null;
					if (!accelBaseTs) return;

					// accel first N tranches; optionally mark some accelerated ones forfeited
					// ALSO: mark accelerated tranches as _lock unless explicitly forfeited by override config
					const accelFirstN = (awardName, n, forfeitIdx = []) => {
						const award = paNode.vesting[awardName];
						const sched = award?.schedule;
						if (!sched || typeof sched !== 'object') return;

						// Sort tranche timestamps ascending (numeric)
						const keys = Object.keys(sched)
							.map((k) => Number(k))
							.filter((x) => Number.isFinite(x))
							.sort((a, b) => a - b)
							.map((x) => String(x));

						if (keys.length < n) return;

						const pulled = [];
						for (let idx = 0; idx < n; idx++) {
							const k = keys[idx];
							const obj = { ...(sched[k] || {}) };

							// Remove originals
							delete sched[k];

							// Clean + re-apply flags
							delete obj.forfeited;
							delete obj._lock;

							const shouldForfeit = forfeitIdx.includes(idx) || forfeitIdx.includes(String(idx));
							if (shouldForfeit) {
								obj.forfeited = true;
							} else {
								// Override-intent: these accelerated tranches are “keep” tranches
								// They must survive the subsequent global forfeiture pass.
								obj._lock = true;
							}

							pulled.push(obj);
						}

						// Re-key contiguously starting at accelBaseTs (+seconds)
						for (let idx = 0; idx < pulled.length; idx++) {
							sched[String(accelBaseTs + idx)] = pulled[idx];
						}
					};

					const programs = OVERRIDE.programs && typeof OVERRIDE.programs === 'object' ? OVERRIDE.programs : {};
					for (const [progName, cfg] of Object.entries(programs)) {
						// DynamoDB strings -> numbers
						const n = Math.max(0, parseInt(String(cfg?.accel_first_n || '0'), 10) || 0);

						// forfeit_idx: ["1","3"] -> [1,3]
						// IMPORTANT: indexes are ZERO-BASED (coders think zero-based; your config is string-only)
						const forfeitIdx = Array.isArray(cfg?.forfeit_idx) ? cfg.forfeit_idx.map((x) => parseInt(String(x), 10)).filter(Number.isFinite) : [];

						if (n > 0) accelFirstN(progName, n, forfeitIdx);
					}
				}

				// Remove transient override-only markers so DynamoDB diffs don’t thrash
				function stripPALocks(paNode) {
					if (!paNode?.vesting || typeof paNode.vesting !== 'object') return;

					for (const vest of Object.values(paNode.vesting)) {
						if (!vest || typeof vest !== 'object') continue;

						if (vest.schedule && typeof vest.schedule === 'object') {
							for (const entry of Object.values(vest.schedule)) {
								if (entry && typeof entry === 'object' && '_lock' in entry) {
									delete entry._lock;
								}
							}
						}
					}
				}

				if (sumPF > 0) {
					newSharesObj['P-F'] = {
						approved: '0',
						committed: '0',
						owned: (sumPF + sumNegPF).toString(),
						permitPurchase: false,
						vesting: {},
					};
					let approvedPF = 0;
					transPF.forEach((tPF) => {
						// re-purposing vestingFlavor, sumP, and issueDate for each tranche
						vestingFlavor = tPF[VESTING_COL];
						sumPF = Number(tPF[UNITS_COL].replace(/^\(/, '-').replace(/[^-.\d]/g, '')) || 0;
						issueDate = new Date(tPF[DATE_COL]);
						if (isNaN(issueDate)) {
							console.warn('MISSING OR INVALID ISSUE DATE for ' + receiver);
							return;
						}
						issueDate = issueDate.toLocaleDateString('en-US');
					});
					newSharesObj['P-F'].approved = approvedPF.toString();
					// NOTE BENE: this will FAIL if the user has BOTH P-F and P-F'
					if (sumOrig) newSharesObj['P-F'].orig = sumOrig.toString();
				}

				if (sumPFp > 0) {
					newSharesObj["P-F'"] = {
						approved: '0',
						committed: '0',
						owned: (sumPFp + sumNegPFp).toString(),
						permitPurchase: false,
						vesting: {},
					};
					let approvedPFp = 0;
					transPFp.forEach((tPFp) => {
						// re-purposing vestingFlavor, sumP, and issueDate for each tranche
						vestingFlavor = tPFp[VESTING_COL];
						sumPFp = Number(tPFp[UNITS_COL].replace(/^\(/, '-').replace(/[^-.\d]/g, '')) || 0;
						issueDate = new Date(tPFp[DATE_COL]);
						if (isNaN(issueDate)) {
							console.warn('MISSING OR INVALID ISSUE DATE for ' + receiver);
							return;
						}
						issueDate = issueDate.toLocaleDateString('en-US');
					});
					newSharesObj["P-F'"].approved = approvedPFp.toString();
					// NOTE BENE: this will FAIL if the user has BOTH P-F and P-F'
					if (sumOrig) newSharesObj["P-F'"].orig = sumOrig.toString();
				}

				mergeEvents(newSharesObj, oldShares, true);

				// to avoid double-entry, intentionally omit Transaction nrid
				// see ledgers.js, around line 3130, classes.forEach(function (c, index)
				if (pushTransactions) {
					let newTransObj = {};
					transCB.forEach(function (t) {
						newTransObj = {
							date: (Date.parse(t[DATE_COL]) / 1000).toString(),
							notes: 'Propel Producer Profits Interest award',
							quantity: (Number(t[UNITS_COL].replace(/^\(/, '-').replace(/[^-.\d]/g, '')) || 0).toString(),
							receiver: receiver,
							receiverName: receiverName,
							securityType: 'Profits Interest',
							sender: '',
							senderName: '',
							shareClass: 'CB',
							sharePrice: (Number(t[PRICE_COL].replace(/[^.\d]/g, '')) || 0).toString(),
							strikePrice: '0',
						};
						if (clientData.equity['CB']?.transactions) clientData.equity['CB'].transactions.push(newTransObj);
					});
					transC.forEach(function (t) {
						// let notes = (t[CLASS_COL] ? t[CLASS_COL] : '') + (t[CLASS_COL] && t[VESTING_COL] ? " - " : '') + (t[VESTING_COL] ? t[VESTING_COL] : '');
						// Handle AMP transactions
						let thisDate = Date.parse(t[DATE_COL]) / 1000;
						let isAMP = thisDate > 1671494400 && thisDate < 1672531200;
						let thisQty = Number(t[UNITS_COL].replace(/^\(/, '-').replace(/[^-.\d]/g, '')) || 0;
						let isSeller = thisQty < 0; // && isAMP
						let isBuyer = thisQty > 0; // && isAMP
						let notes = 'Common Units held by individual';
						newTransObj = {
							date: thisDate.toString(),
							quantity: Math.abs(thisQty).toString(),
							notes: notes,
							receiver: isSeller ? '' : receiver,
							receiverName: isSeller ? 'Buyer' : receiverName,
							securityType: 'Common Units',
							sender: isSeller ? objUserData.userid : '',
							senderName: isSeller ? objUserData.user_name : isBuyer ? 'Seller' : '',
							shareClass: 'C',
							sharePrice: (Number(t[PRICE_COL].replace(/[^.\d]/g, '')) || 0).toString(),
							strikePrice: '0',
						};
						if (clientData.equity['C']?.transactions) clientData.equity['C'].transactions.push(newTransObj);
					});
					transPA.forEach(function (t) {
						newTransObj = {
							date: (Date.parse(t[DATE_COL]) / 1000).toString(),
							notes: 'Management Incentive Profits Interest award',
							quantity: (Number(t[UNITS_COL].replace(/^\(/, '-').replace(/[^-.\d]/g, '')) || 0).toString(),
							receiver: receiver,
							receiverName: receiverName,
							securityType: 'Profits Interest',
							sender: '',
							senderName: '',
							shareClass: 'P-A',
							sharePrice: '0',
							strikePrice: (Number(t[PRICE_COL].replace(/[^.\d]/g, '')) || 0).toString(),
						};
						if (!clientData.equity['P-A']) clientData.equity['P-A'] = { transactions: [] };
						clientData.equity['P-A'].transactions.push(newTransObj);
					});
					transPF.forEach(function (t) {
						newTransObj = {
							date: (Date.parse(t[DATE_COL]) / 1000).toString(),
							notes: 'Management Incentive Profits Interest award',
							quantity: (Number(t[UNITS_COL].replace(/^\(/, '-').replace(/[^-.\d]/g, '')) || 0).toString(),
							receiver: receiver,
							receiverName: receiverName,
							securityType: 'Profits Interest (Field)',
							sender: '',
							senderName: '',
							shareClass: 'P-F',
							sharePrice: '0',
							strikePrice: (Number(t[PRICE_COL].replace(/[^.\d]/g, '')) || 0).toString(),
						};
						if (clientData.equity['P-F']?.transactions) clientData.equity['P-F'].transactions.push(newTransObj);
					});
					transPFp.forEach(function (t) {
						newTransObj = {
							date: (Date.parse(t[DATE_COL]) / 1000).toString(),
							notes: 'Management Incentive Profits Interest award',
							quantity: (Number(t[UNITS_COL].replace(/^\(/, '-').replace(/[^-.\d]/g, '')) || 0).toString(),
							receiver: receiver,
							receiverName: receiverName,
							securityType: "Profits Interest (Field)'",
							sender: '',
							senderName: '',
							shareClass: "P-F'",
							sharePrice: (Number(t[PRICE_COL].replace(/[^.\d]/g, '')) || 0).toString(),
							strikePrice: '0',
						};
						if (clientData.equity["P-F'"]?.transactions) clientData.equity["P-F'"].transactions.push(newTransObj);
					});
					transCE.forEach(function (t) {
						// 	let notes = (t[AFFILIATE_COL] ? t[AFFILIATE_COL] : '') + (t[AFFILIATE_COL] && t[FIRM_COL] ? " - " : '') + (t[FIRM_COL] ? t[FIRM_COL] : '');
						let notes = 'Common Units held by separate entity';
						newTransObj = {
							date: (Date.parse(t[DATE_COL]) / 1000).toString(),
							quantity: (Number(t[UNITS_COL].replace(/^\(/, '-').replace(/[^-.\d]/g, '')) || 0).toString(),
							notes: notes,
							receiver: receiver,
							receiverName: receiverName,
							securityType: 'Common Units (Entity Held)',
							sender: '',
							senderName: '',
							shareClass: 'C-E',
							sharePrice: (Number(t[PRICE_COL].replace(/[^.\d]/g, '')) || 0).toString(),
							strikePrice: '0',
						};
						if (clientData.equity['C-E']?.transactions) clientData.equity['C-E'].transactions.push(newTransObj);
					});

					sessionStorage.setItem(client + '.Data', JSON.stringify(clientData));
				}

				/* =========	BEGIN: persist full shares (simple, simulated-merge compare)	========= */
				/* Front-end only:
				- Simulate server deep-merge + class "DELETE" before deciding to POST
				- Canonicalize for compare only: normalize threshold, normalize ALL vesting amounts, strip " ❌" keys
				- Keep your outgoing payload shape (incl. "DELETE" tombstones)
				- Requires global roundToFour(number)
			*/
				const UPDATE_ALERA_USR_EQUITY = true; // false
				if (UPDATE_ALERA_USR_EQUITY && !OVERRIDE) {
					const CLASS_WHITELIST = ['CB', 'C', 'C-E', 'P-A', 'P-F', "P-F'"];

					const clone = (o) => (typeof structuredClone === 'function' ? structuredClone(o) : JSON.parse(JSON.stringify(o)));

					// stable stringify (key-sorted) for deep equality
					const stableStringify = (obj) => {
						const sortRec = (x) => {
							if (Array.isArray(x)) return x.map(sortRec);
							if (x && typeof x === 'object') {
								const o = {};
								Object.keys(x)
									.sort()
									.forEach((k) => {
										o[k] = sortRec(x[k]);
									});
								return o;
							}
							return x;
						};
						return JSON.stringify(sortRec(obj));
					};

					const hasKeys = (o) => o && typeof o === 'object' && !Array.isArray(o) && Object.keys(o).length;

					// sanitize: keep only whitelisted classes; normalize numeric strings; keep vesting/orig if present
					const sanitizeShares = (shares = {}) => {
						const out = {};
						for (const c of Object.keys(shares)) {
							if (!CLASS_WHITELIST.includes(c)) continue;
							const src = shares[c] || {};
							const node = {
								// numbers -> Number -> roundToFour -> string
								owned: roundToFour(Number(src.owned) ?? 0).toString(),
								approved: roundToFour(Number(src.approved) ?? 0).toString(),
								committed: roundToFour(Number(src.committed) ?? 0).toString(),
								permitPurchase: typeof src.permitPurchase === 'boolean' ? src.permitPurchase : false,
							};
							if (hasKeys(src.vesting)) node.vesting = src.vesting;
							if ('orig' in src) {
								const r = roundToFour(Number(src.orig) || 0);
								if (r !== 0) node.orig = r.toString();
							}
							out[c] = node;
						}
						return out;
					};

					// classes present in old but absent in new (for DELETE tombstones)
					const computeRemovals = (oldS, newS) => {
						const removes = [];
						for (const c of Object.keys(oldS)) if (!(c in newS)) removes.push(c);
						return removes;
					};

					// --- canonicalization for COMPARE ONLY (don’t change what we send) ---
					// normalize vesting.threshold -> clean number-string or drop if zero
					const normalizeThresholds = (shares) => {
						const out = clone(shares);
						for (const cls of Object.keys(out)) {
							const vest = out[cls]?.vesting;
							if (!vest || typeof vest !== 'object') continue;
							for (const prog of Object.keys(vest)) {
								const p = vest[prog];
								if (p && typeof p === 'object' && 'threshold' in p) {
									const n = Number(String(p.threshold).replace(/[^-.\d]/g, '')) || 0;
									const r = roundToFour(n);
									if (r === 0) delete p.threshold;
									else p.threshold = String(r);
								}
							}
						}
						return out;
					};

					// normalize ALL vesting amounts (schedule + goals) using roundToFour
					const normalizeVestingAmounts = (shares) => {
						const out = clone(shares);
						for (const cls of Object.keys(out)) {
							const vest = out[cls]?.vesting;
							if (!vest || typeof vest !== 'object') continue;
							for (const prog of Object.keys(vest)) {
								const p = vest[prog];
								if (!p || typeof p !== 'object') continue;
								if (p.schedule && typeof p.schedule === 'object') {
									for (const t of Object.keys(p.schedule)) {
										const amt = p.schedule[t]?.amount;
										if (amt !== undefined) p.schedule[t].amount = String(roundToFour(Number(amt) || 0));
									}
								}
								if (p.goals && typeof p.goals === 'object') {
									for (const g of Object.keys(p.goals)) {
										const amt = p.goals[g]?.amount;
										if (amt !== undefined) p.goals[g].amount = String(roundToFour(Number(amt) || 0));
									}
								}
							}
						}
						return out;
					};

					// one-pass canonicalizer for compare
					const canon = (shares) => normalizeVestingAmounts(normalizeThresholds(clone(shares)));

					// tiny deep-merge to simulate lambda behavior
					const deepMerge = (dst, src) => {
						if (!src || typeof src !== 'object' || Array.isArray(src)) return src;
						const out = dst && typeof dst === 'object' && !Array.isArray(dst) ? { ...dst } : {};
						for (const k of Object.keys(src)) {
							const sv = src[k];
							const dv = out[k];
							if (sv && typeof sv === 'object' && !Array.isArray(sv) && dv && typeof dv === 'object' && !Array.isArray(dv))
								out[k] = deepMerge(dv, sv);
							else out[k] = sv;
						}
						return out;
					};

					// ---- entry ----
					const serverOldShares = typeof oldShares !== 'undefined' && oldShares ? oldShares : objUserData?.clients?.[client]?.shares || {};

					const sanitizedOld = sanitizeShares(serverOldShares);
					const sanitizedNew = sanitizeShares(newSharesObj || {}); // full target we want

					// DELETE tombstones for removed classes
					const removeSharesClasses = computeRemovals(sanitizedOld, sanitizedNew);
					let sharesPayload = { ...sanitizedNew };
					if (removeSharesClasses.length) {
						for (const c of removeSharesClasses) {
							if (serverOldShares && serverOldShares[c] != null && !(c in sharesPayload)) sharesPayload[c] = 'DELETE';
						}
					}

					// ---- simulate what the lambda will store ----
					// 1) start from current (sanitized) server shares
					const simStart = clone(sanitizedOld);
					// 2) apply class deletes
					const payloadNoDeletes = {};
					for (const k of Object.keys(sharesPayload)) {
						if (sharesPayload[k] === 'DELETE') delete simStart[k];
						else payloadNoDeletes[k] = sharesPayload[k];
					}
					// 3) deep-merge payload into the modified starting object
					const simAfter = deepMerge(simStart, payloadNoDeletes);

					// ---- compare canonicalized versions (prevents tiny numeric / marker diffs) ----
					const cmpOld = canon(sanitizedOld);
					const cmpAfter = canon(simAfter);
					const changed = stableStringify(cmpOld) !== stableStringify(cmpAfter);

					// HARD SAFETY: never auto-persist Custom/Bespoke users unless explicit repair mode is on
					if (PA_CUSTOM_PRESENT && !ALLOW_PERSIST_CUSTOM) {
						// We STILL want to return populated local shares; just skip the write.
						// So: do nothing here except bypass the persist logic.
						// (No confirm dialogs either.)
						// NOTE: do NOT "return" from newSharesObject().
					} else if (changed) {
						// Build a human-readable diff for confirmation
						const diffLines = [];

						for (const cls of Object.keys(sharesPayload)) {
							const before = sanitizedOld[cls] ? JSON.stringify(sanitizedOld[cls]) : '(none)';
							const after = sharesPayload[cls] === 'DELETE' ? 'DELETE' : JSON.stringify(sharesPayload[cls]);
							diffLines.push(`${cls}: ${before}  →  ${after}`);
						}

						if (nthAdmin) {
							const confirmMessage =
								`⚠️ CONFIRM SHARE UPDATE\n\n` +
								`User: ${receiver}\n` +
								`Client: ${client}\n\n` +
								`The following classes will be updated:\n` +
								diffLines.join('\n') +
								`\n\nProceed with update?`;

							if (CANCEL_ALL_ALERA_UPDATES) return;
							if (!confirm(confirmMessage)) {
								CANCEL_ALL_ALERA_UPDATES = true;
								return;
							}
						}

						somethingChanged = true;

						console.groupCollapsed('[persistFullShares] diff (canon compare)');
						console.log('to  :', cmpAfter);
						console.groupEnd();

						const payload = {
							eventName: 'admin_index.update_alera_equity',
							clients: { [client]: { shares: sharesPayload } },
							investor: receiver,
							userid: authedUser,
							client: client,
							cookie: cookie,
						};
						if (typeof env !== 'undefined') payload['io.nthround.env'] = env;

						// strip undefined leaves
						(function strip(o) {
							if (!o || typeof o !== 'object') return;
							for (const k of Object.keys(o)) {
								strip(o[k]);
								if (o[k] === undefined) delete o[k];
							}
						})(payload);

						// console.log("[persistFullShares] sending to:", receiver, { sharesPayload });

						aleraUpdateQueue = aleraUpdateQueue.then(() => {
							// If someone cancelled earlier, stop the chain.
							if (CANCEL_ALL_ALERA_UPDATES) return;
							return axios.post(postURL, JSON.stringify(payload)).catch((e) => console.error('[persistFullShares] update failed:', e));
						});
					} // else console.log("[persistFullShares] no changes after simulated merge; skipped");
				}
				/* =========	END: persist full shares (simple, simulated-merge compare)	========= */

				if (Object.keys(newSharesObj).length == 0 && Object.keys(oldShares).length > 0) return oldShares;
				else return newSharesObj;
			}

			if (somethingChanged && aleraSafetyCount < 4) {
				aleraSafetyCount++;
				setTimeout(() => refreshSessionStorage(), 5000);
			}
		})
		.catch(function (err) {
			// There was an error
			console.warn('Something went wrong.', err);
		});

	// Preserve events from the prior shares object
	function mergeEvents(newSharesObj, oldShares, keepZeroClassesWithEvents = false) {
		if (!oldShares) return;

		Object.keys(oldShares).forEach((cls) => {
			const prior = oldShares[cls];
			const evts = prior && Array.isArray(prior.events) ? prior.events : null;
			if (!evts || evts.length === 0) return;

			// If the class exists in the newly built object, attach/merge events
			if (newSharesObj[cls]) {
				if (!Array.isArray(newSharesObj[cls].events)) newSharesObj[cls].events = [];
				const seen = new Set(newSharesObj[cls].events.map((e) => e && e.nrid).filter(Boolean));
				evts.forEach((e) => {
					if (!e || !e.nrid || seen.has(e.nrid)) return;
					newSharesObj[cls].events.push(structuredClone(e));
					seen.add(e.nrid);
				});
				return;
			}

			// Optionally keep a class with zero balances just to retain historical events
			if (keepZeroClassesWithEvents) {
				newSharesObj[cls] = {
					owned: '0',
					approved: '0',
					committed: '0',
					permitPurchase: prior.permitPurchase === true, // keep a sensible default
					events: structuredClone(evts),
				};
			}
		});
	}

	function getOrdinalSuffix(i) {
		const j = i % 10,
			k = i % 100;
		if (j == 1 && k != 11) {
			return i + 'st';
		}
		if (j == 2 && k != 12) {
			return i + 'nd';
		}
		if (j == 3 && k != 13) {
			return i + 'rd';
		}
		return i + 'th';
	}
}

async function getStandardTransactionsFromFile(caller, res, redirect, fastLoad, newAlias, path) {
	if (caller === 'userData') {
		try {
			const rows = await fetchScopedTransactions({ client, userid: authedUser, cookie, userids: [userData.userid] });
			if (!pn.startsWith('/admin') || redirect) {
				Object.values(clientData.equity).forEach((sc) => (sc.transactions = []));
				rows.forEach((tx) => {
					if (clientData.equity[tx.shareClass]) {
						if (!clientData.equity[tx.shareClass].transactions) clientData.equity[tx.shareClass].transactions = [];
						clientData.equity[tx.shareClass].transactions.push(tx);
					}
				});
				sessionStorage.setItem(client + '.Data', JSON.stringify(clientData));
			}
			sessionStorage.setItem('user.Data', JSON.stringify(userData));
			if (!pn.startsWith('/admin')) {
				document.dispatchEvent(new Event('ExtDataComplete'));
			}
			if (redirect) {
				sessionStorage.setItem('shareholder.Data', JSON.stringify(userData));
				window.location.replace('/' + path);
			} else if (newAlias) finishSetAlias();
		} catch (err) {
			console.warn('getStandardTransactionsFromFile failed:', err);
			// Even if transactions fetch fails, persist the user/shareholder data we already have so the
			// destination of the redirect (e.g. previewShareholder → '/') renders for the intended user
			// instead of stale admin state. Transactions are missing but the rest of the dashboard is correct.
			try {
				sessionStorage.setItem('user.Data', JSON.stringify(userData));
			} catch (e) {
				console.warn('user.Data persist failed in catch:', e);
			}
			if (redirect) {
				try {
					sessionStorage.setItem('shareholder.Data', JSON.stringify(userData));
				} catch (e) {
					console.warn('shareholder.Data persist failed in catch:', e);
				}
			}
			if (!pn.startsWith('/admin')) document.dispatchEvent(new Event('ExtDataComplete'));
			if (redirect) window.location.replace('/' + path);
			else if (newAlias) finishSetAlias();
		}
		return;
	}
	if (caller === 'shareholderData') {
		try {
			const rows = await fetchScopedTransactions({ client, userid: authedUser, cookie, userids: [shareholderData.userid] });
			Object.values(clientData.equity).forEach((sc) => (sc.transactions = []));
			rows.forEach((tx) => {
				if (clientData.equity[tx.shareClass]) {
					if (!clientData.equity[tx.shareClass].transactions) clientData.equity[tx.shareClass].transactions = [];
					clientData.equity[tx.shareClass].transactions.push(tx);
				}
			});
			sessionStorage.setItem(client + '.Data', JSON.stringify(clientData));
			enableUserEditControls(true);
			sessionStorage.setItem('shareholder.Data', JSON.stringify(shareholderData));
			if (fastLoad) document.dispatchEvent(new Event('CarsAndObserversComplete'));
			else document.dispatchEvent(new CustomEvent('ShareholderComplete', { detail: { refreshed: true } }));
		} catch (err) {
			console.warn('getStandardTransactionsFromFile failed:', err);
			if (fastLoad) document.dispatchEvent(new Event('CarsAndObserversComplete'));
			else document.dispatchEvent(new CustomEvent('ShareholderComplete', { detail: { refreshed: true } }));
		}
		return;
	}
}

/**
 * Load external transactions for consolidated view (multiple userids)
 * Fetches the external CSV and filters transactions for all consolidated userids
 * @param {Array} consolidatedUserIds - Array of userids to load transactions for
 * @param {Function} callback - Callback to execute when complete
 */
async function loadConsolidatedExternalTransactions(consolidatedUserIds, callback) {
	const userIdSet = new Set(consolidatedUserIds.map((id) => id?.toLowerCase()).filter(Boolean));
	if (!userIdSet.size) {
		if (callback) callback();
		return;
	}

	const holderNames = {};
	if (userData?.consolidatedTags) {
		Object.entries(userData.consolidatedTags).forEach(([uid, data]) => {
			holderNames[uid.toLowerCase()] = data.holderName || uid;
		});
	} else {
		consolidatedUserIds.forEach((uid) => {
			holderNames[uid.toLowerCase()] = uid;
		});
	}

	try {
		const rows = await fetchScopedTransactions({ client, userid: authedUser, cookie, userids: consolidatedUserIds });

		Object.values(clientData.equity).forEach((sc) => (sc.transactions = []));
		userData.clients[client].consolidatedTransactions = [];

		rows.forEach((tx) => {
			const sender = (tx.sender || '').toLowerCase();
			const receiver = (tx.receiver || '').toLowerCase();
			const holder = userIdSet.has(receiver) ? receiver : sender;
			const holderName = holderNames[holder] || holder;
			const enriched = { ...tx, holder, holderName };

			if (clientData.equity[tx.shareClass]) {
				if (!clientData.equity[tx.shareClass].transactions) clientData.equity[tx.shareClass].transactions = [];
				clientData.equity[tx.shareClass].transactions.push(enriched);
			}
			userData.clients[client].consolidatedTransactions.push(enriched);
		});

		sessionStorage.setItem(client + '.Data', JSON.stringify(clientData));
		sessionStorage.setItem('user.Data', JSON.stringify(userData));
		sessionStorageSize();
	} catch (err) {
		console.warn('loadConsolidatedExternalTransactions failed:', err);
	}
	if (callback) callback();
}
/**
 * Load Asplundh transactions for consolidated view (multiple userids)
 * Asplundh uses FFM (Founder/Family/Member) tags instead of sender/receiver
 * @param {Object} consolidatedTags - Object keyed by userid with tags and holderName
 * @param {Function} callback - Callback to execute when complete
 */
async function loadConsolidatedAsplundhTransactions(consolidatedTags, callback) {
	if (!clientData.transactions_data) {
		if (callback) callback();
		return;
	}

	let stockRecordFile = await getContentUrl(clientData.transactions_data);
	fetch(stockRecordFile)
		.then(function (response) {
			return response.text();
		})
		.then(function (csv) {
			let array = cleanCSV(csv);

			let orderedFields = [
				'Founder',
				'Family',
				'Member',
				'Sequence',
				'Type',
				'Certificate',
				'Shares',
				'Issued Date',
				'Division',
				'Rate',
				'Amount',
				'Delete Tag',
				'Soc Sec',
				'UserName',
				'ProcessDate',
				'TransactionBasis',
			];

			// Map field names to column indexes
			const fieldMap = {};
			orderedFields.forEach((field, i) => {
				const idx = array[0].indexOf(field);
				if (idx >= 0) fieldMap[field] = idx;
			});

			let f0 = fieldMap['Founder'];
			let f1 = fieldMap['Family'];
			let f2 = fieldMap['Member'];
			let f5 = fieldMap['Certificate'];
			let f6 = fieldMap['Shares'];
			let f7 = fieldMap['Issued Date'];
			let f15 = fieldMap['TransactionBasis'];

			// Build a lookup of FFM prefixes to user data
			const ffmLookup = {};
			Object.entries(consolidatedTags).forEach(([userid, data]) => {
				const userTags = data.tags ? data.tags.join('') : '';
				if (userTags) {
					ffmLookup[userTags] = {
						userid: userid,
						holderName: data.holderName,
					};
				}
			});

			// Clear any existing transactions
			Object.values(clientData.equity).forEach((shareClass) => (shareClass.transactions = []));

			// Clear consolidated transactions array to prevent duplicates on re-entry
			userData.clients[client].consolidatedTransactions = [];

			let existingNridSet = new Set();

			array.forEach((r) => {
				let ffm = 'F' + r[f0] + r[f1] + r[f2];

				// Find which user this transaction belongs to by FFM tag matching
				let matchedUser = null;
				Object.keys(ffmLookup).forEach((userTags) => {
					if (userTags.startsWith(ffm)) {
						matchedUser = ffmLookup[userTags];
					}
				});

				if (matchedUser) {
					let certNumber = Number(r[f5].replace(/[^.\d]/g, '')).toString();
					let quantity = Number(r[f6]);
					if (!quantity) return;
					let notes = '#' + certNumber + ' (' + quantity.toLocaleString() + ' shares)';
					let nrid = 'f' + r[f0] + '-' + r[f1] + '-' + r[f2] + '-' + r[f5];
					nrid = nrid + '-' + '0'.repeat(36 - nrid.length - 1);

					if (existingNridSet.has(nrid)) return;
					existingNridSet.add(nrid);

					let trans = {
						date: Date.parse(r[f7]) / 1000,
						notes: certNumber,
						nrid: nrid,
						quantity: quantity.toString(),
						receiver: matchedUser.userid,
						receiverName: matchedUser.holderName,
						securityType: 'Common Shares',
						sender: '',
						senderName: '',
						shareClass: 'C',
						sharePrice: r[f15] ? r[f15].replace(/[^.\d]/g, '') : '',
						holder: matchedUser.userid,
						holderName: matchedUser.holderName,
					};

					// Add to clientData.equity for compatibility
					if (clientData.equity['C']?.transactions) {
						clientData.equity['C'].transactions.push(trans);
					}

					// Add to consolidated transactions
					userData.clients[client].consolidatedTransactions.push(trans);
				}
			});

			sessionStorage.setItem(client + '.Data', JSON.stringify(clientData));
			sessionStorage.setItem('user.Data', JSON.stringify(userData));
			sessionStorageSize();

			if (callback) callback();
		})
		.catch(function (err) {
			console.warn('loadConsolidatedAsplundhTransactions failed:', err);
			if (callback) callback();
		});
}

/**
 * Load Alera transactions for consolidated view (multiple userids)
 * Alera uses dynamic column positions from clientData.transactions_columns
 * @param {Array} consolidatedUserIds - Array of userids/emails to load transactions for
 * @param {Function} callback - Callback to execute when complete
 */
async function loadConsolidatedAleraTransactions(consolidatedUserIds, callback) {
	if (!clientData.transactions_data || !clientData.transactions_columns) {
		if (callback) callback();
		return;
	}

	// Convert Excel column letters to numbers (A=0, B=1, ..., Z=25)
	const excelColumnToIndex = (letter) => letter.charCodeAt(0) - 65;

	// Build column index map from clientData.transactions_columns
	const colMap = {};
	for (const [letter, key] of Object.entries(clientData.transactions_columns)) {
		colMap[key.toUpperCase()] = excelColumnToIndex(letter);
	}

	let stockRecordFile = await getContentUrl(clientData.transactions_data);
	fetch(stockRecordFile)
		.then(function (response) {
			return response.text();
		})
		.then(function (csv) {
			let array = cleanCSV(csv);

			const EMAIL_COL = colMap['EMAIL'];
			const CLASS_COL = colMap['CLASS'];
			const DATE_COL = colMap['DATE'];
			const UNITS_COL = colMap['UNITS'];
			const PRICE_COL = colMap['PRICE'];
			const VESTING_COL = colMap['VESTING'];
			const ENTITY_COL = colMap['ENTITY'];

			// Build a set of consolidated userids (lowercase emails)
			const userIdSet = new Set(consolidatedUserIds.map((id) => id?.toLowerCase()).filter(Boolean));

			// Build holder name lookup
			const holderNames = {};
			const consolidatedUserData = sessionStorage.getItem('user.Data') ? JSON.parse(sessionStorage.getItem('user.Data')) : null;
			consolidatedUserIds.forEach((uid) => {
				holderNames[uid.toLowerCase()] = uid;
			});

			// Clear any existing transactions
			Object.values(clientData.equity).forEach((shareClass) => {
				if (shareClass.transactions) shareClass.transactions = [];
			});

			// Initialize consolidated transactions array
			// Clear consolidated transactions array to prevent duplicates on re-entry
			userData.clients[client].consolidatedTransactions = [];

			// Group transactions by user email
			array.forEach((t, index) => {
				if (index === 0) return; // Skip header row

				const email = (t[EMAIL_COL] || '').toLowerCase().trim();
				if (!email || !userIdSet.has(email)) return;

				const cls = t[CLASS_COL] || '';
				const holderName = holderNames[email] || email;

				// Determine share class mapping (same logic as getAleraHoldingsFromFile)
				let shareClass = null;
				let securityType = '';
				let notes = '';

				if (/Profits Interest\s*\(\s*Class\s+(?:C\s*-?\s*)?B\s*\)/i.test(cls)) {
					shareClass = 'CB';
					securityType = 'Profits Interest';
					notes = 'Propel Producer Profits Interest award';
				} else if (/Profits Interest \(Class P\)/.test(cls)) {
					shareClass = 'P-A';
					securityType = 'Profits Interest';
					notes = 'Management Incentive Profits Interest award';
				} else if (/Profits Interest \(Field\)/.test(cls) && t[VESTING_COL] !== 'Y') {
					shareClass = 'P-F';
					securityType = 'Profits Interest (Field)';
					notes = 'Management Incentive Profits Interest award';
				} else if (/Profits Interest \(Field\)/.test(cls) && t[VESTING_COL] === 'Y') {
					shareClass = "P-F'";
					securityType = "Profits Interest (Field)'";
					notes = 'Management Incentive Profits Interest award';
				} else if (/Common/.test(cls) && !t[ENTITY_COL]?.trim()) {
					shareClass = 'C';
					securityType = 'Common Units';
					notes = 'Common Units held by individual';
				} else if (/Common/.test(cls) && t[ENTITY_COL]?.trim()) {
					shareClass = 'C-E';
					securityType = 'Common Units (Entity Held)';
					notes = 'Common Units held by separate entity';
				}

				if (!shareClass) return;

				const quantity = Math.abs(Number((t[UNITS_COL] || '').replace(/^\(/, '-').replace(/[^-.\d]/g, '')) || 0);
				if (quantity === 0) return;

				const isSeller = (Number((t[UNITS_COL] || '').replace(/^\(/, '-').replace(/[^-.\d]/g, '')) || 0) < 0;

				const newTransObj = {
					date: (Date.parse(t[DATE_COL]) / 1000).toString(),
					notes: notes,
					quantity: quantity.toString(),
					receiver: isSeller ? '' : email,
					receiverName: isSeller ? 'Buyer' : holderName,
					securityType: securityType,
					sender: isSeller ? email : '',
					senderName: isSeller ? holderName : '',
					shareClass: shareClass,
					sharePrice: (Number((t[PRICE_COL] || '').replace(/[^.\d]/g, '')) || 0).toString(),
					strikePrice: '0',
					holder: email,
					holderName: holderName,
				};

				// Add to clientData.equity for compatibility
				if (clientData.equity[shareClass]) {
					if (!clientData.equity[shareClass].transactions) clientData.equity[shareClass].transactions = [];
					clientData.equity[shareClass].transactions.push(newTransObj);
				}

				// Add to consolidated transactions
				userData.clients[client].consolidatedTransactions.push(newTransObj);
			});

			sessionStorage.setItem(client + '.Data', JSON.stringify(clientData));
			sessionStorage.setItem('user.Data', JSON.stringify(userData));
			sessionStorageSize();

			if (callback) callback();
		})
		.catch(function (err) {
			console.warn('loadConsolidatedAleraTransactions failed:', err);
			if (callback) callback();
		});
}

function enableUserEditControls(enable) {
	// No-op: GT and Events Trans icons now use in-modal loading spinners
	// instead of disabling the icons. Kept for call-site compatibility.
}

function autoPopulateUser(thisUserId) {
	//const thisUserId = userData["userid"];
	// Post updated values to the DB
	let userClientData = {
		investor: {
			complete: false,
			entity: false,
			income: false,
			netWorth: false,
			knowledge: false,
		},
		joinder: {
			complete: false,
		},
		shares: {},
		documents: [],
		tags: [],
	};
	axios
		.post(
			postURL,
			JSON.stringify({
				eventName: 'index.user_autopopulated',
				investor: thisUserId,
				userid: authedUser,
				client: client,
				cookie: cookie,
				clients: {
					[client]: userClientData,
				},
			})
		)
		.then(function (res) {
			console.log(res);

			if (window.analytics)
				window.analytics.track('Autopopulated New User', {
					userEmail: authedUser,
					workspace: client,
					newUser: thisUserId,
				});
		})
		.catch(function (err) {
			console.error(err);
		});

	if (!userData['clients']) userData['clients'] = { [client]: userClientData };
	else userData.clients[client] = userClientData;
	isAdmin = false;

	return userData;
}

function sessionStorageSize() {
	if (!isAdmin) return;
	let _ssTotal = 0,
		_xLen,
		_x;
	for (_x in sessionStorage) {
		if (!sessionStorage.hasOwnProperty(_x)) continue;
		_xLen = (sessionStorage[_x].length + _x.length) * 2;
		_ssTotal += _xLen;
	}
	let msg = 'Total SS = ' + (_ssTotal / 1024).toLocaleString('en-US', m0) + ' KB';
	let subj = 'Session Storage Size Alert (' + client + '.nthround.io)';
	if (_ssTotal > 6000000) {
		msg +=
			'.<br><br>Consider adding ' + client + ' to LARGE_CLIENTS array in returnShareholderData lambda function and to shouldCompressClient() in auth.js.';
		sendAlert('Session Storage Size Alert', msg);
	}
	console.log(msg);

	let _lsTotal = 0;
	for (_x in localStorage) {
		if (!localStorage.hasOwnProperty(_x)) continue;
		_xLen = (localStorage[_x].length + _x.length) * 2;
		_lsTotal += _xLen;
	}
	console.log('Total LS = ' + (_lsTotal / 1024).toLocaleString('en-US', m0) + ' KB');

	if ('webkitTemporaryStorage' in navigator) {
		navigator.webkitTemporaryStorage.queryUsageAndQuota(
			function (usedBytes, grantedBytes) {
				const remainingBytes = grantedBytes - usedBytes;
				const remainingMB = Math.round(remainingBytes / 1024 / 1024).toLocaleString();
				console.log(`Remaining browser memory: ${remainingMB} MB`);
			},
			function (error) {
				console.error('Error querying browser memory:', error);
			}
		);
	} else {
		console.error('navigator.webkitTemporaryStorage API is not supported in this browser');
	}

	subj = 'DynamoDB Record Size Alert (' + client + '.nthround.io)';

	let clientConfigSize = new TextEncoder().encode(JSON.stringify(clientData)).length / 1024;
	msg = 'ClientConfig = ' + clientConfigSize.toLocaleString('en-US', m0) + ' KB';
	let alertSize = 380;
	if (clientData?.clientconfig_alert_size) alertSize = Number(clientData.clientconfig_alert_size);
	if (clientConfigSize > alertSize) {
		msg +=
			'.<br><br>Consider moving ' +
			client +
			' to External Data with this procedure:<br><br> 1) Export Transactions from Ledgers to a local file<br> 2) Import this Transactions file using Company Profile';
		sendAlert(subj, msg);
	}
	console.log(msg);

	let userRecordSize = new TextEncoder().encode(JSON.stringify(userData)).length / 1024;
	msg = 'UserRecord = ' + userRecordSize.toLocaleString('en-US', m0) + ' KB';
	if (userRecordSize > 380) {
		msg += '.<br><br>This is unexpected. Please investigate user record in DynamoDB for ' + userData.userid + '.';
		sendAlert(subj, msg);
	}
	console.log(msg);

	let capTableSize = new TextEncoder().encode(JSON.stringify(capTableData)).length / 1024;
	msg = 'CapTable = ' + capTableSize.toLocaleString('en-US', m0) + ' KB';
	alertSize = NthUtil.sessionStore.shouldCompressClient(client) ? 6000 : 4000;
	if (clientData?.captable_alert_size) alertSize = Number(clientData.captable_alert_size);
	if (capTableSize > alertSize) {
		msg +=
			'.<br><br>Consider adding ' + client + ' to LARGE_CLIENTS array in returnShareholderData lambda function and to shouldCompressClient() in auth.js.';
		sendAlert(subj, msg);
	}
	console.log(msg);
}

// 5-27-2024 I'm not sure we are using this anymore...
function redirectToHome() {
	if (authedUser) console.log('This user does not have a complete record for this client');
	console.log('No userid so redirecting from /.');
	document.getElementById('shareholderDashboard').style.display = 'none';
	document.getElementById('footer').style.display = 'none';
	console.log('Redirecting - redirectToHome');
	// don't redirect for join and forms
	if (!pn.includes('/join') && !pn.includes('/forms')) {
		window.location.replace('/signin/');
	}
}

if (typeof Dropzone !== 'undefined' && Dropzone) {
	Dropzone.autoDiscover = false;
}

msieversion();
function msieversion() {
	let ua = navigator.userAgent.toLowerCase();
	ua = ua || navigator.userAgent;
	let match = /\b(MSIE |Trident.*?rv:|Edge\/)(\d+)/.exec(ua);

	if (match) {
		// If Internet Explorer, return version number
		console.log(parseInt(match[2]));
		window.location.replace('/not-supported');
	} else {
		// If another browser, return 0
		console.log('Supported browser');
		return;
	}
	return false;
}

function hideBanner() {
	// Hide the trading banner
	// TODO: Make this dynamic based on user data
	document.getElementById('welcomeBanner').style.display = 'none';
}

function getGreeting() {
	let greeting;
	let hours = new Date().getHours();
	if (hours >= 0 && hours < 12) greeting = 'morning';
	else if (hours >= 12 && hours < 17) greeting = 'afternoon';
	else if (hours >= 17 && hours < 24) greeting = 'evening';
	return greeting;
}

async function trackUser() {
	let timestamp = Math.floor(Date.now() / 1000).toString();
	axios
		.post(
			postURL,
			JSON.stringify({
				eventName: 'index.user_tracked',
				userid: authedUser,
				client: client,
				cookie: cookie,
				clients: {
					[client]: {
						lastActive: {
							viewed: pn,
							timestamp: timestamp,
							tracer: tracer,
						},
					},
				},
			})
		)
		.then(function (res) {
			if (res.data == 'Unknown') signOut();
			console.log('Data logged');
		})
		.catch(function (err) {
			console.error(err);
		});
}

function initializeComponents() {
	// Detect Safari (but not Chrome iOS or Firefox iOS)
	let isSafari =
		navigator.vendor &&
		navigator.vendor.indexOf('Apple') > -1 &&
		navigator.userAgent &&
		navigator.userAgent.indexOf('CriOS') == -1 &&
		navigator.userAgent.indexOf('FxiOS') == -1;

	let safari = `
		<ol>
			<li class="text-sm mb-2">Click the "Safari" menu, choose "Preferences" Make sure you have a Safari window open and active; you will see the "Safari" menu in the top left of your screen.</li>
			<li class="text-sm mb-2">Click on the "Privacy" tab. The contents of the Privacy tab screen will now appear.</li>
			<li class="text-sm">Choose the <span class="font-bold">"Allow from websites I visit"</span> cookies setting.</li>
		</ol>
	`;

	let chrome = `
		<ol>
			<li>Open Chrome preferences click on Settings, then Show Advanced Settings.</li>
			<li>Under Privacy, click on Content Settings.</li>
			<li>Make sure "Block third-party cookies and site data" is not checked.</li>
		</ol>
	`;

	// Handle expired / failed auth links via ?failurecode=...
	if (new URLSearchParams(window.location.search).get('failurecode')) {
		if (!authenticated) {
			duplicateModal.innerHTML = `
				<div class="p-1">
					<h2 class="text-2xl font-bold mt-0 mb-2">Oops, that link has expired</h2>
					<p class="mb-4 mt-0">It looks like you were trying to authenticate to access your account securely but the link you clicked had expired. </p>
					<p class="mb-4 mt-0">We use one-time authentication links to improve security and prevent unauthorized access to your account. Use the <span class="font-bold">Sign In</span> button to request another one-time log in.</p>
					<p class="mb-0">Not sure what to do next? <a href="mailto:support@nthround.com">Contact support</a></p>
				</div>
			`;

			M.Modal.getInstance(document.getElementById('duplicateNotice')).open();
		}
	}

	// Check if cookies are enabled
	if (!navigator.cookieEnabled) {
		cookieModal.innerHTML = `
			<div class="p-1">
				<h2 class="text-2xl font-bold mt-0 mb-2">This site uses cookies for privacy and security.</h2>
				<p class="mb-4 mt-0">We use cookies to provide secure authentication and to ensure you have a great experience. Please update your privacy settings using the instructions below to allow the use of cookies on this site.</p>
				${isSafari ? safari : chrome}
			</div>
		`;

		M.Modal.getInstance(document.getElementById('cookieNotice')).open();
	}

	// --- Route whitelist helper
	function isKnownRoute(pathname) {
		// Exact matches
		const exactRoutes = [
			'/',
			'/faq/',
			'/team/',
			'/trade/',
			'/enroll/',
			'/updates/',
			'/reports/',
			'/profile/',
			'/calendar/',
			'/campaign/',
			'/documents/',
			'/data-room/',
			'/supervisor/',
			'/admin/users/',
		];
		// Substring / prefix matches
		const prefixRoutes = [
			'/join',
			'/forms',
			'/rooms',
			'/survey',
			'/signin',
			'/portfolio',
			'/proxy-voting',
			'/campaign/view',
			'/admin/voting',
			'/admin/ledgers',
			'/admin/buy-sell',
			'/admin/calendar',
			'/admin/payments',
			'/admin/requests',
			'/admin/tax-forms',
			'/admin/dashboard',
			'/admin/surveying',
			'/admin/signatures',
			'/admin/users/new',
			'/admin/users/edit',
			'/investor-agreement',
		];

		if (exactRoutes.includes(pathname)) return true;
		return prefixRoutes.some((fragment) => pathname.includes(fragment));
	}

	// If this is NOT a known route, show loader (and historically trackUser)
	if (!isKnownRoute(pn)) {
		// trackUser();
		if (config.base_path !== client || window.location.hostname === 'localhost') {
			console.log('LOADER');
			if (loading) loading.innerHTML = `<div class="loader loader--style6 width-full m-auto center-align" title="5">${loader}</div>`;
		}
	}

	// Initialize Materialize components
	let autoOptions = {};
	let modalOptions = {};
	let sidenavOptions = {};
	let dropdownOptions = { coverTrigger: false, constrainWidth: false };
	let pickerOptions = { autoClose: true, showDaysInNextAndPreviousMonths: true };
	let boxOptions = {};

	let autoElems = document.querySelectorAll('.autocomplete');
	let tabElems = document.querySelectorAll('.tabs');
	let modalElems = document.querySelectorAll('.modal');
	let sideNavElems = document.querySelectorAll('.sidenav');
	let selectElems = document.querySelectorAll('.select');
	let dropdownElems = document.getElementById('userDropdownTrigger');
	let pickerElems = document.querySelectorAll('.datepicker');
	let materialBoxElems = document.querySelectorAll('.materialboxed');

	if (typeof M !== 'undefined' && M != null) {
		// Tabs: skip on certain routes, and also special-case proxy voting
		const noTabsRoutes = ['/', '/investor-agreement', '/investor-agreement/'];
		const proxyVotingRoutes = ['/proxy-voting', '/proxy-voting/'];

		if (proxyVotingRoutes.includes(pn)) {
			// proxy-voting page: no tabs to init (reserved for custom logic)
		} else if (!noTabsRoutes.includes(pn)) {
			if (tabElems && tabElems.length) M.Tabs.init(tabElems);
		} else console.log('no tabs to init');

		M.Modal.init(modalElems, modalOptions);

		if (typeof ensureTwoFactorModalNonDismissible === 'function') ensureTwoFactorModalNonDismissible();

		M.Sidenav.init(sideNavElems, sidenavOptions);
		M.FormSelect.init(selectElems);

		if (dropdownElems) M.Dropdown.init(dropdownElems, dropdownOptions);

		M.Autocomplete.init(autoElems, autoOptions);
		// M.Datepicker.init(pickerElems, pickerOptions);
		M.Materialbox.init(materialBoxElems, boxOptions);
	}
}

let CHART = false;
let SHOW_REF = false;
let SELECTED_ROW = false;
let EQUITY_LABELS = {};
// let CONTRACT_SYMBOL = 'NTH';

let bases = {};
let classes = [];
let distribs = {};
let contributions = {}; // Track gross contributions (before RoC reductions)
let returnsOfCapital = {}; // Track returns of capital separately
let incomeAllocations = {}; // Track income allocations (K-1 allocations that increase tax basis)
let accruals = {}; // Track gain/loss accruals (CER, JMH)
let netValue = 0;
let acctStats = {};
let shareKeys = [];
let statements = [];
let graphArray = [];
let latestValue = {};
let latestValuationDate = ''; // Formatted date string of most recent valuation
let landingImages = {};
let cumulativeBalance = 0;
let tvpiContributions = 0;
let tvpiDistributions = 0;

let wrongAcct;
let enrollCTA;
let enrollDone;
let alreadySigned;
let alreadyEnrolled;

let latestTVPI;
let tryShowGraph;
let selectedClass;
let hasEventNotes;
let hasEquityData;
let hasPriceHistory;
let hasEventHistory;
let paymentTransactions = [];
let currentShareClass = SHOW_AGGREGATE ? -1 : 0;
let reportHtmlExists = false;
// Unhappy with how something looks?  Search for "DRAGONS"
// 1) GRAPH LABELS AND ASSOCIATED VALUES [buildValuationArray()]
// 2) EQUITY HEADLINE [renderLanding()]
// 3) CLASS CARDS [renderEquityData() near bottom]
// 4) Graph legend [drawChart()]

function renderInvestorPortal() {
	/* NOTES
	This method scaffolds the Overview landing page:
	 - Greeting welcome message
	 - Reuest Information button
	 - New Investment materials
	 - Your Holdings section [renderEquityData()]
	 - Center headline and image/graph [renderLanding()]
	 - Events & Transactions [buildEventsAndTransactionsModal()]
	 - Account Statements [lookForFullAccountStatements(), buildUserCapitalAccountModal()]
	 - Account Summary [buildAccountSummary()]
	 - Your Documents section [previewDocuments()]
	 - Your Rooms section
	 - Recent Updates section
	*/

	// Guard against missing client data
	if (!userData?.clients?.[client]) {
		console.warn('renderInvestorPortal: no client data for', client);
		return;
	}

	// SHOW GREETING
	let welcome = `Good ${getGreeting()}, ${firstName(userData)}`;
	document.getElementById('welcomeMessage').innerHTML = welcome;

	const btnGroupLink = document.getElementById('btnGroupLink');
	const leaveMessageLink = document.getElementById('leaveMessageLink');
	let labelBuyBack = clientData.language?.label_buy_back || 'Request Stock Buy-Back';
	leaveMessageLink.innerHTML = clientData.language?.label_leave_message || 'Request Information';
	if (clientData.flags?.hide_leave_message) leaveMessageLink.classList.add('hidden');
	else if (REQUESTS) {
		const BUY_BACK = pn == '/' && clientData.flags?.buy_back_active;
		leaveMessageLink.innerHTML = BUY_BACK ? labelBuyBack : 'Request Share Transfer';
		leaveMessageLink.href = '#viewGiftTransferModal';
		btnGroupLink.classList.remove('hidden');
		const dropdownElems = document.querySelectorAll('.dropdown-trigger');
		M.Dropdown.init(dropdownElems, {
			constrainWidth: false, // Dropdown menu can be wider than the trigger
			coverTrigger: false, // Menu opens below the trigger
			alignment: 'right',
			container: document.querySelector('.btn-group'), // Ensures the dropdown aligns to the parent group
		});

		// Check if any transfer request has "status" == "Open"
		const transferRequests = userData.clients[client].transfer_requests || {};
		const hasOpenRequest = Object.values(transferRequests)
			.flat()
			.some((gift) => gift.status === 'Open');
		if (hasOpenRequest) {
			leaveMessageLink.classList.add('disabled');
			leaveMessageLink.innerHTML = BUY_BACK ? labelBuyBack.replace('Request', '').trim() + ' Request Pending' : 'Transfer Request Pending';
			btnGroupLink.classList.replace('bg-grey-darker', 'grey');
		} else {
			leaveMessageLink.addEventListener('click', function (e) {
				giftTransfer(userData.userid, true);
			});
		}
	}

	if (labelBuyBack.includes('Redemption')) document.getElementById('requestHeadline').innerHTML = 'Redemption Request Form';

	classes = Object.keys(clientData.equity).sort();

	/* NOTES ON OFFERING MATERIALS (and these may not be 100% correct!)
	In addition to the standard way we handle New Offering, we've built several other flows through this top-right element
	 - New Offering for many clients (standard flow)
	 - Proxy Voting for Asplundh (VOTING = true, use campaign.js, https://asplundh.nthround.io/campaign/view/?document=1647999364815)
	 - Signature for Alera (SIGNING = true, use signature.js, Amendment No. 1 to Second Amended and Restated LLC Agreement.pdf)
	 - Signature for PharmaJet (SIGNING = true, use signature.js, Statement of Lost, Destroyed or Returned Stock Certificate.pdf)
	 - Subscription for Alera (VOTING = true, use campaign.js)
	In other cases, like Buy/Sell, we use the equity class element (not the top right element)
	 - Buy/Sell for many clients (standard flow)
	 - Enrollment for Alera (ENROLL = true, use enroll.js, AMP Participation Agreement (investorId) for userid.pdf)
	 - Make Payments for JM Search (PAYING = true)
	*/

	// THIS IS A CRITICAL LINE - E&T fails without it !!
	AppState.shareholderData = userData;

	let allDone;
	let signedDoc;
	let userOwned = 0;
	let shClientData = userData.clients[client];
	let userShares = shClientData.shares || {};
	let userDocs = shClientData.documents || [];
	Object.keys(userShares).forEach((c) => {
		userOwned += campaignClasses.includes(c) && userShares[c]?.owned ? Number(userShares[c].owned) : 0;
	});
	if (campaignClasses.includes('ALL')) userOwned++; // everyone is included

	let canView;
	if (VIEWING) {
		if (userOwned) canView = true;
	}

	let canSurvey = false;
	if (SURVEYING) {
		// ----- Generic (e.g., Cerity) survey tag gating -----
		let surveyTagMismatch = false;
		if (campaignTags && campaignTags.length) {
			const userTags = userData?.clients?.[client]?.tags || [];
			if (!campaignTags.some((tag) => userTags.includes(tag))) surveyTagMismatch = true;
		}

		const baseLower = (s) =>
			String(s || '')
				.toLowerCase()
				.replace(/\+[^@]*?(?=@)/, '');
		const getAdvisorId = (obj) => obj?.investor?.advisor_userid || obj?.investor?.advisorUserid || obj?.advisor_userid || obj?.advisorUserid || null;

		const advisorMode = (campaignTags || []).includes('ADVISOR');
		const isAdvisor = userData.clients[client].tags?.includes('ADVISOR');
		const DOC0 = campaignDocuments?.[0] || 'Survey Response';
		const ownsAny = (shares) => Object.values(shares || {}).some((s) => Number(s?.owned || 0) > 0);
		const waitingModal = document.getElementById('waitingModal');
		const waitingHeader = document.getElementById('waitingHeader');

		if (advisorMode && isAdvisor) {
			// --- async enrich: advisor mode (load all members, filter to my advised accounts) ---
			let advisedData = JSON.parse(sessionStorage.getItem('advised.Data'));
			if (advisedData?.length) {
				recomputeAndRender(advisedData);
			} else {
				setTimeout(() => {
					M.Modal.getInstance(waitingModal).open();
					waitingHeader.innerHTML = 'Fetching your advised accounts...';
				}, 50);
				axios
					.get('https://api.nthround.io/portal/user/alias-names', {
						params: {
							eventName: 'index.surveying_advisees',
							alias_list: '[]',
							client: client,
							userid: authedUser,
							cookie: cookie,
						},
					})
					.then((res) => {
						const members = removeNthAdmins(res.data) || [];

						// prefer client-scoped status; fallback to top-level
						const advised = members.filter(
							(m) => m.status === userData.user_name || (userData.friendly_name && m.status === userData.friendly_name)
						);

						if (!advised.length) {
							M.Modal.getInstance(waitingModal).close();
							waitingHeader.innerHTML = 'One Moment please...';
							sessionStorage.setItem('advised.Data', '[]');
							return;
						}

						// Defensive check: filter out null/undefined before accessing userid
						const advisedList = advised.filter((m) => m?.userid).map((m) => m.userid); // ids only (adjust if API expects aliases)

						return axios
							.post('https://api.nthround.io/portal/user/alias-names', {
								eventName: 'index.surveying_advisedData',
								alias_list: advisedList,
								client: client,
								userid: authedUser,
								cookie: cookie,
							})
							.then((postRes) => {
								advisedData = (postRes.data || []).map((m) => ({
									accountId: m.userid,
									user_name: m.user_name,
									shares: m.shares || {},
									documents: m.documents || [],
									tags: m.tags || [],
									notes: m.notes || '',
								}));

								advisedData = sortByFFM(advisedData);
								sessionStorage.setItem('advised.Data', JSON.stringify(advisedData));
								recomputeAndRender(advisedData);
								M.Modal.getInstance(waitingModal).close();
								waitingHeader.innerHTML = 'One Moment please...';
							});
					})
					.catch((err) => {
						console.error(err);
						// leave as-is; nothing to add
					});
			}
		}

		if (!advisorMode) {
			// --- async enrich: trustee aliases in default mode ---
			let responsibleAccounts = [];

			// primary (safe, synchronous)
			const shClient = userData?.clients?.[client] || {};
			const primary = {
				accountId: userData.userid,
				user_name: (userData.user_name || '').replace(/\(.*\)/, '').trim(),
				shares: shClient.shares || {},
				documents: shClient.documents || [],
				tags: shClient.tags || [],
				advisorUserid: getAdvisorId(shClient) || getAdvisorId(userData) || null,
			};

			// default/trustee mode: start with primary immediately
			responsibleAccounts.push(primary);

			// --- pass #1 (immediate): compute from what we already have ---
			recomputeAndRender(responsibleAccounts);

			let workspaces = [];
			const aliases = shClient?.alias || [];
			if (!userData.userid.includes('+') && aliases.length) {
				aliases.forEach((a) => {
					if (a.includes('+') && baseLower(a) === baseLower(userData.userid)) workspaces.push(a);
				});
			}
			if (workspaces.length) {
				axios
					.post(
						'https://api.nthround.io/portal/user/alias-names',
						JSON.stringify({ eventName: 'index.surveying_advisor', alias_list: workspaces, client, userid: authedUser, cookie })
					)
					.then((res) => {
						let members = removeNthAdmins(res.data) || [];
						let enriched = responsibleAccounts.slice();
						workspaces.forEach((ws) => {
							const m = members.find((x) => x.userid === ws);
							if (m && baseLower(ws) === baseLower(userData.userid)) {
								enriched.push({
									accountId: m.userid,
									user_name: (m.user_name || '').replace(/\(.*\)/, '').trim(),
									shares: m.shares || {},
									documents: m.documents || [],
									tags: m.tags || [],
								});
							}
						});
						recomputeAndRender(enriched);
					})
					.catch(() => {
						// ignore; keep initial state
					});
			}
		}

		function recomputeAndRender(acctList) {
			let anyRequired = false;
			allDone = true;
			signedDoc = null;

			// If this campaign is tag-scoped and the user tags don't match, no survey at all.
			if (surveyTagMismatch) {
				canSurvey = false;
				return;
			}

			for (const acct of acctList || []) {
				if (!ownsAny(acct.shares)) continue;
				anyRequired = true;

				const doc = (acct.documents || []).find((d) => d?.name?.startsWith?.(DOC0) || d?.name?.includes?.(DOC0));
				const done = !!doc;

				if (!done) allDone = false;
				else if (!signedDoc) signedDoc = doc;
			}

			canSurvey = anyRequired;

			if (isFish) {
				const userClient = userData.clients[client];
				const userOwned = userClient.shares['C']?.owned || '0';
				const _statusVal = parseFloat(String(userClient?.status || ''));
				const _eligibleShares = !isNaN(_statusVal) && _statusVal > 0 ? _statusVal : Number(userOwned);
				const _ownedShares = Number(userOwned);
				const _availableShares = Math.max(0, _eligibleShares - _ownedShares);
				canSurvey = _availableShares > 0;

				const doc = (userClient.documents || []).find((d) => d?.name?.startsWith?.(DOC0) || d?.name?.includes?.(DOC0));
				if (!doc) allDone = false;
				else if (!signedDoc) signedDoc = doc;
			}

			if (canSurvey) showCTAPanel();
			if (allDone && canSurvey) {
				labelButtonCTA = labelButtonDone;
				if (signedDoc) campaignHref = '/documents/view/?document=' + signedDoc.id;
			}
		}
	}

	let canVote;
	if (VOTING) {
		if (AppState.campaignClasses.includes('ALL')) {
			canVote = true;
			// Check if user already signed the campaign document
			let doc = userDocs?.find((d) => d.name.includes(campaignDocuments[0]));
			if (doc) {
				signedDoc = doc;
				allDone = true;
			} else {
				allDone = false;
			}
		} else {
			// For each trust, for each voting claass: Own shares?  Alreadyy voted ?
			// Initialize ownership and document status for each class
			let userOwnedByClass = {};
			let alreadySignedByClass = {};
			campaignClasses.forEach((cls) => {
				userOwnedByClass[cls] = 0;
				alreadySignedByClass[cls] = false;
			});

			// Calculate ownership for trustee's account by class
			campaignClasses.forEach((cls) => {
				userOwnedByClass[cls] = Number(userShares[cls]?.owned || 0);
				if (userOwnedByClass[cls] > 0) canVote = true;
			});

			// Check if trustee already signed the document for each class
			campaignClasses.forEach((cls) => {
				let doc = userDocs?.find((d) => d.name.includes(campaignDocuments[0] + ((activeCampaign?.ballots?.length ?? 0) > 1 ? ` (${cls})` : '')));
				if (doc) {
					alreadySignedByClass[cls] = true;
					if (!signedDoc) signedDoc = doc; // Capture the first signed document
				}
			});

			allDone = true; // Assume all done initially
			campaignClasses.forEach((cls) => {
				let userDoneForClass = alreadySignedByClass[cls] || userOwnedByClass[cls] == 0;
				if (!userDoneForClass) {
					console.log('NOT SIGNED: ' + cls + ' class for trustee account');
					allDone = false; // If trustee hasn't signed and owns shares, set allDone to false
				}
			});
		}

		let isTrustee;
		let workspaces = [];
		if (!userData.userid.includes('+') && userData.clients[client].alias) {
			userData.clients[client].alias.forEach((c) => {
				if (c.includes('+') && c.replace(/\+[^@]*?(?=@)/, '') == userData.userid) {
					workspaces.push(c);
					isTrustee = true;
				}
			});
		}

		let aliasData = JSON.parse(sessionStorage.getItem('alias.Data'));
		if (aliasData?.length && aliasData[0]?.userid && userData?.userid) {
			wrongAcct = aliasData[0].userid != userData.userid.replace(/\+[^@]*?(?=@)/, '');
			if (wrongAcct) labelButtonCTA = 'Switch Account to Owner';
		}

		if (canVote) showCTAPanel(); // minimize flashing

		if (isTrustee) {
			// Trust accounts (workspaces)
			let accounts = []; // To store trust accounts info
			axios
				.post(
					'https://api.nthround.io/portal/user/alias-names',
					JSON.stringify({
						eventName: 'index.voting_trustee',
						alias_list: workspaces, // workspaces are trust accounts
						client: client,
						userid: authedUser,
						cookie: cookie,
					})
				)
				.then(function (res) {
					let members = removeNthAdmins(res.data);
					workspaces.forEach(function (acct, idx) {
						let shAccount = members.find((c) => c.userid == workspaces[idx]);
						if (shAccount) {
							let shShares = shAccount.shares || {};
							let ownershipByClass = {}; // Track ownership by class for this trust account

							// Calculate ownership for each class in this trust account
							campaignClasses.forEach((cls) => {
								ownershipByClass[cls] = Number(shShares[cls]?.owned || 0);
								if (ownershipByClass[cls] > 0) canVote = true; // Can vote if the trust owns shares
							});

							// If any ownership > 0, check for documents and collect account details
							let hasOwnership = Object.values(ownershipByClass).some((own) => own > 0);
							if (hasOwnership) {
								let baseEmail = acct.replace(/\+[^@]*?(?=@)/, '');
								if (baseEmail == userData.userid) {
									let shName = shAccount.user_name.replace(/\(.*\)/, '').trim();
									let shNotes = shAccount.notes;
									let shDocs = shAccount.documents || [];
									let signedByClass = {}; // Track document signing status by class for this trust account

									// Check if the trust account already signed the document for each class
									const multiDoc = (activeCampaign?.ballots?.length ?? 0) > 1;
									campaignClasses.forEach((cls) => {
										let doc = shDocs?.find(
											(d) =>
												(multiDoc && d.name.includes(`${campaignDocuments[0]} (${cls})`)) || // Multi-ballot: separate doc per class
												(!multiDoc && d.name.includes(campaignDocuments[0])) // Single ballot: one doc covers all classes
										);
										if (doc) signedByClass[cls] = true;
										if (doc && !signedDoc) signedDoc = doc; // Capture the first signed document
									});

									// Push trust account data to accounts array
									accounts.push({
										accountId: acct,
										name: shName,
										ownershipByClass: ownershipByClass,
										signedByClass: signedByClass,
										notes: shNotes,
										shares: shShares,
									});
								}
							}
						}
					});

					// After collecting all trust data, check if voting is done for all classes
					campaignClasses.forEach((cls) => {
						accounts.forEach((acc) => {
							let trustDoneForClass = acc.signedByClass[cls] || acc.ownershipByClass[cls] == 0;
							if (!trustDoneForClass) {
								console.log('NOT SIGNED: ' + cls + ' class for ' + acc.accountId);
								allDone = false; // If any trust account hasn’t signed for this class, set allDone to false
							}
						});
					});

					mightBeAllDone();
				});
		} else mightBeAllDone();

		function mightBeAllDone() {
			if (allDone) {
				console.log('All voting complete!');
				labelButtonCTA = labelButtonDone;
				if (signedDoc) campaignHref = '/documents/view/?document=' + signedDoc.id;
			} else console.log('Trustee still needs to vote.');
			if (canVote) showCTAPanel();
		}

		/* OLD CODE Can vote with shareholder.Data and previewEmail
		let userOwned = 0;
		let userShares = shClientData.shares || {};
		Object.keys(userShares).forEach(c => {userOwned += campaignClasses.includes(c) && userShares[c]?.owned ? Number(userShares[c].owned) : 0;});
		if ((userOwned) || isTrustee || isNTH) {
			let baseEmail = shareholderData.userid.replace(/\+[^@]*?(?=@)/, '');
			let shData = JSON.parse(sessionStorage.getItem('shareholder.Data'));
			let previewEmail = shData ? shData.userid : "";
			if (baseEmail == previewEmail || baseEmail == authedUser) canVote = true;
		} */

		/* SPECIAL SITUATIONS
		let declined = shDocs ? shDocs.find(d => d?.name?.includes("Issuance Decline")) : null;
		let subscribed = shDocs ? shDocs.find(d => d?.name?.includes("Common Equity Raise Subscription")) : null;
		if (ADDITIONAL_INV) labelButtonCTA = declined ? "Declined - Thank you" : subscribed ? "Subscribed - Thank you!" : "";

		if (isPHJ) {
			declined = shClientData.investor ? shClientData.investor.declinedDate : null;
			if (declined) {
				if (!alreadySigned) labelButtonCTA = "Consent Withheld";
				alreadySigned = true;
			} else {
				let shAgmt = shDocs ? shDocs.find(d => d.name.startsWith("PharmaJet Amended and Restated Shareholders Agreement - April 14 2023")) : null;
				if (!shAgmt) alreadySigned = null;
			}
		}
		*/
	}

	let canSign;
	if (SIGNING) {
		function signingTagsEligible(shClientData) {
			if (!campaignTags || !campaignTags.length) return true;
			const userTags = shClientData?.tags || [];
			return campaignTags.some((tag) => userTags.includes(tag));
		}

		function signingHasEligibleOwnership(shClientData) {
			let shares = shClientData?.shares || {};
			return campaignClasses.some((cls) => {
				if (cls == 'ALL') return Object.keys(shares).some((c) => Number(shares[c]?.owned || 0) > 0);
				return Number(shares[cls]?.owned || 0) > 0;
			});
		}

		function canThisAccountSign(shClientData) {
			if (!shClientData) return false;
			if (!signingTagsEligible(shClientData)) return false;
			if (!signingHasEligibleOwnership(shClientData)) return false;
			return true;
		}

		function signingRequiresPDF(userid, shClientData) {
			if (!canThisAccountSign(shClientData)) return false;

			let isBaseTrustee =
				!String(userid || '').includes('+') &&
				userData.clients[client]?.alias?.some((c) => c.includes('+') && c.replace(/\+[^@]*?(?=@)/, '') == userid);

			if (isBaseTrustee && !signingHasEligibleOwnership(shClientData)) return false;
			return true;
		}

		function signingDocsComplete(shClientData) {
			let docs = shClientData.documents || [];
			let signedFirst = docs.find((d) => d.name.includes(campaignDocuments[0]));
			let mustSignSecondDoc = !isAlera || shClientData?.investor?.election == 'Buy';
			let signedSecond = true;
			if (campaignDocuments.length > 1 && mustSignSecondDoc) signedSecond = docs.find((d) => d.name.includes(campaignDocuments[1]));
			if (signedFirst && !signedDoc) signedDoc = signedFirst;
			if (!signedDoc && signedSecond !== true) signedDoc = signedSecond;
			return !!signedFirst && !!signedSecond;
		}

		canSign = canThisAccountSign(shClientData);
		// Tagged accounts can see CTA; PDFs are required unless this is a zero-share base trustee.
		allDone = signingRequiresPDF(userData.userid, shClientData) ? signingDocsComplete(shClientData) : true;

		let isTrustee;
		let workspaces = [];
		if (!userData.userid.includes('+') && userData.clients[client].alias) {
			userData.clients[client].alias.forEach((c) => {
				if (c.includes('+') && c.replace(/\+[^@]*?(?=@)/, '') == userData.userid) {
					workspaces.push(c);
					isTrustee = true;
				}
			});
		}

		if (isTrustee) {
			axios
				.post(
					'https://api.nthround.io/portal/user/alias-names',
					JSON.stringify({
						eventName: 'index.signing_trustee',
						alias_list: workspaces,
						client: client,
						userid: authedUser,
						cookie: cookie,
					})
				)
				.then(function (res) {
					let members = removeNthAdmins(res.data);

					members.forEach((member) => {
						let baseEmail = member.userid.replace(/\+[^@]*?(?=@)/, '');
						if (baseEmail != userData.userid) return;

						let memberCanSign = canThisAccountSign(member);
						let memberRequiresPDF = signingRequiresPDF(member.userid, member);
						let memberDone = memberRequiresPDF ? signingDocsComplete(member) : true;

						if (memberCanSign) canSign = true;
						if (memberRequiresPDF && !memberDone) allDone = false;
					});

					mightBeAllDone();
				});
		} else {
			mightBeAllDone();
		}

		function mightBeAllDone() {
			if (allDone) {
				labelButtonCTA = labelButtonDone;
				if (signedDoc) campaignHref = '/documents/view/?document=' + signedDoc.id;
			}

			if (canSign) showCTAPanel();
		}
	}

	// MAY STILL NEED REVISITING
	let canTrade;
	if (ENROLL || PAYING || BUYSELL || clientData.trading_platform) {
		if (userOwned) {
			let baseEmail = shareholderData.userid.replace(/\+[^@]*?(?=@)/, '');
			let shData = JSON.parse(sessionStorage.getItem('shareholder.Data'));
			let previewEmail = shData ? shData.userid : '';
			if (baseEmail == previewEmail || baseEmail == authedUser) canTrade = true;
		}
		let userAchData = getUserDwollaData(userData);
		let bankConnected = userAchData && userAchData.primary_funding_source_id;
		if (isAlera || isMAZ) {
			let alreadyAgreed = userDocs?.find((d) => d.name.includes(campaignDocuments[0])); // subscription
			alreadySigned = userDocs?.find((d) => d.name.includes(campaignDocuments[1])); // AI questionnaire
			let userInvestor = userData.clients[client].investor;
			if (userInvestor?.signed && userInvestor?.election == 'Sell') alreadySigned = userInvestor.signed;
			let bankConnected = userData.ach_record?.bank_name;
			alreadyEnrolled = alreadyAgreed && alreadySigned && bankConnected;
			if (alreadySigned) signedDoc = alreadySigned.id;
			// NEVER_ENROLLED = !alreadyAgreed || !shClientData.investor;
			enrollCTA = 'Buy/Sell&ensp;&ndash;&ensp;Enroll Now';
			enrollDone = userInvestor && userInvestor.election ? 'Enrolled as ' + userInvestor.election + 'er' : 'No election made';
		} else {
			// isJMS
			alreadyEnrolled = bankConnected;
			NEVER_ENROLLED = !alreadyEnrolled && userOwned;
			enrollCTA = 'Sign up to Pay Online';
			enrollDone = 'Signed up - Thank you!';
		}
	}

	let canFund = FUNDING;

	/* SPECIAL SITUATIONS
	if (isMAZ) {
		canEnroll = true;
		alreadySigned = userDocs?.find(d => d.name.startsWith(campaignDocuments[0]));	// <-- ??????
		if (alreadySigned) {
			signedDoc = alreadySigned.id;
			labelButtonCTA = ENROLL ? "Enrolled - Thank You!" : "Signed - Thank You!";
		}
		declined = userDocs?.find(d => d?.name?.includes(campaignDocuments[1]));
		subscribed = userDocs?.find(d => d?.name?.includes(campaignDocuments[0]));		
	}

	// For PharmaJet, only current shareholders can see the investment materials
	if (isPHJ) {
		if (userOwned) canFund = true;
		alreadySigned = userDocs?.find(d => d.name.startsWith(campaignDocuments[0]));
		if (alreadySigned) {
			signedDoc = alreadySigned.id;
			labelButtonCTA = "Subscribed - Thank You!";
		}
	}
	*/

	// if (clientData.new_investment_materials && (clientData.currently_raising && (canTrade || !ENROLL) && (subscribed || !ADDITIONAL_INV) && SHOW_INVEST) || (canVote && VOTING) || (shouldSign && SIGNING))  {

	let okayToShow =
		(SURVEYING && canSurvey) || (VIEWING && canView) || (VOTING && canVote) || (ENROLL && canEnroll) || (SIGNING && canSign) || (FUNDING && canFund);
	if (okayToShow) showCTAPanel();

	// function needed for callback from trustee voting
	function showCTAPanel() {
		let material = clientData.new_investment_materials?.[0];
		let thumbnail = activeCampaign?.thumbnailUrl;
		if ((material || thumbnail) && clientData.currently_raising) {
			callToActionDetailsSmall = document.getElementById('callToActionDetailsSmall');
			callToActionDetailsLarge = document.getElementById('callToActionDetailsLarge');
			let thumbnailImage = new Image(); // preload image source
			if (thumbnail || (material.file && material.id)) {
				let CONF_DOCS = [];
				let CONFIDENTIAL_DOC = false;
				if (clientData.confidential_modal) {
					document.getElementById('ctaConfidentialityModal').innerHTML = DOMPurify.sanitize(
						clientData.confidential_modal.replace('confidentialityAccept', 'ctaConfidentialityAccept')
					);
					CONF_DOCS = ['CONFIDENTIAL']; // <-- This word in all caps triggers the modal
					if (CONF_DOCS.some((c) => material.title.includes(c))) CONFIDENTIAL_DOC = true;
					labelButtonCTA = CONFIDENTIAL_DOC ? 'View Confidential Material' : labelButtonCTA;
					campaignHref = CONFIDENTIAL_DOC ? '#ctaConfidentialityModal' : campaignHref;
				}
				// Give activeCampaign.thumbnailUrl priority over new_investment_materials
				if (thumbnail) thumbnailImage.src = formatPreview(thumbnail, 'h_240,c_fill/', null);
				else thumbnailImage.src = formatPreview(material.file, 'h_240,c_fill/', material.preview);
				let callToActionMarkup = `
					<h3 class="text-xl font-bold mt-7">${labelHeadingCTA}</h3>
					<div class="border-grey rounded p-1">
						<div class="center mb-0 update-link">
							<a ${CONFIDENTIAL_DOC ? 'class="modal-trigger"' : ''} ${wrongAcct ? `` : `href="${allDone ? '/documents/view/?document=' + signedDoc?.id : campaignHref || '/campaign/view/?document=' + material.id}"`}>
							<img src=${thumbnailImage.src} class="mb-0 card responsive-img" />
							</a>
						</div>
						${
							HIDE_CTA
								? ``
								: `
							<hr class="border-t-1">
							<a id="callToActionButton" href="${campaignHref || '/campaign/view/?document=' + material.id}"						
							class="${CONFIDENTIAL_DOC ? 'modal-trigger ' : ''} ${allDone || wrongAcct ? 'disabled ' : ''}invest btn waves-effect waves-light bg-grey-darker block">${labelButtonCTA}</a>
						`
						}
					</div>
				`;
				// Code for multiple new investment docs
				/* let callToActionMarkup = `
					<h3 class="text-xl font-bold">New Offering</h3>
					${clientData.new_investment_materials.map(material =>
						`
						<div class="border-grey rounded p-1">
							<div class="mb-0">
								<a href="/campaign/view/?document=${material.id}">
									<img src=${formatPreview(material.file, material.preview)} class="card responsive-img" />
									<div class="justify-between flex items-center mt-2">
										<h3 class="left text-lg text-black font-bold mt-0 mb-2">${material.title}</h3>
									</div>
								</a>
							</div>
						</div>
					`
					).join('')}
					<hr class="border-t-1">
					<a id="callToActionButton" href="/investor-agreement/" class="modal-trigger btn waves-effect waves-light bg-grey-darker block">Invest Now</a>
				`; */

				callToActionDetailsSmall.innerHTML = callToActionMarkup;
				callToActionDetailsLarge.innerHTML = callToActionMarkup;

				let ctaConfidentialityAccept = document.getElementById('ctaConfidentialityAccept');
				if (ctaConfidentialityAccept)
					ctaConfidentialityAccept.addEventListener('click', () => {
						let ctaConfidentialityModal = document.getElementById('ctaConfidentialityModal');
						M.Modal.getInstance(ctaConfidentialityModal).close();
						window.location.replace('/campaign/view/?document=' + material.id); // CONFIDENTIAL AMP CONFIDENTIAL_DOC - The Alera Market Place
					});

				if (!SHOW_INVEST) {
					callToActionDetailsSmall.classList.add('hidden');
					callToActionDetailsLarge.classList.add('hidden');
				}

				if (READY_TO_PAY) {
					// && userData.ach_record?.bank_name) {
					console.log('***Speed Test - BEFORE axios.get() : ' + (Date.now() - refreshTime) + ' ms');
					axios
						.get('https://api.nthround.io/portal/auth/get-payments', {
							params: {
								participant: userData.userid,
								userid: authedUser,
								client: client,
								cookie: cookie,
							},
						})
						.then(function (response) {
							console.log('***Speed Test - AFTER axios.get() : ' + (Date.now() - refreshTime) + ' ms');
							let payments = response.data;
							let payment;
							if (payments.length)
								payment = payments.find(
									(p) =>
										p.achId.startsWith(client + '-from-' + userData.userid + '-for-') ||
										(p.achId.startsWith(client + '-WIRE-from-' + userData.userid + '-for-') && p.achStatus == 'SENT')
								);
							if (payment) {
								let investButtons = document.querySelectorAll('a.invest');
								investButtons[0].classList.add('disabled');
								investButtons[1].classList.add('disabled');
								investButtons[0].innerText = 'Paid - Thank You!';
								investButtons[1].innerText = 'Paid - Thank You!';
							}
						})
						.catch(function (err) {
							console.error(err);
						});
				}
			}
		} else {
			if (callToActionDetailsSmall) callToActionDetailsSmall.innerHTML = ``;
			if (callToActionDetailsLarge) callToActionDetailsLarge.innerHTML = ``;
		}
	}

	// SHOW CLASS CARDS, HEADLINE, AND CENTER GRAPH/IMAGE
	renderEquityData(); // <-- This establishes shareKeys

	if (isJMS) {
		document.getElementById('exportPaymentDetails').addEventListener('click', function (e) {
			e.preventDefault();
			export_table_to_excel('paymentDetailsSortTable', 'Payment Details (' + new Date().toLocaleString() + ').csv');
		});
	}

	// SHOW MODAL BUTTONS
	let hasEventsOrTransactions;
	const activeUserIds = getActiveUserIds(userData);
	shareKeys.forEach(function (key) {
		if (!hasEventsOrTransactions) {
			let events = userData.clients[client].shares[key].events;
			if (events && events.length) hasEventsOrTransactions = true;
			if (clientData.equity[key] && clientData.equity[key].transactions) {
				let transactions = clientData.equity[key].transactions.filter((trans) => isUserTransaction(trans, activeUserIds));
				if (transactions && transactions.length) hasEventsOrTransactions = true;
			}
		}
	});

	// For consolidated view, also check consolidatedTransactions
	if (isConsolidatedView(userData) && userData.clients[client].consolidatedTransactions?.length) {
		hasEventsOrTransactions = true;
	}

	// Check if EXTERNAL_DATA clients actually have transactions before showing the icon
	if (EXTERNAL_DATA) {
		let hasActualTransactions = false;
		shareKeys.forEach(function (key) {
			if (!hasActualTransactions && clientData.equity[key] && clientData.equity[key].transactions) {
				let transactions = clientData.equity[key].transactions.filter((trans) => isUserTransaction(trans, activeUserIds));
				if (transactions && transactions.length) hasActualTransactions = true;
			}
		});
		if (hasActualTransactions) hasEventsOrTransactions = true;
	}

	let viewEventsIconLarge = document.getElementById('viewEventsTransIconLarge');
	let viewEventsIconSmall = document.getElementById('viewEventsTransIconSmall');
	let viewEventsReportIcon = document.getElementById('viewEventsTransReportIconLarge');
	if (hasEventsOrTransactions) {
		if (viewEventsIconLarge && !clientData.flags?.enable_dashboard) {
			viewEventsIconLarge.classList.remove('hidden');
			if (!addedBuildETListenerAlready)
				viewEventsIconLarge.addEventListener('click', function (e) {
					buildEventsAndTransactionsModal();
				});
		} else viewEventsIconLarge.classList.add('hidden');
		if (viewEventsIconSmall) {
			if (!clientData.flags?.enable_dashboard) viewEventsIconSmall.classList.remove('hidden');
			else viewEventsIconSmall.classList.add('hidden');
			if (!addedBuildETListenerAlready)
				viewEventsIconSmall.addEventListener('click', function (e) {
					buildEventsAndTransactionsModal();
				});
		}

		if (viewEventsReportIcon && isMAR) {
			// viewEventsReportIcon.classList.remove('hidden');
			if (!addedBuildETListenerAlready)
				viewEventsReportIcon.addEventListener('click', function (e) {
					buildEventsAndTransactionsModal();
				});
		}
		addedBuildETListenerAlready = true;
	} else {
		if (viewEventsIconLarge) viewEventsIconLarge.classList.add('hidden');
		if (viewEventsIconSmall) viewEventsIconSmall.classList.add('hidden');
		if (viewEventsReportIcon) viewEventsReportIcon.classList.add('hidden');
	}
	let scenarioModelingIconLarge = document.getElementById('scenarioModelingIconLarge');
	let scenarioModelingIconSmall = document.getElementById('scenarioModelingIconSmall');
	if (clientData.flags && clientData.flags.show_scenario_modeling) {
		if (scenarioModelingIconLarge) {
			scenarioModelingIconLarge.classList.remove('hidden');
			scenarioModelingIconLarge.addEventListener('click', function (e) {
				buildScenarioModelingModal();
			});
		}
		if (scenarioModelingIconSmall) {
			scenarioModelingIconSmall.classList.remove('hidden');
			scenarioModelingIconSmall.addEventListener('click', function (e) {
				buildScenarioModelingModal();
			});
		}
	} else {
		if (scenarioModelingIconLarge) scenarioModelingIconLarge.classList.add('hidden');
		if (scenarioModelingIconSmall) scenarioModelingIconSmall.classList.add('hidden');
	}
	let viewValuationsIconLarge = document.getElementById('viewValuationsIconLarge');
	let viewValuationsIconSmall = document.getElementById('viewValuationsIconSmall');
	if (isPSS) {
		viewValuationsIconLarge.title = 'Cash Payouts Projection';
		viewValuationsIconSmall.title = 'Cash Payouts Projection';
		document.getElementById('DollarTimeIconLarge').src = './../../resources/assets/cash_projection_icon.bmp';
		document.getElementById('DollarTimeIconSmall').src = './../../resources/assets/cash_projection_icon.bmp';
	}
	if (isASP || isPSS) {
		if (viewValuationsIconLarge) {
			viewValuationsIconLarge.classList.remove('hidden');
			viewValuationsIconLarge.addEventListener('click', function (e) {
				if (isASP) renderValuationsTable();
				else if (isPSS) renderCashFlowTable();
			});
		}
		if (viewValuationsIconSmall) {
			viewValuationsIconSmall.classList.remove('hidden');
			viewValuationsIconSmall.addEventListener('click', function (e) {
				if (isASP) renderValuationsTable();
				else if (isPSS) renderCashFlowTable();
			});
		}
	} else if (clientData.statement_filename) {
		let stmt = userData.clients[client].documents?.find((d) => d.name.startsWith(clientData.statement_filename));
		if (stmt && !clientData.flags?.enable_dashboard) {
			viewCapAcctIconLarge.classList.remove('hidden');
			viewCapAcctIconLarge.title = 'Account Statement';
			document.getElementById('viewCapAcctTriggerLarge')?.classList.remove('modal-trigger');
			viewCapAcctIconLarge.addEventListener('click', function (e) {
				window.location.replace(`/documents/view/?document=${stmt.id}`);
			});
		}
	} else {
		if (viewValuationsIconLarge) viewValuationsIconLarge.classList.add('hidden');
		if (viewValuationsIconSmall) viewValuationsIconSmall.classList.add('hidden');
	}

	function renderValuationsTable() {
		let allValuations = clientData.equity['C'].valuations.sort((a, b) => (Number(a.date) > Number(b.date) ? -1 : 1));

		let emptyState = `<div class="bg-grey-lightest rounded border-grey grey-text center-align p-2">No stock prices have been added yet</div>`;
		let valuationsMarkup = `
			${
				allValuations.length
					? `
				<div class="table-wrap card px-4">
					<div class="sm-hz-scroll-no-margin">
						<table id="valuationsSortTable" class="highlight table-content">
							<thead>
								<tr>
									<th>Date</th>
									<th>Description</th>
									<th class="center-align">Share Price</th>
								</tr>
							</thead>
							<tbody>
								${allValuations
									.map(
										(valuation, index) =>
											`
									<tr>
										<td class="text-sm" data-sort='${valuation.date}'>
											${moment.unix(valuation.date).format('MMM D, YYYY')}
										</td>
										<td class="text-sm">
											${valuation.notes}
										</td>
										<td class="text-sm center-align" data-sort='${Number(valuation.sharePrice.replace(/[^.\d]/g, ''))}'>
											${Number(valuation.sharePrice.replace(/[^.\d]/g, '')).toLocaleString('en-US', usp)}
										</td>
									</tr>
									`
									)
									.join('')}
							</tbody>
						</table>
					</div>
				</div>
			`
					: `${emptyState}`
			}`;

		document.getElementById('valuationsTableModal').innerHTML = valuationsMarkup;
		let table = document.getElementById('valuationsSortTable');
		if (table) {
			let sort = new Tablesort(table, { descending: true });
		}

		if (!addedExportValuationsListenerAlready) {
			addedExportValuationsListenerAlready = true;
			document.getElementById('exportValuations').addEventListener('click', function (e) {
				e.preventDefault();
				export_table_to_excel('valuationsSortTable', clientData.company_name + ' Price History as of ' + new Date().toLocaleString() + '.csv');
			});
		}
	}

	function renderCashFlowTable() {
		function getLatestPriceForYear(awardYear) {
			const valuations = clientData.equity?.[awardYear]?.valuations;
			if (!Array.isArray(valuations) || valuations.length === 0) return null;
			const latest = valuations.reduce((a, b) => (Number(a.date) > Number(b.date) ? a : b));
			return Number(latest.sharePrice);
		}

		const shares = userData.clients[client].shares;
		const cashFlowRows = [];
		const columnYears = new Set();
		let totalsByYear = {};
		const priceNote = [];

		for (let year in shares) {
			const entry = shares[year];
			const units = Number(entry.owned);
			if (!units || units <= 0) continue;

			const pricePerUnit = getLatestPriceForYear(year);
			if (!pricePerUnit) continue;

			const value = +(units * pricePerUnit).toFixed(2);
			const vestYear = parseInt(year) + 4;
			const payout = +(value / 3).toFixed(2);

			priceNote.push(`${year}: $${pricePerUnit}`);

			let row = {
				awardYear: year,
				units,
				value,
				distributions: {},
			};

			for (let i = 0; i < 3; i++) {
				let payoutYear = vestYear + i;
				row.distributions[payoutYear] = payout;
				columnYears.add(payoutYear);
				totalsByYear[payoutYear] = (totalsByYear[payoutYear] || 0) + payout;
			}

			cashFlowRows.push(row);
		}

		// Sort years for consistent column order
		const sortedYears = Array.from(columnYears).sort((a, b) => a - b);
		const totalUnits = cashFlowRows.reduce((sum, row) => sum + row.units, 0);

		if (!cashFlowRows.length) {
			document.getElementById('valuationsTableModal').innerHTML = `
				<div class="bg-grey-lightest rounded border-grey grey-text center-align p-2">
					No MIP awards or projected cash payouts found
				</div>`;
			return;
		}

		const DISCLAIMER =
			'These projections are based on certain assumptions and estimates, which may not prove to be accurate. Actual results may differ materially from the projections. We make no guarantee that the projections will be achieved.';
		document.getElementById('valuationsDisclaimer').innerHTML = DOMPurify.sanitize(clientData.disclaimer || DISCLAIMER);

		const uniquePrices = Array.from(new Set(priceNote.map((p) => p.split('$')[1])));
		const singlePrice = uniquePrices.length === 1 ? uniquePrices[0] : null;

		document.getElementById('valuationsModalHeading').innerHTML =
			`Cash Payouts by Year <span class="text-secondary text-lg font-md">` +
			(singlePrice ? `(rounded dollars – $${singlePrice}/unit)` : `(rounded dollars – ${priceNote.join(', ')})`) +
			`</span>`;

		let tableHTML = `
			<div class="table-wrap card px-4">
				<div style="overflow-x: auto; width: 100%;">
					<table id="payoutSortTable" class="highlight table-content text-xs nowrap">
						<thead>
							<tr>
								<th class="text-xs center-align" style="width: 60px;">Award Year</th>
								<th class="text-xs right-align">Units</th>
								<th class="text-xs right-align">Value ($)</th>
								${sortedYears.map((y) => `<th class="text-xs right-align">${y}</th>`).join('')}
							</tr>
						</thead>
						<tbody>
							${cashFlowRows
								.map(
									(row) => `
								<tr>
									<td class="text-xs center-align">${row.awardYear}</td>
									<td class="text-xs right-align">${row.units.toLocaleString()}</td>
									<td class="text-xs right-align">${row.value.toLocaleString('en-US', m0)}</td>
									${sortedYears
										.map(
											(y) => `
										<td class="text-xs right-align">${row.distributions[y] ? row.distributions[y].toLocaleString('en-US', m0) : ''}</td>
									`
										)
										.join('')}
								</tr>`
								)
								.join('')}
							<tr>
								<td class="text-xs center-align"><b>Total</b></td>
								<td class="text-xs right-align"><b>${totalUnits.toLocaleString()}</b></td>
								<td class="text-xs right-align"><b>${cashFlowRows.reduce((a, b) => a + b.value, 0).toLocaleString('en-US', m0)}</b></td>
								${sortedYears
									.map(
										(y) => `
									<td class="text-xs right-align"><b>${(totalsByYear[y] || 0).toLocaleString('en-US', m0)}</b></td>
								`
									)
									.join('')}
							</tr>
						</tbody>
					</table>
				</div>
			</div>
		`;

		document.getElementById('valuationsTableModal').innerHTML = tableHTML;

		// Export logic (same as your other table)
		if (!addedExportValuationsListenerAlready) {
			addedExportValuationsListenerAlready = true;
			document.getElementById('exportValuations').addEventListener('click', function (e) {
				e.preventDefault();
				export_table_to_excel('payoutSortTable', clientData.company_name + ' Cash Payouts Projection as of ' + new Date().toLocaleString() + '.csv');
			});
		}
	}

	// SHOW ACCOUNT SUMMARY - flag-based with fallback to legacy logic
	let userClient = userData.clients[client];
	const statementFlags = clientData.flags || {};
	if (clientData.equity && userClient.shares && Object.keys(userClient.shares).length > 0) {
		if (Object.keys(userClient.shares).some((s) => Number(userClient.shares[s]?.owned) > 0)) {
			// Account Summary - flag with fallback to legacy logic
			const showAccountSummary = statementFlags.show_account_summary ?? (!HAS_STMTS && classes.length > 4 && !clientData.statement_filename && !isMAR);
			if (showAccountSummary) {
				buildAccountSummary(clientData, userData, isMAR, addedAccountSummaryListenersAlready);
			}

			// Account Statement - flag with fallback to legacy logic
			const showAccountStatement = statementFlags.show_account_statement ?? (!HAS_STMTS && (isBB || isLT || isMAR));
			if (showAccountStatement) {
				buildAccountStatement();
			}

			// Transaction Statement - flag with fallback to legacy logic
			const showTransactionStatement = statementFlags.show_transaction_statement ?? (!HAS_STMTS && isMAR);
			if (showTransactionStatement) {
				buildTransactionStatement();
			}
		}
	}

	function buildAccountStatement() {
		/* NOTES
		This generates a pdf of the center graph, current holdings, and E&T tables
		It uses Regex to strip out and relace unwanted html
		*/
		const fundMethods = ['fund_investor_capital_account', 'tvpi_fund'];
		const isFundStatement = fundMethods.includes(clientData.flags?.default_value_method);
		document.getElementById('viewCapAcctTriggerLarge')?.classList.remove('modal-trigger');
		document.getElementById('viewCapAcctTriggerSmall')?.classList.remove('modal-trigger');

		// Check if account statement is explicitly enabled via flag
		const accountStatementEnabled = clientData.flags?.show_account_statement === true;

		//MARVIN CUSTOM REPORT
		if (isMAR) {
			viewEventsTransReportIconLarge.classList.remove('hidden');
			viewEventsTransReportIconSmall.classList.remove('hidden');
			viewEventsTransReportIconLarge.title = 'Account Statement';
			viewEventsTransReportIconSmall.title = 'Account Statement';
			document.getElementById('viewCapAcctMarvinTriggerLarge')?.classList.remove('modal-trigger');
			document.getElementById('viewCapAcctMarvinTriggerSmall')?.classList.remove('modal-trigger');
		} else if (accountStatementEnabled || isBW || isSheetz || isFundStatement || !clientData.flags?.enable_dashboard) {
			viewCapAcctIconLarge.classList.remove('hidden');
			viewCapAcctIconSmall.classList.remove('hidden');
			viewCapAcctIconLarge.title = 'Account Statement';
			viewCapAcctIconSmall.title = 'Account Statement';
			// For BW, replace icon with text link
			if (isBW) {
				viewCapAcctIconLarge.innerHTML = '<a href="#" class="link-accent text-sm">Account Statement</a>';
				viewCapAcctIconSmall.innerHTML = '<a href="#" class="link-accent text-sm">Account Statement</a>';
			}
		}

		if (!addedAccountSummaryListenersAlready) {
			addedAccountSummaryListenersAlready = true;
			narrow = screen.width < 600;
			if (isMAR) {
				if (narrow) {
					viewEventsTransReportIconSmall.addEventListener('click', (e) => renderMarvinReportForm()); //THIS IS WHERE IT MATTERS FOR REPORT
					viewCapAcctIconSmall.addEventListener('click', (e) => renderTransactionStatement());
				} else {
					viewEventsTransReportIconLarge.addEventListener('click', (e) => renderMarvinReportForm());
					viewCapAcctIconLarge.addEventListener('click', (e) => renderTransactionStatement());
				}
			} else if (isBW) {
				// Barry-Wehmiller custom account statement
				if (narrow) viewCapAcctIconSmall.addEventListener('click', (e) => renderBWAccountStatement());
				else viewCapAcctIconLarge.addEventListener('click', (e) => renderBWAccountStatement());
			} else if (isSheetz) {
				// Sheetz uses the standard PDF statement (brand_color provides Sheetz red)
				if (narrow) viewCapAcctIconSmall.addEventListener('click', () => renderAccountStatementPdf());
				else viewCapAcctIconLarge.addEventListener('click', () => renderAccountStatementPdf());
			} else if (isFundStatement) {
				// Fund investor account statement (fund_investor_capital_account, tvpi_fund)
				if (narrow) viewCapAcctIconSmall.addEventListener('click', () => renderFundAccountStatement());
				else viewCapAcctIconLarge.addEventListener('click', () => renderFundAccountStatement());
			} else {
				// Default: structured PDF statement (colors from brand_color or app accent)
				if (narrow) viewCapAcctIconSmall.addEventListener('click', () => renderAccountStatementPdf());
				else viewCapAcctIconLarge.addEventListener('click', () => renderAccountStatementPdf());
			}
		}

		let reportFilterDate;
		let generateButton;
		let radioButtons;
		let viewReportDataModal;
		let customDateModal;
		let dataModalInstance;
		let customDateModalInstance;
		let stmtHeadline = '';

		// Define the event handler function outside renderMarvinReportForm
		function handleRadioChange(e) {
			reportFilterDate = e.target.value;
			console.log('Selected date range:', reportFilterDate);
			generateButton.disabled = false;
		}

		async function renderMarvinReportForm() {
			reportFilterDate;

			// reportDateFilter = document.getElementById('reportDateFilter');
			generateButton = document.getElementById('generateReportButton');
			radioButtons = document.querySelectorAll('input[name="dateRange"]');
			viewReportDataModal = document.getElementById('viewReportDataModal');
			customDateModal = document.getElementById('customDateModal');

			// Initialize Materialize modals
			dataModalInstance = M.Modal.init(viewReportDataModal);
			customDateModalInstance = M.Modal.init(customDateModal);
			M.Modal.getInstance(document.getElementById('viewReportDataModal')).open();

			// Reset reportDateFilter when the modal is opened
			viewReportDataModal.addEventListener('modal:open', () => {
				generateButton.disabled = true;
				radioButtons.forEach((radio) => (radio.checked = false));
			});

			// Remove existing event listeners and add new ones for radio buttons
			radioButtons.forEach((radio) => {
				radio.removeEventListener('change', handleRadioChange);
				radio.addEventListener('change', handleRadioChange);
			});

			// Remove existing event listener and add a new one for form submission
			const form = document.getElementById('viewReportDataForm');
			form.removeEventListener('submit', handleFormSubmit);
			form.addEventListener('submit', handleFormSubmit);

			// Add event listener for the "Generate" button in the custom date range modal
			const generateCustomDateRangeButton = document.getElementById('generateCustomDateRange');
			generateCustomDateRangeButton.removeEventListener('click', handleCustomDateRange);
			generateCustomDateRangeButton.addEventListener('click', handleCustomDateRange);
		}

		function handleFormSubmit(e) {
			e.preventDefault();
			if (reportFilterDate === 'custom') {
				customDateModalInstance.open();
			} else {
				processDateFilter(reportFilterDate);
			}
		}

		function handleCustomDateRange() {
			const startDate = document.getElementById('startDate').value;
			const endDate = document.getElementById('endDate').value;
			if (customDateModalInstance) customDateModalInstance.close();
			setTimeout(() => {
				processDateFilter('custom', startDate, endDate);
			}, 0);
		}

		function processDateFilter(filterType, startDate = null, endDate = null) {
			const reportDataModal = document.getElementById('viewReportDataModal');
			if (reportDataModal && M.Modal.getInstance(reportDataModal)) {
				M.Modal.getInstance(reportDataModal).close();
			}
			stmtHeadline = 'Statement';
			let date = new Date();
			switch (filterType) {
				case 'mtd':
					startDate = new Date(date.getFullYear(), date.getMonth(), 1);
					endDate = new Date();
					stmtHeadline += ` for ${moment(startDate).format('MMMM D')} through ${moment(endDate).format('MMMM D, YYYY')}`;
					break;
				case 'qtd':
					const currentMonth = date.getMonth();
					const currentYear = date.getFullYear();
					// Determine the start of the current quarter
					let quarterStartMonth;
					if (currentMonth < 3) {
						quarterStartMonth = 0; // January
						stmtHeadline += ` for Quarter 1 `;
					} else if (currentMonth < 6) {
						quarterStartMonth = 3; // April
						stmtHeadline += ` for Quarter 2 `;
					} else if (currentMonth < 9) {
						quarterStartMonth = 6; // July
						stmtHeadline += ` for Quarter 3 `;
					} else {
						quarterStartMonth = 9; // October
						stmtHeadline += ` for Quarter 4 `;
					}

					startDate = new Date(currentYear, quarterStartMonth, 1);
					endDate = new Date(currentYear, currentMonth, date.getDate());
					stmtHeadline += `(${startDate.toLocaleDateString()} - ${endDate.toLocaleDateString()})`;
					break;
				case 'ytd':
					startDate = new Date(date.getFullYear(), 0, 1);
					endDate = new Date();
					stmtHeadline += ` for ${moment(startDate).format('MMMM D')} through ${moment(endDate).format('MMMM D, YYYY')}`;
					break;
				case 'custom':
					startDate = startDate;
					endDate = endDate;
					if (startDate.length && endDate.length) {
						stmtHeadline += ` for ${startDate} through ${endDate}`;
					} else stmtHeadline = `Statement as of ${date.getFullYear()}`;
					break;
			}
			reportStartDate = startDate;
			reportEndDate = endDate;

			// Render statement after a small delay to ensure modals are closed
			setTimeout(() => {
				renderPrintableStatement(startDate, endDate);
			}, 0);
		}

		async function renderPrintableStatement(startDate = null, endDate = null) {
			const GENERATE_PDF = clientData.flags?.show_account_statement ?? (isBB || isLT || isMAR);
			if (GENERATE_PDF) M.Modal.getInstance(document.getElementById('statementWaitState')).open();

			/**************************  Report Graph HTML - START  **************************/
			let priceLabel = document.getElementById('priceLabel');
			let lastTraded = document.getElementById('lastTraded');

			let hide_price_label = isLT;

			if (hide_price_label) {
				priceLabel.style.display = 'none';
				lastTraded.style.display = 'none';
			}
			let landingDiv = document.getElementById('landingDiv').cloneNode(true);
			let selectedWrap = landingDiv.querySelector('#switchShareClass');
			selectedWrap.setAttribute('hidden', true);
			let secondChildElement = landingDiv.children[1];
			let lastChildElement = secondChildElement.lastElementChild;
			secondChildElement.removeChild(lastChildElement);
			let showImageOrGraphNode = landingDiv.querySelector('#showImageOrGraph');
			showImageOrGraphNode.style.display = 'none';
			landingDiv.style.height = '210px !important';

			/**************************************  END  ************************************/

			/**************************  Report Nascar Details - START  **************************/

			let userEquityTotalsTable = document.getElementById('userEquityTotalsTable');
			let userEquityTotals = userEquityTotalsTable.cloneNode(true);

			if (isLT) {
				function addStylingToDivs(element) {
					let divs = element.querySelectorAll('.stat-item-3');
					divs.forEach(function (div) {
						div.style.cssText = `
						font-size: 12px; 
					`;
					});

					let hdline = element.querySelectorAll('.pdf-text-secondary');
					hdline.forEach(function (line) {
						line.style.cssText = `
						font-weight: bold !important; 
						text-decoration: underline;
					`;
					});

					let pdfText = element.querySelectorAll('.pdf-text');
					pdfText.forEach(function (line) {
						line.style.cssText = `
						font-size: 12px !important;
					`;
					});

					let sharePricePerShareCard = element.querySelectorAll('.stats-card');
					sharePricePerShareCard.forEach(function (line) {
						line.style.cssText = `
						display:flex; flex-direction: row; /* This ensures the items are laid out in a row */
						justify-content: space-between; /* This adds space between the items */
						border-style: none; border-collapse: collapse; max-width: 100% !important; border-radius: 20px !important; background-color: #f0f4f8 !important;
					`;
					});
				}
				addStylingToDivs(userEquityTotals);
			}

			/**************************  Report Share Details - START  **************************/

			let shareClassDetails = document.getElementById('shareClassDetails').cloneNode(true);
			let shareCardNodes = shareClassDetails.querySelectorAll('.share-card');
			let priceLabelReport = shareClassDetails.querySelector('#priceLabelReport');
			if (priceLabelReport) priceLabelReport.style.cssText = `margin-top: -5px !important`;
			if (shareCardNodes.length > 0) {
				let shareCardNode = shareCardNodes[0];

				if (isMAR) {
					for (let i = 0; i < shareCardNodes.length; i++) {
						let card = shareCardNodes[i];
						card.style.cssText = `
							font-size: 12px;
							border-style: none;
							border-collapse: collapse;
							max-width: 35% !important;
							border-radius: 20px !important;
							background-color: #f0f4f8 !important;
						`;

						let divs = Array.from(card.querySelectorAll('div'));
						let owningDiv = divs.find((div) => div.textContent.includes('You Own:'));
						let balanceDiv = divs.find((div) => div.textContent.includes('Balance:'));
						if (owningDiv) updateCardsForReport(owningDiv);
						if (balanceDiv) updateCardsForReport(balanceDiv);

						function updateCardsForReport(element) {
							// remove unwanted wording + style
							element.textContent = element.textContent.replace('You Own:' || 'Balance:', '');
							element.style.marginLeft = '12px';
							element.style.marginTop = '0px';
							element.style.marginBottom = '6px';
						}

						let selectedEls = shareClassDetails.querySelectorAll('.selectedEl');
						const selectedElArray = Array.from(selectedEls);
						selectedElArray.forEach((el) => {
							el.setAttribute('hidden', true);
						});
						let equityNameEls = shareClassDetails.querySelectorAll('.equityName');
						const equityNameElArray = Array.from(equityNameEls);

						let costBasisDiv = card.lastElementChild;
						let classHeadline = card.querySelector('h5');

						equityNameElArray.forEach((equityNameEl) => {
							equityNameEl.style.margin = '0px';
							equityNameEl.style.marginLeft = '12px';
							equityNameEl.style.marginTop = '6px';
							equityNameEl.style.fontSize = '12px';
							equityNameEl.classList.remove('font-bold');
						});

						if (costBasisDiv.innerText.includes('Cost Basis')) costBasisDiv.remove();
						classHeadline.remove();

						// Remove hrNode from the first shareCardNode
						let hrNode = card.querySelector('hr');
						if (hrNode) {
							hrNode.remove();
						}

						// Remove all other shareCardNodes (class cards)
						for (let i = 2; i < shareCardNodes.length; i++) {
							shareCardNodes[i].remove();
						}
					}
				} else {
					// Process ALL share cards (not just the first one)
					for (let i = 0; i < shareCardNodes.length; i++) {
						let card = shareCardNodes[i];
						card.style.cssText = `
							font-size: 12px;
							border-style: none;
							border-collapse: collapse;
							max-width: 35% !important;
							border-radius: 20px !important;
							background-color: #f0f4f8 !important;
						`;

						let divs = Array.from(card.querySelectorAll('div'));
						let owningDiv = divs.find((div) => div.textContent.includes('You Own:'));
						if (owningDiv) {
							// remove unwanted wording + style
							owningDiv.textContent = owningDiv.textContent.replace('You Own:', '');
							owningDiv.style.marginLeft = '12px';
							owningDiv.style.marginTop = '0px';
							owningDiv.style.marginBottom = '6px';
						}

						if (card.children[2]) card.children[2].textContent = card.children[2].textContent.replace(/\s/g, ' ');

						let selectedEl = card.querySelector('.selectedEl');
						let equityNameEl = card.querySelector('.equityName');
						let costBasisDiv = card.lastElementChild;
						let classHeadline = card.querySelector('h5');

						if (selectedEl) selectedEl.setAttribute('hidden', true);
						if (equityNameEl) {
							equityNameEl.style.margin = '0px';
							equityNameEl.style.marginLeft = '12px';
							equityNameEl.style.marginTop = '6px';
							equityNameEl.style.fontSize = '12px';
							equityNameEl.classList.remove('font-bold');
						}
						if (costBasisDiv && costBasisDiv.innerText.includes('Cost Basis')) costBasisDiv.remove();
						if (classHeadline) classHeadline.remove();

						// Remove hrNode
						let hrNode = card.querySelector('hr');
						if (hrNode) {
							hrNode.remove();
						}
					}
				}
			} else {
				console.log('No shareCardNodes found.');
			}

			/**************************************  END  ************************************/

			/** Prepares all tables for report */
			let userEquityEventsTable = prepareTable('userEquityEventsTable', isBB);
			let userEquityTransactionsTable = prepareTable('userEquityTransactionsTable', isBB);

			let userDebtEventsTable;
			if (isMAR) userDebtEventsTable = prepareTable('userDebtEventsTable', isBB);

			// Generate basis table if flag is enabled or client is Sheetz
			let basisTable = '';
			const showBasisTable = clientData.flags?.show_basis_table_in_statement || isSheetz;

			// Debug log to help diagnose the issue
			console.log('Basis table check:', {
				client: client,
				isSheetz: isSheetz,
				flagEnabled: clientData.flags?.show_basis_table_in_statement,
				showBasisTable: showBasisTable,
			});

			if (showBasisTable) {
				basisTable = await generateBasisTable();
				console.log('Basis table generated, length:', basisTable ? basisTable.length : 0);
			}

			const viewTransactionsModal = document.getElementById('viewTransactionsModal');
			viewTransactionsModal.classList.add('hidden');
			buildEventsAndTransactionsModal();

			// let reportYear = '2023';
			// let dateNow = moment().format("MMM D, YYYY");
			let reportYear = moment().format('YYYY'); // This will get the previous year
			let dateNow = moment().format('MMM D, YYYY'); // This will set the date to December 31st of the previous year
			let userName = userData.user_name.replace(/_/g, ' ');
			let reportHtml = isMAR ? document.getElementById('viewMarvinReportModal') : document.getElementById('viewCapAcctModal');
			let reportTitle = isMAR ? 'Shareholder Report' : `${reportYear} Annual Shareholder Report`;
			let headline = isMAR ? stmtHeadline : `Statement as of ${dateNow}`;

			reportHtml.innerHTML = `
				<div id="accountSummary" style="font-family: 'Source Sans Pro', sans-serif; font-size: 18px;">
					<img id="brandLogo" class="responsive-img" src="${clientData.company_logo ? formatImg(clientData.company_logo, '/h_60/') : ''}" " style="text-align: center;">
					<div class="row mb-0" style="text-align: center; width: 100%;">
						<h2 class="text-2xl font-bold mt-0 mb-0" style="margin-bottom: 10px !important;">${reportTitle}</h2>
						<div class="s12 justify-between flex items-center">
							<h4 class="text-secondary" style="font-size:12px; margin:0;">${headline}</h4>
							<h4 id="accountDetails" class="col s12 text-secondary" style="font-weight: lighter; color: gray; font-size:12px; margin:0;">${userName}</h4>
						</div>
					</div>
					<div>
						<div class="row">
							<h3 class="text-2xl font-bold mb-0 mt-4 ml-4">Your Holdings</h3>
							${shareClassDetails.innerHTML}
						</div>
						<div class="row">${landingDiv.innerHTML}</div>
						${isLT ? `<h3 class="text-2xl font-bold mb-0 mt-4 ml-4">Stock Price per Share</h3>` : ''}
						${isLT ? `<div class="row">${userEquityTotals.innerHTML}</div>` : ''}
						<div class="row">${userEquityEventsTable}</div>
						<div class="row">${userEquityTransactionsTable}</div>
						${
							isMAR
								? `
									<div class="row">${userDebtEventsTable}</div>`
								: ''
						}
						${
							showBasisTable && basisTable
								? `
									<div class="row">${basisTable}</div>`
								: ''
						}

					</div>
				</div>
			`;

			function prepareTable(id, isBB = null) {
				/* Clones table node to prepare before adjusting html for report*/
				let tableElement = document.getElementById(id);
				let tableNode = tableElement.cloneNode(true);
				let tableWrap = tableNode.getElementsByClassName('table-wrap')[0];
				if (tableWrap) reportHtmlHandler(tableWrap, id);
				return tableNode.outerHTML;
			}

			function reportHtmlHandler(tableEl, tableName, isBB = null) {
				/* Styles table for shareholder report */
				let table = tableEl.querySelector('table');
				if (!table) return;
				let tableHead = table.getElementsByTagName('thead')[0];
				let rows = table.rows;
				let specificYearStart = isMAR ? new Date(startDate) : new Date(new Date().getFullYear(), 0, 1);
				let specificYearEnd = isMAR ? new Date(endDate) : new Date(new Date().getFullYear(), 11, 31);

				Array.from(table.rows).forEach((row, index) => {
					/** Removes transactions not within the year 2023 from table */
					// let rowDate = new Date(row.cells[1].innerText);
					let rowDate = table.id === 'userGOAccountEventsSortTable' ? new Date(row.cells[0].innerText) : new Date(row.cells[1].innerText);
					if (rowDate < specificYearStart || rowDate > specificYearEnd) {
						row.remove();
					}
				});

				tableHead.style.marginBottom = '12px';
				table.style.cssText = `font-size: 12px; border-collapse: collapse; min-width: 100% !important; border-radius: 20px !important;background-color: #f0f4f8 !important; text-align: center !important;`;

				if (tableName === 'userEquityEventsTable') {
					for (let i = 0; i < rows.length; i++) {
						rows[i].deleteCell(0);
						rows[i].deleteCell(-1);
					}
				}

				if (tableName === 'userEquityTransactionsTable') {
					for (let i = 0; i < rows.length; i++) {
						/** Removes unwanted columns: share class, Qty*Ref.Cost, Qty *ExactCost, ReferenceCost, ExactCost*/
						rows[i].deleteCell(0);
						rows[i].deleteCell(1);
						rows[i].deleteCell(-1);
						rows[i].deleteCell(-1);
						rows[i].deleteCell(-1);
						rows[i].deleteCell(-1);
					}
				}

				let totalRow = table.querySelector('.total-row');
				if (isBB && totalRow) {
					totalRow.classList.remove('hidden');
				}
			}

			function checkTableHasRows(reportHtml, tableId) {
				// Parse the reportHtml string into a Document
				let parser = new DOMParser();
				let doc = parser.parseFromString(reportHtml.innerHTML, 'text/html');

				// Select the table
				let tableParent = doc.querySelector(`:last-child`);
				let tableContainer = tableParent.querySelector(`#${tableId}`);
				let table = tableContainer.querySelector(`table`);

				// Check if the table exists
				if (!table) {
					console.error(`No table found with ID "${tableId}"`);
					return false;
				}

				// Select all 'tr' elements inside the table
				let rows = table.querySelectorAll('tbody tr');

				// Check if the table has rows
				if (rows.length > 0) {
					console.log(`Table "${tableId}" has rows.`);
					return true;
				} else {
					console.log(`Table "${tableId}" has no rows.`);
					tableContainer.parentNode.removeChild(tableContainer);
					let updatedInnerHTML = new XMLSerializer().serializeToString(doc.body);
					reportHtml.innerHTML = updatedInnerHTML;
					return false;
				}
			}

			if (!GENERATE_PDF) {
				M.Modal.getInstance(reportHtml).open();
				checkTableHasRows(reportHtml, 'userEquityEventsTable');
				checkTableHasRows(reportHtml, 'userEquityTransactionsTable');
			} else {
				if (!reportHtmlExists) {
					reportHtmlExists = true;
					if (isMAR) renderPrintableStatement(reportStartDate, reportEndDate);
					else renderPrintableStatement();
					return;
				}
				checkTableHasRows(reportHtml, 'userEquityEventsTable');
				checkTableHasRows(reportHtml, 'userEquityTransactionsTable');
				if (isMAR) checkTableHasRows(reportHtml, 'userDebtEventsTable');
				let Api2Pdf = require('api2pdf');
				let a2pClient = new Api2Pdf('6e053f8e-75b7-4271-a316-1981d7640681');
				let fileName = 'Statement for ' + userName + ' as of ' + moment().format('MM-D-YYYY') + '.pdf';
				a2pClient.chromeHtmlToPdf(reportHtml.innerHTML, { inline: false, filename: fileName }).then(function (result) {
					console.log(result);
					let request = new XMLHttpRequest();
					request.open('GET', result.FileUrl, true);
					request.responseType = 'blob';
					request.onload = function () {
						saveAs(result.FileUrl, cleanStampedName(fileName));
						M.Modal.getInstance(document.getElementById('statementWaitState')).close();
					};
					request.send();
				});
			}
			viewTransactionsModal.classList.remove('hidden'); // unhide E&T modal
			if (hide_price_label) {
				priceLabel.style.display = 'inline-block';
				lastTraded.style.display = 'inline-block';
			}
		}
	}

	async function generateBasisTable() {
		/* Generates basis table using the same processing logic as the gift transfer modal */

		try {
			// Use shareholder data if viewing another user's statement, otherwise use current user
			let shareholderData = JSON.parse(sessionStorage.getItem('shareholder.Data'));
			let targetUserId = shareholderData ? shareholderData.userid : userData.userid;

			if (!userData || !clientData || !clientData.equity) {
				return '';
			}

			// Call giftTransfer with dataOnly mode to get the exact same processed data
			// This ensures the basis table uses identical logic to the gift transfer modal
			const { giftTransfer } = await import('./helpers/gift-transfer.js');
			const result = giftTransfer(targetUserId, false, {
				dataOnly: true,
				clientData: clientData,
				shareholderData: shareholderData || userData,
			});

			if (!result || !result.giftTransactions) {
				return '';
			}

			// The basis table shows giftTransactions exactly like the gift transfer modal does
			// Filter out transactions with quantity == 0 (modal hides these with class="hidden")
			const processedTransactions = result.giftTransactions.filter((t) => Number(String(t.quantity).replace(/[^.\d]/g, '')) > 0);

			// Debug: Log first few processed transactions showing before/quantity
			processedTransactions.slice(0, 3).forEach((txn, i) => {
				console.log(`Processed Transaction ${i}:`, {
					tranche: txn.tranche,
					shareClass: txn.shareClass,
					before: txn.before,
					quantity: txn.quantity, // This is the remaining shares after deductions
					sharePrice: txn.sharePrice,
					notes: txn.notes,
					date: new Date(Number(txn.date) * 1000).toLocaleDateString(),
				});
			});

			if (!processedTransactions.length) {
				return '';
			}

			// Transactions have tranche letters assigned by giftTransfer dataOnly mode
			const transactionsWithTranches = processedTransactions;
			console.log('Using', transactionsWithTranches.length, 'transactions with pre-assigned tranche letters');

			// Determine column visibility
			let classes = Object.keys(clientData.equity);
			let hasClassColumn = classes.length > 1;
			let hasCertColumn = transactionsWithTranches.some((t) => t.notes && t.notes.includes('#'));

			// Generate the table markup
			return buildBasisTableMarkup(transactionsWithTranches, hasClassColumn, hasCertColumn);
		} catch (error) {
			console.error('Error generating basis table:', error);
			return '';
		}
	}

	function buildBasisTableMarkup(transactions, hasClassColumn, hasCertColumn) {
		/* Builds the HTML markup using the exact same structure as gift transfer modal */

		try {
			if (!transactions.length) {
				console.log('buildBasisTableMarkup: No transactions provided');
				return '';
			}

			console.log('buildBasisTableMarkup: Starting with', transactions.length, 'transactions');

			// Follow the exact same table structure as gift transfer modal
			let basisTableMarkup = `
				<div id="userBasisDetailsTable" class="table-wrap card p-1 mt-4 mb-2">
					<div class="sm-hz-scroll-no-margin">
						<h5 class="text-xl font-bold mt-4 ml-4">Cost Basis Details</h5>
						<table id="userBasisDetailsSortTable" class="highlight table-content">
							<thead>
								<tr>
									<th class="text-xs center-align p-05" style="width:4%" data-sort-default>Tranche</th>
									${hasClassColumn ? `<th class="text-xs center-align p-05" style="width:6%">Class</th>` : ''}
									${hasCertColumn ? `<th class="text-xs center-align p-05">Cert #</th>` : ''}
									<th class="text-xs center-align p-05">Date</th>
									<th class="text-xs p-05">Description</th>
									<th class="text-xs center-align p-05"># of Shares</th>
									<th class="text-xs center-align p-05">Cost&nbsp;Basis</th>
								</tr>
							</thead>
							<tbody id="basisTableBody">`;

			let totalInitial = 0;
			let totalBasisValue = 0;
			let rowsGenerated = 0;

			console.log('buildBasisTableMarkup: Processing transactions one per row');

			// Process each transaction as a separate row (matching gift transfer modal exactly)
			transactions.forEach((transaction, index) => {
				let certNumber = extractCertificateNumber(transaction.notes);
				let tranche = transaction.tranche || 'A';
				// Use same string cleaning as gift transfer modal: .replace(/[^.\d]/g, '')
				let sharePrice = transaction.sharePrice ? Number(String(transaction.sharePrice).replace(/[^.\d]/g, '')) : 0;
				let quantity = transaction.quantity ? Number(String(transaction.quantity).replace(/[^.\d]/g, '')) : 0;
				let costBasis = !isNaN(sharePrice) && sharePrice > 0 ? quantity * sharePrice : 0;

				// Use formatted date safely
				let transactionDate;
				try {
					transactionDate = moment.unix(transaction.date);
					if (!transactionDate.isValid()) {
						transactionDate = moment(); // fallback to current date
					}
				} catch (e) {
					console.warn('Date parsing error, using current date:', e);
					transactionDate = moment(); // fallback to current date
				}

				// Use gift transfer modal logic: transaction.quantity is already the remaining shares
				// transaction.before would be the original shares, but we only show remaining
				let remainingShares = quantity;

				// For cost basis calculation, use the remaining shares
				totalInitial += remainingShares;
				if (!isNaN(sharePrice) && sharePrice > 0) {
					totalBasisValue += remainingShares * sharePrice;
				}

				// Debug first few rows
				if (index < 3) {
					console.log(`Processing transaction ${index}:`, {
						tranche: tranche,
						shareClass: transaction.shareClass,
						certNumber: certNumber,
						date: transactionDate.format('MMM D, YYYY'),
						description: transaction.notes,
						remainingShares: remainingShares,
						sharePrice: sharePrice,
						costBasis: costBasis,
					});
				}

				basisTableMarkup += `
					<tr>
						<td class="text-xs font-bold center-align p-0" data-sort="${tranche.charCodeAt(0) - 64}">
							${tranche}
						</td>
						${
							hasClassColumn
								? `
							<td class="text-xs center-align p-0">
								${transaction.shareClass || ''}
							</td>
						`
								: ''
						}
						${
							hasCertColumn
								? `
							<td class="text-xs center-align p-0">
								${certNumber || ''}
							</td>
						`
								: ''
						}
						<td class="text-xs center-align">
							${transactionDate.format('MMM D, YYYY')}
						</td>
						<td class="text-xs">
							${transaction.notes || 'Share Grant'}
						</td>
						<td class="text-xs center-align">
							${remainingShares.toLocaleString()}
						</td>
						<td class="text-xs center-align">
							${sharePrice > 0 ? sharePrice.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 6 }) : 'n.a.'}
						</td>
					</tr>`;

				rowsGenerated++;
			});

			console.log('buildBasisTableMarkup: Generated', rowsGenerated, 'table rows');

			let avgCostBasis = totalInitial > 0 ? totalBasisValue / totalInitial : 0;

			// Add total row
			basisTableMarkup += `
								<tr class="total-row">
									<td class="text-xs font-bold center-align p-0">Total</td>
									${hasClassColumn ? `<td class="center-align"></td>` : ''}
									${hasCertColumn ? `<td class="center-align"></td>` : ''}
									<td class="center-align"></td>
									<td class="left-align"></td>
									<td class="text-xs font-bold center-align">
										${totalInitial.toLocaleString()}
									</td>
									<td class="text-xs font-bold center-align">
										${avgCostBasis > 0 ? avgCostBasis.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 }) : 'n.a.'}
									</td>
								</tr>
							</tbody>
						</table>
					</div>
				</div>`;

			console.log('buildBasisTableMarkup: Final markup length:', basisTableMarkup.length);

			// Create a temporary DOM element to apply styling like other tables
			let tempContainer = document.createElement('div');
			tempContainer.innerHTML = basisTableMarkup;
			let tableWrap = tempContainer.querySelector('.table-wrap');

			if (tableWrap) {
				// Apply the same styling as reportHtmlHandler
				let table = tableWrap.querySelector('table');
				if (table) {
					let tableHead = table.getElementsByTagName('thead')[0];
					if (tableHead) tableHead.style.marginBottom = '12px';
					table.style.cssText = `font-size: 12px; border-collapse: collapse; min-width: 100% !important; border-radius: 20px !important; background-color: #f0f4f8 !important; text-align: center !important;`;

					// Apply cell styling to all cells
					let allCells = table.querySelectorAll('td, th');
					allCells.forEach((cell) => {
						if (!cell.style.padding) {
							cell.style.padding = '8px 4px';
						}
					});
				}
			}

			console.log('buildBasisTableMarkup: Applied styling, returning formatted markup');
			return tempContainer.innerHTML;
		} catch (error) {
			console.error('Error in buildBasisTableMarkup:', error);
			return '';
		}
	}

	/*******************************************************************************
	 * BARRY-WEHMILLER ACCOUNT STATEMENT FUNCTIONS
	 * Generates a custom account statement matching the BW template format
	 *
	 * Leverages:
	 * - window.lastCalculationResult for share prices (from calculator engine)
	 * - giftTransfer(dataOnly: true) for processed transaction data
	 *******************************************************************************/

	async function renderBWAccountStatement() {
		/* Main function to render Barry-Wehmiller account statement PDF */
		try {
			// Show loading modal
			M.Modal.getInstance(document.getElementById('statementWaitState')).open();

			// Get user data (use shareholder if viewing another user, otherwise current user)
			let shareholderData = JSON.parse(sessionStorage.getItem('shareholder.Data'));
			let targetUserData = shareholderData || userData;
			let targetUserId = targetUserData.userid;

			// On admin users/edit: use shareholder.Data (the user being edited).
			// On investor portal: use global userData (current view) so observers see the observed account's
			// statement, and switching back to Person A shows A's statement (avoids stale sessionStorage).
			const pnForTarget = window.location.pathname || '';
			if (pnForTarget.includes('/admin/users/edit')) {
				targetUserData = shareholderData || userData;
			} else {
				targetUserData = userData;
			}
			targetUserId = targetUserData.userid;

			// Get processed transaction data using giftTransfer dataOnly mode
			const { giftTransfer } = await import('./helpers/gift-transfer.js');
			const result = giftTransfer(targetUserId, false, {
				dataOnly: true,
				clientData: clientData,
				shareholderData: targetUserData,
			});

			if (!result || !result.origTransactions) {
				M.Modal.getInstance(document.getElementById('statementWaitState')).close();
				M.toast({ html: 'No transaction data available' });
				return;
			}

			// Use origTransactions which preserves sharePrice from raw data
			// (giftTransactions is heavily processed and may lose sharePrice during transfers)
			const processedTransactions = result.origTransactions.filter(
				(t) => Number(String(t.quantity).replace(/[^.\d]/g, '')) > 0 && t.receiver?.toLowerCase() === targetUserId.toLowerCase() // Only incoming transactions
			);

			// Enrich transactions with gift date from CSV (not stored in sessionStorage)
			if (clientData.transactions_data) {
				try {
					const csvUrl = await getContentUrl(clientData.transactions_data);
					const csvResp = await fetch(csvUrl);
					const csvText = await csvResp.text();
					const csvRows = cleanCSV(csvText);
					// Find Gift Date column index from header row (cleanCSV removes blank columns, shifting indices)
					const giftDateMap = {};
					const headerRow = csvRows[0] || [];
					const giftDateColIdx = headerRow.findIndex((h) => /gift\s*date/i.test(h));
					if (giftDateColIdx >= 0) {
						csvRows.forEach((r, i) => {
							if (i === 0) return; // skip header
							if (r[4] && r[giftDateColIdx]) {
								let gd = Date.parse(r[giftDateColIdx]);
								if (!isNaN(gd)) giftDateMap[r[4]] = (gd / 1000).toString();
							}
						});
					}
					// Apply gift dates to processed transactions
					processedTransactions.forEach((t) => {
						if (t.nrid && giftDateMap[t.nrid]) t.giftDate = giftDateMap[t.nrid];
					});
				} catch (e) {
					console.warn('[Statement] Failed to fetch gift dates from CSV:', e);
				}
			}

			// Build statement data
			const statementData = buildBWStatementData(processedTransactions, targetUserData);

			// Generate HTML template (body content only, header/footer handled separately)
			const statementHtml = buildBWStatementHtml(statementData);

			// Fetch and convert logo to base64 for PDF header (Puppeteer requires base64 for images)
			let logoBase64 = '';
			if (clientData.company_logo) {
				try {
					const logoResponse = await fetch(clientData.company_logo);
					const logoBlob = await logoResponse.blob();
					logoBase64 = await new Promise((resolve) => {
						const reader = new FileReader();
						reader.onloadend = () => resolve(reader.result);
						reader.readAsDataURL(logoBlob);
					});
				} catch (e) {
					console.error('Failed to fetch logo for PDF header:', e);
				}
			}

			// Generate header and footer templates for PDF
			const headerHtml = buildBWHeaderTemplate(targetUserData, logoBase64);
			const footerHtml = buildBWFooterTemplate();

			// Generate PDF using Api2Pdf
			let Api2Pdf = require('api2pdf');
			let a2pClient = new Api2Pdf('6e053f8e-75b7-4271-a316-1981d7640681');
			let userName = targetUserData.user_name?.replace(/_/g, ' ') || 'Shareholder';
			let fileName = 'Account Statement for ' + userName + ' as of ' + moment().format('MM-D-YYYY') + '.pdf';

			const pdfOptions = {
				inline: false,
				filename: fileName,
				options: {
					displayHeaderFooter: true,
					headerTemplate: headerHtml,
					footerTemplate: footerHtml,
					marginTop: '180px',
					marginBottom: '80px',
					marginLeft: '40px',
					marginRight: '40px',
					printBackground: true,
				},
			};

			a2pClient
				.chromeHtmlToPdf(statementHtml, pdfOptions)
				.then(function (pdfResult) {
					let request = new XMLHttpRequest();
					request.open('GET', pdfResult.FileUrl, true);
					request.responseType = 'blob';
					request.onload = function () {
						saveAs(pdfResult.FileUrl, cleanStampedName(fileName));
						M.Modal.getInstance(document.getElementById('statementWaitState')).close();
					};
					request.send();
				})
				.catch(function (err) {
					console.error('PDF generation error:', err);
					M.Modal.getInstance(document.getElementById('statementWaitState')).close();
					M.toast({ html: 'Error generating PDF' });
				});
		} catch (error) {
			console.error('renderBWAccountStatement error:', error);
			M.Modal.getInstance(document.getElementById('statementWaitState')).close();
			M.toast({ html: 'Error generating statement' });
		}
	}

	function buildBWHeaderTemplate(targetUserData, logoBase64 = '') {
		/* Generates PDF header template with company name, title, and contact info */

		// HTML escape helper
		const escapeHtml = (str) => {
			if (!str) return '';
			return String(str).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#039;');
		};

		const userName = escapeHtml(targetUserData.user_name?.replace(/_/g, ' ') || '');
		const userAddress = targetUserData.clients?.[client]?.address || '';
		const userPhone = escapeHtml(targetUserData.phone || '');
		const userEmail = escapeHtml(targetUserData.userid || '');
		const companyName = escapeHtml(clientData.company_name || 'BARRY-WEHMILLER GROUP, INC.');

		// Split address into lines
		const rawAddressParts = splitBWAddress(userAddress);
		const addressParts = {
			line1: escapeHtml(rawAddressParts.line1),
			line2: escapeHtml(rawAddressParts.line2),
			line3: escapeHtml(rawAddressParts.line3),
		};

		// Use base64 logo if available, otherwise fall back to company name text
		const logoOrName = logoBase64
			? `<img class="company-logo" src="${logoBase64}" alt="${companyName}">`
			: `<span class="company-name">${companyName}</span>`;

		return `<style>
			* { margin: 0; padding: 0; box-sizing: border-box; -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
			.header-container { width: 100%; font-family: Arial, sans-serif; padding: 15px 30px; font-size: 10px; }
			.company-logo { max-height: 44px; }
			.company-name { font-size: 20px; font-weight: bold; color: #243b53; }
			.statement-title { font-size: 13px; font-weight: bold; color: #243b53; margin-top: 6px; margin-bottom: 20px; }
			.contact-row { display: flex; justify-content: space-between; width: 100%; }
			.contact-left { font-size: 10px; color: #243b53; line-height: 1.4; }
			.contact-left .name { font-weight: bold; }
			.contact-right { text-align: right; font-size: 10px; color: #243b53; line-height: 1.4; }
		</style>
		<div class="header-container">
			<div style="text-align: center;">
				${logoOrName}
			</div>
			<div class="statement-title" style="text-align: center;">EQUITY HOLDER ACCOUNT STATEMENT</div>
			<div class="contact-row">
				<div class="contact-left">
					<div class="name">${userName}</div>
					${userPhone ? '<div>' + userPhone + '</div>' : ''}
					${userEmail ? '<div>' + userEmail + '</div>' : ''}
				</div>
				<div class="contact-right">
					${addressParts.line1 ? '<div>' + addressParts.line1 + '</div>' : ''}
					${addressParts.line2 ? '<div>' + addressParts.line2 + '</div>' : ''}
					${addressParts.line3 ? '<div>' + addressParts.line3 + '</div>' : ''}
				</div>
			</div>
		</div>`;
	}

	function buildBWFooterTemplate() {
		/* Generates PDF footer template with company address and page numbers */
		const companyAddress = '8020 Forsyth Boulevard, St. Louis, MO  63105';
		const companyPhone = '+1 (314) 862-8000';

		return `<style>
			* { margin: 0; padding: 0; box-sizing: border-box; -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
			.footer-container { width: 100%; font-family: Arial, sans-serif; font-size: 9px; padding: 8px 30px; color: #243b53; }
			.footer-inner { border-top: 1px solid #243b53; border-bottom: 1px solid #243b53; padding: 6px 0; display: flex; justify-content: space-between; align-items: center; }
			.company-text { }
			.page-num { font-size: 11px; font-weight: bold; }
		</style>
		<div class="footer-container">
			<div class="footer-inner">
				<div><span class="company-text">Barry-Wehmiller</span> Group, Inc.  |  ${companyAddress}  |  ${companyPhone}</div>
				<div class="page-num">Page <span class="pageNumber"></span> of <span class="totalPages"></span></div>
			</div>
		</div>`;
	}

	function buildBWStatementData(transactions, targetUserData) {
		/* Processes transactions to build statement data structure */
		const classes = Object.keys(clientData.equity);
		const statementDate = moment().format('MM/DD/YYYY');
		const currentYear = new Date().getFullYear();

		// Get calculator breakdown for share prices (already calculated during page load)
		const calculatorBreakdown = window.lastCalculationResult?.breakdown || [];

		// Initialize data structure for each share class
		const byClass = {};
		classes.forEach((c) => {
			// Get share price from calculator result (avoids duplicate valuation lookup)
			const calcClassData = calculatorBreakdown.find((cd) => cd.classKey === c);

			byClass[c] = {
				shareClass: c,
				shareClassName: clientData.equity[c]?.name || c,
				sharesType: clientData.equity[c]?.sharesType || 'shares',
				certificated: [],
				uncertificated: [],
				totalCertificated: 0,
				totalUncertificated: 0,
				totalDRIP: 0, // Always 0 (not tracked)
				totalShares: 0,
				currentPrice: calcClassData?.sharePrice || 0,
				currentValue: 0,
				ytdDividends: calculateBWYTDDividends(c, targetUserData, currentYear),
				// Vesting data from calculator
				vestedShares: calcClassData?.vested || 0,
				totalTargetShares: calcClassData?.totalTarget || 0,
				unvestedShares: Math.max(0, (calcClassData?.totalTarget || 0) - (calcClassData?.vested || 0)),
				unvestedValue: calcClassData?.unvestedValue || 0,
			};
		});

		// Process each transaction
		transactions.forEach((txn) => {
			const shareClass = txn.shareClass;
			if (!byClass[shareClass]) return;

			const quantity = Number(String(txn.quantity || '').replace(/[^.\d]/g, '')) || 0;
			const certNumber = extractCertificateNumber(txn.notes);
			// Cost basis stored in strikePrice field (label_tax_basis)
			const costBasisPerShare = Number(String(txn.strikePrice || '').replace(/[^.\d]/g, '')) || 0;

			// Check if gift/inherited: presence of value in sharePrice field (Gift FMV column)
			const giftFMV = Number(String(txn.sharePrice || '').replace(/[^.\d]/g, '')) || 0;
			const isGiftInherited = giftFMV > 0;

			// Check for cancelled/expiration date (expDate field in BW data)
			const cancelledDateRaw = txn.expDate || txn.cancelledDate || txn.cancelled_date || '';
			const cancelledDate = cancelledDateRaw ? moment.unix(cancelledDateRaw).format('MM/DD/YYYY') : '';

			const txnData = {
				originalAcquiredDate: moment.unix(txn.date).format('MM/DD/YYYY'),
				certificateDate: certNumber ? moment.unix(txn.date).format('MM/DD/YYYY') : '',
				certificateNumber: certNumber || '',
				shares: quantity,
				giftInherited: isGiftInherited ? 'Yes' : '',
				giftDate: txn.giftDate ? moment.unix(txn.giftDate).format('MM/DD/YYYY') : '',
				giftBasis: isGiftInherited ? giftFMV : 0,
				costBasisCumulative: quantity * costBasisPerShare,
				costBasisPerShare: costBasisPerShare,
				cancelledDate: cancelledDate,
			};

			if (certNumber) {
				byClass[shareClass].certificated.push(txnData);
				byClass[shareClass].totalCertificated += quantity;
			} else {
				byClass[shareClass].uncertificated.push(txnData);
				byClass[shareClass].totalUncertificated += quantity;
			}
		});

		// Calculate totals for each class and portfolio total
		let totalPortfolioValue = 0;
		let totalYtdDividends = 0;
		let totalUnvestedValue = 0;
		Object.values(byClass).forEach((classData) => {
			classData.totalShares = classData.totalCertificated + classData.totalUncertificated;
			classData.currentValue = classData.totalShares * classData.currentPrice;
			totalPortfolioValue += classData.currentValue;
			totalYtdDividends += classData.ytdDividends;
			totalUnvestedValue += classData.unvestedValue;
		});

		// Build individual unvested grants array for detailed table display
		const unvestedGrants = [];
		classes.forEach((c) => {
			const vesting = targetUserData.clients?.[client]?.shares?.[c]?.vesting;
			const calcClassData = calculatorBreakdown.find((cd) => cd.classKey === c);
			const currentPrice = calcClassData?.sharePrice || 0;
			const className = clientData.equity[c]?.name || c;

			if (vesting) {
				Object.entries(vesting).forEach(([trancheName, trancheData]) => {
					if (trancheData.cashedOut === true) return; // skip cashed-out tranches
					if (trancheData.vested === true) return; // skip fully vested tranches (BW tranche-level flag)
					// Calculate unvested amount from schedule entries where vested !== true
					let unvestedEquity = 0;
					if (trancheData.schedule) {
						Object.values(trancheData.schedule).forEach((entry) => {
							if (!entry.vested && !entry.forfeited) {
								unvestedEquity += Number(entry.amount) || 0;
							}
						});
					}

					if (unvestedEquity > 0) {
						// Parse "Original Grant Value: $XXX" from conditions
						let targetValueAtGrant = 0;
						const condMatch = trancheData.conditions?.match(/Original Grant Value:\s*\$?([\d,]+\.?\d*)/i);
						if (condMatch) targetValueAtGrant = Number(condMatch[1].replace(/,/g, ''));

						unvestedGrants.push({
							stockType: `${trancheName} ${className}`,
							trancheName: trancheName,
							equity: unvestedEquity,
							targetValueAtGrant: targetValueAtGrant,
							currentPrice: currentPrice,
							unvestedValue: unvestedEquity * currentPrice,
						});
					}
				});
			}
		});

		// Sort unvested grants: FY tranches first (oldest to newest), then others
		const isFYKey = (k) => /^FY\s*\d{2,4}\b/i.test(String(k || '').trim());
		unvestedGrants.sort((a, b) => {
			const aIsFY = isFYKey(a.trancheName);
			const bIsFY = isFYKey(b.trancheName);
			if (aIsFY && !bIsFY) return -1;
			if (!aIsFY && bIsFY) return 1;
			if (aIsFY && bIsFY) {
				const ay = Number(String(a.trancheName).match(/FY\s*(\d{2,4})/i)?.[1]) || 0;
				const by = Number(String(b.trancheName).match(/FY\s*(\d{2,4})/i)?.[1]) || 0;
				return ay - by; // Oldest first
			}
			return String(a.trancheName).localeCompare(String(b.trancheName));
		});

		// Get time series data for the chart (already built by TimeSeriesBuilder)
		const timeSeriesData = window.lastTimeSeriesResult?.data || [];

		return {
			statementDate,
			byClass,
			classes,
			totalPortfolioValue,
			totalYtdDividends,
			totalUnvestedValue,
			unvestedGrants,
			timeSeriesData,
		};
	}

	function calculateBWYTDDividends(shareClass, targetUserData, currentYear) {
		/* Calculates year-to-date dividends for a share class */
		const userShares = targetUserData.clients?.[client]?.shares?.[shareClass];
		if (!userShares?.events) return 0;

		const clientEvents = clientData.equity[shareClass]?.events || [];
		const yearStart = new Date(currentYear, 0, 1).getTime() / 1000;

		let totalDividends = 0;
		userShares.events.forEach((ue) => {
			const clientEvent = clientEvents.find((ce) => ce.nrid === ue.nrid);
			if (
				clientEvent &&
				Number(clientEvent.date) >= yearStart &&
				(clientEvent.eventType === 'Distribution' || clientEvent.eventType === 'Cash Dividend')
			) {
				totalDividends += Number(ue.grossDollars) || 0;
			}
		});

		return totalDividends;
	}

	function buildBWStatementHtml(statementData) {
		/* Generates HTML body content - header/footer handled by PDF template */
		const primaryColor = clientData.brand_color && /^#[0-9a-fA-F]{3,6}$/.test(clientData.brand_color) ? clientData.brand_color : '#299aa4';

		// HTML escape helper for user-provided content (XSS prevention)
		const escapeHtml = (str) => {
			if (!str) return '';
			return String(str).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#039;');
		};

		let html = `
		<!DOCTYPE html>
		<html>
		<head>
			<style>
				body {
					font-family: 'Source Sans Pro', Arial, sans-serif;
					font-size: 12px;
					margin: 0;
					padding: 0;
					color: #243b53;
				}
				.class-section {
					margin-bottom: 30px;
				}
				.class-section.page-break {
					page-break-before: always;
				}
				.summary-card {
					background: #f0f4f8;
					border-radius: 8px;
					padding: 15px;
					margin-bottom: 20px;
				}
				.summary-card h3 {
					margin: 0 0 12px 0;
					font-size: 14px;
					color: #4eb5ac;
					font-weight: bold;
				}
				.summary-grid {
					display: flex;
					justify-content: space-between;
					flex-wrap: wrap;
				}
				.summary-item {
					text-align: center;
					flex: 1;
					min-width: 120px;
					padding: 8px;
				}
				.summary-label {
					font-size: 10px;
					color: #666;
					text-transform: uppercase;
					margin-bottom: 4px;
				}
				.summary-value {
					font-size: 14px;
					font-weight: bold;
					color: #243b53;
				}
				.detail-table {
					width: 100%;
					border-collapse: collapse;
					margin-top: 15px;
					font-size: 11px;
				}
				.detail-table th {
					background-color: ${primaryColor};
					color: white;
					padding: 10px 8px;
					text-align: center;
					font-weight: bold;
					font-size: 10px;
					text-transform: uppercase;
				}
				.detail-table td {
					padding: 8px;
					border-bottom: 1px solid #e2e8f0;
				}
				.detail-table tr:nth-child(even) {
					background-color: #f8fafc;
				}
				.detail-table tr.totals-row {
					background-color: #a6dad5;
					font-weight: bold;
				}
				.text-right { text-align: right; }
				.text-center { text-align: center; }
				.balance-row {
					display: flex;
					justify-content: space-around;
					background: white;
					border: 1px solid #e2e8f0;
					border-radius: 6px;
					padding: 12px;
					margin-bottom: 15px;
				}
				.balance-item {
					text-align: center;
				}
				.balance-label {
					font-size: 9px;
					color: #666;
					text-transform: uppercase;
				}
				.balance-value {
					font-size: 13px;
					font-weight: bold;
					color: #243b53;
				}
				/* Portfolio Summary Styles */
				.portfolio-summary {
					margin-bottom: 30px;
					padding-bottom: 20px;
					border-bottom: 2px solid #4eb5ac;
					page-break-after: always;
				}
				.portfolio-title {
					font-size: 18px;
					font-weight: bold;
					color: #243b53;
					margin-bottom: 25px;
				}
				.portfolio-grid {
					display: flex;
					justify-content: space-between;
					flex-wrap: wrap;
					background: #f0f4f8;
					border-radius: 8px;
					padding: 15px;
					margin-bottom: 20px;
				}
				.portfolio-item {
					text-align: center;
					flex: 1;
					min-width: 140px;
					padding: 10px;
				}
				.portfolio-label {
					font-size: 10px;
					color: #666;
					text-transform: uppercase;
					margin-bottom: 5px;
				}
				.portfolio-value {
					font-size: 16px;
					font-weight: bold;
					color: #243b53;
				}
				.portfolio-value.large {
					font-size: 22px;
					color: #4eb5ac;
				}
				.holdings-grid {
					display: flex;
					flex-wrap: wrap;
					gap: 12px;
					margin-bottom: 20px;
				}
				.holding-card {
					background: white;
					border: 1px solid #e2e8f0;
					border-radius: 8px;
					padding: 12px 16px;
					min-width: 150px;
					flex: 1;
				}
				.holding-class-name {
					font-size: 11px;
					color: #666;
					text-transform: uppercase;
					margin-bottom: 4px;
				}
				.holding-shares {
					font-size: 16px;
					font-weight: bold;
					color: #243b53;
				}
				.chart-section {
					margin-bottom: 20px;
				}
				.chart-title {
					font-size: 14px;
					font-weight: bold;
					color: #243b53;
					margin-bottom: 10px;
				}
				/* Unvested Section Styles */
				.unvested-section {
					page-break-before: always;
					margin-top: 30px;
				}
				.unvested-title {
					font-size: 18px;
					font-weight: bold;
					color: #243b53;
					margin-bottom: 15px;
					padding-bottom: 5px;
					border-bottom: 2px solid #4eb5ac;
				}
				.unvested-summary {
					background: #f0f4f8;
					border-radius: 8px;
					padding: 15px;
					margin-bottom: 20px;
				}
				.unvested-total {
					font-size: 16px;
					font-weight: bold;
					color: #243b53;
				}
				.unvested-total-value {
					font-size: 20px;
					color: #4eb5ac;
					font-weight: bold;
				}
				.unvested-table {
					width: 100%;
					border-collapse: collapse;
					margin-top: 15px;
					font-size: 11px;
				}
				.unvested-table th {
					background-color: #4eb5ac;
					color: white;
					padding: 10px 8px;
					text-align: center;
					font-weight: bold;
					font-size: 10px;
					text-transform: uppercase;
				}
				.unvested-table td {
					padding: 8px;
					border-bottom: 1px solid #e2e8f0;
				}
				.unvested-table tr:nth-child(even) {
					background-color: #f8fafc;
				}
			</style>
		</head>
		<body>
		`;

		// Portfolio Summary Section (before per-class details)
		const classesWithShares = statementData.classes.filter((c) => statementData.byClass[c].totalShares > 0);

		if (classesWithShares.length > 0) {
			html += `
			<div class="portfolio-summary">
				<div class="portfolio-title">Portfolio Summary</div>

				<!-- Holdings Cards -->
				<div class="holdings-grid">
			`;

			classesWithShares.forEach((c) => {
				const classData = statementData.byClass[c];
				html += `
					<div class="holding-card">
						<div class="holding-class-name">${escapeHtml(classData.shareClassName)}</div>
						<div class="holding-shares">${classData.totalShares.toLocaleString('en-US', { minimumFractionDigits: 4, maximumFractionDigits: 4 })}${classData.sharesType === 'shares' ? ' shares' : ''}</div>
					</div>
				`;
			});

			html += `
				</div>

				<!-- Summary Grid -->
				<div class="portfolio-grid">
					<div class="portfolio-item">
						<div class="portfolio-label">Statement Date</div>
						<div class="portfolio-value">${statementData.statementDate}</div>
					</div>
					<div class="portfolio-item">
						<div class="portfolio-label">YTD Dividends</div>
						<div class="portfolio-value">$${statementData.totalYtdDividends.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
					</div>
					<div class="portfolio-item">
						<div class="portfolio-label">Total Portfolio Value</div>
						<div class="portfolio-value large">$${statementData.totalPortfolioValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
					</div>
				</div>
			`;

			// Portfolio Value Chart (if time series data available)
			if (statementData.timeSeriesData && statementData.timeSeriesData.length >= 2) {
				// Filter out the "now" point added by TimeSeriesBuilder (show only up to last valuation)
				const oneDayAgo = Date.now() - 24 * 60 * 60 * 1000;
				const chartData = statementData.timeSeriesData.filter((row) => row[0] < oneDayAgo);

				if (chartData.length >= 2) {
					const chartSvg = buildStatementChartSvg(chartData, {
						width: 700,
						height: 180,
						color: '#4eb5ac',
					});
					if (chartSvg) {
						html += `
							<div class="chart-section">
								<div class="chart-title">Portfolio Value Over Time</div>
								<div style="padding: 10px 0;">
									${chartSvg}
								</div>
							</div>
						`;
					}
				}
			}

			html += `
			</div>
			`;
		}

		// Generate section for each share class with shares
		let isFirstClass = true;
		statementData.classes.forEach((shareClass) => {
			const classData = statementData.byClass[shareClass];
			if (!classData.totalShares) return; // Skip classes with no shares

			// Add page break before all classes except the first
			const pageBreakClass = isFirstClass ? '' : 'page-break';
			isFirstClass = false;

			html += `
			<div class="class-section ${pageBreakClass}">
				<!-- SUMMARY CARD -->
				<div class="summary-card">
					<h3>${escapeHtml(classData.shareClassName)}</h3>
					<div class="summary-grid">
						<div class="summary-item">
							<div class="summary-label">Statement Date</div>
							<div class="summary-value">${statementData.statementDate}</div>
						</div>
						<div class="summary-item">
							<div class="summary-label">YTD Dividends</div>
							<div class="summary-value">$${classData.ytdDividends.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
						</div>
						<div class="summary-item">
							<div class="summary-label">Current Price</div>
							<div class="summary-value">$${classData.currentPrice.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
						</div>
						<div class="summary-item">
							<div class="summary-label">Current Value</div>
							<div class="summary-value">$${classData.currentValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
						</div>
					</div>
				</div>

				<!-- BALANCE ROW -->
				<div class="balance-row">
					<div class="balance-item">
						<div class="balance-label">Certificated</div>
						<div class="balance-value">${classData.totalCertificated.toLocaleString('en-US', { minimumFractionDigits: 4, maximumFractionDigits: 4 })}</div>
					</div>
					<div class="balance-item">
						<div class="balance-label">Uncertificated</div>
						<div class="balance-value">${classData.totalUncertificated.toLocaleString('en-US', { minimumFractionDigits: 4, maximumFractionDigits: 4 })}</div>
					</div>
					<div class="balance-item">
						<div class="balance-label">DRIP</div>
						<div class="balance-value">0.0000</div>
					</div>
					<div class="balance-item">
						<div class="balance-label">Total Shares</div>
						<div class="balance-value">${classData.totalShares.toLocaleString('en-US', { minimumFractionDigits: 4, maximumFractionDigits: 4 })}</div>
					</div>
				</div>

				<!-- CERTIFICATE DETAIL TABLE -->
				<table class="detail-table">
					<thead>
						<tr>
							<th>Origination Date</th>
							<th>Current Transfer Date</th>
							<th>Cert # / Book Entry</th>
							<th>Shares / Equity Units</th>
							<th>Gift or Inherited</th>
							<th>G&nbsp;or&nbsp;I Date</th>
							<th>Gift Basis</th>
							<th>Cost Basis</th>
							<th>Basis Per Share</th>
							<th>Cancelled Date</th>
						</tr>
					</thead>
					<tbody>
			`;

			// Add certificated transactions first, then uncertificated
			const allTransactions = [...classData.certificated, ...classData.uncertificated];
			let totalBasis = 0;

			allTransactions.forEach((txn, idx) => {
				totalBasis += txn.costBasisCumulative;
				html += `
						<tr>
							<td class="text-center">${escapeHtml(txn.originalAcquiredDate)}</td>
							<td class="text-center">${escapeHtml(txn.certificateDate)}</td>
							<td class="text-center">${escapeHtml(txn.certificateNumber)}</td>
							<td class="text-right">${txn.shares.toLocaleString('en-US', { minimumFractionDigits: 4, maximumFractionDigits: 4 })}</td>
							<td class="text-center">${txn.giftInherited}</td>
							<td class="text-center">${escapeHtml(txn.giftDate)}</td>
							<td class="text-right">${txn.giftBasis ? txn.giftBasis.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : ''}</td>
							<td class="text-right">${txn.costBasisCumulative.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
							<td class="text-right">${txn.costBasisPerShare.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
							<td class="text-center">${escapeHtml(txn.cancelledDate)}</td>
						</tr>
				`;
			});

			// Total row
			const avgBasisPerShare = classData.totalShares > 0 ? totalBasis / classData.totalShares : 0;
			html += `
						<tr class="totals-row">
							<td colspan="3" class="text-right">${escapeHtml(classData.shareClassName)} Totals:</td>
							<td class="text-right">${classData.totalShares.toLocaleString('en-US', { minimumFractionDigits: 4, maximumFractionDigits: 4 })}</td>
							<td></td>
							<td></td>
							<td></td>
							<td class="text-right">${totalBasis.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
							<td class="text-right">${avgBasisPerShare.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
							<td></td>
						</tr>
					</tbody>
				</table>
			</div>
			`;
		});

		// Unvested Data Section (last page)
		if (statementData.unvestedGrants && statementData.unvestedGrants.length > 0) {
			html += `
			<div class="unvested-section">
				<div class="unvested-title">Unvested Equity</div>

				<div class="unvested-summary">
					<div class="unvested-total">
						Total Original (Target) Equity Unit Unvested Value:
						<span class="unvested-total-value">$${statementData.totalUnvestedValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
					</div>
				</div>

				<table class="unvested-table">
					<thead>
						<tr>
							<th>Stock Type</th>
							<th>Equity</th>
							<th>Target Value @ Grant</th>
							<th>Current Price</th>
							<th>Unvested Value</th>
						</tr>
					</thead>
					<tbody>
			`;

			statementData.unvestedGrants.forEach((grant) => {
				html += `
						<tr>
							<td>${escapeHtml(grant.stockType)}</td>
							<td class="text-right">${grant.equity.toLocaleString('en-US', { minimumFractionDigits: 4, maximumFractionDigits: 4 })}</td>
							<td class="text-right">$${grant.targetValueAtGrant.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
							<td class="text-right">$${grant.currentPrice.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
							<td class="text-right">$${grant.unvestedValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
						</tr>
				`;
			});

			html += `
					</tbody>
				</table>
			</div>
			`;
		}

		html += `
		</body>
		</html>
		`;

		return html;
	}

	function splitBWAddress(address) {
		/* Splits address into BW lines: Street / City, State ZIP / Country */
		const a = splitAddressFields(address);

		const line1 = a.address2 ? `${a.address1}, ${a.address2}` : a.address1;
		const line2 = [a.city, a.state && a.zip ? `${a.state} ${a.zip}` : a.state || a.zip].filter(Boolean).join(', ');
		const line3 = a.country || '';

		return { line1: line1 || address || '', line2: line2 || '', line3 };
	}

	/*******************************************************************************
	 * END BARRY-WEHMILLER ACCOUNT STATEMENT FUNCTIONS
	 *******************************************************************************/

	/*******************************************************************************
	 * ACCOUNT STATEMENT PDF FUNCTIONS
	 * Data-driven HTML template with header/footer for PDF generation
	 *******************************************************************************/

	// Shared HTML escaping helper for account statement functions
	const escapeStatementHtml = (str) => {
		if (!str) return '';
		return String(str).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#039;');
	};

	/**
	 * Build SVG chart for account statements (shared by BW and Sheetz)
	 * Generates a simple area chart from time series data
	 * @param {Array} timeSeriesData - Array of [timestamp, value, ...] from TimeSeriesBuilder
	 * @param {Object} options - Chart options { width, height, color }
	 * @returns {string} SVG markup
	 */
	function buildStatementChartSvg(timeSeriesData, options = {}) {
		const width = options.width || 500;
		const height = options.height || 200;
		const color = options.color || '#299aa4';
		const padding = { top: 20, right: 20, bottom: 40, left: 60 };

		// Need at least 2 points for a chart
		if (!timeSeriesData || timeSeriesData.length < 2) {
			return '';
		}

		// Extract timestamps and values (handle both single-line and stacked formats)
		const points = timeSeriesData.map((row) => {
			const timestamp = row[0];
			// Sum all values after timestamp for stacked format, or just use row[1] for single line
			let value = 0;
			for (let i = 1; i < row.length; i++) {
				if (typeof row[i] === 'number' && !isNaN(row[i])) {
					value += row[i];
				}
			}
			return { x: timestamp, y: value };
		});

		// Calculate bounds
		const xMin = Math.min(...points.map((p) => p.x));
		const xMax = Math.max(...points.map((p) => p.x));
		const yMin = 0; // Start from 0 for area chart
		const yMax = Math.max(...points.map((p) => p.y)) * 1.1; // Add 10% headroom

		// Avoid division by zero
		const xRange = xMax - xMin || 1;
		const yRange = yMax - yMin || 1;

		// Chart dimensions
		const chartWidth = width - padding.left - padding.right;
		const chartHeight = height - padding.top - padding.bottom;

		// Scale functions
		const scaleX = (x) => padding.left + ((x - xMin) / xRange) * chartWidth;
		const scaleY = (y) => padding.top + chartHeight - ((y - yMin) / yRange) * chartHeight;

		// Build path for area chart
		const linePath = points
			.map((p, i) => {
				const x = scaleX(p.x);
				const y = scaleY(p.y);
				return `${i === 0 ? 'M' : 'L'} ${x.toFixed(1)} ${y.toFixed(1)}`;
			})
			.join(' ');

		// Close the area path
		const areaPath =
			linePath +
			` L ${scaleX(points[points.length - 1].x).toFixed(1)} ${scaleY(0).toFixed(1)}` +
			` L ${scaleX(points[0].x).toFixed(1)} ${scaleY(0).toFixed(1)} Z`;

		// Format axis labels
		const formatDate = (ts) => {
			const d = new Date(ts);
			return `${d.getMonth() + 1}/${d.getFullYear().toString().slice(-2)}`;
		};
		const formatValue = (v) => {
			if (v >= 1000000) return '$' + (v / 1000000).toFixed(1) + 'M';
			if (v >= 1000) return '$' + (v / 1000).toFixed(0) + 'K';
			return '$' + v.toFixed(0);
		};

		// Generate axis ticks
		const xTicks = 5;
		const yTicks = 4;
		const xTickValues = [];
		const yTickValues = [];

		for (let i = 0; i <= xTicks; i++) {
			xTickValues.push(xMin + (xRange * i) / xTicks);
		}
		for (let i = 0; i <= yTicks; i++) {
			yTickValues.push(yMin + (yRange * i) / yTicks);
		}

		// Convert hex color to RGB components for rgba fill
		const hexToRgb = (hex) => {
			const r = parseInt(hex.slice(1, 3), 16);
			const g = parseInt(hex.slice(3, 5), 16);
			const b = parseInt(hex.slice(5, 7), 16);
			return `${r},${g},${b}`;
		};
		const colorRgb = hexToRgb(color);

		// Build SVG - use 100% width to fill container, viewBox maintains aspect ratio
		let svg = `
		<svg width="100%" height="${height}" viewBox="0 0 ${width} ${height}" preserveAspectRatio="xMidYMid meet" xmlns="http://www.w3.org/2000/svg">
			<defs>
				<linearGradient id="areaGradient" x1="0%" y1="0%" x2="0%" y2="100%">
					<stop offset="0%" style="stop-color:${color};stop-opacity:0.6"/>
					<stop offset="100%" style="stop-color:${color};stop-opacity:0.15"/>
				</linearGradient>
			</defs>

			<!-- Grid lines -->
			<g stroke="#e2e8f0" stroke-width="1">
				${yTickValues.map((v) => `<line x1="${padding.left}" y1="${scaleY(v).toFixed(1)}" x2="${width - padding.right}" y2="${scaleY(v).toFixed(1)}"/>`).join('\n\t\t\t\t')}
			</g>

			<!-- Area fill -->
			<path d="${areaPath}" fill="rgba(${colorRgb},0.25)" stroke="none"/>

			<!-- Line -->
			<path d="${linePath}" fill="none" stroke="${color}" stroke-width="3"/>

			<!-- X axis labels -->
			<g fill="#666" font-size="10" text-anchor="middle">
				${xTickValues.map((v) => `<text x="${scaleX(v).toFixed(1)}" y="${height - 10}">${formatDate(v)}</text>`).join('\n\t\t\t\t')}
			</g>

			<!-- Y axis labels -->
			<g fill="#666" font-size="10" text-anchor="end">
				${yTickValues.map((v) => `<text x="${padding.left - 8}" y="${(scaleY(v) + 3).toFixed(1)}">${formatValue(v)}</text>`).join('\n\t\t\t\t')}
			</g>

			<!-- End point dot -->
			<circle cx="${scaleX(points[points.length - 1].x).toFixed(1)}" cy="${scaleY(points[points.length - 1].y).toFixed(1)}" r="5" fill="${color}"/>
		</svg>`;

		return svg;
	}

	async function renderAccountStatementPdf() {
		/* Main function to render account statement PDF */
		try {
			// Show loading modal
			M.Modal.getInstance(document.getElementById('statementWaitState')).open();

			// Resolve brand colors (fallback to app accent color)
			const primaryColor = clientData.brand_color && /^#[0-9a-fA-F]{3,6}$/.test(clientData.brand_color) ? clientData.brand_color : '#299aa4';
			const textColor = '#243b53';
			const colors = { primaryColor, textColor };

			// Get user data (use shareholder if viewing another user, otherwise current user)
			let shareholderData = JSON.parse(sessionStorage.getItem('shareholder.Data'));
			let targetUserData = shareholderData || userData;

			// On admin users/edit: use shareholder.Data; on investor portal: use global userData
			const pnForTarget = window.location.pathname || '';
			if (!pnForTarget.includes('/admin/users/edit')) {
				targetUserData = userData;
			}
			const targetUserId = targetUserData.userid;

			// Load transaction data
			let processedTransactions = [];
			const transactionMode = clientData.flags?.statement_transaction_mode || 'all_transactions';

			if (transactionMode === 'gift_transfer') {
				// Gift transfer method: incoming certificates via giftTransfer (like BW)
				const { giftTransfer } = await import('./helpers/gift-transfer.js');
				const result = giftTransfer(targetUserId, false, {
					dataOnly: true,
					clientData: clientData,
					shareholderData: targetUserData,
				});
				if (result?.giftTransactions) {
					processedTransactions = result.giftTransactions.filter(
						(t) => Number(String(t.quantity).replace(/[^.\d]/g, '')) > 0 && t.receiver?.toLowerCase() === targetUserId.toLowerCase()
					);
				}
			} else {
				// All transactions method: both directions from clientData (default)
				const statementClasses = Object.keys(clientData.equity).filter((c) => !c.endsWith('-Debt'));
				statementClasses.forEach((c) => {
					if (clientData.equity[c].transactions) {
						clientData.equity[c].transactions.forEach((t) => {
							if (isUserTransaction(t, targetUserId)) {
								processedTransactions.push({ ...t, shareClass: c });
							}
						});
					}
				});
				// Filter out future transactions
				const nextYear = new Date(new Date().getFullYear() + 1, 1, 1) / 1000;
				processedTransactions = processedTransactions.filter((t) => Number(t.date) < nextYear);
			}

			// Build time series data for chart if not already available
			if (!window.lastTimeSeriesResult?.data?.length) {
				try {
					const calculator = calculationService.getCalculatorForClient(clientData);
					const graphConfig = calculator?.graphConfig;
					if (graphConfig?.supportsTimeSeries && graphConfig?.timeSeriesStrategy) {
						const shareKeys = Object.keys(clientData.equity).filter((c) => !c.endsWith('-Debt'));
						window.lastTimeSeriesResult = TimeSeriesBuilder.build(graphConfig.timeSeriesStrategy, {
							clientData: clientData,
							userData: targetUserData,
							client: client,
							shareClasses: shareKeys,
							currentTotalValue: window.lastCalculationResult?.totalValue || 0,
						});
					}
				} catch (e) {
					console.warn('[Statement] Failed to build time series:', e);
				}
			}

			// Build statement data from all share classes
			const statementData = buildStatementData(targetUserData, processedTransactions, transactionMode);

			// Generate basis table (only if flag is enabled)
			const basisTableHtml = clientData.flags?.show_basis_table_in_statement ? await generateBasisTable() : '';

			// Generate HTML template
			const statementHtml = buildStatementHtml(statementData, basisTableHtml, colors);

			// Fetch and convert logo to base64 for PDF header
			let logoBase64 = '';
			if (clientData.company_logo) {
				try {
					const logoResponse = await fetch(clientData.company_logo);
					const logoBlob = await logoResponse.blob();
					logoBase64 = await new Promise((resolve) => {
						const reader = new FileReader();
						reader.onloadend = () => resolve(reader.result);
						reader.readAsDataURL(logoBlob);
					});
				} catch (e) {
					console.error('Failed to fetch logo for PDF header:', e);
				}
			}

			// Generate header and footer templates for PDF
			const headerHtml = buildStatementHeaderTemplate(targetUserData, logoBase64, colors);
			const footerHtml = buildStatementFooterTemplate();

			// Generate PDF using Api2Pdf
			let Api2Pdf = require('api2pdf');
			let a2pClient = new Api2Pdf('6e053f8e-75b7-4271-a316-1981d7640681');
			let userName = targetUserData.user_name?.replace(/_/g, ' ') || 'Shareholder';
			let fileName = 'Account Statement for ' + userName + ' as of ' + moment().format('MM-D-YYYY') + '.pdf';

			const pdfOptions = {
				inline: false,
				filename: fileName,
				options: {
					displayHeaderFooter: true,
					headerTemplate: headerHtml,
					footerTemplate: footerHtml,
					marginTop: '140px',
					marginBottom: '60px',
					marginLeft: '40px',
					marginRight: '40px',
					printBackground: true,
				},
			};

			a2pClient
				.chromeHtmlToPdf(statementHtml, pdfOptions)
				.then(function (pdfResult) {
					let request = new XMLHttpRequest();
					request.open('GET', pdfResult.FileUrl, true);
					request.responseType = 'blob';
					request.onload = function () {
						saveAs(pdfResult.FileUrl, cleanStampedName(fileName));
						M.Modal.getInstance(document.getElementById('statementWaitState')).close();
					};
					request.send();
				})
				.catch(function (err) {
					console.error('PDF generation error:', err);
					M.Modal.getInstance(document.getElementById('statementWaitState')).close();
					M.toast({ html: 'Error generating PDF' });
				});
		} catch (error) {
			console.error('renderAccountStatementPdf error:', error);
			M.Modal.getInstance(document.getElementById('statementWaitState')).close();
			M.toast({ html: 'Error generating statement' });
		}
	}

	function buildStatementData(targetUserData, transactions = [], transactionMode = 'all_transactions') {
		/* Builds statement data from all share classes */
		const classes = Object.keys(clientData.equity).filter((c) => !c.endsWith('-Debt'));
		const statementDate = moment().format('MMMM D, YYYY');
		const userShares = targetUserData.clients?.[client]?.shares || {};

		// Get calculator breakdown for share prices
		const calculatorBreakdown = window.lastCalculationResult?.breakdown || [];

		// Build data for each class
		const byClass = {};
		let totalValue = 0;

		classes.forEach((c) => {
			const calcClassData = calculatorBreakdown.find((cd) => cd.classKey === c);
			const equityConfig = clientData.equity[c] || {};
			const sharesOwned = Number(userShares[c]?.owned || userShares[c]?.approved || 0);
			const currentPrice = calcClassData?.sharePrice || 0;
			const currentValue = sharesOwned * currentPrice;

			byClass[c] = {
				shareClass: c,
				shareClassName: equityConfig.name || c,
				sharesOwned: sharesOwned,
				currentPrice: currentPrice,
				currentValue: currentValue,
				priceEffectiveDate: equityConfig.valuations?.[0]?.effective_date || '',
				transactions: [],
			};

			totalValue += currentValue;
		});

		// Process transactions into per-class arrays
		transactions.forEach((txn) => {
			const sc = txn.shareClass;
			if (!byClass[sc]) return;
			const qty = Number(String(txn.quantity || '').replace(/[^.\d]/g, '')) || 0;
			const price = Number(String(txn.sharePrice || '').replace(/[^.\d]/g, '')) || 0;
			byClass[sc].transactions.push({
				date: txn.date ? moment.unix(txn.date).format('MMM D, YYYY') : '',
				description: txn.notes || '',
				shares: qty,
				sharePrice: price,
				sender: txn.senderName || txn.sender || 'Issuer',
				receiver: txn.receiverName || txn.receiver || 'Issuer',
				certNumber: extractCertificateNumber(txn.notes || txn.description || ''),
			});
		});

		// Get time series data for the chart (already built by TimeSeriesBuilder)
		const timeSeriesData = window.lastTimeSeriesResult?.data || [];

		return {
			statementDate,
			byClass,
			classes,
			totalValue,
			userName: targetUserData.user_name?.replace(/_/g, ' ') || '',
			timeSeriesData,
			transactionMode,
		};
	}

	function buildStatementHtml(statementData, basisTableHtml, colors = {}) {
		/* Generates HTML body content for account statement (BW-style multi-page layout) */
		const primaryColor = colors.primaryColor || '#299aa4';
		const textColor = colors.textColor || '#243b53';
		const thStyle = `background-color: ${primaryColor}; color: white; padding: 10px 8px; text-align: center; font-weight: bold; font-size: 10px; text-transform: uppercase;`;

		let html = `
		<!DOCTYPE html>
		<html>
		<head>
			<style>
				* { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; box-sizing: border-box; }
				html, body { height: auto !important; overflow: visible !important; }
				body {
					font-family: 'Source Sans Pro', Arial, sans-serif;
					font-size: 12px;
					margin: 0;
					padding: 0;
					color: ${textColor};
				}
				table { page-break-inside: auto; width: 100%; border-collapse: collapse; }
				thead { display: table-header-group; }
				tr { page-break-inside: avoid; page-break-after: auto; }
				.portfolio-summary {
					padding: 20px;
					page-break-after: always;
				}
				.class-section {
					padding: 20px;
				}
				.class-section.page-break {
					page-break-before: always;
				}
				.section-title {
					font-size: 16px;
					font-weight: bold;
					color: ${textColor};
					margin-bottom: 15px;
					border-bottom: 2px solid ${primaryColor};
					padding-bottom: 5px;
				}
				.holdings-grid {
					display: flex;
					flex-wrap: wrap;
					gap: 12px;
					margin-bottom: 20px;
				}
				.holding-card {
					background: white;
					border: 1px solid #e2e8f0;
					border-radius: 8px;
					padding: 12px 16px;
					min-width: 150px;
					flex: 1;
				}
				.holding-class {
					font-size: 11px;
					color: #666;
					text-transform: uppercase;
					margin-bottom: 4px;
				}
				.holding-shares {
					font-size: 16px;
					font-weight: bold;
					color: ${textColor};
				}
				.summary-grid {
					background: #f0f4f8;
					border-radius: 8px;
					padding: 15px;
					margin-bottom: 20px;
					display: flex;
					justify-content: space-between;
					flex-wrap: wrap;
				}
				.summary-item {
					text-align: center;
					flex: 1;
					min-width: 140px;
					padding: 10px;
				}
				.summary-label {
					font-size: 10px;
					color: #666;
					text-transform: uppercase;
					margin-bottom: 5px;
				}
				.summary-value {
					font-size: 16px;
					font-weight: bold;
					color: ${textColor};
				}
				.summary-value.large {
					font-size: 22px;
					color: ${primaryColor};
				}
				.summary-card {
					background: #f0f4f8;
					border-radius: 8px;
					padding: 15px;
					margin-bottom: 20px;
				}
				.summary-card h3 {
					margin: 0 0 12px 0;
					font-size: 14px;
					color: ${primaryColor};
					font-weight: bold;
				}
				.detail-table {
					width: 100%;
					border-collapse: collapse;
					margin-top: 15px;
					font-size: 11px;
				}
				.detail-table th {
					background-color: ${primaryColor};
					color: white;
					padding: 10px 8px;
					text-align: center;
					font-weight: bold;
					font-size: 10px;
					text-transform: uppercase;
				}
				.detail-table td {
					padding: 8px;
					border-bottom: 1px solid #e2e8f0;
				}
				.detail-table tr:nth-child(even) {
					background-color: #f8fafc;
				}
				.text-right { text-align: right; }
				.text-center { text-align: center; }
				.chart-section { margin-bottom: 20px; }
				.chart-title {
					font-size: 14px;
					font-weight: bold;
					color: ${textColor};
					margin-bottom: 10px;
				}
				.basis-section { margin-top: 30px; }
				.basis-section table { width: 100%; border-collapse: collapse; font-size: 11px; }
				.basis-section td { padding: 8px; border-bottom: 1px solid #e2e8f0; text-align: center; }
				.basis-section tr:nth-child(even) { background-color: #f8fafc; }
			</style>
		</head>
		<body>
		`;

		// === PAGE 1: Portfolio Summary ===
		const classesWithShares = statementData.classes.filter(
			(c) => statementData.byClass[c].sharesOwned > 0 || (statementData.byClass[c].transactions && statementData.byClass[c].transactions.length > 0)
		);

		html += `<div class="portfolio-summary">`;

		// Holdings Cards
		html += `
			<div class="section-title">Your Holdings</div>
			<div class="holdings-grid">
		`;
		classesWithShares.forEach((c) => {
			const classData = statementData.byClass[c];
			html += `
				<div class="holding-card">
					<div class="holding-class">${escapeStatementHtml(classData.shareClassName)}</div>
					<div class="holding-shares">${classData.sharesOwned.toLocaleString('en-US')} shares</div>
				</div>
			`;
		});
		html += `</div>`;

		// Summary Grid
		html += `
			<div class="summary-grid">
				<div class="summary-item">
					<div class="summary-label">Statement Date</div>
					<div class="summary-value">${statementData.statementDate}</div>
				</div>
				<div class="summary-item">
					<div class="summary-label">Total Current Value</div>
					<div class="summary-value large">$${statementData.totalValue.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</div>
				</div>
			</div>
		`;

		// Portfolio Value Chart
		if (statementData.timeSeriesData && statementData.timeSeriesData.length >= 2) {
			const oneDayAgo = Date.now() - 24 * 60 * 60 * 1000;
			const chartData = statementData.timeSeriesData.filter((row) => row[0] < oneDayAgo);
			if (chartData.length >= 2) {
				const chartSvg = buildStatementChartSvg(chartData, { width: 700, height: 180, color: primaryColor });
				if (chartSvg) {
					html += `
						<div class="chart-section">
							<div class="chart-title">Portfolio Value Over Time</div>
							<div style="padding: 10px 0;">${chartSvg}</div>
						</div>
					`;
				}
			}
		}

		// Value by Class breakdown on summary page
		if (classesWithShares.length > 0) {
			html += `<div class="section-title">Value by Share Class</div>`;
			classesWithShares.forEach((c) => {
				const cd = statementData.byClass[c];
				html += `
					<div style="display: flex; justify-content: space-between; padding: 10px 15px; border-bottom: 1px solid #e2e8f0;">
						<div style="font-weight: bold;">${escapeStatementHtml(cd.shareClassName)}</div>
						<div style="text-align: right;">
							${cd.sharesOwned.toLocaleString('en-US')} shares \u00d7 $${cd.currentPrice.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} =
							<strong>$${cd.currentValue.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</strong>
						</div>
					</div>
				`;
			});
		}

		html += `</div>`; // close .portfolio-summary (triggers page-break-after: always)

		// === SUBSEQUENT PAGES: Per-Class Detail with Transactions ===
		let isFirstClass = true;
		classesWithShares.forEach((c) => {
			const classData = statementData.byClass[c];
			const pageBreakClass = isFirstClass ? '' : 'page-break';
			isFirstClass = false;

			html += `
			<div class="class-section ${pageBreakClass}">
				<div class="summary-card">
					<h3>${escapeStatementHtml(classData.shareClassName)}</h3>
					<div style="display: flex; justify-content: space-between; flex-wrap: wrap;">
						<div class="summary-item">
							<div class="summary-label">Shares Owned</div>
							<div class="summary-value">${classData.sharesOwned.toLocaleString('en-US')}</div>
						</div>
						<div class="summary-item">
							<div class="summary-label">Current Price</div>
							<div class="summary-value">$${classData.currentPrice.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
						</div>
						<div class="summary-item">
							<div class="summary-label">Current Value</div>
							<div class="summary-value">$${classData.currentValue.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</div>
						</div>
					</div>
				</div>
			`;

			// Transaction table for this class
			if (classData.transactions && classData.transactions.length > 0) {
				const hasPrice = classData.transactions.some((txn) => txn.sharePrice > 0);
				const isGiftTransfer = statementData.transactionMode === 'gift_transfer';
				html += `
				<table class="detail-table">
					<thead>
						<tr>
							<th bgcolor="${primaryColor}" style="${thStyle}">Date</th>
							${isGiftTransfer ? `<th bgcolor="${primaryColor}" style="${thStyle}">Cert #</th>` : ''}
							<th bgcolor="${primaryColor}" style="${thStyle}">Description</th>
							${!isGiftTransfer ? `<th bgcolor="${primaryColor}" style="${thStyle}">Sender</th>` : ''}
							${!isGiftTransfer ? `<th bgcolor="${primaryColor}" style="${thStyle}">Receiver</th>` : ''}
							<th bgcolor="${primaryColor}" style="${thStyle}">Quantity</th>
							${hasPrice ? `<th bgcolor="${primaryColor}" style="${thStyle}">Share Price</th>` : ''}
						</tr>
					</thead>
					<tbody>
				`;

				classData.transactions.forEach((txn) => {
					html += `
						<tr>
							<td class="text-center">${escapeStatementHtml(txn.date)}</td>
							${isGiftTransfer ? `<td class="text-center">${escapeStatementHtml(txn.certNumber || '')}</td>` : ''}
							<td>${escapeStatementHtml(txn.description)}</td>
							${!isGiftTransfer ? `<td>${escapeStatementHtml(txn.sender)}</td>` : ''}
							${!isGiftTransfer ? `<td>${escapeStatementHtml(txn.receiver)}</td>` : ''}
							<td class="text-right">${txn.shares.toLocaleString('en-US')}</td>
							${hasPrice ? `<td class="text-right">${txn.sharePrice ? '$' + txn.sharePrice.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : ''}</td>` : ''}
						</tr>
					`;
				});

				html += `
					</tbody>
				</table>
				`;
			}

			html += `</div>`; // close .class-section
		});

		// Basis Table Section (if enabled)
		if (basisTableHtml) {
			html += `
				<div class="class-section page-break">
					<div class="basis-section">
						<div class="section-title">Cost Basis Details</div>
						${basisTableHtml}
					</div>
				</div>
			`;
		}

		html += `
		</body>
		</html>
		`;

		return html;
	}

	function buildStatementHeaderTemplate(targetUserData, logoBase64 = '', colors = {}) {
		/* Generates PDF header template for account statement */
		const primaryColor = colors.primaryColor || '#299aa4';
		const textColor = colors.textColor || '#243b53';

		const userName = escapeStatementHtml(targetUserData.user_name?.replace(/_/g, ' ') || '');
		const companyName = escapeStatementHtml(clientData.company_name || '');

		const logoOrName = logoBase64
			? `<img class="company-logo" src="${logoBase64}" alt="${companyName}">`
			: `<span class="company-name">${companyName}</span>`;

		return `<style>
			* { margin: 0; padding: 0; box-sizing: border-box; -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
			.header-container { width: 100%; font-family: Arial, sans-serif; padding: 15px 30px; font-size: 10px; }
			.company-logo { max-height: 50px; }
			.company-name { font-size: 20px; font-weight: bold; color: ${primaryColor}; }
			.header-row { display: flex; justify-content: space-between; align-items: center; margin-top: 10px; }
			.statement-title { font-size: 14px; font-weight: bold; color: ${textColor}; }
			.user-name { font-size: 12px; color: #666; }
		</style>
		<div class="header-container">
			<div style="text-align: center;">
				${logoOrName}
			</div>
			<div class="header-row">
				<div class="statement-title">Shareholder Account Statement</div>
				<div class="user-name">${userName}</div>
			</div>
		</div>`;
	}

	function buildStatementFooterTemplate() {
		/* Generates PDF footer template for account statement */
		return `<style>
			* { margin: 0; padding: 0; box-sizing: border-box; -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
			.footer-container { width: 100%; font-family: Arial, sans-serif; font-size: 9px; padding: 8px 30px; color: #666; }
			.footer-inner { border-top: 1px solid #e2e8f0; padding: 8px 0; display: flex; justify-content: space-between; align-items: center; }
			.page-num { font-size: 10px; color: #243b53; }
		</style>
		<div class="footer-container">
			<div class="footer-inner">
				<div>Confidential - For recipient only</div>
				<div class="page-num">Page <span class="pageNumber"></span></div>
			</div>
		</div>`;
	}

	/*******************************************************************************
	 * END ACCOUNT STATEMENT PDF FUNCTIONS
	 *******************************************************************************/

	/*******************************************************************************
	 * FUND INVESTOR ACCOUNT STATEMENT FUNCTIONS
	 *******************************************************************************/

	async function renderFundAccountStatement() {
		try {
			M.Modal.getInstance(document.getElementById('statementWaitState')).open();

			let shareholderData = JSON.parse(sessionStorage.getItem('shareholder.Data'));
			let targetUserData = shareholderData || userData;

			const statementData = buildFundStatementData(targetUserData);
			const statementHtml = buildFundStatementHtml(statementData);

			let logoBase64 = '';
			if (clientData.company_logo) {
				try {
					const logoResponse = await fetch(clientData.company_logo);
					const logoBlob = await logoResponse.blob();
					logoBase64 = await new Promise((resolve) => {
						const reader = new FileReader();
						reader.onloadend = () => resolve(reader.result);
						reader.readAsDataURL(logoBlob);
					});
				} catch (e) {
					console.error('Failed to fetch logo for PDF header:', e);
				}
			}

			const headerHtml = buildFundHeaderTemplate(targetUserData, logoBase64);
			const footerHtml = buildFundFooterTemplate();

			let Api2Pdf = require('api2pdf');
			let a2pClient = new Api2Pdf('6e053f8e-75b7-4271-a316-1981d7640681');
			let userName = targetUserData.user_name?.replace(/_/g, ' ') || 'Investor';
			let fileName = 'Account Statement for ' + userName + ' as of ' + moment().format('MM-D-YYYY') + '.pdf';

			const pdfOptions = {
				inline: false,
				filename: fileName,
				options: {
					displayHeaderFooter: true,
					headerTemplate: headerHtml,
					footerTemplate: footerHtml,
					marginTop: '140px',
					marginBottom: '60px',
					marginLeft: '40px',
					marginRight: '40px',
					printBackground: true,
				},
			};

			a2pClient
				.chromeHtmlToPdf(statementHtml, pdfOptions)
				.then(function (pdfResult) {
					let request = new XMLHttpRequest();
					request.open('GET', pdfResult.FileUrl, true);
					request.responseType = 'blob';
					request.onload = function () {
						saveAs(pdfResult.FileUrl, cleanStampedName(fileName));
						M.Modal.getInstance(document.getElementById('statementWaitState')).close();
					};
					request.send();
				})
				.catch(function (err) {
					console.error('PDF generation error:', err);
					M.Modal.getInstance(document.getElementById('statementWaitState')).close();
					M.toast({ html: 'Error generating PDF' });
				});
		} catch (error) {
			console.error('renderFundAccountStatement error:', error);
			M.Modal.getInstance(document.getElementById('statementWaitState')).close();
			M.toast({ html: 'Error generating statement' });
		}
	}

	function buildFundStatementData(targetUserData) {
		const calcResult = window.lastCalculationResult || {};

		return {
			statementDate: moment().format('MMMM D, YYYY'),
			userName: targetUserData.user_name?.replace(/_/g, ' ') || '',
			totalValue: calcResult.totalValue || 0,
			totalContributions: calcResult.totalContributions || 0,
			totalDistributions: calcResult.totalDistributions || 0,
			totalCapitalAccount: calcResult.totalCapitalAccount || 0,
			totalReturnsOfCapital: calcResult.totalReturnsOfCapital || 0,
			breakdown: calcResult.breakdown || [],
			fundMetrics: calcResult.fundMetrics || {},
		};
	}

	function buildFundStatementHtml(statementData) {
		const textColor = '#243b53';
		const fmtCurrency = (v) => '$' + Number(v || 0).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
		const fmtMultiple = (v) => Number(v || 0).toFixed(2) + 'x';

		let html = `
		<!DOCTYPE html>
		<html>
		<head>
			<style>
				body {
					font-family: 'Source Sans Pro', Arial, sans-serif;
					font-size: 12px;
					margin: 0;
					padding: 20px;
					color: #243b53;
				}
				.summary-section {
					page-break-inside: avoid;
					background: #f0f4f8;
					border-radius: 8px;
					padding: 20px;
					margin-bottom: 30px;
				}
				.summary-grid {
					display: flex;
					justify-content: space-between;
					flex-wrap: wrap;
				}
				.summary-item {
					text-align: center;
					flex: 1;
					min-width: 120px;
					padding: 10px;
				}
				.summary-label {
					font-size: 10px;
					color: #666;
					text-transform: uppercase;
					margin-bottom: 5px;
				}
				.summary-value {
					font-size: 14px;
					font-weight: bold;
					color: #243b53;
				}
				.summary-value.large {
					font-size: 24px;
					color: #1a5276;
				}
				.section-title {
					font-size: 16px;
					font-weight: bold;
					color: ${textColor};
					margin-bottom: 15px;
					border-bottom: 2px solid #1a5276;
					padding-bottom: 5px;
				}
				.holdings-section {
					margin-bottom: 30px;
				}
				table.breakdown {
					width: 100%;
					border-collapse: collapse;
					font-size: 11px;
				}
				table.breakdown th {
					background-color: #1a5276;
					color: white;
					padding: 10px 8px;
					text-align: right;
					font-weight: bold;
					font-size: 10px;
				}
				table.breakdown th:first-child {
					text-align: left;
				}
				table.breakdown td {
					padding: 8px;
					border-bottom: 1px solid #e2e8f0;
					text-align: right;
				}
				table.breakdown td:first-child {
					text-align: left;
					font-weight: bold;
				}
				table.breakdown tr:nth-child(even) {
					background-color: #f8fafc;
				}
				table.breakdown tfoot td {
					font-weight: bold;
					border-top: 2px solid #1a5276;
					background: #f0f4f8;
				}
			</style>
		</head>
		<body>
		`;

		// Summary block: Statement Date + Total Value
		html += `
			<div class="summary-section">
				<div class="summary-grid">
					<div class="summary-item">
						<div class="summary-label">Statement Date</div>
						<div class="summary-value">${statementData.statementDate}</div>
					</div>
					<div class="summary-item">
						<div class="summary-label">Total Value</div>
						<div class="summary-value large">${fmtCurrency(statementData.totalValue)}</div>
					</div>
				</div>
			</div>
		`;

		// Account Metrics
		html += `
			<div style="display: flex; justify-content: space-between; margin-bottom: 30px; padding-bottom: 20px; border-bottom: 1px solid #e2e8f0;">
				<div style="text-align: center; flex: 1;">
					<div class="summary-label">Capital Account</div>
					<div class="summary-value">${fmtCurrency(statementData.totalCapitalAccount)}</div>
				</div>
				<div style="text-align: center; flex: 1;">
					<div class="summary-label">Contributions</div>
					<div class="summary-value">${fmtCurrency(statementData.totalContributions)}</div>
				</div>
				<div style="text-align: center; flex: 1;">
					<div class="summary-label">Distributions</div>
					<div class="summary-value">${fmtCurrency(statementData.totalDistributions)}</div>
				</div>
				<div style="text-align: center; flex: 1;">
					<div class="summary-label">Returns of Capital</div>
					<div class="summary-value">${fmtCurrency(statementData.totalReturnsOfCapital)}</div>
				</div>
				<div style="text-align: center; flex: 1;">
					<div class="summary-label">TVPI</div>
					<div class="summary-value">${fmtMultiple(statementData.fundMetrics.tvpi)}</div>
				</div>
			</div>
		`;

		// 2-bar comparison chart: Contributions vs Total Value
		if (statementData.totalContributions > 0 || statementData.totalValue > 0) {
			const chartSvg = buildFundComparisonChartSvg(statementData);
			if (chartSvg) {
				html += `
					<div class="holdings-section">
						<div class="section-title">Paid-In Capital vs Total Value</div>
						<div style="padding: 10px 0;">
							${chartSvg}
						</div>
					</div>
				`;
			}
		}

		// Holdings by Fund/Class breakdown table
		if (statementData.breakdown.length > 0) {
			html += `
				<div class="holdings-section">
					<div class="section-title">Holdings by Fund</div>
					<table class="breakdown">
						<thead>
							<tr>
								<th>Fund / Class</th>
								<th>Contributions</th>
								<th>Distributions</th>
								<th>Returns of Capital</th>
								<th>Capital Account</th>
								<th>Total Value</th>
								<th>TVPI</th>
							</tr>
						</thead>
						<tbody>
			`;

			statementData.breakdown.forEach((row) => {
				html += `
							<tr>
								<td>${escapeStatementHtml(row.className)}</td>
								<td>${fmtCurrency(row.contributions)}</td>
								<td>${fmtCurrency(row.distributions)}</td>
								<td>${fmtCurrency(row.returnsOfCapital)}</td>
								<td>${fmtCurrency(row.capitalAccount)}</td>
								<td>${fmtCurrency(row.value)}</td>
								<td>${fmtMultiple(row.tvpi)}</td>
							</tr>
				`;
			});

			html += `
						</tbody>
						<tfoot>
							<tr>
								<td>Total</td>
								<td>${fmtCurrency(statementData.totalContributions)}</td>
								<td>${fmtCurrency(statementData.totalDistributions)}</td>
								<td>${fmtCurrency(statementData.totalReturnsOfCapital)}</td>
								<td>${fmtCurrency(statementData.totalCapitalAccount)}</td>
								<td>${fmtCurrency(statementData.totalValue)}</td>
								<td>${fmtMultiple(statementData.fundMetrics.tvpi)}</td>
							</tr>
						</tfoot>
					</table>
				</div>
			`;
		}

		html += `
		</body>
		</html>
		`;

		return html;
	}

	function buildFundComparisonChartSvg(statementData) {
		const width = 700;
		const height = 250;
		const barWidth = 140;
		const gap = 100;
		const maxVal = Math.max(statementData.totalContributions, statementData.totalValue, 1);
		const chartTop = 45;
		const chartBottom = height - 50;
		const chartHeight = chartBottom - chartTop;
		const centerX = width / 2;
		const r = 4; // border radius

		const bar1X = centerX - gap / 2 - barWidth;
		const bar2X = centerX + gap / 2;

		const bar1Height = Math.max((statementData.totalContributions / maxVal) * chartHeight, 2);
		const bar2Height = Math.max((statementData.totalValue / maxVal) * chartHeight, 2);

		const fmtVal = (v) => '$' + Number(v || 0).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });

		// Stacked segments for Total Value bar
		const capAcct = statementData.totalCapitalAccount || 0;
		const distrib = statementData.totalDistributions || 0;
		const roc = statementData.totalReturnsOfCapital || 0;
		const totalVal = statementData.totalValue || 1;

		const capAcctH = (capAcct / totalVal) * bar2Height;
		const distribH = (distrib / totalVal) * bar2Height;
		const rocH = (roc / totalVal) * bar2Height;

		// Gain calculation
		const gain = statementData.totalValue - statementData.totalContributions;
		const gainPct = statementData.totalContributions > 0 ? ((gain / statementData.totalContributions) * 100).toFixed(1) : 0;
		const gainSign = gain >= 0 ? '+' : '';

		const legendY = chartBottom + 32;

		return `<svg viewBox="0 0 ${width} ${height}" xmlns="http://www.w3.org/2000/svg" style="width:100%;max-width:${width}px;">
			<defs>
				<clipPath id="bar1Clip"><rect x="${bar1X}" y="${chartBottom - bar1Height}" width="${barWidth}" height="${bar1Height}" rx="${r}"/></clipPath>
				<clipPath id="bar2Clip"><rect x="${bar2X}" y="${chartBottom - bar2Height}" width="${barWidth}" height="${bar2Height}" rx="${r}"/></clipPath>
			</defs>

			<!-- Bar 1: Contributions -->
			<rect x="${bar1X}" y="${chartBottom - bar1Height}" width="${barWidth}" height="${bar1Height}" fill="#90a4ae" clip-path="url(#bar1Clip)"/>
			<text x="${bar1X + barWidth / 2}" y="${chartBottom - bar1Height - 10}" text-anchor="middle" font-size="13" font-weight="bold" fill="#243b53">${fmtVal(statementData.totalContributions)}</text>
			<text x="${bar1X + barWidth / 2}" y="${chartBottom + 18}" text-anchor="middle" font-size="11" font-weight="bold" fill="#243b53">Paid-In Capital</text>

			<!-- Bar 2: Total Value (stacked segments clipped to rounded rect) -->
			<g clip-path="url(#bar2Clip)">
				<rect x="${bar2X}" y="${chartBottom - capAcctH}" width="${barWidth}" height="${capAcctH}" fill="#1a5276"/>
				<rect x="${bar2X}" y="${chartBottom - capAcctH - distribH}" width="${barWidth}" height="${distribH}" fill="#2980b9"/>
				<rect x="${bar2X}" y="${chartBottom - bar2Height}" width="${barWidth}" height="${rocH}" fill="#5dade2"/>
			</g>
			<text x="${bar2X + barWidth / 2}" y="${chartBottom - bar2Height - 10}" text-anchor="middle" font-size="13" font-weight="bold" fill="#243b53">${fmtVal(statementData.totalValue)}</text>
			<text x="${bar2X + barWidth / 2}" y="${chartBottom + 18}" text-anchor="middle" font-size="11" font-weight="bold" fill="#243b53">Total Value</text>

			<!-- Net return annotation (centered between bars) -->
			<text x="${centerX}" y="${chartTop - 18}" text-anchor="middle" font-size="11" fill="#486581">${gainSign}${fmtVal(Math.abs(gain))} (${gainSign}${gainPct}%) net return</text>

			<!-- Legend -->
			<rect x="${centerX - 180}" y="${legendY}" width="10" height="10" fill="#1a5276" rx="2"/>
			<text x="${centerX - 165}" y="${legendY + 9}" font-size="10" fill="#666">Capital Account</text>
			<rect x="${centerX - 60}" y="${legendY}" width="10" height="10" fill="#2980b9" rx="2"/>
			<text x="${centerX - 45}" y="${legendY + 9}" font-size="10" fill="#666">Distributions</text>
			<rect x="${centerX + 55}" y="${legendY}" width="10" height="10" fill="#5dade2" rx="2"/>
			<text x="${centerX + 70}" y="${legendY + 9}" font-size="10" fill="#666">Returns of Capital</text>
		</svg>`;
	}

	function buildFundHeaderTemplate(targetUserData, logoBase64 = '') {
		const textColor = '#243b53';
		const userName = escapeStatementHtml(targetUserData.user_name?.replace(/_/g, ' ') || '');
		const companyName = escapeStatementHtml(clientData.company_name || '');

		const logoOrName = logoBase64
			? `<img class="company-logo" src="${logoBase64}" alt="${companyName}">`
			: `<span class="company-name">${companyName}</span>`;

		return `<style>
			* { margin: 0; padding: 0; box-sizing: border-box; -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
			.header-container { width: 100%; font-family: Arial, sans-serif; padding: 15px 30px; font-size: 10px; }
			.company-logo { max-height: 50px; }
			.company-name { font-size: 20px; font-weight: bold; color: #1a5276; }
			.header-row { display: flex; justify-content: space-between; align-items: center; margin-top: 10px; }
			.statement-title { font-size: 14px; font-weight: bold; color: ${textColor}; }
			.user-name { font-size: 12px; color: #666; }
		</style>
		<div class="header-container">
			<div style="text-align: center;">
				${logoOrName}
			</div>
			<div class="header-row">
				<div class="statement-title">Account Statement</div>
				<div class="user-name">${userName}</div>
			</div>
		</div>`;
	}

	function buildFundFooterTemplate() {
		return `<style>
			* { margin: 0; padding: 0; box-sizing: border-box; -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
			.footer-container { width: 100%; font-family: Arial, sans-serif; font-size: 9px; padding: 8px 30px; color: #666; }
			.footer-inner { border-top: 1px solid #e2e8f0; padding: 8px 0; display: flex; justify-content: space-between; align-items: center; }
			.page-num { font-size: 10px; color: #243b53; }
		</style>
		<div class="footer-container">
			<div class="footer-inner">
				<div>Confidential - For recipient only</div>
				<div class="page-num">Page <span class="pageNumber"></span></div>
			</div>
		</div>`;
	}

	/*******************************************************************************
	 * END FUND INVESTOR ACCOUNT STATEMENT FUNCTIONS
	 *******************************************************************************/

	async function buildTransactionStatement() {
		/* NOTES
		This generates a pdf of the Account Statement template
		*/

		// Check if transaction statement is explicitly enabled via flag
		const transactionStatementEnabled = clientData.flags?.show_transaction_statement === true;

		if (transactionStatementEnabled || !clientData.flags?.enable_dashboard) {
			viewCapAcctIconLarge.classList.remove('hidden');
			viewCapAcctIconSmall.classList.remove('hidden');
			viewCapAcctIconLarge.title = 'Share Transaction Statement';
			viewCapAcctIconSmall.title = 'Share Transaction Statement';
			document.getElementById('viewCapAcctTriggerLarge')?.classList.remove('modal-trigger');
			document.getElementById('viewCapAcctTriggerSmall')?.classList.remove('modal-trigger');
		}
		if (!addedAccountSummaryListenersAlready) {
			addedAccountSummaryListenersAlready = true;
			narrow = screen.width < 600;
			if (narrow) viewCapAcctIconSmall.addEventListener('click', (e) => renderTransactionStatement());
			else viewCapAcctIconLarge.addEventListener('click', (e) => renderTransactionStatement());
		}
	}

	async function renderTransactionStatement() {
		M.Modal.getInstance(document.getElementById('statementWaitState')).open();

		const statementUserIds = getActiveUserIds(userData);
		const lastTransactionDate = clientData
			? moment(
					Math.max(
						...Object.values(clientData.equity || {})
							.flatMap((s) => s.transactions || [])
							.filter((t) => isUserTransaction(t, statementUserIds))
							.map((t) => Number(t.date) * 1000)
					)
				).format('MMMM D, YYYY') || null
			: null;

		const classesWithShares = Object.keys(userData.clients[client]?.shares || {}).filter(
			(shareClass) => shareClass !== 'GO' && Number(userData.clients[client].shares[shareClass]?.owned) > 0
		);

		// for Marvin
		let specialClassWording = 'unknown';
		if (classesWithShares?.length) {
			if (classesWithShares?.length > 1) specialClassWording = 'combined voting and non-voting';
			else
				switch (classesWithShares[0]) {
					case 'V':
						specialClassWording = 'voting';
						break;
					case 'NV':
						specialClassWording = 'non-voting';
						break;
				}
		}

		let thisDate = moment().format('MMMM D, YYYY');
		let stmtShares = userData.clients[client]?.shares;

		if (isMAR) {
			const hasGOProperty = 'GO' in stmtShares;
			if (hasGOProperty) delete stmtShares.GO;
		}

		stmtShares = Object.values(stmtShares);

		let aggregateBalance = stmtShares.reduce((sum, s) => sum + (Number(s?.owned) || 0) && Number(s.owned), 0);
		let statementJson = {
			today: thisDate,
			shareholder_name: userData.user_name,
			last_transaction_date: lastTransactionDate,
			special_class_wording: specialClassWording,
			aggregate_balance: aggregateBalance.toLocaleString(),
		};

		let statementFileUrl = await getContentUrl(clientData.templates.find((t) => t.includes('_ALL_Account_Statement.html')));
		fetch(statementFileUrl)
			.then(function (response) {
				console.log(response);
				return response.text();
			})
			.then(function (html) {
				// TODO: #4629 - migrate template image URLs to presigned
				let htmlResults = html.replace(/src="images\//g, 'src="' + statementFileUrl + '_');
				// htmlResults = htmlResults.replace(/<body /, '<body style="width:600px; min-width:600px; max-width:600px;" ');
				Object.keys(statementJson).forEach(
					(e) =>
						(htmlResults = htmlResults
							.replace(new RegExp('{' + e + '}', 'g'), statementJson[e])
							.replace(new RegExp('{<span.*class=SpellE>' + e + '</span>}', 'g'), '<span class=SpellE>' + statementJson[e] + '</span>'))
				);
				generateStatement(htmlResults);
			})
			.catch(function (err) {
				// There was an error
				console.warn('Something went wrong.', err);
			});

		// HTML Approach
		function generateStatement(htmlResults) {
			console.log('Creating capital statement PDF');
			let fileName = userData.user_name + ' Transaction Statement for ' + thisDate + '.pdf';
			document.dispatchEvent(new CustomEvent('ConvertToPDF', { detail: { htmlResults: htmlResults, fileName: fileName, investor: userData.userid } }));
			if (!addedResultantPDFListenerAlready) {
				addedResultantPDFListenerAlready = true;
				document.addEventListener('ResultantPDF', function (ev) {
					// uploadToYourDocs(ev.detail.url, ev.detail.fileName, ev.detail.investor);
					// window.open(ev.detail.url, '_blank', 'noopener,noreferrer');

					// DOWNLOAD PDF FROM URL
					let request = new XMLHttpRequest();
					request.open('GET', ev.detail.url, true);
					request.responseType = 'blob';
					request.onload = function () {
						saveAs(ev.detail.url, cleanStampedName(fileName));
						M.Modal.getInstance(document.getElementById('statementWaitState')).close();
					};
					request.send();

					if (window.analytics)
						window.analytics.track('Generated Statement', {
							userEmail: authedUser,
							workspace: client,
							documentTitle: fileName,
						});
				});
			}
		}
	}

	// SHOW YOUR DOCUMENTS
	let certs = JSON.parse(sessionStorage.getItem('stockCerts')) || [];
	let documents = userData.clients[client].documents || [];
	if (certs.length > 0 || !USE_CERTIFICATES) {
		if (documents) documents = documents.concat(certs);
		else documents = certs;
		previewDocuments();
	} else {
		async function getStockCertificates() {
			documents = await addStockCertificates(documents);
			previewDocuments();
		}
		getStockCertificates();
	}

	function previewDocuments() {
		let yourDocumentsDiv = document.getElementById('yourDocumentsDiv');
		if (clientData.tabs?.hide_your_docs) {
			yourDocumentsDiv.classList.add('hidden');
			return;
		} else yourDocumentsDiv.classList.remove('hidden');

		documents = stripObsoleteCertificates(documents);
		if (isSheetz) documents = addUnifiedCertificates(documents);
		if (documents) documents = sortByDate(documents, 'date').slice(0, 3); //5)

		let documentList = document.getElementById('documentList');
		let emptyState = '';
		let documentsMarkup = '';
		let emptyStateWords = 'Your K-1s, signature pages, and other personal items will appear here.';
		if (!UFL && !clientData.use_real_estate_language) emptyStateWords = 'Your NDAs, certificates, and other personal items will appear here.';
		if (userData.clients[client].alias?.length > 0)
			emptyStateWords =
				'No documents here. To look elsewhere, click&ensp;<a href="#workspaceModal" class="modal-trigger text-sm link-accent">Switch Account</a>.';
		emptyState = `<div class="bg-grey-lightest rounded border-grey grey-text center-align p-2">${emptyStateWords}</div>`;
		if (!documents) {
			console.log('This shareholder has an incomplete data record, please contact support.');
			documentsMarkup = emptyState;
		} else {
			// console.log(email, ethAddress, documents)
			// TODO: Display document list
			let docData = documents;

			if (docData.length) {
				// Not doing Tooltip Views yet, or ever?
				if (false && isAdmin) {
					docData.forEach(function (d, index) {
						let tooltip = 'Recent Views:';
						if (d.views) {
							let views = d.views.split(',');
							views.forEach(function (v, index) {
								tooltip +=
									'\r\n' +
									moment(Number(v) * 1000)
										.format('lll')
										.replace(/,/g, '');
							});
							docData[index].tooltip = tooltip;
						}
					});
				}
				documentsMarkup = `
					<div class="border-grey rounded p-1">
					${docData
						.map(
							(doc) =>
								`
						<div class="row border-b-1 mb-0 mb-4" ${isAdmin && doc.tooltip ? 'title="' + doc.tooltip + '"' : ''}>
							<div class="col s8">
							<p class="text-black mb-0 mt-0 document-link">
								<a href="/documents/view/?document=${doc.id}">${getShareholderDocName(doc)}${doc.tag && doc.tag == 'TAX' ? '&ensp;<span class="pill pill--gray">' + doc.tag + '</span>' : ''}</a>
							</p>
							<p class="text-sm text-secondary mb-4 mt-0">${doc.description || ''}</p>
							</div>
							<div class="col s4">
								<p class="text-xs mb-0 mt-0 text-secondary">${doc.date}</p>
							</div>
							
						</div>
						`
						)
						.join('')}
					<hr class="border-t-1" />
					<a href="/documents" class="btn bg-grey-darker block">View All</a>
					</div>
				`;
			} else {
				documentsMarkup = emptyState;
			}
		}
		documentList.innerHTML = documentsMarkup;

		// SHOW ROOMS SECTION (only for clients without enable_dashboard flag)
		let roomsList = document.getElementById('roomsList');
		let rooms = userData.rooms;
		if (rooms && !clientData.flags?.enable_dashboard) {
			let roomsMarkup = `
			<div class="row mt-0">
				<div class="col s12 m8">
					<h3 class="text-xl font-bold">Your Rooms</h3>
				</div>
			</div>`;
			let clientRooms = rooms.filter(function (r) {
				return r.client == client;
			});
			clientRooms = clientRooms.sort((a, b) => (Number(a.date) > Number(b.date) ? -1 : 1));
			let roomEmptyState = `
			<div class="bg-grey-lightest rounded border-grey grey-text center-align p-2">
				You have not been added to any rooms yet
			</div>
			`;
			const roomsWrapper = document.getElementById('roomsWrapper');
			if (!clientRooms || !clientRooms.length) {
				document.getElementById('roomsWrapper').style.display = 'none';
				roomsMarkup = '';
			} else {
				// TODO: Display document list
				roomsWrapper.style.display = 'block';
				let roomData = clientRooms;
				if (roomData.length) {
					roomsMarkup += `
						<div class="border-grey rounded px-4">
						${roomData
							.map(
								(room) =>
									`
							<div class="row border-b-1 mb-0">
								<div class="px-4 overflow-hidden">
									<div class="flex justify-between items-center mt-4">
										<p class="text-black font-bold mb-0 mt-0 room-link"><a href="/rooms/?id=${room.roomid}">${room.name}</a></h4>
										<p class="text-xs mb-0 mt-0 text-secondary">${room.participants.length} participants</p>
									</div>
									<p class="text-sm text-secondary mt-1">${room.notes ? room.notes.substring(0, 250) : ''}${room.notes ? (room.notes.length > 250 ? '...' : '') : ''}</p>
								</div>
								
							</div>
							`
							)
							.join('')}
					`;
				} else {
					roomsMarkup = roomEmptyState;
				}
			}
			roomsList.innerHTML = roomsMarkup;

			if (isMH) {
				roomsWrapper.classList.remove('mb-12');
				roomsWrapper.classList.add('mb-8', 'mt-16');
			}
		}
	}

	// SHOW UPDATES SECTION
	let updates;
	if (true) {
		//itsaFund) {
		// use allKeys here instead of shareKeys to handle HIDE_ZEROS flag
		let allKeys = Object.keys(userData.clients[client].shares || {});
		if (isAdmin) allKeys = classes; // global access for admin
		updates = clientData.company_updates.filter(function (item) {
			if (!item.tag || item.tag.toUpperCase() == 'ALL') return true;
			let tags = item.tag.split(',').map((tag) => tag.trim());
			return allKeys.some((tag) => tags.includes(tag));
		});
	}

	updates = sortByDate(updates, 'date');
	let hideUpdates = clientData.tabs?.hide_updates;
	let updatesMarkup = '';
	if (!hideUpdates && (updates || updates.length)) {
		// NOT ENABLED.  NO CAP TABLE DATA ON USER SIDE.
		if (false && isAdmin) {
			updates.forEach(function (i, index) {
				if (i.views) {
					let views = i.views.split(',');
					let tooltip = views.length + ' Recent View' + (views.length > 1 ? 's' : '') + ':\r\n';
					views.forEach(function (v) {
						let viewer = v.split('|')[0];
						let viewerName = capTableData ? capTableData.find((x) => x.userid == viewer).user_name : 'Unknown';
						tooltip += viewerName + ' (' + viewer + ') - ' + moment(Number(v.split('|')[1]) * 1000).fromNow() + '\r\n';
					});
					updates[index].tooltip = tooltip;
				}
			});
		}

		let CONF_DOCS = [];
		if (clientData.confidential_modal) {
			document.getElementById('confidentialityModal').innerHTML = DOMPurify.sanitize(clientData.confidential_modal);
			CONF_DOCS = ['CONFIDENTIAL']; // <-- This word in all caps triggers the modal
		}

		updatesMarkup = `
			<div class="row mt-0">
				<div class="col s12 m8">
					<h3 class="text-xl font-bold mb-0 mt-4">Recent ${labelUpdatesTab}</h3>
				</div>
			</div>

			${updates
				.slice(0, 3)
				.map(
					(u, index) =>
						`
				<!-- <div class="row border-t-1 pt-4" ${isAdmin ? 'title="' + (u.tooltip ? u.tooltip : 'No views yet') + '"' : ''}> -->
				<div class="row border-t-1 pt-4 update-link" ${isAdmin && u.tooltip ? 'title="' + u.tooltip + '"' : ''}>
					<a class="text-black ${(!u.link && !u.document) || CONF_DOCS.some((c) => u.title.includes(c)) ? `modal-trigger` : ''}"
						onClick="handleView(${index})" href="${
							CONF_DOCS.some((c) => u.title.includes(c))
								? `#confidentialityModal`
								: !u.link && !u.document
									? '#' + u.id
									: u.link
										? u.link
										: `/updates/view/?document=${u.id}`
						}" ${u.link ? 'target="_blank" rel="noopener noreferrer"' : ''}>
						<div class="col s12 hide-on-med-and-up">
							<img class="responsive-img mb-2" style="width: 100%; height: 8rem; object-fit: cover;" src="${u.image ? formatImg(u.image, '/w_470,q_auto,f_auto/') : ''}" />
						</div>
						<div class="col s12 m8">
							<h6 class="font-bold mt-0 mb-0 text-base">${u.title}</h6>
							<div class="flex items-center mt-1">
								<div class="text-sm mb-2 text-secondary mr-1">${u.date}</div>
								<!-- <div class="update-date mb-4">${u.date}</div> -->
								${
									u.tag && u.tag != 'ALL'
										? `
								${shareKeys
									.filter((scTag) =>
										u.tag
											.split(',')
											.map((tag) => tag.trim())
											.includes(scTag)
									)
									.map(
										(jointTag) =>
											`
								<div class="pill pill--gray mt-0 mb-1 ml-1 mr-1">${jointTag}</div>
								`
									)
									.join('')}
								<!-- <div class="pill pill--gray mt-0 mb-1 ml-8">${u.tag}</div> -->
								`
										: ''
								}
							</div>
							<!--<div class="text-sm mb-2 text-secondary">${moment(new Date(u.date)).format('MMMM Do, YYYY')}</div>-->
							<div class="mt-0 mb-2 text-sm text-secondary">${u.body
								.replace(/^\[\d{13}_post_content\.html\] /i, '')
								.replace(/<img src=.*?>/g, '[IMAGE]')
								.substring(0, 250)}${u.body.length > 250 ? '...' : ''}</div>
						</div>
						<div class="col m4 hide-on-small-only">
							<img class="responsive-img" style="width: 100%; height: 8rem; object-fit: cover;" src="${u.image ? formatImg(u.image, '/w_470,q_auto,f_auto/') : ''}" />
						</div>
					</a>
				</div>

				${
					!u.link && !u.document
						? `
					<div id="${u.id}" class="modal modal-md">
						<div class="modal-content">
							<h5 class="text-2xl font-bold mt-0 mb-0">${u.title}</h5>
							<p class="text-secondary text-sm mt-0">${moment(new Date(u.date)).format('MMMM Do, YYYY')}</p>
							<span id="postModalBody_${u.id}">${u.body.replace(/^\[\d{13}_post_content\.html\] /, '')}</span>
						</div>
					</div>
					`
						: ''
				}
				
				`
				)
				.join('')}
			${updates.length > 3 ? '<p><a href="/updates/" class="text-sm text-bold">View All Updates</a></p>' : ''}
		`;
	} else {
		console.log('No updates to show');
		// TODO: Design a nice empty state in case there are no updates
		updatesMarkup = ``; //`No updates to display`;
	}
	document.getElementById('recentUpdates').innerHTML = clientData.flags?.enable_dashboard ? '' : updatesMarkup;

	// Handle Post-flavor Update (fetch long content) and record Views
	let clickedDocId;
	window.handleView = async function (index) {
		// Add null check for updates array and index bounds
		if (!updates || !Array.isArray(updates) || index < 0 || index >= updates.length) {
			console.error('Invalid update index or updates array:', index, updates);
			return;
		}

		if (!updates[index]) {
			console.error('Update at index does not exist:', index);
			return;
		}

		clickedDocId = updates[index].id;
		if (!updates[index].link && !updates[index].document) {
			let match = updates[index].body.match(/\d{13}_post_content\.html/);
			if (match) {
				let postModalBody = document.getElementById('postModalBody_' + updates[index].id);
				let postContent = await getContentUrl(match[0]);
				fetch(postContent)
					.then(function (response) {
						console.log(response);
						return response.text();
					})
					.then(function (html) {
						postModalBody.innerHTML = html;
					})
					.catch(function (err) {
						console.warn('Something went wrong.', err);
					});
			}
		}
		if (true) {
			//!editPage) {
			let timeStamp = Math.floor(Date.now() / 1000).toString();
			AppState.clientData = JSON.parse(sessionStorage.getItem(client + '.Data'));
			updates[index].views = updates[index].views
				? (authedUser + '|' + timeStamp + ',' + updates[index].views).substring(0, isPHJ || isAlera ? 10000 : 4000)
				: authedUser + '|' + timeStamp;
			clientData.company_updates = updates;

			sessionStorage.setItem(client + '.Data', JSON.stringify(clientData));

			axios
				.post(
					'https://api.nthround.io/portal/clientConfig/company-updates',
					JSON.stringify({
						userid: authedUser,
						client: client,
						cookie: cookie,
						company_update: {
							id: updates[index].id,
							views: updates[index].views,
						},
					})
				)
				.then(function (response) {
					// console.log(response);
				})
				.catch(function (error) {
					console.error(error);
				});
		}
	};

	let confidentialityAccept = document.getElementById('confidentialityAccept');
	if (confidentialityAccept)
		confidentialityAccept.addEventListener('click', () => {
			let confidentialityModal = document.getElementById('confidentialityModal');
			M.Modal.getInstance(confidentialityModal).close();
			window.location.replace('/updates/view/?document=' + clickedDocId); // CONFIDENTIAL AMP CONFIDENTIAL_DOC - The Alera Market Place
		});

	if (nthAdmin) renderUserSettings();
}

async function addStockCertificates(docs) {
	let cert = {};
	let certs = [];
	if (isASP && userData && !userData.userid.includes('nthround.com')) {
		let userTags = userData.clients?.[client]?.tags;
		if (!userTags || userTags.length < 5) return docs;

		let founder = userTags[0].replace('F', '');
		let family = userTags[1] + userTags[2];
		let member = userTags[3];

		let stockRecordFile = await getContentUrl(clientData.transactions_data);
		await fetch(stockRecordFile)
			.then(function (response) {
				// console.log(response);
				return response.text();
			})
			.then(function (csv) {
				let array = cleanCSV(csv);

				let orderedFields = [
					'Founder',
					'Family',
					'Member',
					'Sequence',
					'Type',
					'Certificate',
					'Shares',
					'Issued Date',
					'Division',
					'Rate',
					'Amount',
					'Delete Tag',
					'Soc Sec',
					'UserName',
					'ProcessDate',
					'TransactionBasis',
				];
				let f0 = array[0].findIndex((f) => f == orderedFields[0]);
				let f1 = array[0].findIndex((f) => f == orderedFields[1]);
				let f2 = array[0].findIndex((f) => f == orderedFields[2]);
				let f5 = array[0].findIndex((f) => f == orderedFields[5]);
				let f6 = array[0].findIndex((f) => f == orderedFields[6]);
				let f7 = array[0].findIndex((f) => f == orderedFields[7]);
				let f15 = array[0].findIndex((f) => f == orderedFields[15]);

				array = array.filter((row) => row[f0]?.includes(founder));
				array = array.filter((row) => row[f1]?.includes(family));
				array = array.filter((row) => row[f2]?.includes(member));
				array.forEach((r) => {
					let certNumber = Number(r[f5]).toString(); //.toLocaleString();
					let shares = Number(r[f6]).toLocaleString();
					if (shares == 0) return;
					let description = '#' + certNumber + ' (' + shares + ' shares)';
					let id = 'f' + founder + '-' + family + '-' + member + '-' + r[f5];
					id = id + '-' + '0'.repeat(36 - id.length - 1);
					cert = {
						date: moment(Date.parse(r[f7] == '' ? '1/1/1970' : r[f7])).format('MMM D, YYYY'), // <-- there may be a few invalid/blank dates
						// description: t.notes + " for Class " + t.shareClass,
						description: description,
						id: id,
						name: 'Stock Certificate',
						require2Factor: false,
						type: 'Certificate',
						tag: 'C',
						url: 'TBD',
						userid: userData.userid,
						userName: userData.clients[client].notes ? userData.clients[client].notes : userData.user_name,
					};
					addStockRecord();
				});
			})
			.catch(function (err) {
				// There was an error
				console.warn('Something went wrong.', err);
			});

		saveStockRecords();
		return docs;
	} else {
		// Build a new transactions list
		if (!clientData) clientData = JSON.parse(sessionStorage.getItem(client + '.Data'));
		let transactions = [];
		const certUserIds = getActiveUserIds(userData);
		classes.forEach(function (c, index) {
			let hasThisCertificate = clientData.templates.some((t) => t.includes(c + '_Stock_Certificate'));
			if (clientData.equity[c].transactions && hasThisCertificate) {
				clientData.equity[c].transactions.forEach(function (i) {
					// Support consolidated view - check if receiver is any of the consolidated userids
					const receiverLower = i.receiver?.toLowerCase();
					const senderLower = i.sender?.toLowerCase();
					const matchesReceiver = receiverLower && certUserIds.some((uid) => uid?.toLowerCase() === receiverLower);
					const matchesSender = senderLower && certUserIds.some((uid) => uid?.toLowerCase() === senderLower);

					// Sheetz unified cert logic needs both receiver-side and sender-side cert events.
					// Other clients keep the old receiver-only behavior.
					if ((isSheetz && (matchesReceiver || matchesSender)) || (!isSheetz && matchesReceiver)) {
						i.shareClass = classes[index];
						transactions.push(i);
					}
				});
			}
			// Dont't display future (beyond this year) Events or Transactions
			let nextYear = new Date(new Date().getFullYear() + 1, 1, 1) / 1000;
			transactions = transactions.filter((t) => Number(t.date) < nextYear);

			// Only do certificates with # (except Sheetz)
			if (!isSheetz && transactions.some((t) => t.notes?.includes('#'))) transactions = transactions.filter((t) => t.notes?.includes('#'));
		});
		transactions.forEach((t) => {
			let itsDebt = clientData.equity[t.shareClass].hasOwnProperty('interestRate');
			let certNumber = getCertNumber(t.notes);
			if (!certNumber && !itsDebt && !isROK && !isBB && !isMAR) return; // && !NO_CERT_NUMS && !itsDebt) return;
			className = clientData.equity[t.shareClass].name || thisClass;
			let description = className + (t.notes ? ' - ' + t.notes : '');
			if (t.notes.includes(className)) description = t.notes;
			if (!description.includes(' ' + t.shareClass) && !description.includes(t.shareClass + ' ')) description = t.shareClass + ' ' + description;
			if (isASP || isROK) description = t.notes.replace('Certificate', '').trim() + ' (' + Number(t.quantity).toLocaleString() + ' shares)';
			if (itsDebt) description = className;
			cert = {
				date: moment.utc(Number(t.date) * 1000).format('MMM D, YYYY'),
				description: description,
				id: t.nrid,
				name: itsDebt ? 'Promissory Note' : 'Stock Certificate',
				require2Factor: false,
				type: 'Certificate',
				tag: t.shareClass,
				url: 'TBD',
				userid: userData.userid,
				userName: userData.user_name,
				sender: t.sender || '',
				senderName: t.senderName || '',
				receiver: t.receiver || '',
				receiverName: t.receiverName || '',
			};
			addStockRecord();
		});

		saveStockRecords();
		return docs;
	}

	function addStockRecord() {
		const itsDebt = clientData.equity[cert.tag]?.hasOwnProperty('interestRate');
		const isDuplicate = docs.some(
			(d) =>
				(d.description === cert.description || d.description?.includes(cert.description.replace(d.tag, '').trim()) || isROK || itsDebt) &&
				d.date === cert.date
		);

		if (!isDuplicate) {
			certs.push(cert);
		}
	}

	function saveStockRecords() {
		if (docs) docs = docs.concat(certs);
		else docs = certs;
		sessionStorage.setItem('stockCerts', JSON.stringify(certs));
	}
}

function isCertificateDoc(doc) {
	// return (doc?.type === "Certificate") || /stock certificate/i.test(doc?.name || "");
	return /stock certificate/i.test(doc?.name || '');
}

function stripObsoleteCertificates(docs) {
	if (!Array.isArray(docs)) return [];
	const hasHashString = (s) => typeof s === 'string' && s.includes('#');

	return docs.filter((d) => {
		const isCert = isCertificateDoc(d);
		const isDebt = /promissory note/i.test(d?.name || '');
		// Keep all non-certs and debt docs
		if (!isCert || isDebt) return true;
		// Sheetz: do NOT require "#" for certificates
		if (isSheetz) return !!d?.isUnified;
		// Legacy unified certs
		if (d?.isUnified || /unified stock certificate/i.test(d?.name || '')) return true;
		// Legacy stock certs: keep only if name or description contains "#"
		return hasHashString(d?.description) || hasHashString(d?.name);
	});
}

function getShareholderDocName(doc) {
	// Default: fall back to stored name
	if (!doc) return '';
	// Only remap unified certs, and only when unified mode is actually on
	const unifiedOn = isSheetz;
	if (!unifiedOn || !doc.isUnified) return doc.name || '';
	// For unified certs, build a friendlier label based on class/tag
	const cls = doc.tag;
	if (!cls) return doc.name || '';
	// What the shareholder should see:e.g. "Class A Stock Certificate"
	return `Class ${cls} Stock Certificate`;
}

// Latest/canonical unified cert state for this holder + class.
// For this client, transfers can issue a new USCN to both sender and receiver.
// Fallback unified cert state from stockCerts only.
// For Sheetz-style descriptions with multiple certs, avoid the retired cert and
// prefer the newest surviving cert when transaction data is unavailable.
function latestUnifiedCertForClass(cls) {
	function parseUnifiedCertDescriptionForHolder(description, opts = {}) {
		function uscnNumber(cert) {
			const clean = normalizeUSCN(cert);
			const m = clean.match(/-(\d+)$/);
			return m ? Number(m[1]) : 0;
		}

		function normalizeUSCN(cert) {
			return String(cert || '')
				.replace(/#/g, '')
				.trim()
				.toUpperCase();
		}

		const desc = String(description || '').trim();
		const uid = String(opts.uid || '')
			.trim()
			.toLowerCase();
		const sender = String(opts.sender || '')
			.trim()
			.toLowerCase();
		const receiver = String(opts.receiver || '')
			.trim()
			.toLowerCase();

		let m;

		// Gift Transfer from CB-1902 (now CB-1994) to CB-1995
		// Gen Z, LP Distribution from CB-1877 (now CB-1900) to CB-1902
		m = desc.match(/from\s+#?([A-Z]+-\d+)\s+\(now\s+#?([A-Z]+-\d+)\)\s+to\s+#?([A-Z]+-\d+)/i);
		if (m) {
			const oldCert = normalizeUSCN(m[1]);
			const senderNew = normalizeUSCN(m[2]);
			const receiverNew = normalizeUSCN(m[3]);

			if (sender && sender === uid) {
				return {
					certStr: senderNew,
					certNum: uscnNumber(senderNew),
					role: 'senderNew',
					priorCert: oldCert,
				};
			}

			if (receiver && receiver === uid) {
				return {
					certStr: receiverNew,
					certNum: uscnNumber(receiverNew),
					role: 'receiverNew',
					priorCert: oldCert,
				};
			}

			// Last-resort fallback if role data is missing
			const senderNum = uscnNumber(senderNew);
			const receiverNum = uscnNumber(receiverNew);
			return receiverNum >= senderNum
				? { certStr: receiverNew, certNum: receiverNum, role: 'receiverNew', priorCert: oldCert }
				: { certStr: senderNew, certNum: senderNum, role: 'senderNew', priorCert: oldCert };
		}

		// Sale Transfer CB-1909 (now CB-1935)
		m = desc.match(/Sale Transfer\s+#?([A-Z]+-\d+)\s+\(now\s+#?([A-Z]+-\d+)\)/i);
		if (m) {
			const oldCert = normalizeUSCN(m[1]);
			const senderNew = normalizeUSCN(m[2]);

			if (!sender || sender === uid) {
				return {
					certStr: senderNew,
					certNum: uscnNumber(senderNew),
					role: 'senderNew',
					priorCert: oldCert,
				};
			}

			return {};
		}

		// Transfer Received (#CA-280 from CA-162)
		// Transfer Received (#CB-2001)
		m = desc.match(/Transfer Received\s+\(#?([A-Z]+-\d+)(?:\s+from\s+([A-Z]+-\d+))?\)/i);
		if (m && (!receiver || receiver === uid)) {
			const cert = normalizeUSCN(m[1]);
			return {
				certStr: cert,
				certNum: uscnNumber(cert),
				role: 'receiverNew',
				priorCert: normalizeUSCN(m[2] || ''),
			};
		}

		// Issuance to CB-1437
		m = desc.match(/Issuance to\s+#?([A-Z]+-\d+)/i);
		if (m && (!receiver || receiver === uid)) {
			const cert = normalizeUSCN(m[1]);
			return {
				certStr: cert,
				certNum: uscnNumber(cert),
				role: 'issuance',
			};
		}

		// Stock Certificate Issuance (#CA-276)
		m = desc.match(/Stock Certificate Issuance\s+\(#?([A-Z]+-\d+)\)/i);
		if (m && (!receiver || receiver === uid)) {
			const cert = normalizeUSCN(m[1]);
			return {
				certStr: cert,
				certNum: uscnNumber(cert),
				role: 'issuance',
			};
		}

		// Sale Transfer CB-1935
		m = desc.match(/Sale Transfer\s+#?([A-Z]+-\d+)/i);
		if (m && (!sender || sender === uid)) {
			const cert = normalizeUSCN(m[1]);
			return {
				certStr: cert,
				certNum: uscnNumber(cert),
				role: 'senderNew',
			};
		}

		const fallback = getCertNumber(desc);
		if (fallback) {
			const cert = normalizeUSCN(fallback[2] || fallback[1] || '');
			return {
				certStr: cert,
				certNum: uscnNumber(cert),
				role: 'fallback',
			};
		}

		return {};
	}

	function parseUnifiedCertEvent(tx) {
		try {
			const desc = String(tx?.description || tx?.Description || '').trim();
			if (!desc) return null;

			const sender = String(tx?.sender || tx?.Sender || tx?.senderEmail || tx?.['Sender Email'] || '')
				.trim()
				.toLowerCase();
			const receiver = String(tx?.receiver || tx?.Receiver || tx?.receiverEmail || tx?.['Receiver Email'] || '')
				.trim()
				.toLowerCase();
			const uid = String(userData?.userid || '')
				.trim()
				.toLowerCase();
			if (!uid) return null;

			const shareClass = tx?.shareClass || tx?.Class;
			const epoch = normalizeDateToEpoch(tx?.date || tx?.Date);
			if (!epoch) return null;

			const parsed = parseUnifiedCertDescriptionForHolder(desc, { sender, receiver, uid });
			if (!parsed.certNum) return null;

			return {
				class: shareClass,
				epoch,
				certStr: parsed.certStr,
				certNum: parsed.certNum,
				role: parsed.role,
				source: tx,
				priorCert: parsed.priorCert,
			};
		} catch (e) {
			console.warn('parseUnifiedCertEvent failed:', e, tx);
			return null;
		}
	}

	try {
		const uid = (userData.userid || '').toLowerCase();

		// 1) Prefer transaction/event truth
		if (typeof transactionsData !== 'undefined' && Array.isArray(transactionsData)) {
			const matches = transactionsData.map(parseUnifiedCertEvent).filter((evt) => evt && evt.class === cls);

			if (matches.length) {
				matches.sort((a, b) => {
					if (Number(b.epoch || 0) !== Number(a.epoch || 0)) return Number(b.epoch || 0) - Number(a.epoch || 0);
					return Number(b.certNum || 0) - Number(a.certNum || 0);
				});

				return matches[0];
			}
		}

		// 2) Fallback to session stockCerts because Documents can render before transactionsData is ready
		let stockCerts = [];
		try {
			stockCerts = JSON.parse(sessionStorage.getItem('stockCerts')) || [];
		} catch (e) {
			console.warn('latestUnifiedCertForClass: bad stockCerts JSON', e);
			return null;
		}

		const candidates = stockCerts
			.filter((s) => {
				const userField = (s.userid || s.userId || '').toLowerCase();
				return s.tag === cls && userField === uid && !s.isUnified;
			})
			.map((s) => {
				const parsed = parseUnifiedCertDescriptionForHolder(s.description, {
					sender: s.sender,
					receiver: s.receiver,
					uid,
				});

				const epoch = moment(s.date, 'MMM D, YYYY').unix();

				return {
					source: s,
					epoch,
					certStr: parsed.certStr,
					certNum: parsed.certNum,
					role: parsed.role,
					priorCert: parsed.priorCert,
					class: cls,
				};
			})
			.filter((x) => x.certNum > 0);

		if (!candidates.length) return null;

		candidates.sort((a, b) => {
			if (b.epoch !== a.epoch) return b.epoch - a.epoch;
			return b.certNum - a.certNum;
		});

		return candidates[0];
	} catch (e) {
		console.warn('latestUnifiedCertForClass failed:', e);
		return null;
	}
}

function addUnifiedCertificates(docs) {
	try {
		if (!Array.isArray(docs)) docs = [];

		// Unified certs are synthetic/current-state docs.
		// Do not show saved certificate docs here; rebuild one card per owned class.
		const out = docs.filter((d) => !isCertificateDoc(d));
		const stockCerts = JSON.parse(sessionStorage.getItem('stockCerts')) || [];

		const makeUnifiedId = (cls) => {
			const base = `unified-${client}-${cls}-${userData.userid}`;
			return base
				.toLowerCase()
				.replace(/[^a-z0-9]/g, '')
				.slice(0, 36)
				.padEnd(36, '0');
		};

		if (!classes?.length) classes = Object.keys(clientData.equity).sort();

		for (const c of classes || []) {
			const sharesNode = userData?.clients?.[client]?.shares?.[c];
			if (!sharesNode) continue;

			const owned = Number(sharesNode.owned || 0);
			if (!owned || Math.abs(owned) < 1e-9) continue;

			const info = latestUnifiedCertForClass(c);
			if (!info) continue;

			const { epoch } = info;
			const className = clientData?.equity?.[c]?.name || c;
			const listDate = moment.unix(epoch).format('MMM D, YYYY');
			const id = makeUnifiedId(c);

			const unifiedDoc = {
				date: listDate,
				description: `${className} (${owned.toLocaleString()} shares)`,
				id,
				name: 'Stock Certificate',
				require2Factor: false,
				type: 'Certificate',
				tag: c,
				url: 'TBD',
				userid: userData.userid,
				userName: userData.user_name,
				isUnified: true,
			};

			out.push(unifiedDoc);

			// Session-only launch record for /documents/view.
			const idx = stockCerts.findIndex((s) => s.id === id);
			if (idx >= 0) stockCerts[idx] = { ...unifiedDoc };
			else stockCerts.push({ ...unifiedDoc });
		}

		sessionStorage.setItem('stockCerts', JSON.stringify(stockCerts));
		return out;
	} catch (e) {
		console.warn('addUnifiedCertificates failed:', e);
		return docs || [];
	}
}

function getEquityData() {
	/* NOTES
	This method gathers basic equity data, including cost bases
	It sorts classes a few times: alphabetically, then by amount owned, and finally by latest value
	Latest (current) value can be calculated different ways
	  1) From Cap Table: amount owned x price or TVPI
	  2) From Transactions: amount x (price - strike price)
	  3) For a sponsor scenario (isTSG), add basis to latest value
	  4) From Events (JMH): share of rolled-up contributions + all gain/loss accruals [but not cash! to avoid double-counting]
	The cost basis for each holding comes from transactions, certain events, or both.
	*/

	// Calculate the number of share classes this user owns, and sort various ways
	let localUserData = pn.includes('/users/edit') ? shareholderData : userData;
	// else if (hasEquityData) return;  <-- DON"T DO THIS (can render incorrect data with aliases)

	if (!localUserData || !localUserData.clients || !localUserData.clients[client]) {
		console.warn('getEquityData: userData not available yet');
		return;
	}

	let userDataShares = localUserData.clients[client].shares;
	if (userDataShares) {
		// filter out classes that have no "owned" property (just an image preference)
		userDataShares = Object.fromEntries(Object.entries(userDataShares).filter(([key, value]) => value.hasOwnProperty('owned')));
		shareKeys = Object.keys(userDataShares).sort();
		AppState.SHOW_SMALL = clientData.flags && clientData.flags.show_small_values;
		AppState.HIDE_ZEROS = clientData.flags && clientData.flags.hide_zero_balances;
		if (HIDE_ZEROS) shareKeys = shareKeys.filter((k) => Number(userDataShares[k].owned) > 0);

		shareKeys.sort(function (a, b) {
			if (userDataShares[b].owned == userDataShares[a].owned) return 1;
			return userDataShares[b].owned - userDataShares[a].owned;
		});

		// store latestValues
		let mostRecentValuationTimestamp = 0; // Track the most recent valuation date across all classes
		// Use weighted average strike price (netting contra / forfeitures)
		shareKeys.forEach(function (key) {
			let keyEquity = clientData.equity[key];
			if (keyEquity) {
				const strikePrice = getEffectiveStrikePriceForUser(clientData.equity[key], localUserData.userid, {
					useWeighted: true,
					netContra: true,
					outstandingOnly: true,
				});

				if (strikePrice > 0) {
					clientData.equity[key].overrideCbPrice = strikePrice;
					clientData.equity[key].overrideCbLabel = 'Weighted Avg ' + clientData.equity[key].costbasisLabel;
				} else {
					delete clientData.equity[key].overrideCbPrice;
					delete clientData.equity[key].overrideCbLabel;
				}

				if (keyEquity.valuations) {
					let latest = keyEquity.valuations.sort((u, v) => (Number(u.date) > Number(v.date) ? -1 : 1));
					// Track the most recent valuation date across all classes
					if (latest && latest[0] && Number(latest[0].date) > mostRecentValuationTimestamp) {
						mostRecentValuationTimestamp = Number(latest[0].date);
					}
					// Skip legacy latestValue calculation for clients using tvpi_fund calculator
					// The calculator will set latestValue with the correct Fund NAV × Percentage formula
					const usesFundNavCalculator = clientData.flags?.default_value_method === 'tvpi_fund';
					if (latest && latest[0] && !labelValuation.startsWith('%') && !usesFundNavCalculator) {
						const owned = Number(userDataShares[key].owned);
						const sharePrice = Number(latest[0].sharePrice);
						const effectiveStrike = isBW ? 0 : strikePrice;
						latestValue[key] = owned * (sharePrice - effectiveStrike);
					}
				}
			}
		});

		if (isJMH) {
			shareKeys.forEach(function (key) {
				let sumAccruals = 0;
				let sumContributions = 0;
				let clientEvents = clientData.equity[key].events;
				let userShares = userDataShares[key];
				if (clientEvents && userShares.events) {
					let userEvents = userShares.events;
					userEvents.forEach(function (e, index) {
						let clientEvent = clientEvents.find((obj) => obj.nrid == e.nrid);
						if (clientEvent) {
							if (clientEvent.eventType == 'Contribution') sumContributions += Number(e.grossDollars);
							else if (clientEvent.eventType == 'Gain (Loss) Accrual') sumAccruals += Number(e.grossDollars);
						}
					});
					latestValue[key] = sumContributions + sumAccruals;
				}
			});
		}

		// Format the most recent valuation date for display (used in headline templates)
		if (mostRecentValuationTimestamp > 0) {
			latestValuationDate = moment.unix(mostRecentValuationTimestamp).format('MMM D, YYYY');
		} else {
			latestValuationDate = '';
		}

		// !!! BASIS ISSUES !!!
		// Fundementally, we have a mess here with basis, especially with strike prices.
		// For most clients, basis is the gross dollars invested.  All good.
		// For others, we suddenly switch to basis as the purchase price pre share.
		// We get away with this, until we examine details, like Events & Transactions.
		// I think the best solution is to use gross dollars ALWAYS.
		// Then, when appropriate, we can divide by shares owned to get back to purchase price per share.
		// Do a global search on bases, and see when it needs to be adjusted back to price per share.

		let events = [];
		let clientEvents = [];
		let transactions = [];

		// For E&T from admin side
		if (!classes?.length) classes = Object.keys(clientData.equity).sort();

		// Support consolidated view - get array of userids to filter by
		const equityUserIds = getActiveUserIds(localUserData);
		const nowTs = Math.floor(Date.now() / 1000);

		classes.forEach(function (c, index) {
			bases[c] = -1;
			distribs[c] = 0;
			contributions[c] = 0; // Track gross contributions separately
			returnsOfCapital[c] = 0; // Track returns of capital separately
			incomeAllocations[c] = 0; // Track income allocations (K-1) separately
			accruals[c] = 0; // Track gain/loss accruals separately
			if (userDataShares[c] && userDataShares[c].events) userDataShares[c].events.forEach((e) => events.push(e));
			if (clientData.equity[c].events) {
				clientData.equity[c].events.forEach(function (e) {
					const ts = Number(e.record_date || e.date);
					// Falsy/NaN dates treated as past (legacy data); typo defense
					// lives in the ledger UI which caps record_date to +5y.
					if (!ts || ts <= nowTs) {
						e.shareClass = c;
						clientEvents.push(e);
					}
				});
			}
			if (clientData.equity[c].transactions) {
				clientData.equity[c].transactions.forEach(function (t) {
					if (t.notes?.toLowerCase().includes('gift')) return;
					if (isUserTransaction(t, equityUserIds)) {
						t.shareClass = c;
						transactions.push(t);
					}
				});
			}
		});

		// 6-15-2023 Alera unitholders now have multiple P tranches with different strike prices, schedules, etc.  BLEH...
		transactions.forEach(function (t) {
			if (t.notes?.toLowerCase().includes('gift')) return;
			if (bases[t.shareClass] == -1) bases[t.shareClass] = 0;
			let dollarAmount = (Number(t.quantity) || 0) * (Number(t.sharePrice) || 0);
			let use_strike = clientData.equity[t.shareClass].useStrike;
			const senderLower = t.sender?.toLowerCase();
			const isSender = senderLower && equityUserIds.some((uid) => uid?.toLowerCase() === senderLower);
			if (isSender) {
				// Don't lower basis if it's an option (i.e., has a strike price).  Also, don't change basis on JMS sales.
				if (!t.strikePrice || t.strikePrice <= 0) bases[t.shareClass] -= isJMS ? 0 : dollarAmount;
				// if (bases[t.shareClass] < 0 ) bases[t.shareClass] = 0;
				if (use_strike) {
					// Assume lowest cost basis for sold shares (big assumption!)
					// To be precise, we need to track "consumed" quantity of each tranche
					let highestCostBasis = 0.0001;
					transactions.forEach((s) => {
						const sReceiverLower = s.receiver?.toLowerCase();
						const sIsReceiver = sReceiverLower && equityUserIds.some((uid) => uid?.toLowerCase() === sReceiverLower);
						highestCostBasis =
							t.shareClass == s.shareClass && sIsReceiver && Number(s.sharePrice) > highestCostBasis ? Number(s.sharePrice) : highestCostBasis;
					});
					let totalBasisStepDown = (Number(t.quantity) || 0) * ((Number(t.sharePrice) || 0) - highestCostBasis);
					bases[t.shareClass] += totalBasisStepDown;
					console.log('totalBasisStepDown = ' + totalBasisStepDown);
				}
			} else {
				// bases[t.shareClass] = bases[t.shareClass] + dollarAmount + (t.strikePrice ? Number(t.sharePrice) + Number(t.strikePrice) : 0);
				bases[t.shareClass] += dollarAmount;
				// if (t.strikePrice > 0) bases[t.shareClass] += Number(t.quantity) * Number(t.strikePrice);
				if (use_strike && bases[t.shareClass] > 0) bases[t.shareClass] += 0;
				else if (t.strikePrice > 0 && !NO_BASIS) bases[t.shareClass] += (Number(t.sharePrice) || 0) + (Number(t.strikePrice) || 0);
			}

			// Debug logging for Sequoia C-1 basis calculation - after processing
			if (isSEQ && t.shareClass === 'C-1') {
				console.log('[Sequoia C-1 Debug] After processing transaction:', {
					shareClass: t.shareClass,
					newBasis: bases[t.shareClass],
					basisChange: bases[t.shareClass] - (Number(t.currentBasis) || 0),
				});
			}
		});
		// console.log("bases:", bases);

		// Debug logging for final Sequoia C-1 basis
		if (isSEQ && bases['C-1'] !== undefined) {
			console.log('[Sequoia C-1 Debug] Final basis for C-1:', {
				basis: bases['C-1'],
				transactions: transactions.filter((t) => t.shareClass === 'C-1').length,
			});
		}

		events.forEach(function (e) {
			let clientEvent = clientEvents.find((obj) => obj.nrid == e.nrid);
			if (clientEvent) {
				e.eventType = clientEvent.eventType;
				e.shareClass = clientEvent.shareClass;
				let grossDollars = Number(e.grossDollars) || 0;
				if (bases[e.shareClass] == -1 && ['Contribution', 'Redemption', 'Return of Capital'].includes(e.eventType)) bases[e.shareClass] = 0;
				// Check if this class includes RoC in capital account calculation (per cross-check.js logic)
				let includeClassROC = clientData.equity[e.shareClass]?.includeROC;
				switch (e.eventType) {
					case 'Distribution':
						distribs[e.shareClass] += grossDollars;
						break;
					case 'Cash Dividend':
						distribs[e.shareClass] += grossDollars;
						break;
					case 'Contribution':
						bases[e.shareClass] += grossDollars;
						contributions[e.shareClass] += grossDollars; // Track gross contributions
						break;
					case 'Redemption':
						bases[e.shareClass] -= grossDollars;
						break;
					case 'Return of Capital':
						// Subtract RoC from bases by default (backward compatible behavior)
						// Only skip subtracting if includeROC is explicitly set to false
						if (includeClassROC !== false) {
							bases[e.shareClass] -= grossDollars;
						}
						returnsOfCapital[e.shareClass] += grossDollars; // Always track returns of capital
						break;
					case 'Gain (Loss) Accrual':
						// K-1 income allocations that increase tax basis
						incomeAllocations[e.shareClass] += grossDollars;
						accruals[e.shareClass] += grossDollars;
						break;
				}
			}
		});

		if (isTSG) shareKeys.forEach((k) => (latestValue[k] += bases[k] > 0 ? bases[k] : 0));

		// For Maguire Hayden, Capital Account = Contributions - Redemptions - RoC (which is bases)
		if (isMH) shareKeys.forEach((k) => (latestValue[k] = bases[k] || 0));

		// ============================================================
		// CALCULATION ENGINE: Build input and calculate portfolio values
		// ============================================================
		if (clientData.flags?.prefer_portfolio_graph) {
			const eventData = { bases, distributions: distribs, contributions, returnsOfCapital, incomeAllocations, accruals };
			const calculatorInput = buildCalculatorInput({
				userShares: userDataShares,
				clientData,
				shareClasses: shareKeys,
				userIds: equityUserIds,
				eventData,
				options: { floorAtZero: isSEQ, zeroStrikePrice: isBW, clientFlags: { isUA } },
			});

			// Get the appropriate calculator for this client and calculate
			const calculator = calculationService.getCalculatorForClient(clientData);
			if (calculatorInput) {
				window.lastCalculationResult = calculator.calculate(calculatorInput);

				// CAGR is now computed inside calculator.calculate() via attachCAGR.
				// See #5198 Phase 4 — each calculator owns its CAGR semantic (e.g.
				// ContributionsAccruals / IncomeAllocations exclude transfers so
				// received gifts don't inflate the growth rate).

				// Update latestValue from calculator result to ensure consistency
				if (window.lastCalculationResult?.breakdown) {
					window.lastCalculationResult.breakdown.forEach(function (classData) {
						if (classData.classKey && classData.value !== undefined) {
							latestValue[classData.classKey] = classData.value;
						}
					});
				}
			}

			// Determine graph type based on calculator recommendation (or client override)
			const graphConfig = calculator.graphConfig || { recommendedType: GraphTypes.COMPARISON_BARS };
			window.recommendedGraphType = clientData.flags?.portfolio_graph_type || graphConfig.recommendedType;

			// Build time series data if needed for line graph or stacked time series
			if (
				(window.recommendedGraphType === GraphTypes.TIME_SERIES_LINE || window.recommendedGraphType === GraphTypes.TIME_SERIES_STACKED) &&
				graphConfig.supportsTimeSeries
			) {
				// Build vested shares map from calculator breakdown for vested-only graphs
				const vestedSharesByClass = {};
				if (window.lastCalculationResult?.breakdown) {
					window.lastCalculationResult.breakdown.forEach(function (classData) {
						if (classData.vested !== undefined) {
							vestedSharesByClass[classData.classKey] = classData.vested;
						}
					});
				}

				const timeSeriesInput = {
					clientData: clientData,
					userData: localUserData,
					client: client,
					shareClasses: shareKeys,
					currentTotalValue: isBW ? window.lastCalculationResult?.vestedValue || 0 : window.lastCalculationResult?.totalValue || 0,
					vestedSharesByClass: vestedSharesByClass,
					useVestedOnly: isBW,
				};
				window.lastTimeSeriesResult = TimeSeriesBuilder.build(graphConfig.timeSeriesStrategy, timeSeriesInput);
			}
		}

		// Run calculation engine for all clients (defaults to private_company_409a)
		// Results are stored in window.lastCalculationResult for templates, cards, and future features
		if (!window.lastCalculationResult) {
			const eventData = { bases, distributions: distribs, contributions, returnsOfCapital, incomeAllocations, accruals };
			const calculatorInput = buildCalculatorInput({
				userShares: userDataShares,
				clientData,
				shareClasses: shareKeys,
				userIds: getActiveUserIds(localUserData),
				eventData,
				options: { floorAtZero: isSEQ, zeroStrikePrice: isBW, clientFlags: { isUA } },
			});
			if (calculatorInput) {
				const calculator = calculationService.getCalculatorForClient(clientData);
				window.lastCalculationResult = calculator.calculate(calculatorInput);

				// CAGR is now computed inside calculator.calculate() via attachCAGR.
				// See #5198 Phase 4 — each calculator owns its CAGR semantic (e.g.
				// ContributionsAccruals / IncomeAllocations exclude transfers so
				// received gifts don't inflate the growth rate).

				// Only overwrite latestValue when client has explicitly opted in via calculator or template
				if ((clientData.flags?.default_value_method || clientData.language?.headline_template) && window.lastCalculationResult?.breakdown) {
					window.lastCalculationResult.breakdown.forEach(function (classData) {
						if (classData.classKey && classData.value !== undefined) {
							latestValue[classData.classKey] = classData.value;
						}
					});
				}
			}
		}
		// ============================================================

		// If defined in client config, use equity_sort_order
		let sortOrder = clientData.equity_sort_order;
		if (sortOrder) {
			const sorted = sortOrder.filter((c) => shareKeys.includes(c));
			const remaining = shareKeys.filter((c) => !sortOrder.includes(c));
			shareKeys = [...sorted, ...remaining];
		} else if (isORR) shareKeys = shareKeys.sort().reverse();
		else if (isACR) {
			// sort on distributions
			shareKeys.sort(function (a, b) {
				if (!distribs[b] && !distribs[a]) return 1;
				return (distribs[b] || 0) - (distribs[a] || 0);
			});
		} else {
			// default: sort shareKeys on descending value
			shareKeys.sort(function (a, b) {
				if (!latestValue[b] && !latestValue[a]) return 1;
				return (latestValue[b] || 0) - (latestValue[a] || 0);
			});
			if (ENROLL && client.startsWith('onward') && shareKeys.includes('ONW')) {
				shareKeys.splice(shareKeys.indexOf('ONW'), 1);
				shareKeys.unshift('ONW');
			}
		}

		hasEquityData = true;
	}
}

/**
 * Preview headline rendering for the currently-viewed user
 * Uses the same global state (bases, latestValue, etc.) that getEquityData populated
 * No separate calculator pipeline — guaranteed to match the UI exactly
 */
window.previewHeadlines = function (templates) {
	if (!clientData || !client) return null;

	// Default to DB-configured templates, allow overrides
	const lang = clientData.language || {};
	templates = Object.assign(
		{
			template: lang.headline_template || '',
			subtitleTemplate: lang.headline_subtitle_template || '',
			tertiaryTemplate: lang.headline_tertiary_template || '',
			classTemplate: lang.class_headline_template || '',
			classSubtitleTemplate: lang.class_subtitle_template || '',
			classTertiaryTemplate: lang.class_tertiary_template || '',
		},
		templates || {}
	);

	// Use preview context if available, otherwise use live globals
	const ctx = window._previewContext;
	const prevShareKeys = ctx ? ctx.shareKeys : shareKeys;
	const prevBases = ctx ? ctx.bases : bases;
	const prevLatestValue = ctx ? ctx.latestValue : latestValue;
	const prevDistribs = ctx ? ctx.distribs : distribs;
	const prevContributions = ctx ? ctx.contributions : contributions;
	const prevReturnsOfCapital = ctx ? ctx.returnsOfCapital : returnsOfCapital;
	const prevUserData = ctx ? ctx.userData : userData;
	const calcResult = ctx ? ctx.calculationResult : window.lastCalculationResult;

	if (!prevShareKeys.length) return null;

	// Calculator-driven legacyData (#5198 Phase 5). The pre-Phase-5 inline math
	// duplicated Path A/B/C totals derivation against `prev*` aliases. The
	// helper subsumes both branches (calcResult-present and fallback).
	const legacyData = HeadlineService.buildFromCalculation(calcResult, {
		shareKeys: prevShareKeys,
		userShares: prevUserData?.clients?.[client]?.shares,
		bases: prevBases,
		latestValue: prevLatestValue,
		distribs: prevDistribs,
		contributions: prevContributions,
		returnsOfCapital: prevReturnsOfCapital,
		labelPaidIn,
		itsaFund: UFL,
		valuationDate: latestValuationDate,
		stockStatus: prevUserData?.clients?.[client]?.status,
		userData: prevUserData,
		clientData,
		client,
	});

	const vars = HeadlineFormatter.buildUnifiedVariableMap({
		calculatorResult: calcResult,
		legacyData: legacyData,
	});

	const portfolioHeadline = HeadlineFormatter.buildUnifiedHeadline(
		{
			template: templates.template || '',
			subtitleTemplate: templates.subtitleTemplate || '',
			tertiaryTemplate: templates.tertiaryTemplate || '',
		},
		{ calculatorResult: calcResult, legacyData: legacyData }
	);

	const classHeadlines = [];
	const classVars = [];
	prevShareKeys.forEach(function (k) {
		const classCalcData = calcResult?.breakdown?.find(function (b) {
			return b.classKey === k;
		});
		const equityConfig = clientData.equity[k];
		const equityName = equityConfig?.name || k;
		const eqCurrentLabel = equityConfig?.currentLabel || '';

		const classVarMap = HeadlineFormatter.buildUnifiedClassVariableMap({
			classData: classCalcData,
			legacyData: legacyData,
			classKey: k,
			equityName: equityName,
			currentLabel: eqCurrentLabel,
		});
		classVars.push({ classKey: k, className: equityName, vars: classVarMap });

		// Per-class headline overrides take precedence over global class templates
		const classEquityConf = clientData.equity?.[k] || {};
		const classHeadline = HeadlineFormatter.buildUnifiedClassHeadline(
			{
				classTemplate: classEquityConf.class_headline_template || templates.classTemplate || '',
				classSubtitleTemplate: classEquityConf.class_subtitle_template || templates.classSubtitleTemplate || '',
				classTertiaryTemplate: classEquityConf.class_tertiary_template || templates.classTertiaryTemplate || '',
			},
			{
				classData: classCalcData,
				legacyData: legacyData,
				classKey: k,
				equityName: equityName,
				currentLabel: eqCurrentLabel,
			}
		);
		classHeadlines.push({ classKey: k, className: equityName, headline: classHeadline });
	});

	return { portfolioHeadline, classHeadlines, vars, classVars, shareKeys: prevShareKeys, variableTypes: HeadlineFormatter.variableTypes };
};

/**
 * Preview equity card templates with real shareholder data.
 * Mirrors previewHeadlines pattern for equity cards.
 *
 * @param {Object} templates - Optional per-class template overrides { line1: "...", line2: "...", line3: "...", line4: "..." }
 * @returns {Object} - { classCards: [{ classKey, className, lines, vars }], variableTypes }
 */
window.previewEquityCards = function (templates) {
	if (!clientData || !client) return null;

	// templates is optional -- if omitted, uses DB-configured templates per class

	const ctx = window._previewContext;
	const prevShareKeys = ctx ? ctx.shareKeys : shareKeys;
	const prevUserData = ctx ? ctx.userData : userData;
	const prevBases = ctx ? ctx.bases : bases;
	const prevLatestValue = ctx ? ctx.latestValue : latestValue;
	const prevInterest = ctx ? ctx.interest : typeof interest !== 'undefined' ? interest : {};
	const calcResult = ctx ? ctx.calculationResult : window.lastCalculationResult;
	const prevAggregates = ctx ? ctx.classAggregates : null;

	if (!prevShareKeys.length) return null;

	const prevUserShares = prevUserData?.clients?.[client]?.shares || {};
	const classCards = [];

	prevShareKeys.forEach(function (k) {
		const equityConfig = clientData.equity[k] || {};
		const equityName = equityConfig.name || k;

		// Apply template overrides to a copy of equityConfig if provided
		let effectiveConfig = equityConfig;
		if (templates) {
			effectiveConfig = Object.assign({}, equityConfig);
			if (templates.line1 !== undefined) effectiveConfig.line1_template = templates.line1;
			if (templates.line2 !== undefined) effectiveConfig.line2_template = templates.line2;
			if (templates.line3 !== undefined) effectiveConfig.line3_template = templates.line3;
			if (templates.line4 !== undefined) effectiveConfig.line4_template = templates.line4;
		}

		// Build a temporary clientData with the overridden config
		const previewClientData = Object.assign({}, clientData, {
			equity: Object.assign({}, clientData.equity, { [k]: effectiveConfig }),
		});

		const result = EquityCardService.renderCardLines(
			k,
			prevUserShares[k],
			previewClientData,
			prevBases[k] || 0,
			prevLatestValue[k] || 0,
			prevInterest[k] || 0,
			calcResult,
			{
				stockStatus: prevUserData?.clients?.[client]?.status,
				userData: prevUserData,
				aggregates: prevAggregates?.[k] || null,
				client: client,
			}
		);

		classCards.push({
			classKey: k,
			className: equityName,
			lines: { line1: result.line1, line2: result.line2, line3: result.line3, line4: result.line4 },
			vars: result.vars,
		});
	});

	return { classCards, shareKeys: prevShareKeys };
};

/**
 * Load a shareholder from the cap table for template preview.
 * Computes calculator result and display data, stores in window._previewContext.
 *
 * For EXTERNAL_DATA clients (e.g. PharmaJet) the CSV transactions loaded into
 * clientData.equity[*].transactions are filtered to the logged-in admin's rows —
 * not the previewed shareholder's. We fetch the preview target's transactions
 * scoped to this call only (no mutation of clientData), and pass them through
 * as an overlay so aggregateEventData can derive the correct per-class basis.
 *
 * Async because of the CSV fetch; synchronous for event-driven clients.
 *
 * @param {string} userid - Email/userid of the shareholder to preview
 * @returns {Promise<Object|null>} PreviewContext or null if shareholder not found
 */
window.loadPreviewShareholder = async function (userid) {
	const capTable = capTableData || NthUtil.sessionStore.readCapTable(client);
	if (!capTable || !clientData) return null;

	let txOverrides = null;
	// Only fetch when we genuinely need preview-scoped transactions:
	// - client uses EXTERNAL_DATA CSV transactions
	// - the previewed user is not the logged-in admin (admin's own transactions
	//   are already in clientData.equity[*].transactions)
	if (window.EXTERNAL_DATA && userid && userid !== userData?.userid) {
		try {
			txOverrides = await fetchTransactionsForPreview({ clientData, userid });
		} catch (err) {
			console.warn('[loadPreviewShareholder] Failed to fetch preview transactions, falling back to event-only:', err);
			txOverrides = null;
		}
	}

	const context = computeShareholderPreview(userid, clientData, capTable, calculationService, client, txOverrides);
	if (context) {
		window._previewContext = context;
	}
	return context;
};

/**
 * Clear the preview context to revert to the logged-in user.
 */
window.clearPreviewShareholder = function () {
	window._previewContext = null;
};

const postPayNowViaDwollaProxy = (paymentData, nthApiTokenId, nthApiTokenValue, smsAccessCode) => {
	const requestParams = {
		payment: paymentData,
		client: client,
		userid: authedUser,
		cookie: cookie,
		processor: getDwollaProcessor(),
		proxy_method: 'initiate_pay_now_transfer',
		token_id: nthApiTokenId,
		token_value: nthApiTokenValue,
		access_code: smsAccessCode,
	};
	return new Promise((resolve, reject) => {
		axios
			.post(dwollaTokenAPI, JSON.stringify(requestParams))
			.then((res) => {
				resolve(res);
			})
			.catch((err) => {
				const errResponse = err?.response?.data;
				if (errResponse === 'Token expired') {
					alert("Sorry, that code is expired. Your browser will refresh. Please click the 'Pay Online Now' button again.");
					//showToast("error-toast", "Sorry, that code is expired.");
					window.location.reload();
				} else if (errResponse === 'Invalid token') {
					alert("Sorry, that code is invalid. Your browser will refresh. Please click the 'Pay Online Now' button again.");
					window.location.reload();
				} else {
					alert(`Transfer failed to process. Please contact your NthRound customer service representative.\nError code: '${errResponse}'`);
					reject(err);
				}
			});
	});
};

function renderEquityData() {
	/* NOTES
	After getting equity data, this method render the landing page equity
	Various resources (image, video, trades, valuations) and decides what to show on landing
	Valuation data are particularly tricky and can be derived from Trades, Events, Transactions, or Valuations
	Finally it renders the equity sections: center panel, headline, and class cards
	*/

	getEquityData();

	// Resolve template syntax in clientData.about before passing to footnote
	// helpers (#5198 Phase 7b-1). Backwards-compatible: strings without {{...}}
	// flow through untouched. Lets Cerity's about consume {{value24x}} from the
	// same template engine the headlines use.
	const resolveAbout = () =>
		HeadlineFormatter.renderAboutString(clientData.about, {
			calculatorResult: window.lastCalculationResult,
			legacyData: { userData, client },
		});

	// Set default labels for graph
	let currentClass = shareKeys[currentShareClass];
	let currentEquity = clientData.equity[currentClass];
	let classValuations = currentEquity ? currentEquity.valuations : [];
	let contractAddress = currentEquity ? currentEquity.contract_address : null;
	SHOW_REF = !isVMS && (currentEquity?.referenceLabel || currentEquity?.shareLabel);
	EQUITY_LABELS = {
		currentLabel: currentEquity ? currentEquity.currentLabel : '',
		referenceLabel: currentEquity ? currentEquity.referenceLabel : '',
		discountLabel: currentEquity ? currentEquity.discountLabel : '',
		referenceAbbrev: currentEquity ? currentEquity.referenceAbbrev : '',
		shareLabel: currentEquity ? currentEquity.shareLabel : '',
		shareAbbrev: currentEquity ? currentEquity.shareAbbrev : '',
		priceLabel: UFL || isTCA ? labelTVPI + ':\xa0' : isAES ? 'value/share:\xa0$' : isPSS ? 'portfolio value:\xa0$' : 'Price:\xa0$',
	};
	if (currentEquity && currentEquity.priceLabel) EQUITY_LABELS.priceLabel = currentEquity.priceLabel;

	let classData = {
		contract: contractAddress,
		symbol: currentClass,
		valuations: classValuations,
	};

	let valuations = classData.valuations ? classData.valuations : [];
	let contract = classData.contract ? classData.contract : null; // Expects a single contract item
	symbol = classData.symbol ? classData.symbol : null;
	className = currentShareClass == -1 ? 'Portfolio' : currentEquity?.name || symbol;

	// detect basis of presentation, dollars vs percentage
	let allOwned = 0;
	let userDataShares = userData.clients[client].shares;
	shareKeys.map((key, index) => {
		allOwned += Number(userDataShares[key].owned);
	});
	usePct = allOwned < 9999 && allOwned != 0 && !SHOW_SMALL;

	const MIP_UNITS = clientData.metrics?.mip_units || 1444.62 + 988.65 + 35.317;
	const TOTAL_UNITS = clientData.metrics?.total_units || 3750.38;
	clientData.about = origClientDataAbout; // reset
	if (isCER) {
		allOwned = 0; // reset
		let mipOwned = 0;
		shareKeys.map((key, index) => {
			allOwned += key == 'CCA' ? 0 : Number(userDataShares[key].owned);
		});
		shareKeys.map((key, index) => {
			mipOwned += ['A', 'C'].includes(key) ? Number(userDataShares[key].owned) : 0;
		});
		mipOwned += Number(clientData.equity['C']?.MIP_adjustments?.[userData.userid] || 0);
		clientData.about = clientData.about.replace(/{ttl_units}/g, allOwned.toLocaleString());
		clientData.about = clientData.about.replace(/{ttl_pct}/g, ((100 * allOwned) / TOTAL_UNITS).toLocaleString());
		clientData.about = clientData.about.replace(
			/{mip_words}/g,
			mipOwned
				? '<br>&emsp;&emsp;&ensp;Also, your MIP participation rate is <b>' + ((100 * mipOwned) / MIP_UNITS).toLocaleString() + ' %</b>.<br><br>'
				: ''
		);
	}

	let tradingData = document.getElementById('tradingData');
	let overviewLandingPage = document.getElementById('overviewLandingPage');

	// Get an image for the landing page
	let landingPage;
	let firstEquityKey = classes[0];
	if (!userDataShares || !Object.keys(userDataShares).length) {
		landingPage = clientData.product_image;
	} else if (currentClass && clientData.equity[currentClass]) {
		landingPage = clientData.equity[currentClass].landingPage;
	} else if (!currentClass && clientData.equity[firstEquityKey]) {
		landingPage = clientData.equity[firstEquityKey].landingPage;
	}

	// Prefer graph? (controlled by per-class landingPage.preferGraph setting)
	let preferGraph = landingPage?.preferGraph;
	hasPriceHistory = contract || valuations?.length > 1;
	hasEventHistory = userDataShares?.[symbol]?.events?.length > 1;
	const transUserIdsForGraph = getActiveUserIds(userData);
	let hasTransactions = classes.some((e) => clientData.equity[e]?.transactions?.some((t) => isUserTransaction(t, transUserIdsForGraph)));
	let hasDistributions = Object.values(userDataShares ?? {}).some((s) => s.events?.some((e) => e.eventType == 'Distribution'));
	let showImageOrGraph = document.getElementById('showImageOrGraph');
	let preference = userDataShares?.[symbol]?.preference ?? 'Graph';
	tryShowGraph = preference === 'Graph';

	// With statements: prefer graph, set graph toggles, and get VMS data/tooltips
	if (HAS_STMTS) {
		tryShowGraph = true;
		showImageOrGraph.classList.add('hidden');

		let showTVPI = document.getElementById('showTVPI');
		let hasShowTVPI = userData.clients[client].showTVPI != null;
		showTVPI.checked = !showTVPI || userData.clients[client].showTVPI;
		document.getElementById('showTvpiElement').style.display = 'block';
		if (isVMS) {
			// default intial state (with no flag yet in user data) = show the tooltip
			let showTooltip = document.getElementById('showTooltip');
			let hasShowTooltip = userData.clients[client].showTooltip != null;
			showTooltip.checked = !hasShowTooltip || userData.clients[client].showTooltip;
			document.getElementById('showTooltipElement').style.display = 'block';

			// Need this here to generate trade data, including currentValue (last data point)
			lookForFullAccountStatements(userData);
		}
	}

	// Decide on initial state: Graph or Image?

	// Reset portfolio graph flags when switching views
	window.isValueCompositionGraph = false;
	window.isPortfolioTimeSeriesStacked = false;
	window.isClassTimeSeries = false;
	window.isIncomeAllocationsDualLine = false;
	window.portfolioClasses = null;
	window.valueCompositionLabels = null;

	// Check for portfolio graph preference
	let preferPortfolioGraph = clientData.flags?.prefer_portfolio_graph;
	let hasPortfolioValuations = false;
	if (!symbol && preferPortfolioGraph) {
		// Check if any equity classes user owns have valuations
		let userShares = userData.clients[client]?.shares || {};
		Object.keys(clientData.equity)
			.filter((shareClass) => Number(userShares[shareClass]?.owned) > 0)
			.forEach(function (shareClass) {
				if (clientData.equity[shareClass].valuations && clientData.equity[shareClass].valuations.length > 1) {
					hasPortfolioValuations = true;
				}
			});
	}

	// For event-based calculators (income_allocations, fund_capital_account), check for time series data
	// For valuation-based calculators, check for valuations
	// For income_allocations, also check hasEvents flag to ensure shareholder has actual event data
	const isIncomeAllocationsMethod = window.lastCalculationResult?.metadata?.methodUsed === 'income_allocations';
	const hasTimeSeriesData = window.lastTimeSeriesResult?.data?.length > 0 && (!isIncomeAllocationsMethod || window.lastTimeSeriesResult?.hasEvents);
	// Also allow entry for calculator-based clients (fund_investor, etc.) that don't have traditional valuations
	// Require actual value (totalValue > 0 or contributions > 0) to avoid showing empty graph
	const calcResult = window.lastCalculationResult;
	// Skip calculator-driven graph when vested-only mode produced no time series (cashed-out BW users)
	const vestedOnlyEmpty = isBW && calcResult?.vestedValue === 0;
	const hasCalculatorResult = calcResult?.totalValue !== undefined && (calcResult.totalValue > 0 || calcResult.totalContributions > 0);
	if (!symbol && preferPortfolioGraph && !vestedOnlyEmpty && (hasPortfolioValuations || hasTimeSeriesData || hasCalculatorResult)) {
		// Determine graph type: time series line, stacked, or comparison bars
		const graphType = window.recommendedGraphType || GraphTypes.COMPARISON_BARS;

		if (graphType === GraphTypes.TIME_SERIES_STACKED && window.lastTimeSeriesResult?.data?.length > 0) {
			// Render stacked area time series graph by share class
			graphArray = window.lastTimeSeriesResult.data;
			window.isValueCompositionGraph = false;
			window.isPortfolioTimeSeries = false;
			window.isPortfolioTimeSeriesStacked = true;
			window.portfolioClasses = window.lastTimeSeriesResult.shareClasses || [];
			window.valueCompositionLabels = null;
		} else if (graphType === GraphTypes.TIME_SERIES_LINE && window.lastTimeSeriesResult?.data?.length > 0) {
			// Render single-line time series graph
			graphArray = window.lastTimeSeriesResult.data;
			window.isValueCompositionGraph = false;
			window.isPortfolioTimeSeries = true;
			window.isPortfolioTimeSeriesStacked = false;
			window.portfolioClasses = null;
			window.valueCompositionLabels = null;
		} else {
			// Render 2-bar comparison chart (existing logic)
			let valueCompositionData = buildValueCompositionTimeSeries();
			graphArray = valueCompositionData.data;
			window.portfolioClasses = valueCompositionData.classes;
			window.valueCompositionLabels = valueCompositionData.labels;
			window.portfolioClassDetails = valueCompositionData.classDetails; // For tooltip breakdown
			window.isValueCompositionGraph = true;
			window.isPortfolioTimeSeries = false;
			window.isPortfolioTimeSeriesStacked = false;
		}

		// Set headline labels for user's portfolio
		let currentLabel = document.getElementById('currentLabel');
		let currentSymbol = document.getElementById('currentSymbol');
		let currentValue = document.getElementById('currentValue');

		// Single render entry point (#5198 Phase 7a). Replaces the explicit
		// buildFromCalculation + renderPortfolioHeadline pair.
		const graphHeadlineResult = HeadlineService.renderHeadline(
			{ label: currentLabel, amount: currentSymbol, subtitle: currentValue },
			window.lastCalculationResult,
			{
				viewMode: 'portfolio',
				shareKeys,
				userShares: userData.clients?.[client]?.shares,
				bases,
				latestValue,
				distribs,
				contributions,
				returnsOfCapital,
				labelPaidIn,
				itsaFund: UFL,
				valuationDate: latestValuationDate,
				stockStatus: userData.clients?.[client]?.status,
				userData,
				clientData,
				client,
				defaultTemplate: 'Your Total Portfolio Value: {{totalValue}}',
			}
		);

		// Also update graph view elements (for tertiary line)
		if (graphHeadlineResult.handled && graphHeadlineResult.headline) {
			HeadlineService.renderToGraphElements(graphHeadlineResult.headline);
		}

		// Hide the "Most recent Price" line for portfolio graph
		// Only apply when templates aren't in control - renderToGraphElements handles visibility when templates are used
		if (!graphHeadlineResult.handled) {
			document.getElementById('priceLabelReport').classList.add('hidden');
		}

		// Add footnote below portfolio graph if enabled
		// Skip for Cerity here — 24x footnote runs later after graphArray is ready
		if (clientData.flags?.enable_footnote && clientData.about && !(isCER && clientData.about.includes('{24x_words}'))) {
			addFootnoteAndAdjustSpacing(resolveAbout());
		}

		showGraph(false);
	} else if (!symbol && ((isPSS && hasTransactions) || (isACR && hasDistributions))) {
		buildPortfolioArray();
		showGraph(false);
	} else if (isCER && symbol && preferGraph && symbol != 'CCA') {
		buildTransactionArray();
		showGraph(false);
	} else if (
		symbol &&
		window.lastTimeSeriesResult?.data?.length > 1 &&
		window.lastTimeSeriesResult?.hasEvents &&
		window.lastCalculationResult?.metadata?.methodUsed === 'income_allocations'
	) {
		// Income allocations time series graph for single class
		// Rebuild time series for just the selected class so graph shows per-class data
		const pn = window.location.pathname;
		const classUserData = pn.includes('/users/edit') ? shareholderData : userData;
		const classTimeSeriesInput = {
			clientData: clientData,
			userData: classUserData,
			client: client,
			shareClasses: [symbol],
		};
		const classTimeSeriesResult = TimeSeriesBuilder.build('income_allocations', classTimeSeriesInput);
		console.log('[IncomeAllocations] Using pre-built time series for class:', symbol);
		graphArray = classTimeSeriesResult.data;
		window.isClassTimeSeries = true;
		window.isIncomeAllocationsDualLine = classTimeSeriesResult.isDualLine || false;

		showGraph(false);
	} else if (
		symbol &&
		window.recommendedGraphType === GraphTypes.TIME_SERIES_STACKED &&
		clientData.equity[symbol]?.valuations?.length > 1 &&
		clientData.equity[symbol]?.prefer_value_graph
	) {
		// Single-class time series graph (matches portfolio stacked time series style)
		// Only enabled when prefer_value_graph is explicitly set for this class
		// Use calculator output for vested values to match headline display
		const calcClassData = window.lastCalculationResult?.breakdown?.find((c) => c.classKey === symbol);
		const classTimeSeriesResult = TimeSeriesBuilder.buildSingleClassTimeSeries({
			clientData: clientData,
			userData: userData,
			client: client,
			shareClass: symbol,
			currentValue: calcClassData?.vestedValue || latestValue[symbol] || 0,
			vestedShares: calcClassData?.vested,
			strikePrice: calcClassData?.strikePrice,
			// Barry Wehmiller: show only vested shares in graph, starting from first vesting date
			useVestedOnly: isBW,
		});

		if (classTimeSeriesResult?.data?.length > 1) {
			graphArray = classTimeSeriesResult.data;
			window.isClassTimeSeries = true;
			showGraph(false);
		} else if (isBW) {
			// Barry Wehmiller vested-only mode: don't fall back to price graph
			// If no vested shares, show landing image instead
			window.isClassTimeSeries = false;
			tradingData.style.display = 'none';
			renderLanding(clientData.equity[symbol]?.landingPage, clientData.equity[symbol]?.valuations);
		} else {
			// Fall back to traditional price graph
			window.isClassTimeSeries = false;
			if (classData && preferGraph && tryShowGraph && (hasPriceHistory || hasEventHistory)) {
				document.getElementById('priceLabelReport').classList.remove('hidden');
				showGraph(true);
			}
		}
	} else if (classData && symbol && preferGraph && tryShowGraph && (hasPriceHistory || hasEventHistory)) {
		// Show the "Most recent Price" line for individual class graphs
		document.getElementById('priceLabelReport').classList.remove('hidden');
		showGraph(true);
	} else {
		//  12-7-2024 Hide this button globally - keep all the associated logic (for now), and see if anyone squeaks
		if (false && classData && symbol && preferGraph && (hasPriceHistory || hasEventHistory)) {
			showImageOrGraph.classList.remove('hidden');
			showImageOrGraph.innerHTML = 'Show Graph';
		} else showImageOrGraph.classList.add('hidden');
		tradingData.style.display = 'none';
		console.log('No price history for this client');
		if (landingPage || clientData.product_image) {
			// 2-7-2024 Robb at PharmaJet reported that some users, on first render, see number of shares instead of Paid In Capital
			// There are no async processes that I can see, but maybe JavaScript can get ahead of itself??  <-- WRONG, External Data
			renderLanding(landingPage, valuations);
		} else overviewLandingPage.style.display = 'none';
	}

	function showGraph(get) {
		// Initialize the Google Graphs library
		if (typeof google === 'undefined' || !google.charts) {
			console.warn('Google Charts not loaded yet — skipping graph render');
			return;
		}
		google.charts.load('current', { packages: ['corechart'] });
		google.charts.setOnLoadCallback(get ? getTrades : drawChart);
		//fade(document.getElementById('landingPageImage'));
		tradingData.style.display = 'block';
		overviewLandingPage.style.display = 'none';
		//  12-7-2024 Hide this button globally - keep all the associated logic (for now), and see if anyone squeaks
		if (false && !HAS_STMTS && !isMAR) {
			showImageOrGraph.classList.remove('hidden');
			showImageOrGraph.innerHTML = 'Show Image';
		}
	}

	function getTrades() {
		/* NOTES
		This method tries to get pricing data (trades)
		For staging, it uses a hard-coded array
		With an ETH contract address, it pulls data from Etherscan
		If there are no trades, Valuations, or Events, it just renders the landing
		Otherwise it tries to build a valuation array
		*/
		let trades = [];
		if (!contract || isStaging) {
			if (isStaging && symbol == 'NTH')
				trades = [
					[1542327008000, 2, 25, 2],
					[1542327008000, 2, 25, 2],
					[1550962280000, 2.5, 100, 2.5],
					[1551831189000, 2.5, 1000, 2.5],
					[1552168460000, 2.5, 900, 2.5],
					[1552265313000, 2.5, 210, 2.5],
					[1552265313000, 2.5, 500, 2.5],
					[1552265313000, 2.5, 900, 2.5],
					[1552699937000, 2.5, 900, 2.5],
					[1554507113000, 2.5, 5000, 2.5],
					[1560815469000, 2.5, 5000, 2.5],
				];
			let hasValuations = valuations && valuations.length > 1;
			let hasEvents = userDataShares && userDataShares[symbol] && userDataShares[symbol].events && userDataShares[symbol].events.length > 1;
			if (trades.length < 3 && !hasValuations && !hasEvents) {
				tradingData.style.display = 'none';
				renderLanding(clientData.equity[currentClass].landingPage, valuations);
			}
			buildValuationArray(trades, valuations);
			return;
		}

		let scanKey = 'UQA9Z9XCYMCHRYW8JCK8P9AIVID9SQ3N27'; // Etherscan API key
		const scanURL =
			'https://api.etherscan.io/api?module=logs&action=getLogs&fromBlock=0&toBlock=latest&address=' +
			contract +
			'&topic0=0xc62f8b1313b549bc5c94efe776ee0559a8f88efdb3db504331857fc163553c71&apikey=' +
			scanKey;

		document.getElementById('currentSymbol').innerHTML = symbol + ' Value'; // Set the symbol in the UI

		// Get trade details from Etherscan
		axios
			.get(scanURL, { withCredentials: false })
			.then(function (response) {
				// Filter to return only data for current client
				if (!response.data || !response.data.result) {
					console.log('No trade events found for this client.');
				} else {
					let result = JSON.stringify(response.data.result);

					// Firefox does not allow named groups, so use numbered groups
					//const regexp = RegExp('"data":"0x(?<RequestKey>[0-9a-fA-F]{64})(?<EtherAmount>[0-9a-fA-F]{64})(?<TokenAmount>[0-9a-fA-F]{64})(?<Price>[0-9a-fA-F]{64}).*?"timeStamp":"0x(?<TimeStamp>[0-9a-fA-F]{8})','g');
					const regexp = RegExp(
						'"data":"0x([0-9a-fA-F]{64})([0-9a-fA-F]{64})([0-9a-fA-F]{64})([0-9a-fA-F]{64}).*?"timeStamp":"0x([0-9a-fA-F]{8})',
						'g'
					);
					let match = regexp;
					let tradeData = [];

					while ((match = regexp.exec(result)) !== null) {
						tradeData.push(match);
					}

					let tokenSplits = null;
					switch (symbol) {
						case 'NTH':
							tokenSplits = { 1609459200: { ratio: '2' } };
							break;
						case 'NTHP':
							tokenSplits = { 1609459200: { ratio: '2' } };
							break;
					}
					for (let i = 0; i < tradeData.length; i++) {
						let date = parseInt(tradeData[i][5], 16) * 1000; //["TimeStamp"], 16) * 1000);
						let price = parseInt(tradeData[i][4], 16) / 1e18; //["Price"]);
						let volume = parseInt(tradeData[i][3], 16) / 1e18; //["TokenAmount"]);
						if (tokenSplits) {
							// this code only handles a single split
							let splitDate = Object.keys(tokenSplits)[0];
							if (date < parseInt(splitDate) * 1000) {
								let ratio = Number(tokenSplits[splitDate].ratio);
								price /= ratio;
								volume *= ratio;
							}
						}
						let fairMarketPrice = price;
						let k = [date, price, volume, fairMarketPrice];
						//let k = [(new Date(parseInt(tradeData[i]["TimeStamp"], 16) * 1000).toLocaleString()), decimalValue(tradeData[i]["Price"]), decimalValue(tradeData[i]["TokenAmount"]), (parseInt(tradeData[i]["RequestKey"], 16))];
						trades.push(k);
					}

					//TODO: Calculate price change of last and penultimate trades
					// let diff = trades[trades.length - 1][1].toFixed(2) - trades[trades.length - 2][1].toFixed(2);
					// let original = trades[trades.length - 1][1].toFixed(2)
					// let percentChange = (diff / original) * 100;

					// document.getElementById('lastTraded').innerHTML = trades[trades.length - 1][1].toLocaleString('en-US', usd) + ' (' + percentChange + '%)';
				}

				if (trades.length < 3 && (!valuations || valuations.length < 2)) {
					tradingData.style.display = 'none';
					showImageOrGraph.classList.add('hidden');
					renderLanding(clientData.equity[currentClass].landingPage, valuations);
				}
				buildValuationArray(trades, valuations); //, currentClass);
			})
			.catch(function (response) {
				console.log(response);
			});
	}

	function buildTransactionArray() {
		let firstDate = 10 ** 12;
		let transArray = [];

		getEquityData();

		// Dont't display future (beyond this year) Events or Transactions
		let nextYear = new Date(new Date().getFullYear() + 1, 1, 1) / 1000;

		// Deep-clone so mutations don't affect the shared AppState object
		let transUserData = structuredClone(pn.includes('/admin/users/edit') ? shareholderData : userData);

		if (!transUserData) return;

		// Support consolidated view
		const transArrayUserIds = getActiveUserIds(transUserData);

		let clientEquity = clientData.equity[symbol];
		if (clientEquity.transactions) {
			let valuations = clientEquity.valuations || [];
			valuations.sort((a, b) => (Number(a.date) > Number(b.date) ? -1 : 1));

			// Cumulate transactions
			let cumulativeTrans = 0;
			clientEquity.transactions.sort((a, b) => (Number(a.date) < Number(b.date) ? -1 : 1)); // ascending
			clientEquity.transactions.forEach(function (t) {
				let date = Number(t.date);
				if (date > nextYear) return;
				let valuation = 1;
				// Not sure if we are tracking cost basis or current value. Ignore both for now.
				// if (valuations.length) {
				// 	let thisVal = valuations.find(v => Number(v.date) <= date) || null;
				// 	if (!thisVal) thisVal = valuations[valuations.length - 1];
				// 	valuation = Number(thisVal.sharePrice);
				// }
				const receiverLower = t.receiver?.toLowerCase();
				const senderLower = t.sender?.toLowerCase();
				const isReceiver = receiverLower && transArrayUserIds.some((uid) => uid?.toLowerCase() === receiverLower);
				const isSender = senderLower && transArrayUserIds.some((uid) => uid?.toLowerCase() === senderLower);
				if (isReceiver) {
					if (date < firstDate) firstDate = date;
					cumulativeTrans += Number(t.quantity) * valuation;
				} else if (isSender) {
					if (date < firstDate) firstDate = date;
					cumulativeTrans -= Number(t.quantity) * valuation;
				}
				transArray.push([date * 1000, cumulativeTrans, 0, cumulativeTrans]);
			});
		}

		graphArray = transArray;
		let landingDivStyle = document.getElementById('landingDiv').style;
		landingDivStyle.minHeight = '520px';
		landingDivStyle.maxHeight = '800px';
		currentLabel.innerHTML = 'Your Current Capital Balance';
	}

	function buildPortfolioArray() {
		// For Acropolis, and other future clients
		let firstDate = 10 ** 12;
		let portArray = [];
		let clientEvents = [];

		getEquityData();

		// Dont't display future (beyond this year) Events or Transactions
		let nextYear = new Date(new Date().getFullYear() + 1, 1, 1) / 1000;

		// Deep-clone so mutations don't affect the shared AppState object
		let eventUserData = structuredClone(pn.includes('/admin/users/edit') ? shareholderData : userData);

		if (!eventUserData) return;

		// Support consolidated view
		const portArrayUserIds = getActiveUserIds(eventUserData);

		classes.forEach(function (c, index) {
			let clientEquity = clientData.equity[c];
			if (clientEquity.transactions) {
				let valuations = clientEquity.valuations || [];
				valuations.sort((a, b) => (Number(a.date) > Number(b.date) ? -1 : 1));

				// 👇 Insert virtual valuation if the latest one is recent or in the future
				if (isPSS && valuations.length > 0) {
					const now = Date.now() / 1000;
					const latestVal = valuations[0];
					const latestValDate = Number(latestVal.date);

					const virtualLag = 30 * 86400; // Insert virtual point 30 days before real one
					const keepAliveBuffer = 15 * 86400; // Allow 15 days after real valuation becomes effective

					if (latestValDate - virtualLag > 0 && latestValDate > now - keepAliveBuffer) {
						const virtualValuation = {
							date: (latestValDate - virtualLag).toFixed(0), // Rounded to nearest second
							sharePrice: latestVal.sharePrice,
							notes: '[virtual copy of upcoming valuation]',
						};
						valuations.push(virtualValuation);
						valuations.sort((a, b) => (Number(a.date) > Number(b.date) ? -1 : 1));
					}
				}

				// first include each equity issuance at its most recent valuation
				clientEquity.transactions.forEach(function (t) {
					let date = Number(t.date);
					if (date > nextYear) return;
					let valuation = 1;
					if (valuations.length) {
						let thisVal = valuations.find((v) => Number(v.date) <= date) || null;
						if (!thisVal) thisVal = valuations[valuations.length - 1];
						valuation = Number(thisVal.sharePrice);
					}
					const receiverLower = t.receiver?.toLowerCase();
					const senderLower = t.sender?.toLowerCase();
					const isReceiver = receiverLower && portArrayUserIds.some((uid) => uid?.toLowerCase() === receiverLower);
					const isSender = senderLower && portArrayUserIds.some((uid) => uid?.toLowerCase() === senderLower);
					if (isReceiver) {
						if (date < firstDate) firstDate = date;
						if (isPSS) portArray.push([date, Number(t.quantity) * valuation]);
					} else if (isSender) {
						if (date < firstDate) firstDate = date;
						if (isPSS) portArray.push([date, -Number(t.quantity) * valuation]);
					}
				});

				// next add the step-up (step-down?) adjustments associated with each valuation
				let vLen = valuations.length;
				if (vLen > 1 && isPSS) {
					valuations.reverse();
					for (let i = 1; i < vLen; i++) {
						let priorTransactions = clientEquity.transactions.filter((t) => Number(t.date) < Number(valuations[i].date));
						if (priorTransactions.length) {
							let vDiff = Number(valuations[i].sharePrice) - Number(valuations[i - 1].sharePrice);
							if (vDiff) {
								let priorQty = priorTransactions.reduce((acc, t) => {
									return acc + Number(t.quantity);
								}, 0);
								let adjustment = vDiff * priorQty;
								let thisDate = Number(valuations[i].date);
								while (portArray.some((p) => p[0] == thisDate)) thisDate++;
								portArray.push([thisDate, adjustment]);
							}
						}
					}
				}
			}

			let userShares = eventUserData.clients?.[client]?.shares?.[c];
			if (userShares && userShares.events) {
				// console.log(userShares.events);
				userShares.events.forEach(function (ev) {
					if (Number(ev.date) < nextYear) return;
					let thisEvent = clientData.equity[c].events.find((x) => x.nrid == ev.nrid);
					let date = thisEvent ? Number(thisEvent.date) : 10 ** 12;
					if (date < firstDate) firstDate = date;
					let addEvents = ['Distribution', 'Cash Dividend']; //["Contribution","Distribution","Gain (Loss) Accrual","Principal Funded","Interest Accrual"];
					let subEvents = []; //["Return of Capital","Redemption","Principal Repayment"];
					if (thisEvent && (addEvents.includes(thisEvent.eventType) || subEvents.includes(thisEvent.eventType))) {
						// console.log(moment.unix(date).format('ll'), thisEvent.eventType, ev.grossDollars);
						let portItem = [date, Number(ev.grossDollars)];
						if (subEvents.includes(thisEvent.eventType)) portItem = [date, -Number(ev.grossDollars)];
						portArray.push(portItem);
					}
				});
			}
		});

		portArray.sort((a, b) => a[0] - b[0]);
		// console.log(portArray);
		cumulativeBalance = 0;
		portArray.forEach(function (a, i) {
			cumulativeBalance += a[1];
			portArray[i][0] = portArray[i][0] * 1000;
			portArray[i][1] = cumulativeBalance;
			portArray[i][2] = 0;
			portArray[i][3] = 0;
		});
		if (portArray.length > 0) {
			let firstItem = [(firstDate - (isPSS ? 31556926 : 0)) * 1000, 0, 0, 0];
			let lastItem = [Date.now(), cumulativeBalance, 0, 0];
			portArray.splice(0, 0, firstItem);
			portArray.push(lastItem);
		}

		portArray.sort((a, b) => a[0] - b[0]); // again
		graphArray = portArray;
		let landingDivStyle = document.getElementById('landingDiv').style;
		// landingDivStyle.height = "520px";
		landingDivStyle.minHeight = '520px';
		landingDivStyle.maxHeight = '800px';

		// Skip headline updates if template system already handled it
		if (!clientData.language?.headline_template) {
			let currentLabel = document.getElementById('currentLabel');
			let currentSymbol = document.getElementById('currentSymbol');
			let currentValue = document.getElementById('currentValue');
			currentLabel.innerHTML = 'Your Current '; //default

			if (isPSS) {
				currentLabel.innerHTML = 'Current Portfolio Value: $';
				currentValue.innerHTML =
					Number(graphArray[graphArray.length - 1][1]).toLocaleString('en-US', m0) +
					`<span class="text-lg text-light font-md"> (assumes fully vested)</span>`;
			}

			let totalPaidInCapital = 0;
			let uD = userData.clients[client].shares;
			shareKeys.forEach(function (key) {
				if (bases[key] >= 0)
					totalPaidInCapital += bases[key] * (isAES ? Number(uD[key].owned) : 1); // <-- BIG CHANGE !!!
				else if (clientData.equity[key]?.hasOwnProperty('interestRate')) totalPaidInCapital += Number(uD[key].owned);
			});

			if (totalPaidInCapital > 0) {
				currentLabel.innerHTML = 'Your Total ' + labelPaidIn + ': &ensp;$&nbsp;';
				currentSymbol.innerHTML = totalPaidInCapital.toLocaleString('en-US', m2);
				if (isACR) {
					let totalCash =
						Number(userDataShares['CASH'] ? userDataShares['CASH'].owned : '0') +
						Number(userDataShares['CASH-2'] ? userDataShares['CASH-2'].owned : '0');
					currentValue.innerHTML = '&ensp; (&nbsp;' + totalCash.toLocaleString('en-US', usd) + '&nbsp;in&nbsp;cash&nbsp;)';
					currentValue.classList.add('text-lg');
				} else currentValue = '';
				// valueHeading.classList.remove('hidden');
			}
		} // end if (!clientData.language?.headline_template)

		// drawChart();
	}

	function buildValuationArray(trades, valuations) {
		/* NOTES
		This method tries to get a stream of data for the graph
		It looks at Valuations, net of Strike Price if found in Transactions
		It also looks at Events and tries to generate a stream of TVPIs
		*/

		if (!userData) AppState.userData = JSON.parse(sessionStorage.getItem('userData'));
		let strikePrice = getEffectiveStrikePriceForUser(clientData.equity[symbol], userData.userid, {
			useWeighted: true,
			netContra: true,
			outstandingOnly: true,
		});

		// GRAPH SHADING
		// Right now, this code cannot handle more complex scenarios than a single hold period. Multiple hold periods would be quite challenging!
		// Also, the graph doesn't indicate anything about how much is held, or changes in amounts held.  It's just in, or out.
		// Last thing: all of the dates are Valuation dates.  The graph does not add points for entry or exit dates that may be different than Valuation dates.
		// That's doable, but quite a bit more complicated, and would need to make assumptions (or use Transaction data) about entry and exit price that may be different that Valuation prices.
		// UPDATE: we now use linear interpolation between Valuation points to show precise entry/exit dates.  Pretty cool.
		let doneIn;
		let doneOut;
		let priorDate;
		let priorPrice;
		let dateIn = 9999999999999;
		let dateOut = 9999999999999;
		USE_STRIKE = clientData.equity[currentClass].useStrike;
		SHADE_GRAPH = clientData.equity[currentClass].shadeGraph;

		let transactions = getUserTransactionsForClass(clientData.equity[symbol], userData.userid);

		if (SHADE_GRAPH) {
			// Get in/out dates for graph shading
			valuations.sort((a, b) => (Number(a.date) < Number(b.date) ? -1 : 1)); // ascending
			let transIn = transactions.filter((t) => getSignedQuantityForUser(t, userData.userid) > 0);
			let transOut = transactions.filter((t) => getSignedQuantityForUser(t, userData.userid) < 0);
			if (transIn.length) {
				transIn.sort((a, b) => (Number(a.date) < Number(b.date) ? -1 : 1)); // ascending
				dateIn = moment(Number(transIn[0].date) * 1000)
					.startOf('day')
					.valueOf();
				// console.log("dateIn " + dateIn);
			}
			if (transOut.length) {
				transOut.sort((a, b) => (Number(a.date) < Number(b.date) ? 1 : -1)); // descending
				if (userData.clients[client].shares[currentClass].owned == 0) {
					dateOut = moment(Number(transOut[0].date) * 1000)
						.startOf('day')
						.valueOf();
					// console.log("dateOut " + dateOut);
				}
			}
		}

		// Format the valuations array
		valuations.forEach(function (v, i) {
			let date = Number(v.date.replace(/[^.\d]/g, '')) * 1000;
			if (isNaN(date)) date = new Date(v.date).getTime();
			let sharePrice = Number(v.sharePrice.replace(/[^.\d]/g, '')) - (USE_STRIKE || isEDM || isBW ? 0 : strikePrice);
			if (sharePrice < 0) sharePrice = 0;
			if (!isNaN(date) && !isNaN(sharePrice)) {
				let volume = 0;
				if (v.volume) {
					volume = Number(v.volume.replace(/[^.\d]/g, ''));
				}
				let referencePrice = sharePrice;
				if (v.referencePrice) {
					referencePrice = Number(v.referencePrice.replace(/[^.\d]/g, ''));
				}
				if (USE_STRIKE && referencePrice > strikePrice) referencePrice = strikePrice;
				let dateMidnight = moment(date).startOf('day').valueOf();
				if (SHADE_GRAPH && dateMidnight >= dateIn) {
					if (dateMidnight <= dateOut) {
						referencePrice = 0;
						// console.log("Zero dropline for " + date);
						if (!doneIn) {
							doneIn = true;
							referencePrice = sharePrice;
							if (Math.abs(dateIn - date) < 432000000)
								trades.push([date + 1, sharePrice, volume, 0]); // within 5 days
							else {
								let interPrice = priorPrice + ((sharePrice - priorPrice) * (dateIn - priorDate)) / (date - priorDate);
								if (interPrice) {
									trades.push([dateIn, interPrice, volume, interPrice]);
									trades.push([dateIn + 1, interPrice, volume, 0]);
								}
								referencePrice = 0;
							}
							// console.log("Done in dropline for " + date);
						}
					} else if (!doneOut) {
						doneOut = true;
						if (Math.abs(dateOut - date) < 432000000)
							trades.push([priorDate + 1, priorPrice, volume, priorPrice]); // within 5 days
						else {
							let interPrice = priorPrice + ((sharePrice - priorPrice) * (dateOut - priorDate)) / (date - priorDate);
							trades.push([dateOut, interPrice, volume, 0]);
							trades.push([dateOut + 1, interPrice, volume, referencePrice]);
						}
						// console.log("Done out dropline for " + priorDate);
					}
				}
				priorDate = date;
				priorPrice = sharePrice;
				trades.push([date, sharePrice, volume, referencePrice]);
			}
		});

		let sumAccruals = 0;
		let sumContributions = 0;
		let sumDistributions = 0;
		if ((UFL && !isJMS) || isTCA || isCER) {
			let navValues = [];
			let tvpiValues = [];
			let userClient = userData.clients[client];
			let clientEvents = clientData.equity[symbol].events;
			if (userClient.shares[symbol] && userClient.shares[symbol].events) {
				let allEvents = [...userClient.shares[symbol].events];
				transactions.forEach(function (t) {
					let newEvent = {
						date: t.date,
						grossDollars: (Number(t.quantity) * Number(t.strikePrice ? t.strikePrice : t.sharePrice)).toString(),
						nrid: t.sender == userData.userid ? 'transactionSender' : 'transactionReceiver',
					};
					allEvents.push(newEvent);
				});

				// allEvents = allEvents.filter(e => Number(e.grossDollars) > 0);
				allEvents.forEach(function (e, i) {
					let thisEvent = clientEvents.find((obj) => obj.nrid == e.nrid);
					if (thisEvent) allEvents[i].date = thisEvent.date;
				});
				allEvents = allEvents.filter((e) => e.date);
				allEvents.sort((a, b) => (Number(a.date) < Number(b.date) ? -1 : 1));

				allEvents.forEach(function (e, index) {
					if (e.nrid.startsWith('transaction')) {
						if (e.nrid == 'transactionSender') sumContributions -= Number(e.grossDollars);
						else sumContributions += Number(e.grossDollars);
						if (index >= allEvents.length - 1) pushTVPI(e.date); // only push if final event
						return;
					}
					let tvpi = 1;
					let clientEvent = clientEvents.find((obj) => obj.nrid == e.nrid);
					if (clientEvent) {
						if (false) {
							//isIQ3 && clientEvent.eventType == "Contribution" && clientEvent.notes.includes("Re-invested")) {
							sumDistributions += Number(e.grossDollars);
						} else if (clientEvent.eventType == 'Contribution') {
							sumContributions += Number(e.grossDollars);
							if (isCER || isTCA || index >= allEvents.length - 1) pushTVPI(clientEvent.date); // only push if final event
							// } else if (clientEvent.eventType == "Redemption" || clientEvent.eventType == "Return of Capital") {  // <-- Made this code change, and 4 lines below, on 12/3/2021 while working on Symmetrical (who does not use RoC) ???
						} else if (clientEvent.eventType == 'Redemption') {
							// WHY the change above and four lines below??????  Having RoC reduce Contributions (and NOT increase returns) doesn't work for Maguire Hayden, that's for sure.
							sumContributions -= Number(e.grossDollars);
							pushTVPI(clientEvent.date);
						} else if (clientEvent.eventType == 'Gain (Loss) Accrual') {
							sumAccruals += Number(e.grossDollars);
							pushTVPI(clientEvent.date);
						} else if (
							clientEvent.eventType == 'Distribution' ||
							clientEvent.eventType == 'Cash Dividend' ||
							clientEvent.eventType == 'Return of Capital'
						) {
							// <-- Included RoC again on 12/16/2021
							sumDistributions += Number(e.grossDollars);
							pushTVPI(clientEvent.date);
						}
					}
				});

				function pushTVPI(date) {
					if (isJMH) {
						let netChange = sumAccruals - sumDistributions;
						let netTVPI = 1 + netChange / sumContributions;
						let grossTVPI = 1 + sumAccruals / sumContributions;
						tvpiValues.push([Number(date) * 1000, netTVPI, 0, grossTVPI]);
					} else if (isCER || isTCA || isORR) {
						let thisNAV = sumDistributions + sumAccruals + (isCER || client == 'theclosingagent' ? sumContributions : 0);
						navValues.push([Number(date) * 1000, thisNAV, 0, thisNAV]);
					} else {
						let thisTVPI = sumContributions == 0 ? 100 * sumDistributions : (100 * sumDistributions) / sumContributions;
						tvpiValues.push([Number(date) * 1000, thisTVPI, 0, thisTVPI]);
					}
				}

				// what are we trying to do here???  (It fails for Gabe and HC-III)
				//if (tvpiValues.length > trades.length / 2) trades = tvpiValues;
				// changed trigger from 3 to 5, for OnwardPE  ** HAIR TRIG GER **
				if (isCER || isTCA) trades = navValues;
				else if ((tvpiValues.length > 5 || isJMH || isIQ3) && !isVMS) trades = tvpiValues;

				// Need this here to generate latestValue data (totalCurrentValue for each class)
				if (isJMH) createBasicAccountStatements(allEvents);
			}
		}

		// GRAPH LABELS AND ASSOCIATED VALUES
		// Handle the case where this client doesn't have any valuations to display. (except AE Studio for the moment...)
		let showImageOrGraph = document.getElementById('showImageOrGraph');
		if (!trades.length || (!isAES && !trades.reduce((a, b) => a + b[1], 0))) {
			tradingData.style.display = 'none';
			console.log('No price history for this client');
			showImageOrGraph.classList.add('hidden');
			if (clientData.product_image) {
				renderLanding(clientData.equity[symbol].landingPage, valuations);
			} else {
				overviewLandingPage.style.display = 'none';
			}
			//document.getElementById('tradingData').style.display = 'none';
		} else {
			// Draw the trading graph using the information returned
			graphArray = trades;
			graphArray.sort((a, b) => a[0] - b[0]);
			if (HAS_STMTS) {
				addStatements();
			} else {
				// Commented out on 4-26-2022 (see Github #887)
				// showImageOrGraph.classList.remove('hidden');
				// showImageOrGraph.innerHTML = "Show Image";
			}

			let landingDivStyle = document.getElementById('landingDiv').style;
			// landingDivStyle.height = "520px";
			landingDivStyle.minHeight = '520px';
			landingDivStyle.maxHeight = '800px';

			drawChart(); //symbol);

			// Adds footnote text below graph
			if (clientData.flags?.enable_footnote) addFootnoteAndAdjustSpacing(resolveAbout());

			let vestedValue = -1;
			let showTVPI = document.getElementById('showTVPI');
			let calculatedValue = trades[trades.length - 1][1] - (USE_STRIKE ? strikePrice : 0);
			if (USE_STRIKE && calculatedValue < 0) calculatedValue = 0; // <-- IS THIS TRUE FOR ALL CLIENTS???
			if (userData.clients[client].shares[symbol]) {
				if (!HAS_STMTS || (showTVPI && showTVPI.checked))
					if (USE_STRIKE && symbol == 'P-A') {
						// Calculate potential value and vested value for P-A shares
						const today = Math.floor(Date.now() / 1000);
						const userShares = userData.clients[client].shares[symbol];
						const vestingShares = userShares?.vesting;
						const approvedShares = Number(userShares['approved']) || 0;
						const ownedShares = Number(userShares['owned']) || 0;
						const sharePrice = trades[trades.length - 1][1];

						// Default vested value to 0 if no approved shares
						if (approvedShares === 0) {
							vestedValue = 0;
						}

						if (vestingShares) {
							// Calculate potential value per-tranche (not using weighted average)
							// Each tranche may have a different strike price, so we need to:
							// 1. Calculate value for each tranche independently
							// 2. Floor underwater tranches at zero
							// 3. Sum across all tranches
							let perTranchePotentialValue = 0;
							let hasTrancheData = false;

							Object.keys(vestingShares).forEach((v) => {
								const tranche = vestingShares[v];
								const threshold = tranche.threshold?.replace(/[^.\d]/g, '');
								if (threshold) {
									const trancheStrike = Number(threshold);
									// Per-tranche value: floor at zero for underwater tranches
									const perShareValue = Math.max(0, sharePrice - trancheStrike);

									// Get total quantity for this tranche (sum of non-forfeited schedule + goals amounts)
									let trancheQty = 0;
									const schedule = Object.entries(tranche.schedule || {});
									if (schedule.length) {
										hasTrancheData = true;
										// Exclude forfeited entries from potential value
										trancheQty += schedule.reduce((acc, s) => {
											const entry = s[1];
											if (entry?.forfeited) return acc;
											return acc + Number(entry?.amount || 0);
										}, 0);
									}
									const goals = Object.entries(tranche.goals || {});
									if (goals.length) {
										hasTrancheData = true;
										// Exclude forfeited goals from potential value
										trancheQty += goals.reduce((acc, [_, g]) => {
											if (g?.forfeited) return acc;
											return acc + Number(g?.amount || 0);
										}, 0);
									}

									perTranchePotentialValue += perShareValue * trancheQty;

									// Also calculate vested value per-tranche (only if user has vested shares)
									if (approvedShares > 0) {
										// Floor at zero for underwater tranches (vested options worth $0, not negative)
										const thisValue = Math.max(0, sharePrice - trancheStrike);
										if (schedule.length) {
											if (vestedValue < 0) vestedValue = 0;
											// Check tranche-level vested flag (BW-style)
											// Back-compat: if no vested flag, check threshold presence
											const trancheHasVestedFlag = 'vested' in tranche;
											const isTrancheVested = trancheHasVestedFlag ? !!tranche.vested : true;
											vestedValue +=
												thisValue *
												schedule.reduce((acc, s) => {
													const entry = s[1];
													// Skip forfeited entries
													if (entry?.forfeited) return acc;
													let isVested;
													if (trancheHasVestedFlag) {
														isVested = isTrancheVested;
													} else if ('vested' in entry) {
														isVested = !!entry.vested;
													} else {
														isVested = Number(s[0]) <= today;
													}
													return isVested ? acc + Number(entry.amount) : acc;
												}, 0);
										}
										if (goals.length) {
											if (vestedValue < 0) vestedValue = 0;
											// Skip forfeited goals
											vestedValue +=
												thisValue *
												goals.reduce((acc, [_, g]) => {
													if (g?.forfeited) return acc;
													return g.vested ? acc + Number(g.amount) : acc;
												}, 0);
										}
									}
								}
							});

							// Use per-tranche calculation if we found valid tranche data
							// This ensures proper handling when tranches have different strike prices
							if (hasTrancheData) {
								calculatedValue = perTranchePotentialValue;
							} else {
								// Fallback: no tranche thresholds found, use weighted average calculation
								calculatedValue = Math.max(0, calculatedValue) * ownedShares;
							}
						} else {
							// No vesting data - use original weighted average calculation
							calculatedValue = Math.max(0, calculatedValue) * ownedShares;
						}
					}
				if (!isCER && !isTCA && !(USE_STRIKE && symbol == 'P-A')) calculatedValue *= Number(userData.clients[client].shares[symbol]['owned']);
				// Huh??? Shouldn't it be this?  NO !!!!!
				// calculatedValue += Number(userData.clients[client].shares[symbol]["owned"]);
				else if (isJMH) calculatedValue += tvpiDistributions;
			} else calculatedValue = 0;

			// HEADLINE ABOVE GRAPH -- DRAGONS GROWING HERE
			// Skip if template system already handled the headline. Async callers
			// (e.g. Google Charts → getTrades → buildValuationArray) hit this AFTER
			// updateHeadlineUnconditionally has already rendered the class headline
			// to graph elements. Clients that drive the headline via class_headline_template
			// only (e.g. Cerity — no portfolio headline_template) used to get clobbered
			// here because the guard checked only headline_template.
			if (
				clientData.language?.headline_template ||
				clientData.language?.class_headline_template ||
				clientData.equity?.[symbol]?.class_headline_template
			) {
				// Template system handled headline, skip legacy logic
			} else {
				let currentSymbol = document.getElementById('currentSymbol');
				let currentLabel = document.getElementById('currentLabel');
				let currentValue = document.getElementById('currentValue');
				currentLabel.innerHTML = 'Your Current '; // default

				// Check to see if these trades are percentage returns
				// console.log("last trade = " + trades[trades.length - 1][1]);
				// Try using midpoint instead of last
				// Oof.  This is getting difficult.  There is tension between 3rdmg (237-C) and activatecp (GP)
				// for 3rdmg, changed mid factors to 1.4 and 4
				let mid = trades[Math.floor(trades.length / 1.4)][1]; // (trades.length / 2)
				if (!isCER && !isJMS && UFL && mid > 3.5) {
					// changed from 4 to 3.5 for MH HC-IV	//trades[trades.length - 1][1] > 5) {
					if (usePct) calculatedValue /= 100;
					currentSymbol.innerHTML = 'Your Cumulative Return';
					currentLabel.innerHTML = '';
					document.getElementById('priceLabel').innerHTML = 'Cumulative return as '; //percent of ";
				} else {
					currentSymbol.innerHTML = ' Value';
				}

				if (isCER) document.getElementById('priceLabelReport').classList.add('hidden');

				// which one should OVERRIDE???
				if (EQUITY_LABELS.currentLabel) {
					currentLabel.innerHTML = EQUITY_LABELS.currentLabel;
				} else if (labelHeadline) {
					currentLabel.innerHTML = labelHeadline; //"Aggregate Value + Return";
					currentSymbol.innerHTML = '';
				}

				//if (labelValuation) {	//} && sumContributions > 0) {	<-- needed this for some weird situation.  Which one?  (Breaks other sites like conestoga and ONWP)
				if (labelValuation != 'Share Price' && sumDistributions > 0 && sumContributions > 0) {
					//} && !HAS_STMTS && !isORR && client != "prodoscapital") {
					calculatedValue = sumDistributions;
					//trades[trades.length - 1][1] = 100 * (sumDistributions / sumContributions);
					document.getElementById('lastTraded').innerHTML =
						labelValuation + ': ' + (sumContributions > 0 ? (100 * (sumDistributions / sumContributions)).toLocaleString('en-US', m2) : 'n.a.');
				} else {
					// This works for MaguireHayden, but fails for Conestoga.  So tricky!
					//document.getElementById('lastTraded').innerHTML = labelValuation + ": " + trades[trades.length - 1][1].toLocaleString('en-US', m2);
				}

				let valuePrefix = EQUITY_LABELS.shareAbbrev?.length > 0 && !isJMS ? ' (' + EQUITY_LABELS.shareAbbrev + ')' + csp : csp;

				if (isNaN(calculatedValue)) calculatedValue = 0;

				currentValue.innerHTML =
					valuePrefix + calculatedValue.toLocaleString('en-US', calculatedValue >= 1000 || Math.abs(calculatedValue) < 0.01 ? m0 : m2); // > 999999 ? m0 : m2);
				if (USE_STRIKE && vestedValue >= 0)
					currentValue.innerHTML +=
						'<span class="text-base text-secondary font-md">&emsp;(Vested Value: $ ' + vestedValue.toLocaleString('en-US', m0) + ')</span>';
				currentValue.classList.remove('text-lg'); // hack for Acropolis
			} // end else block for template headline check
		}
	}

	function getThresholdPrice(shareClass) {
		/* NOTES
		Helper function to extract threshold price for a given equity class
		Returns weighted average threshold price if user has multiple tranches
		*/
		let thresholdPrice = 0;
		if (!userData) AppState.userData = JSON.parse(sessionStorage.getItem('userData'));

		// Support consolidated view
		const thresholdUserIds = getActiveUserIds(userData);
		let transactions = clientData.equity[shareClass].transactions.filter((trans) => isUserTransaction(trans, thresholdUserIds));

		// Look for strike price in transactions
		transactions.forEach(function (t) {
			if (t.strikePrice) thresholdPrice = Number(t.strikePrice);
		});

		// Use weighted average strike price if multiple tranches exist
		let tranches = transactions.filter((t) => t.strikePrice && (!t.expDate || Number(t.expDate) > Date.now() / 1000));

		if (tranches.length > 1) {
			let aggTranches = 0;
			let ttlTranches = 0;
			tranches.forEach((t) => (aggTranches += Number(t.quantity) * Number(t.strikePrice)));
			tranches.forEach((t) => (ttlTranches += Number(t.quantity)));
			thresholdPrice = aggTranches / ttlTranches;
		}

		return thresholdPrice;
	}

	function buildAggregatePortfolioArray() {
		/* NOTES
		Builds multi-series data for portfolio graph showing user's equity performance
		Shows only equity classes the user owns (owned > 0)
		Returns object with class names and data matrix for Google Charts
		*/

		let classData = {}; // Store { className: { date: sharePrice } }
		let allDates = new Set(); // Collect all unique dates
		let classList = []; // Array of equity class names user owns

		// Get user's ownership data
		let userShares = userData.clients[client]?.shares || {};
		console.log('=== Building Portfolio Array ===');

		// Loop through equity classes, filtering to only those user owns
		Object.keys(clientData.equity)
			.filter((shareClass) => Number(userShares[shareClass]?.owned) > 0)
			.forEach(function (shareClass) {
				let equityClass = clientData.equity[shareClass];
				let valuations = equityClass.valuations;

				if (!valuations || valuations.length === 0) return;

				// Find user's first acquisition date for this class
				// Only show valuations from when they actually owned the shares
				let firstAcquisitionTimestamp = null;
				const userTransactions = getUserTransactionsForClass(equityClass, userData.userid);
				if (userTransactions.length > 0) {
					// Find earliest transaction where user received shares (positive quantity for them)
					const acquisitions = userTransactions
						.filter((t) => getSignedQuantityForUser(t, userData.userid) > 0)
						.map((t) => Number(t.date))
						.filter((d) => !isNaN(d) && d > 0);
					if (acquisitions.length > 0) {
						firstAcquisitionTimestamp = Math.min(...acquisitions);
						console.log(`  ${shareClass}: First acquisition on ${new Date(firstAcquisitionTimestamp * 1000).toDateString()}`);
					}
				}

				classList.push(shareClass);
				classData[shareClass] = {};

				// Process each valuation for this equity class
				valuations.forEach(function (v, idx) {
					// Parse timestamp and normalize to midnight UTC on that calendar date
					// This ensures the same calendar date always has the same timestamp
					let timestamp = Number(v.date.replace(/[^.\d]/g, ''));
					let momentDate = moment.unix(timestamp).utc().startOf('day');
					let date = momentDate.valueOf(); // Milliseconds at midnight UTC for this date

					// Skip valuations from before the user acquired this class
					if (firstAcquisitionTimestamp !== null && timestamp < firstAcquisitionTimestamp) {
						return; // Don't show values from before user owned shares
					}

					// Just use the share price - no threshold adjustment, no ownership multiplication
					// Add error handling for missing or invalid sharePrice
					let sharePrice = v.sharePrice ? Number(v.sharePrice.replace(/[^.\d]/g, '')) : NaN;
					if (isNaN(sharePrice)) {
						console.warn(`Invalid sharePrice for ${shareClass} on ${v.date}:`, v);
					}

					// Log first and last valuation for each class
					if (idx === 0 || idx === valuations.length - 1) {
						console.log(`  ${shareClass} valuation [${idx}]: date=${new Date(date).toDateString()}, price=${sharePrice}`);
					}

					// Store share price for this class at this date
					if (!isNaN(date) && !isNaN(sharePrice)) {
						classData[shareClass][date] = sharePrice;
						allDates.add(date);
					}
				});
			});

		// Convert to array format for Google Charts
		// Format: [[date, class1_value, class2_value, ...], ...]
		let sortedDates = Array.from(allDates).sort((a, b) => a - b);
		let chartArray = sortedDates
			.filter((date) => !isNaN(date) && date > 0) // Filter out invalid dates
			.map(function (date) {
				let row = [Number(date)];
				// Add value for each class (or null if no valuation on this date)
				classList.forEach(function (className) {
					let value = classData[className][date];
					row.push(value !== undefined ? value : null);
				});
				return row;
			});

		console.log('Final portfolio data:');
		console.log('  Classes:', classList);
		console.log('  Total dates:', chartArray.length);
		if (chartArray.length > 0) {
			console.log('  First row:', chartArray[0]);
			console.log('  Last row:', chartArray[chartArray.length - 1]);
		}

		return {
			data: chartArray,
			classes: classList,
		};
	}

	function buildValueCompositionTimeSeries() {
		/* NOTES
		Builds simple 2-bar comparison chart:
		Bar 1: Contributions (total money put in across all classes)
		Bar 2: Total Value = Capital Account (by class) + Distributions
		Capital Account = Contributions - Redemptions - Returns of Capital
		*/

		// Use ChartFormatter if calculation result is available
		if (window.lastCalculationResult) {
			const formatted = ChartFormatter.formatAsComparisonBars(window.lastCalculationResult);

			// ChartFormatter returns { data, labels, classes, classDetails, yAxisMin }
			// We need to capitalize the class names for display
			const seriesLabels = formatted.labels.map((label, index) => {
				// First is 'Contributions', rest are class names (no separate Distributions)
				if (index === 0) return label;
				return capitalize(label) || label;
			});

			// Store Y-axis minimum for chart rendering
			// Client can override with portfolio_y_axis_min flag, or use calculated baseline
			window.portfolioYAxisMin = clientData.flags?.portfolio_y_axis_min ?? formatted.yAxisMin ?? 0;

			return {
				data: formatted.data,
				classes: formatted.classes,
				labels: seriesLabels,
				classDetails: formatted.classDetails, // Include for tooltip breakdown
			};
		}

		// Fallback: Calculate manually if no calculation result (legacy path)

		// Calculate total contributions (gross amount put in)
		let totalContributions = 0;
		shareKeys.forEach(function (key) {
			totalContributions += contributions[key] || 0;
		});

		// Calculate current capital account value for each class
		// Capital Account = Contributions - Redemptions - RoC (stored in latestValue for isMH, bases otherwise)
		let classMarketValues = [];
		shareKeys.forEach(function (key) {
			let value = isSEQ ? Math.max(0, latestValue[key] || 0) : latestValue[key] || 0;
			classMarketValues.push(value);
		});

		// Calculate total distributions
		let totalDistributions = 0;
		shareKeys.forEach(function (key) {
			totalDistributions += distribs[key] || 0;
		});

		// Build 2 data rows: Contributions bar and Total Value bar
		// Row format: [label, contributions, class1_totalValue, class2_totalValue, ...]
		// Note: Distributions are included in each class's total value, not as a separate column
		let contributionsRow = ['Contributions', totalContributions];
		for (let i = 0; i < shareKeys.length; i++) {
			contributionsRow.push(0); // No class breakdown for contributions
		}

		// In legacy path, add distributions evenly across classes (rough approximation)
		// This is a fallback - the ChartFormatter path is preferred
		let totalValueRow = ['Total Value', 0]; // No contributions value
		let distPerClass = shareKeys.length > 0 ? totalDistributions / shareKeys.length : 0;
		classMarketValues.forEach((v) => totalValueRow.push(v + distPerClass));

		let chartData = [contributionsRow, totalValueRow];

		// Build labels for the stacks (no separate Distributions label)
		let seriesLabels = ['Contributions'];
		shareKeys.forEach((k) => seriesLabels.push(capitalize(k) || k));

		return {
			data: chartData,
			classes: shareKeys,
			labels: seriesLabels,
		};
	}

	function getOwnershipAtDate(shareClass, targetDate) {
		/* Calculate shares owned at a specific date by replaying transactions */
		let owned = 0;
		let transactions = clientData.equity[shareClass]?.transactions || [];
		let relevantTransactions = transactions.filter((t) => Number(t.date) <= targetDate);

		// Support consolidated view
		const ownershipUserIds = getActiveUserIds(userData);

		console.log(
			`[getOwnershipAtDate] Class: ${shareClass}, Target: ${targetDate}, Total txns: ${transactions.length}, Relevant txns: ${relevantTransactions.length}`
		);

		relevantTransactions.forEach((t) => {
			const receiverLower = t.receiver?.toLowerCase();
			const senderLower = t.sender?.toLowerCase();
			const isReceiver = receiverLower && ownershipUserIds.some((uid) => uid?.toLowerCase() === receiverLower);
			const isSender = senderLower && ownershipUserIds.some((uid) => uid?.toLowerCase() === senderLower);
			if (isReceiver) {
				owned += Number(t.quantity) || 0;
				console.log(`  -> Received ${t.quantity} shares on ${t.date}`);
			}
			if (isSender) {
				owned -= Number(t.quantity) || 0;
				console.log(`  -> Sent ${t.quantity} shares on ${t.date}`);
			}
		});

		console.log(`  -> Total owned: ${owned}`);
		return owned;
	}

	function getBasisAtDate(shareClass, targetDate) {
		/* Calculate cumulative paid-in capital (cost basis) at a specific date */
		let basis = 0;
		let transactions = clientData.equity[shareClass]?.transactions || [];
		let userShares = userData.clients[client]?.shares || {};
		let userEvents = userShares[shareClass]?.events || [];
		let clientEvents = clientData.equity[shareClass]?.events || [];

		// Support consolidated view
		const basisUserIds = getActiveUserIds(userData);

		// Process transactions chronologically
		transactions
			.filter((t) => Number(t.date) <= targetDate)
			.sort((a, b) => Number(a.date) - Number(b.date))
			.forEach((t) => {
				let dollarAmount = (Number(t.quantity) || 0) * (Number(t.sharePrice) || 0);

				const receiverLower = t.receiver?.toLowerCase();
				const senderLower = t.sender?.toLowerCase();
				const isReceiver = receiverLower && basisUserIds.some((uid) => uid?.toLowerCase() === receiverLower);
				const isSender = senderLower && basisUserIds.some((uid) => uid?.toLowerCase() === senderLower);

				if (isReceiver) {
					// User is buying - increase basis
					basis += dollarAmount;

					// Add strike price for options if applicable
					if (t.strikePrice && Number(t.strikePrice) > 0) {
						basis += (Number(t.quantity) || 0) * (Number(t.strikePrice) || 0);
					}
				}

				if (isSender) {
					// User is selling - reduce basis
					if (!t.strikePrice || Number(t.strikePrice) <= 0) {
						basis -= dollarAmount;
					}
				}
			});

		// Add contribution/redemption events
		userEvents.forEach((ue) => {
			let clientEvent = clientEvents.find((ce) => ce.nrid == ue.nrid);
			if (clientEvent && Number(clientEvent.date) <= targetDate) {
				let grossDollars = Number(ue.grossDollars) || 0;

				if (clientEvent.eventType == 'Contribution') {
					basis += grossDollars;
				} else if (clientEvent.eventType == 'Redemption' || clientEvent.eventType == 'Return of Capital') {
					basis -= grossDollars;
				}
			}
		});

		return Math.max(0, basis); // Don't return negative basis
	}

	function getDistributionsAtDate(shareClass, targetDate) {
		/* Calculate cumulative distributions received at a specific date */
		let distribs = 0;
		let userShares = userData.clients[client]?.shares || {};
		let userEvents = userShares[shareClass]?.events || [];
		let clientEvents = clientData.equity[shareClass]?.events || [];

		userEvents.forEach((ue) => {
			let clientEvent = clientEvents.find((ce) => ce.nrid == ue.nrid);
			if (clientEvent && Number(clientEvent.date) <= targetDate) {
				if (clientEvent.eventType == 'Distribution' || clientEvent.eventType == 'Cash Dividend') {
					distribs += Number(ue.grossDollars) || 0;
				}
			}
		});

		return distribs;
	}

	function getValuationAtDate(shareClass, targetDate) {
		/* Get the most recent valuation share price at or before target date */
		let valuations = clientData.equity[shareClass]?.valuations || [];

		console.log(`[getValuationAtDate] Class: ${shareClass}, Target: ${targetDate}, Total valuations: ${valuations.length}`);

		// Find the most recent valuation before or at target date
		let relevantValuations = valuations.filter((v) => Number(v.date) <= targetDate).sort((a, b) => Number(b.date) - Number(a.date));

		console.log(`  -> Relevant valuations: ${relevantValuations.length}`);

		if (relevantValuations.length > 0) {
			let price = Number(relevantValuations[0].sharePrice) || 0;
			console.log(`  -> Using price: ${price} from ${relevantValuations[0].date}`);
			return price;
		}

		// If no valuation found, return 0
		return 0;
	}

	function renderLanding(landingPage, valuations) {
		/* NOTES
		This method sets layout properties for the center panel
		It also calculate total values for the headline
		And it renders the center headline (tricky...)
		*/
		// Four ways to get here:
		// - few trades, no valuations, and no events (getTrades)
		// - same as above, but after checking Ethereum for trades
		// - prefer graph in equity class setting (getTrades)
		// - tried to draw graph, but data are meaningless (buildValuationArray)

		let landingPageHref = document.getElementById('landingPageHref');
		if (client == 'apriori') {
			landingPageHref.innerHTML = `
				<img id="landingPageImage" src="" class="responsive-img" style="width: 100%; height: 22rem; object-fit: cover; cursor: default;">
			`;
		}

		// SET PROPERTIES OF CENTER PANEL (IMAGE, VIDEO)
		let fixImage = clientData.flags && clientData.flags.fix_landing_images;
		narrow = screen.width < 600;
		if (narrow) fixImage = true;
		let landingPageImage = document.getElementById('landingPageImage');
		let landingPageCaption = document.getElementById('landingPageCaption');
		// console.log(clientData)
		let hasLandingImage = landingPage?.image?.length > 5;
		if (hasLandingImage || clientData.product_image?.length > 5) {
			overviewLandingPage.style.display = 'block';
			if (landingPageImage) {
				// vs. product_video
				let originalImageSource = hasLandingImage && currentShareClass >= 0 ? landingPage.image : clientData.product_image;
				// accencio was using an animated gif, but not any more as best I can tell
				if (isACC) landingPageImage.src = originalImageSource;
				else {
					landingPageImage.src = formatImg(originalImageSource, '/w_826,q_auto,f_auto/');
					landingPageImage.src = formatImg(originalImageSource, 'q_auto,f_auto/');
				}
				if (fixImage) {
					landingPageImage.style.height = '100%';
					landingPageImage.style.objectFit = null;
				}
				// landingPageImage.src = formatImg(landingPageImage.src, '/fit_826x396/');
				// landingPageImage.src = formatImg(landingPageImage.src, '/w_826,h_396,q_auto,f_auto/');
				landingPageImage.style.opacity = 0;
				unfade(landingPageImage);
				if (isCER && symbol == 'CCA' && clientData.flags?.enable_footnote) addFootnoteToClassImage(resolveAbout());
				else addFootnoteToClassImage(null);
			}
			landingPageCaption.innerHTML = hasLandingImage && currentShareClass >= 0 ? landingPage.caption : '';
			if (clientData.product_video?.length > 5 && (client != 'apriori' || currentShareClass == 0)) {
				let preview = clientData.product_video + '.png';
				landingPageHref.innerHTML = `
					<video width="100%" height="100%" preload="metadata" poster="${preview}" controls>
						<source src="${clientData.product_video}" width="100%" height="100%"></source>
					</video>
				`;
			} else if (!fixImage) {
				let landingDivStyle = document.getElementById('landingDiv').style;
				// landingDivStyle.height = "520px";
				landingDivStyle.minHeight = '520px';
				landingDivStyle.maxHeight = '800px';
			}
			// Some clients need a dynamic div because of long, variable-length captions
			if (isGG || isNMS) {
				let landingDiv = document.getElementById('landingDiv');
				if (landingDiv) landingDiv.classList.remove('landing');
			}
			//document.getElementById('landingPageDescription').innerHTML = hasLandingImage ? landingPage.description : clientData.about;
		} else {
			overviewLandingPage.style.display = 'none';
		}

		// If currently raising, clicking center image redirects to campaign??
		landingPageHref.target = '_self';
		if (clientData.product_video) landingPageHref.href = '#';
		else if (clientData.onclick_url) {
			landingPageHref.href = clientData.onclick_url;
			landingPageHref.target = '_blank';
		} else if (FUNDING) landingPageHref.href = '/campaign/';
		else landingPageHref.href = '#'; //"/reports/";

		// netValue is a module-level side effect consumed downstream by
		// drawStackedAreaChart and the per-class portfolio markup. Defined as
		// (headline's totalCurrentValue) - totalPaidInCapital — distinct from
		// calcResult.netValue for fund clients (which would add distributions).
		// Pre-#5198 Phase 5 this path re-summed bases/latestValue with
		// isSEQ/isUA/isAES/isJMH carve-outs (all dead post-Phase 3).
		const calcForChart = window.lastCalculationResult;
		netValue = (calcForChart?.totalCapitalAccount || calcForChart?.totalValue || 0) - (calcForChart?.totalPaidInCapital || 0);

		// HEADLINE ABOVE IMAGE     !!! HERE LIE DEMONS AND DRAGONS !!!
		// This is quite tricky. We may end up needing feature flags, but for now, we'll keep trying to use math.

		let valueHeading = document.getElementById('valueHeading');
		let valueLabel = document.getElementById('valueLabel');
		let valueAmount = document.getElementById('valueAmount');
		let valueDate = document.getElementById('valueDate');
		let valueTertiary = document.getElementById('valueTertiary');
		let headlineHandled = false;

		// Skip headline updates if already handled by template system (prevents async overwrite)
		if (clientData.language?.headline_template) {
			headlineHandled = true; // Mark as handled so remaining CASE logic is skipped
		}

		// CASE 0: Use HeadlineService for clients with templates configured
		// This provides template-based headlines with fallback to legacy logic
		const hasHeadlineTemplate = clientData.language?.headline_template;
		const hasClassHeadlineTemplate = clientData.language?.class_headline_template;
		const isPortfolioView = currentShareClass === -1;
		const isSingleClassView = currentShareClass >= 0;
		const currentClassKey = isSingleClassView ? shareKeys[currentShareClass] : null;

		// Use HeadlineService for clients with templates configured.
		// Single render entry point (#5198 Phase 7a) — dispatches class vs
		// portfolio internally based on viewMode.
		if (hasHeadlineTemplate || hasClassHeadlineTemplate) {
			const currentClassValuationDate = valuations?.[0]?.date ? moment.unix(valuations[0].date).format('M/D/YYYY') : '';
			const calcResult = window.lastCalculationResult;

			const elements = { label: valueLabel, amount: valueAmount, subtitle: valueDate, tertiary: valueTertiary };

			const renderCtx = {
				viewMode: isSingleClassView ? 'class' : 'portfolio',
				shareKeys,
				userShares: userData.clients?.[client]?.shares,
				bases,
				latestValue,
				distribs,
				contributions,
				returnsOfCapital,
				labelPaidIn,
				itsaFund: UFL,
				valuationDate: latestValuationDate,
				currentClassValuationDate,
				stockStatus: userData.clients?.[client]?.status,
				userData,
				clientData,
				client,
			};

			if (isSingleClassView) {
				renderCtx.classKey = currentClassKey;
				renderCtx.classData = calcResult?.breakdown?.find((c) => c.classKey === currentClassKey);
				renderCtx.equityConfig = clientData.equity?.[currentClassKey] || {};
			}

			const result = HeadlineService.renderHeadline(elements, calcResult, renderCtx);
			headlineHandled = result.handled;
		}

		// Fallback: hide headline if template system did not handle it
		if (!headlineHandled) hideHeading();

		if (SHOW_STATS) {
			buildEventsAndTransactionsModal();
			let userStatsCard = document.getElementById('userStatsCard');
			if (userStatsCard) {
				userStatsCard.outerHTML = '';
				addedStatsHeadlineAlready = false;
			}
			let userEquityTotalsTable = document.getElementById('userEquityTotalsTable');
			if (userEquityTotalsTable) {
				let userEquityTotals = userEquityTotalsTable.cloneNode(true);
				let userStatsCards = userEquityTotals.querySelectorAll('.stats-card');
				if (userStatsCards && userStatsCards.length) {
					userStatsCard = userStatsCards[0];
					userStatsCard.id = 'userStatsCard';
					userStatsCard.style.cssText = `
						display:flex; flex-direction: row; /* This ensures the items are laid out in a row */
						justify-content: space-between; /* This adds space between the items */
						border-style: none; border-collapse: collapse; max-width: 100% !important; border-radius: 10px !important; background-color: #f0f4f8 !important;
					`;

					let elements = userEquityTotals.querySelectorAll('.text-2xl');
					elements.forEach((element) => {
						element.classList.remove('text-2xl');
						element.classList.add('text-sm');
					});
					elements = userEquityTotals.querySelectorAll('.pdf-text-secondary');
					elements.forEach((element) => {
						element.classList.remove('pdf-text-secondary');
						element.classList.add('text-xs');
					});
					elements = userEquityTotals.querySelectorAll('.p-4');
					elements.forEach((element) => {
						element.classList.remove('p-4');
						element.classList.add('p-0');
					});
					elements = userEquityTotals.querySelectorAll('.stat-item-3');
					elements[0].style.borderLeft = 'none';
					elements.forEach((element) => {
						element.classList.remove('md-border-r-1');
						element.classList.add('p-05');
					});
					elements = userEquityTotals.querySelectorAll('dl');
					elements.forEach((element) => {
						element.classList.add('mt-0', 'mb-0');
					});
					valueHeading.classList.remove('mb-4');
					valueHeading.classList.add('mb-2');
					userStatsCard.style.width = '90%';
					userStatsCard.classList.add('mb-6');

					if (!addedStatsHeadlineAlready) {
						addedStatsHeadlineAlready = true;
						valueHeading.classList.add('ml-4');
						valueHeading.outerHTML = valueHeading.outerHTML + userStatsCard.outerHTML;
					}
				}
			} else {
				addedStatsHeadlineAlready = false;
			}
		}

		function hideHeading() {
			valueLabel.innerHTML = '&nbsp;'; //"Your home for " + clientData.company_name;	//"No current balance";
			valueAmount.innerHTML = '&nbsp;';
			valueDate.innerHTML = '&nbsp;';
		}

		function fade(element) {
			let op = 1; // initial opacity
			const timer = setInterval(function () {
				if (op <= 0.1) {
					clearInterval(timer);
					element.style.display = 'none';
				}
				element.style.opacity = op;
				element.style.filter = 'alpha(opacity=' + op * 100 + ')';
				op -= op * 0.04;
			}, 50);
		}

		function unfade(element) {
			let op = 0.1; // initial opacity
			element.style.display = 'block';
			const timer = setInterval(function () {
				if (op >= 1) {
					clearInterval(timer);
				}
				element.style.opacity = op;
				element.style.filter = 'alpha(opacity=' + op * 100 + ')';
				op += op * (freshSignIn ? 0.03 : 0.04);
			}, 10);
		}
	}

	function toggleVesting(target, issue, type) {
		let id = issue.replace(/\s+/g, '_');
		let selector = id;
		let elems = document.getElementsByClassName(selector);

		Array.from(elems).forEach(function (elem) {
			if (elem.classList.contains(type)) {
				if (!elem.classList.contains('hidden')) {
					target.innerHTML = 'Show';
					elem.classList.add('hidden');
				} else {
					target.innerHTML = 'Hide';
					elem.classList.remove('hidden');
				}
			}
		});
		if (window.analytics)
			window.analytics.track('Viewed Vesting Schedule', {
				userEmail: authedUser,
				workspace: client,
				newUser: currentClass,
			});
	}

	function sortVestingBySchedule(sharesObj) {
		let grantsWithSchedule = [];
		let grantsWithoutSchedule = [];

		Object.entries(sharesObj.vesting).forEach(([grantName, grantData]) => {
			const sched = grantData?.schedule;

			if (sched && Object.keys(sched).length > 0) {
				// ✅ only numeric keys count for "earliestTimestamp"
				const ts = Object.keys(sched)
					.map((k) => Number(k))
					.filter((n) => Number.isFinite(n));

				if (ts.length) {
					const earliestTimestamp = Math.min(...ts);
					grantsWithSchedule.push({ grantName, earliestTimestamp, grantData });
				} else {
					// schedule exists but has no numeric dates (e.g., FORFEITED-only marker rows)
					grantsWithoutSchedule.push({ grantName, grantData });
				}
			} else {
				grantsWithoutSchedule.push({ grantName, grantData });
			}
		});

		// Sort grants with schedules by newest first
		grantsWithSchedule.sort((a, b) => b.earliestTimestamp - a.earliestTimestamp);

		// Sort grants without schedules in reverse alphabetical order
		grantsWithoutSchedule.sort((a, b) => b.grantName.localeCompare(a.grantName));

		let sortedVesting = [...grantsWithSchedule, ...grantsWithoutSchedule].reduce((acc, { grantName, grantData }) => {
			acc[grantName] = grantData;
			return acc;
		}, {});

		return { ...sharesObj, vesting: sortedVesting };
	}

	function renderVestingSchedule(origVestingData, key) {
		let program = {};
		let schedules = {};
		// NOTE BENE: to maintain separate tranches, we need a new object here, not just a pointer
		let vestingData = JSON.parse(JSON.stringify(origVestingData));

		Object.keys(vestingData).map((issue, index) => {
			// console.log(vestingData[issue].schedule)
			let schedule = [];
			let totalShares = 0;
			let totalVested = 0;
			if (vestingData[issue] !== null) {
				if (vestingData[issue].schedule) {
					// for JMS (and others??) combine adjacent items
					if (isJMS) {
						let tranches = vestingData[issue].schedule;
						// let times = Object.keys(tranches);
						let times = Object.keys(tranches)
							.map((k) => Number(k))
							.filter((n) => Number.isFinite(n))
							.sort((a, b) => a - b);
						times.sort();
						let i = 0;
						let j = 1;
						while (i < times.length - 1) {
							if (times[i + j] - times[i] < 1000) {
								tranches[times[i]].amount = (Number(tranches[times[i]].amount) + Number(tranches[times[i + j]].amount)).toString();
								delete tranches[times[i + j]];
								console.log('combined tranche ' + times[i + j]);
								j++;
							} else {
								i += j;
								j = 1;
							}
						}
						vestingData[issue].schedule = tranches;
					}

					Object.keys(vestingData[issue].schedule).map((i, index) => {
						const rawKey = i;
						const ts = Number(rawKey);
						const hasTs = Number.isFinite(ts);
						const entry = vestingData[issue].schedule[i] || {};
						const forcedVested = !!entry.vested || !!entry.posted;
						const isForfeited = !!entry.forfeited;

						// UI styling: forfeited always “past looking”; otherwise based on time or forced vest
						const isPastForUI = isForfeited ? true : !hasTs ? true : moment(ts * 1000).isBefore(moment()) || forcedVested;

						// Totals: a tranche counts toward totals ONLY if it has a timestamp and is NOT forfeited
						const amountNum = Number(entry.amount) || 0;
						const countsInTotals = hasTs && amountNum !== 0;
						if (countsInTotals) {
							// Denominator includes forfeited
							totalShares += amountNum;
							// Numerator excludes forfeited
							const isVestedForTotals = !isForfeited && (moment(ts * 1000).isBefore(moment()) || forcedVested);
							if (isVestedForTotals) totalVested += amountNum;
						}

						schedule.push({
							past: isPastForUI,
							forfeited: isForfeited,
							key: rawKey,
							ts: hasTs ? ts : null,
							amount: entry.amount,
							note: entry.note || '',
						});
					});

					// console.log(schedule, totalShares, totalVested)
					schedules[issue] = {
						issue: issue,
						totalShares: totalShares,
						totalVested: totalVested,
						cashedOut: vestingData[issue].cashedOut,
						percentVested: totalShares > 0 ? (totalVested / totalShares) * 100 : 0,
						schedule: schedule,
					};
					if (vestingData[issue].threshold) schedules[issue].threshold = vestingData[issue].threshold;
					if (vestingData[issue].conditions) schedules[issue].conditions = vestingData[issue].conditions;
				}

				if (vestingData[issue].goals) {
					totalShares = 0;
					program[issue] = {
						goals: vestingData[issue].goals,
					};
					Object.keys(program[issue].goals).forEach((g) => (totalShares += Number(vestingData[issue].goals[g].amount)));
					if (totalShares > 0) {
						program[issue].totalShares = totalShares;
						program[issue].totalVested = Object.values(vestingData[issue].goals).reduce((acc, g) => acc + (g.vested ? parseFloat(g.amount) : 0), 0);
						program[issue].percentVested = (100 * program[issue].totalVested) / totalShares;
					}
				}
			}
		});

		let vestingKeys = Object.keys(vestingData);
		// Preserve any legacy overrides
		if (isH5H) vestingKeys.sort();
		else if (isBW) {
			const isFYKey = (k) => /^FY\s*\d{2,4}\b/i.test(String(k || '').trim());
			vestingKeys.sort((a, b) => {
				const aIsFY = isFYKey(a);
				const bIsFY = isFYKey(b);
				// FY tranches first
				if (aIsFY && !bIsFY) return -1;
				if (!aIsFY && bIsFY) return 1;
				// Both FY → newest first (FY25 → FY24 → FY23)
				if (aIsFY && bIsFY) {
					const ay = Number(String(a).match(/FY\s*(\d{2,4})/i)?.[1]) || 0;
					const by = Number(String(b).match(/FY\s*(\d{2,4})/i)?.[1]) || 0;
					return by - ay;
				}
				// Both non-FY → reverse alpha (existing feel)
				return String(b).localeCompare(String(a));
			});
		}

		let stringOptions = isAlera ? '' : isBW ? m4 : UFL && !isCER ? usd : m5;
		let equityKeyData = clientData.equity[key];
		if (equityKeyData) {
			if (equityKeyData.sharesType) sharesType = equityKeyData.sharesType.toLowerCase();
			if (equityKeyData.vestingType) vestingType = equityKeyData.vestingType.toLowerCase();
			else if (equityKeyData.sharesType) vestingType = equityKeyData.sharesType.toLowerCase();
		}
		let vestingMarkup = `
			${UFL || !vestingKeys.length ? `` : `<div class="pt-2 font-bold mb-0 border-t-1">Vesting Schedule${vestingKeys.length > 1 ? 's' : ''}</div>`}
			${vestingKeys
				.map(
					(issue, index) => `
				<div>
					<div class="mt-2 ${isJMS && schedules[issue]['totalVested'] == schedules[issue]['totalShares'] ? '' : 'border-t-1'} mb-0 flex items-center justify-between">
						<h5 class="text-sm font-bold mb-0 mt-2">${issue}${schedules[issue] && schedules[issue].threshold ? ' <span class="text-secondary font-md">(' + schedules[issue].threshold + ')</span>' : ''}</h5>
					</div>
					<p class="text-xs mt-1">${schedules[issue] && schedules[issue].conditions ? (isBW ? '' : 'Conditions: ') + schedules[issue].conditions : ''}</p>
					
					${
						program[issue]
							? `
						<p class="text-sm mt-2 mb-0${schedules[issue] ? '' : ' hidden'}">Goal Based:</p>
						<div class="flex items-center justify-between">
							<p class="text-secondary text-sm mt-2 mb-2">${
								program[issue]['totalVested'] === program[issue]['totalShares']
									? `${Number(program[issue]['totalVested']).toLocaleString('en-US', stringOptions)}&ensp;${isMH || isCER ? 'vested' : UFL ? 'paid' : vestingType.replace(/original \(target\) /i, '') + ' vested'}`
									: `${Number(program[issue]['totalVested']).toLocaleString('en-US', stringOptions)}&ensp;of&ensp;${Number(program[issue]['totalShares']).toLocaleString('en-US', stringOptions)}&ensp;${isMH || isCER ? 'vested' : UFL ? 'paid' : vestingType + ' vested'}`
							}</p>
							<p class="text-xs mt-2 mb-2"><a href="#" class="goals-toggle" data-issue="${key}|${issue}">Show</a></p>
						</div>
						<div class="mb-2">
							<div class="vesting-progress">
								<div class="vesting-progress-bar" style="width: ${program[issue]['percentVested']}%;"></div>
							</div>
						</div>
						<div class="mb-0 ${key}|${issue.replace(/\s+/g, '_')} vesting-goals hidden">
							${Object.keys(program[issue].goals)
								.sort()
								.map((goal) => {
									const g = program[issue].goals[goal] || {};
									const leftLabel = g.forfeited ? 'FORFEITED' : g.vested ? '✔' : '☐';
									return `
									<p class="text-secondary text-sm lh-06 mt-3 mb-0">
										${leftLabel}&ensp;${Number(g.amount).toLocaleString() + ' ' + vestingType}
									</p>
									<p class="text-xs lh-11 mt-2 mb-4">${goal}</p>
								`;
								})
								.join('')}
						</div>
					`
							: ``
					}

					${
						schedules[issue]
							? `
						<p class="text-sm mt-2 mb-0${program[issue] ? '' : ' hidden'}">Time Based:</p>
						<div class="flex items-center justify-between">
							<p class="text-secondary text-sm mt-1 mb-1">${
								schedules[issue]['totalVested'] === schedules[issue]['totalShares']
									? `${Number(schedules[issue]['totalVested']).toLocaleString('en-US', stringOptions)}&ensp;${schedules[issue].cashedOut ? sharesType + ' cashed out' : isMH || isCER ? 'vested' : UFL ? 'paid' : vestingType.replace(/original \(target\) /i, '') + ' vested'}`
									: `${Number(schedules[issue]['totalVested']).toLocaleString('en-US', stringOptions)}&ensp;of&ensp;${Number(schedules[issue]['totalShares']).toLocaleString('en-US', stringOptions)}&ensp;${isMH || isCER ? 'vested' : UFL ? 'paid' : vestingType + ' vested'}`
							}</p>
							<p class="text-xs mt-1 mb-1"><a href="#" class="time-toggle" data-issue="${key}|${issue}">Show</a></p>
						</div>
						<div class="border-b-1 mb-1">
							<div class="vesting-progress">
								<div class="vesting-progress-bar" style="width: ${schedules[issue]['percentVested']}%;"></div>
							</div>
						</div>
						<div class="${key.replace(/\s+/g, '_')}|${issue.replace(/\s+/g, '_')} vesting-schedule hidden">
							${schedules[issue]['schedule']
								.map(
									(item, index) => `
								<div class="border-b-1 py-2">
									<span class="text-sm ${item.past ? 'text-secondary' : ''}">
										${item.forfeited ? 'FORFEITED&ensp;' : ''}
										${item.ts !== null ? moment.unix(item.ts).utc().format('MM/DD/YYYY') : '—'}
									</span>&ensp;
									<span class="text-extra-light">-</span>&ensp;
									<span class="text-sm ${item.past ? 'text-secondary' : ''}">
										${Number(item.amount).toLocaleString('en-US', stringOptions)}
										${UFL ? '' : '&ensp;' + vestingType}
									</span>
									${item.note ? `<span class="text-xs right mt-1 ${item.past ? 'text-secondary' : ''}">${item.note}</span>` : ``}
								</div>
							`
								)
								.join('')}
						</div>
					`
							: ``
					}
				</div>
			`
				)
				.join('')}
		`;

		return vestingMarkup;
	}

	/**
	 * Render consolidated vesting schedules grouped by account holder
	 * @param {Object} vestingByHolder - Object with userids as keys, each containing shareClasses with vesting data
	 * @param {string} shareClass - The share class to render vesting for
	 * @returns {string} HTML markup for consolidated vesting display
	 */
	function renderConsolidatedVesting(vestingByHolder, shareClass) {
		if (!vestingByHolder || !Object.keys(vestingByHolder).length) return '';

		let consolidatedMarkup = `<div class="pt-2 font-bold mb-0 border-t-1">Vesting Schedules by Account</div>`;

		Object.keys(vestingByHolder).forEach((holderId) => {
			const holderData = vestingByHolder[holderId];
			const holderName = holderData.holderName || holderId;
			const holderVesting = holderData.shareClasses?.[shareClass];

			if (holderVesting) {
				// Render the vesting schedule for this holder
				const vestingHtml = renderVestingSchedule(holderVesting, shareClass);
				if (vestingHtml) {
					consolidatedMarkup += `
						<div class="mt-3 mb-2">
							<div class="text-sm font-bold text-secondary border-b-1 pb-1">${holderName}</div>
							${vestingHtml}
						</div>
					`;
				}
			}
		});

		return consolidatedMarkup;
	}

	// ============================================================
	// HEADLINE UPDATE (runs unconditionally, regardless of graph/landing)
	// This ensures headlines update when switching between classes.
	// ============================================================
	(function updateHeadlineUnconditionally() {
		const valueLabel = document.getElementById('valueLabel');
		const valueAmount = document.getElementById('valueAmount');
		const valueDate = document.getElementById('valueDate');
		const valueTertiary = document.getElementById('valueTertiary');

		if (!valueLabel || !valueAmount || !valueDate) return;

		const hasHeadlineTemplate = clientData.language?.headline_template;
		// Include per-class templates (clientData.equity[X].class_headline_template) so this gate
		// matches the legacy-skip gate in the per-class graph headline path. Without this, clients
		// that only configure per-class templates would skip the legacy fallback AND never run
		// the template system, leaving the headline as the HTML default "--".
		const hasClassHeadlineTemplate =
			clientData.language?.class_headline_template || Object.values(clientData.equity || {}).some((e) => e && e.class_headline_template);
		const isPortfolioView = currentShareClass === -1;
		const isSingleClassView = currentShareClass >= 0;
		const currentClassKey = isSingleClassView ? shareKeys[currentShareClass] : null;

		// Run for clients with templates configured (works with or without calculator)
		if (hasHeadlineTemplate || hasClassHeadlineTemplate) {
			const currentClassValuationDate = valuations?.[0]?.date ? moment.unix(valuations[0].date).format('M/D/YYYY') : '';
			const calcResult2 = window.lastCalculationResult;

			const elements = { label: valueLabel, amount: valueAmount, subtitle: valueDate, tertiary: valueTertiary };

			const renderCtx = {
				viewMode: isSingleClassView ? 'class' : 'portfolio',
				shareKeys,
				userShares: userData.clients[client]?.shares,
				bases,
				latestValue,
				distribs,
				contributions,
				returnsOfCapital,
				labelPaidIn,
				itsaFund: UFL,
				valuationDate: latestValuationDate,
				currentClassValuationDate,
				stockStatus: userData.clients?.[client]?.status,
				userData,
				clientData,
				client,
			};

			if (isSingleClassView) {
				renderCtx.classKey = currentClassKey;
				renderCtx.classData = calcResult2?.breakdown?.find((c) => c.classKey === currentClassKey);
				renderCtx.equityConfig = clientData.equity?.[currentClassKey] || {};
			}

			const result = HeadlineService.renderHeadline(elements, calcResult2, renderCtx);

			if (result.handled && isSingleClassView && result.headline) {
				// Class view also pushes to graph elements
				HeadlineService.renderToGraphElements(result.headline);
			}
		}
	})();

	// Class names and symbols for cards
	let equityNames = [];
	let equityTags = [];
	shareKeys.map((key, index) => {
		if (clientData.equity[key]) {
			equityNames[key] = clientData.equity[key].name;
			if (clientData.equity[key].tags) {
				let values = [];
				clientData.equity[key].tags.forEach(function (tag) {
					values.push(tag.value);
				});
				equityTags[key] = values;
			}
		}
	});

	// Vesting dates (primarily for JM Search, but need vesting too!)
	let vesting = 0;
	let sched = [];
	let nextDate = [];
	let nextPayment = [];
	// Wait 24 days before shifting to next period
	let now = Date.now() / 1000 - 24 * 24 * 3600;

	// TO MODEL OTHER PERIODS
	// now = moment().add(-0.2, 'Y').unix();

	shareKeys.forEach(function (key) {
		if (userDataShares[key].vesting) {
			vesting++;
			if (userDataShares[key].vesting['Payables']) {
				sched[key] = userDataShares[key].vesting['Payables'].schedule;
			} else if (userDataShares[key].vesting['Receivables']) {
				sched[key] = userDataShares[key].vesting['Receivables'].schedule;
			}
			if (sched[key]) {
				// for JMS (and others??) combine adjacent items
				if (isJMS) {
					let tranches = sched[key];
					let times = Object.keys(tranches);
					times.sort();
					let i = 0;
					let j = 1;
					while (i < times.length - 1) {
						if (times[i + j] - times[i] < 1000) {
							tranches[times[i]].amount = (Number(tranches[times[i]].amount) + Number(tranches[times[i + j]].amount)).toString();
							delete tranches[times[i + j]];
							// console.log("combined tranche " + times[i + j]);
							j++;
						} else {
							i += j;
							j = 1;
						}
					}
					sched[key] = tranches;
				}

				let thisDate = Object.keys(sched[key])
					.filter((d) => d > now)
					.sort()[0];
				if (thisDate) {
					nextPayment[key] = Number(sched[key][thisDate].amount);
					nextDate[key] = moment(dateTimeUTC(Number(thisDate) * 1000)).format('MMMM D');
				} else {
					nextPayment[key] = 0;
					nextDate[key] = '';
				}
			}
		}
	});

	// CLASS CARDS     !!! HERE LIE DEMONS AND DRAGONS !!!
	// TODO: Handle the case where someone doesn't have any shares yet.
	// document.getElementById('currentValue').innerHTML = userData.shareholderData.shares[0] * tradeData[tradeData.length - 1][1].toFixed(2);
	// document.getElementById('lastTraded').innerHTML = tradeData[tradeData.length - 1][1].toLocaleString('en-US', usd);

	// Massive HACK (not needed anymore with payable schedule correctly truncated)
	// if (isJMS && userData.userid == "paflint@gmail.com") shareKeys = shareKeys.filter(k => k != "NP");

	let shareClassMarkup = '';
	if (!userDataShares || !shareKeys.length) {
		console.log('No shares added, but shares object exists');

		// Render an empty state in the My Equity section
		let emptyStateWords = 'Your investment, or participation amount, will appear here.';
		if (userData.clients[client].alias?.length > 0)
			emptyStateWords =
				'No holdings here. To look elsewhere, click&ensp;<a href="#workspaceModal" class="modal-trigger text-sm link-accent">Switch Account</a>.';
		shareClassMarkup = `<div class="bg-grey-lightest rounded border-grey grey-text center-align p-2">${emptyStateWords}</div>`;

		document.getElementById('aggregatePortfolioLarge').innerHTML = '';
		document.getElementById('aggregatePortfolioSmall').innerHTML = '';
	} else {
		let c1 = "class='text-secondary'";
		let c3 = "class='text-secondary text-xs mt-2'";
		let c4 = "class='text-secondary text-xs mt-2 mb-2'";
		if (isFish) c4 = "class='text-secondary text-xs mt-0 mb-2'"; // <-- CFM 11/4/2025 should this be universal??
		let s1 = '&#160;';
		let s2 = '&#160;&#160;';
		let s3 = '&#36;&#160;';
		let s4 = '&#160;&#160; $&#160;';
		let cD = clientData;
		let uD = userDataShares;
		let lP = labelPaidIn;
		let lV = latestValue;
		let pC = isFAV ? m0 : m5; // new precision variable as of 1/27/2023

		// Support consolidated view
		const cardUserIds = getActiveUserIds(userData);

		let interest = {};
		Object.keys(uD).forEach(function (s) {
			let tranches = cD.equity[s]?.transactions
				? cD.equity[s].transactions.filter((t) => {
						const receiverLower = t.receiver?.toLowerCase();
						const isReceiver = receiverLower && cardUserIds.some((uid) => uid?.toLowerCase() === receiverLower);
						return isReceiver && t.expDate && t.expDate != 'NaN';
					})
				: [];
			if (tranches.length == 1) uD[s].expDate = moment.unix(tranches[0].expDate).format('l');
			else if (tranches.length > 1) uD[s].tranches = tranches.length;

			// add debt interest to principal
			if (isACC) {
				interest[s] = 0;
				let userEvents = uD[s].events;
				let clientEvents = cD.equity[s].events;
				if (clientEvents) clientEvents = clientEvents.filter((e) => ['Interest Accrual', 'Interest Payment'].includes(e.eventType));
				if (userEvents && clientEvents) {
					clientEvents.forEach((c) => {
						let event = userEvents.find((u) => u.nrid == c.nrid);
						if (event) interest[s] += Number(event.grossDollars);
					});
				}
			}

			if (uD[s].vesting) uD[s] = sortVestingBySchedule(uD[s]);
		});

		const HIDE_JMS_DETAILS = clientData.flags?.hideDetails;

		// Calculate class aggregates for template variables (ownershipPercent, totalClassShares)
		// Priority: 1) Per-class config (equity[key].totalShares), 2) Client metrics, 3) Cap table calculation
		const classAggregates = {};
		shareKeys.forEach((key) => {
			const classConfig = cD.equity[key] || {};
			let totalOwned = null;

			// Check per-class config first
			if (classConfig.totalShares !== undefined) {
				totalOwned = Number(classConfig.totalShares);
			}
			// Fall back to client-level metrics (like Cerity's total_units)
			else if (cD.metrics?.total_units !== undefined) {
				totalOwned = Number(cD.metrics.total_units);
			}
			// Calculate from cap table if available (admin mode only)
			else if (typeof capTableData !== 'undefined' && capTableData && Array.isArray(capTableData)) {
				totalOwned = 0;
				capTableData.forEach((holder) => {
					const holderShares = holder?.clients?.[client]?.shares?.[key];
					if (holderShares?.owned) {
						totalOwned += Number(holderShares.owned) || 0;
					}
				});
			}

			if (totalOwned !== null && totalOwned > 0) {
				classAggregates[key] = { totalOwned };
			}
		});

		let userInv = userData.clients[client].investor || {};
		let canTrade = !BUYERS_ONLY || userInv.election == 'Buy';
		let tradeAction = isFish ? 'purchase' : isAlera ? (userInv.election == 'Buy' ? 'buy' : 'sell') : 'trade';

		shareClassMarkup = `${shareKeys
			.map(
				(key, index) =>
					`
			<div class="share-card mb-4" data-class="${capitalize(key)}">
				<div class="flex items-center justify-between">
					<h5 class="text-lg font-bold mt-0 mb-0">${capitalize(key)}</h5>
					${capitalize(key) !== shareKeys[currentShareClass] ? `<p class="mb-0 mt-0 text-sm"><a class="select-class selectedEl" href="#" data-class="${capitalize(key)}">Select</a></p>` : `<span class="text-sm text-secondary selectedEl">Selected</span>`}
				</div>
				${
					equityNames[key]
						? `
					<div class="flex items-center justify-between">
						<h5 class="text-lg mt-0 mb-0 equityName">${equityNames[key]}</h5></div>
					<hr class="border-t-1" />
				`
						: ``
				}
				${
					cD.use_real_estate_language
						? `

					${
						lV[key]
							? `
						<div><span ${c1}>Current Value:${s2} </span>$${s1}${Math.round(lV[key]).toLocaleString()}</div>
						${
							usePct
								? `<div ${c3}>Ownership Interest:${s2}${Number(uD[key].owned ? uD[key].owned : 0).toLocaleString('en-US', m5)}${s1}%</div>
						${bases[key] >= 0 ? `<div ${c3}>${s1}${cD.equity[key].costbasisLabel ? cD.equity[key].costbasisLabel : 'Cost Basis'}:${s4}${bases[key].toLocaleString('en-US', bases[key] >= 10000 ? m5 : m2)}</div>` : ``}
						`
								: `<div ${c3}>${lP}:${s4}${Number(uD[key].owned).toLocaleString('en-US', m5)}</div>`
						}

					`
							: usePct
								? `<div ${c3}>Ownership Interest:${s2}${Number(uD[key].owned ? uD[key].owned : 0).toLocaleString('en-US', m5)}${s1}%</div>
						${bases[key] >= 0 ? `<div ${c3}>${s1}${cD.equity[key].costbasisLabel ? cD.equity[key].costbasisLabel : 'Cost Basis'}:${s4}${bases[key].toLocaleString('en-US', bases[key] >= 10000 ? m5 : m2)}</div>` : ``}

					`
								: isACR && !key.startsWith('FUND') && !key.startsWith('CASH') && distribs[key] > 0
									? `<div><span ${c1}>Cash Gain:${s2} </span>$${s1}${distribs[key].toLocaleString()}</div>
						<div ${c3}>${s1}${'Residual'}:${s4}${Number(uD[key].owned ? uD[key].owned : 0).toLocaleString()}</div>

					`
									: `<div><span ${c1}>${lP}:${s2} </span>$${s1}${Number(uD[key].owned ? uD[key].owned : 0).toLocaleString()}</div>
					`
					}

				`
						: isAND || ['onwardpe'].includes(client)
							? `
					<div><span ${c1}>Current Value:${s2} </span>${s3}${lV[key] ? Math.round(lV[key]).toLocaleString() : Number(uD[key].owned ? uD[key].owned : 0).toLocaleString('en-US', m8)}</div>
					<div ${c3}>${lP}:${s4}${Number(uD[key].owned ? uD[key].owned : 0).toLocaleString('en-US', m8)}</div>
					<div ${c3}>${labelUncalled}:${s4}${(Number(uD[key].approved) - Number(uD[key].owned)).toLocaleString('en-US', m5)}</div>

				`
							: isPHJ && key == 'G1'
								? `
					<div><span ${c1}>Principal:${s2} </span>${s3}${bases[key] ? Math.round(bases[key]).toLocaleString('en-US', m2) : ''}</div>
					<!-- <div ${c3}>Accrued Interest/PIK:${s4}${(Number(uD[key].owned) - Number(bases[key])).toLocaleString('en-US', m2)}</div> -->

				`
								: isJMS && (key == 'NP' || key == 'NR')
									? nextPayment[key]
										? `
					<div class="mt-0 mb-0 flex items-center justify-between">
						<div><span ${c1}>Due ${nextDate[key]}:${s2} </span>${s3}${nextPayment[key].toLocaleString('en-US', m2)}</div>
						<p class="text-xs mb-0 mt-1 mr-2"><a href="#paymentDetailsModal" class="get-details modal-trigger${HIDE_JMS_DETAILS ? ' hidden' : ''}" data-class="${capitalize(key)}">Details</a></p>
					</div>
					<div ${c3}>Remaining:${s4}${(Number(uD[key].owned) - Number(uD[key].approved) - nextPayment[key]).toLocaleString('en-US', m2)}</div>
					`
										: `
				`
									: cD.use_fund_language
										? (() => {
												// Check if templates are configured for this equity class
												const fundClassConfig = cD.equity[key] || {};
												const fundHasTemplates =
													fundClassConfig.line1_template ||
													fundClassConfig.line2_template ||
													fundClassConfig.line3_template ||
													fundClassConfig.line4_template;
												if (fundHasTemplates) {
													const cardLines = EquityCardService.renderCardLines(
														key,
														uD[key],
														cD,
														bases[key],
														latestValue[key],
														interest[key],
														window.lastCalculationResult,
														{
															stockStatus: userData.clients[client]?.status,
															userData: userData,
															aggregates: classAggregates[key] || null,
															client: client,
														}
													);
													let html = '';
													if (cardLines.line1) html += `<div><span ${c1}>${cardLines.line1}</span></div>`;
													if (cardLines.line2) html += `<div ${c4}>${cardLines.line2}</div>`;
													if (cardLines.line3) html += `<div ${c3}>${cardLines.line3}</div>`;
													if (cardLines.line4) html += `<div ${c4}>${cardLines.line4}</div>`;
													return html;
												}
												// Fall through to existing hardcoded fund language markup
												return `
					<div><span ${c1}>${cD.equity[key]?.youownLabel ? cD.equity[key].youownLabel : lP}:${s2} </span>${cD.equity[key]?.sharesType ? '' : s3}${Number(uD[key].owned ? uD[key].owned : 0).toLocaleString('en-US', isCER && key == 'CCA' ? m0 : pC)}${cD.equity[key]?.sharesType ? s1 + cD.equity[key].sharesType : ''}</div>
					${
						isORR
							? `
					`
							: isCER && key != 'CCA'
								? `
						<div ${c4}>${((100 * Number(uD[key].owned)) / TOTAL_UNITS).toLocaleString('en-US', m4)} % of Total (All) Units</div>
					`
								: isCER || isIQ3 || isFAV
									? `
						<div ${c3}>${userData.clients[client].notes || ``}</div>
					`
									: `
						<div ${c3}>${isMH && key.startsWith('HC') ? 'Unvested Equity' : labelUncalled}:${s4}${Number(uD[key].committed ? uD[key].committed : 0) > 0 ? Number(uD[key].committed ? uD[key].committed : 0).toLocaleString('en-US', m8) : Math.abs(Number(uD[key].approved ? uD[key].approved : 0) - Number(uD[key].owned ? uD[key].owned : 0)).toLocaleString('en-US', m5)}</div>
					`
					}
				`;
											})()
										: `
					${(() => {
						// Check if templates are configured for this equity class
						const classConfig = cD.equity[key] || {};
						const hasTemplates =
							classConfig.line1_template || classConfig.line2_template || classConfig.line3_template || classConfig.line4_template;

						if (hasTemplates) {
							// Use template-based rendering - just numbered lines with appropriate styling
							const cardLines = EquityCardService.renderCardLines(
								key,
								uD[key],
								cD,
								bases[key],
								latestValue[key],
								interest[key],
								window.lastCalculationResult,
								{
									stockStatus: userData.clients[client]?.status,
									userData: userData,
									aggregates: classAggregates[key] || null,
									client: client,
								}
							);
							let html = '';

							// Line 1 - Primary (bold styling)
							if (cardLines.line1) html += `<div><span ${c1}>${cardLines.line1}</span></div>`;

							// Line 2 - Secondary
							if (cardLines.line2) html += `<div ${c4}>${cardLines.line2}</div>`;

							// Line 3 - Tertiary
							if (cardLines.line3) html += `<div ${c3}>${cardLines.line3}</div>`;

							// Line 4 - Additional
							if (cardLines.line4) html += `<div ${c4}>${cardLines.line4}</div>`;

							return html;
						}

						// FALLBACK: Original hardcoded logic for clients without templates
						let html = `<div><span ${c1}>${classConfig.youownLabel ? classConfig.youownLabel : uD[key].vesting ? 'Your plan' : 'You Own'}: </span>${s2}${classConfig.hasOwnProperty('interestRate') ? '$ ' : ''}`;
						html += (Number(uD[key].owned ? uD[key].owned : 0) + (isACC ? interest[key] : 0)).toLocaleString(
							'en-US',
							isTBC ? m6 : isBW ? m4 : isACC || isMAR ? m2 : isCER && key == 'CCA' ? m0 : isTCA && key == 'ER' ? usd : ''
						);
						html += `${s1}${classConfig.hasOwnProperty('interestRate') ? '' : classConfig.sharesType ? classConfig.sharesType.toLowerCase() : 'shares'}</div>`;

						// Alera subtext
						if (isAlera && uD[key].orig) {
							html += `<div ${c4}>${s1}${classConfig.subText}:${s1}${Number(uD[key].orig).toLocaleString('en-US', m0)} units</div>`;
						}

						// Tranches or cost basis
						if (uD[key].tranches) {
							html += `<p ${c4}><a href="#trancheDetailsModal" class="get-tranches modal-trigger" data-class="${capitalize(key)}">Details of ${uD[key].tranches} tranches</a></p>`;
						} else if (bases[key] >= 0 && !classConfig.hideCostBasis) {
							const cbLabel = classConfig.overrideCbLabel || classConfig.costbasisLabel || (uD[key].vesting ? 'Basis' : 'Cost Basis');
							const cbValue = classConfig.overrideCbPrice
								? classConfig.overrideCbPrice.toLocaleString('en-US', usp)
								: bases[key].toLocaleString('en-US', usd);
							const expiry = uD[key].expDate ? ',' + s2 + 'Expires:' + s1 + uD[key].expDate + s1 : '';
							html += `<div ${c4}>${s1}${cbLabel}:${s1}${cbValue}${expiry}</div>`;
						}

						return html;
					})()}
				 `
				}

				${(() => {
					// Skip this section if templates are in use (they handle "available to purchase" display)
					const classConfig = cD.equity[key] || {};
					const hasTemplates = classConfig.line1_template || classConfig.line2_template || classConfig.line3_template || classConfig.line4_template;
					if (hasTemplates) return '';

					return (cD.trading_platform || !isAlera) && cD.equity[key]?.structure && (alreadyEnrolled || !REQUIRE_ENROLL)
						? `
					<div><span ${c1}>You can ${tradeAction}:</span> ${Number(uD[key].approved).toLocaleString('en-US', isTBC ? m6 : '')}${s1}${cD.equity[key]?.sharesType ? cD.equity[key].sharesType.toLowerCase() : 'shares'}</div>
					<hr class="border-t-1" />`
						: ``;
				})()}

				${
					ENROLL && !NEVER_ENROLLED && ['C', 'NTH', 'ONW'].includes(key) && !(userData.clients[client].tags || []).includes('Shared Services - NO!')
						? alreadyEnrolled
							? `
					<a href="#" class="btn waves-effect waves-light bg-grey-darker block disabled mt-4">${enrollDone}</a>`
							: clientData.currently_raising
								? `
					<a href=/enroll/ class="btn bg-grey-darker block mt-4">${enrollCTA}</a>
					`
								: ``
						: ``
				}

				${
					canTrade && cD.trading_platform && cD.equity[key]?.showTradeCTA && (alreadyEnrolled || !REQUIRE_ENROLL)
						? `
						<a href=/trade/ class="btn bg-grey-darker block">Buy/Sell Platform</a>
					`
						: ``
				}

				${
					isConsolidatedView(userData) && userData.clients[client]?.vestingByHolder
						? renderConsolidatedVesting(userData.clients[client].vestingByHolder, key)
						: uD[key].vesting
							? renderVestingSchedule(uD[key].vesting, key)
							: ''
				}

				${
					equityTags[key]
						? equityTags[key]
								.map(
									(item, i) => `
					<span class="category-tag" ${item.toLowerCase() == 'sold' ? "style='background:#cccccc;'" : ''}>${item}</span>
				`
								)
								.join(``)
						: ''
				}

				${
					PAYING && alreadyEnrolled && (key == 'NR' || (key == 'NP' && Number(uD[key].owned) > Number(uD[key].approved)))
						? `
						<hr class="border-t-1" />
						<a onClick="openDetails('${key}')" class="payOnlineNow${key} btn bg-grey-darker block mt-4">${key == 'NR' ? 'View Amounts to Receive' : 'Pay Online Now'}</a>
					`
						: NEVER_ENROLLED && ['NP'].includes(key)
							? `<a href=/enroll/ class="btn bg-grey-darker block mt-4">${enrollCTA}</a>`
							: ``
				}
			</div>
			`
			)
			.join('')}
		`;

		// Aggregate Portfolio card (for AND, AES, JMH, ACR, ORR, MH)
		if (SHOW_AGGREGATE) {
			let totalCurrentValue = 0;
			let totalPaidInCapital = 0;
			let totalUncalledBalance = 0;
			const USE_AGGREGATE_GRAPH = isAES || isAND;
			if (uD) {
				shareKeys.forEach(function (key) {
					if (uD[key].owned) {
						if (bases[key] >= 0 && USE_AGGREGATE_GRAPH) totalPaidInCapital += bases[key] * Number(uD[key].owned);
						else totalPaidInCapital += Number(uD[key].owned);

						if (uD[key].approved) {
							let uncalled = Number(uD[key].approved) - Number(uD[key].owned);
							if (uncalled < 0) uncalled = 0; // see similar logic in buildEventsAndTransactionsModal(), near Line 7368 (95 lines down)
							if (UFL && uD[key].committed && Number(uD[key].committed) > 0) uncalled = Number(uD[key].committed); // see similar logic in buildEventsAndTransactionsModal(), near Line 7372 (100 lines down)
							totalUncalledBalance += uncalled;
						}
					}
					// 11/5/2025 CFM don't add negative numbers (underwater values) - should this logic be global?
					totalCurrentValue += isSEQ ? Math.max(0, latestValue[key] || 0) : latestValue[key] || 0;
				});
				netValue = totalCurrentValue - totalPaidInCapital;
			}

			if (currentShareClass < 0 && USE_AGGREGATE_GRAPH) {
				document.getElementById('tradingData').classList.add('hidden');
				document.getElementById('overviewLandingPage').classList.add('hidden');
				document.getElementById('portfolioData').classList.remove('hidden');

				// Add footnote to portfolio graph if enabled
				if (clientData.flags?.enable_footnote && clientData.about) {
					addFootnoteToPortfolioGraph(resolveAbout());
				}
			}

			let key = currentShareClass < 0 ? 'ALL' : shareKeys[currentShareClass];
			let aggregatePortfolioMarkup = `
				<!-- <div class="share-card mb-4${capitalize(key) == 'ALL' ? ` border-blue-2` : ``}" data-class="ALL"> -->
				<div class="mt-4 mb-6" data-class="ALL">
					<div class="flex items-center justify-between">
						<h3 class="text-xl font-bold mt-0 mb-2">Your Portfolio</h3>
						${capitalize(key) == 'ALL' ? `<span class="text-sm text-secondary">Selected</span>` : `<p class="mb-2 mt-0 mr-4 text-sm"><a class="select-class" href="#" data-class="ALL"}>Select</a></p>`}
					</div>
					${
						isAND && userData.userid == 'bob@andesiteholdings.com'
							? `
						<div><span ${c1}>Current Value:${s2} </span>$${s1}${Math.round('2874947').toLocaleString()}</div>
						<div ${c3}>${lP}:${s4}${Number('1824209').toLocaleString('en-US', m5)}</div>
					`
							: isAND && userData.userid == 'bob+robert_hall_gst@andesiteholdings.com'
								? `
						<div><span ${c1}>Current Value:${s2} </span>${s1}${Math.round('9966885').toLocaleString('en-US', usd)}</div>
						<div ${c3}>${lP}:${s4}${Number('7098367.23').toLocaleString('en-US', m5)}</div>
					`
								: isAND
									? `
						<div><span ${c1}>Current Value:${s2} </span>${s1}${Math.round('12841832').toLocaleString('en-US', usd)}</div>
						<div ${c3}>${lP}:${s4}${Number('8922576').toLocaleString('en-US', m5)}</div>
					`
									: isAES
										? `
						<div><span ${c1}>Total Value:${s2} </span>${s1}${netValue.toLocaleString('en-US', usd)}</div>
					`
										: false
											? `
						<div><span ${c1}>Current Value:${s2} </span>$${s1}${totalCurrentValue.toLocaleString('en-US', m5)}</div>
						<div ${c3}>Total ${labelPaidIn}:${s4}${totalPaidInCapital.toLocaleString('en-US', m5)}</div>
					`
											: isJMH || isMH
												? `
						<div><span ${c1}>Total ${labelPaidIn}:${s2} </span>$${s1}${totalPaidInCapital.toLocaleString('en-US', m5)}</div>
						<div ${c3}>Total ${labelUncalled}:${s4}${totalUncalledBalance.toLocaleString('en-US', m5)}</div>
					`
												: isORR
													? `
						<div><span ${c1}>Total ${labelPaidIn}:${s2} </span>$${s1}${totalPaidInCapital.toLocaleString('en-US', m5)}</div>
					`
													: isPSS
														? `
						<div><span ${c1}>Total ${labelPaidIn}:${s2} </span>${s1}${totalPaidInCapital.toLocaleString('en-US', m5)} units</div>
					`
														: isACR
															? `
						<div><span ${c1}>Total Investment:${s2} </span>$${s1}${totalPaidInCapital.toLocaleString('en-US', m0)}</div>
						<div ${c3}>Cumulative Return:${s4}${cumulativeBalance.toLocaleString('en-US', m0)}</div>
					`
															: ``
					}

				</div>
			`;

			document.getElementById('aggregatePortfolioLarge').innerHTML = aggregatePortfolioMarkup;
			document.getElementById('aggregatePortfolioSmall').innerHTML = aggregatePortfolioMarkup;
		}

		window.openDetails = function (key) {
			let elements = document.querySelectorAll('.get-details');
			for (let i = 0; i < elements.length; i++) {
				if (elements[i].dataset.class == key) {
					elements[i].dispatchEvent(new Event('click'));
					i = elements.length;
				}
			}

			M.Modal.getInstance(document.getElementById('paymentDetailsModal')).open();
		};
	}

	// Vesting toggle listeners (only add if have vesting)
	if (!addedVestingListenerAlready && (vesting > 0 || isBOH)) {
		addedVestingListenerAlready = true;
		document.addEventListener('click', function (e) {
			let classList = e.target.classList;
			if (classList.contains('time-toggle')) {
				e.preventDefault();
				toggleVesting(e.target, e.target.getAttribute('data-issue'), 'vesting-schedule');
			} else if (classList.contains('goals-toggle')) {
				e.preventDefault();
				toggleVesting(e.target, e.target.getAttribute('data-issue'), 'vesting-goals');
			} else if (classList.contains('modal-overlay')) {
				if (
					!M.Modal.getInstance(document.getElementById('amortizationModal')).isOpen &&
					!M.Modal.getInstance(document.getElementById('trancheAnalysisModal')).isOpen
				) {
					document.getElementById('viewTransactionsModal').classList.remove('hidden');
				}
			}
		});
	}

	// Markup for each class
	let classDetails = document.getElementsByClassName('shareClassDetails');
	for (let i = 0; i < classDetails.length; i++) {
		classDetails[i].innerHTML = shareClassMarkup;
	}

	// Dark border for selected class
	let classCards = document.getElementsByClassName('share-card');
	for (let i = 0; i < classCards.length; i++) {
		// Apply a selected border to the current share class card
		if (classCards[i].dataset.class == shareKeys[currentShareClass]) {
			classCards[i].classList.add('border-blue-2', 'active');
		}
	}

	// Show Graph versus Show Image
	if (!addedLandingListenersAlready && !clientData.product_video) {
		addedLandingListenersAlready = true;

		// landingPageHref.addEventListener('click', function(e) {
		// 	tryShowGraph = hasPriceHistory || hasEventHistory;
		// 	renderEquityData();
		// });
		// tradingData.addEventListener('click', function(e) {
		// 	// This listener fails to recognize clicks on the Google Chart SVG while these verbose warnings are firing:
		// 	// [Violation] Added non-passive event listener to a scroll-blocking 'mousewheel' event.
		// 	// Consider marking event handler as 'passive' to make the page more responsive
		// 	// See https://www.chromestatus.com/feature/5745543795965952
		// 	tryShowGraph = false;
		// 	renderEquityData();
		// });

		let showImageOrGraph = document.getElementById('showImageOrGraph');
		showImageOrGraph.addEventListener('click', function (e) {
			let action = showImageOrGraph.innerHTML;
			setPreference(action.replace('Show ', ''));
			if (action == 'Show Graph') {
				tryShowGraph = hasPriceHistory || hasEventHistory;
				renderEquityData();
				showImageOrGraph.innerHTML = 'Show Image';
				if (window.analytics)
					window.analytics.track('Show Image Link Clicked', {
						userEmail: authedUser,
						workspace: client,
					});
			} else {
				tryShowGraph = false;
				renderEquityData();
				showImageOrGraph.innerHTML = 'Show Graph';
				if (window.analytics)
					window.analytics.track('Show Graph Link Clicked', {
						userEmail: authedUser,
						workspace: client,
					});
			}
			function setPreference(action) {
				userDataShares[symbol].preference = action;
				userData.clients[client].shares[symbol].preference = action;
				sessionStorage.setItem('user.Data', JSON.stringify(userData));
				if (capTableData) {
					let index = capTableData.findIndex((h) => h.userid == userData.userid);
					if (index >= 0) capTableData[index].clients[client].shares[symbol].preference = action;
					NthUtil.sessionStore.updateCapTable(client, capTableData);
				}
				axios
					.post(
						postURL,
						JSON.stringify({
							eventName: 'index.share_preference_set',
							userid: authedUser,
							client: client,
							cookie: cookie,
							clients: {
								[client]: {
									shares: {
										[symbol]: {
											preference: action,
										},
									},
								},
							},
						})
					)
					.then(function (res) {
						console.log(res);
					})
					.catch(function (err) {
						console.error(err);
					});
			}
		});
	}

	// Main class Select listeners
	document.querySelectorAll('.select-class').forEach((item) => {
		item.addEventListener('click', (event) => {
			event.preventDefault();
			//handle click
			symbol = event.target.dataset.class;
			currentShareClass = shareKeys.indexOf(symbol);
			if (isAES || isAND) {
				if (symbol == 'ALL') {
					document.getElementById('tradingData').classList.add('hidden');
					document.getElementById('overviewLandingPage').classList.add('hidden');
					document.getElementById('portfolioData').classList.remove('hidden');
					document.getElementById('showImageOrGraph').classList.add('hidden');
					// drawPortfolioChart();
				} else {
					document.getElementById('portfolioData').classList.add('hidden');
					document.getElementById('tradingData').classList.remove('hidden');
					document.getElementById('overviewLandingPage').classList.remove('hidden');
				}
			}
			let preference = userDataShares[symbol] && userDataShares[symbol].preference ? userDataShares[symbol].preference : 'Graph';
			tryShowGraph = preference == 'Graph';
			renderEquityData();

			if (window.analytics)
				window.analytics.track('Switched Class', {
					userEmail: authedUser,
					workspace: client,
					newClass: symbol,
				});
		});
	});

	document.querySelectorAll('.get-tranches').forEach((item) => {
		item.addEventListener('click', (event) => {
			event.preventDefault();

			let trancheDetailsHeading = document.getElementById('trancheDetailsHeading');
			let trancheDetailsTable = document.getElementById('trancheDetailsTable');
			if (trancheDetailsTable) trancheDetailsTable.innerHTML = '';
			let trancheDetailsFooter = document.getElementById('trancheDetailsFooter');
			trancheDetailsFooter.classList.add('hidden');

			let noteCol = false;
			let latest = Date.now() / 1000;
			let eventClass = event.target.dataset.class;

			// Support consolidated view
			const trancheUserIds = getActiveUserIds(userData);
			let tranches = clientData.equity[eventClass].transactions.filter((t) => {
				const receiverLower = t.receiver?.toLowerCase();
				const isReceiver = receiverLower && trancheUserIds.some((uid) => uid?.toLowerCase() === receiverLower);
				return isReceiver && t.expDate;
			});
			tranches.sort((a, b) => (Number(a.date) < Number(b.date) ? -1 : 1)); // oldest to newest
			tranches.forEach((t, i) => (tranches[i].notes = ''));

			let dispositions = clientData.equity[eventClass].transactions.filter((t) => {
				const senderLower = t.sender?.toLowerCase();
				return senderLower && trancheUserIds.some((uid) => uid?.toLowerCase() === senderLower);
			});
			let dispTypes = ['expired', 'exercised', 'rescinded', 'forfeited'];
			dispTypes.forEach((t) => {
				dispositions.forEach((d, i) => {
					if (d.notes.toLowerCase().includes(t)) {
						for (let i = 0; i < tranches.length; i++) {
							if (tranches[i].quantity == d.quantity) {
								tranches[i].notes = capitalize(t);
								noteCol = true;
								break;
							}
						}
					}
				});
			});

			showTrancheDetails();

			function showTrancheDetails() {
				let avgTranches = 0;
				let totalTranches = 0;
				tranches.forEach((t) => (avgTranches += Number(t.quantity) * Number(t.strikePrice)));
				tranches.forEach((t) => (totalTranches += Number(t.quantity)));
				avgTranches /= totalTranches;

				let emptyState = `<div class="bg-grey-lightest rounded border-grey grey-text center-align p-2">No tranches</div>`;
				let trancheDetailsMarkup = `
					${
						tranches.length
							? `
						<div class="table-wrap ml-1 mt-8 mb-0" style="width: 100% !important">
							<h2 class="text-2xl font-bold">Tranche Details</h2>
							<table id="trancheDetailsSortTable" class="highlight table-content" style="width: 100% !important">
								<thead>
									<tr>
										<th class="center-align">Quantity</th>
										<th class="center-align">Strike Price</th>
										<th class="center-align" data-sort-default>Expiration Date</th>
										${noteCol ? `<th class="center-align"></th>` : ``}
									</tr>
								</thead>
								<tbody>
								${tranches
									.map(
										(tranche, index) =>
											`
									<tr>
										<td class="text-sm center-align">
											${Number(tranche.quantity).toLocaleString()}
										</td>
										<td class="text-sm center-align">
											${Number(tranche.strikePrice).toLocaleString('en-US', usd)}
										</td>
										<td class="text-sm center-align" data-sort='${tranche.expDate}'>
											${moment.unix(Number(tranche.expDate)).format('l')}
										</td>
										${
											noteCol
												? `
											<td class="text-xs center-align"'>
												${tranche.notes}
											</td>
										`
												: ``
										}
									</tr>
									`
									)
									.join('')}
								</tbody>
								<tfoot>
									<tr>
										<th class="center-align">${totalTranches.toLocaleString()}</th>
										<th class="center-align">${avgTranches.toLocaleString('en-US', usd)}</th>
									</tr>
								</tfoot>
							</table>
						</div>

						`
							: `${emptyState}`
					}
				`;

				trancheDetailsTable.innerHTML = trancheDetailsMarkup;
				trancheDetailsFooter.classList.remove('hidden');
				trancheDetailsHeading.innerHTML = 'Details of ' + tranches.length + ' Tranches';
				let table = document.getElementById('trancheDetailsSortTable');
				if (table) {
					let sort = new Tablesort(table, { descending: false });
				}

				if (window.analytics) {
					window.analytics.track('Viewed Tranches', {
						userEmail: authedUser,
						workspace: client,
						userId: userData.userid,
					});
				}
			}
		});
	});

	if (!addedExportTranchesListenerAlready) {
		addedExportTranchesListenerAlready = true;
		document.getElementById('exportTrancheDetails').addEventListener('click', function (e) {
			e.preventDefault();
			export_table_to_excel('trancheDetailsSortTable', 'Tranche Details (' + new Date().toLocaleString() + ').csv');
		});
	}

	if (!Object.keys(landingImages).length) preLoadImages();

	function preLoadImages() {
		shareKeys.forEach(function (key) {
			if (clientData.equity[key]) {
				let keyLandingPage = clientData.equity[key].landingPage;
				if (keyLandingPage && keyLandingPage.image) {
					landingImages[key] = new Image();
					landingImages[key].src = keyLandingPage.image;
				}
			}
		});
	}

	// Only used by JM Search
	let JMS_ACH = getUserDwollaData(userData);
	let PREVIEW = authedUser != userData.userid;
	let plaidCounter = 0;
	let NO_PLAID = false;
	let paymentIndex = 0;
	let thisPayment = {};
	let paymentTable = '';
	let confirmationId = '';
	let totalActuallyPaid = 0;
	let completedPayments = [];
	let alreadyListeningPlaidRetry = false;
	const paySelectionNow = document.getElementById('paySelectionNow');

	// Disable Pay Online Now CTA id already paid
	if (PAYING && alreadyEnrolled) {
		let shClientData = userData.clients[client];
		let shDocs = shClientData ? shClientData.documents : [];
		let quarter = moment().subtract(1.5, 'M').format('[Q]Q Y');
		let alreadyPaid = shDocs ? shDocs.find((d) => d.name.includes('Payment Confirmation for ' + quarter)) : null;
		if (alreadyPaid) {
			console.log('***Speed Test - BEFORE axios.get() : ' + (Date.now() - refreshTime) + ' ms');
			axios
				.get('https://api.nthround.io/portal/auth/get-payments', {
					params: {
						participant: userData.userid,
						userid: authedUser,
						client: client,
						cookie: cookie,
					},
				})
				.then(function (response) {
					console.log('***Speed Test - AFTER axios.get() : ' + (Date.now() - refreshTime) + ' ms');
					let payments = response.data;
					payments = payments.filter((p) => Number(p.Id) > Date.now() - 45 * 24 * 3600000); // last 45 days
					if (!payments.length) alreadyPaid = null;
					if (alreadyPaid) {
						let payOnlineCTAs = document.getElementsByClassName('payOnlineNowNP');
						for (let i = 0; i < payOnlineCTAs.length; i++) {
							payOnlineCTAs[i].classList.add('disabled');
							payOnlineCTAs[i].innerHTML = 'Paid - Thank you!';
						}
					}
				})
				.catch(function (error) {
					console.error(error);
				});
		}
	}

	document.querySelectorAll('.get-details').forEach((item) => {
		item.addEventListener('click', (event) => {
			event.preventDefault();
			paySelectionNow.classList.add('hidden');
			let paymentDetailsHeading = document.getElementById('paymentDetailsHeading');
			paymentDetailsHeading.innerHTML = 'One moment...';
			let paymentDetailsTable = document.getElementById('paymentDetailsTable');
			if (paymentDetailsTable) paymentDetailsTable.innerHTML = '';
			let paymentDetailsFooter = document.getElementById('paymentDetailsFooter');
			paymentDetailsFooter.classList.add('hidden');
			document.getElementById('preloadHtml').innerHTML = `<i class="material-symbols-outlined text-white">check</i>`;

			let uid = userData.userid;
			let eventClass = event.target.dataset.class;
			let latest = Date.now() / 1000;

			// TO MODEL OTHER PERIODS
			// latest = moment().add(-0.2, 'Y').unix();

			// Date.now() fails to get correct payment details between Jan 1 and Jan 15 (Transaction date)
			let today = new Date();
			if (today.getMonth() == 0 && today.getDate() < 15) latest = Date.parse('12/31/' + (new Date().getFullYear() - 1)) / 1000;
			//latest = latest - 24*24*3600;	// wait 24 days
			latest = latest + 66 * 24 * 3600; // look ahead 66 days
			console.log('latest = ' + latest + ' (' + moment.unix(latest).format('lll') + ')');

			paymentTransactions = [];
			axios
				.post(
					'https://api.nthround.io/portal/user/alias-names',
					JSON.stringify({
						eventName: 'index.JMSearch_members',
						alias_list: [], // alias_list: clientData.userlist})
						client: client,
						userid: authedUser,
						cookie: cookie,
					})
				)
				.then(function (res) {
					// console.log(res.data);
					let members = removeNthAdmins(res.data);

					// NEW CODE
					let assocEvents = clientData.equity[eventClass].events;
					assocEvents = assocEvents.filter((a) => Number(a.date) < latest); // - 24*24*3600);	// wait 24 days
					assocEvents = assocEvents.sort((a, b) => (Number(a.date) < Number(b.date) ? 1 : -1));
					let clientEventDate = assocEvents[0].date;
					let eventPayments = getPayments(userData.userid, eventClass, clientEventDate);
					eventPayments.forEach((p) => {
						let pmt = {};
						pmt.userId = p.uid;
						pmt.userName = p.name;
						pmt.payment = p.prin + p.int;
						let thisUser = members.find((x) => p.uid == x.userid);
						const thisUserAchData = getUserDwollaData(thisUser);
						pmt.bankName = thisUserAchData?.primary_funding_source_bank_acct_name || '';
						let existingIdx = paymentTransactions.findIndex((x) => x.userName == p.name);
						if (existingIdx >= 0) paymentTransactions[existingIdx].payment += p.prin + p.int;
						else paymentTransactions.push(pmt);
					});

					let period = moment().subtract(1.5, 'M').format('[Q]Q_Y');

					let hasPaid = false;

					// if (GET_PAYMENTS) {
					axios
						.get('https://api.nthround.io/portal/auth/get-payments', {
							params: {
								participant: uid,
								userid: authedUser,
								client: client,
								cookie: cookie,
							},
						})
						.then(function (response) {
							let payments = response.data;
							// console.log(payments);
							setPaymentsAchStatus(payments);
						})
						.catch(function (error) {
							console.error(error);
						});
					// } else setPaymentsAchStatus([]);

					function setPaymentsAchStatus(payments) {
						paymentTransactions.forEach(function (t, i) {
							let fromTo = uid + '-to-' + t.userId;
							if (eventClass == 'NR') fromTo = t.userId + '-to-' + uid;
							paymentTransactions[i].achId = client + '-' + period + '-' + fromTo + '-for-' + (Math.round(t.payment * 100) / 100).toFixed(2);
						});

						// Need to do in two steps to include Andrew Henry true-Up transaction
						paymentTransactions.forEach(function (t, i) {
							if (payments && payments.length > 0) {
								if (eventClass == 'NP') {
									payments.forEach(function (p) {
										if (p.achId == t.achId) {
											hasPaid = true;
											paymentTransactions[i].achStatus = p.achStatus;
										}
									});
								} else {
									payments.forEach(function (p) {
										if (p.achId == t.achId) {
											t.amtPaid = Number(p.amount);
											// NEED TO HANDLE FAILED...
											t.achStatus = t.achStatus
												? t.achStatus == 'Completed' && p.achStatus == 'Completed'
													? 'Completed'
													: 'PAID'
												: p.achStatus;
										}
									});
									paymentTransactions[i].amtPaid = t.amtPaid ? t.amtPaid : 0;
									if (t.achStatus) paymentTransactions[i].achStatus = t.achStatus;
								}
							} else
								Object.keys(paymentTransactions).forEach((k) => {
									let GETTING_READY_TO_MAKE_PAYMENTS = true;
									if (GETTING_READY_TO_MAKE_PAYMENTS) {
										paymentTransactions[k].amtPaid = 0;
										paymentTransactions[k].achStatus = '';
									} else {
										paymentTransactions[k].amtPaid = paymentTransactions[k].payment;
										paymentTransactions[k].achStatus = 'Completed';
									}
								});
						});
						showPaymentDetails();
					}

					function showPaymentDetails() {
						let totalPayment = 0;
						let totalAchPayment = 0;
						let preposition = eventClass == 'NP' ? 'to' : 'from';

						if (PAYING && preposition == 'to') paySelectionNow.classList.remove('hidden');
						else paySelectionNow.classList.add('hidden');

						paymentTransactions.sort((a, b) => (a.userName > b.userName ? 1 : -1));
						paymentTransactions.forEach(function (t, i) {
							totalPayment += t.payment;
							if (preposition == 'to' && (!t.achStatus || t.achStatus.length < 2) && t.bankName) totalAchPayment += t.payment || 0;
							else if (preposition == 'from') totalAchPayment += t.amtPaid || 0;
						});

						const greenCheckJMS = `<span class="material-icons-outlined align-middle" style="color: green; font-size: 30px;" title="Ready to receive payment.">check</span>`;
						const redClearJMS = `<span class="material-icons-outlined align-middle" style="color: red; font-size: 30px;" title="Missing ACH/Bank information.">clear</span>`;
						const completed = `<i class="material-symbols-outlined green-text right mt-05" title="Payment completed.">check</i>`;
						const pending = `<i class="material-symbols-outlined text-white right mt-05" title="Payment pending.">check</i>`;
						const failed = `<i class="material-symbols-outlined red-text right mt-05" title="Payment failed.">close</i>`;
						let emptyState = `<div class="bg-grey-lightest rounded border-grey grey-text center-align p-2">No payments</div>`;

						let paymentDetailsMarkup = `
						${
							paymentTransactions.length
								? `
							<div class="table-wrap ml-1 mt-6 mb-0">
								<table id="paymentDetailsSortTable" class="highlight table-content">
									<thead>
										<tr>
											<!-- <th data-sort-default style="width: 175px;">Date</th> -->
											<th data-sort-default>Payment ${preposition}:</th>
											<th class="center-align" style="width: 150px;">Amount</th>
											${
												JMS_ACH
													? `<th id="selectToPay" class="center-align" style="width: 130px;">${preposition == 'from' ? 'Amount Paid' : hasPaid ? 'Status' : 'Ready'}</th>
											<!-- <th>
												<label class="ml-8" title="Select ALL">
													<input type="checkbox" data-pay="ALL"/>
													<span></span>
												</label>
											</th> -->`
													: ``
											}
										</tr>
									</thead>
									<tbody>
									${paymentTransactions
										.map(
											(trans, index) =>
												`
										<tr>
											<!-- <td class="text-xs" data-sort='${trans.date}'>
												${moment.unix(Number(trans.date / 1000)).format('llll')}
											</td> -->
											<td class="text-sm">
												${trans.userName}
											</td>
											<td class="text-sm center-align">
												${trans.payment.toLocaleString('en-US', usd)}
											</td>
											${
												JMS_ACH
													? `
												${
													preposition == 'to'
														? `
												<td class="center-align">
													<!-- <label>
														<input id="selectPay${index}" type="checkbox" checked onChange="selectPay('${index}')" />
														<input id="selectPay${index}" type="checkbox" checked onchange="(function(){document.getElementById('selectPay${index}').checked = true})()" />
														<span></span>
													</label> -->
													<span id="check${index}" class="mr-1">${trans.achStatus && trans.achStatus.length > 2 ? (trans.achStatus == '-' ? '-' : 'PAID') : trans.bankName ? greenCheckJMS : redClearJMS}</span>
													<span id="paid${index}" class="text-sm mr-1 hidden">PAID</span>
													<div id="spinner${index}" class="hidden">
														<svg class="spinner" viewBox="0 0 50 50" style="position: relative; top: 6px;">
															<circle class="path" cx="25" cy="25" r="20" fill="none" stroke-width="5"></circle>
														</svg>
													</div>
												`
														: `
												<td class="right-align">
													<span id="check${index}" class="text-sm${trans.amtPaid ? '' : ' mr-6'}">${trans.amtPaid ? trans.amtPaid.toLocaleString('en-US', usd) : '-'}</span>
													<span class="ml-0">${trans.achStatus ? (trans.achStatus == 'PAID' ? pending : trans.achStatus == 'Completed' ? completed : trans.achStatus == 'failed' ? failed : pending) : pending}</span>
												`
												}
											</td>`
													: ``
											}
										</tr>
										`
										)
										.join('')}
									</tbody>
									<tfoot>
										<tr class="border-none">
											<!-- <th data-sort-default style="width: 175px;">Date</th> -->
											<th>Total</th>
											<th class="center-align text-secondary">${totalPayment.toLocaleString('en-US', usd)}</th>
											${JMS_ACH ? `<th id="selectPayTotal" class="center-align">${totalAchPayment.toLocaleString('en-US', usd)}</th>` : ``}
										</tr>
									</tfoot>
								</table>
							</div>

							`
								: `${emptyState}`
						}
					`;

						paymentDetailsTable.innerHTML = paymentDetailsMarkup;
						paymentDetailsFooter.classList.remove('hidden');
						paymentDetailsHeading.innerHTML = 'Payment Details';
						let table = document.getElementById('paymentDetailsSortTable');
						if (table) {
							let sort = new Tablesort(table, { ascending: true });
						}

						// let netDollars = 0;
						// let NO_PLAID = false;

						if (!JMS_ACH) {
							paySelectionNow.innerHTML = 'NO ACH/Bank Info';
							paySelectionNow.classList.add('disabled');
						}
						if (PAYING && preposition == 'to') getCheckingAccountBalance();

						function getCheckingAccountBalance() {
							// !!! DEBUG !!!  *** DEMO ***
							// if (authedUser == "zkhalid@nthround.com" || userData.userid == "kayep@jmsearch.com") {
							// 	if (totalAchPayment < 0.02) {
							// 		paySelectionNow.innerHTML = "Nothing to Pay";
							// 		paySelectionNow.classList.add('disabled');
							// 	} else {
							// 		paySelectionNow.innerHTML = "PAY NOW";
							// 		paySelectionNow.classList.remove('disabled');
							// 	}
							// 	return;
							// }

							let accountBalanceTable = document.getElementById('accountBalanceTable');

							if (NO_PLAID) {
								if (totalAchPayment < 0.02) {
									paySelectionNow.innerHTML = 'Nothing to Pay';
									paySelectionNow.classList.add('disabled');
								} else if (PREVIEW) {
									paySelectionNow.innerHTML = 'Previewing - Disabled';
									paySelectionNow.classList.add('disabled');
								}
								accountBalanceTable.innerHTML = `
							<div class="bg-grey-lightest rounded border-grey grey-text text-xs center-align p-0">
								No checking account information available (<a id="tryPlaidAgain" class="link-accent" href="#">retry</a>).
							</div>
						`;
								alreadyListeningPlaidRetry = false;
								listenForPlaidRetry();
								return;
							}

							accountBalanceTable.innerHTML = `
							<div class="p-0 mt-0 mb-0">
								<svg class="spinner" viewBox="0 0 50 50" style="position: absolute; bottom: 100px; left: 150px;">
									<circle class="path" cx="25" cy="25" r="20" fill="none" stroke-width="5"></circle>
								</svg>
							</div>
							<h3 id="fetchingHeader" class="text-xs text-secondary mt-0 mb-0 center-align">Fetching data from Plaid</h3>
						`;
							const now = Date.now();
							let plaidAPI = 'plaidGet';
							let realTimePlaidStamp = localStorage.getItem('realTimePlaidStamp');
							if (plaidCounter == 0 && (!realTimePlaidStamp || Number(realTimePlaidStamp) + 4 * 3600000 < now)) {
								plaidAPI = 'plaidGetRT';
								localStorage.setItem('realTimePlaidStamp', now);
							}

							axios
								.get('https://api.nthround.io/portal/auth/call-plaid', {
									params: {
										api: plaidAPI,
										client: client,
										userid: authedUser,
										cookie: cookie,
										plaid_env: getPlaidEnvForDwolla(),
									},
								})
								.then(function (res) {
									console.log('axios.get(call-plaid)');
									// throw "Fatal error.";
									if (!res || !res.data || !res.data.accounts) {
										// 7-3-2024 Changed to throw error to handle Elba Pillion (no account, but Plaid no longer throws error)
										throw 'Fatal error.';
										// paySelectionNow.innerHTML = "No Account Found";
										// paySelectionNow.classList.add('disabled');
										// accountBalanceTable.innerHTML = `
										// 	<div class="bg-grey-lightest rounded border-grey grey-text text-xs center-align p-0" style="width: 200px;">
										// 		No checking account information available (<a id="tryPlaidAgain" class="link-accent" href="#">retry</a>). Please <a class="link-accent" href="/profile/#ach" title="Go to Your Profile and reconnect to your bank.">reconnect Plaid</a>.
										// 	</div>
										// `;
										// alreadyListeningPlaidRetry = false;
										// listenForPlaidRetry();
										return;
									}
									let account = res.data.accounts[0];
									let bankName = account.name || 'Unknown';
									let officialName = account.official_name || '';
									let accountType = (account.type || '') + ' (' + (account.subtype || '') + ')';
									let balances = account.balances;
									// From https://plaid.com/docs/api/products/balance/: Note that not all institutions calculate the available balance.
									// In the event that available balance is unavailable, Plaid will return an available balance value of null. (Actually 'None' from call-plaid at AWS.)
									let dollarsAvailable = 0;
									if (!isNaN(balances.available) && Number(balances.available) > 0) dollarsAvailable = Number(balances.available);
									else if (!isNaN(balances.current) && Number(balances.current) > 0) dollarsAvailable = Number(balances.current);
									// let dollarsAvailable = Number(balances.available);

									// With insufficient funds try again with RT
									if (dollarsAvailable < totalAchPayment && plaidAPI == 'plaidGet') {
										axios
											.get('https://api.nthround.io/portal/auth/call-plaid', {
												params: {
													api: 'plaidGetRT',
													client: client,
													userid: authedUser,
													cookie: cookie,
													plaid_env: getPlaidEnvForDwolla(),
												},
											})
											.then(function (res) {
												console.log(res);
												// handle  plaidGetRT error
												if (!res || !res.data || !res.data.accounts) {
													NO_PLAID = true;
													getCheckingAccountBalance();
													return;
												}
												let accountRT = res.data.accounts[0];
												let balancesRT = accountRT.balances;
												if (!isNaN(balancesRT.available) && Number(balancesRT.available) > 0)
													dollarsAvailable = Number(balancesRT.available);
												else if (!isNaN(balancesRT.current) && Number(balancesRT.current) > 0)
													dollarsAvailable = Number(balancesRT.current);
												// dollarsAvailable = Number(balancesRT.available);
												displayPlaidResult();
											})
											.catch(function (err) {
												console.error(err);
											});
									} else displayPlaidResult();

									function displayPlaidResult() {
										// Check if payment was already completed - don't reset button if so
										const currentButtonText = paySelectionNow.innerHTML;
										if (currentButtonText.toUpperCase() === 'VIEW CONFIRMATION') {
											// Payment already completed this session - don't reset button
											accountBalanceTable.innerHTML = `
									<p class="text-xs font-bold mt-0 mb-2 center-align">Payment Completed!</p>
									<p class="text-xxs text-secondary mt-0 mb-0 center-align">Your confirmation is ready to view.</p>
								`;
											return;
										}

										accountBalanceTable.innerHTML = `
								<p class="text-xxs mt-0 mb-2"><i>Displayed privately and securely by <a href="https://plaid.com/" target="_blank" rel="noopener noreferrer">Plaid</a>.</i></p>
								<p class="text-xs font-bold mt-0 mb-0">${bankName}</p>
								<p class="text-xxs text-secondary mt-0 mb-0">${bankName != officialName ? '(' + officialName + ')' : ''}</p>

								<table class="p-2 mt-0" style="width: 180px">
									<tr class="border-none">
										<td class="text-xs text-secondary p-0">Current Balance:</td>
										<td id="balancesCurrent" class="text-xs right-align p-0">${Number(balances.current).toLocaleString('en-US', usd)}</td>
									</tr>
									<tr class="border-none">
										<td class="text-xs text-secondary p-0">Available:</td>
										<td id="dollarsAvail" class="text-xs right-align p-0">${dollarsAvailable.toLocaleString('en-US', usd)}</td>
									</tr>
									<tr class="border-none">
										<td class="text-xs text-secondary p-0">Limit:</td>
										<td id="balancesLimit" class="text-xs right-align p-0">${balances.limit}</td>
									</tr>
								</table>
									<!-- <p id="balanceStatus" class="text-xs mt-6 mb-4 right hidden"></p> -->
									<!-- <p class="text-sm text-secondary mt-6 mb-0">Acount Type: ${accountType}</p> -->
									<!-- <div class="flex justify-between items-center mb-0">
										<p id="checkRealTimeElem" class="ml-1 mt-2 mb-3" title="Fetch real-time data"><a><i id="checkRealTime" class="text-secondary text-xs material-icons-outlined mt-2 mb-0 left">update</i></a></p>
										<p id="balanceStatus" class="text-xs mt-4 mb-2 right hidden"></p>
									</div> -->
								`;

										// document.getElementById('selectPayTotal').innerHTML = totalAmountToPay.toLocaleString('en-US', usd);

										const userAchRecord = getUserDwollaData(userData);
										if (userAchRecord?.primary_funding_source_bank_acct_name) {
											if (totalAchPayment < 0.02) {
												paySelectionNow.innerHTML = 'Nothing to Pay';
												paySelectionNow.classList.add('disabled');
											} else if (PREVIEW) {
												paySelectionNow.innerHTML = 'Previewing - Disabled';
												paySelectionNow.classList.add('disabled');
											} else if (dollarsAvailable >= totalAchPayment) {
												paySelectionNow.innerHTML = 'Verify and PAY';
												paySelectionNow.classList.remove('disabled');
											} else {
												paySelectionNow.innerHTML = 'Insufficient Funds';
												paySelectionNow.classList.add('disabled');
											}
										} else {
											paySelectionNow.classList.add('disabled');
											paySelectionNow.innerHTML = 'No ACH/Bank Info';
										}
									}
								})
								.catch(function (err) {
									console.error(err);
									if (err.response) {
										// The request was made and the server responded with a status code
										// that falls out of the range of 2xx
										console.log('Plaid error data: ' + err.response.data);
										console.log('Plaid error status: ' + err.response.status);
										console.log('Plaid error headers: ' + err.response.headers);
									} else if (err.request) {
										// The request was made but no response was received
										// `err.request` is an instance of XMLHttpRequest in the browser and an instance of
										// http.ClientRequest in node.js
										console.log('Plaid error request: ' + err.request);
									} else {
										// Something happened in setting up the request that triggered an Error
										console.log('Plaid error message: ' + err.message);
									}
									console.log('Plaid error config: ' + JSON.stringify(err.message));

									// try again without RT
									plaidCounter++;
									if (plaidCounter < 3) getCheckingAccountBalance();
									else {
										NO_PLAID = true;
										if (totalAchPayment > 0.02) {
											M.Modal.getInstance(document.getElementById('helpPlaidModal')).open();
											document
												.getElementById('helpPlaidProceed')
												.addEventListener('click', (e) => window.location.replace('/profile/#ach'));
											document.getElementById('helpPlaidClose').addEventListener('click', (e) => {
												const userAchRecord = getUserDwollaData(userData);
												if (userAchRecord?.primary_funding_source_bank_acct_name && !PREVIEW)
													paySelectionNow.classList.remove('disabled');
												accountBalanceTable.innerHTML = `
											<div class="bg-grey-lightest rounded border-grey grey-text text-xs center-align p-0">
												No checking account information available (<a id="tryPlaidAgain" class="link-accent" href="#">retry</a>).
											</div>
										`;
												alreadyListeningPlaidRetry = false;
												listenForPlaidRetry();
											});
										}
									}
								});
						}

						function listenForPlaidRetry() {
							const tryPlaidAgain = document.getElementById('tryPlaidAgain');
							if (tryPlaidAgain && !alreadyListeningPlaidRetry)
								tryPlaidAgain.addEventListener('click', () => {
									alreadyListeningPlaidRetry = true;
									plaidCounter = 0;
									NO_PLAID = false;
									getCheckingAccountBalance();
								});
						}
					}
				})
				.catch(function (err) {
					console.error(err);
				});

			/* Not used anymore (no active checkboxes)
			window.selectPay = function (index) {
				console.log(paymentTransactions[index]);
				let thisCB = document.getElementById('selectPay' + index);
				paymentTransactions[index].status = thisCB.checked ? "PAY" : "HOLD";
				let totalAmountToPay = 0;
				paymentTransactions.forEach(t => totalAmountToPay += (t.status == "PAY" ? t.payment : 0));
				document.getElementById('selectPayTotal').innerHTML = totalAmountToPay.toLocaleString('en-US', usd);
				let paySelectionNow = document.getElementById('paySelectionNow');
				if (totalAmountToPay > 0.001) paySelectionNow.classList.remove('disabled');
				else paySelectionNow.classList.add('disabled');
			} */

			if (window.analytics)
				window.analytics.track('Viewed Details', {
					userEmail: authedUser,
					workspace: client,
					userId: userData.userid,
				});
		});
	});

	const cn = document.getElementsByClassName('company-name');
	for (let i = 0; i < cn.length; i++) {
		cn[i].innerHTML = clientData.company_name;
	}

	paySelectionNow.classList.remove('disabled');
	// Process each payment sequentially
	function makePayments(token_id, token_value, accessCodeInput) {
		if (paymentIndex >= paymentTransactions.length) {
			finishPayments();
			return;
		}

		const indexedPayment = paymentTransactions[paymentIndex];

		let names = userData.user_name.split(' ');
		let lastName = names[names.length - 1];
		let firstName = userData.user_name
			.substring(0, userData.user_name.length - lastName.length - 1)
			.replace(/_/g, ' ')
			.replace(/,$/, ''); // use all names
		thisPayment = {
			achStatus: 'PAID',
			payer: userData.userid,
			id: Date.now().toString(),
			achId: indexedPayment.achId,
			recipient: indexedPayment.userId,
			recipientName: indexedPayment.userName,
			amount: (Math.round(indexedPayment.payment * 100) / 100).toString(),
			metadata: {
				reason: moment().subtract(1.5, 'M').format('[Q]Q_Y') + ' Quarterly Payment',
				amount: Number(thisPayment.amount).toLocaleString('en-US', usd),
				email: thisPayment.payer,
				first_name: firstName,
				last_name: lastName,
			},
		};

		/**** TESTING ****
		thisPayment.payer = "cmcconnell@nthround.com";	//userData.userid;
		if (thisPayment.recipient == "hildebrandj@jmsearch.com") thisPayment.recipient = "gmcconnell@nthround.com";
		if (thisPayment.recipient == "warrackj@jmsearch.com") thisPayment.recipient = "ahall@nthround.com";
		thisPayment.amount = "1.28";	//(Math.round(paymentTransactions[paymentIndex].payment * 100) / 100).toString();
		*/

		postPayNowViaDwollaProxy(thisPayment, token_id, token_value, accessCodeInput)
			.then((res) => {
				console.log(res);
				const newTransfer = res.data.transfer;
				const newTransferStatus = newTransfer.status;
				if (newTransferStatus == 'pending') {
					thisPayment.timeStamp = Date.now().toString();
					completedPayments.push(thisPayment);
					totalActuallyPaid += Number(thisPayment.amount);
					paymentTable += `
					<tr class="c16">
						<td class="c2" style="width: 250pt;">
							<p class="c3" style="text-align: left;"><span class="c3">${thisPayment.recipientName}</span></p>
						</td>
						<td class="c9" style="width: 100pt;">
							<p class="c3" style="text-align: right;"><span class="c3">${Number(thisPayment.amount).toLocaleString('en-US', usd)}</span></p>
						</td>
					</tr>
				`;
					// Check if elements exist before trying to access them
					const spinnerElement = document.getElementById('spinner' + paymentIndex);
					const paidElement = document.getElementById('paid' + paymentIndex);
					if (spinnerElement) spinnerElement.classList.add('hidden');
					if (paidElement) paidElement.classList.remove('hidden');
					paymentIndex++;
					if (paymentIndex < paymentTransactions.length) {
						makePayments(token_id, token_value, accessCodeInput);
					} else {
						// Note: finishPayments() will have to change because dwolla
						// it may be replaced by a webhook listener
						finishPayments();
					}
				} else {
					alert('Transfer failed to process. Please contact your NthRound customer service representative.');
					window.location.reload();
				}
				return;
			})
			.catch((err) => {
				console.error(err);
				document.body.style.cursor = 'default';
				document.body.style.pointerEvents = null;
				alert('Transfer failed to process. Please contact your NthRound customer service representative.');
				window.location.reload();
			});
		if (window.analytics)
			window.analytics.track('PAID for equity interest', {
				userEmail: authedUser,
				workspace: client,
			});
	}

	// After all payments are processed, change button to "Confirming..." and create confirmation
	function finishPayments() {
		console.log('Completed these payments: ', completedPayments);
		paySelectionNow.innerHTML = `Confirming&emsp;&emsp;
			<svg class="spinner" viewBox="0 0 50 50" style="position: relative; top: 8px;">
				<circle class="path" cx="25" cy="25" r="15" fill="none" stroke-width="5"></circle>
			</svg>`;
		paymentTable += `
			</tbody>
			<tfoot>
				<tr class="c16">
					<td class="c2" style="width: 250pt;">
						<p class="c3 c19"><span class="c3"></span></p>
					</td>
					<td class="c9" style="width: 100pt;">
						<p class="c3 c19"><span class="c3"></span></p>
					</td>
				</tr>
				<tr class="c16">
					<td class="c2" style="width: 250pt;">
						<p class="c3" style="text-align: left;"><span class="c7">Total:</span></p>
					</td><td class="c9" style="width: 100pt;">
						<p class="c3" style="text-align: right;"><span class="c7">${totalActuallyPaid.toLocaleString('en-US', usd)}</span></p>
					</td>
				</tr>
			</tfoot>
			</table>
		`;
		createConfirmation();
	}

	// PAY NOW button click handler
	if (!addedPayNowListenerAlready) {
		addedPayNowListenerAlready = true;
		paySelectionNow.addEventListener('click', function (e) {
			if (PREVIEW || !PAYING) return;

			// BUGFIX: Use textContent instead of innerHTML to avoid Materialize ripple div HTML
			// innerHTML includes child elements like <div class="waves-ripple">, which breaks string comparison
			const buttonTextUpper = paySelectionNow.textContent.trim().toUpperCase();

			if (buttonTextUpper === 'VIEW CONFIRMATION') {
				if (window.analytics)
					window.analytics.track('View Confirmation Button Clicked', {
						userEmail: authedUser,
						workspace: client,
					});
				if (confirmationId) {
					window.location.replace('/documents/view/?document=' + confirmationId);
				} else {
					console.warn('Button shows VIEW CONFIRMATION but confirmationId not set');
				}
				// BUGFIX: Return here to prevent fall-through to payment flow below
				// Without this, clicking "View Confirmation" would reopen the MFA modal
				return;
			} else {
				// Disable immediately to prevent double-clicks during async token API call
				paySelectionNow.classList.add('disabled');
				// First, create the access token. That can be reused for each payment API call.
				createDwollaNthApiTokenForPayNow()
					.then(function (createNthApiTokenRes) {
						const nthTokenApiBody = createNthApiTokenRes.data;
						const { token_id, token_value } = nthTokenApiBody;
						M.Modal.getInstance(document.getElementById('twoFactorModal')).open();

						// Make payment modal non-dismissible while 2FA modal is open (prevent ESC closing parent)
						const paymentModalInstance = M.Modal.getInstance(paymentDetailsModal);
						if (paymentModalInstance) {
							paymentModalInstance.options.dismissible = false;
						}

						//document.getElementById('twoFactorModal').style.display = "block";
						const enterCode = document.getElementById('enterCode');
						enterCode.disabled = true;
						const accessCode = document.getElementById('accessCode');

						// Clear the access code input for a fresh start
						accessCode.value = '';

						// Remove any existing event listeners by cloning the elements to prevent stacking
						const newAccessCode = accessCode.cloneNode(true);
						accessCode.parentNode.replaceChild(newAccessCode, accessCode);
						const newEnterCode = enterCode.cloneNode(true);
						enterCode.parentNode.replaceChild(newEnterCode, enterCode);

						// Now add fresh event listeners to the cloned elements
						const freshEnterCode = document.getElementById('enterCode');
						const freshAccessCode = document.getElementById('accessCode');

						freshEnterCode.disabled = true;

						// Enable button when 6 digits entered
						freshAccessCode.addEventListener('input', function (e) {
							if (this.value.length == 6) freshEnterCode.disabled = false;
							else freshEnterCode.disabled = true;
						});

						// Handle code submission
						freshEnterCode.addEventListener('click', function (e) {
							e.preventDefault();
							const accessCodeInput = freshAccessCode.value;
							// Close the MFA modal (Pay Now modal remains open)
							M.Modal.getInstance(document.getElementById('twoFactorModal')).close();

							// Make payment modal non-dismissible during processing
							const paymentModalInstance = M.Modal.getInstance(paymentDetailsModal);
							if (paymentModalInstance) {
								paymentModalInstance.options.dismissible = false;
							}

							// Disable the button on the Pay Now modal (keep text unchanged, just gray it out)
							paySelectionNow.classList.add('disabled');

							document.body.style.cursor = 'wait';
							document.body.style.pointerEvents = 'none';
							paymentTable = `<table class="c26" style="margin-left: 50pt; margin-right: 50pt;">
						<thead>
							<tr class="c16">
								<td class="c2" style="width: 250pt;">
									<p class="c3" style="text-align: left;"><span class="c22">Payment to:</span></p>
								</td>
								<td class="c9" style="width: 100pt;">
									<p class="c3" style="text-align: right;"><span class="c7">Amount:</span></p>
								</td>
							</tr>
							<tr class="c16">
								<td class="c2" style="width: 250pt;">
									<p class="c11"><span class="c7"></span></p>
								</td>
								<td class="c9" style="width: 100pt;">
									<p class="c19 c24"><span class="c7"></span></p>
								</td>
							</tr>
						</thead>
						<tbody>
					`;

							// Kicks off payment flow.
							makePayments(token_id, token_value, accessCodeInput);
						});
					})
					.catch(function (createNthApiTokenErr) {
						console.error(createNthApiTokenErr);
					});
			}
		});
	} // end addedPayNowListenerAlready guard

	const achAgreementModal = document.getElementById('achAgreementModal');
	const paymentDetailsModal = document.getElementById('paymentDetailsModal');
	achAgreementModal.innerHTML = achTerms;
	const ba = document.getElementsByClassName('bank-account');
	let userAchData = getUserDwollaData(userData);
	let userBankName = (userAchData && userAchData.primary_funding_source_bank_acct_name) || 'your bank account';
	for (let i = 0; i < ba.length; i++) ba[i].textContent = userBankName;

	// Initialize payment modal with callback to reset button if modal is closed during processing
	M.Modal.init(paymentDetailsModal, {
		onCloseEnd: function () {
			// Reset modal to dismissible state when closed (in case it was locked during processing)
			const paymentModalInstance = M.Modal.getInstance(paymentDetailsModal);
			if (paymentModalInstance) {
				paymentModalInstance.options.dismissible = true;
			}

			// If button is in "Processing" state when modal closes, reset it
			const buttonText = paySelectionNow.innerHTML;
			if (buttonText.includes('Processing') && !buttonText.toUpperCase().includes('VIEW CONFIRMATION')) {
				// User closed modal during payment - reset button to allow retry
				paySelectionNow.classList.remove('disabled');
				const userAchRecord = getUserDwollaData(userData);
				if (userAchRecord?.primary_funding_source_bank_acct_name && !PREVIEW) {
					paySelectionNow.innerHTML = 'Verify and PAY';
				} else {
					paySelectionNow.innerHTML = 'PAY NOW';
				}
				document.body.style.cursor = 'default';
				document.body.style.pointerEvents = null;
				console.log('🔄 Payment modal closed - button reset');
			}
		},
	});

	document.getElementById('authorizeAch').addEventListener('click', function (e) {
		M.Modal.getInstance(achAgreementModal).open();
		M.Modal.getInstance(paymentDetailsModal).close();
	});
	document.getElementById('achAgreementClose').addEventListener('click', function (e) {
		M.Modal.getInstance(achAgreementModal).close();
		M.Modal.getInstance(paymentDetailsModal).open();
	});

	// Create confirmation document for completed payments
	async function createConfirmation() {
		let date = moment();
		let quarter = moment().subtract(1.5, 'M').format('[Q]Q Y');
		let greeting = firstName(userData);
		let confirmationJson = {
			greeting: greeting,
			shareholder_name: userData.user_name.replace(/_/g, ' '),
			payment_date: date.format('MMMM D, YYYY'),
			current_quarter: quarter,
			payment_table: paymentTable,
		};

		let request = new XMLHttpRequest();
		let templateName = clientData.templates.find((t) => t.includes('NP_Capital_Call'));
		let templateUrl = await getContentUrl(templateName);
		fetch(templateUrl)
			.then(function (response) {
				console.log(response);
				return response.text();
			})
			.then(async function (html) {
				let htmlResults = html;
				// Replace template image URLs with presigned URLs via shared helper
				if (typeof rewriteTemplateImagesToPresigned === 'function') {
					htmlResults = await rewriteTemplateImagesToPresigned(htmlResults, (file) => `${templateName.substring(14, 99)}_${file}`);
				}
				// htmlResults = htmlResults.replace(/<body /, '<body style="width:600px; min-width:600px; max-width:600px;" ');
				Object.keys(confirmationJson).forEach(
					(e) =>
						(htmlResults = htmlResults
							.replace(new RegExp('{' + e + '}', 'g'), confirmationJson[e])
							.replace(new RegExp('{<span.*class=SpellE>' + e + '</span>}', 'g'), '<span class=SpellE>' + confirmationJson[e] + '</span>'))
				);
				generateConfirmation(htmlResults);
			})
			.catch(function (err) {
				// There was an error
				console.warn('Something went wrong.', err);
			});

		function generateConfirmation(htmlResults) {
			console.log('Creating confirmation PDF');
			let fileName = 'Payment Confirmation for ' + quarter;
			document.dispatchEvent(new CustomEvent('ConvertToPDF', { detail: { htmlResults: htmlResults, fileName: fileName, investor: userData.userid } }));
			if (!addedResultantPDFListenerAlready) {
				addedResultantPDFListenerAlready = true;
				document.addEventListener('ResultantPDF', function (ev) {
					// Set confirmationId for later use when viewing the confirmation
					confirmationId = Date.now().toString();
					uploadToYourDocs(ev.detail.url);

					function uploadToYourDocs(pdfUrl) {
						// Generate unique filename fresh on each execution to prevent closure bug
						// where stale filename from first call would be reused for subsequent calls
						let awsFileToUpload = cleanStampedName(fileName) + '.pdf';

						let newDoc = {
							name: fileName,
							description: totalActuallyPaid.toLocaleString('en-US', usd),
							require2Factor: false,
							type: 'Statement',
							tag: 'NP',
							url: awsFileToUpload,
							date: date.format('MMM D, YYYY'),
							id: confirmationId,
						};

						let holderData = userData.clients[client];
						if (!holderData.documents) {
							holderData.documents = [];
						}
						holderData.documents.push(newDoc);
						// update all session data
						userData.clients[client] = holderData;
						sessionStorage.setItem('user.Data', JSON.stringify(userData));

						axios
							.post(
								postURL,
								JSON.stringify({
									eventName: 'index.payment_confirmation_document_generated',
									investor: userData.userid,
									userid: authedUser,
									client: client,
									cookie: cookie,
									clients: {
										[client]: {
											documents: holderData.documents,
										},
									},
									userid: userData.userid,
								})
							)
							.then(function (res) {
								// needed for doc
								refreshCapTable();

								// 3) UPLOAD PDF AS NEW DOC
								fetch(pdfUrl)
									.then(function (response) {
										return response.blob();
									})
									.then(function (pdfFile) {
										axios
											.get(authUploadUrl, {
												params: {
													filename: client + '/' + userData.userid + '/' + awsFileToUpload,
													client: client,
													userid: authedUser,
													cookie: cookie,
												},
											})
											.then(function (res1) {
												let formData = secureFormData(res1);
												formData.append('file', pdfFile);
												axios
													.post(res1.data.url, formData, axconf)
													.then(function (res2) {
														//console.log(res2);
														// Re-enable button and change to "VIEW CONFIRMATION"
														document.body.style.cursor = 'default';
														document.body.style.pointerEvents = null;
														paySelectionNow.classList.remove('disabled');
														paySelectionNow.innerHTML = 'VIEW CONFIRMATION';

														let payOnlineBtn = document.querySelector('.payOnlineNowNP');
														if (payOnlineBtn) {
															payOnlineBtn.classList.add('disabled');
															payOnlineBtn.innerHTML = 'Paid - Thank you!';
														}
													})
													.catch(function (err2) {
														console.error(err2);
													});
											})
											.catch(function (err1) {
												console.error(err1);
											});

										if (window.analytics)
											window.analytics.track('Payment Confirmation Added', {
												userEmail: authedUser,
												workspace: client,
												documentTitle: newDoc.name,
												documentFileType: newDoc.url.split('.').pop(),
											});
									});
							})
							.catch(function (res) {
								console.log(res);
							});
					}

					// WHAT IS THIS???
					return;
					window.open(ev.detail.url);

					// DOWNLOAD PDF FROM URL
					request = new XMLHttpRequest();
					request.open('GET', ev.detail.url, true);
					request.responseType = 'blob';
					request.onload = function () {
						saveAs(ev.detail.url, cleanStampedName(fileName)); //Date.now() + "_" + fileName.replace(/[,\s]/g, '_'));
						M.Modal.getInstance(document.getElementById('statementWaitState')).close();
						M.Modal.getInstance(document.getElementById('viewCapAcctModal')).open();
					};
					request.send();

					if (window.analytics)
						window.analytics.track('Generated Confirmation', {
							userEmail: authedUser,
							workspace: client,
							documentTitle: fileName,
						});
				});
			}
		}
	}
}

function generateColorPalette(numClasses) {
	/* NOTES
	Generates an array of distinct colors for multi-line portfolio graph
	Returns array of hex color codes
	*/
	// Base color palette from nthColor
	let baseColors = ['#4eb5ac', '#a6dad5', '#6bc4ba', '#85d0c9', '#3da59d', '#2d8b84'];

	// If we need more colors than available, generate variations
	let colors = [];
	for (let i = 0; i < numClasses; i++) {
		if (i < baseColors.length) {
			colors.push(baseColors[i]);
		} else {
			// Generate additional colors by adjusting hue
			let baseIndex = i % baseColors.length;
			colors.push(baseColors[baseIndex]);
		}
	}

	return colors;
}

function addStatements() {
	/* NOTES
	This method creates the graph tooltip (mini-account-statement)
	Importantly, it also compiles cumulative summaries:
	 - tvpiContributions
	 - tvpiDistributions
	 - latestTVPI
	 These are required for headline values
	*/
	let lastStmt;
	tvpiContributions = 0;
	tvpiDistributions = 0;
	let showTVPI = document.getElementById('showTVPI');

	function acctFormat(n) {
		return n < 0 ? '(' + Math.abs(n).toLocaleString('en-US', m0) + ')' : n.toLocaleString('en-US', m0);
	}

	graphArray.forEach(function (row, index) {
		let date = new Date(row[0] - 432000000);
		let year = date.getFullYear();
		let month = date.getMonth();
		let qtr = '';
		if (month < 3) qtr = year + ' Q1';
		else if (month < 6) qtr = year + ' Q2';
		else if (month < 9) qtr = year + ' Q3';
		else if (month < 12) qtr = year + ' Q4';
		// console.log(qtr);
		let stmt = statements.find((s) => s.qtr == qtr);

		// console.log(stmt);
		let toolTable = ``;
		if (!stmt) {
			toolTable = `
			<table style="width: 140px; border-collapse: separate; border-spacing: 10px;">
				<tr></tr>
				<tr style="line-height: 8px;">
					<td style="padding: 0;">No data for this quarter</td>
				</tr>
				<tr></tr>
			</table>
			`;
		} else {
			if (!isJMH) {
				toolTable = `
				<table style="width: 210px; border-collapse: separate; border-spacing: 10px;">
					<tr style="line-height: 8px;">
						<td style="padding: 0; font-weight: bold;">Begining Balance</td>
						<td style="padding: 0; font-weight: bold;" class="right-align">${acctFormat(stmt.beg)}</td>
					</tr>
					<tr></tr>
					<tr style="line-height: 8px;">
						<td style="padding: 0;">Contributions</td>
						<td style="padding: 0;" class="right-align">${acctFormat(stmt.cont)}</td>
					</tr>
					<tr style="line-height: 8px;">
						<td style="padding: 0;">Distributions</td>
						<td style="padding: 0;" class="right-align">${acctFormat(stmt.dist)}</td>
					</tr>
					<tr></tr>
					<tr style="line-height: 8px;">
						<td style="padding: 0;">Management Fees</td>
						<td style="padding: 0;" class="right-align">${acctFormat(stmt.mgmt)}</td>
					</tr>
					<tr style="line-height: 8px;">
						<td style="padding: 0;">Operating Gain (Loss)</td>
						<td style="padding: 0;" class="right-align">${acctFormat(stmt.oper)}</td>
					</tr>
					<tr style="line-height: 8px;">
						<td style="padding: 0;">Syndication Costs</td>
						<td style="padding: 0;" class="right-align">${acctFormat(stmt.synd)}</td>
					</tr>
					<tr></tr>
					<tr style="line-height: 8px;">
						<td style="padding: 0;">Realized Gain (Loss)</td>
						<td style="padding: 0;" class="right-align">${acctFormat(stmt.real)}</td>
					</tr>
					<tr style="line-height: 8px;">
						<td style="padding: 0;">Unrealized Gain (Loss)</td>
						<td style="padding: 0;" class="right-align">${acctFormat(stmt.unre)}</td>
					</tr>
					<tr></tr>
					<tr style="line-height: 8px;">
						<td style="padding: 0; font-weight: bold;">Ending Balance</td>
						<td style="padding: 0; font-weight: bold;" class="right-align">${acctFormat(stmt.end)}</td>
					</tr>
				</table>
				`;
			}

			if (index == 0 && stmt != statements[0]) {
				tvpiContributions += statements[0].cont;
				tvpiDistributions += statements[0].dist;
			}
			if (stmt != lastStmt) {
				tvpiContributions += stmt.cont;
				tvpiDistributions += stmt.dist;
			}
			lastStmt = stmt;

			let netTVPI = stmt.end / tvpiContributions;
			latestTVPI = (stmt.end + tvpiDistributions) / tvpiContributions;

			if (showTVPI && showTVPI.checked) {
				graphArray[index][1] = netTVPI;
				graphArray[index][3] = latestTVPI;
			} else {
				graphArray[index][1] = stmt.end;
				graphArray[index][3] = stmt.end + tvpiDistributions;
			}
		}
		if (!isJMH) row.push(toolTable);
	});

	if (!isJMH) {
		let newGraphArray = graphArray.filter((x) => !x[4].includes('No data for this quarter'));
		if (newGraphArray.length > 2) graphArray = newGraphArray;
		else
			graphArray = graphArray.map((row) => {
				return row.slice(0, -1);
			});
	}

	// Is this really needed???
	let userStatemntsTVPI = document.getElementById('userStatemntsTVPI');
	if (userStatemntsTVPI)
		userStatemntsTVPI.innerHTML = latestTVPI == 'Infinity' ? "<span style='font-size: 2rem; line-height: 120%;'>∞</span>" : latestTVPI.toFixed(2);
}

const chart_div = document.getElementById('chart_div');
function drawChart() {
	// graphArray[date, price, volume, referencePrice]
	// CONTRACT_SYMBOL = symbol;

	if (graphArray.length > 0) {
		chart_div.style.display = 'block';
	} else {
		// TODO: Add graph empty state
		chart_div.style.display = 'none';
		return;
	}

	// isVMS = ["onwardpe","activatecp"].includes(client);
	let itsVMS = isVMS && graphArray[0].length > 4;
	let showTVPI = document.getElementById('showTVPI');
	let showTooltip = document.getElementById('showTooltip');
	let showIrrElement = document.getElementById('showIrrElement');
	let showTvpiElement = document.getElementById('showTvpiElement');
	let showTooltipElement = document.getElementById('showTooltipElement');

	if (HAS_STMTS) showTVPI.style.display = 'block';
	else showTVPI.style.display = 'none';

	if (itsVMS) showTooltip.style.display = 'block';
	else showTooltip.style.display = 'none';

	// Calculate price change of last and penultimate trades
	// 7-6-2022 Can't do math with strings!!
	// let last = HAS_STMTS && latestTVPI ? latestTVPI.toFixed(2) : graphArray[graphArray.length - 1][1].toFixed(2);
	let isPortfolioMultiLine = !symbol && window.portfolioClasses && window.portfolioClasses.length > 0;
	let last, prior;

	if (isPortfolioMultiLine) {
		// For portfolio multi-line, calculate average share price across all classes
		let lastRow = graphArray[graphArray.length - 1];
		let sum = 0;
		let count = 0;
		for (let i = 1; i < lastRow.length; i++) {
			if (lastRow[i] !== null) {
				sum += lastRow[i];
				count++;
			}
		}
		last = count > 0 ? sum / count : 0;

		// Calculate prior average value
		let priorRow = graphArray[0];
		if (graphArray.length > 1) {
			let offset = 2;
			let lastTime = graphArray[graphArray.length - 1][0];
			while (lastTime - graphArray[graphArray.length - offset][0] < 1000 && offset <= graphArray.length) offset++;
			priorRow = graphArray[graphArray.length - offset];
		}
		let priorSum = 0;
		let priorCount = 0;
		for (let i = 1; i < priorRow.length; i++) {
			if (priorRow[i] !== null) {
				priorSum += priorRow[i];
				priorCount++;
			}
		}
		prior = priorCount > 0 ? priorSum / priorCount : 0;
	} else {
		last = HAS_STMTS && latestTVPI ? latestTVPI : graphArray[graphArray.length - 1][1];
		prior = graphArray[0][1]; //default
		if (graphArray.length > 1) {
			// with graph shading, might need to look back further
			let offset = 2;
			let lastTime = graphArray[graphArray.length - 1][0];
			while (lastTime - graphArray[graphArray.length - offset][0] < 1000 && offset <= graphArray.length) offset++;
			prior = graphArray[graphArray.length - offset][1];
		}
	}
	let diff = last - prior;
	let percentChange = Math.round((diff / prior) * 100, 0);
	last = last > 999 ? last.toLocaleString() : last.toFixed(2);
	// last = last > 999 ? "$&nbsp;" + last.toLocaleString() : last.toFixed(2);

	// DRAGONS STARTING TO GROW HERE ****
	let showAbbreviations = false; //SHOW_REF && EQUITY_LABELS.shareAbbrev && EQUITY_LABELS.shareAbbrev.length > 0;
	let abbrevString = 'price (' + EQUITY_LABELS.shareAbbrev + '): $';
	let suffixString = ' (' + (diff == 0 ? 'no change)' : (diff > 0 ? 'up ' : 'down ') + percentChange + '%)');
	if (!EQUITY_LABELS.priceLabel.startsWith('price') || isAlera) suffixString = '';
	let prefixString = showAbbreviations ? abbrevString : EQUITY_LABELS.priceLabel;

	// Changed from 5 to 15 for OnwardPE -- another HAIR TRIGGER
	// Changed from 15 to 10 for MH
	// Try using midpoint instead of last
	// I'm afraid that we are going to end up needing a flag here... although can't we use language.label_tvpi = %contrib ???
	// For orrick added TVPI condition.  Feels like we can get rid of all this mess with language.label_tvpi.
	if (labelTVPI != 'TVPI') {
		let mid = graphArray[Math.floor(graphArray.length / 2)][1];
		if (EQUITY_LABELS.priceLabel.trim().startsWith('price') && !prefixString.trim().endsWith('$')) {
			prefixString = prefixString + '$';
			EQUITY_LABELS.priceLabel = EQUITY_LABELS.priceLabel + '$';
		} else if (UFL && !isCER && ((mid > 10 && mid < 1000) || is3MG) && !EQUITY_LABELS.priceLabel.startsWith('%'))
			EQUITY_LABELS.priceLabel = '%' + EQUITY_LABELS.priceLabel;
	}

	// Skip price label updates if template system handled the headline. Same
	// reasoning as the buildValuationArray skip-guard: clients driving the
	// headline via class_headline_template only (Cerity) used to get the
	// subtitle clobbered because the check looked at headline_template alone.
	const _templateInControl =
		clientData.language?.headline_template || clientData.language?.class_headline_template || clientData.equity?.[symbol]?.class_headline_template;
	if (!_templateInControl) {
		document.getElementById('lastTraded').innerHTML =
			prefixString + (last == 'Infinity' ? "<span style='font-size: 1.5rem; vertical-align: middle;'> ∞</span>" : last) + suffixString;
		if (isPSS) {
			const latestValuation = Object.values(clientData.equity)
				.flatMap((e) => e.valuations)
				.sort((a, b) => b.date - a.date)[0];
			document.getElementById('lastTraded').innerHTML = 'valuation: $' + Number(latestValuation.sharePrice).toLocaleString('en-US', m2) + ' per unit';
		}
		// Handle portfolio graph mode
		let preferPortfolioGraph = clientData.flags?.prefer_portfolio_graph;
		if (!symbol && preferPortfolioGraph) {
			document.getElementById('lastTraded').innerHTML = 'Your equity portfolio performance';
		}
		// Handle time series headline
		if (window.isPortfolioTimeSeries) {
			document.getElementById('lastTraded').innerHTML = 'Your portfolio value over time';
		}
	}

	//let lastRequestPrice = graphArray[graphArray.length - 1][1];
	let loValues, hiValues;
	if (window.isIncomeAllocationsDualLine) {
		// For dual-line mode, get both Tax Basis (col 1) and Market Value (col 2) values
		// graphArray format: [[timestamp, taxBasis, marketValue, tooltip], ...]
		let taxBasisValues = graphArray.map((row) => row[1]).filter((v) => v !== null && v !== undefined);
		let marketValues = graphArray.map((row) => row[2]).filter((v) => v !== null && v !== undefined);
		loValues = [...taxBasisValues, ...marketValues];
		hiValues = loValues;
	} else if (window.isPortfolioTimeSeries) {
		// For time series, values are in column 1 (single value per row)
		loValues = graphArray.map((row) => row[1]).filter((v) => v !== null && v !== undefined);
		hiValues = loValues;
	} else if (window.isPortfolioTimeSeriesStacked) {
		// For stacked time series, sum all class values per row to get the total height
		loValues = graphArray.map((row) => {
			let sum = 0;
			for (let i = 1; i < row.length; i++) {
				if (row[i] !== null && row[i] !== undefined) sum += row[i];
			}
			return sum;
		});
		hiValues = loValues;
	} else if (window.isValueCompositionGraph || isPortfolioMultiLine) {
		// For portfolio multi-line or value composition, get all values from all class columns
		loValues = [];
		graphArray.forEach((row) => {
			for (let i = 1; i < row.length; i++) {
				if (row[i] !== null) loValues.push(row[i]);
			}
		});
		hiValues = loValues; // No separate high values for portfolio mode
	} else {
		loValues = graphArray.map((x) => x[1]);
		hiValues = graphArray.map((x) => x[3]);
	}
	let loMax = Math.max(...loValues);
	let hiMax = Math.max(...hiValues);

	let scaler = 1;
	// Use millions for values >= $1M (so $1000k becomes $1M)
	if (loMax >= 1000000 || hiMax >= 1000000) scaler = 1000000;
	else if (loMax > 1000 || hiMax > 1000) scaler = 1000;
	// else if (loMax > 2000 || hiMax > 2000) scaler = 2000;

	loMax /= scaler;
	hiMax /= scaler;

	// let theMax = Math.max(loMax, hiMax) * (1 + (loMax > 100 ? 0.2 : (loMax > 10 ? 0.25 : 0.3)));

	// Nice ticks helper: 1, 2, 2.5, 5, 10 × 10^k
	function niceStep(step) {
		const exp = Math.floor(Math.log10(step));
		const base = Math.pow(10, exp);
		const frac = step / base;
		const niceFrac = frac <= 1 ? 1 : frac <= 2 ? 2 : frac <= 2.5 ? 2.5 : frac <= 5 ? 5 : 10;
		return niceFrac * base;
	}

	// Compute a "nice" axis max (and step) that always covers dataMax.
	// Set desiredTicks to how many labeled ticks you want INCLUDING zero.
	function niceAxisMax(loMax, hiMax, desiredTicks = 7, addHeadroom = false) {
		const dataMax = Math.max(loMax, hiMax);
		// If you sometimes start Y at something other than 0, adapt this.
		const rawStep = dataMax / Math.max(1, desiredTicks - 1);
		const step = niceStep(rawStep);
		let axisMax = Math.ceil(dataMax / step) * step;
		// Add headroom above the highest data point so it doesn't touch the top
		if (addHeadroom) {
			axisMax += step * 1.5; // Add 1.5 ticks worth of headroom for breathing room
		}
		return { max: axisMax, step };
	}

	// ==== usage (your existing variables) ====
	// Add headroom for value composition graphs and stacked time series so data doesn't touch the top
	const needsHeadroom = window.isValueCompositionGraph || window.isPortfolioTimeSeriesStacked || window.isClassTimeSeries;
	const { max: theMax } = niceAxisMax(loMax, hiMax, 7, needsHeadroom); // e.g., 7 ticks: 0, 20, 40, ... 120, 140

	let theMin = Math.min(Math.min(...loValues), Math.min(...hiValues)) / scaler;
	let margin = scaler > 1 || loMax < 2000 ? 100 : loMax > 100 ? 68 : loMax < 4 ? 72 : 60;
	if (HAS_STMTS) margin = showTVPI.checked ? 84 : 102;
	// let theTicks = [...Array(Math.ceil(theMax)).keys()].map(x => x + 1);
	let viewMax = Math.max(...graphArray.map((x) => x[2])) * 5;
	// get rid of little hash marks in middle of no-volume charts
	let SHOW_VOL = true;
	if (viewMax == 0) {
		viewMax = 100;
		if (graphArray.length > 1) SHOW_VOL = false;
	}
	let timeSpan = graphArray[graphArray.length - 1][0] - graphArray[0][0];
	let SHOW_MONTH = timeSpan < 150000000000;
	let elemSize = chart_div.getBoundingClientRect();
	let graphHeight = elemSize.height;
	let graphWidth = elemSize.width;
	// let ONE_LINE = true;
	// Object.keys(graphArray).forEach(e => {if (graphArray[e][1] != graphArray[e][3]) {ONE_LINE = false; return;}});
	let graphData;
	// Handle PORTFOLIO TIME SERIES graph mode (single-line showing total value over time)
	let isPortfolioTimeSeries = window.isPortfolioTimeSeries && graphArray.length > 0;
	if (isPortfolioTimeSeries) {
		// Build column definitions for single line time series
		let columns = [
			{
				label: 'Date',
				type: 'date',
			},
			{
				label: 'Portfolio Value',
				type: 'number',
			},
		];

		// Map data rows - graphArray format: [[timestamp_ms, value, 0, 0], ...]
		let dataRows = graphArray
			.filter((row) => row[0] && !isNaN(row[0]) && row[0] > 0)
			.map(function (row) {
				let timestamp = Number(row[0]);
				let momentDate = moment(timestamp).utc();
				let dateObj = new Date(Date.UTC(momentDate.year(), momentDate.month(), momentDate.date(), 12, 0, 0));

				if (isNaN(dateObj.getTime())) {
					console.error('[TimeSeries] Invalid date from timestamp:', timestamp);
					return null;
				}

				let value = row[1] !== null && row[1] !== undefined ? row[1] / scaler : 0;
				return [dateObj, value];
			})
			.filter((row) => row !== null);

		if (dataRows.length === 0) {
			console.error('[TimeSeries] No valid data rows for time series graph');
			chart_div.style.display = 'none';
			return;
		}

		try {
			graphData = google.visualization.arrayToDataTable([columns].concat(dataRows));
		} catch (error) {
			console.error('[TimeSeries] Error creating graphData:', error);
			chart_div.style.display = 'none';
			return;
		}
	}
	// Handle STACKED TIME SERIES graph mode (stacked area showing value by share class over time)
	else if (window.isPortfolioTimeSeriesStacked && window.portfolioClasses?.length > 0) {
		// Build column definitions: Date + (value + tooltip) for each share class
		// Each series needs its own tooltip column for hovering over any stacked area
		let columns = [
			{
				label: 'Date',
				type: 'date',
			},
		];

		// Add columns for each share class, each followed by its tooltip
		window.portfolioClasses.forEach(function (className) {
			let displayName = clientData.equity?.[className]?.name;
			let label = displayName || capitalize(className) || className || 'Unknown';
			columns.push({
				label: label,
				type: 'number',
			});
			// Add tooltip column after each data column
			columns.push({
				type: 'string',
				role: 'tooltip',
				p: { html: true },
			});
		});

		// Map data rows - graphArray format: [[timestamp_ms, class1Val, class2Val, ...], ...]
		// Data row structure: [Date, class1Value, tooltip1, class2Value, tooltip2, ...]
		let dataRows = graphArray
			.filter((row) => row[0] && !isNaN(row[0]) && row[0] > 0)
			.map(function (row) {
				let timestamp = Number(row[0]);
				let momentDate = moment(timestamp).utc();
				let dateObj = new Date(Date.UTC(momentDate.year(), momentDate.month(), momentDate.date(), 12, 0, 0));

				if (isNaN(dateObj.getTime())) {
					console.error('[StackedTimeSeries] Invalid date from timestamp:', timestamp);
					return null;
				}

				// Build tooltip once for this row (all series show same full breakdown)
				let tooltipHtml = buildStackedTimeSeriestooltip(row, window.portfolioClasses, clientData);

				let mappedRow = [dateObj];
				// Add value + tooltip for each share class
				for (let i = 1; i < row.length; i++) {
					let value = row[i] !== null && row[i] !== undefined ? row[i] / scaler : 0;
					mappedRow.push(value);
					mappedRow.push(tooltipHtml);
				}
				return mappedRow;
			})
			.filter((row) => row !== null);

		if (dataRows.length === 0) {
			console.error('[StackedTimeSeries] No valid data rows for stacked time series graph');
			chart_div.style.display = 'none';
			return;
		}

		try {
			graphData = google.visualization.arrayToDataTable([columns].concat(dataRows));
		} catch (error) {
			console.error('[StackedTimeSeries] Error creating graphData:', error);
			chart_div.style.display = 'none';
			return;
		}

		console.log('[StackedTimeSeries] Built graph with', dataRows.length, 'rows,', window.portfolioClasses.length, 'classes');
	}
	// Handle SINGLE CLASS TIME SERIES graph mode (single area showing class value over time)
	// Or DUAL LINE mode for income allocations (Tax Basis + Market Value)
	else if (window.isClassTimeSeries && graphArray.length > 0) {
		let columns, dataRows;

		if (window.isIncomeAllocationsDualLine) {
			// Dual-line mode: Tax Basis + Market Value with separate tooltips
			// graphArray format: [[timestamp_ms, taxBasis, marketValue, breakdownData], ...]
			columns = [
				{
					label: 'Date',
					type: 'date',
				},
				{
					label: 'Tax Basis',
					type: 'number',
				},
				{
					type: 'string',
					role: 'tooltip',
					p: { html: true },
				},
				{
					label: 'Market Value',
					type: 'number',
				},
				{
					type: 'string',
					role: 'tooltip',
					p: { html: true },
				},
			];

			const formatDualCurrency = (val) => '$' + val.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });

			dataRows = graphArray
				.filter((row) => row[0] && !isNaN(row[0]) && row[0] > 0)
				.map(function (row) {
					let timestamp = Number(row[0]);
					let momentDate = moment(timestamp).utc();
					let dateObj = new Date(Date.UTC(momentDate.year(), momentDate.month(), momentDate.date(), 12, 0, 0));

					if (isNaN(dateObj.getTime())) {
						console.error('[DualLineTimeSeries] Invalid date from timestamp:', timestamp);
						return null;
					}

					let taxBasisRaw = row[1] !== null && row[1] !== undefined ? row[1] : 0;
					let marketValueRaw = row[2] !== null && row[2] !== undefined ? row[2] : null;
					let breakdown = row[3] || {};
					let taxBasis = taxBasisRaw / scaler;
					let marketValue = marketValueRaw !== null ? marketValueRaw / scaler : null;

					let dateStr = new Date(timestamp).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });

					// Tax Basis tooltip with full breakdown
					let taxBasisTooltip = `<div style="padding:10px;min-width:180px;">
						<div style="font-weight:bold;color:${nthColor[0]};margin-bottom:4px;">Tax Basis</div>
						<div style="color:#666;font-size:0.9em;margin-bottom:8px;">${dateStr}</div>
						<div style="font-size:0.9em;color:#666;margin-bottom:6px;">
							Contributions: ${formatDualCurrency(breakdown.contributions || 0)}<br/>
							+ Income Allocations: ${formatDualCurrency(breakdown.incomeAllocations || 0)}<br/>
							− Distributions: ${formatDualCurrency(breakdown.distributions || 0)}
						</div>
						<div style="border-top:1px solid #ddd;padding-top:6px;font-size:1.1em;font-weight:bold;">
							${formatDualCurrency(taxBasisRaw)}
						</div>
					</div>`;

					// Market Value tooltip with gain/loss
					let marketValueTooltip =
						marketValueRaw !== null
							? `<div style="padding:10px;min-width:180px;">
						<div style="font-weight:bold;color:${stdColor[3]};margin-bottom:4px;">Market Value</div>
						<div style="color:#666;font-size:0.9em;margin-bottom:8px;">${dateStr}</div>
						<div style="font-size:1.1em;font-weight:bold;margin-bottom:6px;">${formatDualCurrency(marketValueRaw)}</div>
						${
							taxBasisRaw > 0
								? `<div style="border-top:1px solid #ddd;padding-top:6px;color:${marketValueRaw >= taxBasisRaw ? '#2d8b84' : '#e63946'};">
							Unrealized ${marketValueRaw >= taxBasisRaw ? 'Gain' : 'Loss'}: ${formatDualCurrency(Math.abs(marketValueRaw - taxBasisRaw))}
						</div>`
								: ''
						}
					</div>`
							: '';

					return [dateObj, taxBasis, taxBasisTooltip, marketValue, marketValueTooltip];
				})
				.filter((row) => row !== null);

			console.log('[DualLineTimeSeries] Built graph with', dataRows.length, 'rows (Tax Basis + Market Value)');
		} else {
			// Single-line mode: just class value
			// graphArray format: [[timestamp_ms, value, tooltip/0, 0], ...]
			columns = [
				{
					label: 'Date',
					type: 'date',
				},
				{
					label: className || 'Value',
					type: 'number',
				},
				{
					type: 'string',
					role: 'tooltip',
					p: { html: true },
				},
			];

			dataRows = graphArray
				.filter((row) => row[0] && !isNaN(row[0]) && row[0] > 0)
				.map(function (row) {
					let timestamp = Number(row[0]);
					let momentDate = moment(timestamp).utc();
					let dateObj = new Date(Date.UTC(momentDate.year(), momentDate.month(), momentDate.date(), 12, 0, 0));

					if (isNaN(dateObj.getTime())) {
						console.error('[ClassTimeSeries] Invalid date from timestamp:', timestamp);
						return null;
					}

					let value = row[1] !== null && row[1] !== undefined ? row[1] / scaler : 0;
					// Use custom tooltip from row[2] if provided (e.g., income allocations breakdown)
					// Otherwise fall back to generic tooltip builder
					let tooltipHtml = row[2] && typeof row[2] === 'string' && row[2].includes('<div') ? row[2] : buildClassTimeSeriestooltip(timestamp, row[1]);
					return [dateObj, value, tooltipHtml];
				})
				.filter((row) => row !== null);

			console.log('[ClassTimeSeries] Built graph with', dataRows.length, 'rows for class:', className);
		}

		if (dataRows.length === 0) {
			console.error('[ClassTimeSeries] No valid data rows for class time series graph');
			chart_div.style.display = 'none';
			return;
		}

		try {
			graphData = google.visualization.arrayToDataTable([columns].concat(dataRows));
		} catch (error) {
			console.error('[ClassTimeSeries] Error creating graphData:', error);
			chart_div.style.display = 'none';
			return;
		}
	}
	// Handle VALUE COMPOSITION graph mode (2-bar comparison: Contributions vs Total Value)
	else if (window.isValueCompositionGraph && window.portfolioClasses) {
		// Build column definitions - category label + stacked value columns with tooltips and annotations
		let columns = [
			{
				label: 'Category',
				type: 'string',
			},
		];

		// Columns: Contributions, then each equity class (each with tooltip + annotation)
		// Note: Distributions are now included in each class's total value, not separate
		columns.push({ label: 'Contributions', type: 'number' });
		columns.push({ type: 'string', role: 'tooltip', p: { html: true } });
		columns.push({ type: 'string', role: 'annotation' });
		window.portfolioClasses.forEach(function (className) {
			let displayName = clientData.equity?.[className]?.name;
			let label = displayName || capitalize(className) || className || 'Unknown';
			columns.push({ label: label, type: 'number' });
			columns.push({ type: 'string', role: 'tooltip', p: { html: true } });
			columns.push({ type: 'string', role: 'annotation' });
		});

		// Build tooltip values object for a row using classDetails for breakdown
		let buildTooltipValues = function (row) {
			let values = {
				contributions: row[1] || 0,
				classes: [],
			};
			// Use classDetails for detailed breakdown (includes distributions per class)
			if (window.portfolioClassDetails) {
				window.portfolioClassDetails.forEach(function (detail, i) {
					let displayName = clientData.equity?.[detail.classKey]?.name || detail.className;
					values.classes.push({
						name: displayName,
						value: detail.totalValue || 0,
						capitalAccount: detail.capitalAccount || 0,
						distributions: detail.distributions || 0,
						returnsOfCapital: detail.returnsOfCapital || 0,
					});
				});
			} else {
				// Fallback: use row values directly (legacy path)
				for (let i = 0; i < window.portfolioClasses.length; i++) {
					let className = window.portfolioClasses[i];
					let displayName = clientData.equity?.[className]?.name || className;
					values.classes.push({
						name: displayName,
						value: row[i + 2] || 0,
					});
				}
			}
			return values;
		};

		// Scale data values and add tooltips (no annotations for this graph type)
		let dataRows = graphArray.map(function (row) {
			let category = row[0]; // Category label (string)
			let tooltipValues = buildTooltipValues(row);
			let tooltipHtml = buildValueCompositionTooltip(category, tooltipValues);

			let mappedRow = [category];
			// For each numeric value, add scaled value, tooltip, and empty annotation
			for (let i = 1; i < row.length; i++) {
				let rawValue = row[i] !== null && row[i] !== undefined ? row[i] : 0;
				let scaledValue = rawValue / scaler;
				mappedRow.push(scaledValue);
				mappedRow.push(tooltipHtml); // Same tooltip for all segments in stacked bar
				mappedRow.push(''); // No annotations - tooltip provides the breakdown
			}
			return mappedRow;
		});

		if (dataRows.length === 0) {
			console.error('No valid data rows for value composition graph');
			chart_div.style.display = 'none';
			return;
		}

		try {
			graphData = google.visualization.arrayToDataTable([columns].concat(dataRows));
		} catch (error) {
			console.error('Error creating value composition graphData:', error);
			console.error('Stack:', error.stack);
			chart_div.style.display = 'none';
			return;
		}
	}
	// Handle portfolio multi-line mode (isPortfolioMultiLine declared earlier in function)
	else if (isPortfolioMultiLine) {
		// Build column definitions dynamically
		let columns = [
			{
				label: 'Date',
				type: 'date',
			},
		];

		// Add a column for each equity class (use display name from equity config if available)
		window.portfolioClasses.forEach(function (className) {
			let displayName = clientData.equity?.[className]?.name;
			let label = displayName || capitalize(className) || className || 'Unknown';
			columns.push({
				label: label,
				type: 'number',
			});
		});

		// Map data rows to include dates
		let dataRows = graphArray
			.filter((row) => row[0] && !isNaN(row[0]) && row[0] > 0) // Filter invalid timestamps
			.map(function (row) {
				let timestamp = Number(row[0]);
				// Timestamp is already normalized to midnight UTC, create Date at noon UTC
				// Noon prevents timezone boundary issues while keeping the correct calendar date
				let momentDate = moment(timestamp).utc();
				let dateObj = new Date(Date.UTC(momentDate.year(), momentDate.month(), momentDate.date(), 12, 0, 0));

				// Validate the Date object is valid
				if (isNaN(dateObj.getTime())) {
					console.error('Invalid date created from timestamp:', timestamp);
					return null;
				}

				let mappedRow = [dateObj];
				for (let i = 1; i < row.length; i++) {
					mappedRow.push(row[i] ? row[i] / scaler : null);
				}
				return mappedRow;
			})
			.filter((row) => row !== null); // Remove any null rows

		// Check if we have valid data
		if (dataRows.length === 0) {
			console.error('No valid data rows for portfolio graph');
			chart_div.style.display = 'none';
			return;
		}

		try {
			graphData = google.visualization.arrayToDataTable([columns].concat(dataRows));
		} catch (error) {
			console.error('Error creating graphData:', error);
			console.error('Stack:', error.stack);
			chart_div.style.display = 'none';
			return;
		}
	} else if (itsVMS)
		graphData = google.visualization.arrayToDataTable(
			[
				[
					{
						label: 'Year', //"Month",
						type: SHOW_VOL ? 'number' : 'date',
					},
					{
						label: EQUITY_LABELS.shareLabel,
						type: 'number',
					},
					{
						type: 'string',
						role: 'tooltip',
						p: { html: true },
					},
				],
			].concat(graphArray.map(([d, p, v, r, t], idx) => [new Date(d), p / scaler, t]))
		);
	else
		graphData = google.visualization.arrayToDataTable(
			[
				[
					{
						label: 'Year', //"Month",
						type: SHOW_VOL ? 'number' : 'date',
					},
					{
						label: EQUITY_LABELS.shareLabel, //"Free Market Value",
						type: 'number',
					},
					{
						label: EQUITY_LABELS.referenceLabel, //"Freely Traded Value",
						type: 'number',
					},
					{
						label: '', //CONTRACT_SYMBOL, //"NTH",
						type: 'number',
					},
				],
			].concat(graphArray.map(([d, p, v, r], idx) => [SHOW_VOL ? idx : new Date(d), p / scaler, r / scaler, v]))
		);
	// console.log(graphArray);

	let yAxisFormat = EQUITY_LABELS.priceLabel.startsWith('price') ? (loMax < 4 ? '$0.0' : '$#,###.#') : loMax < 4 ? '0.0' : '#,###.#';
	if (scaler == 1000000) yAxisFormat = '$ #.#M';
	else if (scaler == 1000) yAxisFormat = '$ #k';

	// Format year labels (skip for portfolio multi-line and value composition as they use different formats)
	if (!isPortfolioMultiLine && !window.isValueCompositionGraph) {
		graphArray.forEach(([d], idx) => graphData.setFormattedValue(idx, 0, new Date(d).getFullYear().toString()));
	}
	let options = {
		animation: {
			duration: 1000,
			easing: 'out',
			startup: true,
		},
		// Use HTML tooltips with 'both' trigger for value composition - hover shows, click pins for scrolling
		tooltip: window.isValueCompositionGraph
			? {
					isHtml: true,
					trigger: 'both', // Hover to preview, click to pin tooltip for scrolling
				}
			: { isHtml: true },
		titlePosition: 'out',
		height: graphHeight,
		width: graphWidth,
		chartArea: {
			// leave room for y-axis labels
			width: graphWidth - margin, //'90%'	//'94%'
		},
		baselineColor: 'transparent',
		// Annotation styling for value composition bars
		annotations: window.isValueCompositionGraph
			? {
					textStyle: {
						fontName: 'Source Sans Pro',
						fontSize: 12,
						bold: true,
						color: '#333',
					},
					alwaysOutside: false,
					stem: {
						color: 'transparent',
						length: 0,
					},
				}
			: {},
		bar: {
			groupWidth: window.isValueCompositionGraph ? '80%' : '10%', // Much wider bars for value composition
		},
		// focusTarget 'category' enables hover-anywhere behavior
		// Stacked charts with custom HTML tooltips need null to avoid hasAttribute errors
		focusTarget: itsVMS || window.isValueCompositionGraph || window.isPortfolioTimeSeriesStacked || window.isClassTimeSeries ? null : 'category',
		legend: {
			//'none',
			position: 'bottom',
			alignment: 'center',
			textStyle: {
				fontFamily: 'Source Sans Pro, sans-serif',
				color: nthColor[4], //"rgb(36, 59, 83)",
				bold: false, //true
			},
			pagingTextStyle: {
				color: '#243B53', // Primary blue for pagination text
			},
			scrollArrows: {
				activeColor: '#243B53', // Primary blue for active arrows
				inactiveColor: '#9caea9', // Light gray for inactive arrows
			},
		},
		titleTextStyle: {
			color: stdColor[4], //nthColor[5],	//'46A29A',	//'4EB5AC',
			fontFamily: 'Source Sans Pro, sans-serif',
			fontSize: 14,
			bold: false,
			italic: false,
		},
		colors: isPortfolioTimeSeries
			? [
					'#4eb5ac', // Teal - Portfolio Value line
				]
			: window.isIncomeAllocationsDualLine
				? [
						nthColor[0], // Teal - Tax Basis line
						stdColor[3], // Dark blue - Market Value line
					]
				: window.isClassTimeSeries
					? [
							'#4eb5ac', // Teal - Class time series (matches portfolio style)
						]
					: window.isPortfolioTimeSeriesStacked
						? [
								// Stacked area colors for share classes (teal palette)
								'#4eb5ac', // Dark teal - first share class
								'#6bc4ba', // Medium teal - second share class
								'#85d0c9', // Light teal - third share class
								'#a6dad5', // Lighter teal - fourth share class
								'#3da59d', // Deep teal - fifth share class
								'#2d8b84', // Darker teal - sixth share class
								'#b2f5ea', // Pale teal - overflow
								'#98D8C8', // Mint - overflow
								'#7cc9be', // Medium-light teal - overflow
							]
						: window.isValueCompositionGraph
							? [
									'#e2e8f0', // Blue-gray - Contributions (paid-in capital) - oklch(70.7% 0.022 261.325)
									'#4eb5ac', // Dark teal - Capital Account (primary value color)
									'#6bc4ba', // Medium teal - Capital Account class 2
									'#85d0c9', // Light teal - Capital Account class 3
									'#a6dad5', // Lighter teal - Capital Account class 4
									'#3da59d', // Deep teal - Capital Account class 5
									'#2d8b84', // Darker teal - Capital Account class 6
									'#b2f5ea', // Pale teal - additional classes
									'#98D8C8', // Mint - Distributions
									'#7cc9be', // Medium-light teal - overflow
								]
							: isPortfolioMultiLine
								? generateColorPalette(window.portfolioClasses.length)
								: USE_STRIKE
									? [nthColor[0], stdColor[0], stdColor[3]]
									: SHADE_GRAPH
										? [nthColor[0], nthColor[2], stdColor[3]]
										: [nthColor[0], nthColor[1], stdColor[3]],
		hAxis: {
			textStyle: {
				fontFamily: 'Source Sans Pro, sans-serif',
				color: 'transparent',
				bold: true,
			},
			gridlines: {
				color: 'transparent',
			},
			format: SHOW_VOL ? '' : SHOW_MONTH ? 'M/d/yy' : 'yyyy',
			slantedText: HAS_STMTS,
			slantedTextAngle: 35, //20
			//format: SHOW_VOL ? '' : (SHOW_MONTH ? 'MMM-d' : 'yyyy')
			//ticks: [],
		},
		gridlines: {
			color: 'white',
		},
		vAxes: {
			0: {
				textStyle: {
					fontFamily: 'Source Sans Pro, sans-serif',
					color: nthColor[4],
					bold: false,
				},
				viewWindow: {
					min: window.portfolioYAxisMin || (isAND && theMin > 0.95 ? 0.95 : 0),
					max: theMax, //Math.max(...graphArray.map(x => x[1])) * 1.2
				},
				//ticks: theTicks,	//[...Array(Math.ceil(Math.max(...graphArray.map(x => x[1])))).keys()].map(x => x + 1),
				format: yAxisFormat,
				minorGridlines: {
					count: 0,
					//lineDashStyle: [2, 1, 3],
					color: 'transparent',
				},
				gridlines: {
					color: nthColor[3], //"rgb(240, 244, 248)"
				},
				decimals: 0,
				//title: "Historical Pricing  ( USD per " + CONTRACT_SYMBOL + " )",
				//titleColor: "rgb(77, 182, 172)",
				//titleBold: true
				//	scaleType: 'log'
			},
			1: {
				ticks: [],
				textStyle: {
					fontFamily: 'Source Sans Pro, sans-serif',
					color: stdColor[3],
					bold: true,
				},
				gridlines: {
					color: stdColor[3],
				},
				viewWindow: {
					min: 0,
					max: viewMax, //Math.max(...graphArray.map(x => x[2])) * 5
				},
			},
		},
		// TO DO: add tooltips (https://developers.google.com/chart/interactive/docs/customizing_tooltip_content)
		series: isPortfolioTimeSeries
			? {
					// Single area/line for time series portfolio value
					0: {
						targetAxisIndex: 0,
						type: 'area',
						lineWidth: 2,
						areaOpacity: 0.15,
					},
				}
			: window.isIncomeAllocationsDualLine
				? {
						// Dual line for income allocations (Tax Basis + Market Value)
						0: {
							targetAxisIndex: 0,
							type: 'line',
							lineWidth: 2,
						},
						1: {
							targetAxisIndex: 0,
							type: 'line',
							lineWidth: 2,
							lineDashStyle: [4, 4], // Dashed line for Market Value
						},
					}
				: window.isClassTimeSeries
					? {
							// Single area for class time series (matches portfolio style)
							0: {
								targetAxisIndex: 0,
								type: 'area',
								lineWidth: 2,
								areaOpacity: 0.15,
							},
						}
					: window.isPortfolioTimeSeriesStacked
						? (function () {
								// Generate series configuration for STACKED area time series
								let seriesConfig = {};
								for (let i = 0; i < (window.portfolioClasses?.length || 0); i++) {
									seriesConfig[i] = {
										targetAxisIndex: 0,
										type: 'area',
										lineWidth: 1,
										areaOpacity: 0.7,
									};
								}
								return seriesConfig;
							})()
						: window.isValueCompositionGraph
							? (function () {
									// Generate series configuration for STACKED bar chart
									// Series: Contributions (0) + equity classes (1..n) + Distributions (n+1)
									let seriesConfig = {};
									let numSeries = window.portfolioClasses.length + 2; // contributions + classes + distributions
									for (let i = 0; i < numSeries; i++) {
										seriesConfig[i] = {
											targetAxisIndex: 0,
											type: 'bars',
										};
									}
									return seriesConfig;
								})()
							: isPortfolioMultiLine
								? (function () {
										// Generate series configuration dynamically for portfolio multi-line
										let seriesConfig = {};
										for (let i = 0; i < window.portfolioClasses.length; i++) {
											seriesConfig[i] = {
												targetAxisIndex: 0,
												type: 'line',
												lineWidth: 2,
											};
										}
										return seriesConfig;
									})()
								: {
										0: {
											targetAxisIndex: 0,
											type: 'area',
										},
										1: {
											targetAxisIndex: 0,
											type: 'area',
										},
										2: {
											targetAxisIndex: 1,
											type: 'bars',
										},
									},
		isStacked: window.isValueCompositionGraph || window.isPortfolioTimeSeriesStacked ? true : false,
	};

	// Build custom y-axis ticks for time series graphs to show mixed k/M format
	if ((window.isPortfolioTimeSeriesStacked || window.isClassTimeSeries || window.isIncomeAllocationsDualLine) && scaler === 1000) {
		// Generate nice tick values from 0 to theMax
		const tickCount = 6;
		const step = Math.ceil(theMax / tickCount / 100) * 100; // Round step to nearest 100
		const customYTicks = [];
		for (let val = 0; val <= theMax + step; val += step) {
			let formatted;
			if (val >= 1000) {
				// Show as millions (val is in thousands, so 1000 = $1M)
				const millions = val / 1000;
				formatted = '$' + (millions % 1 === 0 ? millions.toFixed(0) : millions.toFixed(1)) + 'M';
			} else {
				formatted = '$' + val + 'k';
			}
			customYTicks.push({ v: val, f: formatted });
		}
		options.vAxes[0].ticks = customYTicks;
	}

	// No initial title for stacked time series - will show on hover
	// Enable HTML tooltips with class breakdown for stacked charts
	// Enable crosshair for hover-anywhere UX with vertical line at date
	if (window.isPortfolioTimeSeriesStacked) {
		options.title = '';
		options.tooltip = {
			isHtml: true,
			trigger: 'focus',
		};
		options.crosshair = {
			trigger: 'both',
			orientation: 'vertical',
			color: nthColor[5],
		};
		// Build explicit year ticks to avoid repeated labels (use UTC to match data dates)
		const yearTicks = [];
		const seenYears = new Set();
		graphArray.forEach((row) => {
			const year = new Date(row[0]).getUTCFullYear();
			if (!seenYears.has(year)) {
				seenYears.add(year);
				// Use UTC date to match data format
				yearTicks.push({ v: new Date(Date.UTC(year, 6, 1, 12, 0, 0)), f: year.toString() });
			}
		});
		// Show years on x-axis with explicit ticks
		options.hAxis = {
			ticks: yearTicks,
			textStyle: {
				fontFamily: 'Source Sans Pro, sans-serif',
				color: nthColor[4],
				fontSize: 12,
			},
			gridlines: { color: 'transparent' },
		};
	}

	// Enable HTML tooltips and crosshair for single-class time series (matches portfolio style)
	if (window.isClassTimeSeries) {
		options.title = '';
		options.tooltip = {
			isHtml: true,
			trigger: 'focus',
		};
		options.crosshair = {
			trigger: 'both',
			orientation: 'vertical',
			color: nthColor[5],
		};
		// Build explicit year ticks to avoid repeated labels (use UTC to match data dates)
		const classYearTicks = [];
		const classSeenYears = new Set();
		graphArray.forEach((row) => {
			const year = new Date(row[0]).getUTCFullYear();
			if (!classSeenYears.has(year)) {
				classSeenYears.add(year);
				// Use UTC date to match data format
				classYearTicks.push({ v: new Date(Date.UTC(year, 6, 1, 12, 0, 0)), f: year.toString() });
			}
		});
		// Show years on x-axis with explicit ticks
		options.hAxis = {
			ticks: classYearTicks,
			textStyle: {
				fontFamily: 'Source Sans Pro, sans-serif',
				color: nthColor[4],
				fontSize: 12,
			},
			gridlines: { color: 'transparent' },
		};
	}

	// Enable HTML tooltips for value composition graph
	if (window.isValueCompositionGraph) {
		options.tooltip = {
			isHtml: true,
			trigger: 'focus',
		};
	}

	if (!SHOW_VOL) {
		// Only modify series[2] for single-class graphs (portfolio mode has different series structure)
		if (!isPortfolioMultiLine && !isPortfolioTimeSeries && options.series[2]) {
			options.series[2].color = 'transparent';
		}
		options.hAxis.textStyle.bold = false;
		options.hAxis.textStyle.color = nthColor[4];
		options.crosshair = { trigger: 'both', orientation: 'vertical', color: nthColor[5] };
		//document.getElementById('graphHeading').innerHTML = "";

		// Skip custom tick building for portfolio multi-line, time series, and stacked time series modes
		if (!isPortfolioMultiLine && !isPortfolioTimeSeries && !window.isPortfolioTimeSeriesStacked && !window.isClassTimeSeries) {
			// build x-axis ticks to prevent repeated labels
			let twoDays = 50 * 60 * 60 * 1000;
			let dateRange = graphData.getColumnRange(0);

			// Ensure min and max are Date objects (Google Charts may return numbers)
			if (typeof dateRange.min === 'number') {
				dateRange.min = new Date(dateRange.min);
			}
			if (typeof dateRange.max === 'number') {
				dateRange.max = new Date(dateRange.max);
			}

			// Validate dateRange values before using them
			if (!dateRange.min || !dateRange.max || isNaN(dateRange.min.getTime?.()) || isNaN(dateRange.max.getTime?.())) {
				console.warn('[GraphRendering] Invalid dateRange, skipping custom ticks:', dateRange);
			} else {
				let ticks = [];
				if (SHOW_MONTH) {
					// End date of last quarter = moment().subtract(lastQuarterNumber, 'quarter').endOf('quarter');
					let dateTick = dateRange.min;
					let eoqDate = moment(dateTick).endOf('quarter').toDate();
					let soqDate = moment(dateTick).endOf('quarter').subtract(1, 'quarter').toDate();
					// if (Math.abs(dateTick - eoqDate) > 50*60*60*1000)
					// 	dateTick = moment(dateTick).subtract(1, 'quarter').endOf('quarter').toDate();
					if (Math.abs(dateTick - eoqDate) < twoDays) dateTick = moment(soqDate).add(1, 'quarter').endOf('month').toDate();
					else if (Math.abs(dateTick - soqDate) < twoDays) dateTick = soqDate;
					else dateTick = moment(dateRange.min).startOf('quarter').toDate();
					while (dateTick.getTime() <= dateRange.max.getTime()) {
						ticks.push(dateTick);
						dateTick = new Date(dateTick.getFullYear(), dateTick.getMonth() + 4, 0);
					}
				} else {
					let dateTick = dateRange.min;
					let eoyDate = moment(dateTick).endOf('year').toDate();
					let soyDate = moment(dateTick).startOf('year').toDate();
					if (Math.abs(dateTick - eoyDate) < twoDays) dateTick = moment(soyDate).add(1, 'year').toDate();
					else if (Math.abs(dateTick - soyDate) < twoDays) dateTick = soyDate;
					else dateTick = moment(dateRange.min).startOf('year').toDate();
					while (dateTick.getTime() <= dateRange.max.getTime()) {
						ticks.push(dateTick);
						dateTick = new Date(dateTick.getFullYear() + 1, dateTick.getMonth(), dateTick.getDate());
					}
				}
				options.hAxis.ticks = ticks;
			}
		}
	}
	if (!SHOW_REF && !isPortfolioMultiLine && !isPortfolioTimeSeries && !window.isIncomeAllocationsDualLine) {
		//&& !(EQUITY_LABELS.shareAbbrev && EQUITY_LABELS.shareAbbrev.length > 0)) {
		options.legend = 'none';
		//options.titleTextStyle = {};

		// Calculate price change of last and penultimate trades
		// let last = graphArray[graphArray.length - 1][1].toFixed(2)
		// let diff = graphArray.length  > 1 ? last - graphArray[graphArray.length - 2][1].toFixed(2) : 0;
		// let percentChange = Math.round((diff / last) * 100, 0);
		// document.getElementById('graphHeading').innerHTML =
		// `
		// 	<h5 class="text-lg font-bold mb-0 mt-0 text-black">Current </span>${capitalize(symbol)} Value: $</span>${(1000 * last).toLocaleString()}</h5>
		// 	<h6 class="text-base mt-1 mb-4 text-secondary">Most recent price: $</span>${last + ' (' + (diff == 0 ? 'no change)' : (diff > 0 ? 'up ' : 'down ') + percentChange + '%)')}</h6>
		// `;
		//<h5 class="text-lg font-bold mb-0 mt-0 text-black">Current </span>${capitalize(symbol)} Value: $</span>${(userData.clients.shares[symbol]["owned"] * last).toLocaleString()}</h5>
	}

	// if (HAS_STMTS) showTVPI.style.display = 'block';
	// else showTVPI.style.display = 'none';

	// if (itsVMS) showTooltip.style.display = 'block';
	// else showTooltip.style.display = 'none';

	function titlefunc(row) {
		try {
			const USE_CLASS_NAME = true;
			const HIDE_CLASS_LABEL = true;
			if (graphArray.length <= row) return '';

			// Handle portfolio time series mode (single line showing value over time)
			if (isPortfolioTimeSeries) {
				let format = { year: 'numeric', month: 'short', day: 'numeric' };
				let date = new Date(graphArray[row][0]).toLocaleDateString('en-US', format);
				let value = graphArray[row][1] || 0;
				let formattedValue = '$' + Math.round(value).toLocaleString();
				return date + '\xa0\xa0\xa0\xa0Value:\xa0' + formattedValue;
			}

			// Handle stacked time series mode (stacked area showing value by class over time)
			if (window.isPortfolioTimeSeriesStacked && window.portfolioClasses?.length > 0) {
				let format = { year: 'numeric', month: 'short', day: 'numeric' };
				let date = new Date(graphArray[row][0]).toLocaleDateString('en-US', format);
				let totalValue = 0;
				// Sum all class values
				for (let i = 1; i < graphArray[row].length && i <= window.portfolioClasses.length; i++) {
					totalValue += graphArray[row][i] || 0;
				}
				let formattedTotal = '$' + Math.round(totalValue).toLocaleString();
				// Only show date and total value in title (class breakdown shown in native tooltip)
				return date + '\xa0\xa0\xa0\xa0Total Value:\xa0' + formattedTotal;
			}

			// Handle portfolio multi-line mode
			if (isPortfolioMultiLine) {
				let format = { year: 'numeric', month: 'short', day: 'numeric' };
				let date = new Date(graphArray[row][0]).toLocaleDateString('en-US', format);
				let totalValue = 0;
				// Sum all class values for this row
				for (let i = 1; i < graphArray[row].length; i++) {
					if (graphArray[row][i] !== null) {
						totalValue += graphArray[row][i];
					}
				}
				let formattedTotal = totalValue > 999 ? '$' + totalValue.toLocaleString() : '$' + totalValue.toFixed(2);
				return date + '\xa0\xa0\xa0\xa0' + formattedTotal;
			}

			let price =
				isCER && symbol == 'CCA'
					? graphArray[row][1].toLocaleString('en-US', m0)
					: graphArray[row][1] > 999 || isCER
						? graphArray[row][1].toLocaleString()
						: graphArray[row][1].toFixed(2);
			// let price = graphArray[row][1] > 999 ? "$ " + graphArray[row][1].toLocaleString() : graphArray[row][1].toFixed(2);
			// let format = {year: 'numeric', month: 'short'};
			// if (SHOW_VOL || SHOW_MONTH) format = {year: 'numeric', month: 'short', day: 'numeric' };
			let format = { year: 'numeric', month: 'short', day: 'numeric' };
			let date = new Date(graphArray[row][0]).toLocaleDateString('en-US', format);
			if (
				SHOW_REF &&
				EQUITY_LABELS.referenceAbbrev &&
				EQUITY_LABELS.shareAbbrev &&
				EQUITY_LABELS.referenceAbbrev.length > 0 &&
				EQUITY_LABELS.shareAbbrev.length > 0
			) {
				let ftv = graphArray[row][3].toFixed(2);
				return (
					symbol +
					':\xa0\xa0' +
					date +
					'\xa0\xa0\xa0 ' +
					EQUITY_LABELS.shareAbbrev +
					':\xa0$' +
					price +
					'\xa0\xa0\xa0\xa0' +
					EQUITY_LABELS.referenceAbbrev +
					':\xa0$' +
					ftv
				);
			} else if (HAS_STMTS) {
				if (showTVPI.checked) return symbol + ':\xa0\xa0' + date + '\xa0\xa0\xa0 ' + 'NAV-to-PIC:\xa0' + price.toLocaleString();
				else return symbol + ':\xa0\xa0' + date + '\xa0\xa0\xa0 ' + 'NAV: \xa0$ ' + Math.round(price.replace(/[^-.\d]/g, '')).toLocaleString();
			} else if (EQUITY_LABELS.shareAbbrev && EQUITY_LABELS.shareAbbrev.length > 0) {
				return symbol + ':\xa0\xa0' + date + '\xa0\xa0\xa0 ' + EQUITY_LABELS.shareAbbrev + ':\xa0$' + price;
			} else {
				//let date = new Date(graphArray[row][0]).toISOString(graphArray[row][0]).slice(0, 10);
				let volume = ''; //graphArray[row][2] == 0 ? "" : "\xa0\xa0\xa0 Volume:\xa0" + graphArray[row][2];
				return (
					(HIDE_CLASS_LABEL ? '' : (USE_CLASS_NAME ? className : symbol ? symbol : 'Date') + ':\xa0\xa0') +
					date +
					volume +
					'\xa0\xa0\xa0\xa0' +
					EQUITY_LABELS.priceLabel +
					price
				); // + "\n\n";
				//return volume == "" ? "$" + price + "\n" + date : volume + ", $" + price + "\n" + date;
			}
		} catch (error) {
			console.error('Error in titlefunc:', error, 'row:', row);
			console.error('Stack:', error.stack);
			return 'Error generating tooltip';
		}
	}
	function vVal(rownum) {
		if (graphArray.length <= rownum) return 0;
		return graphArray[rownum][2];
	}
	function pVal(rownum) {
		if (graphArray.length <= rownum) return 0;
		return graphArray[rownum][1].toFixed(2);
	}
	function vLabel(rownum) {
		return vVal(rownum) == 0 ? '' : 'vol: ' + vVal(rownum);
	}
	function pLabel(rownum) {
		return '$' + pVal(rownum);
	}
	function pticks(rownum) {
		return [pVal(rownum), ...[...Array(Math.ceil(Math.max(...graphArray.map((x) => x[1])))).keys()].map((x) => (x + 1).toFixed(2))].sort();
	}

	if (CHART) {
		google.visualization.events.removeAllListeners(CHART);
		CHART = false;
	}
	if (!CHART) {
		try {
			CHART = new google.visualization.ChartWrapper({
				chartType: 'ComboChart',
				containerId: 'chart_div',
				dataTable: graphData,
				options: options,
			});
		} catch (error) {
			console.error('Error creating ChartWrapper:', error);
			return;
		}

		google.visualization.events.addOneTimeListener(CHART, 'ready', function () {
			SELECTED_ROW = graphArray.length - 1;

			// For value composition graphs, add tooltip persistence with delayed hide
			if (window.isValueCompositionGraph) {
				// Track tooltip state
				let lastTooltipContent = null;
				let persistentTooltip = null;
				let hideTimeout = null;
				const HIDE_DELAY = 500; // ms

				// Create a persistent tooltip container that we control
				function createPersistentTooltip() {
					if (persistentTooltip) return persistentTooltip;

					persistentTooltip = document.createElement('div');
					persistentTooltip.id = 'persistent-chart-tooltip';
					persistentTooltip.style.cssText = `
						position: absolute;
						background: white;
						border: 1px solid #ccc;
						border-radius: 4px;
						box-shadow: 0 2px 10px rgba(0,0,0,0.2);
						z-index: 10000;
						display: none;
						pointer-events: auto;
						max-height: 350px;
						overflow-y: auto;
					`;
					document.body.appendChild(persistentTooltip);

					// Keep tooltip visible when hovering over it
					persistentTooltip.addEventListener('mouseenter', function () {
						if (hideTimeout) {
							clearTimeout(hideTimeout);
							hideTimeout = null;
						}
					});

					persistentTooltip.addEventListener('mouseleave', function () {
						hideTimeout = setTimeout(function () {
							persistentTooltip.style.display = 'none';
						}, HIDE_DELAY);
					});

					return persistentTooltip;
				}

				// Watch for Google's tooltip and copy its content to our persistent one
				const observer = new MutationObserver(function (mutations) {
					mutations.forEach(function (mutation) {
						mutation.addedNodes.forEach(function (node) {
							if (node.classList && node.classList.contains('google-visualization-tooltip')) {
								// Get position and content from Google's tooltip
								const rect = node.getBoundingClientRect();
								const content = node.innerHTML;

								// Only update if content changed (avoid flicker)
								if (content !== lastTooltipContent) {
									lastTooltipContent = content;

									const tooltip = createPersistentTooltip();
									tooltip.innerHTML = content;
									tooltip.style.left = rect.left + 'px';
									tooltip.style.top = rect.top + 'px';
									tooltip.style.display = 'block';

									// Clear any pending hide
									if (hideTimeout) {
										clearTimeout(hideTimeout);
										hideTimeout = null;
									}
								} else {
									// Even if content unchanged, make sure tooltip is visible and clear hide timeout
									if (persistentTooltip) {
										persistentTooltip.style.display = 'block';
										if (hideTimeout) {
											clearTimeout(hideTimeout);
											hideTimeout = null;
										}
									}
								}

								// Hide Google's tooltip (we're using our own)
								node.style.visibility = 'hidden';
							}
						});
					});
				});

				// Observe for tooltip additions
				const chartContainer = document.getElementById('chart_div');
				if (chartContainer) {
					observer.observe(chartContainer, { childList: true, subtree: true });
				}

				// Handle mouse leaving the chart area
				const chartElement = CHART.getChart().getContainer();
				if (chartElement) {
					chartElement.addEventListener('mouseleave', function () {
						// Start hide timer, but tooltip stays if mouse enters it
						hideTimeout = setTimeout(function () {
							if (persistentTooltip && !persistentTooltip.matches(':hover')) {
								persistentTooltip.style.display = 'none';
								lastTooltipContent = null;
							}
						}, HIDE_DELAY);
					});
				}

				return;
			}

			google.visualization.events.addListener(CHART.getChart(), 'onmouseover', function ({ row }) {
				if (row == undefined) return;
				if (row < graphArray.length - 1) options.animation = false;
				SELECTED_ROW = row;
				options.title = titlefunc(SELECTED_ROW);
				// Draw vertical tie-line (skip for portfolio multi-line as it uses different axis structure)
				if (SHOW_VOL && !isPortfolioMultiLine) {
					options.hAxis.textStyle.color = stdColor[3];
					options.hAxis.gridlines.color = stdColor[3];
					options.hAxis.ticks = [
						{
							v: SELECTED_ROW,
							f: vLabel(SELECTED_ROW),
						},
					];
				}
				if (itsVMS && showTooltip.checked) {
					options.tooltip = {
						isHtml: true,
						trigger: 'selection', //'focus'	//'both'
					};
				} else {
					options.tooltip = {};
				}
				// Redraw chart to update title on hover
				// For stacked time series, class time series, and portfolio multi-line, skip redraw to preserve native tooltips
				if (!isPortfolioMultiLine && !window.isPortfolioTimeSeriesStacked && !window.isClassTimeSeries) {
					CHART.draw(); // kills tooltips without above options.tooltip
					// Place dot on data
					CHART.getChart().setSelection([
						{
							row: SELECTED_ROW,
							column: 1,
						},
					]);
				}
			});
		});

		if (!addedToolTipListenerAlready) {
			addedToolTipListenerAlready = true;

			showTVPI.addEventListener('change', function (e) {
				e.preventDefault();
				addStatements();

				// update Session Storage
				userData.clients[client].showTVPI = this.checked;
				sessionStorage.setItem('user.Data', JSON.stringify(userData));
				drawChart();

				axios
					.post(
						postURL,
						JSON.stringify({
							eventName: 'index.show_tvpi_changed',
							userid: authedUser,
							client: client,
							cookie: cookie,
							clients: {
								[client]: {
									showTVPI: this.checked,
								},
							},
						})
					)
					.then(function (res) {
						console.log(res);
					})
					.catch(function (err) {
						console.error(err);
					});

				CHART.draw();
				// Place dot on data
				CHART.getChart().setSelection([
					{
						row: SELECTED_ROW,
						column: 1,
					},
				]);
			});

			showTooltip.addEventListener('change', function (e) {
				e.preventDefault();
				if (this.checked) {
					options.tooltip = {
						isHtml: true,
						trigger: 'selection', //'focus'	//'both'
					};
				} else {
					options.tooltip = {};
				}

				// update Session Storage
				userData.clients[client].showTooltip = this.checked;
				sessionStorage.setItem('user.Data', JSON.stringify(userData));
				drawChart();

				axios
					.post(
						postURL,
						JSON.stringify({
							eventName: 'index.show_tvpi_tooltip_changed',
							userid: authedUser,
							client: client,
							cookie: cookie,
							clients: {
								[client]: {
									showTooltip: this.checked,
								},
							},
						})
					)
					.then(function (res) {
						console.log(res);
					})
					.catch(function (err) {
						console.error(err);
					});

				CHART.draw();
				// Place dot on data
				CHART.getChart().setSelection([
					{
						row: SELECTED_ROW,
						column: 1,
					},
				]);
			});
		}
	}
	SELECTED_ROW = SELECTED_ROW || graphArray.length - 1;
	CHART.draw();
	// Skip triggering onmouseover for value composition graphs - they use different interaction model
	if (!window.isValueCompositionGraph) {
		google.visualization.events.trigger(CHART.getChart(), 'onmouseover', { row: SELECTED_ROW, passive: true });
	}
}

let incGains;
let totalIntPaid;
let totalPrinPaid;
function getPayments(eventUser, eventClass, clientEventDate) {
	let finance = new Finance();
	totalIntPaid = 0;
	totalPrinPaid = 0;
	let payments = [];
	let eventDate = moment.unix(clientEventDate);
	let eventStamp = Number(clientEventDate);
	let eventMonth = eventDate.month();
	let eventYear = eventDate.year();

	// Until now, all stock sales have occurred on January 1.
	// We just added July 1, which now is handled.  April 1 and October 1 also might work.
	// Any other trade date likely will require more sophisticated logic and testing.
	function getPeriod(stamp) {
		let year = moment.unix(stamp).year();
		let month = moment.unix(stamp).month();
		let delta = eventYear - year;
		// console.log("Old code: ", (eventMonth / 3) + (delta * 4));
		// console.log("New code: ", ((eventMonth - month) / 3) + (delta * 4));
		return (eventMonth - month) / 3 + delta * 4;
	}

	// NOTE BENE: Prepayments by Tom Hansche and Dan Campbell are exceptions.  See (relatively simple) custom code 100 lines below here.
	// Simultaneously, the associated Events (for each individual, for every effected quarter) need to be adjusted to the modified payments.
	// See Event Notes for for Hansche -> Marshall and Campbell -> Hildebrand. Their individual amounts are increased/decreased.
	// The Gross Amounts for each Event were not adjusted, although the Gross Amounts probably could be adjusted, if desired.

	/*
	let vintages = {};	// and destinations: recipient-quantity

	// The comma separations below don't really matter because we only use vintages[x].includes()
	// vintages["JH 2014"] = ["SW-53","SW-1042","MJ-2500","PK-4000, TM-4000, RM-2000"];		// No debt assumption for Scott Wieler
	vintages["JH 2014"] = ["MJ-2500","PK-4000, TM-4000, RMa-2000"];
	vintages["JH 2015"] = ["RMa-500"];
	vintages["JH 2017"] = ["BM-5000, LS-1000, RMi-1000"];
	vintages["JH 2019"] = ["AM-2500","KK-5000"];

	vintages["JM 2015"] = ["AM-2000, AJ-2500, BM-3000, BR-2500, CG-2500, ES-2500, SG-2500, TM-2500"];
	vintages["JM 2016"] = ["JS-2500, TF-500, RMi-3500, LS-3500, TV-2000"];
	vintages["JM 2017"] = ["JC-5000, KB-2500","BB-5000","MB-2500, RG-2500, DR-500, CG-1000, SG-1000"];

	vintages["RS 2015"] = ["BB-4000, BD-4000, TV-2000"];
	vintages["RS 2016"] = ["DF-5000, RMa-5000"];
	vintages["RS 2017"] = ["AJ-2500, BR-500, CG-500, ES-500, SG-500, TV-500","JS-2500, BB-2500, KK-2500"];

	// 2024 Interim sales
	vintages["PF 2021"] = ["JM-2500"];
	vintages["DC 2022"] = ["JM-6000"];
	vintages["HT 2022"] = ["JM-2500h"];	// need to distinguish from PF 2021
	vintages["JM 2022"] = ["JM-2500"];
	vintages["JM 2023"] = ["JM-1000"];
	vintages["JW 2023"] = ["JM-4000"];
	vintages["JMg 2023"] = ["JM-1500"];
	*/

	// 3/30/2025 Now abstracted out
	const vintages = clientData.equity['JMS'].vintages;

	// Find all transactions associated with this event
	let transactions = clientData.equity['JMS'].transactions;
	let assocTransactions = transactions.filter((t) => Number(t.date) < Number(clientEventDate) - 60 * 24 * 3600); // 60 days
	let referenceVals = clientData.equity['JMS'].valuations;
	// insert reference prices if available
	Object.keys(assocTransactions).forEach(function (t) {
		let trans = assocTransactions[t];
		let refVal = referenceVals.find((v) => Number(v.sharePrice) == Number(trans.sharePrice) && Number(v.date) <= Number(trans.date) + 2 * 86400);
		if (refVal) trans.referencePrice = refVal.referencePrice;
	});
	assocTransactions = assocTransactions.filter((t) => t.referencePrice);
	assocTransactions = assocTransactions.filter((t) => !['Lump Sum', 'Three Payments'].some((s) => t.notes.includes(s))); // && !t.notes.includes("Final"));
	if (eventClass == 'NP') assocTransactions = assocTransactions.filter((t) => t.receiver == eventUser);
	else if (eventClass == 'NR') assocTransactions = assocTransactions.filter((t) => t.sender == eventUser);
	// console.log(assocTransactions);

	// For each associated transaction, find payment number, assuming 40 payments over 10 years (1/15, 4/15, 7/15, 10/15)
	assocTransactions.forEach((t) => {
		let ppmtOrig = 0;
		let ipmtOrig = 0;
		let deductQty = 0;

		let notes = t.notes;
		let per = getPeriod(t.date);
		if (per > 40) return;
		let qty = Number(t.quantity);

		// Handle debt assumptions
		if (Object.keys(vintages).some((n) => notes.includes(n)) || Object.values(vintages).some((n) => n.some((i) => notes.includes(i)))) {
			let thisNote = notes.match(/\(([^)]+)\)/)[1];
			if (thisNote.includes(':')) thisNote = thisNote.split(':')[1].trim();
			let assumKey = Object.keys(vintages).find((v) => thisNote == v || Object.values(vintages[v]).some((x) => thisNote == x));

			let jmsTransactions = clientData.equity['JMS'].transactions;
			// This was a real pita to figure out!!!
			let assumedTransactions = jmsTransactions.filter(
				(a) => a.notes.includes(assumKey) && ([a.sender, a.receiver].includes(eventUser) || (t.sender == eventUser && a.sender == t.receiver))
			);

			let qtyOrig = 0; // (if NP?) need to accumulate for multiple assumptions from same person
			assumedTransactions.forEach(function (assumTrans) {
				// Is the assumption data (7/1/2024 for Trisata's JM 2015) well before this pay period (60 days before 4/15/2025)?
				// If so, the debt likely is being assumed.
				if (Number(assumTrans.date) < eventStamp - 60 * 24 * 3600) {
					let origIdx = jmsTransactions.findIndex((o) => o.receiver == t.sender && vintages[assumKey].some((v) => o.notes.includes(v)));
					if (origIdx < 0) origIdx = jmsTransactions.findIndex((o) => o.notes.includes(thisNote)); // && o.receiver == t.sender);
					let o = JSON.parse(JSON.stringify(jmsTransactions[origIdx]));
					let perOrig = getPeriod(o.date);
					let destination = getInitials(assumTrans.receiverName) + '-' + assumTrans.quantity;
					let sameTransaction = t.receiver == assumTrans.receiver && t.quantity == assumTrans.quantity;
					// console.log("sameTransaction:", sameTransaction);

					// It is in this section where a large payment gets split appropriately
					// If the flow does not get here, then no split will occur
					// The most critical requirement is this below: o.notes.includes(destination)

					// CHATGPT Debt Assumption and Vintages debugging logs
					// if (eventUser.includes('mccarth')) {
					// 	console.log('t.notes', t.notes);
					// 	console.log('thisNote', thisNote);
					// 	console.log('assumKey', assumKey);
					// 	console.log('assumedTransactions', assumedTransactions);
					// 	console.log('origIdx', origIdx);
					// 	console.log('o', o);
					// 	console.log('destination', destination);
					// 	console.log('sameTransaction', sameTransaction);
					// 	console.log('gate passes', perOrig <= 40 && (o.sender == eventUser || ((eventClass == 'NP' || sameTransaction) && o.notes.includes(destination))));
					// 	console.log('ppmtOrig + ipmtOrig', ppmtOrig + ipmtOrig);
					// }

					// This was an even bigger pita to figure out!!!
					if (perOrig <= 40 && (o.sender == eventUser || ((eventClass == 'NP' || sameTransaction) && o.notes.includes(destination)))) {
						// not sure about this logic, maybe too simple...
						if (eventClass == 'NP') qtyOrig += Number(assumTrans.quantity);
						else qtyOrig = Number(assumTrans.quantity);
						let pvOrig = Number(o.sharePrice) * qtyOrig;
						let grossOrig = Number(o.referencePrice) * qtyOrig;
						let pmtOrig = grossOrig / 40;
						let rateOrig = finance.RATE(40, -pmtOrig, pvOrig);
						ppmtOrig = -finance.PPMT(rateOrig, perOrig, 40, pvOrig);
						ipmtOrig = -finance.IPMT(rateOrig, perOrig, 40, pvOrig);

						// handle special 2022 amortization - $0.64 problem!
						if (assumTrans.notes.includes(' 2022')) {
							let scheduledPmt = -finance.PMT(0.0181 / 4, 40, pvOrig);
							for (let i = 1; i <= perOrig; i++) {
								ipmtOrig = (pvOrig * 0.0181) / 4;
								ppmtOrig = scheduledPmt - ipmtOrig;
								// this feels incorrect as it skips the last interest payment
								if (scheduledPmt >= pvOrig) {
									ppmtOrig = pvOrig;
									ipmtOrig = 0;
								}
								pvOrig -= ppmtOrig;
							}
						}

						console.log('Combined: ', ppmtOrig + ipmtOrig);
						if (o.receiver != eventUser) {
							totalPrinPaid += ppmtOrig;
							totalIntPaid += ipmtOrig;
							console.log('Combined: ', ppmtOrig + ipmtOrig);
							console.log('Total Combined: ', totalPrinPaid + totalIntPaid);
							if (t.receiver == eventUser) {
								let name = o.senderName + '<br> (assumed from ' + getInitials(o.receiverName) + ')';
								addPmt(perOrig, eventDate, name, o.sender, ppmtOrig, ipmtOrig);
							} else {
								let name = assumTrans.receiverName + '<br> (assumed from ' + getInitials(assumTrans.senderName) + ')';
								addPmt(perOrig, eventDate, name, assumTrans.receiver, ppmtOrig, ipmtOrig);
								ppmtOrig = 0;
								ipmtOrig = 0;
								deductQty += qtyOrig;
							}
						}
					}
				}
			});
		}

		// handle rounding fudge done by JMS
		if (t.receiver == 'henrya@jmsearch.com' && qty == 3333) qty = 3334;

		// Handle Apr 2022 Hanzsche -> Marshall pre-payment
		if (t.receiver == 'tomh@jmsearch.com' && qty == 10000) {
			if (per == 37)
				qty = 40000; // full year in one 4x payment
			else if (per > 37 && per < 41) return;
		}
		// Handle Jan 2023 Campbell -> Hildebrand pre-payment
		if (t.receiver == 'campbelld@jmsearch.com' && qty == 15000) {
			if (per == 36)
				qty = 75000; // this payment plus 4 more (5x payment)
			else if (per > 36 && per < 41) return;
		}

		if (t.sender == eventUser) qty -= deductQty;
		if (qty == 0) return;
		let pv = Number(t.sharePrice) * qty;
		let gross = Number(t.referencePrice) * qty;
		let pmt = gross / 40;
		let rate = finance.RATE(40, -pmt, pv);
		// console.log("Annual Rate: " + rate * 400);
		let ppmt, ipmt;

		let transYear = moment.unix(t.date).year();
		if (transYear == '2010') {
			// 5 year annual program
			per = eventYear - moment.unix(t.date).year();
			if (per > 5) return;
			pmt = gross / 5;
			rate = finance.RATE(5, -pmt, pv);
			console.log('Annual Rate: ' + rate * 100);
			ppmt = -finance.PPMT(rate, per, 5, pv);
			ipmt = -finance.IPMT(rate, per, 5, pv);
		} else if (transYear == '2011') {
			// 9 year program with final stub payment
			let finalYearPmt = (qty * 9575.51) / 10000;
			let subGross = gross - finalYearPmt;
			console.log(subGross + finalYearPmt);
			for (let i = 1; i <= per; i++) {
				if (i <= 36) pmt = (qty * 12239.17) / 10000 / 4;
				else pmt = finalYearPmt / 4;
				ipmt = pv * rate;
				ppmt = pmt - ipmt;
				pv -= ppmt;
			}
		} else if (transYear == '2012') {
			// 10 year program with final stub payment
			let finalYearPmt = Number(notes.replace(/[^.\d]/g, '')); // <-- DOES THIS ALWAYS WORK ???
			let subGross = gross - finalYearPmt;
			console.log(subGross + finalYearPmt);
			for (let i = 1; i <= per; i++) {
				if (i <= 36) pmt = subGross / 36;
				else pmt = finalYearPmt / 4;
				ipmt = pv * rate;
				ppmt = pmt - ipmt;
				pv -= ppmt;
			}
		} else if (transYear == '2022') {
			// 10 year program with slightly different stub payment
			let scheduledPmt = -finance.PMT(0.0181 / 4, 40, pv);
			for (let i = 1; i <= per; i++) {
				ipmt = (pv * 0.0181) / 4;
				ppmt = scheduledPmt - ipmt;
				// this feels incorrect as it skips the last interest payment
				if (scheduledPmt >= pv) {
					ppmt = pv;
					ipmt = 0;
				}
				pv -= ppmt;
			}
			ppmt -= ppmtOrig;
			ipmt -= ipmtOrig;
		} else {
			// standard 10 year program with 40 equal payments
			ppmt = -finance.PPMT(rate, per, 40, pv) - ppmtOrig;
			ipmt = -finance.IPMT(rate, per, 40, pv) - ipmtOrig;
		}

		// switch from quarterly payments to annual payments
		if (eventStamp < 1551398400) {
			// March 1, 2019
			ppmt *= 4;
			ipmt *= 4;
			per = per - 3 + '-' + per;
		}
		// console.log("Gross Payment: ", ppmt + ipmt + ppmtOrig + ipmtOrig);
		// console.log("Net Payment: ", ppmt + ipmt);
		// console.log("Interest: " + ipmt);
		// console.log("Payment: " + (ppmt + ipmt));
		totalPrinPaid += ppmt;
		totalIntPaid += ipmt;
		console.log('Total Payment: ', totalPrinPaid + totalIntPaid);
		if (ppmt != 0 && ipmt != 0)
			addPmt(per, eventDate, eventClass == 'NR' ? t.receiverName : t.senderName, eventClass == 'NR' ? t.receiver : t.sender, ppmt, ipmt);
	});

	function addPmt(per, date, name, uid, ppmt, ipmt) {
		console.log('Payment: ', ppmt + ipmt);
		let pmt = {
			period: per,
			date: date,
			name: name,
			uid: uid,
			prin: ppmt,
			int: ipmt,
		};

		// Legacy Trista payment fixes
		if (eventClass === 'NP' && eventUser.endsWith('trista.mccarthy@gmail.com')) {
			const total = Number((pmt.prin + pmt.int).toFixed(2));
			const base = pmt.prin + pmt.int;
			const ratio = base === 0 ? 0 : pmt.prin / base;

			// zero-based months:
			// Q4 2023 payment window = Jan 2024 => month 0
			// Q1 2024 payment window = Apr 2024 => month 3
			// Q2 2024 payment window = Jul 2024 => month 6
			const throughQ4_2023 = eventYear < 2024 || (eventYear === 2024 && eventMonth < 3);
			const throughQ2_2024 = eventYear < 2024 || (eventYear === 2024 && eventMonth <= 6);
			const q1OrQ2_2024 = eventYear === 2024 && (eventMonth === 3 || eventMonth === 6);

			const splitOut = (newName, newUid, splitTotal, remainingTotal) => {
				const extra = { ...JSON.parse(JSON.stringify(pmt)) };
				extra.name = newName;
				extra.uid = newUid;
				extra.prin = splitTotal * ratio;
				extra.int = splitTotal - extra.prin;
				pmt.prin = remainingTotal * ratio;
				pmt.int = remainingTotal - pmt.prin;
				payments.push(extra);
			};

			// PF split applies through Q2 2024
			if (throughQ2_2024 && pmt.uid === 'paflint@gmail.com' && Math.abs(total - 2549.61) < 0.05)
				splitOut('John C Marshall (assumed from PF)', 'marshallj@jmsearch.com', 1445.97, 1103.64);

			// JW split applies only through Q4 2023
			if (throughQ4_2023 && pmt.uid === 'warrackj@jmsearch.com' && Math.abs(total - 14000.13) < 0.05)
				splitOut('John Hildebrand (assumed from JW)', 'hildebrandj@jmsearch.com', 2120.76, 11879.37);

			// In Q1/Q2 2024, JW no longer splits; remove stray Hildebrand row
			if (q1OrQ2_2024 && pmt.uid === 'hildebrandj@jmsearch.com' && Math.abs(total - 2120.76) < 0.05) return;

			// In Q1/Q2 2024, Warrack should be full 14000.13
			if (q1OrQ2_2024 && pmt.uid === 'warrackj@jmsearch.com' && Math.abs(total - 11879.37) < 0.1) {
				const correctTotal = 14000.13;
				pmt.prin = correctTotal * ratio;
				pmt.int = correctTotal - pmt.prin;
			}
		}

		// Safest approach to correct assumed/circular debt in Oct 2024 and Jan 2025
		if ((eventYear === 2024 && eventMonth === 9) || (eventYear === 2025 && eventMonth === 0)) {
			const [int, prin] = eventYear === 2024 ? [121.28, 1324.69] : [116.81, 1329.16];

			const correctForTristaAssumedDebt = () => {
				const thisPmt = { ...JSON.parse(JSON.stringify(pmt)) };
				thisPmt.name = 'John C. Marshall (assumed from TM)';
				thisPmt.uid = 'marshallj@jmsearch.com';
				thisPmt.date = pmt.date;
				thisPmt.prin = prin;
				thisPmt.int = int;
				payments.push(thisPmt);
			};

			if (eventUser === 'marshallj@jmsearch.com') {
				if (eventClass === 'NR' && ['grossro@gmail.com', 'trista.mccarthy@gmail.com'].some((base) => pmt.uid.endsWith(base))) {
					// Circular payment: John now paying himself
					const assumedFrom = pmt.uid.includes('gross') ? 'RGr' : 'TM';
					pmt.name = `John C. Marshall (assumed from ${assumedFrom})`;
					pmt.uid = 'marshallj@jmsearch.com';
				} else if (pmt.uid === 'paflint@gmail.com') {
					// John assumes debt already assumed by Trista from PF
					correctForTristaAssumedDebt();
					pmt.prin -= prin;
					pmt.int -= int;
				}
			} else if (eventUser === 'paflint@gmail.com' && eventClass === 'NR' && pmt.uid === 'marshallj@jmsearch.com') {
				// Reflect circular already-assumed debt: JM -> TM -> PF -> JM
				pmt.prin -= prin;
				pmt.int -= int;
			} else if (eventUser.endsWith('grossro@gmail.com') && eventClass === 'NP' && pmt.uid === 'marshallj@jmsearch.com') {
				// Gross no longer pays (why not Trista or Louis?)
				return;
			}
		} else {
			// Gross should only be suppressed starting with the Q3 2024 payment window
			// (Oct 2024 and later), not in earlier windows like Q4 2023 / Q1 2024 / Q2 2024.
			const suppressGrossToMarshall =
				eventUser.endsWith('grossro@gmail.com') &&
				eventClass === 'NP' &&
				pmt.uid === 'marshallj@jmsearch.com' &&
				((eventYear === 2024 && eventMonth >= 9) || eventYear >= 2025);

			if (suppressGrossToMarshall) return;

			if (eventUser === 'marshallj@jmsearch.com' && eventClass === 'NR' && pmt.uid.endsWith('grossro@gmail.com')) {
				pmt.name = 'John C Marshall (assumed from RGr)';
				pmt.uid = 'marshallj@jmsearch.com';
			}
		}

		// Handle name changes
		const cutovers = {
			'MaryJeanne Scott': { date: moment('2025-09-01', 'YYYY-MM-DD'), newName: 'JM Search in lieu of MJ' },
			'Alissa Moody': { date: moment('2026-03-01', 'YYYY-MM-DD'), newName: 'JM Search in Lieu of AM' },
		};
		if (cutovers[pmt.name] && pmt.date.isSameOrAfter(cutovers[pmt.name].date, 'day')) pmt.name = cutovers[pmt.name].newName;

		payments.push(pmt);
		// console.log("pmt", pmt);
	}

	return payments;
}

export function buildEventsAndTransactionsModal(fromShowDecimals = false, isReport = null, fromScenarioModel = false, overrideClass = null) {
	/* NOTES
	This method creates the Events & Transactions modal
	For each class, it gets relevant data for the active user
	The resultant display can be filtered by class
	For 3rd MG, debt classes can be selected
	USE_GAINS presents ROI an the accrual basis
	Three tables: 1) Events, 2) Debt, 3) Transactions
	*/
	return new Promise((resolve, reject) => {
		try {
			// Guard: ensure core data is available before building modal
			if (!AppState.userData || !AppState.clientData) {
				console.warn('buildEventsAndTransactionsModal: userData or clientData not available');
				resolve();
				return;
			}

			// Build a new transactions list
			let events = [];
			let oneShot = true;
			let allUnfunded = 0;
			let allCommitted = 0;
			let unfundedAmts = [];
			let committedAmts = [];
			let clientEvents = [];
			let transactions = [];
			let includedClasses = [];
			let allClientEvents = [];

			let HIDE_ACCRUALS = !isTCA && !isCER && !isLLProducts; //true;

			const debtEventsTableName = isMAR ? 'userGOAccountEventsSortTable' : 'userDebtEventsSortTable';
			const debtEquityTabs = document.getElementById('debtEquityTabs');
			const showDecimals = document.getElementById('showDecimals');
			if ((isACC || isMAR) && !fromShowDecimals) showDecimals.checked = true;
			if (isMAR) document.getElementById('showDecimalsDiv').classList.add('hidden');

			const viewTransactionsModal = document.getElementById('viewTransactionsModal');
			const eventsTransHelpModal = document.getElementById('eventsTransHelpModal');
			viewTransactionsModal.classList.remove('hidden'); // In case it was left hidden
			const eventsTransHelpTrigger = document.getElementById('eventsTransHelpTrigger');
			if (isJMS || isBOH) {
				eventsTransHelpTrigger.classList.remove('hidden');
				eventsTransHelpTrigger.addEventListener('click', (e) => viewTransactionsModal.classList.add('hidden'));
				M.Modal.init(eventsTransHelpModal, {
					onCloseStart: () => {
						viewTransactionsModal.classList.remove('hidden');
					},
				});
			}

			getEquityData();

			// Deep-clone so mutations don't affect the shared AppState object
			let eventUserData = structuredClone(pn.includes('/admin/users/edit') ? shareholderData : userData);

			if (!eventUserData) return resolve();

			// Support consolidated view
			const etModalUserIds = getActiveUserIds(eventUserData);

			// For consolidated view, use the pre-consolidated transactions if available
			let consolidatedShareClasses = new Set();
			const hasConsolidatedTxs = isConsolidatedView(eventUserData) && eventUserData.clients[client]?.consolidatedTransactions?.length > 0;
			if (hasConsolidatedTxs) {
				transactions = [...eventUserData.clients[client].consolidatedTransactions];
				// Extract unique share classes from consolidated transactions for filter dropdown
				transactions.forEach((t) => {
					if (t.shareClass) consolidatedShareClasses.add(t.shareClass);
				});
			}

			const nowTs = Math.floor(Date.now() / 1000);
			const isAdminEditView = pn.startsWith('/admin/users/edit/');
			classes.forEach(function (c, index) {
				// console.log(clientData.equity[c].transactions)
				let classIncluded = false;
				// Skip clientData filtering only when consolidated transactions are already loaded (EXTERNAL_DATA)
				// For non-EXTERNAL_DATA consolidated view, fall through to standard filtering with etModalUserIds
				if (clientData.equity[c].transactions && !hasConsolidatedTxs) {
					clientData.equity[c].transactions.forEach(function (t) {
						if (isUserTransaction(t, etModalUserIds)) {
							t.shareClass = classes[index];
							transactions.push(t);
							classIncluded = true;
						}
					});
				}
				// For consolidated view, include class if it has consolidated transactions
				if (hasConsolidatedTxs && consolidatedShareClasses.has(c)) {
					classIncluded = true;
				}

				if (clientData.equity[c].events) clientEvents = clientEvents.concat(clientData.equity[c].events);

				// Dont't display future (beyond this year) Events or Transactions
				let nextYear = new Date(new Date().getFullYear() + 1, 1, 1) / 1000;
				transactions = transactions.filter((t) => Number(t.date) < nextYear);
				clientEvents = clientEvents.filter((e) => Number(e.date) < nextYear);

				if (HIDE_ACCRUALS) clientEvents = clientEvents.filter((e) => e.eventType != 'Gain (Loss) Accrual');

				let userShares = eventUserData.clients?.[client]?.shares?.[c];
				if (userShares && userShares.events) {
					//console.log(userClient.shares[c].events);
					userShares.events.forEach(function (i) {
						i.shareClass = classes[index];
						let thisEvent = clientEvents.find((x) => x.nrid == i.nrid);
						// allow negative distributions, and negative gain/loss accruals, and now negative contributions (which should appear as positive!!) ???
						// if (thisEvent && (i.grossDollars > 0 || thisEvent.eventType == "Gain (Loss) Accrual" || thisEvent.eventType == "Distribution" || thisEvent.eventType == "Contribution")) {
						if (thisEvent && (i.grossDollars > 0 || thisEvent.eventType == 'Gain (Loss) Accrual' || thisEvent.eventType == 'Distribution')) {
							const eventTs = Number(thisEvent.record_date || thisEvent.date);
							if (eventTs > nowTs) {
								if (isAdminEditView) {
									i.isFutureEstimated = true;
									events.push(i);
								}
								// else: hide future-dated events from shareholder view
							} else {
								events.push(i);
							}
						}
						classIncluded = true;
					});
				}
				if (classIncluded) includedClasses.push(c);
				//clientEvents = clientEvents.concat(clientData.equity[c].events);

				// sorry about the horribly confusing lingo here
				// for companies that trade shares, users "commit" (=committed) or join shares to the platform
				// for funds that accept investment, LPs "commit" (=approved) capital into the fund
				if (userShares) {
					let committed = Number(userShares.committed);
					if (!committed || committed == 0) {
						committed = Number(userShares.approved) - Number(userShares.owned);
						if (committed < 0) committed = 0; // see similar logic in if(SHOW_AGGREGATE), near Line 5228 (12 lines down)
					}
					// This is tricky and may not the best answer.  LPs need it.  Others don't.
					if (UFL) {
						allUnfunded += committed; // see similar logic if(SHOW_AGGREGATE), near Line 5229 (13 lines down)
						allCommitted += Number(userShares.approved);
						committedAmts.push({ class: classes[index], amount: Number(userShares.approved) });
					}
					unfundedAmts.push({ class: classes[index], amount: committed });
					// committedAmts.push({"class" : classes[index], "amount" : Number(userShares.approved)});
				}
			});

			// if (transactions.length > 0 || events.length > 0) showFilteredEventsTrans();

			// This works too
			//transactions.sort((a, b) => b.date - a.date);
			transactions.sort((a, b) => (Number(a.date) > Number(b.date) ? -1 : 1));
			events.sort((a, b) => (Number(a.date) > Number(b.date) ? -1 : 1));
			allClientEvents = clientEvents;

			// if (transactions.length > 0 || events.length > 0) showFilteredEventsTrans();		// TOO EARLY (JM Search)

			let filterViewET = document.getElementById('filterViewEventsTrans');
			let labelViewET = document.getElementById('labelViewEventsTrans');
			let shareClassFilterWrap = document.getElementById('shareClassFilterWrap');

			// Always show the filter wrapper (for date range filter)
			document.getElementById('filterWrapViewEventsTrans').style.display = 'block';

			if (includedClasses.length <= 1 || isMAR) {
				selectedClass = 'ALL';
				filterViewET.value = 'ALL';
				// Hide only the share class filter wrapper, keep date range visible
				if (shareClassFilterWrap) shareClassFilterWrap.style.display = 'none';
			} else {
				// Show share class filter wrapper
				if (shareClassFilterWrap) shareClassFilterWrap.style.display = '';
				if (labelViewET) {
					labelViewET.innerHTML = clientData.use_real_estate_language ? 'Property' : UFL ? 'Fund' : 'Share&nbsp;Class';
				}

				if (isJMS || isACC) selectedClass = classes[0];
				else {
					includedClasses.unshift('ALL');
					selectedClass = 'ALL';
				}

				// If overrideClass is provided, use it
				if (overrideClass !== null) {
					selectedClass = overrideClass;
				}

				filterViewET.innerHTML = `
			<option value="" disabled>Select</option>
			${includedClasses
				.map(
					(c) =>
						`
					<option ${c === selectedClass ? 'selected' : ''} value="${c}">${c}</option>
				`
				)
				.join('')}
			<label>Fund</label>
		`;
				// No need to set .value - HTML already has correct 'selected' attribute
			}

			if (transactions.length > 0 || events.length > 0) showFilteredEventsTrans(); // OK NOW (JM Search)

			initializeDebtEquityTabs();
			function initializeDebtEquityTabs() {
				let instance = M.Tabs.init(debtEquityTabs, {});
				// debtEquityTabs.classList.add('hidden');
				setTimeout(() => {
					if (showTabs) {
						debtEquityTabs.classList.remove('hidden');
						instance.updateTabIndicator();
						console.log('Intialized Debt-Equity tabs');
					}
				}, 100);
			}

			let USE_GAINS = isCER || isORR || isTSG || isStaging || ['conestoga', 'theclosingagent'].includes(client);

			if (USE_GAINS && !clientData?.flags?.hide_ET_stats) {
				let includeGainsElement = document.getElementById('includeGainsElement');
				if (!isORR) includeGainsElement.classList.remove('hidden');
				narrow = screen.width < 600;
				if (narrow) {
					includeGainsElement.innerHTML = `
				<label>
					Cash Only
					<input id="includeGainsToggle" type="checkbox">
					<span class="lever"></span>
					With Gains
				</label>
			`;
				} else {
					includeGainsElement.innerHTML = `
				<label>
					Cash Yield Only
					<input id="includeGainsToggle" type="checkbox">
					<span class="lever"></span>
					Include Capital Gains
				</label>
			`;
				}
				document.getElementById('includeGainsElement').addEventListener('change', function (e) {
					selectedClass = filterViewET ? filterViewET.value : classes[0];
					showFilteredEventsTrans();
				});
			}

			let options = {
				dropdownOptions: {
					coverTrigger: false,
				},
			};
			let elems = document.querySelectorAll('select');
			let instances = M.FormSelect.init(elems, options);

			async function showFilteredEventsTrans() {
				// console.log(events)
				let filteredEvents = events;
				let filteredUnfunded = allUnfunded;
				let filteredCommitted = allCommitted;
				let filteredTransactions = transactions;
				let filteredEquityTransactions = transactions.filter(
					(t) => !clientData.equity[t.shareClass].hasOwnProperty('interestRate') || (isCER && t.shareClass == 'CCA')
				);
				let filteredDebtTransactions = transactions.filter(
					(t) => clientData.equity[t.shareClass].hasOwnProperty('interestRate') && (!isCER || t.shareClass != 'CCA')
				);
				if (!selectedClass || selectedClass == 'ALL') {
					clientEvents = allClientEvents;
				} else {
					clientEvents = clientData.equity[selectedClass].events;
					filteredTransactions = filteredTransactions.filter(function (item) {
						if (item.shareClass) return item.shareClass == selectedClass;
						else return true;
					});
					filteredEquityTransactions = filteredEquityTransactions.filter(function (item) {
						if (item.shareClass) return item.shareClass == selectedClass;
						else return true;
					});
					filteredDebtTransactions = filteredDebtTransactions.filter(function (item) {
						if (item.shareClass) return item.shareClass == selectedClass;
						else return true;
					});
					filteredEvents = filteredEvents.filter(function (item) {
						if (item.shareClass) {
							return item.shareClass == selectedClass;
						} else return true;
					});
					let classUnfunded = unfundedAmts.find((item) => item.class == selectedClass);
					filteredUnfunded = classUnfunded ? classUnfunded.amount : 0;
					let classCommitted = committedAmts.find((item) => item.class == selectedClass);
					filteredCommitted = classCommitted ? classCommitted.amount : 0;
				}

				// Apply date range filter
				const dateRange = getDateRangeTimestampsET(selectedETDateRange, customDateStartET, customDateEndET);
				if (dateRange) {
					filteredEvents = filterByDateRangeET(filteredEvents, dateRange);
					filteredEquityTransactions = filterByDateRangeET(filteredEquityTransactions, dateRange);
					filteredDebtTransactions = filterByDateRangeET(filteredDebtTransactions, dateRange);
				}

				isReport = isMAR || isBB ? true : null;
				renderUserTransactionsModal(filteredEquityTransactions, false, isReport);
				renderUserTransactionsModal(filteredDebtTransactions, true, isReport);
				renderUserEquityModal(
					filteredEvents,
					filteredEquityTransactions,
					filteredUnfunded,
					filteredCommitted,
					isReport,
					dateRange,
					transactions,
					events
				);
				if (isMAR) {
					// MARVIN GO ACCOUNT
					let qbNthMap = await getContentUrl('Marvi.QB.vs.Cap.Table.csv');
					fetch(qbNthMap)
						.then(function (response) {
							return response.text();
						})
						.then(async function (map) {
							let qbIntegrationFile = await getContentUrl(clientData.integration_data);
							fetch(qbIntegrationFile)
								.then(function (response) {
									return response.text();
								})
								.then(function (csv) {
									const mapRows = cleanCSV(map);
									const thisOwner = pn.includes('/users/edit') ? shareholderData.userid : userData.userid;
									// Find the row where the third column matches the userid
									const match = mapRows.find((row) => row[2]?.trim() === thisOwner.trim());
									// Return the value in the first column, or null if not found
									const ownerName = match[0] || null;
									//const ownerName = pn.includes('/users/edit') ? shareholderData.user_name : userData.user_name;
									const rows = cleanCSV(csv);
									let startRow = -1;
									let endRow = -1;
									rows.forEach((row, index) => {
										if (row[0].replace(/\./g, '') === ownerName.replace(/\./g, '')) startRow = index;
										if (row[0].replace(/\./g, '') === 'Total for ' + ownerName.replace(/\./g, '')) endRow = index;
									});

									// Remapping export schema
									// QB -> QBO rows
									// Date 4 -> 1
									// Type 3 -> 2
									// Memo 7 -> 5
									// Name 6 -> 4
									// Num  5 -> 3

									const ownerRows = rows.slice(startRow + 1, endRow);
									const qbEvents = ownerRows.map((row) => ({
										nrid: uuidv4(),
										grossDollars: row[8]?.replace(/[^-.\d]/g, '').trim() || '0',
										balance: row[9]?.replace(/[^-.\d]/g, '').trim() || '0',
										notes: row[3] || '',
										shareClass: 'GO',
										date: moment(row[1], dateFormats).unix(),
										description: joinDesc(row) || 'Miscellaneous',
										eventType: row[2] || 'Other',
									}));

									// function joinDesc (row) {
									// 	let desc = row[7]?.trim();
									// 	let row6 = row[6]?.trim()
									// 	if (row6 && !desc?.includes(row6)) return desc + ' - ' + row6;
									// 	else return desc;
									// }

									function joinDesc(row) {
										// Regex pattern to match PII-like formats (e.g., SSN or EIN: ###-##-#### or ##-#######)
										const piiRegex = /\b\d{2,3}-\d{2}-\d{4}\b|\b\d{2}-\d{7}\b/;
										// Get and clean column 7
										let desc = row[5]?.trim();
										if (desc) {
											// Remove PII-like patterns from the description
											desc = desc.replace(piiRegex, '').trim();
											// Remove leftover punctuation (commas, semicolons, etc.) if they are at the edges
											desc = desc.replace(/^[,;.-\s]+|[,;.-\s]+$/g, '');
										}
										// Get and clean column 6
										let row6 = row[4]?.trim();
										// Append column 6 if it's not already included in column 7's cleaned description
										if (row6 && !desc?.includes(row6)) return desc ? `${desc} - ${row6}` : row6;
										return desc || '';
									}

									// Apply date range filter to QB events (GO Account)
									const qbDateRange = getDateRangeTimestampsET(selectedETDateRange, customDateStartET, customDateEndET);
									let filteredQbEvents = qbEvents;
									if (qbDateRange) {
										filteredQbEvents = filterByDateRangeET(qbEvents, qbDateRange);
									}

									renderUserDebtModal(filteredQbEvents, [], false);
								})
								.catch(function (err) {
									console.warn('Something went wrong.', err);
								});
						})
						.catch(function (err) {
							console.warn('Something went wrong.', err);
						});
				} else renderUserDebtModal(filteredEvents, filteredDebtTransactions, isReport);
			}

			function renderUserTransactionsModal(transactions, itsDebt, isReport = null) {
				// By date then amount
				transactions.sort(function (a, b) {
					let aDate = Number(a.date);
					let bDate = Number(b.date);
					if (moment(1000 * aDate).format('DDMMYY') == moment(1000 * bDate).format('DDMMYY')) {
						// Amount is only important when dates are the same
						return (
							Number(b.quantity.replace(/[^-.\d]/g, '')) * Number(b.sharePrice.replace(/[^.\d]/g, '')) -
							Number(a.quantity.replace(/[^-.\d]/g, '')) * Number(a.sharePrice.replace(/[^.\d]/g, ''))
						);
					}
					return aDate < bDate ? 1 : -1;
				});

				// Compile list of reference prices from Valuations
				let referenceVals = [];
				classes.forEach(function (c) {
					if (clientData.equity[c] && clientData.equity[c].valuations) {
						clientData.equity[c].valuations.forEach(function (v) {
							let ref = {
								class: c,
								date: v.date,
								price: v.sharePrice,
								ref: v.referencePrice,
							};
							referenceVals.push(ref);
						});
					}
				});

				Object.keys(transactions).forEach(function (t, index) {
					let trans = transactions[t];
					// insert reference price if available
					let refPrice = referenceVals.find(
						(v) =>
							v.class == trans.shareClass &&
							Number(v.price) == Number(trans.sharePrice) &&
							Math.abs(Number(v.date) - Number(trans.date)) <= 2 * 86400
					);
					if (refPrice) transactions[index].referencePrice = refPrice.ref;

					// use override names as appropriate -- DOES NOT WORK FOR USERS (WHO HAVE NO CAP TABLE ACCESS)
					// transactions[index].receiverName = nameWithOverride(trans.receiver);
					// transactions[index].senderName = nameWithOverride(trans.sender);
					let thisName = nameWithOverride(trans.sender);
					if (thisName !== 'UNKNOWN') transactions[index].senderName = thisName;
					thisName = nameWithOverride(trans.receiver);
					if (thisName !== 'UNKNOWN') transactions[index].receiverName = thisName;
				});

				let stringOptions = showDecimals.checked ? m2 : m0;
				let sortedSortedTransactions;
				let reportTotalTransactionsAmount = null;

				if (isBB && isReport) {
					try {
						// Sorts table conditionally for Modal vs Report
						sortedSortedTransactions = transactions.sort((a, b) => {
							// Check if dates exist before comparing
							const dateA = a.date && typeof a.date === 'number' ? a.date : Date.now();
							const dateB = b.date && typeof b.date === 'number' ? b.date : Date.now();
							return dateA - dateB;
						});

						// Calculate the sum of the amounts for 2023 events
						reportTotalTransactionsAmount = sortedSortedTransactions.reduce((acc, event) => {
							let amount = Number(event.sharePrice) * Number(event.quantity);
							return acc + amount;
						}, 0);
					} catch (error) {
						console.error('Error sorting transactions:', error);
						// Provide a default value or fallback behavior
						sortedSortedTransactions = [];
						reportTotalTransactionsAmount = 0;
					}
				} else {
					sortedSortedTransactions = transactions;
					reportTotalTransactionsAmount = 0;
				}

				// console.log(reportTotalTransactionsAmount);

				const SHOW_BALANCES = clientData?.flags?.show_ET_balances;
				let finalBalances = {}; // Track the final balances for each share class
				let balanceHistory = {}; // Store balance for each transaction using nrid as the key
				if (SHOW_BALANCES) {
					let localUserData = pn.includes('/users/edit') ? shareholderData : userData;
					// Calculate balances using the shadow copy, but don't modify the original transactions array
					// Create a shadow copy of transactions and sort it by date in reverse order (newest to oldest)
					let shadowTransactions = transactions.slice().sort((a, b) => b.date - a.date);
					// Initialize final balance for each share class
					Object.keys(localUserData.clients[client].shares).forEach((shareClass) => {
						// Defensive check: ensure owned exists and convert to string before replace
						let owned = localUserData.clients[client].shares[shareClass].owned;
						if (owned === null || owned === undefined) {
							console.warn(`renderUserTransactionsModal: owned is null/undefined for shareClass ${shareClass}`);
							finalBalances[shareClass] = 0;
							return;
						}
						// Convert to string first to handle both string and number types
						finalBalances[shareClass] = Number(String(owned).replace(/[^.\d]/g, ''));
					});
					// Support consolidated view
					const shadowUserIds = getActiveUserIds(localUserData);

					// Iterate through shadow copy and calculate the balance
					for (let i = 0; i < shadowTransactions.length; i++) {
						let shadowTrans = shadowTransactions[i];
						let shareClass = shadowTrans.shareClass;
						// Defensive check: convert quantity to string before replace to handle both string and number types
						let quantity = Number(String(shadowTrans.quantity || '0').replace(/[^.\d]/g, '')) || 0;
						// Store the current balance in the balanceHistory before processing the transaction
						balanceHistory[shadowTrans.nrid] = isNaN(finalBalances[shareClass]) ? 0 : finalBalances[shareClass];
						// Check if the receiver is the user (balance increases) or the sender (balance decreases)
						const receiverLower = shadowTrans.receiver?.toLowerCase();
						const senderLower = shadowTrans.sender?.toLowerCase();
						const isReceiver = receiverLower && shadowUserIds.some((uid) => uid?.toLowerCase() === receiverLower);
						const isSender = senderLower && shadowUserIds.some((uid) => uid?.toLowerCase() === senderLower);
						if (isReceiver && isSender) {
						} // Internal transfer between consolidated users - no net effect
						else if (isReceiver)
							finalBalances[shareClass] -= quantity; // Subtract to get the previous balance
						else if (isSender) finalBalances[shareClass] += quantity; // Add to get the previous balance
					}
				}

				const SHOW_EXACT = transactions.some((txn) => {
					const price = txn.sharePrice;
					if (!price || isNaN(price)) return false;
					const decimal = price.split('.')[1];
					return decimal && decimal.length > 2;
				});

				// Check if we're in consolidated view for holder attribution
				const showHolderColumn = isConsolidatedView(eventUserData);

				function getSyntheticCertSort(trans) {
					let certNumber = '';
					if (trans?.notes) {
						const certMatch = getCertNumber(trans.notes);
						if (certMatch && certMatch[2]) certNumber = certMatch[2];
					}
					if (!certNumber) return 0;
					const certTail = certNumber.split('-')[1];
					return Number((certTail ? certTail : certNumber).replace(/[^\d.]/g, '')) || 0;
				}

				function hasHashCert(trans) {
					return !!(trans?.notes && getCertNumber(trans.notes) && getCertNumber(trans.notes)[2]);
				}

				transactions.sort((a, b) => {
					const dayDiff = (normalizeDateString(b.date, true) || '').localeCompare(normalizeDateString(a.date, true) || '');
					if (dayDiff !== 0) return dayDiff;

					const aHasCert = hasHashCert(a);
					const bHasCert = hasHashCert(b);

					// Only trust cert sort when BOTH sides have a real # cert
					if (aHasCert && bHasCert) {
						const certDiff = getSyntheticCertSort(b) - getSyntheticCertSort(a);
						if (certDiff !== 0) return certDiff;
						const descDiff = String(b.notes || '').localeCompare(String(a.notes || ''));
						if (descDiff !== 0) return descDiff;
					}

					const senderDiff = (a.senderName || 'Issuer').toLowerCase().localeCompare((b.senderName || 'Issuer').toLowerCase());
					if (senderDiff !== 0) return senderDiff;

					const receiverDiff = (a.receiverName || 'Issuer').toLowerCase().localeCompare((b.receiverName || 'Issuer').toLowerCase());
					if (receiverDiff !== 0) return receiverDiff;

					const qtyDiff = (Number(a._qtyNum) || 0) - (Number(b._qtyNum) || 0);
					if (qtyDiff !== 0) return qtyDiff;

					return String(a.nrid || '').localeCompare(String(b.nrid || ''));
				});

				let userTransactionsMarkup = `
					<div class="table-wrap" style="width: 100% !important">
						<div class="sm-hz-scroll-no-margin">
							<h5 class="text-xl font-bold mt-4 ml-4">Transactions</h5>
							${
								transactions.length
									? `
								<table id="user${itsDebt ? 'Debt' : 'Equity'}TransactionsSortTable" class="highlight table-content" style="width: 100% !important">
									<thead>
									  <tr class="text-xs" style="text-align: center !important;">
										<!-- <th class="text-xs ml-1">${isJMS ? 'Class' : UFL ? 'Fund' : 'Share Class'}</th> -->
										<th class="text-xs ml-1">${labelInvestments ? labelInvestments : clientData.use_real_estate_language ? 'Property' : UFL ? 'Fund' : 'Class'}</th>
										${showHolderColumn ? `<th class="left-align ml-1">Account</th>` : ``}
										<th class="left-align ml-1">Date</th>
										<th>Description</th>
										<th>Sender</th>
										<th>Receiver</th>
										<!-- NOTE: the order below matters for Symmetrical and MH -->
										${
											!isCER && UFL
												? `
											<th class="center-align">Contribution</th>
										`
												: clientData.use_real_estate_language
													? `
											<th class="center-align">Ownership</th>
										`
													: `
											<th>${isASP ? 'Shares' : 'Quantity'}</th>
											<th class="center-align">${labelCostPrice}</th>
											${
												SHOW_BALANCES
													? `<th class="center-align">Balance</th>
										`
													: ``
											}
										${
											labelCostPrice.toLowerCase().includes('cost')
												? `
											${SHOW_EXACT ? `<th class="center-align hidden">Exact Cost</th>` : ``}
											<th class="center-align hidden">Reference Cost</th>
											${SHOW_EXACT ? `<th class="center-align hidden">Qty * Exact Cost</th>` : ``}
											<th class="center-align hidden">Qty * Ref. Cost</th>
										`
												: `
											${SHOW_EXACT ? `<th class="center-align hidden">Exact Price</th>` : ``}
											${USE_TAX_BASIS ? `<th class="center-align">${labelTaxBasis}</th>` : ``}
											<th class="center-align hidden">Reference Price</th>
											${SHOW_EXACT ? `<th class="center-align hidden">Qty * Exact Price</th>` : ``}
											<th class="center-align hidden">Qty * Ref. Price</th>
										`
										}
										<th class="center-align ${SHOW_TAX_BASIS_COLUMN ? `` : `hidden`}">Tax Basis</th>
										`
										}
										${isJMS ? `<th></th` : ``}
									  </tr>
									</thead>
									<tbody>
									${transactions
										.map(
											(transaction, index) =>
												`
										<tr style="min-height: 51px">
											<td class="text-xs ml-1">
												<span class="pill pill--gray">${transaction.shareClass}</span></a>
											</td>
											${
												showHolderColumn
													? `
											<td class="text-xs left-align">
												${transaction.holderName || transaction.holder || ''}
											</td>
											`
													: ``
											}
											<td class="text-xs left-align" data-sort='${normalizeDateString(transaction.date, true)}'>
												${fastFormatDate(transaction.date)}
											</td>
											<td class="text-xs">
												${transaction.notes}
											</td>
											<td class="text-xs">
												${transaction.senderName ? transaction.senderName : 'Issuer'}
											</td>
											<td class="text-xs">
												${transaction.receiverName ? transaction.receiverName : 'Issuer'}
											</td>
											${
												(!isCER && UFL) || clientData.use_real_estate_language
													? `
												<td class="text-xs center-align" data-sort='${Number(transaction.quantity.replace(/[^-.\d]/g, '')) * (transaction.sender == shareholderData?.userid ? -1 : 1)}'>
													${
														transaction.sender == shareholderData?.userid || transaction.quantity.startsWith('-')
															? `
														($${Math.round(Number(transaction.quantity.replace(/[^.\d]/g, '')) * Number(transaction.sharePrice.replace(/[^.\d]/g, ''))).toLocaleString('en-US', m5)})
													`
															: `
														$${Math.round(Number(transaction.quantity.replace(/[^-.\d]/g, '')) * Number(transaction.sharePrice.replace(/[^.\d]/g, ''))).toLocaleString('en-US', m5)}
													`
													}
												</td>
												`
													: ` 
												<td class="text-xs center-align" data-sort='${Number(transaction.quantity.replace(/[^-.\d]/g, '')) * (transaction.sender == shareholderData?.userid ? -1 : 1)}'>
													${
														transaction.sender == shareholderData?.userid || transaction.quantity.startsWith('-')
															? `
														(${Number(transaction.quantity.replace(/[^.\d]/g, '')).toLocaleString('en-US', isMAR ? stringOptions : isTBC ? m6 : m5)})
													`
															: `
														${Number(transaction.quantity.replace(/[^-.\d]/g, '')).toLocaleString('en-US', isMAR ? stringOptions : isTBC ? m6 : m5)}
													`
													}
												</td>
												<td class="text-xs center-align">
													${!transaction.sharePrice || isNaN(transaction.sharePrice) || (isTBC && Number(transaction.sharePrice) == 0) ? 'n.a.' : Number(transaction.sharePrice.replace(/[^.\d]/g, '')).toLocaleString('en-US', usp)}
												</td>

												${
													SHOW_BALANCES
														? `
													<td class="text-xs center-align">
														${balanceHistory[transaction.nrid]?.toLocaleString('en-US', isMAR ? stringOptions : isTBC ? m6 : m5)}
													</td>
												`
														: ``
												}
												
												${
													SHOW_EXACT
														? `
													<td class="text-sm center-align hidden">
														${isNaN(Number(transaction.sharePrice.replace(/[^.\d]/g, ''))) ? 'n.a.' : Number(transaction.sharePrice.replace(/[^.\d]/g, '')).toLocaleString('en-US', usp)}
													</td>
												`
														: ``
												}

												${
													USE_TAX_BASIS
														? `
													<td class="text-xs center-align">
														${!transaction.strikePrice || isNaN(transaction.strikePrice) ? '' : Number(transaction.strikePrice.replace(/[^.\d]/g, '')).toLocaleString('en-US', usp)}
													</td>
												`
														: ``
												}
												<td class="text-sm center-align hidden">
													${transaction.referencePrice ? Number(transaction.referencePrice.replace(/[^.\d]/g, '')).toString() : ''}
												</td>
												${
													SHOW_EXACT
														? `
													<td class="text-sm center-align hidden">
														${
															transaction.quantity
																? transaction.sender == shareholderData.userid || transaction.quantity.startsWith('-')
																	? `
																(${(Number(transaction.quantity.replace(/[^.\d]/g, '')) * Number(transaction.sharePrice.replace(/[^.\d]/g, ''))).toLocaleString('en-US', usp)})
															`
																	: `
																${(Number(transaction.quantity.replace(/[^-.\d]/g, '')) * Number(transaction.sharePrice.replace(/[^.\d]/g, ''))).toLocaleString('en-US', usp)}
															`
																: ''
														}
													</td>
												`
														: ``
												}
												<td class="text-sm center-align hidden">
													${
														transaction.quantity && transaction.referencePrice
															? transaction.sender == shareholderData.userid || transaction.quantity.startsWith('-')
																? `
															(${(Number(transaction.quantity.replace(/[^.\d]/g, '')) * Number(transaction.referencePrice.replace(/[^.\d]/g, ''))).toLocaleString('en-US', usp)})
														`
																: `
															${(Number(transaction.quantity.replace(/[^-.\d]/g, '')) * Number(transaction.referencePrice.replace(/[^.\d]/g, ''))).toLocaleString('en-US', usp)}
														`
															: ''
													}
												</td>
												<td class="text-xs center-align ${SHOW_TAX_BASIS_COLUMN ? `` : `hidden`}">
													${!transaction.taxBasis || isNaN(transaction.taxBasis) ? '' : Number(transaction.taxBasis.replace(/[^-.\d]/g, '')).toLocaleString('en-US', usp)}
												</td>
											`
											}
										${
											isJMS
												? `
											<td class="text-xxs center-align">
												${
													moment.unix(transaction.date).year() < 2013
														? `n.a.`
														: transaction.referencePrice && !transaction.notes.includes('Lump Sum)')
															? `
													<a href="#" onClick="generateAmortizationSchedule('${transaction.nrid}')"><span class="link-accent">Amortization</span></a>
												`
															: `
													<span>Lump Sum</span>
												`
												}
											</td>
											`
												: ``
										}
										${
											isBOH
												? `
											<td class="text-xxs center-align">
												${/\(\s*(NP|NR)(-\d+)?[^)]*\)/.test(transaction.notes) ? `<a href="#" onClick="generateAmortizationSchedule('${transaction.nrid}')"><span class="link-accent">Amortization</span></a>` : ``}
											</td>
											`
												: ``
										}
										</tr>
										`
										)
										.join('')}
									${
										isBB && reportTotalTransactionsAmount
											? `
										<tr class="total-row hidden">
											<td></td>
											<td></td>
					                        <td class="text-xs">Total:</td>
											<td></td>
											<td></td>
					                        <td class="text-xs center-align" data-sort='${reportTotalTransactionsAmount}'>
					                            $${reportTotalTransactionsAmount.toLocaleString('en-US', stringOptions)}
					                        </td>
											<td></td>
											<td></td>
											<td></td>
					                    </tr>
										`
											: ``
									}
									</tbody>
								</table>
							`
									: `
								<div class="bg-grey-lightest rounded border-grey grey-text center-align p-2 ml-4 mr-4">
									No transactions found for the selected filters.
								</div>
							`
							}
						</div>
					</div>
				`;

				let table, sort;
				if (itsDebt) {
					// Clear previous Tablesort instance
					if (debtTableSort) {
						try {
							debtTableSort.refresh = null; // Clear references
						} catch (e) {}
						debtTableSort = null;
					}

					document.getElementById('userDebtTransactionsTable').innerHTML = userTransactionsMarkup;
					table = document.getElementById('userDebtTransactionsSortTable');
					if (table) {
						sort = new Tablesort(table, { descending: true });
						debtTableSort = sort; // Store instance globally
					}
				} else {
					// Clear previous Tablesort instance
					if (equityTableSort) {
						try {
							equityTableSort.refresh = null; // Clear references
						} catch (e) {}
						equityTableSort = null;
					}

					document.getElementById('userEquityTransactionsTable').innerHTML = userTransactionsMarkup;
					table = document.getElementById('userEquityTransactionsSortTable');
					if (table) {
						sort = new Tablesort(table, { descending: true });
						equityTableSort = sort; // Store instance globally
					}
				}
			}

			function renderUserEquityModal(
				events,
				equityTransactions,
				unfunded,
				committed,
				isReport = null,
				dateRange = null,
				allTransactions = null,
				allEvents = null
			) {
				let totalAccruals = 0;
				let totalProceeds = 0;
				let totalRedemption = 0;
				let totalContribution = 0;
				let totalWithholdings = 0;
				let totalBasisStepDown = 0;
				let totalUnrealizedGain = 0;
				let totalReturnOfCapital = 0;
				let totalCashDistribution = 0;
				let totalDebtPrincipalFunded = 0;

				if (!userData) AppState.userData = JSON.parse(sessionStorage.getItem('user.Data'));
				let ownerId = userData.userid;
				let ownerData = userData.clients[client];
				if (pn.includes('/users/edit')) {
					if (!shareholderData) shareholderData = JSON.parse(sessionStorage.getItem('shareholder.Data'));
					ownerId = shareholderData.userid;
					ownerData = shareholderData.clients[client];
				}

				// Check if we're in consolidated view for holder attribution
				const showHolderColumn = isConsolidatedView(userData);

				let equityEvents = [];
				events.forEach(function (e) {
					let clientEvent = clientEvents.find((obj) => obj.nrid == e.nrid);
					if (clientEvent) {
						if (e.notes || e.isFutureEstimated) hasEventNotes = true;
						if (
							!['Principal Funded', 'Principal Repayment', 'Interest Accrual', 'Interest Payment', 'Withholding'].includes(clientEvent.eventType)
						) {
							equityEvents.push(e);
						} else {
							let grossDollars = Number(e.grossDollars) || 0;
							if (clientEvent.eventType == 'Principal Funded') {
								totalDebtPrincipalFunded += grossDollars;
							} else if (clientEvent.eventType == 'Principal Repayment') {
								totalDebtPrincipalFunded -= grossDollars;
							}
						}
					}
				});

				committed -= totalDebtPrincipalFunded; // <-- move down below transactions??? (4/23/2022)

				let eventsLength = 0;
				let valuations = {};
				let equityKeys = [selectedClass];
				if (!selectedClass || selectedClass == 'ALL') equityKeys = classes;

				let ownerEquity = ownerData.shares;
				equityKeys.forEach(function (e) {
					let newVal;
					let newest = 0;
					let itsDebt = clientData.equity[e].hasOwnProperty('interestRate');
					eventsLength += ownerEquity[e] && ownerEquity[e].events && !itsDebt ? ownerEquity[e].events.length : 0;
					let vals = clientData.equity[e].valuations;
					if (vals) {
						if (dateRange) {
							// When date-filtered, use the valuation closest to (but not after) the end date
							vals.forEach(function (v) {
								let vDate = Number(v.date);
								if (vDate <= dateRange.end && vDate > newest) {
									newest = vDate;
									newVal = v;
								}
							});
						} else {
							vals.forEach(function (v) {
								if (Number(v.date) > newest) {
									newest = Number(v.date);
									newVal = v;
								}
							});
						}
						if (newVal) valuations[e] = newVal;
					}
				});

				incGains = false;
				let gainsToggle = document.getElementById('includeGainsToggle');
				if (gainsToggle) incGains = gainsToggle.checked;
				if (equityEvents.length == 0 && eventsLength == 0 && oneShot) {
					incGains = true;
					oneShot = false;
					if (gainsToggle) gainsToggle.checked = true;
				}
				// if (isUA) incGains = true;
				// if (isACR) incGains = false;

				if (isJMS) {
					if (selectedClass == 'JMS') incGains = true;
					else incGains = false;
				}

				// master override
				if (clientData.flags && 'include_gains' in clientData.flags) incGains = clientData.flags.include_gains;

				// When date-filtered, compute cumulative totals up to the period end date
				// (not just within the period) so current value reflects all prior activity
				let effectiveTransactions = equityTransactions;
				let effectiveEvents = events;
				if (dateRange && allTransactions) {
					// Use all transactions up to the end of the filter period, respecting class filter
					effectiveTransactions = allTransactions.filter(function (t) {
						if (clientData.equity[t.shareClass]?.hasOwnProperty('interestRate')) return false;
						if (selectedClass && selectedClass !== 'ALL' && t.shareClass !== selectedClass) return false;
						return Number(t.date) <= dateRange.end;
					});
				}
				if (dateRange && allEvents) {
					effectiveEvents = allEvents.filter(function (e) {
						if (selectedClass && selectedClass !== 'ALL' && e.shareClass && e.shareClass !== selectedClass) return false;
						let eDate = Number(e.date);
						if (!eDate) {
							let ce = clientEvents.find((obj) => obj.nrid == e.nrid);
							eDate = ce ? Number(ce.date) : 0;
						}
						return eDate <= dateRange.end;
					});
				}

				effectiveTransactions.forEach(function (t) {
					// if (bases[t.shareClass] == -1) bases[t.shareClass] = 0;
					let tShPrice = Number(t.sharePrice) ? Number(t.sharePrice) : 0; //handles NaN share prices
					let dollarAmount = (Number(t.quantity) || 0) * tShPrice;

					// We need to filter out stock swaps and stock splits since they are non-cash, e.g. not a proceed or return
					// Look for same-date, same-amount, reverse transaction(s) (sender <> receiver)
					// Not sure this handles "Issuer" hack correctly
					let isSwapOrSplit = false;
					let reverseTransactions = equityTransactions.filter((f) => f.receiver == t.sender && f.sender == t.receiver);
					if (reverseTransactions && reverseTransactions.length > 0) {
						let sameDateTransactions = reverseTransactions.filter((f) => Math.abs(Number(t.date) - Number(f.date)) < 175000);
						if (sameDateTransactions && sameDateTransactions.length > 0) {
							let sumSameDateAmount = sameDateTransactions.reduce((a, b) => a + Number(b.quantity) * Number(b.sharePrice), 0);
							if (Math.abs(dollarAmount - sumSameDateAmount) < 3) isSwapOrSplit = true;
							sameDateTransactions.forEach((s) => {
								if (Math.abs(dollarAmount - Number(s.quantity) * Number(s.sharePrice)) < 3) isSwapOrSplit = true;
							});
						}
					}

					const BLOCK_ROC_DOUBLE_COUNT = clientData.flags?.block_roc_double_count; // isMH || isSEQ || isAlera || isJMS || isMAZ || isBOH

					if (t.sender == ownerId) {
						if (!isSwapOrSplit) {
							let giftTransaction = t.notes && t.notes.toLowerCase().includes('gift');
							if (!giftTransaction) {
								if (incGains) totalProceeds += USE_STRIKE ? Math.abs(dollarAmount) : dollarAmount;
								// I don't really understand this.  It fails for Jim Maguire's Maguire Hayden 3/21/2022
								// Maybe do this for all clients where UFL == true?? 7/30/2023
								// Causes double-counting a Realized Return for Mazzetti.  Why not for everyone???  11/8/2023
								// In theory RoC is up to invested $$, then it becomes taxable income, right?
								if (!BLOCK_ROC_DOUBLE_COUNT) totalReturnOfCapital += dollarAmount;
								// else totalReturnOfCapital += dollarAmount;
							} else console.log('GIFT = ' + dollarAmount);
						}

						// *** NOT SURE ABOUT THIS ***  added for 3rdMG 100% redemption, but it really screwed up JMS
						// It also royally screwed up Bill's Conestoga account
						// Needed for Jim Maguire's Maguire Hayden 12/16/2021
						if (is3MG || isMH) totalContribution -= dollarAmount;
						// if (!isJMS) totalContribution -= dollarAmount;
						if (t.securityType == 'Debt') totalDebtPrincipalFunded -= dollarAmount; // added on 4/23/2022, maybe irrelevant
						if (USE_STRIKE) {
							// Assume highest cost basis for sold shares (big assumption!)
							// To be precise, we need to track "consumed" quantity of each tranche
							let highestCostBasis = 0.0001;
							equityTransactions.forEach((s) => {
								let sShPrice = Number(s.sharePrice) ? Number(s.sharePrice) : 0; //handles NaN share prices
								highestCostBasis =
									t.shareClass == s.shareClass && s.receiver == ownerId && sShPrice > highestCostBasis ? sShPrice : highestCostBasis;
							});
							totalBasisStepDown += Number(t.quantity) * (tShPrice - highestCostBasis);
							console.log('totalBasisStepDown = ' + totalBasisStepDown);
						}
					} else {
						// t.receiver == ownerId
						if (!isSwapOrSplit) totalContribution += dollarAmount;
						if (t.securityType == 'Debt') totalDebtPrincipalFunded += dollarAmount; // added on 4/23/2022, maybe irrelevant
					}
				});

				effectiveEvents.forEach(function (e) {
					let clientEvent = clientEvents.find((obj) => obj.nrid == e.nrid);
					if (clientEvent) {
						e.date = clientEvent.date;
						e.description = clientEvent.notes;
						e.eventType = clientEvent.eventType;
						let grossDollars = Number(e.grossDollars) || 0;
						switch (e.eventType) {
							case 'Withholding':
								totalWithholdings += grossDollars;
								break;
							case 'Contribution':
								totalContribution += grossDollars;
								break;
							case 'Distribution':
								totalCashDistribution += grossDollars;
								break;
							case 'Cash Dividend':
								totalCashDistribution += grossDollars;
								break;
							case 'Gain (Loss) Accrual':
								totalAccruals += grossDollars;
								break;
							case 'Return of Capital':
								totalReturnOfCapital += grossDollars;
								break;
							// totalContribution -= grossDollars;  // <-- Is this correct?  Added but left commented out on 12/3/2021
							case 'Redemption':
								totalProceeds += grossDollars;
								totalRedemption += grossDollars;
								totalContribution -= grossDollars;
								break;
						}
					}
				});

				let periodMarketValue = 0;
				equityKeys.forEach(function (b) {
					let strikePrice = getEffectiveStrikePriceForUser(clientData.equity[b], ownerId, {
						useWeighted: true,
						netContra: true,
						outstandingOnly: true,
					});

					if (strikePrice > 0) {
						clientData.equity[b].overrideCbPrice = strikePrice;
						clientData.equity[b].overrideCbLabel = 'Weighted Avg ' + clientData.equity[b].costbasisLabel;
					} else {
						delete clientData.equity[b].overrideCbPrice;
						delete clientData.equity[b].overrideCbLabel;
					}

					let shares = ownerData.shares[b];
					let ownedShares = shares ? Number(shares.owned) : 0;
					let periodBasis = bases[b];
					let isDebt = clientData.equity[b].hasOwnProperty('interestRate');

					// When date-filtered, reconstruct ownership and basis at period end
					// by removing the effect of transactions after the filter end date
					if (dateRange && allTransactions) {
						allTransactions.forEach(function (t) {
							if (t.shareClass !== b) return;
							let tDate = Number(t.date);
							if (tDate <= dateRange.end) return; // transaction is within the period
							let qty = Number(t.quantity) || 0;
							let dollarAmt = qty * (Number(t.sharePrice) || 0);
							if (t.receiver === ownerId) {
								ownedShares -= qty; // undo a post-period acquisition
								if (periodBasis !== -1) periodBasis -= dollarAmt; // undo basis added
							}
							if (t.sender === ownerId) {
								ownedShares += qty; // undo a post-period sale
								if (periodBasis !== -1) periodBasis += dollarAmt; // undo basis removed
							}
						});
					}

					let currentVal = ownedShares * (valuations[b] ? Number(valuations[b].sharePrice) - (strikePrice ? strikePrice : 0) : 0);
					if (USE_STRIKE && currentVal < 0) currentVal = 0; // <-- IS THIS TRUE FOR ALL CLIENTS???
					// Orrick is like a fund; however, their TVPI includes distributions, so we need to back out those distributions
					if (isORR) currentVal -= distribs[b];
					if (!isDebt && (b != 'GO' || !isMAR)) periodMarketValue += currentVal;
					let gain = periodBasis == -1 ? currentVal : currentVal - periodBasis; //bases[b] == 0 ? (currentVal - bases[b]) : 0;

					// strikePrice HACK for aestudio ???  (see long note above regarding bases.)
					gain = periodBasis == -1 || strikePrice > 0 ? currentVal : currentVal - periodBasis; //bases[b] == 0 ? (currentVal - bases[b]) : 0;
					if (gain && !clientData.equity[b].hasOwnProperty('interestRate') && (b != 'GO' || !isMAR)) totalUnrealizedGain += gain;
				});

				totalUnrealizedGain = Math.round(100 * totalUnrealizedGain) / 100;
				if (totalUnrealizedGain == 0) totalUnrealizedGain = 0; // avoid negative zero

				// By date then amount
				equityEvents.sort(function (a, b) {
					let aDate = Number(a.date);
					let bDate = Number(b.date);
					if (moment(1000 * aDate).format('DDMMYY') == moment(1000 * bDate).format('DDMMYY')) {
						// Amount is only important when dates are the same
						return Number(b.grossDollars.replace(/[^-.\d]/g, '')) - Number(a.grossDollars.replace(/[^-.\d]/g, ''));
					}
					return aDate < bDate ? 1 : -1;
				});

				if (clientData.flags?.hide_event_notes) hasEventNotes = false;

				let stringOptions = showDecimals.checked ? m2 : m0;
				let sortedEquityEvents;
				let filteredEquityEvents;
				let reportTotalEquityAmount = null;
				if (isBB) {
					sortedEquityEvents = equityEvents.sort((a, b) => a.date - b.date);
					// Filter events for 2023
					filteredEquityEvents = sortedEquityEvents.filter((event) => {
						let eventDate = new Date(event.date * 1000);
						return eventDate.getFullYear() === 2024;
					});

					// Calculate the sum of the amounts for 2023 events
					reportTotalEquityAmount = filteredEquityEvents.reduce((acc, event) => {
						let amount = Number(event.grossDollars.replace(/[^-.\d]/g, ''));
						return acc + (event.eventType === 'Contribution' ? -amount : amount);
					}, 0);
				} else {
					sortedEquityEvents = equityEvents;
				}

				// console.log(reportTotalEquityAmount);
				let userEquityEventsMarkup = `
					<div class="table-wrap mt-8 mb-0 userEquityEventsTable" style="width: 100% !important">
						<div class="sm-hz-scroll-no-margin">
							<h5 class="text-xl font-bold mt-4 ml-4">Events</h5>
							${
								sortedEquityEvents.length
									? `
								<table id="userEquityEventsSortTable" class="highlight table-content" style="width: 100% !important">
									<thead>
										<tr>
											<!-- <th class="text-xs ml-1">${isJMS || is1251 ? 'Class' : UFL ? 'Fund' : 'Share Class'}</th> -->
											<th class="text-xs ml-1">${labelInvestments ? labelInvestments : clientData.use_real_estate_language ? 'Property' : UFL ? 'Fund' : 'Class'}</th>
											${showHolderColumn ? `<th class="left-align ml-1">Account</th>` : ``}
											<th>Date</th>
											<th>Event Type</th>
											<th>Description</th>
											<th class="center-align">Dollar Amount</th>
											<th class="center-align ${hasEventNotes ? '' : 'hidden'}">Notes</th>
											${isJMS ? `<th></th` : ``}
										</tr>
									</thead>
									<tbody>
									${sortedEquityEvents
										.map(
											(event, index) =>
												`
									<tr>
									<td class="text-xs ml-1">
									<span class="pill pill--gray">${event.shareClass}</span>
									</td>
									${
										showHolderColumn
											? `
									<td class="text-xs left-align">
									${event.holderName || event.holder || ''}
									</td>
									`
											: ``
									}
									<td class="text-xs" data-sort='${event.date}'>
									${moment.unix(event.date).format('MMM D, YYYY')}
									</td>
									<td class="text-xs">
									${event.eventType}
									</td>
									<td class="text-xs">
									${event.description}
									</td>
									<td class="text-xs center-align" data-sort='${Number(event.grossDollars.replace(/[^-.\d]/g, '')) * (event.eventType == 'Contribution' ? -1 : 1)}'>
									${
										event.eventType == 'Contribution' && !isCER
											? `
									($${Number(event.grossDollars.replace(/[^-.\d]/g, '')).toLocaleString('en-US', stringOptions)})
									`
											: Number(event.grossDollars.replace(/[^-.\d]/g, '')) < 0
												? `
									($${Number(event.grossDollars.replace(/[^.\d]/g, '')).toLocaleString('en-US', stringOptions)})
									`
												: `
									$${Number(event.grossDollars.replace(/[^-.\d]/g, '')).toLocaleString('en-US', stringOptions)}
									`
									}
									</td>
									<td class="text-xs text-secondary center-align ${hasEventNotes ? '' : 'hidden'}">
									${event.isFutureEstimated ? '<span class="text-secondary text-xs">Pending</span>' : event.notes ? event.notes : ''}
									</td>
									${
										isJMS && ['NP', 'NR'].includes(event.shareClass)
											? `
									<td class="text-xxs center-align mr-4">
									${
										moment.unix(event.date).year() < 2018
											? `n.a.`
											: `
									<a href="#" onClick="generateTrancheAnalysis('${event.nrid}')"><span class="link-accent">Analysis</span></a>
									`
									}
									</td>
									`
											: ``
									}
									</tr>
									`
										)
										.join('')}
									${
										isBB && reportTotalEquityAmount
											? `
									<tr class="total-row hidden">
										<td></td>
										${showHolderColumn ? `<td></td>` : ``}
										<td></td>
										<td></td>
										<td class="text-xs">Total:</td>
										<td class="text-xs center-align" data-sort='${reportTotalEquityAmount}'>
											$${reportTotalEquityAmount.toLocaleString('en-US', stringOptions)}
										</td>
										<td></td>
										<td></td>
									</tr>
									`
											: ``
									}
									</tbody>
								</table>
							`
									: `
								<div class="bg-grey-lightest rounded border-grey grey-text center-align p-2 ml-4 mr-4">
									No events found for the selected filters.
								</div>
							`
							}
						</div>
					</div>
				`;

				document.getElementById('userEquityEventsTable').innerHTML = userEquityEventsMarkup;
				let table = document.getElementById('userEquityEventsSortTable');
				if (table) {
					let sort = new Tablesort(table, { descending: true });
				}

				let numAbbr = new NumAbbr(['K', 'M', 'B', 'T']);

				// if (!selectedClass || selectedClass == "ALL") totalContribution -= totalReturnOfCapital;

				// Rosh Realty fuuly-redeemed edge case
				let fullyRedeemed;
				if (totalContribution == 0 && totalRedemption > 0) {
					totalContribution = totalRedemption;
					fullyRedeemed = true;
				}

				let nowRedeeming;
				if (isJMS && totalContribution < 0) {
					totalContribution = totalRedemption;
					nowRedeeming = true;
				}

				if (USE_STRIKE) totalUnrealizedGain += totalBasisStepDown;
				console.log('Current Value totalBasisStepDown = ' + totalBasisStepDown);

				let totalRealizedGain = totalProceeds + totalCashDistribution + totalReturnOfCapital;

				// Fund math, right?
				let currentValue = Math.round(100 * (totalContribution + totalUnrealizedGain - totalRealizedGain)) / 100;

				// Orrick is like a fund; however, their TVPI includes distributions, so we need to ignore these distributions (realized gains)
				const IGNORE_DISTRIBUTIONS = clientData.flags?.ignore_distributions; // isORR || isLT || isJMS || isMAR || isBOH
				if (IGNORE_DISTRIBUTIONS) currentValue = Math.round(100 * (totalContribution + totalUnrealizedGain)) / 100;

				// *** HACK ***  For now, we sledgehammer Marvin, and revert to ownership x price, rather than the preferred approach: "back door" cross-check.
				if (isMAR)
					currentValue =
						Number(userData.clients[client].shares['V']?.owned || 0) * Number(valuations['V']?.sharePrice || 0) +
						Number(userData.clients[client].shares['NV']?.owned || 0) * Number(valuations['NV']?.sharePrice || 0);

				// When date-filtered and no client-specific override applies,
				// use direct market value (periodOwned x periodSharePrice)
				// to avoid basis reconstruction complexity
				if (dateRange && !IGNORE_DISTRIBUTIONS && !isMAR) {
					currentValue = Math.round(100 * periodMarketValue) / 100;
				}

				if (currentValue == 0) currentValue = 0; // avoid negative zero

				// Massive HACK
				if (isJMS && ownerData.shares['JMS'] && Number(ownerData.shares['JMS'].owned || '0') < 1) {
					//} userData.userid == "paflint@gmail.com") {
					// totalRealizedGain = totalProceeds;
					totalUnrealizedGain = 0;
					// currentValue = 0;
				}

				let excessRealizedReturn = totalRealizedGain > totalContribution ? totalRealizedGain - totalContribution : 0;
				let excessCapitalGain = totalUnrealizedGain > totalRealizedGain ? totalUnrealizedGain - totalRealizedGain : 0;
				let roiGain = totalContribution + excessRealizedReturn + excessCapitalGain + totalRealizedGain; // + totalBasisStepDown;  or - totalBasisStepDown;  ???
				let roi = 0;
				if (roiGain > 0) roi = Math.round(10000 * (roiGain / totalContribution - 1)) / 100;
				// Maybe do this for all clients where UFL == false??  Except why UNrealized??? 7/30/2023
				if (isMAZ) roi = Math.round(10000 * (totalUnrealizedGain / totalContribution)) / 100;
				if (IGNORE_DISTRIBUTIONS) roi = Math.round(10000 * ((totalRealizedGain + totalUnrealizedGain) / totalContribution)) / 100;
				let altRoi = Math.round(10000 * (totalUnrealizedGain / (totalRealizedGain - totalUnrealizedGain))) / 100;
				if (altRoi > roi && !USE_STRIKE && !IGNORE_DISTRIBUTIONS) roi = altRoi;
				if (roi == 0) roi = 0; // avoid negative zero

				// For 1251 and others who prefer TVPI/MoM rather ROI
				const USE_TVPI = /tvpi|mom/i.test(labelROI);
				if (USE_TVPI) roi = 1 + roi / 100;

				// let gainsToggle = document.getElementById('includeGainsToggle');
				// if (gainsToggle) gainsToggle.checked = totalRealizedGain == 0;
				// let incGains = gainsToggle ? gainsToggle.checked : false;

				// To omit ANY subtext or numbers beneath the E&T stats, set clientData.language.subtext_ET to 'none' or 'hide'
				const subtextForET = clientData.language?.subtext_ET || null;

				narrow = screen.width < 600;
				let userEquityTotalsMarkup = `
			${
				equityEvents.length || equityTransactions.length || dateRange
					? `
				<div class="row">					
					<div class="stats-card">
						${
							isMAR
								? `
							<div class="stat-item-3" style="border: none; background-color: #fafafa;">
							</div>
						`
								: `
							<div class="stat-item-3 sm-border-t-1">
								<div class=${narrow ? 'p-0' : 'px-4'}>
									<dl>
										<dd class="text-secondary pdf-text-secondary">${incGains ? 'Investment' : nowRedeeming ? 'Redemptions' : 'Contributions'}</dd>
										<dd class="text-2xl font-bold text-primary">
											$${narrow ? numAbbr.abbreviate(Math.round(totalContribution * 1000) / 1000, 2) : totalContribution.toLocaleString('en-US', stringOptions)}
										</dd>
									</dl>
								</div>
							</div>
						`
						}

						<div class="stat-item-3 ${narrow ? '' : 'md-border-l-1'} sm-border-t-1">
							<div class=${narrow ? 'p-0' : 'px-4'}>
								<dl>
									<dd class="text-secondary pdf-text-secondary">${incGains ? 'Realized&nbsp;Return' : 'Distributions'}</dd>
									<dd class="text-2xl font-bold text-primary">
										${
											incGains
												? `
											$${narrow ? numAbbr.abbreviate(totalRealizedGain, 2) : totalRealizedGain.toLocaleString('en-US', stringOptions)}
										`
												: `
											$${narrow ? numAbbr.abbreviate(totalCashDistribution, 2) : totalCashDistribution.toLocaleString('en-US', stringOptions)}
										`
										}
									</dd>
								</dl>
							</div>
						</div>

						<div class="stat-item-3 ${narrow ? '' : 'md-border-l-1'} sm-border-t-1">
							<div class=${narrow ? 'p-0' : 'px-4'}>
								<dl>
									<dd class="text-secondary pdf-text-secondary">${incGains ? labelGain : 'Returns&nbsp;of&nbsp;Capital'}</dd>
									<dd class="text-2xl font-bold text-primary">
										${
											labelGain.includes('Unrealized')
												? `
											$${narrow ? numAbbr.abbreviate(totalUnrealizedGain) : totalUnrealizedGain.toLocaleString('en-US', stringOptions)}
										`
												: incGains
													? `
											$${narrow ? numAbbr.abbreviate(totalUnrealizedGain - (USE_STRIKE ? totalRealizedGain : 0), 2) : (totalUnrealizedGain - (USE_STRIKE ? totalRealizedGain : 0)).toLocaleString('en-US', stringOptions)}
										`
													: `
											$${narrow ? numAbbr.abbreviate(totalReturnOfCapital, 2) : totalReturnOfCapital.toLocaleString('en-US', stringOptions)}
										`
										}
									</dd>
								</dl>
							</div>
						</div>

						${
							isMAR
								? ``
								: `
							<div class="stat-item-3 ${narrow ? '' : 'md-border-l-1'} sm-border-t-1">
								<div class=${narrow ? 'p-0' : 'px-4'}>
									<dl>
										<dd class="text-secondary pdf-text-secondary">${incGains || isCER || client == 'theclosingagentllc' ? labelROI : '%&nbsp;of&nbsp;Contributions'}</dd>
										<dd class="text-2xl font-bold text-primary">
											${
												totalContribution < 0
													? "<span style='font-size: 2rem; line-height: 120%;'>∞</span>"
													: incGains
														? `
													${isFinite(roi) ? (USE_TVPI ? roi.toFixed(2) : roi.toLocaleString('en-US', stringOptions) + '%') : "<span style='font-size: 2rem; line-height: 120%;'>∞</span>"}
												`
														: `
													${totalContribution > 0 ? ((100 * (totalCashDistribution + totalReturnOfCapital)) / totalContribution).toLocaleString('en-US', stringOptions) + '%' : 'n.a.'}
												`
											} 
										</dd>
									</dl>
								</div>
							</div>
						`
						}

						<div class="stat-item-3 ${narrow ? '' : `md-border-l-1 ${isMAR ? '' : 'md-border-r-1'}`} sm-border-t-1 sm-border-b-1">
							<div class=${narrow ? 'p-0' : 'px-4'}>
								<dl>
									<dd class="text-secondary pdf-text-secondary">${incGains ? (isORR ? 'Estimated&nbsp;FMV' : USE_STRIKE ? 'Potential&nbsp;Value' : 'Current&nbsp;Value') : totalWithholdings > 0 ? 'Taxes&nbsp;Withheld' : fullyRedeemed ? 'Redemptions' : 'Unfunded'}</dd>
									<dd class="text-2xl font-bold text-primary">
										${
											incGains
												? `
											$${narrow ? numAbbr.abbreviate(currentValue, 2) : currentValue.toLocaleString('en-US', stringOptions)}
										`
												: totalWithholdings > 0
													? `
											$${narrow ? numAbbr.abbreviate(totalWithholdings, 2) : totalWithholdings.toLocaleString('en-US', stringOptions)}
										`
													: fullyRedeemed
														? `
											$${narrow ? numAbbr.abbreviate(totalRedemption, 2) : totalRedemption.toLocaleString('en-US', stringOptions)}
										`
														: `
											$${narrow ? numAbbr.abbreviate(unfunded, 2) : unfunded.toLocaleString('en-US', stringOptions)}
										`
										}
									</dd>
								</dl>
							</div>
						</div>

						${
							isMAR
								? `
							<div class="stat-item-3" style="background-color: #fafafa;">
							</div>
						`
								: ``
						}
					</div>

					${
						subtextForET
							? subtextForET.length < 6
								? ``
								: `
						<div class="row mb-0 mt-0 md-border-b-1">
							<div class="col right mr-8">
								<div class="center-align p-1 sm-p-1">
									<span class="text-secondary text-xs">
										${subtextForET}
									</span>
								</div>
							</div>
						</div>
					`
							: `
						<div class="pdf-inline">
							<div class="mb-0 ${narrow ? 'mt-6' : 'mt-0'} md-border-b-1 pdf-text">
								<div class="col s6">
									<div class="center-align p-1 sm-p-1">
										<span class="text-secondary ${narrow ? 'text-xs' : ''} pdf-rep">Committed&nbsp;Capital: </span>
										<span class="text-lg text-primary pdf-value">
											 $${(committed < totalContribution || totalContribution < 0 ? totalContribution + unfunded : committed).toLocaleString('en-US', stringOptions)}
										</span>
									</div>
								</div>
							</div>
							<div class="mb-0 ${narrow ? 'mt-6' : 'mt-0'} md-border-b-1 pdf-text">
								<div class="col s6">
									<div class="center-align p-1 sm-p-1">
										<span class="text-secondary ${narrow ? 'text-xs' : ''} pdf-rep">Capital&nbsp;Balance: </span>
										<span class="text-lg text-primary pdf-val">
											${
												fullyRedeemed
													? `
												$${(0).toLocaleString('en-US', stringOptions)}
											`
													: incGains
														? `
												$${totalRealizedGain > totalContribution ? 0 : (totalContribution - totalRealizedGain).toLocaleString('en-US', stringOptions)}
											`
														: `
												$${totalReturnOfCapital > totalContribution ? 0 : (totalContribution - totalReturnOfCapital).toLocaleString('en-US', stringOptions)}
											`
											}
										</span>
									</div>
								</div>
							</div>
						</div>
					`
					}
				</div>
				`
					: ``
			}
		`;

				if (!clientData?.flags?.hide_ET_stats) document.getElementById('userEquityTotalsTable').innerHTML = userEquityTotalsMarkup;
			}

			// legacy global shim for inline onClick
			window.generateAmortizationSchedule = (nrid, opts = {}) => {
				return _generateAmortizationSchedule(nrid, transactions, opts);
			};

			if (isBOH) {
				const generateLoansReportBtn = document.getElementById('generateLoansReportBtn');
				generateLoansReportBtn.classList.remove('hidden');
				generateLoansReportBtn.onclick = () => generateLoansReport(transactions);
			}

			window.generateTrancheAnalysis = function (nrid) {
				console.log(nrid);
				let clientEvent = allClientEvents.find((e) => e.nrid == nrid);
				let eventClass = clientEvent.shareClass;
				let userEvent = eventUserData.clients[client].shares[eventClass].events.find((e) => e.nrid == nrid);
				let eventDate = moment.unix(clientEvent.date);
				let eventStamp = Number(clientEvent.date);
				let eventUser = eventUserData.userid;

				let payments = getPayments(eventUser, eventClass, clientEvent.date);

				let trancheTable = `
			<div>
				${
					eventClass == 'NP'
						? `
					<p class="text-sm mt-0">Payments sent ${moment.unix(clientEvent.date).format('MMMM, YYYY')}, totalling ${Number(userEvent.grossDollars).toLocaleString('en-US', usd)}.</p>
				`
						: `
					<p class="text-sm mt-0">Payments received ${moment.unix(clientEvent.date).format('MMMM, YYYY')}, totalling ${Number(userEvent.grossDollars).toLocaleString('en-US', usd)}.</p>
				`
				}
			</div>
			<table id="trancheSortTable" class="card highlight table-content">
				<thead>
					<tr>
						<th class="text-xs p-05 center-align">&emsp;#&emsp;&emsp;Payment</th>
						<th class="text-xs left-align p-05" data-sort-default>${eventClass == 'NP' ? 'Made To' : 'Received From'}</th>
						<th class="text-xs right-align p-05">Amount</th>
						<th class="text-xs right-align p-05">Principal</th>
						<th class="text-xs right-align p-05">Interest</th>
					</tr>
				</thead>
				<tbody>
				${payments
					.map(
						(pmt, index) =>
							`
					<tr>
						<td class="text-xs p-0" style="width:25%" data-sort='${pmt.period}'>
							<p class="mb-0 mt-0 center-align"><span>${pmt.period}&ensp;&ensp;<span class="text-white">|</span>&ensp;&ensp;${pmt.date.format('MMM-YYYY')}</span></p>
						</td>
						<td class="text-xs p-0">
							<p class="mb-0 mt-0 mr-4 leftt-align"><span>${pmt.name}</span></p>
						</td>
						<td class="text-xs p-0">
							<p class="mb-0 mt-0 mr-4 right-align"><span>${(pmt.prin + pmt.int).toLocaleString('en-US', usd)}</span></p>
						</td>
						<td class="text-xs p-0">
							<p class="mb-0 mt-0 mr-4 right-align"><span>${pmt.prin.toLocaleString('en-US', usd)}</span></p>
						</td>
						<td class="text-xs p-0">
							<p class="mb-0 mt-0 mr-4 right-align"><span>${pmt.int.toLocaleString('en-US', usd)}</span></p>
						</td>
					</tr>
				`
					)
					.join('')}
				</tbody>
				<tfoot>
					<tr>
						<td class="text-xs font-bold border-t-2 p-0">
							<p class="ml-6 mb-0 mt-0 left-align"><span>TOTAL</span></p>
						</td>
						<td></td>
						<td class="text-xs font-bold border-t-2 p-0">
							<p class="mb-0 mt-0 mr-4 right-align"><span>${(totalPrinPaid + totalIntPaid).toLocaleString('en-US', usd)}</span></p>
						</td>
							<td class="text-xs font-bold border-t-2 p-0">
							<p class="mb-0 mt-0 mr-4 right-align"><span>${totalPrinPaid.toLocaleString('en-US', usd)}</span></p>
						</td>
						<td class="text-xs font-bold border-t-2 p-0">
							<p class="mb-0 mt-0 mr-4 right-align"><span>${totalIntPaid.toLocaleString('en-US', usd)}</span></p>
						</td>
					</tr>
				</tfoot>
			</table>
		`;

				document.getElementById('trancheAnalysisTable').innerHTML = trancheTable;
				let table = document.getElementById('trancheSortTable');
				if (table) {
					let sort = new Tablesort(table, { descending: false });
				}

				let viewTransactionsModal = document.getElementById('viewTransactionsModal');
				let trancheAnalysisModal = document.getElementById('trancheAnalysisModal');
				viewTransactionsModal.classList.add('hidden');
				M.Modal.getInstance(trancheAnalysisModal).open();
				trancheAnalysisModal.scrollTo(0, scroll);
				document.getElementById('closeTrancheAnalysis').addEventListener('click', function (e) {
					M.Modal.getInstance(trancheAnalysisModal).close();
					viewTransactionsModal.classList.remove('hidden');
				});

				if (window.analytics)
					window.analytics.track('Viewed Tranche Analysis', {
						userEmail: authedUser,
						userId: userData.userid,
						workspace: client,
					});
			};

			function renderUserDebtModal(events, debtTransactions, isReport = null) {
				let finalBalance = 0;
				let totalDeposits = 0;
				let totalWithdrawals = 0;
				let totalInterestPaid = 0;
				let totalPrincipalFunded = 0;
				let totalInterestAccrued = 0;
				let totalPrincipalRepaid = 0;
				let totalPrincipalTransferred = 0;
				let totalWithholdings = 0;
				if (!userData) AppState.userData = JSON.parse(sessionStorage.getItem('user.Data'));
				let ownerId = userData.userid;
				let ownerData = userData.clients[client];
				if (pn.includes('/users/edit')) {
					if (!shareholderData) shareholderData = JSON.parse(sessionStorage.getItem('shareholder.Data'));
					ownerId = shareholderData.userid;
					ownerData = shareholderData.clients[client];
				}

				let debtEvents = [];
				events.forEach(function (e) {
					let clientEvent = clientEvents.find((obj) => obj.nrid == e.nrid);
					if (
						clientEvent &&
						['Principal Funded', 'Principal Repayment', 'Interest Accrual', 'Interest Payment', 'Withholding'].includes(clientEvent.eventType)
					) {
						debtEvents.push(e);
					}
				});

				debtTransactions.forEach(function (t) {
					let dollarAmount = Number(t.quantity) * Number(t.sharePrice);
					if (t.sender == ownerId) {
						if (!t.receiver || t.receiver == client || t.receiver.toLowerCase() == 'issuer') totalPrincipalRepaid += dollarAmount;
						else totalPrincipalTransferred += dollarAmount;
						bases[t.shareClass] = bases[t.shareClass] - dollarAmount;
					} else {
						totalPrincipalFunded += dollarAmount;
						bases[t.shareClass] = bases[t.shareClass] + dollarAmount;
					}
				});

				debtEvents.forEach(function (e) {
					// if (bases[e.shareClass] == -1) bases[e.shareClass] = 0;
					let clientEvent = clientEvents.find((obj) => obj.nrid == e.nrid);
					if (clientEvent) {
						e.date = clientEvent.date;
						e.description = clientEvent.notes;
						e.eventType = clientEvent.eventType;
						let grossDollars = Number(e.grossDollars) || 0;
						if (e.eventType == 'Principal Funded') {
							totalPrincipalFunded += grossDollars;
						} else if (e.eventType == 'Principal Repayment') {
							totalPrincipalRepaid += grossDollars;
						} else if (e.eventType == 'Interest Payment') {
							totalInterestPaid += grossDollars;
						} else if (e.eventType == 'Interest Accrual') {
							totalInterestAccrued += grossDollars;
						} else if (e.eventType == 'Withholding') {
							totalWithholdings += grossDollars;
						}
					}
				});

				if (isMAR) {
					debtEvents = debtEvents.concat(events);
					const startDate = clientData.equity['GO'].startDate;
					if (startDate) debtEvents = debtEvents.filter((d) => Number(d.date) * 1000 >= moment(startDate, dateFormats));

					// Apply user's date range filter to debt events (GO Account)
					const debtDateRange = getDateRangeTimestampsET(selectedETDateRange, customDateStartET, customDateEndET);
					if (debtDateRange) {
						debtEvents = filterByDateRangeET(debtEvents, debtDateRange);
					}

					debtEvents.forEach(function (e) {
						let dollars = Number(e.grossDollars);
						if (dollars > 0) totalDeposits += dollars;
						else totalWithdrawals += -dollars;
						finalBalance = e.balance;
					});
					debtEvents.reverse();
				} else {
					// By date then amount, but not Marvin!
					debtEvents.sort(function (a, b) {
						let aDate = Number(a.date);
						let bDate = Number(b.date);
						if (moment(1000 * aDate).format('DDMMYY') == moment(1000 * bDate).format('DDMMYY')) {
							// Amount is only important when dates are the same
							return Number(b.grossDollars.replace(/[^.\d]/g, '')) - Number(a.grossDollars.replace(/[^.\d]/g, ''));
						}
						return aDate < bDate ? 1 : -1;
					});
				}

				// By class then date
				// debtEvents.sort(function(a, b) {
				// 	if (a.shareClass == b.shareClass) {
				// 		// Date is only important when classes are the same
				// 		return b.date - a.date;
				// 	}
				// 	return a.shareClass > b.shareClass ? 1 : -1;
				// });

				// hasEventNotes = is3MG || isMAR;

				let interestRate = 0; //default
				if (selectedClass) {
					if (selectedClass == 'ALL') {
						// Calculate weighted average interest rate
						let totalOwned = 0;
						let weightedInterestRateSum = 0;
						let localUserData = pn.includes('/users/edit') ? shareholderData : userData;
						classes.forEach((shareClass) => {
							if (clientData.equity[shareClass]?.hasOwnProperty('interestRate')) {
								const intRate = Number(clientData.equity[shareClass].interestRate) || 0;
								const owned = Number(localUserData.clients[client]?.shares[shareClass]?.owned) || 0;
								if (owned > 0) {
									weightedInterestRateSum += intRate * owned;
									totalOwned += owned;
								}
							}
						});
						interestRate = totalOwned > 0 ? weightedInterestRateSum / totalOwned : 0;
					} else {
						interestRate = clientData.equity[selectedClass]?.interestRate != undefined ? Number(clientData.equity[selectedClass].interestRate) : 0;
					}
				}

				let accLabel = isPHJ ? 'Interest/PIK&nbsp;Accrued' : 'Interest&nbsp;Accrued';
				let intLabel = isPHJ ? 'Current&nbsp;PIK&nbsp;Rate' : 'Interest&nbsp;Rate';

				let stringOptions = showDecimals.checked ? m2 : m0;

				let userDebtEventsMarkup = `
			<div class="table-wrap mt-8 mb-0">
				<div class="sm-hz-scroll-no-margin">
					<h5 class="text-xl font-bold mt-4 ml-4">${isMAR ? 'GO Account Transactions' : 'Debt Events'}</h5>
					${
						debtEvents.length
							? `
						<table id=${debtEventsTableName} class="highlight table-content">
				<thead>
				  <tr class="text-xs">
					${isMAR ? `` : UFL ? `<th >Fund</th>` : `<th >Share Class</th>`}
					<th>Date</th>
					<th>Event Type</th>
					${isMAR ? `<th class="center-align">Num</th>` : ``}
					<th>Description</th>
					<th class="center-align">Dollar Amount</th>
					${
						isMAR
							? `<th class="center-align">Balance</th>`
							: `
						<th class="center-align ${hasEventNotes ? '' : 'hidden'}">Notes</th>
					`
					}
				  </tr>
				</thead>
				<tbody>
				${debtEvents
					.map(
						(event, index) =>
							`
					<tr>
						${
							isMAR
								? ``
								: `<td class="text-xs">
							<span class="pill pill--gray">${event.shareClass}</span>
						</td>`
						}
						<td class="text-xs" data-sort='${event.date}'>
							${moment.unix(event.date).format('MMM D, YYYY')}
						</td>
						<td class="text-xs">
							${event.eventType}
						</td>
						${
							isMAR
								? `<td class="text-xs text-secondary center-align">
							${event.notes ? event.notes : ''}
						</td>`
								: ``
						}
						<td class="text-xs">
							${event.description}
						</td>
						<td class="text-xs center-align" data-sort='${Number(event.grossDollars.replace(/[^.\d]/g, '')) * (event.eventType == 'Principal Funded' ? -1 : 1)}'>
							${
								event.eventType == 'Principal Funded'
									? `
								($${Number(event.grossDollars.replace(/[^.\d]/g, '')).toLocaleString('en-US', stringOptions)})
								`
									: `
								${
									event.grossDollars.startsWith('-')
										? `
									(${Number(event.grossDollars.replace(/[^.\d]/g, '')).toLocaleString('en-US', stringOptions)})
								`
										: `
									${Number(event.grossDollars.replace(/[^-.\d]/g, '')).toLocaleString('en-US', stringOptions)}
								`
								}

								`
							}
						</td>
						${
							isMAR
								? `
							<td class="text-xs center-align" data-sort='${Number(event.balance.replace(/[^.\d]/g, ''))}'>
								${
									event.balance?.startsWith('-')
										? `
									(${Number(event.balance.replace(/[^.\d]/g, '')).toLocaleString('en-US', stringOptions)})
								`
										: `
									${Number(event.balance.replace(/[^-.\d]/g, '')).toLocaleString('en-US', stringOptions)}
								`
								}
							</td>
						`
								: `
							<td class="text-xs text-secondary center-align ${hasEventNotes ? '' : 'hidden'}">
								${event.notes ? event.notes : ''}
							</td>
						`
						}
					</tr>
					`
					)
					.join('')}
				</tbody>
						</table>
					`
							: `
						<div class="bg-grey-lightest rounded border-grey grey-text center-align p-2 ml-4 mr-4">
							No debt events found for the selected filters.
						</div>
					`
					}
				</div>
			</div>
		`;

				document.getElementById('userDebtEventsTable').innerHTML = userDebtEventsMarkup;
				let table = document.getElementById(debtEventsTableName);
				if (table) {
					let sort = new Tablesort(table, { descending: true });
				}

				let numAbbr = new NumAbbr(['K', 'M', 'B', 'T']);
				if (is3MG) totalInterestAccrued = ((totalPrincipalFunded - totalPrincipalRepaid) * 0.01 * new Date().getDate()) / 31;
				// if (isPHJ) totalPrincipalFunded -= totalPrincipalTransferred;

				let userDebtTotalsMarkup = `
			${
				(debtEvents.length || debtTransactions.length) && !isMAR
					? `
				<div class="">
					<div class="stats-card">
						<div class="stat-item-3 sm-border-t-1">
							<div class=${narrow ? 'p-0' : 'px-4'}>
								<dl>
									<dd class="text-secondary">Principal&nbsp;Funded</dd>
									<dd class="text-2xl font-bold text-primary">
										$${narrow ? numAbbr.abbreviate(totalPrincipalFunded, 2) : totalPrincipalFunded.toLocaleString('en-US', stringOptions)}
									</dd>
								</dl>
							</div>
						</div>
						<div class="stat-item-3 ${narrow ? '' : 'md-border-l-1'} sm-border-t-1">
							<div class=${narrow ? 'p-0' : 'px-4'}>
								<dl>
									<dd class="text-secondary">Interest&nbsp;Paid</dd>
									<dd class="text-2xl font-bold text-primary">
										$${narrow ? numAbbr.abbreviate(totalInterestPaid, 2) : totalInterestPaid.toLocaleString('en-US', stringOptions)}
									</dd>
								</dl>
							</div>
						</div>
						<div class="stat-item-3 ${narrow ? '' : 'md-border-l-1'} sm-border-t-1">
							<div class=${narrow ? 'p-0' : 'px-4'}>
								<dl>
									<dd class="text-secondary">${accLabel}</dd>
									<dd class="text-2xl font-bold text-primary">
										$${totalInterestAccrued.toLocaleString('en-US', stringOptions)}
									</dd>
								</dl>
							</div>
						</div>
						<div class="stat-item-3 ${narrow ? '' : 'md-border-l-1'} sm-border-t-1">
							<div class=${narrow ? 'p-0' : 'px-4'}>
								<dl>
									<dd class="text-secondary">${intLabel}</dd>
									<dd class="text-2xl font-bold text-primary">
										${interestRate.toLocaleString('en-US', stringOptions)}%
									</dd>
								</dl>
							</div>
						</div>
						<div class="stat-item-3 ${narrow ? '' : 'md-border-l-1 md-border-r-1'} sm-border-t-1 sm-border-b-1">
							<div class=${narrow ? 'p-0' : 'px-4'}>
								<dl>
									<dd class="text-secondary">Tax&nbsp;Withheld</dd>
									<dd class="text-2xl font-bold text-primary">
										$${totalWithholdings.toLocaleString('en-US', stringOptions)}
									</dd>
								</dl>
							</div>
						</div>
					</div>
				
					${
						false
							? ``
							: `
						<div>
							<div class="row mb-0 ${narrow ? 'mt-6' : 'mt-0'} md-border-b-1">
								<div class="col s6">
									<div class="center-align p-1 sm-p-1">										
										${
											isPHJ && totalPrincipalTransferred > 0
												? `
											<span class="text-secondary ${narrow ? 'text-xs' : ''}">Principal&nbsp;Transferred: </span>
											<span class="text-lg text-primary">
												$${totalPrincipalTransferred.toLocaleString('en-US', stringOptions)}
											</span>
										`
												: `
											<span class="text-secondary ${narrow ? 'text-xs' : ''}">Principal&nbsp;Repaid: </span>
											<span class="text-lg text-primary">
												$${totalPrincipalRepaid.toLocaleString('en-US', stringOptions)}
											</span>
										`
										}
									</span>
								</div>
							</div>
							<div class="row mb-0 ${narrow ? 'mt-6' : 'mt-0'} md-border-b-1">
								<div class="col s6">
									<div class="center-align p-1 sm-p-1">
										<span class="text-secondary ${narrow ? 'text-xs' : ''}">Loan&nbsp;Balance: </span>
										<span class="text-lg text-primary">
											$${isPHJ ? '0' : (totalPrincipalFunded + totalInterestAccrued - totalPrincipalRepaid - totalPrincipalTransferred).toLocaleString('en-US', stringOptions)}
										</span>
									</div>
								</div>
							</div>
						</div>
					`
					}
				</div>
			`
					: isMAR
						? `
				<div class="">
					<div class="stats-card">
						<div class="stat-item-3" style="border-left: none; background-color: #fafafa;">
							<div>
								<dl>
									<dd class="text-secondary"></dd>
									<dd class="text-2xl font-bold text-primary">
									</dd>
								</dl>
							</div>
						</div>

						<div class="stat-item-3 ${narrow ? '' : 'md-border-l-1'} sm-border-t-1">
							<div class=${narrow ? 'p-0' : 'px-4'}>
								<dl>
									<dd class="text-secondary">Total&nbsp;Deposits</dd>
									<dd class="text-2xl font-bold text-primary">
										$${narrow ? numAbbr.abbreviate(totalDeposits, 2) : totalDeposits.toLocaleString('en-US', stringOptions)}
									</dd>
								</dl>
							</div>
						</div>

						<div class="stat-item-3 ${narrow ? '' : 'md-border-l-1'} sm-border-t-1">
							<div class=${narrow ? 'p-0' : 'px-4'}>
								<dl>
									<dd class="text-secondary">Total&nbsp;Withdrawals</dd>
									<dd class="text-2xl font-bold text-primary">
										$${narrow ? numAbbr.abbreviate(totalWithdrawals, 2) : totalWithdrawals.toLocaleString('en-US', stringOptions)}
									</dd>
								</dl>
							</div>
						</div>

						<div class="stat-item-3 ${narrow ? '' : 'md-border-l-1'} sm-border-t-1">
							<div class=${narrow ? 'p-0' : 'px-4'}>
								<dl>
									<dd class="text-secondary">Current&nbsp;Balance</dd>
									<dd class="text-2xl font-bold text-primary">
										$${Number(finalBalance).toLocaleString('en-US', stringOptions)}
									</dd>
								</dl>
							</div>
						</div>

						<div class="stat-item-3" style="background-color: #fafafa;">
							<div>
								<dl>
									<dd class="text-secondary"></dd>
									<dd class="text-2xl font-bold text-primary">
									</dd>
								</dl>
							</div>
						</div>
					</div>
				</div>
				`
						: ``
			}
		`;

				document.getElementById('userDebtTotalsTable').innerHTML = userDebtTotalsMarkup;
				AppState.showTabs = false;
				let debtWrap = document.getElementById('debtWrap');
				let equityWrap = document.getElementById('equityWrap');
				if (isMAR) document.getElementById('tabDebt').innerHTML = 'GO Account';
				if (!selectedClass) selectedClass = shareKeys.length > 1 ? 'ALL' : shareKeys[0];
				if (selectedClass != 'ALL') {
					debtEquityTabs.classList.add('hidden');
					if (!isCER && clientData.equity[selectedClass] && clientData.equity[selectedClass].hasOwnProperty('interestRate')) {
						document.getElementById('tabEquity').classList.remove('active');
						document.getElementById('tabDebt').classList.add('active');
						debtWrap.style.display = 'block';
						equityWrap.style.display = 'none';
						// equityWrap.style.height = null;
						console.log('Switch to Debt');
					} else {
						document.getElementById('tabEquity').classList.add('active');
						document.getElementById('tabDebt').classList.remove('active');
						equityWrap.style.display = 'block';
						debtWrap.style.display = 'none';
						// debtWrap.style.height = null;
						console.log('Switch to Equity');
					}
				} else {
					// We have a mind-blowing logic problem.  We are happy with hasDebt. That's pretty straightforward.
					// The hasEquity analysis is much more difficult, and we must avoid null object errors.
					// In English, if there are ONLY events, and debtEvents.length == events.length, then hasEquity is false.
					// If there are ONLY transactions, and debtTransactions.length == transactions.length, then hasEquity is false.
					// If there are BOTH events and transactions, AND debtEvents.length == events.length, AND debtTransactions.length == transactions.length, then hasEquity is false.
					// Obviously if there are neither events nor transactions, then hasEquity is false.
					let hasDebt = isMAR || (debtEvents && debtEvents.length > 0) || (debtTransactions && debtTransactions.length > 0);
					let hasEquity =
						(events && events.length > 0 && (!debtEvents || debtEvents.length != events.length)) ||
						(transactions && transactions.length > 0 && (!debtTransactions || debtTransactions.length != transactions.length)) ||
						false; // blame ChatGPT!
					if (hasDebt && hasEquity) {
						document.getElementById('tabEquity').classList.add('active');
						document.getElementById('tabDebt').classList.remove('active');
						debtEquityTabs.classList.remove('hidden');
						equityWrap.style.display = 'block';
						debtWrap.style.display = 'none';
						debtWrap.style.height = null;
						AppState.showTabs = true;
						console.log('Show both Debt and Equity');
						initializeDebtEquityTabs();
					} else if (hasDebt) {
						document.getElementById('tabEquity').classList.remove('active');
						document.getElementById('tabDebt').classList.add('active');
						debtEquityTabs.classList.add('hidden');
						debtWrap.style.display = 'block';
						debtWrap.style.height = null;
						console.log('Switch to Debt');
					} else {
						document.getElementById('tabEquity').classList.add('active');
						document.getElementById('tabDebt').classList.remove('active');
						debtEquityTabs.classList.add('hidden');
						equityWrap.style.display = 'block';
						// Hide debtWrap completely when there's no debt data
						if (!debtEvents || debtEvents.length == 0) {
							debtWrap.style.display = 'none';
						} else {
							debtWrap.style.display = 'block';
						}
						debtWrap.style.height = null;
						console.log('Switch to Equity');
					}
				}
			}

			if (!addedEventsTransListenersAlready) {
				addedEventsTransListenersAlready = true;
				filterViewET.addEventListener('change', function (e) {
					selectedClass = filterViewET.value;
					if (isBOH || (isJMS && selectedClass == 'JMS')) eventsTransHelpTrigger.classList.remove('hidden');
					else eventsTransHelpTrigger.classList.add('hidden');
					showFilteredEventsTrans();
					if (selectedClass == 'ALL') initializeDebtEquityTabs(); //viewEventsIconLarge.dispatchEvent(new Event('click'));
				});

				// Initialize date range filter for E&T modal
				let filterViewDateRange = document.getElementById('filterViewDateRange');
				if (filterViewDateRange) {
					filterViewDateRange.innerHTML = generateDateRangeOptionsHtmlET();
					// Re-initialize Materialize select after populating options
					M.FormSelect.init(filterViewDateRange, { dropdownOptions: { coverTrigger: false } });

					// Add click handler on dropdown items to handle re-selecting CUSTOM
					const dateRangeWrapper =
						filterViewDateRange.closest('.select-wrapper') || filterViewDateRange.parentElement?.querySelector('.select-wrapper');
					if (dateRangeWrapper) {
						const dropdownContent = dateRangeWrapper.querySelector('.dropdown-content');
						if (dropdownContent) {
							const dropdownItems = dropdownContent.querySelectorAll('li');
							dropdownItems.forEach((item, index) => {
								item.addEventListener('click', function () {
									const optionValue = filterViewDateRange.options[index]?.value;
									if (optionValue === 'CUSTOM') {
										setTimeout(() => {
											const customDateModal = document.getElementById('customDateRangeModal');
											if (customDateModal) {
												const modalInstance = M.Modal.getInstance(customDateModal) || M.Modal.init(customDateModal);
												modalInstance.open();
											}
										}, 50);
									}
								});
							});
						}
					}

					filterViewDateRange.addEventListener('change', function (e) {
						selectedETDateRange = this.value;
						if (selectedETDateRange === 'CUSTOM') {
							// Open the custom date range modal
							const customDateModal = document.getElementById('customDateRangeModal');
							if (customDateModal) {
								const modalInstance = M.Modal.getInstance(customDateModal) || M.Modal.init(customDateModal);
								modalInstance.open();
							}
						} else {
							// Clear custom date range when another option is selected
							customDateStartET = null;
							customDateEndET = null;
							const wrapper = filterViewDateRange.closest('.select-wrapper');
							if (wrapper) {
								const displayInput = wrapper.querySelector('input.select-dropdown');
								if (displayInput) {
									delete displayInput.dataset.customDateRange;
								}
							}
							// Clear the modal date inputs
							const modalStartInput = document.getElementById('modalDateRangeStart');
							const modalEndInput = document.getElementById('modalDateRangeEnd');
							if (modalStartInput) modalStartInput.value = '';
							if (modalEndInput) modalEndInput.value = '';
							showFilteredEventsTrans();
						}
					});

					// Custom date modal apply button
					let applyCustomDateModal = document.getElementById('applyCustomDateModal');
					if (applyCustomDateModal && !applyCustomDateModal.hasAttribute('data-listener-added')) {
						applyCustomDateModal.setAttribute('data-listener-added', 'true');
						applyCustomDateModal.addEventListener('click', function () {
							customDateStartET = document.getElementById('modalDateRangeStart').value;
							customDateEndET = document.getElementById('modalDateRangeEnd').value;
							if (customDateStartET && customDateEndET) {
								// Set the date range type to CUSTOM so filtering works
								selectedETDateRange = 'CUSTOM';
								// Set the dropdown value to CUSTOM before updating the label
								const filterViewDateRange = document.getElementById('filterViewDateRange');
								if (filterViewDateRange) {
									filterViewDateRange.value = 'CUSTOM';
								}
								// Close the modal
								const customDateModal = document.getElementById('customDateRangeModal');
								if (customDateModal) {
									const modalInstance = M.Modal.getInstance(customDateModal);
									if (modalInstance) modalInstance.close();
								}
								// Update dropdown to show selected date range
								updateDateRangeDropdownLabel('filterViewDateRange', customDateStartET, customDateEndET, () => openCustomDateRangeModalHelper());
								showFilteredEventsTrans();
								// Dispatch event for Vue component to re-clone with date range info
								document.dispatchEvent(
									new CustomEvent('CustomDateRangeApplied', {
										detail: { startDate: customDateStartET, endDate: customDateEndET },
									})
								);
							}
						});
					}
				}

				showDecimals.addEventListener('change', function (e) {
					e.preventDefault();
					if (includedClasses.length > 1) {
						if (!selectedClass) selectedClass = 'ALL';
						showFilteredEventsTrans();
					} else {
						buildEventsAndTransactionsModal(true, true);
						// renderUserTransactionsModal();
						// renderUserEquityModal();
						// renderUserDebtModal();
					}
				});
				const exportTransactions = document.getElementById('exportTransactions');
				exportTransactions.addEventListener('click', function (e) {
					e.preventDefault();
					export_table_to_excel(
						'userEquityEventsSortTable|' + debtEventsTableName + '|userEquityTransactionsSortTable|userDebtTransactionsSortTable',
						eventUserData.user_name + ' Events and Transactions Report (' + new Date().toLocaleString() + ').csv'
					);
				});
				document.getElementById('exportTrancheAnalysis').addEventListener('click', function (e) {
					e.preventDefault();
					export_table_to_excel('trancheSortTable', eventUserData.user_name + ' Tranche Analysis (' + new Date().toLocaleString() + ').csv');
				});
				document.getElementById('exportAmortization').addEventListener('click', function (e) {
					e.preventDefault();
					const footerRows = document.querySelectorAll('tfoot tr');
					footerRows.forEach((row) => {
						const firstCell = row.children[0];
						// **Remove colspan and insert an extra empty <td>**
						if (firstCell.hasAttribute('colspan')) {
							firstCell.removeAttribute('colspan');
							const newTd = document.createElement('td');
							row.insertBefore(newTd, row.children[1]);
						}
					});
					export_table_to_excel(
						'amortizationSortTable',
						eventUserData.user_name + ' Amortization Schedule (' + new Date().toLocaleString() + ').csv'
					);
				});
			}

			// 1/25/2025 I don't know why GPG needed this.  It seems to work fine without it.
			// if (isGPG && filterViewET && currentShareClass >= 0 && shareKeys[currentShareClass]) {
			// 	filterViewET.value = shareKeys[currentShareClass];
			// 	filterViewET.dispatchEvent(new Event('change'));
			// }

			if (window.analytics && !fromScenarioModel)
				window.analytics.track('Viewed Events & Transactions', {
					userEmail: authedUser,
					userId: userData.userid,
					workspace: client,
				});

			resolve(); //Now returns a promise for use in the TransactionsTable.vue component
		} catch (error) {
			reject(error);
		}
	});
}

function buildScenarioModelingModal(redraw = false) {
	if (!clientData.flags || !clientData.flags.show_scenario_modeling) return;

	const defaultClass = Object.keys(clientData.equity).find((c) => clientData.equity[c].valuations) || null;
	if (!defaultClass) return;

	narrow = window.innerWidth < 600;
	const scenarioModelingModal = document.getElementById('scenarioModelingModal');
	const impliedCAGR = document.getElementById(narrow ? 'impliedCAGRSm' : 'impliedCAGRLg');
	const placeholderGraph = document.getElementById(narrow ? 'placeholderGraphSm' : 'placeholderGraphLg');
	const currentDateField = document.getElementById(narrow ? 'currentDateFieldSm' : 'currentDateFieldLg');
	const currentPriceField = document.getElementById(narrow ? 'currentPriceFieldSm' : 'currentPriceFieldLg');
	const forecastDateInput = document.getElementById(narrow ? 'forecastDateInputSm' : 'forecastDateInputLg');
	const forecastPriceInput = document.getElementById(narrow ? 'forecastPriceInputSm' : 'forecastPriceInputLg');
	const currentScenarioDiv = document.getElementById(narrow ? 'currentScenarioDivSm' : 'currentScenarioDivLg');
	const scenarioHelpTrigger = document.getElementById(narrow ? 'scenarioHelpTriggerSm' : 'scenarioHelpTriggerLg');
	const forecastScenarioDiv = document.getElementById(narrow ? 'forecastScenarioDivSm' : 'forecastScenarioDivLg');
	const currentScenarioTable = document.getElementById(narrow ? 'currentScenarioTableSm' : 'currentScenarioTableLg');
	const forecastScenarioTable = document.getElementById(narrow ? 'forecastScenarioTableSm' : 'forecastScenarioTableLg');

	scenarioHelpTrigger.classList.remove('hidden'); // In case it was left hidden
	if (!addedScenarioHelpListenerAlready) {
		addedScenarioHelpListenerAlready = true;
		scenarioHelpTrigger.addEventListener('click', (e) => {
			scenarioModelingModal.classList.add('hidden');
			document.addEventListener('click', (e) => {
				if (e.target.classList.contains('modal-overlay') && M.Modal.getInstance(scenarioModelingModal).isOpen)
					scenarioModelingModal.classList.remove('hidden');
			});
		});
	}

	let currentValuation = clientData.equity[defaultClass].valuations.sort((a, b) => Number(b.date) - Number(a.date))[0];
	let currentPrice = Number(currentValuation.sharePrice); // "$3.92";
	let currentDate = Number(currentValuation.date); //"1721966400";
	currentPriceField.innerHTML = '$' + currentPrice.toLocaleString('en-US', m2);
	currentDateField.innerHTML = new Date(currentDate * 1000).toLocaleDateString('en-US');

	setTimeout(() => {
		M.updateTextFields();
	}, 100);
	setTimeout(() => {
		forecastPriceInput.focus();
	}, 250);

	function calculateAndDisplay() {
		let forecastPrice = Number(DOMPurify.sanitize(forecastPriceInput.value).replace(/[^-.\d]/g, '')); // "5.15";
		let forecastDate = new Date(DOMPurify.sanitize(forecastDateInput.value)).getTime() / 1000; // 1/1/2028 = "1830315600";
		let dateValid = forecastDate && forecastDate > Date.now() / 1000 && forecastDate < 2500000000;
		let priceValid = forecastPrice && forecastPrice > currentPrice / 2 && forecastPrice < currentPrice * 20;

		if (priceValid && dateValid) {
			buildEventsAndTransactionsModal(false, false, true);
			formatStatsCard(true);

			let modelClasses = Object.keys(clientData.equity);
			classes.forEach(function (c) {
				if (clientData.equity[c].valuations) {
					let testValuation = {
						date: forecastDate.toString(),
						refPrice: forecastPrice.toString(),
						sharePrice: forecastPrice.toString(),
						notes: 'Scenario Test Valuation',
						ref: '',
					};
					clientData.equity[c].valuations.unshift(testValuation);
				}
			});

			buildEventsAndTransactionsModal(false, false, true);
			formatStatsCard(false);

			drawScenarioGraph(clientData.equity[defaultClass].valuations);
			placeholderGraph.classList.add('hidden');

			// Promptly remove the Scenario Test Valuations
			modelClasses.forEach(function (c) {
				clientData.equity[c].valuations = clientData.equity[c].valuations.filter((v) => v.notes !== 'Scenario Test Valuation');
			});

			let cagr = getCAGR(Number(currentValuation.sharePrice), forecastPrice, currentDate, forecastDate);
			impliedCAGR.innerHTML = (cagr * 100).toFixed(2) + '%';
		} else {
			// resultDisplay.textContent = "Please fill in both fields.";
		}
	}

	function formatStatsCard(now = true) {
		let userStatsCard = document.getElementById('userStatsCard');
		let userEquityTotalsTable = document.getElementById('userEquityTotalsTable');
		if (userEquityTotalsTable) {
			let userEquityTotals = userEquityTotalsTable.cloneNode(true);
			let userStatsCards = userEquityTotals.querySelectorAll('.stats-card');
			if (userStatsCards && userStatsCards.length) {
				userStatsCard = userStatsCards[0];
				userStatsCard.id = 'userStatsCard';
				userStatsCard.style.cssText = `
					display:flex; flex-direction: row; /* This ensures the items are laid out in a row */
					justify-content: space-between; /* This adds space between the items */
					border-style: none; border-collapse: collapse; max-width: 100% !important; border-radius: 10px !important; background-color: #f0f4f8 !important;
				`;

				let elements = userEquityTotals.querySelectorAll('.text-2xl');
				elements.forEach((element) => {
					element.classList.remove('text-2xl');
					element.classList.add('text-sm');
				});
				elements = userEquityTotals.querySelectorAll('.pdf-text-secondary');
				elements.forEach((element) => {
					element.classList.remove('pdf-text-secondary');
					element.classList.add('text-xs');
				});
				elements = userEquityTotals.querySelectorAll('.p-4');
				elements.forEach((element) => {
					element.classList.remove('p-4');
					element.classList.add('p-0');
				});
				elements = userEquityTotals.querySelectorAll('.stat-item-3');
				elements[0].style.borderLeft = 'none';
				elements.forEach((element) => {
					element.classList.remove('md-border-r-1');
					element.classList.add('p-05');
				});
				elements = userEquityTotals.querySelectorAll('dl');
				elements.forEach((element) => {
					element.classList.add('mt-0', 'mb-0');
				});
				userStatsCard.style.width = '90%';
				userStatsCard.classList.add('mb-2');

				if (now) {
					currentScenarioTable.innerHTML = userStatsCard.outerHTML;
					currentScenarioDiv.classList.remove('hidden');
				} else {
					forecastScenarioTable.innerHTML = userStatsCard.outerHTML;
					forecastScenarioDiv.classList.remove('hidden');
				}
			}
		} else {
			// addedStatsHeadlineAlready = false;
		}
	}

	function drawScenarioGraph(scenarioArray) {
		google.charts.load('current', { packages: ['corechart'] });
		google.charts.setOnLoadCallback(drawModalChart);

		function drawModalChart() {
			// Calculate the highest share price
			let maxSharePrice = Math.max(...scenarioArray.map((val) => Number(val.sharePrice)));

			// Inject a new data point 1 second after the most recent price
			if (scenarioArray.length > 1) {
				const currentDate = Number(scenarioArray[1].date); // Most recent price date
				const injectedData = {
					date: (currentDate + 1).toString(), // 1 second later
					sharePrice: scenarioArray[1].sharePrice, // Same price as most recent
					shareClass: scenarioArray[1].shareClass,
				};
				scenarioArray.splice(2, 0, injectedData); // Inject the new data point at index 2
			}

			// Prepare the data
			let graphData = google.visualization.arrayToDataTable(
				[
					[
						{ label: 'Date', type: 'date' },
						{ label: 'Historic', type: 'number' },
						{ label: 'Forecast', type: 'number' },
					],
				].concat(
					scenarioArray.map((val, idx) => [
						new Date(Number(val.date) * 1000),
						idx >= 2 ? Number(val.sharePrice) : null, // Forecast data for points after the injected point
						idx <= 1 ? Number(val.sharePrice) : null, // Historic data for points before or at the injected point
					])
				)
			);

			let options = {
				animation: {
					duration: 1000,
					easing: 'out',
					startup: true,
				},
				title: 'Valuations Over Time' + (narrow ? '' : ' — Historic, Current, and Forecast'),
				titleTextStyle: {
					fontSize: 14,
					bold: true,
					color: stdColor[3],
					alignment: 'center',
					padding: 20,
				},
				height: narrow ? 250 : 300,
				width: scenarioModelingModal.offsetWidth * (narrow ? 0.85 : 0.45),
				legend: 'none',
				chartArea: { width: '80%', height: '70%' },
				hAxis: {
					format: 'yyyy',
					textStyle: { fontSize: 12 },
					gridlines: { color: 'transparent' },
				},
				vAxis: {
					textStyle: { fontSize: 12 },
					format: '$#,###.##',
					gridlines: { color: nthColor[3] },
					viewWindow: {
						max: Math.ceil(maxSharePrice * 1.05), // / 2) * 2  // Adjust y-axis max if needed
					},
				},
				series: {
					0: {
						type: 'area', // Historic data
						color: nthColor[0], // Darker shade for historic data (left side)
						lineWidth: 2,
						areaOpacity: 0.5, // Darker shading for historic data
						// pointSize: 5		// Add circular markers for historic points (including current)
					},
					1: {
						type: 'area', // Forecast data
						color: nthColor[1], // Lighter shade for forecast data (right side)
						lineWidth: 1,
						areaOpacity: 0.3, // Lighter shading for forecast data
						pointSize: 4, // Add circular markers for forecast points
					},
				},
				colors: [nthColor[0], nthColor[1]],
				tooltip: {
					textStyle: { color: stdColor[3], fontSize: 12 }, // Subtle text style for tooltip
					trigger: 'focus',
					isHtml: false, // Default tooltip format, but softened
				},
			};

			let chart = new google.visualization.ComboChart(document.getElementById(narrow ? 'scenarioGraphSm' : 'scenarioGraphLg'));
			chart.draw(graphData, options);
		}
	}

	forecastPriceInput.addEventListener('input', function () {
		let value = this.value;
		// If the input doesn't start with $, add it
		if (value.charAt(0) != '$') {
			this.value = '$' + value;
		}
		calculateAndDisplay();
	});

	forecastDateInput.addEventListener('change', function () {
		this.style.color = 'black'; // Set text color to black once date changes
	});

	forecastDateInput.addEventListener('keyup', function () {
		this.style.color = 'black'; // Set text color to black once input starts
	});

	forecastDateInput.addEventListener('blur', function () {
		if (this.value == '') {
			this.style.color = 'lightgray'; // Set back to light gray if input is cleared
		}
	});

	forecastDateInput.addEventListener('input', function () {
		const maxYear = 2199;
		let dateParts = this.value.split('-'); // Split the value into [year, month, day]
		let year = parseInt(dateParts[0], 10); // Get the year as an integer
		if (year > maxYear) {
			// Correct the year to 2199, keeping the month and day unchanged
			const correctedDate = `${maxYear}-${dateParts[1]}-${dateParts[2]}`;
			this.value = correctedDate;
		}
		calculateAndDisplay();
	});

	if (redraw) calculateAndDisplay();

	if (window.analytics && !redraw)
		window.analytics.track('Viewed Scenario Model', {
			userEmail: authedUser,
			userId: userData.userid,
			workspace: client,
		});
}

async function lookForFullAccountStatements(account) {
	/* NOTES
	For activetcp (VMS), this method imports data from their transactions file
	It then creates an array of account statements with categories
	It also creates summary statisitics (acctStats)
	*/

	if (!isVMS) return;

	if (!Object.keys(account.clients[client].shares).length) return;

	let accountName = account.user_name;

	let accountFileUrl = await getContentUrl('Transactions_Export.xlsx');
	let request = new XMLHttpRequest();
	request.open('GET', accountFileUrl, true);
	request.responseType = 'blob';
	request.onload = function () {
		loadTransactionsExport(request.response);
	};
	request.send();

	function loadTransactionsExport(file) {
		let rABS = typeof FileReader !== 'undefined' && FileReader.prototype && FileReader.prototype.readAsBinaryString;
		const reader = new FileReader();
		if (rABS) reader.readAsBinaryString(file);
		else reader.readAsArrayBuffer(file);

		reader.onload = async (e) => {
			if (e.target.result.length > 1e8) {
				alert('File too large for timely process.');
				return;
			}

			let results = e.target.result;
			let wb, arr;
			let readtype = { type: rABS ? 'binary' : 'base64' };
			if (!rABS) results = btoa(arr);

			async function doit() {
				try {
					const XLSX = await window.loadXLSX();
					wb = XLSX.read(results, readtype);
					if (Object.keys(wb.Sheets).length > 1) {
						alert('This workbook has more than one sheet.  Aborting.');
						return;
					}
					results = XLSX.utils.sheet_to_csv(wb.Sheets[wb.SheetNames[0]]);
					let array = cleanCSV(results);
					let partnerNames = [];
					array.forEach((row) => {
						if (!partnerNames.includes(row[1])) partnerNames.push(row[1]);
					});
					let likelyScore = 0;
					let likelyName = '';
					partnerNames.forEach(function (name) {
						if (name == accountName) {
							likelyScore = 10;
							likelyName = name;
							return;
						}
						if (account.clients[client].notes && account.clients[client].notes.includes(name)) {
							likelyScore = 5;
							likelyName = name;
							return;
						}
						let thisScore = similar(name, accountName);
						if (thisScore > likelyScore) {
							likelyScore = thisScore;
							likelyName = name;
						}
					});
					if (likelyScore < 1) likelyName = 'Unknown';
					console.log('likelyName (' + likelyScore.toFixed(2) + ') = ' + likelyName);

					function similar(a, b) {
						let equivalency = 0;
						let long = a;
						let short = b;
						if (a.length < b.length) {
							long = b;
							short = a;
						}
						let minLength = short.length;
						let maxLength = long.length;
						let slack = maxLength - minLength;
						for (let s = 0; s < slack; s++) {
							for (let i = 0; i < minLength; i++) {
								if (short[i + s] == long[i]) {
									equivalency++;
								}
							}
						}
						let weight = equivalency / minLength;
						// console.log("Similar weight = " + weight);
						return weight;
					}

					array = array.filter((row) => row[1] == likelyName);
					// console.log("acctArray rowCount = " + array.length);
					// console.log("acctArray colCount = " + (array.length > 0 ? array[0].length : 0));
					// console.log(array);

					// fund_name	investor_name	transaction_amount	transaction_date	transaction_quarter	transaction_type	transaction_name	is_capital_call	is_distribution
					// let acctData = array.map(([f, s, a, d, q, t, n], idx) => [{"qtr":q, "data":[{"amt":Number(a.replace(/[^.\d]/g, '')), "type":t, "desc":n, "date":d}]}]);
					let acctData = array.map(([f, s, a, d, q, t, n], idx) => [q, Number(a.replace(/[^.-\d]/g, '')), t, n, d]);

					// sort by qtr then type
					acctData = acctData.sort((a, b) => (a[2] < b[2] ? -1 : 1));
					acctData = acctData.sort((a, b) => (a[0] < b[0] ? -1 : 1));
					// console.log(acctData);

					acctStats['com'] = 0;
					acctStats['exp'] = 0;
					acctStats['inv'] = 0;
					acctStats['ret'] = 0;
					acctStats['val'] = 0;

					let stmt = {};
					let qtr = 'q0';
					resetStmt();
					function resetStmt() {
						stmt = Object.create({});
						stmt['qtr'] = '';
						stmt['comm'] = 0;
						stmt['cont'] = 0;
						stmt['dist'] = 0;
						stmt['mgmt'] = 0;
						stmt['oper'] = 0;
						stmt['synd'] = 0;
						stmt['real'] = 0;
						stmt['unre'] = 0;
						stmt['beg'] = 0;
						stmt['end'] = 0;
					}

					acctData.forEach(function (row, idx) {
						if (row[0] != qtr) {
							statements.push(stmt);
							let begBal = stmt.end;
							resetStmt();
							qtr = row[0];
							stmt.qtr = qtr;
							stmt.beg = begBal;
							stmt.end = begBal;
						}
						if (row[0] == qtr) {
							switch (row[2]) {
								case 'Commitment':
									stmt.comm += row[1];
									acctStats.com += row[1];
									break;
								case 'Contribution':
									stmt.cont += row[1];
									stmt.end += row[1];
									acctStats.inv += row[1];
									break;
								case 'Distribution':
									stmt.dist += row[1];
									stmt.end -= row[1];
									acctStats.ret += row[1];
									break;
								case 'Management Fee':
									stmt.mgmt -= row[1];
									stmt.end -= row[1];
									acctStats.exp += row[1];
									break;
								case 'Operating Gain/(Loss)':
									stmt.oper += row[1];
									stmt.end += row[1];
									acctStats.exp -= row[1];
									break;
								case 'Syndication Costs':
									stmt.synd -= row[1];
									stmt.end -= row[1];
									acctStats.exp += row[1];
									break;
								case 'Realized Gain/(Loss)':
									stmt.real += row[1];
									stmt.end += row[1];
									acctStats.val += row[1];
									break;
								case 'Unrealized Gain/(Loss)':
									stmt.unre += row[1];
									stmt.end += row[1];
									acctStats.val += row[1];
									break;
								case 'Transfer of Contributions':
									stmt.cont += row[1];
									stmt.end += row[1];
									acctStats.inv += row[1];
									break;
								case 'Transfer of Distribution':
									stmt.cont -= row[1];
									stmt.end -= row[1];
									acctStats.inv -= row[1];
									break;
								case 'Transfer of P&L':
									stmt.cont += row[1];
									stmt.end += row[1];
									acctStats.inv += row[1];
									break;
								case 'Transfer of Commitment':
									stmt.comm += row[1];
									acctStats.com += row[1];
									break;
							}
						}
						if (idx == acctData.length - 1) statements.push(stmt);
					});

					if (statements && statements.length > 1) {
						statements.shift();
						let lastStmt = statements[statements.length - 1];
						if (Math.abs(lastStmt.beg) < 0.01 && Math.abs(lastStmt.end) < 0.01) statements.pop();
						buildUserCapitalAccountModal(true);
					}
				} catch (err) {
					console.error(err);
				}
			}
			if (e.target.result.length > 1e8) {
				opts.errors.large(e.target.result.length, async function (e) {
					if (e) await doit();
				});
			} else {
				await doit();
			}
		};
	}
}

function createBasicAccountStatements(theseEvents) {
	/* NOTES
	For jmhcapital, this method gets basic activity data from Events
	It then creates an array of account statements with just 3 categories
	It also creates summary statisitics (acctStats)
	*/

	statements = [];
	acctStats['com'] = 0;
	acctStats['val'] = 0;
	acctStats['inv'] = 0;
	acctStats['ret'] = 0;
	acctStats['val'] = 0;

	let stmt = {};
	let thisQtr = 'q0';
	resetStmt();
	function resetStmt() {
		stmt = Object.create({});
		stmt['qtr'] = '';
		stmt['cont'] = 0;
		stmt['dist'] = 0;
		stmt['accr'] = 0;
		stmt['beg'] = 0;
		stmt['end'] = 0;
	}

	let userClient = userData.clients[client];
	let clientEvents = clientData.equity[symbol].events;

	theseEvents.forEach(function (e, index) {
		let date = new Date(e.date * 1000);
		let year = date.getFullYear();
		let month = date.getMonth();
		let qtr = '';
		if (month < 3) qtr = year + ' Q1';
		else if (month < 6) qtr = year + ' Q2';
		else if (month < 9) qtr = year + ' Q3';
		else if (month < 12) qtr = year + ' Q4';

		// if (e.nrid.startsWith("transaction")) {
		// 	if (e.nrid == "transactionSender") sumContributions -= Number(e.grossDollars);
		// 	else sumContributions += Number(e.grossDollars);
		// 	if (index >= allEvents.length - 1) pushTVPI (e.date);	// only push if final event
		// 	return;
		// }
		if (thisQtr != qtr) {
			statements.push(stmt);
			let begBal = stmt.end;
			resetStmt();
			thisQtr = qtr;
			stmt.qtr = qtr;
			stmt.beg = begBal;
			stmt.end = begBal;
		}
		if (thisQtr == qtr) {
			let clientEvent = clientEvents.find((obj) => obj.nrid == e.nrid);
			if (!clientEvent && e.nrid.startsWith('transaction')) clientEvent = e;
			if (clientEvent) {
				let amt = Number(e.grossDollars);
				if (e.nrid.endsWith('Sender')) amt = -amt;
				if (clientEvent.eventType == 'Contribution') {
					stmt.cont += amt;
					stmt.end += amt;
					acctStats.inv += amt;
				} else if (clientEvent.eventType == 'Gain (Loss) Accrual') {
					stmt.accr += amt;
					stmt.end += amt;
					acctStats.val += amt;
				} else if (clientEvent.eventType == 'Distribution') {
					stmt.dist += amt;
					stmt.end -= amt;
					acctStats.ret += amt;
				}
			}
		}
		if (index == theseEvents.length - 1) statements.push(stmt);
	});

	if (statements && statements.length > 1) {
		statements.shift();
		let lastStmt = statements[statements.length - 1];
		if (Math.abs(lastStmt.beg) < 0.01 && Math.abs(lastStmt.end) < 0.01) statements.pop();
		buildUserCapitalAccountModal(false);
	}
}

function buildUserCapitalAccountModal(full) {
	/* NOTES
	This method renders a table of account statments from the statements array
	It also displays summary statistics (acctStats)
	For activatecp (VMS), it will generate conventional pdf statement
	*/

	// Build a new statements list
	let includedClasses = [];

	// Deep-clone so mutations don't affect the shared AppState object
	let statementUserData = structuredClone(pn.includes('/admin/users/edit') ? shareholderData : userData);

	if (!statementUserData) return;

	classes.forEach(function (c, index) {
		let classIncluded = false;
		let userShares = statementUserData?.clients?.[client]?.shares?.[c];
		let clientEvents = clientData.equity[c].events;
		if (userShares && userShares.events) {
			userShares.events.forEach(function (e) {
				let thisEvent = clientEvents.find((ev) => ev.nrid == e.nrid);
				if (
					thisEvent &&
					['Contribution', 'Distribution', 'Cash Dividend', 'Gain (Loss) Accrual', 'Redemption', 'Return of Capital'].includes(thisEvent.eventType)
				)
					classIncluded = true;
			});
		}
		if (classIncluded) includedClasses.push(c);
	});

	let filterViewStatements = document.getElementById('filterViewStatements');

	if (includedClasses.length <= 1) {
		document.getElementById('filterWrapViewStatements').style.display = 'none';
	} else {
		document.getElementById('filterWrapViewStatements').style.display = 'block';
		let labelViewStatements = document.getElementById('labelViewStatements');
		if (labelViewStatements) labelViewStatements.innerHTML = clientData.use_real_estate_language ? 'Property' : UFL ? 'Fund' : 'Share&nbsp;Class';

		selectedClass = classes[0];

		filterViewStatements.innerHTML = `
			<option value="" disabled selected>Select</option>
			${includedClasses
				.map(
					(c, index) =>
						`
					<option ${c == symbol ? 'selected' : ''} value="${c}">${c}</option>
				`
				)
				.join('')}
			<label>Fund</label>
		`;
	}

	//if (transactions.length > 0 || events.length > 0) showFilteredEventsTrans();		// OK NOW (JM Search)

	let addedCreatePdfListener;
	function renderUserCapitalAccountModal() {
		let stringOptions = m0;
		let tvpiOptions = m2;
		let acctStatsVal = acctStats.val;
		if (document.getElementById('showDecimalsCapAcct').checked) {
			stringOptions = m2;
			tvpiOptions = m4;
		} else if (acctStatsVal < 0 && acctStatsVal > -0.5) {
			acctStatsVal = 0;
		}
		function acctFormat(n) {
			return n < 0 ? '(' + Math.abs(n).toLocaleString('en-US', stringOptions) + ')' : n.toLocaleString('en-US', stringOptions);
		}

		narrow = screen.width < 600;
		let userStatementsMarkup = `
			<div class="table-wrap mb-0">
				<div class="sm-hz-scroll-no-margin">
					<table id="userStatementsSortTable" class="highlight table-content responsive-table" style="table-layout:fixed;">
						<thead>
							<tr class="${full || narrow ? `text-xs` : `text-sm`}">
								<th class="center-align" data-sort-default>Reporting${narrow ? ` ` : `<br>&ensp;&ensp;`}Period</th>
								<th class="center-align" style="min-width: 10px;">Beginning${narrow ? ` ` : `<br>&ensp;&ensp;`}Balance</th>
								<th class="center-align" style="min-width: 10px;">${narrow ? ` ` : `&ensp;&ensp;`}Contributions</th>
								<th class="center-align" style="min-width: 10px;">${narrow ? ` ` : `&ensp;&ensp;`}Distributions</th>
								${
									full
										? `
									<th class="center-align" style="min-width: 10px;">Management${narrow ? ` ` : `<br>&ensp;&ensp;`}Fees</th>
									<th class="center-align" style="min-width: 10px;">Operating${narrow ? ` ` : `<br>&ensp;&ensp;`}Gains (Losses)</th>
									<th class="center-align" style="min-width: 10px;">Syndication${narrow ? ` ` : `<br>&ensp;&ensp;`}Costs</th>
									<th class="center-align" style="min-width: 10px;">Realized${narrow ? ` ` : `<br>&ensp;&ensp;`}Gains (Losses)</th>
									<th class="center-align" style="min-width: 10px;">Unrealized${narrow ? ` ` : `<br>&ensp;&ensp;`}Gains (Losses)</th>
								`
										: `
									<th class="center-align" style="min-width: 10px;">${narrow ? ` ` : `&ensp;&ensp;`}Gains (Losses)</th>
								`
								}
								<th class="center-align" style="min-width: 10px;">Ending${narrow ? ` ` : `<br>&ensp;&ensp;`}Balance</th>
							</tr>
						</thead>
						<tbody>
						${statements
							.map(
								(stmt, index) =>
									`
							<tr class="${full || narrow ? `text-xs` : `text-sm`}">
								<td class="center-align${full ? ` text-sm link-accent create-pdf` : ``}">${stmt.qtr}</td>
								<td class="center-align" data-sort='${stmt.beg}'>${acctFormat(stmt.beg)}</td>
								<td class="center-align" data-sort='${stmt.cont}'>${acctFormat(stmt.cont)}</td>
								<td class="center-align" data-sort='${stmt.dist}'>${acctFormat(stmt.dist)}</td>
								${
									full
										? `
									<td class="center-align" data-sort='${stmt.mgmt}'>${acctFormat(stmt.mgmt)}</td>
									<td class="center-align" data-sort='${stmt.oper}'>${acctFormat(stmt.oper)}</td>
									<td class="center-align" data-sort='${stmt.synd}'>${acctFormat(stmt.synd)}</td>
									<td class="center-align" data-sort='${stmt.real}'>${acctFormat(stmt.real)}</td>
									<td class="center-align" data-sort='${stmt.unre}'>${acctFormat(stmt.unre)}</td>
								`
										: `
									<td class="center-align" data-sort='${stmt.accr}'>${acctFormat(stmt.accr)}</td>
								`
								}								
								<td class="center-align" data-sort='${stmt.end}'>${acctFormat(stmt.end)}</td>
							</tr>
							`
							)
							.join('')}
						</tbody>
					</table>
				</div>
			</div>
		`;

		document.getElementById('userStatementsTable').innerHTML = userStatementsMarkup;
		let table = document.getElementById('userStatementsSortTable');
		if (table) {
			let sort = new Tablesort(table, { descending: true });

			document.getElementById('exportStatements').addEventListener('click', function (e) {
				e.preventDefault();
				export_table_to_excel('userStatementsSortTable', userData.user_name + ' Statements Report (' + new Date().toLocaleString() + ').csv');
			});
		}

		let numAbbr = new NumAbbr(['K', 'M', 'B', 'T']);
		let userStatementsTotalsMarkup = `
			<div class="">
				<div class="stats-card">
					<div class="stat-item-3 sm-border-t-1">
						<div class=${narrow ? 'p-0' : 'px-4'}>
							<dl>
								<dd class="text-secondary">Investment</dd>
								<dd class="text-2xl font-bold text-primary">
									$${narrow ? numAbbr.abbreviate(acctStats.inv, 2) : acctStats.inv.toLocaleString('en-US', stringOptions)}
								</dd>
							</dl>
						</div>
					</div>

					<div class="stat-item-3 ${narrow ? '' : 'md-border-l-1'} sm-border-t-1">
						<div class=${narrow ? 'p-0' : 'px-4'}>
							<dl>
								<dd class="text-secondary">Cash Returns</dd>
								<dd class="text-2xl font-bold text-primary">
									$${narrow ? numAbbr.abbreviate(acctStats.ret, 2) : acctStats.ret.toLocaleString('en-US', stringOptions)}
								</dd>
							</dl>
						</div>
					</div>

					<div class="stat-item-3 ${narrow ? '' : 'md-border-l-1'} sm-border-t-1">
						<div class=${narrow ? 'p-0' : 'px-4'}>
							<dl>
								<dd class="text-secondary">${full ? 'Fund Expenses' : 'Gains (Losses)'}</dd>
								<dd class="text-2xl font-bold text-primary">
									$${narrow ? numAbbr.abbreviate(full ? acctStats.exp : acctStatsVal, 2) : (full ? acctStats.exp : acctStatsVal).toLocaleString('en-US', stringOptions)}
								</dd>
							</dl>
						</div>
					</div>

					<div class="stat-item-3 ${narrow ? '' : 'md-border-l-1'} sm-border-t-1">
						<div class=${narrow ? 'p-0' : 'px-4'}>
							<dl>
								<dd class="text-secondary">${full ? 'Portfolio Value' : 'Total Value'}</dd>
								<dd class="text-2xl font-bold text-primary">
									$${narrow ? numAbbr.abbreviate(acctStats.inv + acctStats.val, 2) : (acctStats.inv + acctStats.val).toLocaleString('en-US', stringOptions)}
								</dd>
							</dl>
						</div>
					</div>

					<div class="stat-item-3 ${narrow ? '' : 'md-border-l-1 md-border-r-1'} sm-border-t-1 sm-border-b-1">
						<div class=${narrow ? 'p-0' : 'px-4'}>
							<dl>
								<dd class="text-secondary">TVPI</dd>
								<dd id="userStatemntsTVPI" class="text-2xl font-bold text-primary">
									${acctStats.inv == 0 ? "<span style='font-size: 2rem; line-height: 120%;'>∞</span>" : ((acctStats.inv + acctStats.val + (full ? acctStats.ret - acctStats.exp : 0)) / acctStats.inv).toLocaleString('en-US', tvpiOptions)}
								</dd>
							</dl>
						</div>
					</div>
				</div>
			
				${
					full
						? `
					<div>
						<div class="row mb-0 ${narrow ? 'mt-6' : 'mt-0'} md-border-b-1">
							<div class="col s6">
								<div class="center-align p-1 sm-p-1">
									<span class="text-secondary ${narrow ? 'text-xs' : ''}">Committed&nbsp;Capital: </span>
									<span class="text-lg text-primary">
										$${acctStats.com.toLocaleString('en-US', stringOptions)}
									</span>
								</span>
							</div>
						</div>

						<div class="row mb-0 ${narrow ? 'mt-6' : 'mt-0'} md-border-b-1">
							<div class="col s6">
								<div class="center-align p-1 sm-p-1">
									<span class="text-secondary ${narrow ? 'text-xs' : ''}">Remaining&nbsp;Callable: </span>
									<span class="text-lg text-primary">
										$${(acctStats.com - acctStats.inv).toLocaleString('en-US', stringOptions)}
									</span>
								</div>
							</div>
						</div>
					</div>
				`
						: ``
				}
				</div>
		`;

		document.getElementById('userStatementsTotalsTable').innerHTML = userStatementsTotalsMarkup;

		if (!addedCreatePdfListener && full) {
			// addedCreatePdfListener = true;
			document.querySelectorAll('.create-pdf').forEach((item) => {
				item.addEventListener('click', async (event) => {
					event.preventDefault();

					M.Modal.getInstance(document.getElementById('viewCapAcctModal')).close();
					M.Modal.getInstance(document.getElementById('viewMarvinReportModal')).close();
					M.Modal.getInstance(document.getElementById('statementWaitState')).open();

					let qtr = event.target.innerText;
					let year = qtr.split(' ')[0];
					let thisDate =
						qtr.split(' ')[1].replace('Q1', 'March 31').replace('Q2', 'June 30').replace('Q3', 'September 30').replace('Q4', 'December 31') +
						', ' +
						year;
					let thisIndex = statements.findIndex((s) => s.qtr == qtr);
					let thisStatement = statements[thisIndex];
					let priorStatments = statements.filter((s, i) => i <= thisIndex);
					let thisYearStatements = priorStatments.filter((s, i) => s.qtr.split(' ')[0] == year);

					// let sum = priorStatments.reduce((a, b) => a.beg + b.beg, 0);
					let allComm = priorStatments.reduce((a, b) => a + b.comm, 0);
					let allCont = priorStatments.reduce((a, b) => a + b.cont, 0);

					let statementJson = {
						end_date: thisDate,
						investor_name: statementUserData.user_name, //"Alaska Permanent Fund Corporation",
						commitments: acctFormat(allComm),
						contributions: acctFormat(allCont),
						remaining: acctFormat(allComm - allCont),
						qtd_beginning: acctFormat(thisStatement.beg),
						ytd_beginning: acctFormat(thisYearStatements[0].beg),
						itd_beginning: acctFormat(priorStatments[0].beg),
						qtd_contributions: acctFormat(thisStatement.cont),
						ytd_contributions: acctFormat(thisYearStatements.reduce((a, b) => a + b.cont, 0)),
						itd_contributions: acctFormat(priorStatments.reduce((a, b) => a + b.cont, 0)),
						qtd_distributions: acctFormat(thisStatement.dist),
						ytd_distributions: acctFormat(thisYearStatements.reduce((a, b) => a + b.dist, 0)),
						itd_distributions: acctFormat(priorStatments.reduce((a, b) => a + b.dist, 0)),
						qtd_mgt_fees: acctFormat(thisStatement.mgmt),
						ytd_mgt_fees: acctFormat(thisYearStatements.reduce((a, b) => a + b.mgmt, 0)),
						itd_mgt_fees: acctFormat(priorStatments.reduce((a, b) => a + b.mgmt, 0)),
						qtd_operating: acctFormat(thisStatement.oper),
						ytd_operating: acctFormat(thisYearStatements.reduce((a, b) => a + b.oper, 0)),
						itd_operating: acctFormat(priorStatments.reduce((a, b) => a + b.oper, 0)),
						qtd_syndication: acctFormat(thisStatement.synd),
						ytd_syndication: acctFormat(thisYearStatements.reduce((a, b) => a + b.synd, 0)),
						itd_syndication: acctFormat(priorStatments.reduce((a, b) => a + b.synd, 0)),
						qtd_realized: acctFormat(thisStatement.real),
						ytd_realized: acctFormat(thisYearStatements.reduce((a, b) => a + b.real, 0)),
						itd_realized: acctFormat(priorStatments.reduce((a, b) => a + b.real, 0)),
						qtd_unrealized: acctFormat(thisStatement.unre),
						ytd_unrealized: acctFormat(thisYearStatements.reduce((a, b) => a + b.unre, 0)),
						itd_unrealized: acctFormat(priorStatments.reduce((a, b) => a + b.unre, 0)),
						qtd_ending: acctFormat(thisStatement.end),
						ytd_ending: acctFormat(thisStatement.end),
						itd_ending: acctFormat(thisStatement.end),
					};

					let request = new XMLHttpRequest();
					let accountFileUrl = await getContentUrl('StatementTemplate.docx');

					if (true) {
						let htmFileUrl = await getContentUrl('StatementTemplate.htm');
						fetch(htmFileUrl)
							.then(function (response) {
								console.log(response);
								return response.text();
							})
							.then(function (html) {
								// TODO: #4629 - migrate template image URLs to presigned
								let htmlResults = html.replace(/src="images\//g, 'src="' + htmFileUrl + '_');
								// htmlResults = htmlResults.replace(/<body /, '<body style="width:600px; min-width:600px; max-width:600px;" ');
								Object.keys(statementJson).forEach(
									(e) =>
										(htmlResults = htmlResults
											.replace(new RegExp('{' + e + '}', 'g'), statementJson[e])
											.replace(
												new RegExp('{<span.*class=SpellE>' + e + '</span>}', 'g'),
												'<span class=SpellE>' + statementJson[e] + '</span>'
											))
								);
								generateStatement(htmlResults);
							})
							.catch(function (err) {
								// There was an error
								console.warn('Something went wrong.', err);
							});
					} else {
						// let request = new XMLHttpRequest();
						request.open('GET', accountFileUrl, true);
						request.responseType = 'blob';
						request.onload = function () {
							loadAccountStatementTemplate(request.response);
						};
						request.send();
					}

					// HTML Approach
					function generateStatement(htmlResults) {
						console.log('Creating capital statement PDF');
						let fileName = statementUserData.user_name + ' Capital Statement for ' + thisDate + '.pdf';
						document.dispatchEvent(
							new CustomEvent('ConvertToPDF', { detail: { htmlResults: htmlResults, fileName: fileName, investor: statementUserData.userid } })
						);
						if (!addedResultantPDFListenerAlready) {
							addedResultantPDFListenerAlready = true;
							document.addEventListener('ResultantPDF', function (ev) {
								// uploadToYourDocs(ev.detail.url, ev.detail.fileName, ev.detail.investor);
								window.open(ev.detail.url);

								// DOWNLOAD PDF FROM URL
								request = new XMLHttpRequest();
								request.open('GET', ev.detail.url, true);
								request.responseType = 'blob';
								request.onload = function () {
									saveAs(ev.detail.url, cleanStampedName(fileName)); //Date.now() + "_" + fileName.replace(/[,\s]/g, '_'));
									M.Modal.getInstance(document.getElementById('statementWaitState')).close();
									if (isMAR) M.Modal.getInstance(document.getElementById('viewReportDataModal')).open();
									else M.Modal.getInstance(document.getElementById('viewCapAcctModal')).open();
									// M.Modal.getInstance(document.getElementById('viewCapAcctModal')).open();
								};
								request.send();

								if (window.analytics)
									window.analytics.track('Generated Statement', {
										userEmail: authedUser,
										workspace: client,
										documentTitle: fileName,
									});
							});
						}
					}

					// NOT USED
					function uploadToYourDocs(pdfUrl, name, investor) {
						let awsFileToUpload = cleanStampedName(name); //Date.now() + "_" + name.replace(/[,\s]/g, '_');
						let newDoc = {
							name: 'Capital Statement',
							description: fileName.replace('HASH', '#').replace('.pdf', '').trim(),
							require2Factor: false,
							type: 'Certificate',
							tag: thisClass,
							url: fileToUploadAws,
							date: moment.utc(thisTrans.date).format('MMM D, YYYY'),
							id: docId,
						};

						// NOT ENABLED.  NO CAP TABLE DATA ON USER SIDE.
						let holderData = capTableData.find((x) => investor == x.userid).clients[client];
						if (!holderData.documents) {
							holderData.documents = [];
						}
						holderData.documents.push(newDoc);

						if (authedUser == investor) {
							// update all session data
							AppState.userData = JSON.parse(sessionStorage.getItem('user.Data'));
							userData.clients[client] = holderData;
							sessionStorage.setItem('user.Data', JSON.stringify(userData));
						}
						axios
							.post(
								postURL,
								JSON.stringify({
									eventName: 'index.NOT_USED',
									investor: investor,
									userid: authedUser,
									client: client,
									cookie: cookie,
									clients: {
										[client]: {
											documents: holderData.documents,
										},
									},
								})
							)
							.then(function (res) {
								// needed for doc
								refreshCapTable();

								// 3) UPLOAD PDF AS NEW DOC
								fetch(pdfUrl)
									.then(function (response) {
										return response.blob();
									})
									.then(function (pdfFile) {
										axios
											.get(authUploadUrl, {
												params: {
													filename: client + '/' + investor.replace(/\+/g, 'PLUS') + '/' + awsFileToUpload,
													client: client,
													userid: authedUser,
													cookie: cookie,
												},
											})
											.then(function (res1) {
												let formData = secureFormData(res1);
												formData.append('file', pdfFile);
												axios
													.post(res1.data.url, formData, axconf)
													.then(function (res2) {
														//console.log(res2);
														processWaitState.innerHTML = `
											<div class="p-2 center-align">
												<div class="mb-4"><img src="./../../resources/assets/celebrate-v4.svg" alt="Musicians celebrating" class="responsive-img px-12"/></div>
												<h3 class="text-lg mb-4 mt-4 font-bold">Success!!</h3>
												<span id="processMessage" class="text-xs text-secondary">Processed ${fileName}</span>
											</div>
										`;
													})
													.catch(function (err2) {
														console.error(err2);
													});
											})
											.catch(function (err1) {
												console.error(err1);
											});

										if (window.analytics)
											window.analytics.track('Shareholder Document Added', {
												userEmail: authedUser,
												workspace: client,
												documentTitle: newDoc.name,
												documentFileType: newDoc.url.split('.').pop(),
											});
									});
							})
							.catch(function (res) {
								console.log(res);
							});
					}

					// For .docx
					function loadAccountStatementTemplate(file) {
						let rABS = typeof FileReader !== 'undefined' && FileReader.prototype && FileReader.prototype.readAsBinaryString;
						const reader = new FileReader();
						if (rABS) reader.readAsBinaryString(file);
						else reader.readAsArrayBuffer(file);

						reader.onload = (e) => {
							if (e.target.result.length > 1e8) {
								alert('File too large for timely process.');
								return;
							}

							let results = e.target.result;
							let wb, arr;
							let readtype = { type: rABS ? 'binary' : 'base64' };

							if (!rABS) results = btoa(arr);

							function processTemplate() {
								try {
									let PizZip = require('pizzip');
									let zip = new PizZip(results);
									let Docxtemplater = require('docxtemplater');
									let doc = new Docxtemplater(zip);
									try {
										// render the document ie replace the variables
										doc.render(statementJson);
										let buf = doc.getZip().generate({ type: 'base64' });
										let fileName = clientData.company_name + ' - ' + qtr + ' Capital Account Statement.docx';

										// download Word doc
										// let element = document.createElement('a');
										// element.setAttribute('href', 'data:application/vnd.openxmlformats-officedocument.wordprocessingml.document;base64,' + buf);
										// element.setAttribute('download', fileName);
										// element.style.display = 'none';
										// document.body.appendChild(element);
										// element.click();
										// document.body.removeChild(element);

										// helper function: generate a new file from base64 String
										const dataURLtoFile = (dataurl, fileName) => {
											const arr = dataurl.split(',');
											const mime = arr[0].match(/:(.*?);/)[1];
											const bstr = atob(arr[1]);
											let n = bstr.length;
											const u8arr = new Uint8Array(n);
											while (n) {
												u8arr[n - 1] = bstr.charCodeAt(n - 1);
												n -= 1; // to make eslint happy
											}
											return new File([u8arr], fileName, { type: mime });
										};

										// generate file from base64 string
										const fileContent = dataURLtoFile(
											'data:application/vnd.openxmlformats-officedocument.wordprocessingml.document;base64,' + buf
										);
										// Use nth.IdToken auth token and custom API to get credentials for S3 upload
										let fileToUploadAws = cleanStampedName(fileName); //Date.now() + "_" + fileName.replace(/[,\s]/g, '_');
										axios
											.get(authUploadUrl, {
												params: {
													filename: client + '/' + authedUser + '/' + fileToUploadAws,
													client: client,
													userid: authedUser,
													cookie: cookie,
												},
											})
											.then(function (res1) {
												//console.log(res1);
												let formData = secureFormData(res1);
												formData.append('file', fileContent);
												axios
													.post(res1.data.url, formData, axconf)
													.then(function (res2) {
														console.log(res2);
														axios
															.get('https://api.nthround.io/portal/auth/get-presigned-url', {
																params: {
																	client: client,
																	userid: authedUser,
																	cookie: cookie,
																	shareholder: authedUser,
																	doc_name: fileToUploadAws,
																},
															})
															.then(function (response) {
																// console.log(response);
																console.log(response.data);
																let Api2Pdf = require('api2pdf');
																let a2pClient = new Api2Pdf('6e053f8e-75b7-4271-a316-1981d7640681');
																a2pClient.libreOfficeAnyToPdf(response.data).then(function (result) {
																	console.log(result);

																	// 1) DOWNLOAD PDF FROM URL
																	request = new XMLHttpRequest();
																	request.open('GET', result.FileUrl, true);
																	request.responseType = 'blob';
																	request.onload = function () {
																		loadPdfAccountStatement(request.response);
																	};
																	request.send();

																	function loadPdfAccountStatement(file) {
																		saveAs(result.FileUrl, fileToUploadAws.replace('.docx', '.pdf'));

																		M.Modal.getInstance(document.getElementById('statementWaitState')).close();
																		M.Modal.getInstance(document.getElementById('viewCapAcctModal')).open();
																		M.Modal.getInstance(document.getElementById('viewMarvinReportModal')).open();

																		// 4) DELETE OLD DOCX
																		axios
																			.get('https://api.nthround.io/portal/auth/delete-document', {
																				params: {
																					client: client,
																					userid: authedUser,
																					cookie: cookie,
																					shareholder: authedUser,
																					doc_name: fileToUploadAws.replace('.pdf', '.docx'),
																				},
																			})
																			.then(function (response) {
																				console.log(response);
																			})
																			.catch(function (error) {
																				console.error(error);
																			});
																	}
																});
															})
															.catch(function (error) {
																console.error(error);
															});
													})
													.catch(function (err2) {
														console.error(err2);
													});
											})
											.catch(function (err1) {
												console.error(err1);
											});
									} catch (error) {
										let e = {
											message: error.message,
											name: error.name,
											stack: error.stack,
											properties: error.properties,
										};
										console.log(JSON.stringify({ error: e }));
										throw error;
									}
								} catch (err) {
									console.error(err);
								}
							}
							if (e.target.result.length > 1e8) {
								opts.errors.large(e.target.result.length, function (e) {
									if (e) {
										processTemplate();
									}
								});
							} else {
								processTemplate();
							}
						};
					}
				});
			});
		}
	}

	if (HAS_STMTS || statements.length > 0) {
		viewEventsTransReportIconLarge.classList.remove('hidden');
		if (viewEventsTransReportIconSmall) viewEventsTransReportIconSmall.classList.remove('hidden');

		if (isMAR) {
			//MARVIN CUSTOM REPORT
			viewEventsTransReportIconLarge.classList.remove('hidden');
			if (viewEventsTransReportIconSmall) viewEventsTransReportIconSmall.classList.remove('hidden');
		}

		renderUserCapitalAccountModal();
	} else {
		viewEventsTransReportIconLarge.classList.add('hidden');
		if (isMAR) {
			//MARVIN CUSTOM REPORT
			viewEventsTransReportIconLarge.classList.add('hidden');
			if (viewEventsTransReportIconSmall) viewEventsTransReportIconSmall.classList.add('hidden');
		}
	}

	if (!addedAccountSummaryListenersAlready) {
		addedAccountSummaryListenersAlready = true;
		filterViewStatements.addEventListener('change', function (e) {
			selectedClass = filterViewStatements.value;
			let allEvents = [...userData.clients[client].shares[selectedClass].events];
			if (allEvents) {
				symbol = selectedClass;
				// Support consolidated view
				const filterUserIds = getActiveUserIds(userData);
				let transactions = clientData.equity[symbol].transactions.filter((trans) => isUserTransaction(trans, filterUserIds));
				transactions.forEach(function (t) {
					// Check if sender for consolidated view
					const senderLower = t.sender?.toLowerCase();
					const isSender = senderLower && filterUserIds.some((uid) => uid?.toLowerCase() === senderLower);
					let newEvent = {
						date: t.date,
						eventType: 'Contribution', //t.securityType,
						grossDollars: (Number(t.quantity) * Number(t.sharePrice)).toString(),
						nrid: isSender ? 'transactionSender' : 'transactionReceiver',
					};
					allEvents.push(newEvent);
				});

				allEvents.forEach(function (e, i) {
					let thisEvent = clientData.equity[symbol].events.find((obj) => obj.nrid == e.nrid);
					if (thisEvent) allEvents[i].date = thisEvent.date;
				});
				allEvents = allEvents.filter((e) => e.date);
				allEvents.sort((a, b) => (Number(a.date) < Number(b.date) ? -1 : 1));

				if (isJMH) createBasicAccountStatements(allEvents);
			}
		});
		document.getElementById('showDecimalsCapAcct').addEventListener('change', function (e) {
			e.preventDefault();
			if (false) {
				//includedClasses.length > 1) {
				if (!selectedClass) selectedClass = 'ALL';
				showFilteredEventsTrans();
			} else {
				renderUserCapitalAccountModal();
			}
		});
	}

	let options = {
		dropdownOptions: {
			coverTrigger: false,
		},
	};
	let elems = document.querySelectorAll('select');
	let instances = M.FormSelect.init(elems, options);
}

function updateAdminDashboard() {
	let fname = firstName(userData);
	let welcome = `Good ${getGreeting()}, ${fname}`;
	document.getElementById('welcomeMessage').innerHTML = welcome;
	document.getElementById('clientName').innerHTML = clientData.company_name;

	// Display a list of tasks to be completed
	let tasksMarkup;
	let tasks = userData.clients.tasks;
	let incompleteTasks;
	if (tasks && tasks.length) {
		incompleteTasks = tasks.filter((task) => task.complete == false);
	} else {
		incompleteTasks = [];
	}

	if (incompleteTasks.length) {
		tasksMarkup = `
			${incompleteTasks
				.slice(0, 3)
				.map(
					(task) =>
						`
				<div class="todo-card">
					<a href="${task.link}">
						<h4 class="text-lg font-bold mb-2 text-black">${task.name}</h4>
						<p class="mt-0 text-secondary">${task.description}</p>
					</a>
				</div>
				`
				)
				.join('')}
		`;
	} else {
		console.log('No incomplete tasks to show');
		// TODO: Design a nice empty state in case there are no tasks
		tasksMarkup = `
			<div class="card p-12 center-align nav-shadow">
				<div class="text-lg">You're all set. There's nothing left to do.</div>
			</div>
		`;
	}

	document.getElementById('todoList').innerHTML = tasksMarkup;
}

function previewShareholder(uid, path) {
	console.log('***Speed Test - BEFORE axios.get() : ' + (Date.now() - refreshTime) + ' ms');
	axios
		.get(clientURL, {
			params: {
				viewid: uid,
				client: client,
				userid: authedUser,
				cookie: cookie,
				host: window.location.host,
			},
		})
		.then(function (response) {
			console.log('***Speed Test - AFTER axios.get() : ' + (Date.now() - refreshTime) + ' ms');

			// breadcrumb for preview banner and exit preview
			const canPreview = isSupv || isExec || getAuthType().includes('admin');
			if (canPreview) sessionStorage.setItem('preview', 'true');
			// Preserve original restore behavior: previewer is used by supv/exec flows.
			if ((isSupv || isExec) && userData?.userid) sessionStorage.setItem('previewer', userData.userid);

			AppState.userData = response.data[0]['user'];

			if (userData) {
				if (!userData.carlist) userData.carlist = [];
				AppState.isAdmin = userData?.carlist?.includes('^' + client) || false;
				let savedLanguage = clientData?.language ? JSON.parse(JSON.stringify(clientData.language)) : null;
				AppState.clientData = response.data[1]['client'];
				if (savedLanguage) clientData.language = savedLanguage;
				sessionStorage.setItem('loadCount', 0);
				sessionStorage.removeItem('alias.Data');
				sessionStorage.setItem(client + '.Data', JSON.stringify(clientData));
				if (STD_EXT_DATA) getStandardTransactionsFromFile('userData', null, true, false, false, path);
				else if (isAlera) getAleraHoldingsFromFile('userData', null, true, false, false, path);
				else {
					if (blurNames) userData.user_name = capTableData.find((u) => u.userid == uid).user_name;
					sessionStorage.setItem('user.Data', JSON.stringify(userData));
					sessionStorage.setItem('shareholder.Data', JSON.stringify(userData));
					window.location.replace('/' + path);
				}
			}
			if (window.analytics) {
				window.analytics.track('Previewed Shareholder', {
					userEmail: authedUser,
					workspace: client,
					uid: uid,
				});
			}
		})
		.catch(function (err) {
			showToast('error-toast', 'Hmm. Something went wrong with previewShareholder.');
			console.error(err);
		});
}

document.addEventListener('GetPayments', function (e) {
	let gotPayments = getPayments(e.detail.user, e.detail.class, e.detail.date);
	document.dispatchEvent(
		new CustomEvent('GotPayments', { detail: { gotPayments: gotPayments, user: e.detail.user, class: e.detail.class, date: e.detail.date } })
	);
});

document.addEventListener(
	'previewShareholder',
	function (e) {
		previewShareholder(e.detail.uid, e.detail.path);
	},
	false
);

document.addEventListener('ConvertToPDF', function (e) {
	// console.log(e.detail)
	let Api2Pdf = require('api2pdf');
	let a2pClient = new Api2Pdf('6e053f8e-75b7-4271-a316-1981d7640681');
	const margin = (clientData.pdf_margin || '0.4') + 'in';
	a2pClient
		.chromeHtmlToPdf(e.detail.htmlResults, {
			inline: false,
			filename: e.detail.fileName,
			options: {
				delay: 500,
				width: '8.5in',
				height: '11.0in',
				marginTop: margin,
				marginBottom: margin,
				marginLeft: margin,
				marginRight: margin,
			},
		})
		.then(function (result) {
			// console.log(result);
			document.dispatchEvent(
				new CustomEvent('ResultantPDF', { detail: { url: result.FileUrl, fileName: e.detail.fileName, investor: e.detail.investor } })
			);
		});
});

document.addEventListener('ConvertDocToPDF', function (e) {
	// console.log(e.detail)
	convertToPdf();
	async function convertToPdf() {
		let ipKey = '74e90ebd472d39ba695df5026ba9a8daf9013fb0b504ec248f848527';
		let ipAdd = await fetch(`https://api.ipdata.co?api-key=${ipKey}`)
			.then((res) => res.json())
			.then((json) => String(json.ip));
		/* Converts to pdf - lazy load ConvertApi */
		const ConvertApi = await window.loadConvertApi();
		let convertApi = ConvertApi.auth('TdnhROgCb0g38zGX'); //UGFwSpdFqyfhki5l
		let params = convertApi.createParams();
		params.add('File', new URL(e.detail.data));
		params.add('PdfTitle', e.detail.fileName);
		let result = await convertApi.convert('docx', 'pdf', params);
		document.dispatchEvent(
			new CustomEvent('ResultantDocToPDF', { detail: { url: result.files[0].Url, fileName: e.detail.fileName, investor: e.detail.investor } })
		);
	}
	/*
	var Api2Pdf = require('api2pdf');   
	var a2pClient = new Api2Pdf('6e053f8e-75b7-4271-a316-1981d7640681');
	a2pClient.libreOfficeAnyToPdf(e.detail.data).then(function(result) {
		// console.log(result);
		document.dispatchEvent(new CustomEvent('ResultantDocToPDF', { detail: {"url": result.FileUrl, "fileName": e.detail.fileName, "investor": e.detail.investor }}));
	});
	*/
});

document.addEventListener('ConvertDocToHTML', function (e) {
	// console.log(e.detail)
	convertToHtml();
	async function convertToHtml() {
		let ipKey = '74e90ebd472d39ba695df5026ba9a8daf9013fb0b504ec248f848527';
		let ipAdd = await fetch(`https://api.ipdata.co?api-key=${ipKey}`)
			.then((res) => res.json())
			.then((json) => String(json.ip));
		/* Converts to html - lazy load ConvertApi */
		const ConvertApi = await window.loadConvertApi();
		let convertApi = ConvertApi.auth('TdnhROgCb0g38zGX'); //UGFwSpdFqyfhki5l
		let params = convertApi.createParams();
		params.add('File', new URL(e.detail.url));
		params.add('OutputFormat', 'html');
		let result = await convertApi.convert('docx', 'html', params);
		document.dispatchEvent(new CustomEvent('ResultantDocToHTML', { detail: { url: result.files[0].Url, fileName: e.detail.fileName } }));
	}
});

document.addEventListener('keydown', function (zEvent) {
	//e.preventDefault();
	if (zEvent.ctrlKey && zEvent.altKey && zEvent.key === 'e') {
		// case sensitive
		export_table_to_excel('', 'Ctl-Alt-E Export (' + new Date().toLocaleString() + ').csv');
	}
});

function historyBack() {
	let thisPage = pn;
	history.back();
	setTimeout(() => {
		if (pn == thisPage) history.back();
	}, 50);
}

function setBackButton(buttonId) {
	let uid = userData.userid; //authedUser.toLowerCase();
	let referrer = document.referrer;
	if (!referrer) referrer = sessionStorage.getItem('referrer') || '';
	let destination = '/documents/';
	let backButton = document.getElementById(buttonId);
	if (referrer.includes('/admin/voting/')) {
		referrer = 'voting table';
		destination = '/admin/voting/?view-doc';
	} else if (referrer.includes('/admin/surveying/')) {
		referrer = 'surveying table';
		destination = '/admin/surveying/?view-doc';
	} else if (referrer.includes('/admin/signatures/')) {
		referrer = 'signatures table';
		destination = '/admin/signatures/?view-doc';
	} else if (referrer.includes('/admin/requests/')) {
		referrer = 'requests table';
		destination = '/admin/requests/?view-doc';
	} else if (referrer.includes('users/edit')) {
		uid = referrer.substring(referrer.indexOf('?email=') + 7);
		referrer = labelShareholders.toLowerCase().replace(/s$/, '') + ' edit';
		destination = '/admin/users/edit/?email=' + uid;
	} else if (referrer.includes('updates')) {
		if (window.location.href.endsWith('noback')) referrer = labelUpdatesTab.toLowerCase() + ' admin';
		else referrer = 'all ' + labelUpdatesTab.toLowerCase();
		destination = '/updates/';
	} else if (referrer.includes('reports')) {
		if (window.location.href.endsWith('noback')) referrer = labelReportsTab.toLowerCase() + ' admin';
		else referrer = 'all ' + labelReportsTab.toLowerCase();
		destination = '/reports/';
	} else if (referrer.includes('data-room')) {
		if (window.location.href.endsWith('noback')) referrer = labelDataRoomTab.toLowerCase() + ' admin';
		else referrer = labelDataRoomTab.toLowerCase();
		destination = '/data-room/';
	} else if (referrer.includes('documents')) {
		referrer = 'your documents';
		destination = '/documents/';
	} else if (referrer.includes('campaign')) {
		// if just signed, send back to Overview
		let materials0 = clientData.new_investment_materials[0];
		if (VOTING && materials0 && referrer.endsWith('view/?document=' + materials0.id)) {
			//1647999364815")) {	// Asplundh proxy form
			refreshCapTable();
			referrer = 'overview';
			destination = '/';
		} else if (ENROLL && materials0 && referrer.endsWith('view/?document=' + materials0.id)) {
			//1659405582823")) {
			refreshCapTable();
			referrer = 'overview';
			destination = '/';
		} else {
			referrer = VOTING ? (labelButtonCTA ? labelButtonCTA.toLowerCase() : 'proxy voting') : ENROLL ? 'trade sign up' : 'campaign';
			destination = '/campaign/';
		}
	} else if (referrer.includes('investor-agreement')) {
		referrer = 'investment';
		destination = '/investor-agreement/';
	} else {
		referrer = 'overview';
		destination = '/';
	}
	backButton.innerHTML = '< Back to ' + referrer;
	backButton.addEventListener('click', function (e) {
		e.preventDefault();
		if (window.location.href.endsWith('noback')) historyBack();
		else window.location.replace(destination);
	});
}

let timerId = null;
async function loadPageContent() {
	// Assign DOM refs early — needed by renderInvestorPortal/buildTransactionStatement
	viewCapAcctIconLarge = document.getElementById('viewCapAcctIconLarge');
	viewCapAcctIconSmall = document.getElementById('viewCapAcctIconSmall');
	viewEventsTransReportIconLarge = document.getElementById('viewEventsTransReportIconLarge');
	viewEventsTransReportIconSmall = document.getElementById('viewEventsTransReportIconSmall');

	if (pn.includes('/updates/view')) {
		document.getElementById('footer').classList.add('hidden');
		setBackButton('backToUpdates');

		let docId;
		let image;
		let docName;
		let embedURL;
		let watermark;
		let params = getAllUrlParams(window.location.href, true);
		if (params.document) docId = params.document;
		else window.location.replace('/updates/');

		let updates = clientData.company_updates;

		for (let i = 0; i < updates.length; i++) {
			if (updates[i]['id'] == docId) {
				// console.log(updates[i])
				embedURL = updates[i]['document'];
				docName = updates[i]['title'];
				image = updates[i]['image'];
				watermark = updates[i].watermark;
			}
		}

		if (!embedURL) embedURL = image;
		else if (embedURL && !embedURL.startsWith('http')) embedURL = await getContentUrl(embedURL);

		if (watermark && embedURL.includes('.pdf')) {
			hideLoading();
			let modalElems = document.querySelectorAll('.modal');
			M.Modal.init(modalElems, { onOpenStart: initModal });
			ensureTwoFactorModalNonDismissible();
			setTimeout(() => {
				modifyPdf(embedURL, docName, docId, 'Update');
			}, 100);
		} else renderDocument(embedURL, docName, docId, 'Update');
	}

	if (pn.includes('/reports/view')) {
		document.getElementById('footer').classList.add('hidden');
		setBackButton('backToFinancials');

		let docId;
		let docName;
		let embedURL;
		let watermark;
		let params = getAllUrlParams(window.location.href, true);
		if (params.document) docId = params.document;
		else window.location.replace('/reports/');

		let financialStatements = clientData.financial_statements;
		for (let i = 0; i < financialStatements.length; i++) {
			if (financialStatements[i]['id'] == docId) {
				embedURL = financialStatements[i]['file'];
				docName = financialStatements[i]['description'];
				watermark = financialStatements[i].watermark;
			}
		}

		// shareholder_docs??  What are those?
		// let operatingDocs = clientData.shareholder_docs;
		// if (!embedURL) {
		// 	for (let i = 0; i < operatingDocs.length; i++) {
		// 		if (operatingDocs[i]['slug'].toLowerCase() === docId.toLowerCase()) embedURL = operatingDocs[i]['file'];
		// 	}
		// }

		if (embedURL && !embedURL.startsWith('http')) embedURL = await getContentUrl(embedURL);
		if (watermark && embedURL.includes('.pdf')) {
			hideLoading();
			let modalElems = document.querySelectorAll('.modal');
			M.Modal.init(modalElems, { onOpenStart: initModal });
			ensureTwoFactorModalNonDismissible();
			setTimeout(() => {
				modifyPdf(embedURL, docName, docId, 'Report');
			}, 100);
		} else renderDocument(embedURL, docName, docId, 'Report');
	}

	if (pn.includes('/data-room/view')) {
		document.getElementById('footer').classList.add('hidden');
		setBackButton('backToDataRoom');

		let docId;
		let docName;
		let embedURL;
		let watermark;
		let params = getAllUrlParams(window.location.href, true);
		if (params.document) docId = params.document;
		else window.location.replace('/data-room/');
		let dataRoomDocs = clientData.data_room_docs;
		for (let i = 0; i < dataRoomDocs.length; i++) {
			if (dataRoomDocs[i]['id'] == docId) {
				embedURL = dataRoomDocs[i]['file'];
				docName = dataRoomDocs[i]['description'];
				watermark = dataRoomDocs[i].watermark;
				console.log(dataRoomDocs[i]);
				if (embedURL && !embedURL.startsWith('http')) embedURL = await getContentUrl(embedURL);
				if (watermark && (docName.includes('.pdf') || embedURL.includes('.pdf'))) {
					hideLoading();
					let modalElems = document.querySelectorAll('.modal');
					M.Modal.init(modalElems, { onOpenStart: initModal });
					ensureTwoFactorModalNonDismissible();
					console.log(embedURL + docName + docId);
					setTimeout(() => {
						modifyPdf(embedURL, docName, docId, 'DataRoom Doc');
					}, 100);
				} else renderDocument(embedURL, docName, docId, 'DataRoom Doc');
			}
		}
	}

	if (pn.includes('/documents/view')) {
		document.getElementById('footer').classList.add('hidden');
		setBackButton('backToDocuments');

		let docName;
		let embedURL;
		let isUnifiedCert = false;

		let uid = userData.userid;
		let referrer = document.referrer;
		if (!referrer) referrer = sessionStorage.getItem('referrer') || '';
		let documents = userData.clients[client].documents;
		if (referrer.includes('users/edit') && shareholderData) {
			uid = referrer.substring(referrer.indexOf('?email=') + 7);
			documents = shareholderData.clients[client].documents;
		}

		let docId = getAllUrlParams(window.location.href, true).document;
		if (docId.length != 36) {
			// 36 means it's a stock certficate
			getDocument();
		} else {
			let thisClass;
			let thisTrans;
			let equities = clientData.equity;
			Object.keys(equities).forEach(function (c) {
				if (equities[c].transactions) {
					let trans = equities[c].transactions.find((t) => t.nrid == docId);
					if (trans) {
						thisClass = c;
						thisTrans = trans;
					}
				}
			});

			let certHolder = (thisTrans?.receiver || uid).replace(/\+/g, 'PLUS');

			let thisCertIndex;
			let stockCerts = [];
			try {
				stockCerts = JSON.parse(sessionStorage.getItem('stockCerts')) || [];
			} catch {}

			if (stockCerts && stockCerts.length) {
				thisCertIndex = stockCerts.findIndex((c) => c.id == docId);
				let thisCert = stockCerts[thisCertIndex];

				if (thisCert && thisCert.isUnified) {
					isUnifiedCert = true;
					thisClass = thisCert.tag;
					certHolder = uid.replace(/\+/g, 'PLUS');

					const info = latestUnifiedCertForClass(thisClass);
					if (!info) {
						historyBack();
						return;
					}

					const { source, epoch } = info;

					thisTrans = {
						date: epoch,
						notes: source.description || '',
						quantity: userData.clients[client].shares?.[thisClass],
						sender: source.sender || '',
						senderName: source.senderName || '',
						receiver: source.receiver || '',
						receiverName: source.receiverName || '',
						userName: source.userName || userData.user_name,
					};
				}
			}

			// create the stock certificate
			if (thisTrans) {
				// if this is an admin refreshing, bail out
				if (!isUnifiedCert && thisTrans.receiver != userData.userid) historyBack();

				let certNumber = '';
				if (isUnifiedCert) {
					const info = latestUnifiedCertForClass(thisClass) || {};
					certNumber = info.certStr || '';
				} else {
					certNumber = getCertNumber(thisTrans.notes);
					if (certNumber) certNumber = certNumber[0];
					else certNumber = '';
				}

				// Normalize the quantity to avoid floating-point precision issues
				let normalizedQuantity = parseFloat(Number(thisTrans.quantity).toFixed(6));
				let shareWords = decimalToWords(normalizedQuantity)
					.replace(/(^|-|\s)\S/g, function (t) {
						return t.toUpperCase();
					})
					.trim();

				// Transactions can have an old name, not matching the current name on cap table
				// Starting 11/9/2023, we use the current name (on the cap table), if we can get it without the cap table...
				let shareholderName = isUnifiedCert ? userData.user_name : thisTrans.receiverName || thisTrans.userName;
				if (!isUnifiedCert && userData.userid == thisTrans.receiver) shareholderName = userData.user_name;

				const pre2021 = Number(thisTrans.date) < 1672491600;
				const issueMoment = moment.utc(Number(thisTrans.date), 'X');

				let certificateJson = {
					issue_date: issueMoment.format('MMMM D, YYYY'),
					issue_day: stringifyNumber(issueMoment.format('D')),
					issue_dayth: issueMoment.format('Do'),
					issue_month: issueMoment.format('MMMM'),
					issue_year: issueMoment.format('YYYY'),
					cert_number: certNumber ? certNumber.replace(/#/, '') : '',
					share_count: Number(thisTrans.quantity).toLocaleString(),
					share_words: shareWords.replace(/Point /, 'point ') + (isASP ? '' : ' (' + Number(thisTrans.quantity).toLocaleString() + ')'),
					security_name: equities[thisClass].name || 'Series ' + thisClass,
					shareholder_name: shareholderName,
					// PharmaJet Promissory Note
					header_words: pre2021 ? 'EXHIBIT B - Form of' : '',
					principal_amount: Math.round(Number(thisTrans.quantity) * 3.02).toLocaleString(),
					header_transaction_date: pre2021 ? 'January 30, 2020' : issueMoment.format('MMMM D, YYYY'),
					securities_date: pre2021 ? 'January 31, 2020' : 'February 5, 2021',
					securities_words: pre2021 ? 'to be executed' : 'that have been executed, filed',
					maturity_words: pre2021 ? 'three years after closing, estimated to be January 31, 2023' : 'on February 4, 2024',
					transaction_date: issueMoment.format('MMMM D, YYYY'),
				};

				if (isPHJ) {
					let tags = userData.clients[client].tags;
					if (tags && tags.includes('SHA'))
						certificateJson.legend_words =
							'TRANSFER OF AND THE RIGHT TO VOTE THE SHARES REPRESENTED BY THIS CERTIFICATE ARE RESTRICTED BY THE TERMS OF A CERTAIN THIRD AMENDED AND RESTATED SHAREHOLDERS AGREEMENT, DATED AS OF JUNE 16, 2017, BY AND AMONG PHARMAJET, INC. AND CERTAIN OF ITS STOCKHOLDERS, AS MAY BE AMENDED FROM TIME TO TIME, A COPY OF WHICH IS ON FILE AT THE OFFICE OF PHARMAJET, INC. BY ACCEPTING ANY INTEREST IN SUCH SHARES THE PERSON ACCEPTING SUCH INTEREST SHALL BE DEEMED TO AGREE TO AND SHALL BECOME BOUND BY ALL THE PROVISIONS OF THAT SHAREHOLDERS AGREEMENT.';
					else
						certificateJson.legend_words =
							"THE TRANSFER OF THESE SECURITIES IS SUBJECT TO THE COMPANY'S RIGHT OF FIRST REFUSAL SET FORTH IN THE COMPANY'S BYLAWS, AS AMENDED FROM TIME TO TIME, AND THE TRANSFER IS PROHIBITED EXCEPT IN ACCORDANCE WITH THE PROVISIONS OF REGULATION S AS PROMULGATED BY THE UNITED STATES SECURITIES AND EXCHANGE COMMISSION UNDER THE ACT, PURSUANT TO REGISTRATION UNDER THE ACT, OR PURSUANT TO AN AVAILABLE EXEMPTION FROM REGISTRATION, AND HEDGING TRANSACTIONS INVOLVING THESE SECURITIES (INCLUDING ANY SWAP OR ANY OTHER AGREEMENT OR ANY TRANSACTION THAT TRANSFERS, IN WHOLE OR IN PART THE ECONOMIC CONSEQUENCE OF OWNERSHIP OF THESE SECURITIES, WHETHER ANY SUCH SWAP, AGREEMENT OR TRANSACTION IS TO BE SETTLED BY DELIVERY OF ALL OR ANY PORTION OF THESE SECURITIES OR ANY OTHER SECURITIES, IN CASH OR OTHERWISE), MAY NOT BE CONDUCTED UNLESS IN COMPLIANCE WITH THE ACT.";
				}
				if (isMAZ) {
					certificateJson.shareholder_name = shareholderName.replace(/\(.*\)$/, '').trim();
					let tags = userData.clients[client].tags;
					if (thisTrans.notes.includes('NP') || thisTrans.notes.includes('NR'))
						//tags && tags.includes('NR'))
						certificateJson.legend_words =
							'THE SHARES REPRESENTED BY THIS CERTIFICATE HAVE NOT BEEN FULLY PAID. INFORMATION AS TO AMOUNTS TO BE PAID AFTER ISSUANCE OF THIS CERTIFICATE IS ON FILE WITH THE CORPORATION’S CFO. UNTIL FULLY PAID, THE SHARES REPRESENTED BY THIS CERTIFICATE ARE SECURED BY A PLEDGE AGREEMENT OF EVEN DATE.';
					else certificateJson.legend_words = '';
				}
				if (isVERM) {
					let tags = userData.clients[client].tags;
					if (tags && tags.includes('Buy/Sell Restricted'))
						certificateJson.legend_words =
							'"THE SHARES EVIDENCED BY THIS CERTIFICATE ARE SUBJECT TO A\r\nSHAREHOLDER AGREEMENT, AS MAY BE AMENDED FROM TIME TO TIME,\r\n(A COPY OF WHICH MAY BE OBTAINED UPON WRITTEN REQUEST FROM\r\nTHE COMPANY), AND BY ACCEPTING ANY INTEREST IN SUCH SHARES THE\r\nPERSON ACCEPTING SUCH INTEREST SHALL BE DEEMED TO AGREE TO AND\r\nSHALL BECOME BOUND BY ALL THE PROVISIONS OF THAT SHAREHOLDER AGREEMENT, INCLUDING CERTAIN RESTRICTIONS ON TRANSFER AND\r\nOWNERSHIP AS PROVIDED IN THE SHAREHOLDER AGREEMENT."';
					else certificateJson.legend_words = '';
				}

				let certificateTemplateName = clientData.templates.find((t) => t.includes('_' + thisClass + '_Stock_Certificate'));
				if (!certificateTemplateName) {
					console.error('No stock certificate template found for class: ' + thisClass);
					hideLoading();
					historyBack();
					return;
				}
				let certificateFileUrl = await getContentUrl(certificateTemplateName);

				let request = new XMLHttpRequest();
				request.open('GET', certificateFileUrl, true);
				request.responseType = 'blob';
				request.onload = function () {
					loadStockCertificateTemplate(request.response);
				};
				request.send();

				function loadStockCertificateTemplate(file) {
					let rABS = typeof FileReader !== 'undefined' && FileReader.prototype && FileReader.prototype.readAsBinaryString;
					const reader = new FileReader();
					if (rABS) reader.readAsBinaryString(file);
					else reader.readAsArrayBuffer(file);

					reader.onload = (e) => {
						if (e.target.result.length > 1e8) {
							alert('File too large for timely process.');
							return;
						}

						let results = e.target.result;
						let wb, arr;
						let readtype = { type: rABS ? 'binary' : 'base64' };

						if (!rABS) results = btoa(arr);

						function processCertificate() {
							try {
								let PizZip = require('pizzip');
								let zip = new PizZip(results);
								let Docxtemplater = require('docxtemplater');
								let doc = new Docxtemplater(zip);
								try {
									// render the document ie replace the variables
									doc.render(certificateJson);
									let buf = doc.getZip().generate({ type: 'base64' });
									className = equities[thisClass].name || thisClass;
									let fileName = className + (thisTrans.notes ? ' - ' + thisTrans.notes : '');
									if (thisTrans.notes.includes(className)) fileName = thisTrans.notes;
									if (!fileName.includes(' ' + thisClass) && !fileName.includes(thisClass + ' ')) fileName = thisClass + ' ' + fileName;
									if (equities[thisClass].hasOwnProperty('interestRate')) fileName = className;
									if (isUnifiedCert)
										fileName =
											className + ' (' + Number(userData.clients[client].shares[thisClass]?.owned || '0').toLocaleString() + ' shares)';

									// helper function: generate a new file from base64 String
									const dataURLtoFile = (dataurl, fileName) => {
										const arr = dataurl.split(',');
										const mime = arr[0].match(/:(.*?);/)[1];
										const bstr = atob(arr[1]);
										let n = bstr.length;
										const u8arr = new Uint8Array(n);
										while (n) {
											u8arr[n - 1] = bstr.charCodeAt(n - 1);
											n -= 1; // to make eslint happy
										}
										return new File([u8arr], fileName, { type: mime });
									};

									// generate file from base64 string
									const fileContent = dataURLtoFile(
										'data:application/vnd.openxmlformats-officedocument.wordprocessingml.document;base64,' + buf
									);
									let fileToUploadAws = isUnifiedCert
										? `stock_certificate_${thisClass}.pdf`
										: `${cleanStampedName(fileName).replace(/^\d+_/, '')}.pdf`;

									// For unified certs, DO NOT use thisTrans.receiver.
									// That reflects the transaction role (sender/receiver), not the current owner.
									// Unified certs represent the CURRENT HOLDER, so always store under uid (certHolder).
									// This prevents files being written to the wrong shareholder folder (critical for S3 + viewer).
									axios
										.get(authUploadUrl, {
											params: {
												filename: client + '/' + certHolder + '/' + fileToUploadAws,
												client: client,
												userid: authedUser,
												cookie: cookie,
											},
										})
										.then(function (res1) {
											console.log('***Speed Test - AFTER axios.get() : ' + (Date.now() - refreshTime) + ' ms');
											let certWaitState = document.getElementById('certificateWaitState');
											if (certWaitState) M.Modal.getInstance(certWaitState).open();
											let formData = secureFormData(res1);
											formData.append('file', fileContent);
											axios
												.post(res1.data.url, formData, axconf)
												.then(function (res2) {
													axios
														.get('https://api.nthround.io/portal/auth/get-presigned-url', {
															params: {
																client: client,
																userid: authedUser,
																cookie: cookie,
																shareholder: certHolder,
																doc_name: fileToUploadAws,
															},
														})
														.then(function (response) {
															// console.log(response);
															console.log(response.data);
															let Api2Pdf = require('api2pdf');
															let a2pClient = new Api2Pdf('6e053f8e-75b7-4271-a316-1981d7640681');
															a2pClient.libreOfficeAnyToPdf(response.data).then(function (result) {
																let docId = Date.now().toString();
																docName = fileName.replace('HASH', '#').replace('.docx', '').trim();

																// DOWNLOAD PDF FROM URL
																let request = new XMLHttpRequest();
																request.open('GET', result.FileUrl, true);
																request.responseType = 'blob';
																request.onload = function () {
																	loadPdfStockCertificate(request.response, docId);
																};
																request.send();

																function loadPdfStockCertificate(file, docId) {
																	// GENERATED CERTIFICATES / NOTES:
																	// These are current-state/generated artifacts, not saved shareholder documents.
																	// Render the freshly generated PDF through a temporary S3 upload + presigned URL,
																	// but do not push a document record to Dynamo.
																	fileToUploadAws = fileToUploadAws.replace('.docx', '.pdf');
																	axios
																		.get(authUploadUrl, {
																			params: {
																				filename: client + '/' + certHolder + '/' + fileToUploadAws,
																				client: client,
																				userid: authedUser,
																				cookie: cookie,
																			},
																		})
																		.then(function (res1) {
																			let formData = secureFormData(res1);
																			formData.append('file', file);
																			return axios.post(res1.data.url, formData, axconf);
																		})
																		.then(function () {
																			return axios.get('https://api.nthround.io/portal/auth/get-presigned-url', {
																				params: {
																					client: client,
																					userid: authedUser,
																					cookie: cookie,
																					shareholder: certHolder,
																					doc_name: fileToUploadAws,
																				},
																			});
																		})
																		.then(function (response) {
																			renderDocument(response.data, docName, docId, 'New Certificate');
																		})
																		.catch(function (error) {
																			console.error(error);
																			hideLoading();
																			showToast(
																				'error-toast',
																				'Sorry, we could not generate this certificate. Please try again.'
																			);
																		});
																}
															});
														})
														.catch(function (error) {
															console.error(error);
														});
												})
												.catch(function (err2) {
													console.error(err2);
												});
										})
										.catch(function (err1) {
											console.error(err1);
										});
								} catch (error) {
									let e = {
										message: error.message,
										name: error.name,
										stack: error.stack,
										properties: error.properties,
									};
									console.log(JSON.stringify({ error: e }));
									throw error;
								}
							} catch (err) {
								console.error('Failed to process stock certificate template:', err);
								hideLoading();
								historyBack();
								return;
							}
						}
						if (e.target.result.length > 1e8) {
							opts.errors.large(e.target.result.length, function (e) {
								if (e) processCertificate();
							});
						} else processCertificate();
					};
				}
			}
		}

		function getDocument() {
			let passedUserid = getAllUrlParams(window.location.href, true).userid;
			if (passedUserid) {
				uid = passedUserid;
				let thisUser = capTableData.find((u) => u.userid == uid);
				if (thisUser) documents = thisUser.clients[client].documents;
			}

			if (!documents || documents.length == 0) {
				historyBack();
				return;
			}

			let theDocument = documents.find((x) => x.id == docId);
			if (!theDocument) return;
			embedURL = theDocument.url;
			// Use friendly name for unified certs in the viewer title
			docName = getShareholderDocName(theDocument); //theDocument.name;

			if (uid == 'consolidated.view') uid = theDocument.holder;

			// Initialize Materialize JS Components after DOM has been built
			let modalOptions = {
				onOpenStart: initModal,
			};
			let modalElems = document.querySelectorAll('.modal');
			if (typeof M !== 'undefined' && M != null) {
				M.Modal.init(modalElems, modalOptions);
				ensureTwoFactorModalNonDismissible();
			}

			if (theDocument.require2Factor) {
				// as of 1/3/2021, use phone of authed user if available
				// if (!phoneFor2FA) phoneFor2FA = capTableData.find(x => x.userid == authedUser).phone;

				// USERS MUST RELY ON USER DATA, NOT CAP TABLE DATA
				// let authedPhone = capTableData.find(x => x.userid == authedUser).phone;
				// if (authedPhone) phoneFor2FA = authedPhone;
				const confirmationEnterCode = document.getElementById('confirmationEnterCode');
				const confirmationAccessCode = document.getElementById('confirmationAccessCode');
				let code = (Math.floor(Math.random() * 1000000) + 1000000).toString().substring(1);
				let phoneFor2FA = authedUser == userData.userid ? userData.phone : null;
				if (phoneFor2FA && phoneFor2FA.replace(/\D+/g, '').match(/^\d{10}$/)) {
					let url = 'https://hooks.zapier.com/hooks/catch/4626966/o8j8uqo/';
					axios
						.get(url, {
							params: {
								phone: phoneFor2FA.replace(/\D+/g, ''),
								msg: 'Your Nth Round verification code is: ' + code + '. If someone asks, NEVER share it.',
							},
							withCredentials: false,
						})
						.then(function (res) {
							openVerifyModal(code);
						})
						.catch(function (err) {
							console.error(err);
						});
				} else {
					openVerifyModal('6 digits');
				}

				confirmationAccessCode.addEventListener('input', function (e) {
					if (this.value.length == 6) confirmationEnterCode.disabled = false;
					else confirmationEnterCode.disabled = true;
				});

				function openVerifyModal(code) {
					M.Modal.getInstance(document.getElementById('confirmationTwoFactorModal')).open();
					//document.getElementById('confirmationTwoFactorModal').style.display = "block";
					document.getElementById('confirmationEnterCode').addEventListener('click', function (e) {
						e.preventDefault();
						M.Modal.getInstance(document.getElementById('confirmationTwoFactorModal')).close();
						if (confirmationAccessCode.value != code) {
							showToast('error-toast', 'Sorry, that code is incorrect.');
							setTimeout(() => historyBack(), 2500);
						} else getPresignedUrl();
					});
				}
			} else {
				getPresignedUrl();
			}
		}

		function getPresignedUrl() {
			if (embedURL.startsWith('http')) {
				renderDocument(embedURL, docName, docId, 'Personal Doc');
			} else {
				axios.defaults.withCredentials = cookie ? false : true;
				axios
					.get('https://api.nthround.io/portal/auth/get-presigned-url', {
						params: {
							client: client,
							userid: authedUser,
							cookie: cookie,
							shareholder: uid.replace(/\+/g, 'PLUS'),
							doc_name: embedURL,
						},
					})
					.then(function (response) {
						renderDocument(response.data, docName, docId, 'Personal Doc');
					})
					.catch(function (error) {
						console.error(error);
					});
			}
		}
	}

	if (pn.includes('/campaign/view')) {
		document.getElementById('footer').classList.add('hidden');
		setBackButton('backtoCampaign');

		let docId;
		let docName;
		let embedURL;
		let watermark;
		let params = getAllUrlParams(window.location.href, true);
		if (params.document) docId = params.document;
		else window.location.replace('/campaign/');
		let docs = clientData.new_investment_materials;
		// CONFIDENTIAL_DOC = docs[0].confidential_doc;
		for (let i = 0; i < docs.length; i++) {
			if (docs[i]['id'] == docId) {
				embedURL = docs[i].file;
				docName = docs[i].title;
				watermark = docs[i].watermark;

				if (watermark && (docName.includes('.pdf') || embedURL.includes('.pdf'))) {
					hideLoading();
					let modalElems = document.querySelectorAll('.modal');
					M.Modal.init(modalElems, { onOpenStart: initModal });
					ensureTwoFactorModalNonDismissible();
					setTimeout(() => {
						modifyPdf(embedURL, docName, docId, 'Fundraising Doc');
					}, 100); //invokes watermarking
				} else renderDocument(embedURL, docName, docId, 'Fundraising Doc');
			}
		}
	}

	if (pn.includes('/admin/users/edit')) {
		let shareholder;
		checkAdmin();
		//trackUser();
		//if (Dropzone.instances.length > 0) Dropzone.instances.forEach(dz => dz.destroy());

		// If there is no email param in the url redirect to the shareholders list
		if (getAllUrlParams(window.location.href, true).email) {
			shareholder = getAllUrlParams(window.location.href, true).email;
		} else {
			window.location.replace('/admin/users/');
		}

		document.addEventListener('ShareholderComplete', function (e) {
			sessionStorage.setItem('shareholder.Data', JSON.stringify(shareholderData));
			console.log('Shareholder Complete: refreshed=' + e.detail.refreshed);
			if (!Object.keys(shareholderData.clients[client]).length) {
				alert('This shareholder has an incomplete data record, please contact support.');
			} else {
				document.dispatchEvent(new CustomEvent('renderShareholderView', { detail: { shareholder: shareholder, refreshed: e.detail.refreshed } }));
				// renderShareholderView(shareholder, e.detail.refreshed);
			}
			hideLoading();
		});

		let referrer = document.referrer;
		//http://localhost:8080/documents/view/?document=1599140535103
		getShareholderData(shareholder, !(referrer.includes('/documents/view/?document=') || performance.navigation.type == 2));
	}

	if (pn === '/admin/users' || pn === '/admin/users/' || pn === '/supervisor' || pn === '/supervisor/') {
		// MOVED TO nav.js -> getNavData(client)
		// Revert from Observer or Preview

		// checkAdmin();
		if (!getAuthType().includes('admin') && !isSupv && !isExec) window.location.replace('/');
		//refreshSessionStorage();
		//trackUser();

		// console.log(res);
		// TO DO: create global dictionary of sector labels and vocabulary
		//document.getElementById('addShareholderLabel').innerHTML = UFL ? " Add Partner" : " Add Shareholder";
		// Remove the loading spinner

		if (!clientData) clientData = JSON.parse(sessionStorage.getItem(client + '.Data'));
		if (clientData && clientData.plan && clientData.plan == 'pro') {
			document.getElementById('shareholdersBasic').style.display = 'none';
			document.getElementById('shareholdersPro').style.display = 'block';

			let userlistedShareholders = clientData.userlist;

			if (true) {
				//userlistedShareholders && userlistedShareholders.length) {
				// Hide the empty state
				document.getElementById('userlistEmpty').style.display = 'none';
				document.getElementById('userlistFull').style.display = 'block';
				// document.getElementById('addShareholderTrigger').style.display = 'block';
			} else {
				document.getElementById('userlistEmpty').style.display = 'block';
				document.getElementById('userlistFull').style.display = 'none';
				// document.getElementById('addShareholderTrigger').style.display = 'none';
			}
			// document.getElementById('addShareholderTrigger').style.display = 'block';
		} else {
			document.getElementById('shareholdersBasic').style.display = 'block';
			document.getElementById('shareholdersPro').style.display = 'none';
			// document.getElementById('addShareholderTrigger').style.display = 'none;';
		}

		if (!EXTERNAL_DATA) hideLoading();
		if (!capTableData) capTableData = NthUtil.sessionStore.readCapTable(client);
		if (capTableData && userData)
			// && capTableData.length > 0
			document.dispatchEvent(new CustomEvent('RenderCapTable', { detail: { skipListeners: false } })); // renderCapTable(false);	//capTableData);

		// MANUAL TOOLS IN SCRIPTS.JS
		// addVesting();
		// removeAllowlistedPAVesting();
		// addInvitedAt();
		// moveOperAgmt();
		// setupAuthenticator();
		// updateApprovedFromPosted();
	}

	if (pn === '/documents/' || pn === '/documents') {
		let certs = JSON.parse(sessionStorage.getItem('stockCerts')) || [];
		let documents = userData.clients?.[client]?.documents || [];
		let filteredDocuments = documents;
		let documentsMarkup = '';
		const DOCS_PER_PAGE = 25;
		let currentDocPage = 1;
		let allDocuments = [];
		let sortState = { col: 'date', dir: 'desc' };
		let activeDocYear = 'ALL';

		let emptyState = '';
		let emptyStateWords = 'Your K-1s, signature pages, and other personal items will appear here.';
		if (!UFL && !clientData.use_real_estate_language) emptyStateWords = 'Your NDAs, certificates, and other personal items will appear here.';
		if (userData.clients?.[client]?.alias?.length > 0)
			emptyStateWords =
				'No documents here. To look elsewhere, click&ensp;<a href="#" class="text-sm link-accent" onclick="switchAccount()">Switch Account</a>.';
		emptyState = `<div class="bg-grey-lightest rounded border-grey grey-text center-align p-2">${emptyStateWords}</div>`;

		let tagsList = [];
		let typesList = [];
		let descCount = 0;
		documents.forEach(function (d) {
			if (d.tag && d.tag.length) {
				let tags = d.tag.split(',').map((t) => t.trim());
				tags.forEach((t) => {
					if (!tagsList.includes(d.tag)) tagsList.push(d.tag);
				});
			}
			if (d.type && d.type.length && !typesList.includes(d.type)) typesList.push(d.type);
			if (d.description && d.description.length > 3) descCount++;
		});

		if (certs.length > 0) descCount++;

		if (certs.length > 0 || !USE_CERTIFICATES) {
			if (filteredDocuments) filteredDocuments = filteredDocuments.concat(certs);
			else filteredDocuments = certs;
			filteredDocuments = stripObsoleteCertificates(filteredDocuments);
			if (isSheetz) filteredDocuments = addUnifiedCertificates(filteredDocuments);
			allDocuments = filteredDocuments;
			showDocuments();
		} else {
			async function getStockCertificates() {
				filteredDocuments = await addStockCertificates(filteredDocuments);
				filteredDocuments = stripObsoleteCertificates(filteredDocuments);
				if (isSheetz) filteredDocuments = addUnifiedCertificates(filteredDocuments);
				allDocuments = filteredDocuments;
				showDocuments();
			}
			getStockCertificates();
		}

		function sortableHeader(col, label, visible, state, extraClass = '', prefix = '') {
			const hidden = visible ? '' : ' hidden';
			const arrow = state.col === col ? (state.dir === 'asc' ? ' ▲' : ' ▼') : '';
			return `<th class="${extraClass}py-2 sortable-header${hidden}" data-sort-col="${col}" height="60" style="cursor:pointer">${prefix}${label}${arrow}</th>`;
		}

		function showDocuments() {
			if (!filteredDocuments) {
				//console.log('This shareholder has an incomplete data record, please contact support.')
				document.getElementById('documentList').innerHTML = emptyState;
				document.getElementById('documentPagination').innerHTML = '';
			} else {
				// TODO: Display document list
				const sortedDocs = sortDocuments(filteredDocuments, sortState.col, sortState.dir);
				const docs = getPageSlice(sortedDocs, currentDocPage, DOCS_PER_PAGE);

				if (docs && docs.length) {
					// Not doing Tooltip Views yet, or ever?
					if (false && isAdmin) {
						docs.forEach(function (d, index) {
							let tooltip = 'Recent Views:';
							if (d.views) {
								let views = d.views.split(',');
								views.forEach(function (v, index) {
									tooltip +=
										'\r\n' +
										moment(Number(v) * 1000)
											.format('lll')
											.replace(/,/g, '');
								});
								docs[index].tooltip = tooltip;
							}
						});
					}
					documentsMarkup = `
						<div class="table-wrap hide-on-small-only p-05">
							<div class="sm-hz-scroll-no-margin">
								<table id="documentsSortTable" class="highlight table-content">
									<thead>
										<tr class="border-b-1">
											${sortableHeader('tag', 'Class', tagsList.length > 1, sortState)}
											${sortableHeader('type', 'Type', typesList.length > 0, sortState)}
											${sortableHeader('name', 'Name', true, sortState, 'px-2 ')}
											<th class="py-2${descCount > 0 ? '' : ' hidden'}" height="60">Description</th>
											${sortableHeader('date', 'Date', true, sortState, '', '&emsp;')}
										</tr>
									</thead>
									<tbody>
										${docs
											.map(
												(doc) => `
											<tr class="border-b-1" height="20px">
												<td class="${tagsList.length > 1 ? '' : 'hidden'}">
													<div class="pill pill--gray">${doc.tag ? doc.tag.split(',').map((t) => t.trim()) : ''}</div>
												</td>
												<td class="${typesList.length > 0 ? '' : 'hidden'}">
													<div class="text-xs text-uppercase">${doc.type || ''}</div>
												</td>
												<td class="text-black px-2 p-05">
													<p class="mt-0 mb-0 document-link">
														<a href="/documents/view/?document=${doc.id}">${getShareholderDocName(doc)}</a>
													</p>
												</td>
												<td class="text-secondary p-05${descCount > 0 ? '' : ' hidden'}">
													${doc.description || ''}
												</td>
												<td class="text-secondary p-05" data-sort='${Date.parse(doc.date)}'>
													${doc.date}
												</td>
											</tr>
										`
											)
											.join('')}
									</tbody>
								</table>
							</div>
						</div>
						${docs
							.map(
								(doc) => `
							<div class="row border-b-1 mb-0 hide-on-med-and-up" ${isAdmin ? 'title="' + (doc.tooltip ? doc.tooltip : 'No views yet') + '"' : ''}>
								<div class="col s12 m4">
									<p class="text-black mb-0 mt-0 document-link">
										<a href="/documents/view/?document=${doc.id}">
											${getShareholderDocName(doc)}${doc.tag ? '&ensp;<span class="pill pill--gray">' + doc.tag.split(',').map((t) => t.trim()) + '</span>' : ''}
										</a>
									</p>
								</div>
								<div class="col s12 m6">
									<p class="text-secondary mb-0 mt-0">${doc.description || ''}</p>
								</div>
								<div class="col s12 m2 mt-0">
									<p class="text-secondary mt-0">${doc.date}</p>
								</div>
							</div>
						`
							)
							.join('')}
					`;
				} else if (['impactcapitalfunds'].includes(client)) {
					document.getElementById('verificationForm').classList.remove('hidden');

					let varificationDropzone = new Dropzone(document.getElementById('varificationDropzone'), {
						addRemoveLinks: true,
						autoProcessQueue: false,
						uploadMultiple: false,
						parallelUploads: 1,
						maxFiles: 1,
						maxFilesize: 50, // MB
						paramName: 'file',
						acceptedFiles: mixTypes,
						clickable: true,
						url: '/',
						init: function () {
							let varificationDropzone = this;
							let fileToUploadAws;

							function addDocumentToDatabase() {
								let newDoc = {
									name: 'Verification Document',
									url: fileToUploadAws,
									date: moment().format('MMM D, YYYY'),
									id: Date.now().toString(),
								};

								newDoc.tag = 'ALL';
								newDoc.type = 'Agreement';
								newDoc.description = 'Confirming status as accredited investor';
								newDoc.require2Factor = false;

								// Push the new document object into the existing documents array
								if (newDoc.url == undefined || newDoc.url.length < 10) {
									console.log('Dropzone url is null');
									return;
								}
								let holderData = userData.clients[client];
								if (!holderData.documents) {
									holderData.documents = [];
								}
								holderData.documents.push(newDoc);

								// update all session data
								userData.clients[client] = holderData;
								sessionStorage.setItem('user.Data', JSON.stringify(userData));
								// renderShareholderView(shareholder, true);

								//Post updated data to the DB
								axios
									.post(
										postURL,
										JSON.stringify({
											eventName: 'index.verification_document_added',
											investor: userData.userid,
											userid: authedUser,
											client: client,
											cookie: cookie,
											clients: {
												[client]: {
													documents: holderData.documents,
												},
											},
										})
									)
									.then(function (res) {
										// needed for doc
										refreshCapTable();

										varificationDropzone.removeAllFiles(true);
										if (window.analytics)
											window.analytics.track('Verification Document Added', {
												userEmail: authedUser,
												workspace: client,
												documentTitle: newDoc.name,
												documentFileType: newDoc.url.split('.').pop(),
											});

										filteredDocuments = holderData.documents;
										if (!typesList.includes('Agreement')) typesList.push('Agreement');
										document.getElementById('verificationForm').classList.add('hidden');
										M.Modal.getInstance(document.getElementById('processWaitState')).open();
										showDocuments();
									})
									.catch(function (err) {
										console.log(err);
									});
							}

							this.on('addedfile', function () {
								document.getElementById('verificationMessage').innerHTML = '<b>One moment please...</b>';
								let file = varificationDropzone.files[0];
								if (mixTypes.includes(file.type)) {
									fileToUploadAws = cleanStampedName(file.name);
									axios
										.get(authUploadUrl, {
											params: {
												filename: client + '/' + userData.userid.replace(/\+/g, 'PLUS') + '/' + fileToUploadAws,
												client: client,
												userid: authedUser,
												cookie: cookie,
											},
										})
										.then(function (res1) {
											//console.log(res1);
											let formData = secureFormData(res1);
											formData.append('file', file);
											axios
												.post(res1.data.url, formData, axconf)
												.then(function (res2) {
													addDocumentToDatabase();
												})
												.catch(function (err2) {
													console.error(err2);
												});
										})
										.catch(function (err1) {
											console.error(err1);
										});
								}
							});

							this.on('removedfile', function () {
								fileToUploadAws = '';
							});
						},
						reset: function () {
							console.log('resetFiles');
							this.removeAllFiles(true);
						},
					});
				} else {
					documentsMarkup = emptyState;
				}
				document.getElementById('documentList').innerHTML = documentsMarkup;
				document.getElementById('documentPagination').innerHTML = renderPaginationControls(
					currentDocPage,
					calcTotalPages(filteredDocuments.length, DOCS_PER_PAGE),
					filteredDocuments.length,
					DOCS_PER_PAGE
				);
			}
			hideLoading();
		}

		document.getElementById('documentPagination').addEventListener('click', function (e) {
			const btn = e.target.closest('[data-page]');
			if (!btn || btn.disabled) return;
			const val = btn.dataset.page;
			const totalPages = calcTotalPages(filteredDocuments.length, DOCS_PER_PAGE);
			if (val === 'prev') currentDocPage = Math.max(1, currentDocPage - 1);
			else if (val === 'next') currentDocPage = Math.min(totalPages, currentDocPage + 1);
			else currentDocPage = Number(val);
			showDocuments();
		});

		document.getElementById('documentList').addEventListener('click', function (e) {
			const th = e.target.closest('[data-sort-col]');
			if (!th) return;
			const col = th.dataset.sortCol;
			if (sortState.col === col) {
				sortState.dir = sortState.dir === 'asc' ? 'desc' : 'asc';
			} else {
				sortState.col = col;
				sortState.dir = col === 'date' ? 'desc' : 'asc';
			}
			currentDocPage = 1;
			showDocuments();
		});

		documents = filteredDocuments;

		if (documents && documents.length > 4) {
			let initialTags = [];
			const orderedOptions = {};
			let savedFilterState = sessionStorage.getItem('filterDocuments');
			if (savedFilterState) {
				let savedTags = savedFilterState.split('+')[0].split(',');
				savedTags.forEach((t) => {
					if (t.length) initialTags.push({ tag: t });
				});
			}

			let allYears = [];
			documents.forEach(function (d) {
				let year = new Date(Date.parse(d.date)).getFullYear();
				if (!allYears.includes(year)) allYears.push(year);
				let tags = d.tag ? d.tag.split(',').map((t) => t.trim()) : [];
				if (tags && tagsList)
					tags.forEach((t) => {
						if (!tagsList.includes(t)) tagsList.push(t);
					});
				if (d.type && typesList && !typesList.includes(d.type)) typesList.push(d.type);
			});

			if (tagsList.length > 1)
				tagsList.sort().forEach((c) => {
					orderedOptions[c] = null;
				});
			if (typesList.length > 0)
				typesList.sort().forEach((c) => {
					orderedOptions[c] = null;
				});

			// Initialize the filter (chip) component
			let chipOptions = {
				data: initialTags,
				autocompleteOptions: {
					data: orderedOptions,
					// sortFunction: reverseSort,
					//limit: 8,		//dataLimit,
					minLength: 0,
				},
				placeholder: 'Search / Filter',
				onChipAdd: filterYourDocs,
				onChipDelete: filterYourDocs,
			};

			function reverseSort(a, b, inputString) {
				return Number(b) - Number(a);
			}

			let elems = document.querySelectorAll('.documentChips');
			let chipInstance = M.Chips.init(elems, chipOptions);

			const docYearSelect = document.getElementById('filterDateRangeDocuments');
			if (docYearSelect && allYears.length > 0) {
				const sortedYears = allYears.slice().sort((a, b) => b - a);
				docYearSelect.innerHTML = '<option value="ALL">All time</option>' + sortedYears.map((y) => `<option value="${y}">${y}</option>`).join('');
				M.FormSelect.init(docYearSelect, { dropdownOptions: { coverTrigger: false } });
				docYearSelect.addEventListener('change', function () {
					activeDocYear = this.value;
					filterYourDocs();
				});
				document.getElementById('filterDateRangeDocumentsWrap')?.classList.remove('hidden');
			}
			let dropdownElem = document.getElementById('viewSelect');
			let dropdownOptions = {
				coverTrigger: true,
				constrainWidth: false,
			};

			if (savedFilterState) {
				chipInstance[0].$input[0].value = savedFilterState.split('+')[1];
				filterYourDocs();
			}

			let documentSearchFilter = document.getElementById('documentSearchFilter');
			// This is a PITA!
			// if (narrow) {
			// 	documentSearchFilter.classList.remove("mr-12");
			// 	documentSearchFilter.classList.add("mr-4");
			// } else {
			// 	documentSearchFilter.classList.remove("mr-4");
			// 	documentSearchFilter.classList.add("mr-12");
			// }
			let _filterDebounceTimer;
			documentSearchFilter.addEventListener('keyup', function () {
				clearTimeout(_filterDebounceTimer);
				_filterDebounceTimer = setTimeout(filterYourDocs, 200);
			});

			function filterYourDocs() {
				let tagArray = chipInstance[0].chipsData.map((c) => c.tag.toLowerCase());
				let searchString = chipInstance[0].$input[0].value.toLowerCase();
				if (tagArray.includes(searchString)) searchString = '';
				sessionStorage.setItem('filterDocuments', tagArray + '+' + searchString);
				filteredDocuments = filterDocsByYear(filterDocuments(allDocuments, tagArray, searchString), activeDocYear);
				currentDocPage = 1;
				showDocuments();
			}
		}
	}

	if (pn.includes('/investor-agreement')) {
		setBackButton('backtoCampaign');
	}

	if (pn.includes('/admin/dashboard')) {
		checkAdmin();
		//refreshSessionStorage();
		//trackUser();

		// Initialize the Google Graphs library
		google.charts.load('current', { packages: ['corechart'] });

		// Get the data we need for the view
		updateAdminDashboard();

		// Handle the case where this client doesn't have a trading platform in place.
		if (!clientData.trading_platform) {
			document.getElementById('tradingData').style.display = 'none';
			document.getElementById('tradingHeading').style.display = 'none';
		} else if (Object.keys(equityData).length) {
			// TO DO: Make this all work again...
			//google.setOnLoadCallback(getGraph(userData));
			// } else if (contractData.length) {
			// 	// If there are available contracts display the graph using the first contract returned
			// 	google.setOnLoadCallback(getGraph(userData, contractData[1]));
		} else {
			let emptyGraph = pn.includes('/admin') ? "url('./../../resources/assets/empty-graph.png')" : "url('./resources/assets/empty-graph.png')";
			chart_div.style.backgroundImage = emptyGraph;
			chart_div.style.backgroundSize = 'cover';
			chart_div.innerHTML = "<span class='font-medium'>Not enough data to display trades</span>";
			chart_div.classList.add('center-box');
		}
		// hideLoading();
		hideBanner();
	}

	if (pn === '/') {
		//refreshSessionStorage();
		//trackUser();
		// Initialize the shareholder dashboard view
		if (authenticated) renderInvestorPortal();
		else redirectToHome();
		hideBanner();

		drawPortfolioChart();
		drawStackedAreaChart(netValue);

		let entryModalHtml = clientData?.entry_modal;
		if (entryModalHtml) {
			setTimeout(() => {
				let clientNode = userData.clients[client];
				let firstEntryTime = clientNode.first_entry && clientNode.first_entry.time ? Number(clientNode.first_entry.time) : 0;
				if (!addedFirstEntryEventListenerAlready && Date.now() / 1000 - firstEntryTime > 31536000) {
					// ~1 year
					const firstEntryModal = document.getElementById('firstEntryModal');
					firstEntryModal.innerHTML = entryModalHtml;
					const firstEntryDone = document.getElementById('firstEntryDone');
					M.Modal.getInstance(firstEntryModal).open();
					M.Modal.getInstance(firstEntryModal).options.dismissible = false;
					addedFirstEntryEventListenerAlready = true;
					firstEntryDone.addEventListener('submit', function (e) {
						e.preventDefault();
						let optinInfo = document.getElementById('optinInfo');
						let optinTax = document.getElementById('optinTax');

						let firstEntry = {};
						firstEntry.time = Math.floor(Date.now() / 1000).toString();
						if (optinInfo) firstEntry.info = optinInfo.checked;
						if (optinTax) firstEntry.tax = optinTax.checked;

						// update local data and session storage
						userData.clients[client].first_entry = firstEntry;
						sessionStorage.setItem('user.Data', JSON.stringify(userData));

						axios
							.post(
								postURL,
								JSON.stringify({
									eventName: 'index.first_entry_submitted',
									investor: userData.userid,
									userid: authedUser,
									client: client,
									cookie: cookie,
									clients: {
										[client]: {
											first_entry: firstEntry,
										},
									},
								})
							)
							.then(function (res) {
								M.Modal.getInstance(firstEntryModal).close();
								// needed for new user data
								refreshCapTable();
							})
							.catch(function (err) {
								console.error(err);
							});
					});
				}
			}, 1000);
		}

		let priorWidth = window.innerWidth;
		window.addEventListener('resize', function (e) {
			const firstEntryModal = M.Modal.getInstance(document.getElementById('firstEntryModal'));
			if (firstEntryModal && firstEntryModal.isOpen) return;
			if (priorWidth != window.innerWidth) {
				drawChart(); //CONTRACT_SYMBOL);
				drawPortfolioChart();
				drawStackedAreaChart(netValue);
				buildScenarioModelingModal(true);
				priorWidth = window.innerWidth;
			}
		});

		// SCRIPT TO GENERATE BUSH BROTHERS ANNUAL REPORTS
		// To run the script from the beginning, remove any cookie called reportIndex, then set the generate_reports flag to true
		// To start at a specific point, set the reportIndex cookie to the desired cap table (in session storage) starting point
		let GENERATE_REPORTS = clientData.flags && clientData.flags.generate_reports;
		if (GENERATE_REPORTS) {
			let reportIndex = getCookie('reportIndex');
			if (!reportIndex) reportIndex = -1;
			else if (reportIndex < -1) return;
			else reportIndex = Number(reportIndex) + 1;
			document.cookie = 'reportIndex=' + reportIndex + '; path=/';
			let viewer = setTimeout(() => {
				if (reportIndex === '' || reportIndex >= capTableData.length) clearInterval(viewer);
				else {
					let wsModal = document.getElementById('workspaceModal');
					if (wsModal && M.Modal.getInstance(wsModal).isOpen) M.Modal.getInstance(wsModal).close();

					//MARVIN CUSTOM REPORT
					if (isMAR) viewEventsTransReportIconLarge.dispatchEvent(new Event('click'));
					else viewCapAcctIconLarge.dispatchEvent(new Event('click'));
					// viewCapAcctIconLarge.dispatchEvent(new Event('click'));
				}
			}, 4000);
			let nexter = setTimeout(() => {
				if (reportIndex === '' || reportIndex >= capTableData.length) {
					clearInterval(nexter);
					document.cookie = 'reportIndex=-99; path=/'; //deleteCookie ('reportIndex');
				}
				previewShareholder(capTableData[reportIndex].userid, '');
			}, 10000);
		}
	}

	async function modifyPdf(url, docName, docId, docType) {
		// show wait state
		M.Modal.getInstance(document.getElementById('watermarkWaitState')).open();

		let ipKey = '74e90ebd472d39ba695df5026ba9a8daf9013fb0b504ec248f848527';
		let ipAdd = await fetch(`https://api.ipdata.co?api-key=${ipKey}`)
			.then((res) => res.json())
			.then((json) => String(json.ip));
		let timestamp = moment().format('YYYY/MM/D hh:mm');
		let userIdentifier = `${userData.user_name.replace(/_/g, ' ')} (${userData.userid.replace('@', ' [at] ')})`;
		let dynamicFontSize = userIdentifier.length > 50 ? 12 : userIdentifier.length > 30 ? 15 : 20;
		/* Watermarks pdf - lazy load ConvertApi */
		const ConvertApi = await window.loadConvertApi();
		let convertApi = ConvertApi.auth('TdnhROgCb0g38zGX'); //UGFwSpdFqyfhki5l
		let params = convertApi.createParams();
		params.add('File', new URL(url));
		params.add('Text', `CONFIDENTIAL%N%${userIdentifier}%N%${ipAdd}%N%${timestamp}`);
		params.add('TextAlign', 'center');
		params.add('WordWrap', 'true');
		params.add('LineSpacing', '13');
		params.add('FontName', 'Consolas');
		params.add('FontSize', dynamicFontSize);
		params.add('FontColor', nthColor[5]);
		params.add('StrokeColor', stdColor[0]);
		params.add('TextRenderingMode', 'filltext');
		params.add('Rotate', '45');
		params.add('Opacity', '50');
		params.add('Height', '125');
		params.add('Width', '600');
		params.add('PdfTitle', docName);
		let result = await convertApi.convert('pdf', 'watermark-textbox', params);
		let pdfApi = result.files[0].Url;
		console.log(pdfApi);
		/* Prepare for cloudinary */
		let uploadPreset = 'jv8dz1vj';
		let formData = new FormData();
		formData.append('upload_preset', uploadPreset);

		// Download the PDF from ConvertAPI as a blob before uploading to Cloudinary
		try {
			const response = await fetch(pdfApi);
			const pdfBlob = await response.blob();
			formData.append('file', pdfBlob, 'watermarked.pdf');

			/* Save to cloudinary */
			let watermarkedURL = await axios
				.post(cloudinaryURL, formData, axconf)
				.then(function (response) {
					let newUrl = response.data.secure_url;
					console.log('CHECKS NEW WATERMARKED URL:' + newUrl);

					// Add a delay to allow Cloudinary to process the file
					setTimeout(() => {
						renderDocument(url, docName, docId, docType, newUrl);
					}, 2000); // 2 second delay
				})
				.catch(function (err) {
					console.error(err);
				});
		} catch (error) {
			console.error('Error downloading watermarked PDF:', error);
		}
	}

	function renderDocument(embedURL, docName, docId, docType, watermarkUrl = null) {
		console.log('renderDocument', watermarkUrl ? watermarkUrl : embedURL, docName, docId, docType, Date.now() - refreshTime + ' ms');
		console.log(watermarkUrl);
		// separately preload document <-- NOT NEEDED ANY MORE
		// document.getElementById('hiddenDocument').src = embedURL;

		const footer = document.getElementById('footer');
		const copyLink = document.getElementById('copyLink');
		const oneMoment = document.getElementById('oneMoment');
		const downloadLink = document.getElementById('download');
		const embedElem = document.getElementById('embedElement');
		const imageElem = document.getElementById('imageElement');
		const progressBar = document.getElementById('progressBar');
		const printLink = document.getElementById('print');

		if (clientData.flags && clientData.flags.show_copy_link && copyLink) copyLink.hidden = false;

		if (clientData.flags && clientData.flags.hide_download_link) {
			if (downloadLink) downloadLink.hidden = true;
			if (printLink) printLink.hidden = true;
		}

		// hard guard: Safari crash-proof + admin alert (once per session)
		if (typeof embedURL !== 'string' || !embedURL) {
			console.error('renderDocument: bad embedURL', { embedURL, docName, docId, docType });

			// Send admin alert ONCE per session
			const alertKey = 'alerted_bad_embedURL_renderDocument';
			if (!sessionStorage.getItem(alertKey)) {
				sessionStorage.setItem(alertKey, '1');

				sendAlert(
					'renderDocument: bad embedURL (' + client + ')',
					`
					<b>Bad embedURL detected</b><br><br>
					docId: ${docId}<br>
					docName: ${docName}<br>
					docType: ${docType}<br>
					user: ${authedUser}<br>
					url: ${window.location.href}<br>
					embedURL: ${String(embedURL)}
					`
				);
			}

			// Fail gracefully
			alert('An error occurred while attempting to render this document. Nth Round engineers have been notified.');
			window.location.replace('/reports/');
			return;
		}

		const extension = embedURL.split('.').pop().split('?')[0].toLowerCase();
		const docFiles = ['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx'];
		const itsDoc = docFiles.includes(extension);
		const itsPDF = extension === 'pdf';
		const itsTxt = extension === 'txt';
		const imageFiles = ['bmp', 'gif', 'jpg', 'jpeg', 'png', 'tif', 'tiff'];
		const itsImage = imageFiles.includes(extension);
		let carryOnAlreadyRan = false;
		let iPhoneDetected = false;

		if (itsDoc || itsPDF || itsTxt || docType == 'New Certificate') {
			hideLinks(false);
			imageElem.classList.add('hidden');
			if (itsPDF) renderPDF();
			else renderDoc();
		} else if (itsImage) {
			hideLinks(true);
			imageElem.src = embedURL;
			embedElem.classList.add('hidden');
			footer.classList.remove('hidden');
			imageElem.classList.remove('hidden');
			carryOnWithLoadedDoc(); // <- DO WE WANT THIS ???  (2/22/2024)
		} else {
			hideLinks(true);
			const sameoriginSites = clientData.sameorigin_sites || []; //['lanternpharma','salariuspharma','businesswire','prnewswire','radformation','atreca','puracath'];
			if (isGPG || sameoriginSites.some((b) => embedURL.includes(b))) {
				window.open(embedURL);
				window.location.replace('/reports/');
			} else {
				embedElem.src = embedURL;
				footer.classList.remove('hidden');
				imageElem.classList.add('hidden');
				embedElem.classList.remove('hidden');
				embedElem.setAttribute('scrolling', 'auto');
				carryOnWithLoadedDoc();
			}
		}

		function hideLinks(hide) {
			if (hide) {
				if (copyLink) copyLink.classList.add('hidden');
				if (oneMoment) oneMoment.classList.add('hidden');
				if (progressBar) progressBar.classList.add('hidden');
				if (downloadLink) downloadLink.classList.add('hidden');
			} else {
				if (copyLink) copyLink.classList.remove('hidden');
				if (oneMoment) oneMoment.classList.remove('hidden');
				if (progressBar) progressBar.classList.remove('hidden');
				if (downloadLink) downloadLink.classList.remove('hidden');
			}
			// if (isAlera) {
			// 	if (copyLink) copyLink.classList.add('hidden');
			// 	if (downloadLink) downloadLink.classList.add('hidden');
			// }
		}

		function renderDoc() {
			let iframeInterval;
			let iframeCounter = 0;
			let successfulIframe;
			let url = watermarkUrl ? watermarkUrl : embedURL;

			if (/iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream) {
				let iPhoneTimer;
				iPhoneDetected = true;
				footer.classList.remove('hidden');
				embedElem.classList.remove('hidden');
				console.log('iPad or iPhone is being used');
				let iframe = document.getElementById('embedElement');
				if (iframe) {
					iframe.src = 'https://view.officeapps.live.com/op/embed.aspx?src=' + encodeURIComponent(url);
					console.log('Iframe src: ' + iframe.src);
					iframe.addEventListener('load', function () {
						console.log('Successful iframe load.');
						if (iPhoneTimer) clearInterval(iPhoneTimer);
						iframe.classList.remove('hidden');
						setTimeout(() => {
							carryOnWithLoadedDoc();
						}, 100);
					});

					function reloadIFrame() {
						console.log('Forcing reload', Date.now() - refreshTime + ' ms');
						embedElem.src = null;
						embedElem.src = 'https://view.officeapps.live.com/op/embed.aspx?src=' + encodeURIComponent(url);
					}

					let progress = 2;
					iPhoneTimer = setInterval(function () {
						progress += 1;
						document.getElementById('progress').style.width = progress + '%';
						if (progress == 10 || progress == 20 || progress == 50 || progress == 90) {
							reloadIFrame();
							oneMoment.classList.remove('hidden');
						}
					}, 500);
				} else alert('No viewer iframe found for iOS.');
			}

			function createAndLoadIframe() {
				let iframe = document.createElement('iframe');
				iframe.classList.add('doc-full', 'hidden');
				iframe.name = 'iFrame-' + iframeCounter;
				document.body.appendChild(iframe);

				iframe.addEventListener('load', function () {
					console.log('Successful iframe load.');
					if (!successfulIframe) {
						successfulIframe = iframe;
						console.log('Clearing iframeInterval.');
						clearInterval(iframeInterval);
						console.log('Unhiding iframe: ' + iframe.name);
						iframe.classList.remove('hidden');
						// setTimeout(() => { replaceExistingIframe(iframe); }, 50);
						setTimeout(() => {
							console.log('Removing other iframes (delayed).');
							document.querySelectorAll('iframe.doc-full').forEach((iframe) => {
								if (iframe != successfulIframe) {
									iframe.style.display = 'none'; // Hide first
									setTimeout(() => {
										iframe.parentNode.removeChild(iframe);
										console.log('Removed iFrame: ' + iframe.name);
									}, 3000); // Delay full removal
								}
							});
							carryOnWithLoadedDoc();
						}, 50); // Slightly more delay for stability
					}
				});

				iframe.addEventListener('error', function () {
					console.error('Error loading iframe.');
				});

				iframe.src = 'https://view.officeapps.live.com/op/embed.aspx?src=' + encodeURIComponent(url);
				if (itsTxt) iframe.src = 'https://docs.google.com/viewerng/viewer?embedded=true&url=' + encodeURIComponent(url);
				console.log('Iframe created with src: ' + iframe.src);
			}

			// Create and load virtual iframes
			if (!iPhoneDetected) createAndLoadIframe();

			if (downloadLink) {
				downloadLink.href = url;
				downloadLink.download = docName + extension;
			}

			if (printLink) {
				printLink.addEventListener('click', function (e) {
					e.preventDefault();

					if (itsDoc) {
						const url = watermarkUrl || embedURL;
						let viewerUrl;

						if (itsPDF) {
							// Use local pdf.js viewer for PDFs
							viewerUrl = `/pdfjs-dist/web/viewer.html?file=${encodeURIComponent(url)}`;
							const printWindow = window.open(viewerUrl, '_blank');

							if (printWindow) showToast('info-toast', 'Document opened in new tab. Please wait for it to load before printing.');
							else showToast('error-toast', 'Please allow pop-ups to print documents');
						} else {
							// Use Google viewer for other document types
							viewerUrl = `https://docs.google.com/viewer?url=${encodeURIComponent(url)}`;
							const printWindow = window.open(viewerUrl, '_blank');

							if (printWindow) showToast('info-toast', 'Document opened in new tab. Please use the print icon in the Google Docs viewer.');
							else showToast('error-toast', 'Please allow pop-ups to print documents');
						}
					} else if (itsImage) {
						// For images - use existing image element
						const img = document.getElementById('imageElement');
						if (img && !img.classList.contains('hidden')) window.print();
					}
				});
			}

			if (copyLink)
				copyLink.addEventListener('click', function (e) {
					// https://www.freecodecamp.org/news/copy-text-to-clipboard-javascript/
					const copyContent = async () => {
						try {
							await navigator.clipboard.writeText(itsImage ? url : embedElem.src);
							showToast('success-toast', 'Link copied to clipboard!');
						} catch (err) {
							console.error('Failed to copy: ', err);
						}
					};
					copyContent();
				});

			function reloadIFrame() {
				console.log('Forcing reload', Date.now() - refreshTime + ' ms');
				createAndLoadIframe();
			}

			let progress = 2;
			timerId = setInterval(function () {
				progress += 1;
				document.getElementById('progress').style.width = progress + '%';
				if (progress == 10 || progress == 20 || progress == 50 || progress == 90) {
					reloadIFrame();
					oneMoment.classList.remove('hidden');
				}
			}, 500);
		}

		function renderPDF() {
			let url = watermarkUrl ? watermarkUrl : embedURL;

			footer.classList.remove('hidden');
			embedElem.classList.remove('hidden');

			// Safari and Edge-specific handling to fix black screen/empty viewer issues
			let isSafari =
				/^((?!chrome|android).)*safari/i.test(navigator.userAgent) &&
				!navigator.userAgent.includes('CriOS') &&
				!navigator.userAgent.includes('FxiOS') &&
				!navigator.userAgent.includes('EdgiOS');

			let isEdge = /edg/i.test(navigator.userAgent);

			// Add retry mechanism for watermarked PDFs
			let retryCount = 0;
			const maxRetries = 3;
			let switched = false;
			let loaded = false;
			let fallbackTimer;

			// Prefer hosting your own pdf.js viewer.html to be same-origin.
			// Add a couple params that help some browsers:
			//   - disableStream/range for odd proxies
			//   - textLayerMode=2 for better text
			const pdfViewerUrl = `/pdfjs-dist/web/viewer.html?` + `file=${encodeURIComponent(url)}#disableStream=true&disableRange=true&textLayerMode=2`;

			function switchToGoogle() {
				if (switched) return;
				switched = true;
				clearTimeout(fallbackTimer);
				console.log('Switching to Google Docs viewer');
				embedElem.src = 'https://docs.google.com/viewerng/viewer?embedded=true&url=' + encodeURIComponent(url);
			}

			function loadPDF() {
				loaded = false;
				switched = false;
				clearTimeout(fallbackTimer);

				// Set src and start a timeout. If 'load' never fires, we fallback.
				embedElem.src = pdfViewerUrl;
				console.log('PDF viewer src:', embedElem.src);

				// If the viewer HTML loads, we consider it a success. PDF.js shows its own error UI if the PDF fails.
				embedElem.addEventListener(
					'load',
					() => {
						loaded = true;
						console.log('Successful PDF iframe load.');
						embedElem.classList.remove('hidden');
						setTimeout(() => {
							carryOnWithLoadedDoc();
						}, 100);
					},
					{ once: true }
				);

				// If the iframe never loads within 8s, fallback.
				fallbackTimer = setTimeout(() => {
					if (!loaded) switchToGoogle();
				}, 8000);
			}

			// Optional retry loop for watermarked PDFs (network hiccups, slow edges).
			if (watermarkUrl) {
				embedElem.addEventListener(
					'error',
					() => {
						if (switched) return; // already on Google
						retryCount++;
						if (retryCount < maxRetries) {
							console.log(`PDF load error, retrying (${retryCount}/${maxRetries})…`);
							setTimeout(loadPDF, 1000 * retryCount); // simple backoff
						} else {
							console.log('Max retries hit; falling back to Google.');
							switchToGoogle();
						}
					},
					{ once: true }
				);
			}

			loadPDF();

			if (downloadLink) {
				downloadLink.href = url;
				downloadLink.download = docName + extension;
			}
		}

		function carryOnWithLoadedDoc() {
			// HARD GUARD: run exactly once per renderDocument() call
			// Chris added this on 2-15-2026
			if (carryOnAlreadyRan) return;
			carryOnAlreadyRan = true;

			footer.classList.remove('hidden');
			let watermarkWaitState = document.getElementById('watermarkWaitState');
			if (watermarkWaitState) M.Modal.getInstance(watermarkWaitState).close();
			let certWaitState = document.getElementById('certificateWaitState');
			if (certWaitState) M.Modal.getInstance(certWaitState).close();
			progressBar.classList.add('hidden');
			oneMoment.classList.add('hidden');
			// document.getElementById('hiddenDocument').src = "";
			if (timerId) clearInterval(timerId);
			document.dispatchEvent(new Event('FinallyLoaded'));
			console.log('Finished loading', Date.now() - refreshTime + ' ms');

			if (window.analytics)
				window.analytics.track(docType + ' Viewed', {
					documentTitle: docName,
					documentFileType: embedURL.split('.').pop(),
				});

			// discover and report any missing document
			let shareHolder = '';
			let bucket = 'reports.nthround.io';
			if (docType == 'Personal Doc') {
				bucket = 'docs.nthround.io';
				shareHolder = userData.userid;
				let passedUserid = getAllUrlParams(window.location.href, true).userid; // from admin tables
				if (passedUserid) shareHolder = passedUserid;
				else if (document.referrer.includes('/admin/users/edit')) shareHolder = shareholderData.userid;
				// This fallback approach (embedURL RegEx) always should work, so obviates above 4 lines of code??
				if (shareHolder == 'consolidated.view') shareHolder = embedURL.match(new RegExp(`${client}/([^/]+)/`))?.[1] || 'UserID not found';
			}
			let fileName = embedURL.split('?')[0].replace(/^.*[\\\/]/, '');
			fileName = fileName.replace(/%C2%A0/gi, '\u00A0'); // fix rare NBSP encoding from pasted filenames
			const skipCheck = docType == 'New Certificate';
			if (!skipCheck) {
				axios
					.get('https://api.nthround.io/portal/auth/check-document', {
						params: {
							client: client,
							userid: authedUser,
							cookie: cookie,
							bucket: bucket,
							shareholder: shareHolder.replace(/\+/g, 'PLUS'),
							doc_name: embedURL.includes('.cloudfront.net/') ? fileName : embedURL,
						},
					})
					.then(function (response) {
						console.log(response.data.body);
						// "An error occurred (404) when calling the HeadObject operation: Not Found"
						let res = response.data.body.toLowerCase();
						if (res.includes('error') && res.includes('404'))
							sendAlert(
								'Missing Document Detected',
								'Client: ' + client + '<br><br>Document: ' + embedURL.replace(/^https?:\/\/[^/]+/, '').split('?')[0]
							);
					})
					.catch(function (error) {
						console.error(error);
					});
			}
			// For Alera 2023 Issuance (Common Equity Raise)
			if (ENROLL && docName.startsWith('CONFIDENTIAL PARTICIPATION NOTICE')) {
				document.getElementById('backtoCampaign').classList.add('text-white');
				let btnDecline = document.getElementById('btnDecline');
				let btnProceed = document.getElementById('btnProceed');
				btnDecline.classList.remove('hidden');
				btnProceed.classList.remove('hidden');

				btnProceed.addEventListener('click', function (e) {
					window.location.replace('/investor-agreement/');
				});

				btnDecline.addEventListener('click', function (e) {
					let declineModal = document.getElementById('declineModal');
					M.Modal.getInstance(declineModal).options.dismissible = false;
					M.Modal.getInstance(declineModal).open();
					let declineCheckBox = document.getElementById('declineCheckBox');
					let declineOkayBtn = document.getElementById('declineOkayBtn');
					declineCheckBox.addEventListener('change', function (e) {
						if (this.checked) declineOkayBtn.classList.remove('disabled');
						else declineOkayBtn.classList.add('disabled');
					});
					declineOkayBtn.addEventListener('click', function (e) {
						declineOkayBtn.classList.add('disabled');
						declineOkayBtn.innerHTML = 'Wait...';
						let declineHTML = `<html><head><meta content="text/html; charset=UTF-8" http-equiv="content-type"><style type="text/css">.c0{line-height:1.15;height:11pt}.c5{line-height:1.15;text-align:center}.c3{line-height:1.15;}.c2{font-size:11pt}.c7{max-width:468pt;padding:18pt 72pt 72pt 72pt}.c6{font-weight:700;font-family:"Times New Roman"}.c1{font-weight:400;font-family:"Times New Roman"}.c4{text-indent:36pt}p{margin:0;font-size:11pt}</style></head><body class="c7 doc-content"><p class="c5"><span class="c2 c6">PARTICIPATION ELECTION</span></p><p class="c5"><span class="c2 c6">(Decline)</span></p><p class="c0"><span class="c1 c2"></span></p><p class="c0"><span class="c2 c1"></span></p><p class="c3"><span class="c1">Participation Offeree: </span><span class="c2 c1">{full_name}</span></p><p class="c3"><span class="c1">Email Address: </span><span class="c2 c1">{email}</span></p><p class="c0"><span class="c2 c1"></span></p><p class="c0"><span class="c2 c1"></span></p><p class="c3"><span class="c2 c1">The Participation Offeree hereby acknowledges receipt and availability of the Participation Notice and the Available Materials, and hereby declines to participate in the Issuance described in the Participation Notice and hereby irrevocably waives all rights with respect to such Issuance.</span></p><p class="c0"><span class="c2 c1"></span></p><p class="c3 c4"><span class="c1">&#10004; &nbsp;Acknowledge and Decline&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;</span><span class="c1">{date}</span></p></body></html>`;
						let declineJson = {
							full_name: userData.user_name,
							email: userData.userid,
							date: moment().format('MMMM D, YYYY'),
						};
						Object.keys(declineJson).forEach((e) => (declineHTML = declineHTML.replace(new RegExp('{' + e + '}', 'g'), declineJson[e])));
						generateAcknowledgement(declineHTML);
					});
				});

				function generateAcknowledgement(htmlResults) {
					console.log('Creating decline acknowledgemnt PDF');
					let fileName = 'Issuance Decline Acknowledgemnt';
					let awsFileToUpload = cleanStampedName(fileName) + '.pdf'; //Date.now() + "_" + fileName.replace(/\s/g, "_") + ".pdf";
					document.dispatchEvent(
						new CustomEvent('ConvertToPDF', { detail: { htmlResults: htmlResults, fileName: fileName, investor: userData.userid } })
					);
					if (!addedResultantPDFListenerAlready) {
						addedResultantPDFListenerAlready = true;
						document.addEventListener('ResultantPDF', function (ev) {
							uploadToYourDocs(ev.detail.url);

							function uploadToYourDocs(pdfUrl) {
								let date = moment().format('MMM D, YYYY');
								let newDoc = {
									name: fileName,
									description: 'For ' + userData.user_name + ' dated ' + date,
									require2Factor: false,
									type: 'Agreement',
									url: awsFileToUpload,
									date: date,
									id: Date.now().toString(),
								};

								let holderData = userData.clients[client];
								if (!holderData.documents) {
									holderData.documents = [];
								}
								holderData.documents.push(newDoc);

								let investorObj = {
									declinedOffer: true,
									declinedDate: new Date().toDateString(),
									operatingAgreement: {
										signed: '',
										signedDate: '',
										complete: false,
										token_amount: '',
										investment_amount: '',
										additional_token_amount: '',
										additional_investment_amount: '',
									},
								};
								// merge and update session storage
								if (holderData.investor) {
									let investNew = Object.assign(holderData.investor, investorObj);
									holderData.investor = investNew;
								} else {
									holderData.investor = investorObj;
								}

								// update all session data
								userData.clients[client] = holderData;
								sessionStorage.setItem('user.Data', JSON.stringify(userData));

								axios
									.post(
										postURL,
										JSON.stringify({
											eventName: 'index.acknowledgement_resultant_pdf_uploaded',
											investor: userData.userid,
											userid: authedUser,
											client: client,
											cookie: cookie,
											clients: {
												[client]: {
													documents: holderData.documents,
													investor: investorObj,
												},
											},
										})
									)
									.then(function (res) {
										// needed for doc
										refreshCapTable();

										// 3) UPLOAD PDF AS NEW DOC
										fetch(pdfUrl)
											.then(function (response) {
												return response.blob();
											})
											.then(function (pdfFile) {
												axios
													.get(authUploadUrl, {
														params: {
															filename: client + '/' + userData.userid + '/' + awsFileToUpload,
															client: client,
															userid: authedUser,
															cookie: cookie,
														},
													})
													.then(function (res1) {
														let formData = secureFormData(res1);
														formData.append('file', pdfFile);
														axios
															.post(res1.data.url, formData, axconf)
															.then(function (res2) {
																M.Modal.getInstance(document.getElementById('declineModal')).close();
																window.location.replace('/');
															})
															.catch(function (err2) {
																console.error(err2);
															});
													})
													.catch(function (err1) {
														console.error(err1);
													});

												if (window.analytics)
													window.analytics.track('Generated Acknowledgement', {
														userEmail: authedUser,
														workspace: client,
														documentTitle: newDoc.name,
														documentFileType: newDoc.url.split('.').pop(),
													});
											});
									})
									.catch(function (res) {
										console.log(res);
									});
							}
						});
					}
				}
			}

			let index;
			let timeStamp = Math.floor(Date.now() / 1000).toString();
			let itsNth = isNTH || client.startsWith('onward') || isStaging;

			// Don't record views by Nth Admins, unless an Nth site
			if (!itsNth && authedUser.includes('nthround.com')) return;

			// BLOCKING ALL ADMINS PENDING CODE REVIEW
			if (isAdmin) return;

			if (docType == 'Personal Doc') {
				let documents = userData.clients[client].documents;
				let userDataSS = JSON.parse(sessionStorage.getItem('user.Data'));
				let documentsSS = userDataSS.clients[client].documents;
				if (documents.length != documentsSS.length)
					sendAlert('Documents Mismatch (' + client + '.nthround.io)', JSON.stringify(documents) + '<br><br>' + JSON.stringify(documentsSS));
				// if (!documents && NO_DOCS && document.referrer.includes('/admin/users/edit'))
				if (document.referrer.includes('/admin/users/edit')) {
					documents = shareholderData.clients[client].documents;
					userDataSS = JSON.parse(sessionStorage.getItem('shareholder.Data'));
					documentsSS = userDataSS.clients[client].documents;
					if (documents.length != documentsSS.length)
						sendAlert('Documents Mismatch (' + client + '.nthround.io)', JSON.stringify(documents) + '<br><br>' + JSON.stringify(documentsSS));
				}
				index = documents.findIndex((d) => d.id == docId);
				if (index === -1) {
					console.error('Personal Document not found in user documents array. docId:', docId);
					return;
				}
				if (!documents[index]) {
					console.error('Personal Document at index is null/undefined. docId:', docId, 'index:', index);
					return;
				}
				let thisUser = userData.userid;
				if (thisUser != authedUser) timeStamp += '~' + authedUser;
				documents[index].views = documents[index].views ? (timeStamp + ',' + documents[index].views).substring(0, 274) : timeStamp;
				userData.documents = documents;
				sessionStorage.setItem('user.Data', JSON.stringify(userData));
				axios
					.post(
						postURL,
						JSON.stringify({
							eventName: 'index.personal_doc_rendered',
							investor: thisUser,
							userid: authedUser,
							client: client,
							cookie: cookie,
							clients: {
								[client]: {
									document: {
										id: docId,
										views: documents[index].views,
									},
								},
							},
						})
					)
					.then(function (res) {
						console.log(res);

						// needed for views
						refreshCapTable();
					})
					.catch(function (err) {
						console.error(err);
					});
			} else {
				let apiUrl;
				let params;
				switch (docType) {
					case 'Update': // handled in updates.js
						// apiUrl = "company-updates";
						// let updates = clientData.company_updates;
						// index = updates.findIndex(d => d.id == docId);
						// updates[index].views = updates[index].views ? (authedUser + "|" + timeStamp + "," + updates[index].views).substring(0,4000) : authedUser + "|" + timeStamp;
						// clientData.company_updates = updates;
						// params = {company_updates: clientData.company_updates}
						break;
					case 'Report':
						apiUrl = 'financial-statements';
						let reports = clientData.financial_statements;
						index = reports.findIndex((d) => d.id == docId);
						if (index === -1) {
							console.error('Report not found in financial_statements array. docId:', docId);
							return;
						}
						if (!reports[index]) {
							console.error('Report at index is null/undefined. docId:', docId, 'index:', index);
							return;
						}
						reports[index].views = reports[index].views
							? (authedUser + '|' + timeStamp + ',' + reports[index].views).substring(0, 4000)
							: authedUser + '|' + timeStamp;
						clientData.financial_statements = reports;
						params = { financial_statement: { id: docId, views: reports[index].views } };
						break;
					case 'DataRoom Doc':
						apiUrl = 'data-room';
						let dataRoomDocs = clientData.data_room_docs;
						index = dataRoomDocs.findIndex((d) => d.id == docId);
						if (index === -1) {
							console.error('DataRoom Doc not found in data_room_docs array. docId:', docId);
							return;
						}
						if (!dataRoomDocs[index]) {
							console.error('DataRoom Doc at index is null/undefined. docId:', docId, 'index:', index);
							return;
						}
						dataRoomDocs[index].views = dataRoomDocs[index].views
							? (authedUser + '|' + timeStamp + ',' + dataRoomDocs[index].views).substring(0, 4000)
							: authedUser + '|' + timeStamp;
						clientData.data_room_docs = dataRoomDocs;
						params = { data_room_doc: { id: docId, views: dataRoomDocs[index].views } };
						break;
					case 'Fundraising Doc':
						apiUrl = 'investment-materials';
						let materials = clientData.new_investment_materials;
						index = materials.findIndex((d) => d.id == docId);
						if (index === -1) {
							console.error('Fundraising Doc not found in new_investment_materials array. docId:', docId);
							return;
						}
						if (!materials[index]) {
							console.error('Fundraising Doc at index is null/undefined. docId:', docId, 'index:', index);
							return;
						}
						materials[index].views = materials[index].views
							? (authedUser + '|' + timeStamp + ',' + materials[index].views).substring(0, 4000)
							: authedUser + '|' + timeStamp;
						clientData.new_investment_materials = materials;
						params = { material: { id: docId, views: materials[index].views } };
						break;
					case 'New Stock Certificate':
						apiUrl = '???';
						break;
				}

				sessionStorage.setItem(client + '.Data', JSON.stringify(clientData));

				if (apiUrl) {
					params['cookie'] = cookie;
					params['client'] = client;
					params['userid'] = authedUser;
					axios
						.post('https://api.nthround.io/portal/clientConfig/' + apiUrl, JSON.stringify(params))
						.then(function (response) {
							console.log(response);
						})
						.catch(function (error) {
							console.error(error);
						});
				}
			}
		}
	}
}

if (pn.includes('/signin')) {
	// --> USE DOMContentLoaded, because signin never calls getAuth() <--
	document.addEventListener('DOMContentLoaded', function () {
		//trackUser();
		AppState.clientData = JSON.parse(sessionStorage.getItem(client + '.Data'));
		if (clientData) {
			doSignIn();
		} else {
			axios
				.get('https://api.nthround.io/portal/clientConfig/client-logo', {
					params: {
						client: client,
						host: window.location.host,
						cookie: 'YDcNWRdDVdRrQ8uyeBMKw16epBeL7NIBqcKlEw6-46BI57TcKKthq3Uw4u7VA4wMg-aWz0YpYlF2lOlRRuC82SsgKNrvrrT_kyDsRFv_RcX1OCMJjBXmyhOkh752D2Ak5BQpLDPNT-VjyIGEOW-LL-ncqpjgRMl8o2QXGHNpdE8',
					},
				})
				.then(function (response) {
					AppState.clientData = response.data.Item;
					sessionStorage.setItem(client + '.Data', JSON.stringify(clientData));
					doSignIn();
				})
				.catch(function (response) {
					console.log(response);
				});
		}

		function doSignIn() {
			let sendRequest = document.getElementById('sendRequest');
			if (sendRequest && clientData.currently_raising && clientData.join_image) sendRequest.classList.remove('hidden');
			document.getElementById('brandLogo').src = clientData.company_logo ? formatImg(clientData.company_logo, '/h_48/') : '';
			// document.getElementById('clientToRequest').innerHTML = clientData.company_name;

			document.getElementById('intro').innerHTML = `
				<h1 class="text-3xl font-bold mt-0 mb-8">Welcome to ${clientData.company_name}</h1>
				${clientData.product_image ? `<img id="signInImage" src="${clientData.product_image}" class="responsive-img mb-4" style="width: 100%; height: 18rem; object-fit: cover; opacity: 0;" alt="${clientData.company_name}"/>` : ''}
			`;

			let signInImage = document.getElementById('signInImage');
			if (signInImage) unfade(signInImage);

			function unfade(element) {
				let op = 0.1; // initial opacity
				element.style.display = 'block';
				const timer = setInterval(function () {
					if (op >= 1) {
						clearInterval(timer);
					}
					element.style.opacity = op;
					element.style.filter = 'alpha(opacity=' + op * 100 + ')';
					op += op * 0.03;
				}, 10);
			}

			sessionStorage.clear();
			hideLoading();

			// document.getElementById('requestForm').addEventListener('submit', sendAccessRequest);
		}

		let modalElems = document.querySelectorAll('.modal');
		if (typeof M !== 'undefined' && M != null) {
			M.Modal.init(modalElems);
			ensureTwoFactorModalNonDismissible();
		}
	});
}

function scaffoldAuthenticatorModals() {
	if (addedAuthenticatorListenersAlready) {
		document.dispatchEvent(new Event('authenticatorComplete'));
		return;
	}

	addedAuthenticatorListenersAlready = true;
	console.log('Scaffolding authenticator: ' + (Date.now() - start) + ' ms');

	let authModals = document.createElement('div');
	authModals.innerHTML = `
		<div id="qrcodeModal" class="container py-20 modal-xs hidden">
			<div class="card p-2">
				<div style="display: flex; flex-direction: column; align-items: center;">	
					<h3 class="text-xl mt-0 mb-4 font-bold center-align">Scan this QR code into your favorite Authenticator App<span class="font-md text-secondary text-sm mt-0 mb-0"><p class="mt-0 mb-0">or copy/paste the alphanumeric</p><p class="mt-0 mb-0">string under the QR code</p></span></h3>
					<!-- <canvas id="qrcode"></canvas> -->
					<div id="qrcode"></div>
					<p id="qrcodeLabel" class="text-xs center-align" style="font-family: 'Roboto Mono', monospace; width: 26ch; word-wrap: break-word;"></p>
					<hr class="mb-0" style="border-color: #a9a6a63d !important; width: 100%;">
					<img id="qrcodeLogo" class="mb-0 mt-0"></img>
					<p class="mt-0 mb-0 text-xs">Authenticator Apps</p>
				</div>
				<div class="flex items-center justify-between">
					<p id="qrcodeCancel" class="cursor-pointer text-secondary text-sm mb-0">${pn == '/profile/' ? 'Cancel' : 'Help'}</p>
					<p id="qrcodeFinish" class="link-accent text-sm mb-0">Finished</p>
				</div>
			</div>
		</div>
		<div id="verifyModal" class="container py-20 modal-xs hidden">
			<div class="card p-2">
				<div style="display: flex; flex-direction: column; align-items: center;">
					<img id="companyLogo" class="responsive-img mb-2"></img>
					<p class="text-lg center-align" style="width: 202px; word-wrap: break-word;">Authenticate Device for Sign In Request</p>
					<form id="verifyForm" class="flex items-center justify-between center-box">
						<div class="input-field mb-4 mt-2">
							<input id="verifyInput" type="password" class="validate" name="verify" style="font-size: 28px !important; width: 160px;" autocomplete="off" maxlength="6" required />
							<label id="verifyLabel" for="verifyInput">Please enter your PIN</label>
							<span class="helper-text" data-error="From your authenticator app">From your authenticator app</span>
						</div>
						<div style="position: relative; left: -40px; top: -10px;">
							<span id="check" class="material-icons-outlined hidden" style="color: mediumseagreen; font-size: 30px; position: absolute; top: -20px;">check</span>
							<span id="clear" class="material-icons-outlined hidden" style="color: red; font-size: 30px; position: absolute; top: -20px;">clear</span>
							<svg id="spinner" class="spinner hidden" viewBox="0 0 50 50" style="position: relative; left: 20px;">
								<circle class="path" cx="25" cy="25" r="20" fill="none" stroke-width="5"></circle>
							</svg>
						</div>
						<!-- <button type="submit" class="ml-8 waves-effect waves-light bg-grey-darker btn">Enter</button> -->
					</form>
				</div>
				<p id="needHelp" class="mb-4 mt-0 text-sm center-align hidden">Forgot your PIN?&emsp;<a href="mailto:support@nthround.com?Subject=I've forgotten my PIN&body=Hi--%0D%0A%0D%0ABelow is my name and phone number (for verification):%0D%0A%0D%0A%0D%0A%0D%0AThank you!%0D%0A%0D%0AP.S. I know it may take your team a little while to respond.">Get help.</a></p>
				<div style="display: flex; flex-direction: column; align-items: center;">
					<img id="nthLogo" class="mb-4" src="https://www.nthround.io/shared_assets/s3_assets/favicon.ico" style="height:45px;"></img>
					<hr class="mb-0" style="border-color: #a9a6a63d !important; width: 100%;">
					<img id="verifyLogo" class="mb-0 mt-0"></img>
					<p class="mt-0 mb-0 text-xs">Authenticator Apps</p>
				</div>
			</div>
			<!-- <img id="verifyLogo" class="center-align mb-0" style="position: relative; top: -38px"></img> -->
		</div>
		<div id="helpModal" class="container py-20 modal-md hidden">
			<div class="card p-2">
				<div style="display: flex; flex-direction: column; align-items: center;">
					<img src="https://www.nthround.io/shared_assets/s3_assets/logo_round.svg"></img>
					<h1 class="mt-0 mb-4 font-bold center-align" style="font-size: 30px">Setting up Two-factor Aunthentication with Authenticator Apps</h1>
					<h6>Set up your two-factor authentication by following these steps:</h6>
					<ol type="1">
						<li>
							<p>Scan the QR code displayed with your authenticator app on your device (i.e. Google Authenticator).</p><br>
							<div class="center-align"><img src="https://www.nthround.io/shared_assets/s3_assets/auth1.jpg" style="height: 200px;"></img></div>
						</li>
						<li>
							<p>After you have scanned/registered the QR code with your authenticator app, click the "Finished" button.</p><br>
							<div class="center-align"><img src="https://www.nthround.io/shared_assets/s3_assets/auth2.png" style="height: 185px;"></img></div>
						</li>
						<li>
							<p>Finally, you will enter the code generated within your authenticator app.</p><br>
							<div class="center-align"><img src="https://www.nthround.io/shared_assets/s3_assets/auth3.png" style="height: 500px;"></img></div>
						</li>
					</ol>
				</div>
				<div class="items-center">
					<p id="helpExit" class="link-accent center-align text-sm mb-0">Exit Setup Instructions</p>
				</div>
			</div>
		</div>
		<style type="text/css">
			.spinner {animation: rotate 2s linear infinite; z-index: 2; margin: -25px 0 0 -25px; width: 25px; height: 25px;}
			.spinner .path {stroke: hsl(0, 0%, 70%); stroke-linecap: round; animation: dash 1.5s ease-in-out infinite;}
			@keyframes rotate {100% {transform: rotate(360deg);}}
			@keyframes dash {
				0% {stroke-dasharray: 1, 150; stroke-dashoffset: 0;}
				50% {stroke-dasharray: 90, 150; stroke-dashoffset: -35;}
				100% {stroke-dasharray: 90, 150; stroke-dashoffset: -124;}
			}
		</style>
	`;

	document.body.appendChild(authModals);

	// UI elements in Your Profile
	let requireAuthenticatorToggle = document.getElementById('requireAuthenticatorToggle');
	let challengeAuthenticatorBtn = document.getElementById('challengeAuthenticatorBtn');
	let resetAuthenticatorBtn = document.getElementById('resetAuthenticatorBtn');

	if (resetAuthenticatorBtn) resetAuthenticatorBtn.addEventListener('click', resetAuthenticator);
	if (challengeAuthenticatorBtn) challengeAuthenticatorBtn.addEventListener('click', challengeAuthenticator);

	if (requireAuthenticatorToggle) {
		if (USE_AUTHENTICATOR && authenticatorValue && authenticatorValue != 'Disabled') {
			requireAuthenticatorToggle.checked = true;
			// not sure we need a reset...
			// resetAuthenticatorBtn.classList.remove('hidden');
			challengeAuthenticatorBtn.classList.remove('hidden');
		}
		requireAuthenticatorToggle.addEventListener('change', function (e) {
			if (!this.checked) {
				resetAuthenticatorBtn.classList.add('hidden');
				challengeAuthenticatorBtn.classList.add('hidden');
				configureAuthenticator('disable');
				trackAnalytics('Disabled Authenticator');
			} else {
				configureAuthenticator('enable');
				trackAnalytics('Enabled Authenticator');
				showLoading();
			}

			function trackAnalytics(event) {
				if (window.analytics) {
					window.analytics.track(event, {
						userEmail: authedUser,
						workspace: client,
						userName: userData.user_name,
					});
				}
			}
		});
	}

	// if (authenticatorValue == "NeedAp" || (USE_AUTHENTICATOR && (!authenticatorValue || authenticatorValue == 'Disabled'))) configureAuthenticator();
	// if (authenticatorValue == "NeedAp" || (USE_AUTHENTICATOR && !authenticatorValue)) configureAuthenticator();
	if (authenticatorValue == 'NeedAp') configureAuthenticator();
	else if (authenticatorValue == 'SignIn') challengeAuthenticator();
	else document.dispatchEvent(new Event('authenticatorComplete')); //hideLoading();

	function configureAuthenticator(task) {
		axios
			.get('https://api.nthround.io/portal/auth/authenticator', {
				params: {
					client: client,
					userid: authedUser,
					cookie: cookie,
					task: task,
				},
				withCredentials: true, //make sure this is set or you don't get no cookies
			})
			.then(async function (res) {
				console.log(res);
				if (task == 'disable') return;

				showLoading();
				window.scrollTo({ top: 0, behavior: 'smooth' });
				document.getElementById('qrcodeLogo').src = formatImg(
					'https://res.cloudinary.com/nthround/image/upload/v1704905221/NEW_AUTH_APP_LOGOS_BAR.png',
					'/h_55/'
				);
				let qrcodeModal = document.getElementById('qrcodeModal').classList;
				let qrcodeLabel = document.getElementById('qrcodeLabel');
				let helpModal = document.getElementById('helpModal');
				let needHelp = document.getElementById('needHelp');

				let issuer = 'Nth Round';
				let accountName = authedUser;
				let secretKey = res.data[3]; //'W6NE4C2JXYX5DHHKB3V3AYCVCQ73Q72HVKNX6PUGZMKSJGFEJGRA'; // Replace with your secret key
				let otpauth = 'otpauth://totp/' + issuer + ':' + accountName + '?secret=' + secretKey + '&issuer=' + issuer;

				// Lazy load QRCode library
				const QRCode = await window.loadQRCode();

				let options = {
					// ====== Basic
					text: otpauth,
					width: 200,
					height: 200,
					colorDark: stdColor[4],
					colorLight: stdColor[0],
					correctLevel: QRCode.CorrectLevel.H, // L, M, Q, H

					// for MANY more options, see https://www.npmjs.com/package/easyqrcodejs
				};
				hideLoading(true);
				qrcodeModal.remove('hidden');
				qrcodeLabel.innerHTML = secretKey;
				needHelp.innerHTML = 'For help email support@nthround.com';

				// Create QRCode Object
				new QRCode(document.getElementById('qrcode'), options);

				// alternative QR code generators
				// var qrcode = new QRious({});
				// var qrcode = new QRCode(document.getElementById("qrcode"), {});

				if (challengeAuthenticatorBtn) challengeAuthenticatorBtn.classList.remove('hidden');
				document.getElementById('qrcodeCancel').addEventListener('click', (e) => {
					if (pn == '/profile/') {
						closeQR();
						// NOTE BENE: the code below will *not* work if a client REQUIRES authenticator
						configureAuthenticator('disable'); // challengeAuthenticator();	//signOut();
						if (challengeAuthenticatorBtn) challengeAuthenticatorBtn.classList.add('hidden');
						if (requireAuthenticatorToggle) requireAuthenticatorToggle.checked = false;
						hideLoading();
					} else {
						helpModal.classList.remove('hidden');
						qrcodeModal.add('hidden');
						// window.open('https://frontegg.com/blog/authentication-apps','_blank');
					}
				});
				document.getElementById('qrcodeFinish').addEventListener('click', (e) => {
					closeQR();
					challengeAuthenticator();
				});

				document.getElementById('helpExit').addEventListener('click', (e) => {
					helpModal.classList.add('hidden');
					qrcodeModal.remove('hidden');
				});

				function closeQR() {
					showLoading();
					qrcodeModal.add('hidden');
					document.getElementById('qrcode').innerHTML = '';
				}
			})
			.catch(function (err) {
				console.error(err);
			});
	}

	function challengeAuthenticator() {
		// This has problems with Rooms...
		if (pn == '/rooms/') return;
		showLoading();
		window.scrollTo({ top: 0, behavior: 'smooth' });
		document.getElementById('companyLogo').src = formatImg(clientData.company_logo, '/h_55/');
		document.getElementById('verifyLogo').src = formatImg(
			'https://res.cloudinary.com/nthround/image/upload/v1704905221/NEW_AUTH_APP_LOGOS_BAR.png',
			'/h_55/'
		);
		let verifyModal = document.getElementById('verifyModal').classList;
		let verifyLabel = document.getElementById('verifyLabel');
		let verifyInput = document.getElementById('verifyInput');
		let needHelp = document.getElementById('needHelp');
		hideLoading(true);
		verifyModal.remove('hidden');
		verifyInput.value = '';
		verifyInput.type = 'text';
		verifyInput.classList.remove('invalid');
		verifyInput.classList.remove('text-light');
		verifyInput.classList.remove('teal-text');
		verifyLabel.innerHTML = 'Enter 6-digit code';
		needHelp.innerHTML = 'For help email support@nthround.com';
		M.updateTextFields();

		if (!addedAuthenticatorChallengerAlready) {
			addedAuthenticatorChallengerAlready = true;

			verifyInput.addEventListener('keydown', function (e) {
				// Check if the Enter key was pressed
				if (e.key === 'Enter') e.preventDefault();
			});

			verifyInput.addEventListener('input', function (e) {
				e.preventDefault();
				// Filter out any non-numeric characters
				const originalValue = this.value;
				this.value = originalValue.replace(/\D/g, '');
				// Ensure the length is 6 before proceeding.  Also no Enter key.
				if (this.value.length != 6 || e.key == 'Enter') return;

				document.getElementById('spinner').classList.remove('hidden');

				// force verify-decode-jwt of new nth.IdToken
				sessionStorage.removeItem('user.Auth');

				axios
					.get('https://api.nthround.io/portal/auth/authenticator', {
						params: {
							client: client,
							userid: authedUser,
							cookie: cookie,
							task: verifyInput.value, //entry
						},
						withCredentials: true, //make sure this is set or you don't get no cookies
					})
					.then(function (res) {
						console.log(res.data[3]);
						document.getElementById('spinner').classList.add('hidden');
						if (res.data[3] == 'SUCCESS') {
							document.getElementById('check').classList.remove('hidden');
							// if (verifyInput.value == "Invalid") verifyInput.value = '';
							verifyInput.classList.add('teal-text');

							// can't make any of this work...
							verifyInput.classList.remove('invalid');
							verifyInput.classList.add('valid');
							// verifyInput.style.borderBottomColor = 'teal';
							let label = document.querySelector('label[for="verifyInput"]');
							// Change the underline color to green
							verifyInput.style.borderBottomColor = 'green';
							verifyInput.style.boxShadow = '0 1px 0 0 green';
							// Change the label color to green
							label.style.color = 'green';
							M.updateTextFields();

							setTimeout(() => {
								document.dispatchEvent(new Event('authenticatorComplete'));
								document.getElementById('check').classList.add('hidden');
								verifyInput.classList.remove('teal-text');
								M.updateTextFields();
								verifyModal.add('hidden');
								if (pn.includes('/profile')) showLoading();
							}, 1000);
							setTimeout(() => hideLoading(), 4000); // handle contingencies...
						} else {
							document.getElementById('clear').classList.remove('hidden');
							verifyInput.classList.add('text-light');
							verifyInput.value = 'Invalid';
							needHelp.classList.remove('hidden');
							verifyLabel.innerHTML = 'Please try again';
							setTimeout(() => {
								document.getElementById('clear').classList.add('hidden');
								verifyInput.classList.remove('text-light');
								verifyInput.value = '';
								M.updateTextFields();
							}, 1000);
						}
					})
					.catch(function (err) {
						console.error(err);
					});
			});
		}
	}

	// NOT FINISHED OR TESTED, NOT USED
	function resetAuthenticator() {
		// Probably need Are You Sure?

		// force verify-decode-jwt of new nth.IdToken
		// sessionStorage.removeItem("user.Auth");

		axios
			.get('https://api.nthround.io/portal/auth/authenticator', {
				params: {
					client: client,
					userid: authedUser,
					cookie: cookie,
					task: 'needap',
				},
				withCredentials: true, //make sure this is set or you don't get no cookies
			})
			.then(function (res) {
				console.log(res);
			})
			.catch(function (err) {
				console.error(err);
			});
	}
}

// Listen for events.
document.addEventListener(
	'ExtDataComplete',
	function (e) {
		extDataFinished = true;
		// 4-22-2024 really not sure we need or want the next 5 lines...
		// if (!authFinished && authType) {
		// 	let getAuthType = authType;	//getAuthType();
		// 	authFinished = getAuthType.includes('admin') || getAuthType == "user";
		// 	console.log("getAuthType = " + getAuthType);
		// }
		console.log('Event - ExternalData complete: ' + (Date.now() - refreshTime) + ' ms');
		let ready = !renderEventFired && domFinished && authFinished && storageFinished; // (authFinished || authenticated);
		// console.log("StorageComplete. Ready? = " + (ready ? "true" : ready));
		if (ready) {
			fireReadyToRender();
		} else {
			console.log('ExternalData complete: ' + renderEventFired + ' ' + (authFinished ? 'AUTH' : 'null'));
			//refreshSessionStorage();
		}
	},
	false
);

document.addEventListener(
	'StorageComplete',
	function (e) {
		storageFinished = true;
		// 4-22-2024 really not sure we need or want the next 5 lines...
		// if (!authFinished && authType) {
		// 	let getAuthType = authType;	//getAuthType();
		// 	authFinished = getAuthType.includes('admin') || getAuthType == "user";
		// 	console.log("getAuthType = " + getAuthType);
		// }
		console.log('Event - Storage complete: ' + (Date.now() - refreshTime) + ' ms');
		if (!EXTERNAL_DATA || pn != '/') extDataFinished = true;
		let ready = !renderEventFired && domFinished && authFinished && extDataFinished; // (authFinished || authenticated);
		// console.log("StorageComplete. Ready? = " + (ready ? "true" : ready));
		if (ready) {
			fireReadyToRender();
		} else {
			console.log('Storage complete: ' + renderEventFired + ' ' + (authFinished ? 'AUTH' : 'null'));
			//refreshSessionStorage();
		}
		if (freshSignIn && !nthAdmin && window.analytics && !signInAnalyticsCalled) {
			window.analytics.track('Signed In', {
				userid: authedUser,
				workspace: client,
				identity: {
					Id: client,
					Name: client,
				},
			});
			AppState.signInAnalyticsCalled = true; //to stop event from firing off more than once - still testing this
		}
	},
	false
);

document.addEventListener(
	'CapTableReady',
	function (e) {
		capTableReady = true;
		console.log('Event - CapTable ready: ' + (Date.now() - refreshTime) + ' ms');
		// For admin/supervisor pages that need cap table, check if we should now fire ReadyToRender
		if (!renderEventFired && domFinished && authFinished) {
			fireReadyToRender();
		}
		// For pages already rendered, re-read cap table data so it's available
		readSessionStorage();
	},
	false
);

let needPIN = USE_PIN; // <-- BROKEN NOW WITH AUTHENTICATOR TAKING OVER verifyModal (although *very* similar)
document.addEventListener(
	'AuthComplete',
	function (e) {
		console.log('Event - Auth complete: ' + (Date.now() - start) + ' ms');
		authFinished = true;
		if (needPIN && pn != '/rooms/') {
			// <-- Need to handled Rooms Vue...
			axios
				.get('https://api.nthround.io/portal/auth/verify-user', {
					params: {
						client: client,
						userid: authedUser,
						cookie: cookie,
						verify: '',
					},
				})
				.then(function (res) {
					console.log(res);
					if (['Valid', 'Null', 'Missing'].includes(res.data)) {
						needPIN = false;
						document.dispatchEvent(new Event('AuthComplete'));
					} else {
						let entry = '';
						let stars = '';
						let verifyInput = document.getElementById('verifyInput');
						let verifyModal = document.getElementById('verifyModal').classList;
						// verifyInput.addEventListener('keyup', function(e) {
						// 	let lengthInput = verifyInput.value.length;
						// 	let lengthDiff = lengthInput - entry.length;
						// 	if (lengthInput <= 1) {
						// 		entry = "";
						// 		stars = "";
						// 	} else if (lengthDiff < -1) {
						// 		entry = entry.slice(0, lengthDiff + 1);
						// 		stars = stars.slice(0, lengthDiff + 1);
						// 	}
						// 	if (e.code.toLowerCase() == "backspace") {
						// 		entry = entry.slice(0, -1);
						// 		stars = stars.slice(0, -1);
						// 	} else if (e.code.toLowerCase() != "enter") {
						// 		entry += e.key;
						// 		stars += "*";
						// 	}
						// 	verifyInput.value = stars;
						// });
						document.getElementById('verifyLogo').src = formatImg(
							'https://res.cloudinary.com/nthround/image/upload/v1614014941/xsnrsumwmkvrwbxahdsv.png',
							'/h_48/'
						);
						hideLoading();
						verifyModal.remove('hidden');
						// M.Modal.getInstance(verifyModal).open();
						document.getElementById('verifyForm').addEventListener('submit', function (e) {
							e.preventDefault();
							let verify = document.getElementById('verifyInput').value;
							axios
								.get('https://api.nthround.io/portal/auth/verify-user', {
									params: {
										client: client,
										userid: authedUser,
										cookie: cookie,
										verify: verify, //entry
									},
								})
								.then(function (res) {
									console.log(res);
									if (res.data == 'Valid') {
										needPIN = false;
										showLoading();
										verifyModal.add('hidden');
										// M.Modal.getInstance(verifyModal).close();
										document.dispatchEvent(new Event('AuthComplete'));
									} else {
										document.getElementById('needHelp').classList.remove('hidden');
										document.getElementById('verifyLabel').innerHTML = 'Please try again';
										verifyInput.value = '';
										entry = '';
										stars = '';
									}
								})
								.catch(function (err) {
									console.error(err);
								});
						});
					}
				})
				.catch(function (err) {
					console.error(err);
				});
		} else {
			let userStore = sessionStorage.getItem('user.Data');
			let clientStore = sessionStorage.getItem(client + '.Data');
			// Validate that cached userData has the current client's data (not just that the JSON exists)
			let userHasClientData = !!userData?.clients?.[client];
			// Only read cap table if data exists in sessionStorage to avoid warnings
			let capTableStore = [];
			if (NthUtil.sessionStore.hasCapTable(client)) {
				capTableStore = NthUtil.sessionStore.readCapTable(client);
			}
			// Check if cap table has actual data (not just empty array)
			let hasCapTableData = Array.isArray(capTableStore) && capTableStore.length > 0;
			// Only require cap table for pages that actually render cap table data
			let needCapTableData = pn.includes('/admin/') || pn.includes('/trade') || pn.includes('/supervisor');
			// Allow rendering if we have user/client data, and either have cap table OR don't need it
			// Also allow if storageFinished (data is being/has been loaded)
			let ready =
				!renderEventFired &&
				domFinished &&
				(storageFinished || (userStore && clientStore && userHasClientData && (hasCapTableData || !needCapTableData)));
			if (ready) {
				fireReadyToRender();
			} else {
				// console.log("Auth complete: " + renderEventFired + " " + (storageFinished ? "SF" : "null") + " " + (userStore ? "UDS" : "null") + " " + (clientStore ? "CDS" : "null") + " " + (capTableStore ? "CTS" : "null") + " " + (needCapTableData ? "NCTD" : "null"));
				// Trigger refresh if user client data or cap table data is missing
				if (USE_PIN || !userHasClientData || (!hasCapTableData && needCapTableData && !freshSignIn)) {
					if (!userHasClientData) console.log('AuthComplete - refreshSessionStorage (user client data missing)');
					else console.log('AuthComplete - refreshSessionStorage (cap table missing)');
					refreshSessionStorage(); // <-- THIS CAN CREATE A NEW CAP TABLE SNAPSHOT (e.g., a user entering trade.js after browser refresh)

					// Fallback: if StorageComplete doesn't fire within 10 seconds, allow rendering anyway
					setTimeout(() => {
						if (!storageFinished && !renderEventFired) {
							console.warn('Data load timeout - allowing render to proceed');
							fireReadyToRender();
						}
					}, 10000);
				}
				// }
			}
		}
	},
	false
);

document.addEventListener(
	'DOMContentLoaded',
	function () {
		console.log('Event - DOM complete: ' + (Date.now() - start) + ' ms');
		domFinished = true;
		axios.defaults.withCredentials = cookie ? false : true;
		// showLoading();
		let userStore = sessionStorage.getItem('user.Data');
		let clientStore = sessionStorage.getItem(client + '.Data');
		// Validate that cached userData has the current client's data (not just that the JSON exists)
		let userHasClientData = !!userData?.clients?.[client];
		// Only read cap table if data exists in sessionStorage to avoid warnings
		let clientCapTable = [];
		if (NthUtil.sessionStore.hasCapTable(client)) {
			clientCapTable = NthUtil.sessionStore.readCapTable(client);
		}
		// Check if cap table has actual data (not just empty array) and not blocked
		let hasCapTableData = Array.isArray(clientCapTable) && clientCapTable.length > 0 && !blurBlock;
		// For /admin/users, we need cap table data. For other pages, it's optional.
		let needCapTableForThisPage = pn.includes('/admin/users');
		let capTableStore = hasCapTableData || !needCapTableForThisPage;
		let ready = !renderEventFired && authFinished && userStore && clientStore && userHasClientData && capTableStore; // && sessionStorage.getItem('user.Auth');
		// console.log("DOMContentLoaded. Ready? = " + (ready ? "true" : ready));
		if (ready) {
			fireReadyToRender();
		} else {
			// console.log("DOM complete: " + renderEventFired + " " + (userStore ? "UDS" : "null") + " " + (clientStore ? "CDS" : "null") + " " + (capTableStore ? "CTS" : "null") + " " + (sessionStorage.getItem('user.Auth') ? "UA" : "null"));
			// If user client data or cap table data is missing, trigger refresh but don't block forever
			if ((!userHasClientData || (needCapTableForThisPage && !hasCapTableData)) && !storageFinished) {
				if (!userHasClientData) console.log('DOMContentLoaded - triggering refreshSessionStorage for missing user client data');
				else console.log('DOMContentLoaded - triggering refreshSessionStorage for missing cap table');
				refreshSessionStorage();

				// Fallback timeout to prevent infinite hang
				setTimeout(() => {
					if (!storageFinished && !renderEventFired && authFinished && userStore && clientStore) {
						console.warn('Data load timeout on DOMContentLoaded - allowing render to proceed');
						fireReadyToRender();
					}
				}, 10000);
			}
		}

		// require re-auth after 30 minutes or 30 page loads
		let loadCount = sessionStorage.getItem('loadCount');
		if (!loadCount || loadCount == '0') loadCount = 0;
		else loadCount++;
		// changed logic to above for authenticator on 4-22-2024
		// if (!loadCount) {
		// 	loadCount = 1;
		// } else {
		// 	loadCount = Number(loadCount) + 1;
		// }
		sessionStorage.setItem('loadCount', loadCount);

		let sessionTime = sessionStorage.getItem('sessionTime');
		if (!sessionTime) {
			sessionTime = Date.now() - 10000;
			sessionStorage.setItem('sessionTime', sessionTime);
		}
		if (loadCount > 30 || Date.now() < Number(sessionTime) || Date.now() - Number(sessionTime) > 1800000) {
			sessionStorage.clear();
			userData = null;
			clientData = null;
			capTableData = [];
		}

		// Only initialize Vue dashboard if the mount point exists
		if (document.getElementById('vue-app')) {
			window.initVueDashboard().catch((err) => {
				console.error('Failed to initialize Vue dashboard:', err);
			});
		}
	},
	false
);

document.addEventListener(
	'ReadyToRender',
	function (e) {
		// 3/23/2024 moved to auth.js
		// if (USE_AUTHENTICATOR && !addedAuthenticatorListenersAlready) scaffoldAuthenticatorModals();

		if (!isCoreDataValid(userData, clientData)) {
			renderEventFired = true;
			showFatalError(
				'Oops, this is awkward... Please try again in a few minutes! Thank you. (support@nthround.com)',
				'ReadyToRender fired with invalid core data',
				{ userDataType: typeof userData, clientDataType: typeof clientData, page: window.location.pathname }
			);
			return;
		}

		axios.defaults.withCredentials = cookie ? false : true;
		renderEventFired = true;
		setLabels('ready to render');
		console.log('Event - ReadyToRender: ' + (Date.now() - start) + ' ms');

		//refreshSessionStorage();
		loadPageContent();
		initializeComponents();
		postVestingSchedules();
		showAvailableWorkspaces();
		// don't track if incognito
		let tokenCount = document.cookie.split(';').length;
		console.log('Index token count = ' + tokenCount);
		// if (tokenCount > 6) trackUser();
		trackUser();
		let waitForConsolidatedView =
			userData?.clients?.[client]?.consolidated_viewer && sessionStorage.getItem('loadCount') == '0' && !userData.userid.includes('+');
		if (!pn.includes('/admin/') && ((!VOTING && !ENROLL) || pn != '/documents/') && !waitForConsolidatedView) hideLoading();
		// if (!pn.includes('/admin/') && ((!VOTING && !ENROLL) || pn != '/documents/') && (!authenticatorValue || !USE_AUTHENTICATOR)) hideLoading();
		sessionStorage.setItem('referrer', window.location.href);
		TRACE = false;

		const params = new URLSearchParams(window.location.search);
		const modalId = params.get('openModal');
		if (modalId) {
			const modalEl = document.getElementById(modalId);
			const instance = modalEl ? M.Modal.getInstance(modalEl) : null;
			if (instance) {
				instance.open();
				history.replaceState(null, '', '/');
			}
		}
	},
	false
);

document.addEventListener(
	'GetShareholderData',
	function (e) {
		let shareholder = window.location.search.replace('?email=', '');
		if (shareholder) getShareholderData(shareholder, true);
	},
	false
);

document.addEventListener(
	'RefreshSessionStorage',
	function (e) {
		refreshSessionStorage(); // <-- THIS CAN LOAD CAP TABLE DATA FOR NON-ADMINS (e.g., on any browser refresh)
	},
	false
);

document.addEventListener(
	'RefreshConsolidatedTransactions',
	function () {
		if (userData?.consolidatedUserIds?.length) {
			const callback = () => document.dispatchEvent(new Event('StorageComplete'));
			if (isAlera) {
				loadConsolidatedAleraTransactions(userData.consolidatedUserIds, callback);
			} else {
				loadConsolidatedExternalTransactions(userData.consolidatedUserIds, callback);
			}
		}
	},
	false
);

document.addEventListener(
	'RefreshCapTable',
	function (e) {
		refreshCapTable();
	},
	false
);

document.addEventListener(
	'SetLabelsRemovals',
	function (e) {
		setLabels('refresh');
	},
	false
);

document.addEventListener(
	'buildEventsAndTransactionsModal',
	function (e) {
		buildEventsAndTransactionsModal();
	},
	false
);

document.addEventListener(
	'renderInvestorPortal',
	function (e) {
		renderInvestorPortal();
	},
	false
);

let userTags = userData && userData.clients[client] ? userData.clients[client].tags : null;
if (userTags && userTags.includes('TAX ONLY') && pn != '/documents/' && pn != '/admin/users/') window.location.replace('/documents/');

showLoading();
console.log('Scan complete: ' + (Date.now() - start) + ' ms');
